import { Procedure } from '@igus/material-domain';
import { Procedure as Procedure$1 } from '@igus/material-domain/src/procedure/procedure';
import { spatialTypeOfMovements, TemporalTypeOfMovement, HousingMaterialIds, OppositeMaterialIds, TechnicalEnvironment } from '@igus/tribology-domain';
import { methodCharacteristicsMap } from '@igus/iglidur-designer-domain';
import { round4 as round4$1, getLocalizationKeyForVelocitySymbol, toMetricVelocity, fromMetricVelocity, getLocalizationKeyForTimeSymbol, toMetricTime, fromMetricTime, getLocalizationKeyForThermalConductivity, toMetricThermalConductivity, fromMetricThermalConductivity, getLocalizationKeyForTemperatureSymbol, toMetricTemperature, fromMetricTemperature, getLocalizationKeyForPressureVelocitySymbol, toMetricPressureVelocity, fromMetricPressureVelocity, getLocalizationKeyForPressureMegaSymbol, toMetricPressureMega, fromMetricPressureMega, getLocalizationKeyForPressureSymbol, toMetricPressure, fromMetricPressure, getLocalizationKeyForMassKiloSymbol, toMetricMassKilo, fromMetricMassKilo, getLocalizationKeyForMassSymbol, toMetricMass, fromMetricMass, getLocalizationKeyForForceKiloSymbol, toMetricForceKilo, fromMetricForceKilo, getLocalizationKeyForForceSymbol, toMetricForce, fromMetricForce, getLocalizationKeyForDistanceSymbol, toMetricDistance, fromMetricDistance, getLocalizationKeyForDistanceMilliSymbol, toMetricDistanceMilli, fromMetricDistanceMilli, getLocalizationKeyForAreaSymbol, toMetricArea, fromMetricArea, getLocalizationKeyForAreaMilliSymbol, toMetricAreaMilli, fromMetricAreaMilli, getLocalizationKeyForAngleSymbol, toMetricAngle, fromMetricAngle, UnitSystem, DefaultLogger, iProDomains } from '@igus/kopla-domain';
import { padStart, cloneDeep } from 'lodash-es';
import inspect from 'object-inspect';
import Color from 'color';

const BEVEL_RADIAL_MIN_DISTANCE = 0.1; // in mm
const BEVEL_AXIAL_MIN_DISTANCE = 0.1; // in mm
const BevelNamesArray = ['f1', 'f2', 'f3', 'f4'];

var Context;
(function (Context) {
  Context[Context["IBD"] = 0] = "IBD";
  Context[Context["IBH"] = 1] = "IBH";
  Context[Context["NBS"] = 2] = "NBS";
})(Context || (Context = {}));

const formTypeAsArrayIBD = ['S', 'F', 'T', 'PR', 'EC', 'C', 'RL'];
const formTypeAsArrayICN = ['S', 'F', 'T', 'PR', 'FSG'];
const formTypeAsArrayIBH = ['S', 'F', 'T', 'PR', 'EC', 'C', 'FSG', 'FL', 'VFL', 'Y', 'YK', 'TE', 'D', 'VF', 'K', 'VS'];
const formTypeAsArray = [...new Set([...formTypeAsArrayIBD, ...formTypeAsArrayIBH])];

var ColorCaption;
(function (ColorCaption) {
  ColorCaption["GREAT"] = "GREAT";
  ColorCaption["GOOD"] = "GOOD";
  ColorCaption["FAIR"] = "FAIR";
  ColorCaption["POOR"] = "POOR";
  ColorCaption["LOW"] = "LOW";
})(ColorCaption || (ColorCaption = {}));
/**
 * A quarter of PI.
 *
 * The Excel tool uses 0.785 as π/4.
 * Lars Butenschön decided to do this here as well to be comparable.
 *
 * Because we calculate the volumes now correctly, we need to use the real value so now we use Math.PI/4
 */
const PI_QUARTER = Math.PI / 4;
var CustomizationType;
(function (CustomizationType) {
  CustomizationType[CustomizationType["catalog_part"] = 0] = "catalog_part";
  CustomizationType[CustomizationType["catalog_part_custom_tolerance"] = 1] = "catalog_part_custom_tolerance";
  CustomizationType[CustomizationType["drawing_part"] = 2] = "drawing_part";
})(CustomizationType || (CustomizationType = {}));

/**
 * List of materials existing in parts imported from akeneo
 * We want to exclude those parts from the frontend, so we ban their materials
 *
 * IGL-1652 => 'Q2E'
 *
 * Materials that are not allowed to be used in IBD and IBH
 *
 *  IGL-4195 => C
 *  IGL-707 => J4-Asia
 *  IGL-712 => P230
 *  IGL-3347 => K250
 *  IGL-3545 => L350
 *  IGL-3733 => RN29, RN32, RN36, RN38, RN48, RN57, RN72
 *  IGL-4222 => C210, RN249
 *  IGL-4394 => TX2
 */
const partsMaterialsDisabled = ['C', 'C210', 'Q2E', 'J4-Asia', 'P230', '233/', 'K250', 'L350', 'RN29', 'RN249', 'RN32', 'RN36', 'RN38', 'RN48', 'RN57', 'RN72', 'TX2'];
/**
 * Materials that are only available in certain countries.
 * Use as index in {@link materialAvailabilityIndex}
 */
const materialAvailabilityIndex = ['J-USA', 'GLW'];
/**
 * Country codes with specific material availability rules
 * Use as index in {@link materialAvailabilityIndex}
 */
const materialAvailabilityCountryIndex = ['US', 'CA'];
/**
 * Each material code is paired with a function that determines in which countries and context it's available.
 * If not specified, materials are available in all countries and all context
 */
const materialAvailabilityMap = {
  'J-USA': country => country === 'US',
  GLW: (country, context) => !['US', 'CA'].includes(country) || context === Context.IBD
};
/**
 * Materials that are not allowed to be used for SFT forms.
 *
 *  IGL-1217 => A250
 */
const materialsSFTDisabled = ['A250'];
// materials updated according to IGL-356, IGL-532, IGL-561, IGL-823
const materialsFastLine = ['A180', 'A181', 'A250', 'A350', 'AX500', 'J', 'J260', 'J350', 'JB', 'P', 'M250', 'R', 'RN33', 'RN56', 'Igumid G', 'G', 'GLW', 'H', 'H1', 'H2', 'H370', 'HLW', 'W300', 'P4', 'P210', 'J4', 'J3', 'Q2', 'X', 'xirodur B180'];
// S for WOUND production method
const materialsWoundS = ['TX1', 'TX2', 'TX3'];
// PR
const materialsPistonRing = ['J', 'G', 'J4', 'W300', 'M250', 'X', 'RN213', 'A180', 'A350'];
const materialsPistonRingFastLine = materialsPistonRing.filter(e => materialsFastLine.includes(e) && e !== 'A180');
// EC
const materialsEasyClip = ['J', 'M250', 'RN27', 'RN28', 'A180', 'K230'];
const materialsEasyClipFastLine = materialsEasyClip.filter(e => materialsFastLine.includes(e) && e !== 'A180');
// C
const materialsC = [...materialsEasyClip, 'K250', 'A230'];
const materialsCFastLine = materialsC.filter(e => materialsFastLine.includes(e) && e !== 'A180');
// RL : KnifeEdgeRollers / Messerkantenrollen
const materialsRL = ['A180', 'A250', 'P210'];
const materialsRLFastLine = materialsRL.filter(e => materialsFastLine.includes(e));
// FL : Two hole flange bearing
const materialsFL = ['G', 'A180', 'J', 'X'];
// FL parts do not support fastline, no material returned
// Fixme: Either we use these lists or we remove them
const materialsFLFastLine = [];
// Y : Split bearing
const materialsY = ['M250'];
// Y parts do not support fastline, no material returned
const materialsYFastLine = [];
// YK : Split bearing anti rotation
const materialsYK = ['M250'];
// YK parts do not support fastline, no material returned
const materialsYKFastLine = [];
// VFL : Two hole flange bearing with preload
const materialsVFL = ['J'];
// VFL parts do not support fastline, no material returned
const materialsVFLFastLine = [];
// TE : Disc spring
const materialsTE = ['A500', 'J'];
// TE parts do not support fastline, no material returned
const materialsTEFastLine = [];
// D : Disc spring
const materialsD = ['M250'];
// D parts do not support fastline, no material returned
const materialsDFastLine = [];
// VF : Preloaded flange bearings
const materialsVF = ['J'];
// VF parts do not support fastline, no material returned
const materialsVFFastLine = [];
// K : Double flange bearings
const materialsK = ['M250'];
// K: parts do not support fastline, no material returned
const materialsKFastLine = [];
// VS: Clearance-free preloaded bearing
const materialsVS = ['J'];
// VS: parts do not support fastline, no material returned
const materialsVSFastLine = [];
// FSG: feltring bearings
const materialsFSG = ['P210', 'G', 'Q2'];
// FSG: feltring bearing, no material returned
const materialsFSGFastLine = ['P210', 'G', 'Q2'];
const igutexMaterials = ['TX1', 'TX2', 'TX3'];
const materialTradeMarks = {
  iglidur: 'iglidur®',
  igutex: 'igutex®',
  iglide: 'iglide®'
};

/**
 * When you add values to the following list please also add the PrincipalMoldNames in
 *
 * the array of principalMoldNames: PrincipalMoldName[]
 * below and in the following files:
 *
 *  - cycleTime.ts
 *  - cycleTime.unit.ts
 *  - machine.ts
 *  - machine.unit.ts
 *  - quantitiesOfCavities.ts
 *  - quantitiesOfCavities.unit.ts
 *  - sprue.ts
 *  - sprue.unit.ts
 *
 * And in a second step in the data-service in
 *
 *  - priceDomain/moldInjection/price.ts
 *  - priceDomain/moldInjection/price.unit.ts
 */
// end of types -- starting with consts
const principalMoldNamesSFT = ['S9B', 'S9', 'S17B', 'S17', 'S25', 'S40', 'S50', 'S60', 'S65', 'S80', 'S122', 'S162'];
const principalMoldNameSFTFastLine = ['S17', 'S25', 'S40', 'S60'];
const principalMoldNamesEC = ['Clip1_EC', 'Clip2_EC', 'Clip3_EC'];
const principalMoldNamesPR = ['S25_PR_GS', 'S50_PR_GS', 'AS40_PR_SS', 'AS61_PR_SS', 'S8267_PR_SS', 'S80_PR_SIS', 'S120_PR_GS', 'S120_PR_SIS', 'AS120_PR_SS', 'Clip1_PR_SAS', 'Clip2_PR_SAS', 'Clip3_PR_SAS'];
const principalMoldNamesPRFastLine = ['S25_PR_GS', 'S50_PR_GS', 'AS40_PR_SS', 'AS61_PR_SS'];
const principalMoldNamesMCM = ['Clip1_EC', 'Clip2_EC', 'Clip3_EC'];
const principalMoldNamesKnifeEdgeRollers = ['S50'];
const principalMoldNamesOTHERS = ['S225', 'AS17', 'AS61', 'AS120', 'Clip1', 'Clip2', 'Clip3', '2134', '8003', '8008', '8035', '8055', '8062', '8500', '7727', '7741'];
const principalMoldNames = principalMoldNamesSFT.concat(principalMoldNamesEC, principalMoldNamesPR,
// principalMoldNamesMCM,  // we do not need to add these names because they are already there from the EasyClips
// principalMoldNamesKnifeEdgeRollers, // we do not need to add these names because they are already there from SFT
principalMoldNamesOTHERS);

const nbsCategories = ['iglidur_bearings_F', 'iglidur_bearings_FL', 'iglidur_bearings_S', 'iglidur_bearings_T', 'piston_rings'];

const MI_FALLBACK_PART_NO = 'P-XXXXX';
const SRF_FALLBACK_PART_NO = 'SFR-XXXXX';
const MNA_FALLBACK_PART_NO = 'MNA-XXXXX';
const TD_FALLBACK_PART_NO = '3D-XXXXX';
/**
 * The absolute maximal dimension allow for a part
 */
const ABS_MAX_DIMENSION = 250;
// ===================================================================================
const EASY_CLIP_SHORT_NOSE_LENGTH_MM = 1.2;
const EASY_CLIP_LONG_NOSE_LENGTH_MM = 1.5;
const EASY_CLIP_NOSE_HEIGHT = 0.3;

// ******************
// Processing Methods
// ******************
const processingMethods = {
  MOLD_INJECTION: 'MOLD-INJECTION',
  THREE_DIMENSIONAL_PRINT: 'THREE-DIMENSIONAL-PRINT',
  SEMI_FINISHED: 'SEMI-FINISHED',
  MECHANICAL_REPROCESSING: 'MECHANICAL-REPROCESSING',
  FAST_LINE: 'FAST-LINE'
};
const processingMethodsAsArray = Object.values(processingMethods);
const processingMethodsIbd = Object.assign({}, processingMethods, {
  COMPETITOR_PART: 'COMPETITOR-PART'
});
const processingMethodsIbdCatalog = [processingMethodsIbd.MOLD_INJECTION, processingMethodsIbd.FAST_LINE];
const processingMethodsIbh = Object.assign({}, processingMethods, {
  WOUND: 'WOUND'
});
const processingMethodsIBHAsArray = Object.values(processingMethodsIbh);
const processingMethodsIbhCatalog = [processingMethodsIbh.MOLD_INJECTION, processingMethodsIbh.FAST_LINE, processingMethodsIbh.WOUND];

var FastLineCalculationHint;
(function (FastLineCalculationHint) {
  FastLineCalculationHint["OK"] = "OK";
  FastLineCalculationHint["Error_QuantityNotAllowed"] = "Error_QuantityNotAllowed";
  FastLineCalculationHint["Error_DiscountFactorNotAllowed"] = "Error_DiscountFactorNotAllowed";
  FastLineCalculationHint["Error_InjectionMoldParamsNotAllowed"] = "Error_InjectionMoldParamsNotAllowed";
  FastLineCalculationHint["Error_TypeCatalogNotAllowed"] = "Error_TypeCatalogNotAllowed";
  FastLineCalculationHint["Error_TypeCustomPartNotAllowed"] = "Error_TypeCustomPartNotAllowed";
  FastLineCalculationHint["Error_TypeDerivedCatalogNotAllowed"] = "Error_TypeDerivedCatalogNotAllowed";
  FastLineCalculationHint["Error_MaterialNotAllowed"] = "Error_MaterialNotAllowed";
  FastLineCalculationHint["Error_MaterialIncalculable"] = "Error_MaterialIncalculable";
})(FastLineCalculationHint || (FastLineCalculationHint = {}));

// at the moment there are no translations for this in the application
var MechanicalReprocessingCalculationHint;
(function (MechanicalReprocessingCalculationHint) {
  MechanicalReprocessingCalculationHint["OK"] = "OK";
  MechanicalReprocessingCalculationHint["Error_NotAllowedToChangeB1forTypeT"] = "Error_NotAllowedToChangeB1forTypeT";
  MechanicalReprocessingCalculationHint["Error_NotABearingParam"] = "Error_NotABearingParam";
  MechanicalReprocessingCalculationHint["Error_PartTooShort"] = "Error_PartTooShort";
  MechanicalReprocessingCalculationHint["Error_BearingFormNotValid"] = "Error_BearingFormNotValid";
  MechanicalReprocessingCalculationHint["Error_NoGreaterPartFoundInCatalog"] = "Error_NoGreaterPartFoundInCatalog";
  MechanicalReprocessingCalculationHint["Error_NoPriceForArticleInDb"] = "Error_NoPriceForArticleInDb";
  MechanicalReprocessingCalculationHint["Error_PartFoundInCatalog"] = "Error_PartFoundInCatalog";
  MechanicalReprocessingCalculationHint["Error_QuantityOnRequest"] = "Error_QuantityOnRequest";
  MechanicalReprocessingCalculationHint["Error_WallthicknessToSmall"] = "Error_WallthicknessToSmall";
})(MechanicalReprocessingCalculationHint || (MechanicalReprocessingCalculationHint = {}));

/**
 * definitions for processing method (pm) mold-injection
 */
const DEFAULT_QUANTITY_ORDER_LIMIT = 14999;
const FREE_LOT_SIZE_QUANTITY = 15000;
const DEFAULT_MI_QUANTITIES = [1, 10, 25, 50, 100, 200, 500, 1000, 2500, 5000, 10000];
const DETAILED_MI_QUANTITIES = Array.from({
  length: 24
}, (_, index) => index + 1); // [1, 2, 3, ..., 23, 24]
var PriceCalculationType;
(function (PriceCalculationType) {
  PriceCalculationType[PriceCalculationType["catalog"] = 0] = "catalog";
  PriceCalculationType[PriceCalculationType["calculate_from_production_lot"] = 2] = "calculate_from_production_lot";
  PriceCalculationType[PriceCalculationType["calculate_custom_part"] = 3] = "calculate_custom_part";
})(PriceCalculationType || (PriceCalculationType = {}));
var DistributionChannel;
(function (DistributionChannel) {
  DistributionChannel[DistributionChannel["I"] = 0] = "I";
  DistributionChannel[DistributionChannel["T"] = 1] = "T";
  DistributionChannel[DistributionChannel["H"] = 2] = "H";
  DistributionChannel[DistributionChannel["D"] = 3] = "D";
})(DistributionChannel || (DistributionChannel = {}));
const possibleCavityQuantities = [1, 2, 4, 6, 8];

// at the moment there are no translations for this in the application
var SemiFinishedCalculationHint;
(function (SemiFinishedCalculationHint) {
  SemiFinishedCalculationHint["OK"] = "OK";
  SemiFinishedCalculationHint["Error_BearingFormNotSFT"] = "Error_BearingFormNotSFT";
  SemiFinishedCalculationHint["Error_CustomizationTypeNotValid"] = "Error_CustomizationTypeNotValid";
  SemiFinishedCalculationHint["Error_B1TooShort"] = "Error_B1TooShort";
  SemiFinishedCalculationHint["Error_B1TooBig"] = "Error_B1TooBig";
  SemiFinishedCalculationHint["Error_D2TooBig"] = "Error_D2TooBig";
  SemiFinishedCalculationHint["Error_OuterDiameterOfMatchingPartTooBig"] = "Error_OuterDiameterOfMatchingPartTooBig";
  SemiFinishedCalculationHint["Error_MaterialNotAllowed"] = "Error_MaterialNotAllowed";
  SemiFinishedCalculationHint["Error_NoMatchingArticleInDb"] = "Error_NoMatchingArticleInDb";
  SemiFinishedCalculationHint["Error_NoPriceForArticleInDb"] = "Error_NoPriceForArticleInDb";
  SemiFinishedCalculationHint["Error_NoPriceForSpecifiedDimensions"] = "Error_NoPriceForSpecifiedDimensions";
  SemiFinishedCalculationHint["Error_NotABearingParam"] = "Error_NotABearingParam";
  SemiFinishedCalculationHint["Error_QuantityOnRequest"] = "Error_QuantityOnRequest";
  SemiFinishedCalculationHint["Error_WallthiknessForLongPartTooSmall"] = "Error_WallthiknessForLongPartTooSmall";
  SemiFinishedCalculationHint["Error_WallthiknessForShortPartTooSmall"] = "Error_WallthiknessForShortPartTooSmall";
  SemiFinishedCalculationHint["Error_PartFoundInCatalog"] = "Error_PartFoundInCatalog";
})(SemiFinishedCalculationHint || (SemiFinishedCalculationHint = {}));

/**
 * definitions for processing method threeDimensionalPrint
 */
const maxWallThicknessPerProcedure = {
  [Procedure.DLP]: 0.4,
  [Procedure.SLS]: 0.7,
  [Procedure.FDM]: 1.0
};

const printProcedures = [Procedure$1.SLS, Procedure$1.FDM, Procedure$1.DLP];
const mouldProcedures = [Procedure$1.IM];
const woundProcedures = [Procedure$1.WOUND];
const cncProcedures = [Procedure$1.CNC];
const procedurePerMethodMap = {
  [processingMethodsIbh.THREE_DIMENSIONAL_PRINT]: printProcedures,
  [processingMethodsIbh.WOUND]: woundProcedures,
  [processingMethodsIbh.MOLD_INJECTION]: mouldProcedures,
  [processingMethodsIbh.FAST_LINE]: mouldProcedures,
  [processingMethodsIbh.SEMI_FINISHED]: cncProcedures,
  [processingMethodsIbh.MECHANICAL_REPROCESSING]: [...mouldProcedures, ...woundProcedures]
};

/**
 * definitions for profit calculation
 */
var ProfitColorType;
(function (ProfitColorType) {
  ProfitColorType["green"] = "GREEN";
  ProfitColorType["yellow"] = "YELLOW";
  ProfitColorType["red"] = "RED";
})(ProfitColorType || (ProfitColorType = {}));
const profitColorGreen = '#209C49';
const profitColorYellow = '#F3E03B';
const profitColorRed = '#AF3532';

const shapeWidenIds = {
  S: {
    // eb3f9908-f1fa-498e-9ffc-01b56140239d
    schemaShortKey: 'w5iyzj9n7t',
    // de28d65f-d527-442e-b23a-d19f286b4419
    iconShortKey: '2a2ftmipxx'
  },
  F: {
    // 8024f035-9a29-46d9-9475-a4d87c30bd35
    schemaShortKey: 'pfemju1qhl',
    // f704ce10-1e41-4d3b-9e80-bbd7654635b4
    iconShortKey: 'entjngeogg'
  },
  FL: {
    // 0a545edc-ba80-43fd-9615-d716a89756c2
    schemaShortKey: 'trjsneymr6',
    // 358ab552-2b9d-49d6-a8fa-e1a5e80213c7
    iconShortKey: '0vchkgvmjr'
  },
  T: {
    // fe4fc0df-388c-499a-8df6-564ae60c77b2
    schemaShortKey: 'atxzmyr9xc',
    // a4ddf009-ce33-4ae5-88fd-63682d01f7d0
    iconShortKey: '3fpqttb45c'
  },
  PR: {
    // d3a1bd20-06ef-4a6f-af82-f638e13f39bd
    schemaShortKey: 'qdzglcpwwy',
    // 4bf34394-7ffb-4d86-9cbc-9878f7c34fd6
    iconShortKey: 'dpoe5rkylk'
  },
  C: {
    // 9a7e0fa1-4e4d-43d6-aee6-e5cb32c5d07f
    schemaShortKey: 'dugaoyhl5w',
    // a98dce25-8beb-43f7-8246-abb908a3f005
    iconShortKey: 'dvtgkfdwmw'
  },
  EC: {
    // eb9deb4c-45cc-4d07-8a47-574e77c8c810
    schemaShortKey: 'jni5yrfyd1',
    // 282a6978-9c78-4474-ac06-8d6461b0488e
    iconShortKey: 'b28s3tkr92'
  },
  RL: {
    // 1c065a27-9aed-4cd8-9978-9c2fa9af04f0
    schemaShortKey: 'qb7tppd0tl',
    // 156d24f3-70e9-45b6-84dc-e4bca12874e1
    iconShortKey: '18i0bdcsjg'
  },
  VFL: {
    // 728040e3-9918-4661-85aa-4bb3e4402927
    schemaShortKey: 'xjdjc4ulec'
  },
  Y: {
    // 91587f37-e9dd-4739-9302-bf409d29ba14
    schemaShortKey: 'ewyp0to14g'
  },
  YK: {
    // b011b3e7-1c21-4ec2-be14-2eaf3ce98e3a
    schemaShortKey: 'wrouhjqjnt'
  },
  TE: {
    // 4f7740c4-de2f-41eb-8121-904831d2363c
    schemaShortKey: 'cgjbvyv0gm'
  },
  D: {
    //e64d078e-1467-4b73-82f1-0055ca238904
    schemaShortKey: 'na6uawx4i6'
  },
  VF: {
    //ac4e8732-86cc-441e-aff5-7e3ff0777b44
    schemaShortKey: 'fv1azmunkk'
  },
  K: {
    //03224806-d361-424f-a033-3d49eea9b3b0
    schemaShortKey: '42hi394kjt'
  },
  VS: {
    //41082da6-575d-4a18-a647-a575b221e41f
    schemaShortKey: '1rgeq8wxfy'
  },
  FSG: {
    //baa52a6c-d64b-47af-9af1-4100f2349b61
    schemaShortKey: 'q7v4ni5gxh'
  }
};
const bearingShapes = [{
  shape: 'S',
  widen: shapeWidenIds['S']
}, {
  shape: 'F',
  widen: shapeWidenIds['F']
}, {
  shape: 'FL',
  widen: shapeWidenIds['FL']
}, {
  shape: 'VFL',
  widen: shapeWidenIds['VFL']
}, {
  shape: 'Y',
  widen: shapeWidenIds['Y']
}, {
  shape: 'YK',
  widen: shapeWidenIds['YK']
}, {
  shape: 'T',
  widen: shapeWidenIds['T']
}, {
  shape: 'PR',
  widen: shapeWidenIds['PR']
}, {
  shape: 'C',
  widen: shapeWidenIds['C']
}, {
  shape: 'EC',
  widen: shapeWidenIds['EC']
}, {
  shape: 'TE',
  widen: shapeWidenIds['TE']
}, {
  shape: 'D',
  widen: shapeWidenIds['D']
}, {
  shape: 'VF',
  widen: shapeWidenIds['VF']
}, {
  shape: 'K',
  widen: shapeWidenIds['K']
}, {
  shape: 'VS',
  widen: shapeWidenIds['VS']
}, {
  shape: 'FSG',
  widen: shapeWidenIds['FSG']
}];

var OfferType;
(function (OfferType) {
  OfferType[OfferType["one_off_order"] = 0] = "one_off_order";
  OfferType[OfferType["blanket_order"] = 1] = "blanket_order";
})(OfferType || (OfferType = {}));

const MOLD_SETUP_SALES_FACTOR = 1.25;
/**
 * Numbers of days considered in a weeks
 * Used to re-calculate deliveryTime from weeks to days and vice versa
 * This is relevant in case we decide to only consider work days (5) instead of all (7)
 */
const DAYS_IN_WEEK = 7;
const productionDelays = {
  [processingMethods.MOLD_INJECTION]: DAYS_IN_WEEK * 2,
  [processingMethods.FAST_LINE]: DAYS_IN_WEEK
};
/**
 * Returns true if the given deliveryTime is of type DeliveryTimeRange
 */
const isDeliveryTimeRange = deliveryTime => {
  return deliveryTime instanceof Object;
};
/**
 * Returns true if the given deliveryTime is of type DeliveryTimeRangeExtended
 */
const isDeliveryTimeRangeExtended = deliveryTime => {
  return deliveryTime instanceof Object && 'levelCode' in deliveryTime;
};
/**
 * Returns true if the given deliveryTime is of type DeliveryTime
 */
const isDeliveryTimeCode = deliveryTime => {
  return typeof deliveryTime === 'number';
};
/**
 * Mapping of the IDD deliveryTime level into the kopla standard notation
 */
const mapTimeRangeIntoTimeLevel = (dt, total) => {
  if (isDeliveryTimeCode(dt)) {
    return dt;
  }
  const deliveryTime = dt ? Object.assign({}, dt) : {};
  if (total < 0) {
    deliveryTime.levelCode = 0;
  } else {
    switch (deliveryTime.level) {
      case 'A':
        deliveryTime.levelCode = 3;
        break;
      case 'B':
        deliveryTime.levelCode = 2;
        break;
      case 'C':
      default:
        deliveryTime.levelCode = 1;
        break;
    }
  }
  deliveryTime.levelText = deliveryTime.min === deliveryTime.max ? 'SINGLE' : 'RANGE';
  return deliveryTime;
};
/**
 * Returns the translation key for the text of a given price BasketConfigUnitPrices
 */
const getDeliveryTimeText = (dt, total, dtText, short = false) => {
  let key;
  let translate = true;
  let params = {};
  if (isDeliveryTimeRangeExtended(dt)) {
    if (total > -1 && dt.min > 0 && dt.max > 0) {
      const unit = dt.type === 'weeks' ? '_WEEKS' : '_DAYS';
      key = `ibd.pdf.DELIVERY_TIME_RANGE_${dt.levelText}${unit}${short ? '_SHORT' : ''}`;
      params = {
        min: dt.min,
        max: dt.max
      };
    } else {
      key = short ? `ibd.pdf.DELIVERY_TIME_0_SHORT` : `kopla.common.DELIVERY_TIME_0`;
    }
  } else if (dtText) {
    key = dtText;
    translate = false;
  } else {
    key = short ? `ibd.pdf.DELIVERY_TIME_${dt}_SHORT` : `kopla.common.DELIVERY_TIME_${dt}`;
  }
  return {
    key,
    params,
    translate
  };
};
const mapDaysToDeliveryTimeCode = deliveryTimeInDays => {
  if (deliveryTimeInDays < 1) {
    return 0;
  } else if (deliveryTimeInDays < DAYS_IN_WEEK) {
    return 3;
  } else if (deliveryTimeInDays < DAYS_IN_WEEK * 2) {
    return 2;
  } else if (deliveryTimeInDays >= DAYS_IN_WEEK * 2) {
    return 1;
  } else {
    return 0;
  }
};
const mapDeliveryTimeCodeToDays = deliveryTimeCode => {
  switch (deliveryTimeCode) {
    case 3:
      return 1;
    case 2:
      return DAYS_IN_WEEK;
    case 1:
      return DAYS_IN_WEEK * 2;
    case 0:
    default:
      return -1;
  }
};
const mapDeliveryTimeToDays = ({
  min,
  max,
  type
}, useAverage = false) => {
  if (min === -1 || max === -1) {
    return -1;
  }
  if (min === undefined || max === undefined) {
    return -1;
  }
  const average = useAverage ? (min + max) / 2 : min;
  if (type === 'weeks') {
    return Math.ceil(average * DAYS_IN_WEEK);
  } else {
    return Math.ceil(average);
  }
};
const addDeliveryTimeDelay = (deliveryTime, delayTime, delayLevel, type = 'days') => {
  if (isDeliveryTimeRange(deliveryTime)) {
    if (deliveryTime.type === undefined || type === deliveryTime.type) ; else if (type === 'days') {
      delayTime = delayTime * DAYS_IN_WEEK;
    } else if (type === 'weeks') {
      delayTime = Math.floor(delayTime / DAYS_IN_WEEK);
    }
    if (delayLevel) {
      deliveryTime.level = delayLevel;
    }
    return Object.assign({}, deliveryTime, {
      min: deliveryTime.min + delayTime,
      max: deliveryTime.max + delayTime
    });
  }
  // deliveryTime is DeliveryTimeCode
  delayTime = type === 'weeks' ? delayTime * DAYS_IN_WEEK : delayTime;
  const days = mapDeliveryTimeCodeToDays(deliveryTime);
  if (days === -1) {
    return -1;
  }
  return {
    level: delayLevel != null ? delayLevel : 'B',
    type: 'days',
    min: days + delayTime,
    max: days + delayTime
  };
};
/**
 * Generate current date and time as string
 */
const getCurrentDateTime = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();
  return `${year}${month}${day}_${hour}${minute}`;
};

const defaultLifetimeSpacialMotion = spatialTypeOfMovements.rotation;
/**
 * Default values for the lifetime calculations
 * Used as default in the IBH {@link article-lifetime-request.dto.ts}
 */
const defaultLifetimeConfig = {
  environment: TechnicalEnvironment.dry,
  enabledBearingWallThickness: false,
  oppositeMaterial: OppositeMaterialIds.cf53,
  enabledPVCalculation: true,
  housingMaterial: HousingMaterialIds.aluminium,
  enabledIntermittentCalculation: false,
  intermittentOnTime: 0,
  intermittentOffTime: 0,
  bearingWallThickness: '',
  force: 20,
  surfacePressure: 1,
  temperature: 23,
  acceptableWear: 0.25,
  activityMinutesPerHour: 60,
  spacialTypeOfMovement: defaultLifetimeSpacialMotion,
  temporalTypeOfMovement: TemporalTypeOfMovement.simultaneous,
  temporalLinearPart: 1,
  linearStrokeLength: 10,
  linearFrequency: 10,
  rotationRevolutions: 10,
  pivotingAngle: 45,
  pivotingFrequency: 1
};

/**
 * joins the strings in an array of arrays by OR
 *
 * the result is a flat array of unique strings which exist in at least one array
 */
const joinStringArraysByOr = arr => {
  return [...new Set(arr.flat())];
};
/**
 * joins the strings in an array of arrays by AND
 *
 * the result is a flat array of unique strings which exist in all arrays
 */
const joinStringArraysByAnd = all => {
  const firstArr = all.shift();
  if (all.length === 0) {
    return firstArr ? firstArr : [];
  }
  const resultInAll = [];
  if (firstArr) {
    for (const strElement of firstArr) {
      const foundInAll = all.map(arr => {
        return arr ? arr.indexOf(strElement) > -1 : false;
      }).reduce((p, c) => {
        return p && c;
      });
      if (foundInAll) {
        resultInAll.push(strElement);
      }
    }
  }
  return resultInAll;
};
const stringArraysEqual = (a, b) => {
  return JSON.stringify(a.sort()) === JSON.stringify(b.sort());
};
const requiredAttrExistInGivenAttr = (required, given) => {
  for (const req of required) {
    if (!(given.indexOf(req) >= 0)) {
      return false;
    }
  }
  return true;
};
const uniqueSortedNumArray = arr => {
  return [...new Set(arr)].sort((a, b) => a - b);
};

/**
 * Round passed value with one digit after the decimal point
 */
const round1 = value => {
  return Math.round(value * 10) / 10;
};
/**
 * Round passed value with two digits after the decimal point
 */
const round2 = value => {
  return Math.round(value * 100) / 100;
};
/**
 * Round passed value with three digits after the decimal point
 */
const round3 = value => {
  return Math.round(value * 1000) / 1000;
};
/**
 * Round passed value with four digits after the decimal point
 */
const round4 = value => {
  return Math.round(value * 10000) / 10000;
};
/**
 * Round passed value with five digits after the decimal point
 */
const round5 = value => {
  return Math.round(value * 100000) / 100000;
};
/**
 * Round passed value with ten digits after the decimal point
 */
const round10 = value => {
  return Math.round(value * 10000000000) / 10000000000;
};
/**
 * Truncate passed value with one digit after the decimal point
 */
const trunc1 = value => {
  return Math.trunc(value * 10) / 10;
};
/**
 * Truncate passed value with two digits after the decimal point
 */
const trunc2 = value => {
  return Math.trunc(value * 100) / 100;
};
/**
 * Truncate passed value with three digits after the decimal point
 */
const trunc3 = value => {
  return Math.trunc(value * 1000) / 1000;
};
/**
 * Truncate passed value with four digits after the decimal point
 */
const trunc4 = value => {
  return Math.trunc(value * 10000) / 10000;
};
/**
 * Truncate passed value with five digits after the decimal point
 */
const trunc5 = value => {
  return Math.trunc(value * 100000) / 100000;
};
/**
 * Truncate passed value with ten digits after the decimal point
 */
const trunc10 = value => {
  return Math.trunc(value * 10000000000) / 10000000000;
};
/**
 * Rounds the value up after the 2.th decimal.
 *
 * Special use for wallthickness comparison.
 */
const ceil2 = v => {
  return Math.ceil(v * 100) / 100;
};
/**
 * Rounds the inch value up after the 3.rd decimal.
 *
 * Special use for wallthickness comparison.
 */
const ceil3 = v => {
  return Math.ceil(v * 1000) / 1000;
};
/**
 * Rounds the inch value up after the 4.th decimal.
 *
 * To shape 0.37499991675 back to 0.375
 */
const ceil4 = v => {
  return Math.ceil(v * 10000) / 10000;
};
/**
 * Rounds the value down after the 2.th decimal.
 *
 * Special use for wallthickness comparison.
 */
const floor2 = v => {
  return Math.floor(v * 100) / 100;
};
/**
 * Rounds the value down after the 4.th decimal.
 *
 * Special use for wallthickness comparison.
 */
const floor4 = v => {
  return Math.floor(v * 10000) / 10000;
};

const allowedComparators = ['<=', '==', '>=', '>', '<'];
const regexComparators = new RegExp(allowedComparators.join('|'), '');
const regexOparators = new RegExp('\\+|-|\\*|\\/', '');
const regexpFloat = /[+-]?\d+(?:\.\d+)?/;
const regexpFloatWithParanthesis = /\(([+-]?\d+(?:\.\d+)?)\)/;
const reComp = new RegExp('^(.*?)(' + regexComparators.source + ')(.*?)$');
const reFloatOpFloat = new RegExp('(' + regexpFloat.source + ')(' + regexOparators.source + ')(' + regexpFloat.source + ')');
const rePaFloatOpFloatPa = new RegExp('\\(' + reFloatOpFloat.source + '\\)');
/**
 * cleanTerm() removes whitespace and replaces possible ',' by '.' as the decimal delimiter.
 */
const cleanTerm = term => {
  return term.replace(/\s?/g, '').replace(/,/g, '.');
};
const replaceParentheses = term => {
  const cterm = cleanTerm(term);
  // single value in parentheses
  const r0MatchResult = cterm.match(regexpFloatWithParanthesis);
  if (r0MatchResult) {
    if (r0MatchResult[0] === cterm) {
      // there is nothing more in the term
      return r0MatchResult[1];
    }
  }
  // simple operation in parentheses
  const r1MatchResult = cterm.match(rePaFloatOpFloatPa);
  if (r1MatchResult) {
    if (r1MatchResult[0] === cterm) {
      // there is nothing more in the term
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return replaceOperation(r1MatchResult[1] + r1MatchResult[2] + r1MatchResult[3]);
    }
  }
  // more complex terms
  let newTerm = cterm;
  const r2 = new RegExp(rePaFloatOpFloatPa);
  const r2MatchResult = newTerm.match(r2);
  if (r2MatchResult) {
    const elem = r2MatchResult[1] + r2MatchResult[2] + r2MatchResult[3];
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const simplifiedElem = replaceOperation(elem);
    newTerm = newTerm.replace(r2MatchResult[0], simplifiedElem);
  }
  if (newTerm.length < cterm.length) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const r3 = simplifyTerm(newTerm);
    return r3;
  }
  return cterm;
};
const replaceOperation = term => {
  const cterm = cleanTerm(term);
  let newTerm = cterm;
  let newValue = '';
  const r1MatchResult = newTerm.match(reFloatOpFloat);
  if (r1MatchResult) {
    const left = parseFloat(r1MatchResult[1]);
    const op = r1MatchResult[2];
    const right = parseFloat(r1MatchResult[3]);
    switch (op) {
      case '+':
        newValue = left + right + '';
        break;
      case '-':
        newValue = left - right + '';
        break;
      case '*':
        newValue = left * right + '';
        break;
      case '/':
        if (right === 0) {
          newValue = 'inf';
        } else {
          newValue = left / right + '';
        }
        break;
    }
    if (r1MatchResult[0] === cterm) {
      // there is nothing more in the term
      return newValue;
    } else {
      // only replace the operation by its value and continue
      newTerm = newTerm.replace(r1MatchResult[0], newValue);
    }
  }
  // there was more inside, so handle that too
  if (newTerm.length < cterm.length) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return simplifyTerm(newTerm);
  }
  return cterm;
};
/**
 * simplifyTerm() does two things
 *
 * In a first step, it tries to replace parenthesis pairs (non-greedy) and replaces them by their result.
 * In the second step, it tries to replace one simple calculation (with the allowed operands)
 *
 * If the string was changed by one of these steps, simplifyTerm() is run again on the result.
 *
 * IMPORTANT: Operator-precedence is not implemented - use parentheses!
 */
const simplifyTerm = term => {
  return replaceOperation(replaceParentheses(cleanTerm(term)));
};
/**
 * evaluateTermBoolean evaluates a boolean term and returns the result.
 * So usually true or false is returned.
 *
 * If the term does not match the expectation, then undefined is returned to show that the term is invalid.
 *
 * evaluateTermBoolean does not support variables, so they must be replaced before.
 * evaluateTermBoolean does not support operator precedence, use parentheses!
 */
const evaluateTermBoolean = term => {
  const cTerm = cleanTerm(term);
  const rfMatchResult = cTerm.match(reComp);
  if (rfMatchResult) {
    const left = parseFloat(simplifyTerm(rfMatchResult[1]));
    const comp = rfMatchResult[2];
    const right = parseFloat(simplifyTerm(rfMatchResult[3]));
    switch (comp) {
      case '<':
        return left < right;
      case '<=':
        return left <= right;
      case '==':
        return left === right;
      case '>':
        return left > right;
      case '>=':
        return left >= right;
    }
  }
  return undefined;
};

/**
 * https://mathworld.wolfram.com/LeastSquaresFittingPowerLaw.html
 *
 *  y = a * x ^ b
 *
 *      n *  ∑ni=1(ln xi * ln yi) - ∑ni=1(ln xi) * ∑ni=1(ln yi)
 *  b = -------------------------------------------------------
 *      n * ∑ni=1(ln xi)^2 -  (∑ni=1(ln xi))^2
 *
 *       ∑ni=1(ln yi) - b * ∑ni=1(ln xi)
 *  a_ = --------------------------------
 *                   n
 *  a = e ^ a_
 *
 * restructuring the equation to x
 *
 *                  1
 *                 ---
 *                  b
 *  x = (y / a )
 */
const calcPowerRegressionLeastSquaresParameters = points => {
  if (points.length < 2) {
    throw new Error('function requires at least two points');
  }
  const log = Math.log;
  const n = points.length;
  const sumLogX = points.map(p => log(p.x)).reduce((p, c) => p + c);
  const sumLogY = points.map(p => log(p.y)).reduce((p, c) => p + c);
  const sumXX = points.map(p => log(p.x) * log(p.x)).reduce((p, c) => p + c);
  const sumXY = points.map(p => log(p.x) * log(p.y)).reduce((p, c) => p + c);
  const bNumerator = n * sumXY - sumLogX * sumLogY;
  const bDenominator = n * sumXX - sumLogX * sumLogX;
  const b = bNumerator / bDenominator;
  const aNumerator = sumLogY - b * sumLogX;
  const aDenominator = n;
  const a_ = aNumerator / aDenominator;
  const a = Math.exp(a_);
  return {
    a: round4$1(a),
    b: round4$1(b)
  };
};

/*
 * createLinearFn
 *
 * creates a linear function of the form
 *
 *     y = m * x + b
 *
 * result is rounded to 4 digits
 *
 */
const createLinearFn = (m, b) => {
  return x => round4$1(m * x + b);
};
/*
 * createPowerFn
 *
 * creates a power function of the form
 *
 *     y = a * x^b
 *
 * where c is optional (0 by default),
 * result is rounded to 4 digits
 *
 */
const createPowerFn = (a, b, c = 0) => {
  return x => round4$1(a * Math.pow(x, b) + c);
};
/*
 * createSigmoidFn
 *
 * creates a sigmoid function of the form
 *
 *
 *                a
 *     y = ------------- + d
 *         1 + e^(b*x+c)
 *
 *
 * where c and d are optional (0 by default),
 * c is kind of x-shift (where neg. c move the curve to pos. x)
 * d shifts y
 * result is rounded to 4 digits
 *
 */
const createSigmoidFn = (a, b, c = 0, d = 0) => {
  return x => round4$1(a / (1 + Math.exp(b * x + c)) + d);
};
/*
 * createLogLinearFn
 *
 * creates a function that appears linear when the x-axis is logarithmic in of the form
 *
 *     y = m * log10(x) + b
 *
 * result is rounded to 4 digits
 *
 */
const createLogLinearFn = (m, b) => {
  return x => round4$1(m * Math.log10(x) + b);
};

/**
 * Returns the passed value as a string where a possible decimal point is removed.
 *
 * This behavior is copied from the excel-tool. It has known issues, but the target
 * is to use this less in the future.
 */
const parameterToPartNoElement = (parameter, size = 1) => {
  if (parameter < 0) {
    throw new Error('Parameter must be positive.');
  }
  if (parameter > 1 && parameter < 10) {
    parameter = Math.trunc(parameter);
  }
  const parameterAsString = parameter.toString().replace(/[.]/i, '');
  return padStart(parameterAsString, size, '0');
};
/**
 * Return an array index for a cavity count.
 *
 * This is often used when different values for cavityCounts are stored in an array.
 *
 * e.g.
 *
 *  const i = cavityCountToArrayIndex(cavityCount);
 *  internalMoldSetupCost = [1450, 1800, 2450, 3100, 4050][i];
 */
const cavityCountToArrayIndex = cavityCount => {
  const cavityCounts = [1, 2, 4, 6, 8];
  const index = cavityCounts.lastIndexOf(cavityCount);
  if (index === -1) {
    throw new Error(`Invalid cavity count ${cavityCount}!`);
  }
  return index;
};
/**
 * Calculates if a userPartValue is in the range
 *
 *    catalogPartValue   < userPartValue  <= catalogPartValue + (userPartValue * percentageRange)
 */
const isInLowerRange = (userPartValue, catalogPartValue, percentageRange) => {
  return catalogPartValue < userPartValue && userPartValue <= catalogPartValue + userPartValue * percentageRange;
};
/**
 * Calculates if a userPartValue is in the range
 *
 *    catalogPartValue   > userPartValue  >= catalogPartValue - (userPartValue * percentageRange)
 */
const isInHigherRange = (userPartValue, catalogPartValue, percentageRange) => {
  return catalogPartValue > userPartValue && userPartValue >= catalogPartValue - userPartValue * percentageRange;
};
/**
 * Type-Guard to check if config is a BearingParams
 */
const isBearingParams = config => {
  return config['partNo'] !== undefined;
};
/**
 * Type-Guard to check if config is a InjectionMoldParams
 */
const isInjectionMoldParams = config => {
  return config['partMass'] !== undefined;
};
/**
 * Type-Guard to check if offerConditions is a OneOffOrder
 */
const isOneOffOrder = offerConditions => {
  return offerConditions.offerType === OfferType.one_off_order;
};
/**
 * Type-Guard to check if offerConditions is a BlanketOrder
 */
const isBlanketOrder = offerConditions => {
  return offerConditions.offerType === OfferType.blanket_order;
};
/**
 * Calculate how many years the blanket order will run, depending on the requested partsQuantity
 */
const getBlanketOrderRuntimeInYears = (blanketOrder, partsQuantity) => {
  if (blanketOrder.demandPerYear === 0) {
    throw new Error('Demand per year must be >0!');
  }
  return Math.ceil(partsQuantity / blanketOrder.demandPerYear);
};
/**
 * Calculates the lot size for a blanket order
 */
const getBlanketOrderLotSize = (demandPerYear, lotSize) => {
  return Math.ceil((demandPerYear + lotSize) / 2);
};

var Units;
(function (Units) {
  Units["angle"] = "angle";
  Units["areaMilli"] = "areaMilli";
  Units["area"] = "area";
  Units["distanceMilli"] = "distanceMilli";
  Units["distance"] = "distance";
  Units["force"] = "force";
  Units["forceKilo"] = "forceKilo";
  Units["mass"] = "mass";
  Units["massKilo"] = "massKilo";
  Units["pressure"] = "pressure";
  Units["pressureMega"] = "pressureMega";
  Units["pressureVelocity"] = "pressureVelocity";
  Units["temperature"] = "temperature";
  Units["thermalConductivity"] = "thermalConductivity";
  Units["time"] = "time";
  Units["velocity"] = "velocity";
})(Units || (Units = {}));
const unitsConversionMap = {
  angle: {
    from: fromMetricAngle,
    to: toMetricAngle,
    symbol: getLocalizationKeyForAngleSymbol
  },
  areaMilli: {
    from: fromMetricAreaMilli,
    to: toMetricAreaMilli,
    symbol: getLocalizationKeyForAreaMilliSymbol
  },
  area: {
    from: fromMetricArea,
    to: toMetricArea,
    symbol: getLocalizationKeyForAreaSymbol
  },
  distanceMilli: {
    from: fromMetricDistanceMilli,
    to: toMetricDistanceMilli,
    symbol: getLocalizationKeyForDistanceMilliSymbol
  },
  distance: {
    from: fromMetricDistance,
    to: toMetricDistance,
    symbol: getLocalizationKeyForDistanceSymbol
  },
  force: {
    from: fromMetricForce,
    to: toMetricForce,
    symbol: getLocalizationKeyForForceSymbol
  },
  forceKilo: {
    from: fromMetricForceKilo,
    to: toMetricForceKilo,
    symbol: getLocalizationKeyForForceKiloSymbol
  },
  mass: {
    from: fromMetricMass,
    to: toMetricMass,
    symbol: getLocalizationKeyForMassSymbol
  },
  massKilo: {
    from: fromMetricMassKilo,
    to: toMetricMassKilo,
    symbol: getLocalizationKeyForMassKiloSymbol
  },
  pressure: {
    from: fromMetricPressure,
    to: toMetricPressure,
    symbol: getLocalizationKeyForPressureSymbol
  },
  pressureMega: {
    from: fromMetricPressureMega,
    to: toMetricPressureMega,
    symbol: getLocalizationKeyForPressureMegaSymbol
  },
  pressureVelocity: {
    from: fromMetricPressureVelocity,
    to: toMetricPressureVelocity,
    symbol: getLocalizationKeyForPressureVelocitySymbol
  },
  temperature: {
    from: fromMetricTemperature,
    to: toMetricTemperature,
    symbol: getLocalizationKeyForTemperatureSymbol
  },
  thermalConductivity: {
    from: fromMetricThermalConductivity,
    to: toMetricThermalConductivity,
    symbol: getLocalizationKeyForThermalConductivity
  },
  time: {
    from: fromMetricTime,
    to: toMetricTime,
    symbol: getLocalizationKeyForTimeSymbol
  },
  velocity: {
    from: fromMetricVelocity,
    to: toMetricVelocity,
    symbol: getLocalizationKeyForVelocitySymbol
  }
};

/**
 * Returns the character for the UnitSystem.
 */
const unitSystemCharacter = unitSystem => {
  if (unitSystem === UnitSystem.metric) {
    return 'M';
  } else if (unitSystem === UnitSystem.imperial) {
    return 'I';
  } else {
    throw new Error(`Invalid UnitSystem ${unitSystem} in unitSystemCharacter!`);
  }
};
/**
 * Returns 1 for metric and 16 for imperial.
 */
const getUnitSystemFactor = userPart => {
  return userPart.unitSystem === UnitSystem.metric ? 1 : 16;
};

const imperialCountries = ['CA', 'US', 'MX'];
const isImperialCountry = country => imperialCountries.includes(country.toUpperCase());
/**
 * Returns the complete Material object for the given materialCode in the config.
 */
const getConfiguredMaterial = (config, allMaterials) => {
  const materialCode = config.materialCode;
  const foundMaterial = allMaterials.find(material => material.materialCode === materialCode);
  if (foundMaterial === undefined) {
    throw new Error('Material not found for material code "' + materialCode + '" in getConfiguredMaterial!');
  }
  return foundMaterial;
};
/**
 * Returns true if a material a custom material (user added).
 */
const isUserMaterial = material => {
  return !!material.isCustom;
};
/**
 * Checks if a material can be used for a piston ring in a ejector principal mold form where
 * shrinkage has to be >= 2 %
 *
 * FIXME: is this still used?
 */
const isValidShrinkageForPistonRingWithEjector = material => {
  return material.shrinkage >= 1.02;
};
/**
 * This function returns the equivalent MaterialCode for a LowCostMaterialCode. If
 * the passed materialCode is not a LowCostMaterialCode undefined is returned.
 *
 * see IBD-365
 */
const getMaterialCodeForLowCostMaterialCode = materialCode => {
  switch (materialCode) {
    case 'GLW':
      return 'G';
    case 'J4':
      return 'J';
    case 'P230':
      return 'P';
    case 'P4':
      return 'P';
    case 'HLW':
      return 'H1';
    case 'ALW350':
      return 'A350';
    case 'PLW210':
      return 'P210';
  }
  return undefined;
};
/**
 * Returns true if a material is a lowCostMaterial.
 *
 * see IBD-365
 */
const isLowCostMaterial = material => {
  // custom material can not be lowCost
  if (typeof material.isCustom !== 'undefined' && material.isCustom) {
    return false;
  }
  return !!getMaterialCodeForLowCostMaterialCode(material.materialCode);
};
/***
 * Returns the list of materials that are allowed for 3D printing as defined in IBD-512.
 * density is in g/cm^3.
 *
 * Requested in IBD-519, IGL-654, IGL-1092
 */
const getMaterialsFor3D = () => {
  return [{
    materialCode: 'I3-PL',
    materialDisplayName: 'iglidur® i3-PL',
    processingMethod: Procedure.SLS,
    partNo: 'I3-PS-02',
    density: 1.05,
    toleranceClass: {
      d1: 'E12',
      b1: 'h13'
    }
  }, {
    materialCode: 'I6-PL',
    materialDisplayName: 'iglidur® i6-PL',
    processingMethod: Procedure.SLS,
    partNo: 'I6-PS-02',
    density: 1.06,
    toleranceClass: {
      d1: 'E12',
      b1: 'h13'
    }
  }, {
    materialCode: 'I8-ESD',
    materialDisplayName: 'iglidur® i8-ESD',
    processingMethod: Procedure.SLS,
    partNo: 'I8-ESD',
    density: 1.03,
    toleranceClass: {
      d1: 'E13',
      b1: 'h13'
    }
  }, {
    materialCode: 'I180-PF',
    materialDisplayName: 'iglidur® i180-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I180-PS-01',
    density: 1.21,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I180-BL-PF',
    materialDisplayName: 'iglidur® i180-BL-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I180-BL-PS-01',
    density: 1.21,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I150-BL-PF',
    materialDisplayName: 'iglidur® i150-BL-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I150-BL-PS-01',
    density: 1.3,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I150-PF',
    materialDisplayName: 'iglidur® i150-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I150-PS-01',
    density: 1.3,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I151-PF',
    materialDisplayName: 'iglidur® i151-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I151-PS-01',
    density: 1.42,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'J260-PF',
    materialDisplayName: 'iglidur® J260-PF',
    processingMethod: Procedure.FDM,
    partNo: 'J260-PS-01',
    density: 1.35,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I170-PF',
    materialDisplayName: 'iglidur® i170-PF',
    processingMethod: Procedure.FDM,
    partNo: 'I170-PS-01',
    density: 1.21,
    toleranceClass: {
      d1: 'E14',
      b1: 'h14'
    }
  }, {
    materialCode: 'I3000-PR',
    materialDisplayName: 'iglidur® i3000',
    processingMethod: Procedure.DLP,
    partNo: 'I3000-PS-06',
    density: 1.3,
    toleranceClass: {
      d1: 'E11',
      b1: 'h12'
    }
  },
  // Disabled because of IGL-4029
  {
    materialCode: 'I4000-PR',
    materialDisplayName: 'iglidur® i4000',
    processingMethod: Procedure.DLP,
    partNo: 'I4000-PS-06',
    density: 1.27,
    toleranceClass: {
      d1: 'E11',
      b1: 'h12'
    }
  }];
};
/**
 * Returns the list of materialCodes that are allowed semi-finished production method IBD-819.
 */
const getMaterialsForSemiFinished = () => {
  return ['A160', 'A180', 'A181', 'A350', 'A500', 'C500', 'F2', 'GLW', 'H1', 'HSD350', 'J', 'J2', 'J3', 'J4', 'J200', 'J260', 'J350', 'JB', 'K230', 'M250', 'P210', 'Q2', 'R', 'RN44', 'RW370', 'T220', 'UW160', 'W300', 'X'];
};
/**
 * Some materials need a thicker wall to be produced in FastLine.
 *
 * introduced for IGL-823
 */
const getFastLineLimitedWallthicknessMaterials = () => {
  return ['J350', 'X'];
};
const getWoundMaterialFilters = form => {
  if (form === undefined || ['S', 'F', 'T'].includes(form)) {
    return materialsWoundS;
  } else {
    return [];
  }
};
/**
 * returns the filters for the different mold injection forms
 */
const getMoldInjectionMaterialFilters = (isPmFastLine, form) => {
  if (!form) {
    return undefined;
  }
  switch (form) {
    case 'S':
    case 'F':
    case 'T':
      return isPmFastLine ? materialsFastLine : undefined;
    case 'EC':
      return isPmFastLine ? materialsEasyClipFastLine : materialsEasyClip;
    case 'C':
      return isPmFastLine ? materialsCFastLine : materialsC;
    case 'PR':
      return isPmFastLine ? materialsPistonRingFastLine : materialsPistonRing;
    case 'RL':
      return isPmFastLine ? materialsRLFastLine : materialsRL;
    case 'FL':
      return isPmFastLine ? materialsFLFastLine : materialsFL;
    case 'VFL':
      return isPmFastLine ? materialsVFLFastLine : materialsVFL;
    case 'Y':
      return isPmFastLine ? materialsYFastLine : materialsY;
    case 'YK':
      return isPmFastLine ? materialsYKFastLine : materialsYK;
    case 'TE':
      return isPmFastLine ? materialsTEFastLine : materialsTE;
    case 'D':
      return isPmFastLine ? materialsDFastLine : materialsD;
    case 'VF':
      return isPmFastLine ? materialsVFFastLine : materialsVF;
    case 'K':
      return isPmFastLine ? materialsKFastLine : materialsK;
    case 'VS':
      return isPmFastLine ? materialsVSFastLine : materialsVS;
    case 'FSG':
      return isPmFastLine ? materialsFSGFastLine : materialsFSG;
  }
};
/**
 * returns the defaults for the different mold injection forms
 *
 * @param isPmFastLine boolean
 * @param form         aka shape
 */
const getMoldInjectionMaterialDefaults = (isPmFastLine, form) => {
  const filters = getMoldInjectionMaterialFilters(isPmFastLine, form);
  if (filters) {
    if (filters.indexOf('G') !== -1) {
      return 'G';
    } else if (filters.indexOf('J') !== -1) {
      return 'J';
    } else {
      return filters[0];
    }
  } else {
    return 'G';
  }
};
/**
 * get MaterialDisplayName from MaterialCode
 *
 * prefix MaterialCode with iglidur if none of (iglidur|igumid|xirodur|igutex) is contained
 * add Registered-Trade-Mark character ® if not yet contained
 */
const getMaterialDisplayName = (materialCode, countryCode = 'DE') => {
  let displayName;
  const r = /^(iglidur|igumid|xirodur)\s+(.*)$/i.exec(materialCode);
  if (r) {
    displayName = r[1] + '® ' + r[2];
  } else {
    displayName = `${materialTradeMarks.iglidur} ${materialCode}`;
  }
  if (igutexMaterials.includes(materialCode)) {
    displayName = `${materialTradeMarks.igutex} ${materialCode}`;
  }
  if (isImperialCountry(countryCode)) {
    displayName = displayName.replace(materialTradeMarks.iglidur, materialTradeMarks.iglide);
  }
  return displayName;
};
/**
 * Applies country specific filtering rules, then sets the materialDisplayName for each material
 */
const filterMaterialsByCountry = (allMaterials, countryCode = 'DE', context = Context.IBD) => {
  const isMaterialAvailabilityIndex = materialCode => materialAvailabilityIndex.includes(materialCode);
  return allMaterials.filter(material => isMaterialAvailabilityIndex(material.materialCode) ? materialAvailabilityMap[material.materialCode](countryCode, context) : true).map(material => Object.assign({}, material, {
    materialDisplayName: getMaterialDisplayName(material.materialCode, countryCode)
  }));
};
/**
 * combineMaterials() joins two arrays of materials. The first is the ibd-style Material and the second the
 * one from kopla (gears).
 * The kopla material takes priority over the ibd material. If a kopla material is not found, the ibd material is used.
 */
const combineMaterials = (ibdMaterials, koplaMaterials) => {
  return ibdMaterials.map(ibdMaterial => {
    const koplaMaterial = koplaMaterials.find(e => {
      return e.id === ibdMaterial.materialCode;
    });
    let result;
    if (typeof koplaMaterial === 'undefined') {
      result = ibdMaterial;
    } else {
      result = Object.assign({}, ibdMaterial, koplaMaterial);
    }
    return result;
  });
};
/**
 * Joins two arrays of materials.
 * For common materials,enriches the koplaMaterial with ibdMaterial properties
 */
const joinMaterials = (ibdMaterials, koplaMaterials) => {
  ibdMaterials.forEach(ibdMaterial => {
    koplaMaterials = koplaMaterials.map(koplaMaterial => ibdMaterial.materialCode === koplaMaterial.id ? Object.assign({}, ibdMaterial, koplaMaterial) : koplaMaterial);
  });
  return [...ibdMaterials.filter(ibdMaterial => !koplaMaterials.find(koplaMaterial => ibdMaterial.materialCode === koplaMaterial.id)), ...koplaMaterials];
};
/**
 * getMaterialFiltersSinglePm returns a MaterialFilters object that contains a list of allowed and a list of forbidden
 * materials for the given form (shape) and (single) processing method (production method).
 *
 * If allowed is undefined it means that there is no setting, so all are allowed.
 * If allowed is set then only the entries in the list are allowed to be used.
 * So an empty list means that this is not producible.
 *
 * If forbidden is undefined it means that there is no setting, and so there are no forbidden materials.
 * If forbidden is set then these materials are not allowed to be used.
 */
const getMaterialFiltersSinglePm = (processingMethod, form) => {
  let allowed;
  let forbidden = partsMaterialsDisabled;
  if (form) {
    switch (form) {
      case 'S':
      case 'F':
      case 'T':
        forbidden = forbidden.concat(materialsSFTDisabled);
        break;
    }
  }
  switch (processingMethod) {
    case processingMethodsIbh.MECHANICAL_REPROCESSING:
      // every material is allowed (that exists in the catalog) for S,F,T,RL
      if (form) {
        if (form === 'S' || form === 'F' || form === 'T' || form === 'RL') ; else {
          allowed = [];
        }
      }
      break;
    case processingMethodsIbh.MOLD_INJECTION:
      allowed = getMoldInjectionMaterialFilters(false, form);
      break;
    case processingMethodsIbh.FAST_LINE:
      allowed = getMoldInjectionMaterialFilters(true, form);
      break;
    case processingMethodsIbh.THREE_DIMENSIONAL_PRINT:
      if (form) {
        if (form === 'S' || form === 'F' || form === 'T' || form === 'RL' || form === 'PR' || form === 'C' || form === 'EC') {
          allowed = getMaterialsFor3D().map(mat => mat.materialCode);
        } else {
          allowed = [];
        }
      } else {
        // FIXME: this is the list for IBD
        //        for IBH we need to filter on the existing materials later on
        allowed = getMaterialsFor3D().map(mat => mat.materialCode);
      }
      break;
    case processingMethodsIbh.SEMI_FINISHED:
      allowed = undefined;
      break;
    case processingMethodsIbh.WOUND:
      // every material allowed (that exists in the catalog)
      allowed = getWoundMaterialFilters(form);
      break;
    default:
      allowed = [];
  }
  return {
    allowed,
    forbidden
  };
};
/**
 * getMaterialFiltersSinglePm returns a MaterialFilters object that contains a list of allowed and a list of forbidden
 * materials for the given form (shape) and (multi) processing method (production method).
 *
 * If allowed is undefined it means that there is no setting, so all are allowed.
 * If allowed is set then only the entries in the list are allowed to be used.
 * So an empty list means that this is not producible.
 *
 * If forbidden is undefined it means that there is no setting, and so there are no forbidden materials.
 * If forbidden is set then these materials are not allowed to be used.
 */
const getMaterialFiltersMultiPm = (materialProcessingMethods, form) => {
  if (!materialProcessingMethods || materialProcessingMethods.length === 0) {
    return {
      allowed: [],
      forbidden: undefined
    };
  }
  const materialFilters = (materialProcessingMethods == null ? void 0 : materialProcessingMethods.map(pm => getMaterialFiltersSinglePm(pm, form))) || [];
  // combine allowed by OR
  let oneOfAllowedIsUndefined = false;
  const allowed = materialFilters.map(mf => {
    if (typeof mf.allowed === 'undefined') {
      oneOfAllowedIsUndefined = true;
    }
    return mf.allowed ? mf.allowed : [];
  });
  // at least for one material there is no limitation in the allowed list, so the result is that allowed is undefined
  const allowedJoined = oneOfAllowedIsUndefined ? undefined : joinStringArraysByOr(allowed);
  // combine forbidden by AND
  const forbidden = materialFilters.map(mf => mf.forbidden ? mf.forbidden : []);
  const forbiddenJoined = joinStringArraysByAnd(forbidden);
  return {
    allowed: allowedJoined,
    forbidden: forbiddenJoined
  };
};
/**
 * getMaterialFilters returns a MaterialFilters object that contains a list of allowed and a list of forbidden
 * materials for the given form (shape) and processing method (production method).
 *
 * If allowed is undefined it means that there is no setting, so all are allowed.
 * If allowed is set then only the entries in the list are allowed to be used.
 * So an empty list means that this is not producible.
 *
 * If forbidden is undefined it means that there is no setting, and so there are no forbidden materials.
 * If forbidden is set then these materials are not allowed to be used.
 */
const getMaterialFilters = (form, processingMethod) => {
  if (!processingMethod) {
    return getMaterialFiltersMultiPm([...processingMethodsAsArray], form);
  } else if (Array.isArray(processingMethod)) {
    if (processingMethod.length === 1) {
      return getMaterialFiltersSinglePm(processingMethod[0], form);
    } else {
      return getMaterialFiltersMultiPm(processingMethod, form);
    }
  } else {
    return getMaterialFiltersSinglePm(processingMethod, form);
  }
};
const isMaterialSuitableByProcessingMethod = (materialProcessingMethod, requestedProcessingMethod) => {
  if (materialProcessingMethod === processingMethods.THREE_DIMENSIONAL_PRINT && requestedProcessingMethod === processingMethodsIbh.THREE_DIMENSIONAL_PRINT) {
    return true;
  }
  if (materialProcessingMethod === processingMethodsIbh.WOUND && requestedProcessingMethod === processingMethodsIbh.MECHANICAL_REPROCESSING) {
    return true;
  }
  return materialProcessingMethod === processingMethods.MOLD_INJECTION;
};
/**
 * Determine if a materialCode is allowed to be used for the given form and processingMethod.
 */
const isMaterialAllowed = (materialCode, materialProcessingMethod, materialCavityIndex, form, requestedProcessingMethod) => {
  var _materialFilters$allo, _materialFilters$forb;
  if (!isMaterialSuitableByProcessingMethod(materialProcessingMethod, requestedProcessingMethod)) {
    return false;
  }
  if (requestedProcessingMethod !== processingMethodsIbh.THREE_DIMENSIONAL_PRINT && requestedProcessingMethod !== processingMethodsIbh.MECHANICAL_REPROCESSING && typeof materialCavityIndex !== 'number') {
    // Those  processing Methods do not rely on the cavity count index to calculate their prices
    return false;
  }
  const materialFilters = getMaterialFiltersSinglePm(requestedProcessingMethod, form);
  let allowed = false;
  if (!materialFilters.allowed) {
    // allowed list is not defined => all are allowed
    allowed = true;
  } else if (materialFilters.allowed.indexOf(materialCode) > -1) {
    allowed = true;
  }
  if (((_materialFilters$allo = materialFilters.allowed) == null ? void 0 : _materialFilters$allo.length) === 0) {
    return false;
  }
  let forbidden = false;
  if (!materialFilters.forbidden) {
    // forbidden list is not defined => none are forbidden
    forbidden = false;
  } else if (materialFilters.forbidden.indexOf(materialCode) > -1) {
    forbidden = true;
  }
  if (((_materialFilters$forb = materialFilters.forbidden) == null ? void 0 : _materialFilters$forb.length) === 0) {
    forbidden = false;
  }
  return allowed && !forbidden;
};
const getPrecision = (material, procedure) => {
  const precisions = methodCharacteristicsMap[procedure].precision;
  return precisions[material] || precisions.default;
};

const metricMockCatalogParts = [{
  partNo: 'GSM-0103-01-fake',
  form: 'S',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 1.5,
  d2: 3.0,
  b1: 1.9
}, {
  partNo: 'GSM-0103-02',
  form: 'S',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 1.5,
  d2: 3.0,
  b1: 2.0
}, {
  partNo: 'GSM-0607-06',
  form: 'S',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 6,
  d2: 7.0,
  b1: 6.0
}, {
  partNo: 'GFM-0103-01-fake',
  form: 'F',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 3.0,
  d2: 4.5,
  d3: 7.5,
  b1: 1.8,
  b2: 0.5
}, {
  partNo: 'GFM-0103-02',
  form: 'F',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 3.0,
  d2: 4.5,
  d3: 7.5,
  b1: 2.0,
  b2: 0.5
}, {
  partNo: 'GFM-0607-024',
  form: 'F',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 6.0,
  d2: 7,
  d3: 11,
  b1: 2.4,
  b2: 0.5
}, {
  partNo: 'GTM-0408-005',
  form: 'T',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  d1: 4.0,
  d2: 8,
  b1: 0.5
}, {
  partNo: 'GTM-0611-009-fake',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  form: 'T',
  d1: 6.0,
  d2: 11,
  b1: 0.9
}, {
  partNo: 'GTM-0611-010',
  materialCode: 'G',
  unitSystem: UnitSystem.metric,
  form: 'T',
  d1: 6.0,
  d2: 11,
  b1: 1
}];
const imperialMockCatalogParts = [{
  partNo: 'GSI-0203-03',
  form: 'S',
  materialCode: 'G',
  unitSystem: UnitSystem.imperial,
  d1: 3.175,
  // 1/8"
  d2: 4.7625,
  // 3/16"
  b1: 4.7625 // 3/16"
}, {
  partNo: 'GSI-0405-06',
  form: 'S',
  materialCode: 'G',
  unitSystem: UnitSystem.imperial,
  d1: 6.35,
  // 1/4"
  d2: 7.9375,
  // 5/16"
  b1: 9.525 // 3/8"
}, {
  partNo: 'GFI-0405-06',
  form: 'F',
  materialCode: 'G',
  unitSystem: UnitSystem.imperial,
  d1: 6.35,
  // 1/4"
  d2: 7.9375,
  // 5/16"
  d3: 9.525,
  // 3/8"
  b1: 9.525,
  // 3/8"
  b2: 0.8128
}];

const materialJ = {
  materialCode: 'J',
  density: 1.49,
  price: 11.76,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0195,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
};
const materialG = {
  materialCode: 'G',
  density: 1.46,
  price: 10.23,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0075,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
};
const materialE7 = {
  materialCode: 'E7',
  density: 1.05,
  price: 9.88,
  currency: 'EUR',
  cavityIndex: 2,
  heatedMold: false,
  shrinkage: 1.029,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
};
const materialA160 = {
  materialCode: 'A160',
  density: 1.04,
  price: 4.14,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: false,
  shrinkage: 1.021,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
};
const materialA180 = {
  materialCode: 'A180',
  density: 1.46,
  price: 9.96,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.025,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
};
/**
 * This is a mock material database only to be used for tests!
 * Real materials live in the database available via data-service.
 *
 * density [kg/dm^3] = [g/cm^3]
 * price   [EUR/kg]
 *
 * ```
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_3_1.xlsm
 * Page       : Materialdatenbank
 * ```
 */
const allMockMaterials = [{
  materialCode: 'A160',
  density: 1.04,
  price: 4.14,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: false,
  shrinkage: 1.021,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A180',
  density: 1.46,
  price: 9.96,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.025,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A181',
  density: 1.38,
  price: 9.07,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: false,
  shrinkage: 1.026,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A200',
  density: 1.14,
  price: 4.91,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0225,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A290',
  density: 1.39,
  price: 25.01,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: true,
  shrinkage: 1.008,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A350',
  density: 1.42,
  price: 27,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: true,
  shrinkage: 1.0115,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'A500',
  density: 1.28,
  price: 92.03,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: true,
  shrinkage: 1.015,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'AB',
  density: 1.11,
  price: 35.2,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: false,
  shrinkage: 1.012,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'ALW350',
  density: 1.42,
  price: 12.19,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: true,
  shrinkage: 1.0115,
  customerMaterial: false,
  subsidiaryMaterial: false,
  optionTemperingPreselected: false
}, {
  materialCode: 'B',
  density: 1.15,
  price: 13.84,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: false,
  shrinkage: 1.013,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'B180',
  density: 1.41,
  price: 3.95,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.014,
  customerMaterial: false,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'C',
  density: 1.1,
  price: 5.05,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: false,
  shrinkage: 1.0165,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'C210',
  density: 1.4,
  price: 14.5,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: false,
  shrinkage: 1.005,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'C500',
  density: 1.37,
  price: 90.96,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: true,
  shrinkage: 1.0185,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'D',
  density: 1.41,
  price: 5.63,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0225,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'D180',
  density: 1.2,
  price: 7.65,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: false,
  shrinkage: 1.014,
  customerMaterial: false,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'E7',
  density: 1.05,
  price: 9.88,
  currency: 'EUR',
  cavityIndex: 2,
  heatedMold: false,
  shrinkage: 1.029,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'F',
  density: 1.25,
  price: 17.8,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: true,
  shrinkage: 1.009,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'F180',
  density: 1.33,
  price: 17.77,
  currency: 'EUR',
  cavityIndex: 6,
  heatedMold: false,
  shrinkage: 1.00605,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'F2',
  density: 1.52,
  price: 16.82,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0135,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'G',
  density: 1.46,
  price: 10.23,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0075,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'G1',
  density: 1.58,
  price: 11.8,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: true,
  shrinkage: 1.003,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'GLW',
  density: 1.36,
  price: 3.68,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0065,
  customerMaterial: false,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'GV0',
  density: 1.53,
  price: 12.11,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0095,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'H',
  density: 1.64,
  price: 25.56,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: true,
  shrinkage: 1.004,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'H1',
  density: 1.53,
  price: 27.75,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: true,
  shrinkage: 1.01,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'H2',
  density: 1.68,
  price: 12.27,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: true,
  shrinkage: 1.006,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'H370',
  density: 1.6,
  price: 28.63,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: true,
  shrinkage: 1.0045,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'H4',
  density: 1.82,
  price: 15.34,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: true,
  shrinkage: 1.0055,
  customerMaterial: false,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'HLW',
  density: 1.64,
  price: 10,
  currency: 'EUR',
  cavityIndex: 3,
  heatedMold: true,
  shrinkage: 1.0045,
  customerMaterial: false,
  subsidiaryMaterial: false,
  optionTemperingPreselected: false
}, {
  materialCode: 'HSD350',
  density: 1.39,
  price: 33,
  currency: 'EUR',
  cavityIndex: 2,
  heatedMold: true,
  shrinkage: 1.009,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}, {
  materialCode: 'J',
  density: 1.49,
  price: 11.76,
  currency: 'EUR',
  cavityIndex: 1,
  heatedMold: false,
  shrinkage: 1.0195,
  customerMaterial: true,
  subsidiaryMaterial: true,
  optionTemperingPreselected: false
}];

var NumberRangeIdentifier;
(function (NumberRangeIdentifier) {
  NumberRangeIdentifier["toolNo"] = "TOOL_NO";
  NumberRangeIdentifier["plmPartNoP"] = "PLM_PART_NO_P";
  NumberRangeIdentifier["koplaPing"] = "KOPLA_PING";
})(NumberRangeIdentifier || (NumberRangeIdentifier = {}));
/**
 * You have to add the NumberRange first in https://masterdata-numberrange.white.igus.com
 */
const allNumberRangeDefinitions = [{
  identifier: NumberRangeIdentifier.toolNo,
  displayName: 'IBD_TOOL_NO',
  owner: 'BU iglidur bearings/ Stefan Lookman-Rittich',
  description: 'Number of the moldinjection tool.'
}, {
  identifier: NumberRangeIdentifier.plmPartNoP,
  displayName: 'IBD_PLM_PART_NO',
  owner: 'BU iglidur bearings/ Stefan Lookman-Rittich',
  description: 'Number for the no-catalog-part that was created by the user.'
}, {
  identifier: NumberRangeIdentifier.koplaPing,
  displayName: 'KOPLA_PING',
  owner: 'Oliver Hoos / Oliver Düllberg / Thomas Rössler',
  description: 'Number for testing NRS on all stages. Can be potential used to wake-up the service. ' + 'DO NOT TRUST ON THE VALUE OF THE NUMBER!'
}];

/**
 * Format a value by rounding it to the given precision (default = 2) and adding a unit.
 */
const formatValue = (prefix, value, unit, postfix, precision = 2) => {
  const power = Math.pow(10, precision);
  return (prefix || '') + String(Math.round(value * power) / power) + unit + postfix;
};

const DIAMETER_FACTOR$7 = 0.3;
const LENGTH_FACTOR$7 = 0.3;
/**
 *    Bearing comparison based on wanted dimensions.
 *    Check desired values in both bearings
 *    Returns true if all corresponding dimensions are equal.
 *
 * @param firstBearing bearing to compare
 * @param secondBearing bearing to compare
 * @param dimensionsToCompare dimensions that should be equal in both bearings
 * @param dimensionToSkip this dimension won't be compared
 */
const checkIfCorrespondingDimensionsAreEqual = (firstBearing, secondBearing, dimensionsToCompare, dimensionToSkip) => {
  return dimensionsToCompare.every(dimension => {
    if (dimensionToSkip === dimension) {
      return true;
    }
    const firstBearingDimensionMM = toMetricDistanceMilli(firstBearing.unitSystem, firstBearing[dimension]);
    const secondBearingDimensionMM = toMetricDistanceMilli(secondBearing.unitSystem, secondBearing[dimension]);
    return firstBearingDimensionMM === secondBearingDimensionMM;
  });
};
const getMetricDimensionValue = (params, dimension) => {
  return toMetricDistanceMilli(params.unitSystem, params[dimension]);
};
/**
 *
 * @param userPart part configured by the user
 * @param catalogPart catalog part to which userPart is compared
 * @param dimensions dimensions that must have same values in both parts to be consider as similar
 * @param searchDimension the dimension that may vary, benchmark for comparison
 * @param customThresholdFactor the custom threshold factor, if unspecified uses DIAMETER_FACTOR and LENGTH_FACTOR
 */
const hasSmallerSize = (userPart, catalogPart, dimensions, searchDimension, customThresholdFactor) => {
  // if dimensions differ it can't be considered as smaller
  if (!checkIfCorrespondingDimensionsAreEqual(userPart, catalogPart, dimensions, searchDimension)) {
    return false;
  }
  const userDimensionMM = getMetricDimensionValue(userPart, searchDimension);
  const catalogDimensionMM = getMetricDimensionValue(catalogPart, searchDimension);
  if (searchDimension === 'd1' || searchDimension === 'd2') {
    return isInLowerRange(userDimensionMM, catalogDimensionMM, customThresholdFactor != null ? customThresholdFactor : DIAMETER_FACTOR$7);
  } else {
    return isInLowerRange(userDimensionMM, catalogDimensionMM, customThresholdFactor != null ? customThresholdFactor : LENGTH_FACTOR$7);
  }
};
/**
 * @param userPart part configured by the user
 * @param catalogPart catalog part to which userPart is compared
 * @param dimensions dimensions that must have same values in both parts to be considered as similar
 * @param searchDimension the dimension that may vary, benchmark for comparison
 * @param customThresholdFactor the custom threshold factor, if unspecified uses DIAMETER_FACTOR and LENGTH_FACTOR
 */
const hasGreaterSize = (userPart, catalogPart, dimensions, searchDimension, customThresholdFactor) => {
  // if dimensions differ it can't be considered as greater
  if (!checkIfCorrespondingDimensionsAreEqual(userPart, catalogPart, dimensions, searchDimension)) {
    return false;
  }
  const userDimensionMM = getMetricDimensionValue(userPart, searchDimension);
  const catalogDimensionMM = getMetricDimensionValue(catalogPart, searchDimension);
  if (searchDimension === 'd1' || searchDimension === 'd2') {
    return isInHigherRange(userDimensionMM, catalogDimensionMM, customThresholdFactor != null ? customThresholdFactor : DIAMETER_FACTOR$7);
  } else {
    return isInHigherRange(userDimensionMM, catalogDimensionMM, customThresholdFactor != null ? customThresholdFactor : LENGTH_FACTOR$7);
  }
};

/**
 * Returns the length of the nose (b4) of the easyClip
 *
 * in [mm] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch] when selectedUnitSystem is imperial
 *
 * If the unitSystem is not set, metric is expected.
 * If d1 is not set, then d1=1 is expected and the short lenght will be returned.
 */
const getEasyClipNoseLength = userPart => {
  const unitSystem = userPart.unitSystem || UnitSystem.metric;
  let userPart_d1_mm = 1;
  if (userPart.d1) {
    userPart_d1_mm = toMetricDistanceMilli(unitSystem, userPart.d1);
  }
  const noseLength_mm = userPart_d1_mm < 10 ? EASY_CLIP_SHORT_NOSE_LENGTH_MM : EASY_CLIP_LONG_NOSE_LENGTH_MM;
  return fromMetricDistanceMilli(unitSystem, noseLength_mm);
};

const DIAMETER_FACTOR$6 = 0.3;
const LENGTH_FACTOR$6 = 0.3;
/**
 * Check if the two given easyClips have the same size.
 *
 * This includes that b2 must be equal!
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasEasyClipTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const userPartB3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b3);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartB2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2);
  const catalogPartB3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b3);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartD3MM === catalogPartD3MM && userPartB2MM === catalogPartB2MM && userPartB3MM === catalogPartB3MM;
};
/**
 * Check if the two given easyClips have a similar size.
 *
 * Here b2 is not compared!
 */
const hasEasyClipASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b3);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartB3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b3);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$6) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$6) && catalogPartD3MM >= userPartD3MM * (1 - DIAMETER_FACTOR$6) && catalogPartD3MM <= userPartD3MM * (1 + DIAMETER_FACTOR$6) && catalogPartB3MM >= userPartB3MM * (1 - LENGTH_FACTOR$6) && catalogPartB3MM <= userPartB3MM * (1 + LENGTH_FACTOR$6);
};
/**
 * Check if an easyClip catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasEasyClipASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'b3'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if an easyClip catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasEasyClipASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'b3'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Returns the wall thickness of a EasyClipParams in mm.
 */
const getEasyClipWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the length of a EasyClip (b1)
 *
 * unit system is the one that is passed in the userPart
 */
const getEasyClipLength = userPart => {
  const b4 = getEasyClipNoseLength(userPart);
  return userPart.b2 + userPart.b3 + b4;
};
/**
 * Returns the BoundingBox of an EasyClip.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getEasyClipBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const nl = getEasyClipLength(userPart);
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, nl) : nl;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};
/**
 * Returns the projection area of the easyClip
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getEasyClipProjectionArea = userPart => {
  const b4 = getEasyClipNoseLength(userPart);
  return userPart.d1 * (userPart.b3 + b4);
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedEasyClip = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.d3 = userPart.d3 * shrinkage;
  part.b2 = userPart.b2 * shrinkage;
  part.b3 = userPart.b3 * shrinkage;
  part.sb = userPart.sb * shrinkage;
  return part;
};
/**
 * Convert the easyClip parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const easyClipToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('d3' + keyValueDelim, userPart.d3, unit, paramDelim, precision), formatValue('b2' + keyValueDelim, userPart.b2, unit, paramDelim, precision), formatValue('b3' + keyValueDelim, userPart.b3, unit, paramDelim, precision), formatValue('sb' + keyValueDelim, userPart.sb, unit, paramDelim, precision)].join(', ');
};
/**
 * Returns the flange thickness of a easyClip (b2).
 */
const getEasyClipFlangeThickness = userPart => {
  return userPart.b2;
};

/**
 * Calculates the volume of a rotational shape using Guldinus second theorem.
 * area is the area of the shape to be rotated.
 * radius is the h-radius (distance between x-axis (h) and (x-part of) center of gravitation)
 */
const volume = (area, radius) => {
  return area * 2 * Math.PI * radius;
};
/**
 * Calculate the resulting Area and Center of gravity of an array of AreaAndCenter values.
 *
 * Works with negative areas (to subtract) as well.
 *
 *                Σ xCenter(i) * area(i)
 * xCenterSum =  --------------------
 *                   Σ area(i)
 *
 * ASum = Σ area(i)
 */
const sumAreaAndCenter = areaAndCenter => {
  const numerator = areaAndCenter.map(e => e.area * e.xCenter).reduce((last, current) => last + current);
  const denominator = areaAndCenter.map(e => e.area).reduce((last, current) => last + current);
  return {
    area: denominator,
    xCenter: numerator / denominator
  };
};
/**
 * Calculate the area between two circles given by d1 and d2 and the width s
 * see https://de.wikipedia.org/wiki/Kreissegment
 *
 *
 * Where area is the area between the inner diameter and the outer diameter
 *
 *                                      **********
 *                               *******          *******
 *                          *****                        *****
 *                       ** |                                |**
 *                      |               area                |    *
 *                   d2     |                                |
 *                          |                                |
 *                          |            *********           |
 *                          |      ******         ******     |
 *                          |*****                       ****|
 *                         **                                **
 *
 *                     d1
 *
 * with area = Arect - Ad1 + Ad2
 *                                      **********
 *                               *******          *******
 *                          *****           Ad2          *****
 *                       ** +--------------------------------+ **
 *                      |                                |    *
 *                   d2     |             Arect              |
 *                          |                                |
 *                          |            *********           |
 *                          |      ******  Ad1    ******     |
 *                          |***** --------------------- ****|
 *                         **                                **
 *                        s                   *
 *                     d1
 *
 *  Arect = s * h
 *
 *                                      *****+****
 *                               *******     |    *******
 *                          *****            | h2         *****
 *                       ** +----------------+---------------+ **
 *                      |                |               |    *
 *                   d2     |                |               |
 *                          |                |               | h
 *                          |            ****+***            |
 *                          |      ******    | h1 ******     |
 *                          |***** ----------+---------- ****|
 *                         **                                **
 *
 *
 *  h = r2 - r1 - h2 + h1
 *
 * @param d1 inner diameter in mm
 * @param d2 outer diameter in mm
 * @param s slotwith in mm
 */
const getSlotAreaBetweenTwoCircles = (d1, d2, s) => {
  const r1 = d1 / 2;
  const r2 = d2 / 2;
  const deltaR = r2 - r1;
  const h1 = r1 - Math.sqrt(r1 * r1 - s / 2 * (s / 2));
  const h2 = r2 - Math.sqrt(r2 * r2 - s / 2 * (s / 2));
  const h = deltaR - h2 + h1;
  const aRect = s * h;
  const aD1 = r1 * r1 * Math.acos(1 - h1 / r1) - (r1 - h1) * Math.sqrt(2 * r1 * h1 - h1 * h1);
  const aD2 = r2 * r2 * Math.acos(1 - h2 / r2) - (r2 - h2) * Math.sqrt(2 * r2 * h2 - h2 * h2);
  const a = aRect + aD2 - aD1;
  return a;
};
/**
 * Calculate the real volume of a slot in a hollow cylinder.
 *
 * V = area * l
 */
const getEndToEndSlotVolume = (d1, d2, l, s) => {
  if (!d1 || !d2 || !l || !s) {
    return 0;
  }
  const A = getSlotAreaBetweenTwoCircles(d1, d2, s);
  return A * l;
};
/**
 * Calculates the volume of a rectangle that rotates around an axis with (inner) radius r using _Guldinus second theorem_.
 *
 *            h                    |
 *       +--------+                |
 *       |        |                |
 *       |        |                |
 *    l  |        |<---------------|
 *       |        |        r       |
 *       |        |                |
 *       +--------+                |
 *                                 | rotation axis
 *
 *  area area = h * l
 *
 *  center_x = r + h /2
 */
const getRotatedRectangleVolume = (x, y, r) => {
  const A = x * y;
  return volume(A, r + x / 2);
};
/**
 * Calculates the volume of a rectangle that rotates around an axis with (inner) radius r using _Guldinus second theorem_.
 *
 *                                 |
 *                +                |
 *               /|  l             |
 *              / |                |
 *             /  |<---------------|
 *            /   |        r       |
 *           /    |                |
 *          +-----+                |
 *            h                    | rotation axis
 *
 *  area area = h * l / 2
 *
 *  center_x = r +  h* 1/3
 */
const getRotatedTriangleQ2Volume = (x, y, r) => {
  const A = x * y / 2;
  return volume(A, r + x / 3);
};
/**
 * Returns the volume of a hollow cylinder in mm^3.
 *
 * @param d1 inner diameter in mm
 * @param d2 outer diameter in mm
 * @param l length in mm
 * @param slotWidth slot width in mm (optional)
 */
const volumeHollowCylinder = (d1, d2, l, slotWidth) => {
  if (slotWidth && slotWidth > d1 / 2) {
    throw new Error(`slot volume can not be calculated precisely in volumeHollowCylinder: slotWidth:${slotWidth}, d1:${d1}`);
  }
  const a = (d2 - d1) / 2;
  const A = a * l;
  const r = d1 / 2 + a / 2;
  let v = volume(A, r);
  if (slotWidth) {
    const sv = getEndToEndSlotVolume(d1, d2, l, slotWidth);
    v = v - sv;
  }
  return v;
};
/**
 * Returns the volume of a hollow cylinder in mm^3 with given bevels.
 *
 * Calculation is based on the volume of a rotational shape using _Guldinus second theorem_.
 * That means that the volume depends on the areas and the center of gravity.
 * In this case we need to subtract the triangles (that are the bevels after rotation) from the rectangle (that is the
 * bearing after rotation).
 *
 * Bottom side is where you can have the flange. This is the usual setup for 3D printing bearings.
 */
const volumeHollowCylinderWithBevels = (d1, d2, h, fBottomOutside,
// F1
fBottomInside,
// F2
fTopOutside,
// F3
fTopInside,
// F4
slotwidth) => {
  const a = (d2 - d1) / 2;
  const A = a * h;
  const xCenter = d1 / 2 + a / 2;
  const areaAndCenter = [{
    area: A,
    xCenter
  }];
  if (fBottomInside && fBottomInside.h && fBottomInside.l) {
    // We need to subtract the triangles => negative sign
    areaAndCenter.push({
      area: -fBottomInside.h * fBottomInside.l / 2,
      xCenter: xCenter - a / 2 + 1 / 3 * fBottomInside.h
    });
  }
  if (fTopInside && fTopInside.h && fTopInside.l) {
    // We need to subtract the triangles => negative sign
    areaAndCenter.push({
      area: -fTopInside.h * fTopInside.l / 2,
      xCenter: xCenter - a / 2 + 1 / 3 * fTopInside.h
    });
  }
  if (fBottomOutside && fBottomOutside.h && fBottomOutside.l) {
    // We need to subtract the triangles => negative sign
    areaAndCenter.push({
      area: -fBottomOutside.h * fBottomOutside.l / 2,
      xCenter: xCenter + a / 2 - 1 / 3 * fBottomOutside.h
    });
  }
  if (fTopOutside && fTopOutside.h && fTopOutside.l) {
    // We need to subtract the triangles => negative sign
    areaAndCenter.push({
      area: -fTopOutside.h * fTopOutside.l / 2,
      xCenter: xCenter + a / 2 - 1 / 3 * fTopOutside.h
    });
  }
  const result = sumAreaAndCenter(areaAndCenter);
  let v = volume(result.area, result.xCenter);
  return v;
};

/**
 * Location of the bevels on the bearing. For sleeve bearings usually only F2 and F3 exist.
 *
 
 *
 *         F3                 Outside                    F1
 *         +----------------------------------------------+         /
 *         |                                              |         |
 *         |                                              |         |
 *         +----------------------------------------------+    /    |
 *         F4                  Inside                    F2    |    |
 *                                                             |    |
 *    - . - . - . - . - . - . - . - . - . - . - . - . - . - .  d1   d2
 *                                                             |    |
 *         F4                  Inside                    F2    |    |
 *         +----------------------------------------------+    /    |
 *         |                                              |         |
 *         |                                              |         |
 *         +----------------------------------------------+         /
 *         F3                 Outside                    F1
 *
 *         /--------------------- b1 ---------------------/
 *
 *
 *
 *  Location of the bevel length (in bearing length b1 direction) and bevel height (perpendicular to the rotational
 *  axis)
 *
 *                                         l        F1
 *           --------------------+-------------------+
 *                                -----              |
 *                          angle      -----         | h
 *                                          -----    |
 *                                               ----+
 *                                                   |
 *                                                   |
 *
 */
/**
 * converts a bevel given in BevelLengthHeight into BevelLengthAngle
 */
const bevelToLengthAngle = bevel => {
  const alpha = Math.atan(bevel.h / bevel.l) * 180 / Math.PI;
  return {
    l: bevel.l,
    angle: alpha
  };
};
/**
 * converts a bevel given in BevelLengthAngle into BevelLengthHeight
 */
const bevelToLengthHeight = bevel => {
  const h = Math.tan(bevel.angle * Math.PI / 180) * bevel.l;
  return {
    l: bevel.l,
    h
  };
};
const isBevelChargeable = part => {
  // moldinjectionsParams do not contain bevels
  if (isInjectionMoldParams(part)) {
    return false;
  }
  // bevels do only exist on S and F atm
  if (part.form === 'S' || part.form === 'F') {
    const bevels = part.bevels;
    // no bevels - no charge
    if (!bevels) {
      return false;
    }
    // if not both "inner" bevels (F2, F4) are set - no charged
    if (!bevels.f2 || !bevels.f4) {
      return false;
    }
    if (bevels.f2 && bevels.f2.l > 0 && bevels.f2.angle > 0 && bevels.f4 && bevels.f4.l > 0 && bevels.f4.angle > 0) {
      return true;
    }
  }
  return false;
};

const DIAMETER_FACTOR$5 = 0.3;
const LENGTH_FACTOR$5 = 0.3;
const bevelAngleRoundUp$1 = Math.ceil;
const bevelAngleRoundDown$1 = Math.floor;
const bevelDistanceRoundUp$1 = ceil2;
const bevelDistanceRoundDown$1 = floor2;
/**
 * Check if the two given flangeBearings have the same size.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasFlangeBearingTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  const catalogPartB2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartD3MM === catalogPartD3MM && userPartB1MM === catalogPartB1MM && userPartB2MM === catalogPartB2MM;
};
/**
 * Check if the two given flangeBearings have a similar size.
 */
const hasFlangeBearingASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  const catalogPartB2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$5) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$5) && catalogPartD3MM >= userPartD3MM * (1 - DIAMETER_FACTOR$5) && catalogPartD3MM <= userPartD3MM * (1 + DIAMETER_FACTOR$5) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR$5) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR$5) && catalogPartB2MM >= userPartB2MM * (1 - LENGTH_FACTOR$5) && catalogPartB2MM <= userPartB2MM * (1 + LENGTH_FACTOR$5);
};
/**
 * Check if the catalogPart has a smaller size than the userPart.
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *  - catalogPart.d3 === userPart.d3
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 *  - b2 is not compared because it is not part of the partNo
 */
const hasFlangeBearingASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'b1'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *  - catalogPart.d3 === userPart.d3
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 *  - b2 is not compared because it is not part of the partNo
 */
const hasFlangeBearingASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'b1'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart for mechanical-reprocessing (MNA)
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *  - catalogPart.d3 === userPart.d3
 *
 *  - catalogPart.b1 >= userPart.b1
 *  - catalogPart.b2 === userPart.b2
 *
 *  With IGL-3360 we limit MNA to shortening of b1 only
 */
const hasFlangeBearingAGreaterSizeForMNA = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  const catalogPartB2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM === userPartD2MM && catalogPartD3MM === userPartD3MM && catalogPartB1MM >= userPartB1MM && catalogPartB2MM === userPartB2MM;
};
/**
 * Returns the wall thickness of a FlangeBearingParams in mm.
 */
const getFlangeBearingWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the angle (in degrees) of the bevel.
 *
 * min. wt changed regarding IGL-881
 * mit. wt changed regarding IGL-1053
 */
const getFlangeBearingDefaultBevelAngleF1 = userPart => {
  const d1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const d2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const wtMM = (d2MM - d1MM) / 2;
  if (d1MM <= 0 || d2MM <= 0) {
    return 0;
  }
  return wtMM < 0.6 ? 20 : 30;
};
/**
 * Returns the length of the bevel F3 (outside - top) depending on d1.
 * All values in mm.
 */
const getFlangeBearingDefaultBevelLengthF1 = userPart => {
  const d1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const d2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const wtMM = (d2MM - d1MM) / 2;
  let l = 0;
  if (d1MM >= 1 && d1MM < 6) {
    l = 0.3;
  } else if (d1MM >= 6 && d1MM < 12) {
    l = 0.5;
  } else if (d1MM >= 12 && d1MM < 30) {
    l = 0.8;
  } else if (d1MM >= 30) {
    l = 1.2;
  }
  // In IGL-2950 we noticed that in some cases, the given length values by diameter are not possible.
  // So avoid them by going dow to that limit.
  // create unit system aware constants
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(userPart.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const hRange = {
    min: uBEVEL_RADIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown$1(wtMM - uBEVEL_RADIAL_MIN_DISTANCE) / 2
  };
  const defaultAngle = getFlangeBearingDefaultBevelAngleF1(userPart);
  // When the angle of the bevel is 0° then the possible length is also 0 - which both means: no bevel
  if (defaultAngle === 0) {
    return 0;
  }
  const lmin = bevelDistanceRoundUp$1(hRange.min / Math.tan(defaultAngle * Math.PI / 180));
  const lmax = bevelDistanceRoundDown$1(hRange.max / Math.tan(defaultAngle * Math.PI / 180));
  if (l < lmin) {
    l = lmin;
  }
  if (l > lmax) {
    l = lmax;
  }
  if (userPart.unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return trunc4(fromMetricDistanceMilli(UnitSystem.imperial, l));
  }
};
/**
 * Returns the bevels of the flange bearing or zero values if not set
 * l (length) is in mm
 */
const getFlangeBearingBevelsOrDefaults = userPart => {
  if (userPart.bevels) {
    return userPart.bevels;
  }
  const angle = getFlangeBearingDefaultBevelAngleF1(userPart);
  const l = getFlangeBearingDefaultBevelLengthF1(userPart);
  return {
    f1: {
      l,
      angle
    },
    f2: {
      l: 0,
      angle: 0
    },
    f3: {
      l: 0,
      angle: 0
    },
    f4: {
      l: 0,
      angle: 0
    }
  };
};
/**
 * Returns the volume of the FlangeBearingParams in [cm^3].
 *
 * If the userPart uses metric distances, the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch), the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In most cases, we need the volume only to calculate the mass. Therefore, we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getFlangeBearingVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  if (userPart.bevels) {
    return volumeHollowCylinderWithBevels(userPartD1MM, userPartD2MM, userPartB1MM, bevelToLengthHeight(userPart.bevels.f1), bevelToLengthHeight(userPart.bevels.f2), bevelToLengthHeight(userPart.bevels.f3), bevelToLengthHeight(userPart.bevels.f4)) / 1000 + volumeHollowCylinder(userPartD2MM, userPartD3MM, userPartB2MM) / 1000;
  } else {
    return volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB1MM, 0) / 1000 + volumeHollowCylinder(userPartD2MM, userPartD3MM, userPartB2MM) / 1000;
  }
};
/**
 * Returns the BoundingBox of a FlangeBearing.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getFlangeBearingBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};
/**
 * Returns the projection area of the flangeBearing
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getFlangeBearingProjectionArea = userPart => {
  // Fixme: take reduced size from bevels into account
  return userPart.d1 * (userPart.b1 - userPart.b2);
};
/**
 * Returns the flange thickness of a FlangeBearing (b2).
 */
const getFlangeBearingFlangeThickness = userPart => {
  return userPart.b2;
};
/**
 * Returns the length of a FlangeBearing (b1).
 */
const getFlangeBearingLength = userPart => {
  return userPart.b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedFlangeBearing = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.d3 = userPart.d3 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  part.b2 = userPart.b2 * shrinkage;
  return part;
};
/**
 * Convert the flangeBearing parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const flangeBearingToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('d3' + keyValueDelim, userPart.d3, unit, paramDelim, precision), formatValue('b1' + keyValueDelim, userPart.b1, unit, paramDelim, precision), formatValue('b2' + keyValueDelim, userPart.b2, unit, paramDelim, precision)].join(', ');
};
/**
 * returns the radius of the flange bearing
 *
 * Atm. it is a static value, but it is most probably that it will be depending on other
 * part params later on.
 *
 * result in mm or inches
 */
const getFlangeBearingRadius = unitSystem => {
  const r = 0.5;
  return fromMetricDistanceMilli(unitSystem, r);
};
/**
 * Check if the two given flangeBearings have the same bevels.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasFlangeBearingTheSameBevels = (userPart, catalogPart) => {
  if (userPart.bevels && catalogPart.bevels) {
    const ub = userPart.bevels;
    const cb = catalogPart.bevels;
    return ub.f1.l === cb.f1.l && ub.f1.angle === cb.f1.angle && ub.f2.l === cb.f2.l && ub.f2.angle === cb.f2.angle && ub.f3.l === cb.f3.l && ub.f3.angle === cb.f3.angle && ub.f4.l === cb.f4.l && ub.f4.angle === cb.f4.angle;
  } else {
    return !userPart.bevels;
  }
};
const getEffMaxLength = (params, forBevel) => {
  // create unit system aware constants
  const uBEVEL_AXIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_AXIAL_MIN_DISTANCE);
  const r = getFlangeBearingRadius(params.unitSystem);
  const b3 = params.b1 - params.b2 - r;
  let effMaxLength = 0;
  switch (forBevel) {
    case 'f2':
    case 'f4':
      effMaxLength = Math.max((params.b1 - uBEVEL_AXIAL_MIN_DISTANCE) / 2, 0);
      break;
    case 'f1':
      effMaxLength = Math.max(b3 - uBEVEL_AXIAL_MIN_DISTANCE, 0);
      break;
    case 'f3':
      effMaxLength = Math.max(params.b2 - uBEVEL_AXIAL_MIN_DISTANCE, 0);
      break;
  }
  return effMaxLength;
};
const getEffWt = (params, forBevel) => {
  if (forBevel === 'f1' || forBevel === 'f2') {
    return (params.d2 - params.d1) / 2;
  } else {
    return (params.d3 - params.d1) / 2;
  }
};
/**
 * return the length range for given params and bevel
 * if calculation is not possible undefined is returned
 *
 * all calculations are in the unit system that is passed in params.unitSystem
 * and also the returned values are in that unit system
 */
const getFlangeBearingBevelLengthRange = (params, forBevel) => {
  // create unit system aware constants
  const uBEVEL_AXIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_AXIAL_MIN_DISTANCE);
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const wt = getEffWt(params, forBevel);
  if (!wt || !forBevel) {
    return undefined;
  }
  const bevels = params.bevels;
  // if the angle or length is 0, then the length range may be 0-0
  if (bevels && bevels[forBevel] && (bevels[forBevel].angle === 0 || bevels[forBevel].l === 0)) {
    return {
      min: 0,
      max: 0
    };
  }
  const effLength = getEffMaxLength(params, forBevel);
  const usableHorizontalLength = {
    min: uBEVEL_AXIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown$1(effLength)
  };
  if (bevels && bevels[forBevel] && bevels[forBevel].angle > 0) {
    const h = {
      min: uBEVEL_RADIAL_MIN_DISTANCE,
      max: bevelDistanceRoundDown$1(wt - uBEVEL_RADIAL_MIN_DISTANCE) / 2
    };
    const tanAngle = Math.tan(bevels[forBevel].angle * Math.PI / 180);
    const usableLengthFromHorizontal = {
      min: bevelDistanceRoundUp$1(h.min / tanAngle),
      max: bevelDistanceRoundDown$1(h.max / tanAngle)
    };
    usableHorizontalLength.min = Math.max(usableHorizontalLength.min, usableLengthFromHorizontal.min);
    usableHorizontalLength.max = Math.min(usableHorizontalLength.max, usableLengthFromHorizontal.max);
  }
  return usableHorizontalLength;
};
/**
 * returns the angle range for given params and bevel
 * if calculation is not possible undefined is returned
 *
 * all calculations are in the unit system that is passed in params.unitSystem
 *
 * the result is in ° (to be interpreted as inclusive values)
 */
const getFlangeBearingBevelAngleRange = (params, forBevel) => {
  // create unit system aware constants
  const uBEVEL_AXIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_AXIAL_MIN_DISTANCE);
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const wt = getEffWt(params, forBevel);
  if (!wt || !forBevel) {
    return undefined;
  }
  const bevels = params.bevels;
  // if the angle or length is 0, then angle range may be 0-0
  if (bevels && bevels[forBevel] && (bevels[forBevel].angle === 0 || bevels[forBevel].l === 0)) {
    return {
      min: 0,
      max: 0
    };
  }
  // available, usable height h
  const h = {
    min: uBEVEL_RADIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown$1(wt - uBEVEL_RADIAL_MIN_DISTANCE) / 2
  };
  if (bevels && bevels[forBevel] && bevels[forBevel].l > 0) {
    const angleRange = {
      min: bevelAngleRoundUp$1(Math.atan(h.min / bevels[forBevel].l) * 180 / Math.PI),
      max: bevelAngleRoundDown$1(Math.atan(h.max / bevels[forBevel].l) * 180 / Math.PI)
    };
    return angleRange;
  } else {
    const l = {
      min: uBEVEL_AXIAL_MIN_DISTANCE,
      max: bevelDistanceRoundDown$1(getEffMaxLength(params, forBevel))
    };
    const angleRange = {
      min: bevelAngleRoundUp$1(Math.atan(h.min / l.max) * 180 / Math.PI),
      max: bevelAngleRoundDown$1(Math.atan(h.max / l.min) * 180 / Math.PI)
    };
    return angleRange;
  }
};

const getTwoHoleFlangeBearingVolume = userPart => {
  // flange part (the part with thickness b2)
  const userPartR1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1 / 2);
  const userPartR5MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d5 / 2);
  const userPartR6MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d6 / 2);
  const userPartR7MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d7 / 2);
  const userPartRMM = toMetricDistanceMilli(userPart.unitSystem, userPart.r);
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d4);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const userPartB3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b3);
  const raMM = userPartR5MM - userPartRMM;
  const cMM = Math.sqrt(userPartR7MM * userPartR7MM - raMM * raMM);
  const beta = Math.asin(cMM / userPartR7MM);
  const alpha = 90 - beta;
  const areaA = 2 * Math.PI * userPartRMM * (beta / 360);
  const areaB = 2 * Math.PI * userPartR5MM * (alpha / 360);
  const areaC = userPartRMM * cMM;
  const areaD = raMM * cMM / 2;
  const areaD6Hole = 2 * Math.PI * userPartR6MM * (1 / 2);
  const areaD1Hole = 2 * Math.PI * userPartR1MM * (1 / 4);
  const FpArea = 4 * (areaA + areaB + areaC + areaD - areaD6Hole - areaD1Hole);
  // volume in cm^3
  const FpVolume = FpArea * userPartB2MM / 1000;
  // hollow cylinder on d4 side (the part with thickness b3)
  // volumeHollowCylinder returns mm^3 so divide by 1000 to get cm^3
  const D4SideVolume = volumeHollowCylinder(userPartD1MM, userPartD4MM, userPartB3MM) / 1000;
  // hollow cylinder on d2 side (the part with thickness bx)
  const bx = userPartB1MM - userPartB2MM - userPartB3MM;
  // volumeHollowCylinder returns mm^3 so divide by 1000 to get cm^3
  const D2SideVolume = volumeHollowCylinder(userPartD1MM, userPartD2MM, bx) / 1000;
  return FpVolume + D4SideVolume + D2SideVolume;
};
/**
 * Returns the BoundingBox of an TwoHoleFlangeBearing.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getTwoHoleFlangeBearingBoundingBox = (userPart, forceMetric = true) => {
  const userPartD5 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d5) : userPart.d5;
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD5
  };
};
/**
 * Returns the length of a twoHoleFlangeBearingPreload (b1).
 */
const getTwoHoleFlangeBearingPreloadLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the flange thickness of a twoHoleFlangeBearing (b2).
 */
const getTwoHoleFlangeBearingFlangeThickness = userPart => {
  return userPart.b2;
};

/**
 * Returns the length of a SplitBearing (b1).
 */
const getSplitBearingLength = userPart => {
  return userPart.b1;
};

/**
 * Returns the length of a SplitBearing (b1).
 */
const getSplitBearingAntiRotationLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the flange thickness of a SplitBearing (b2).
 */
const getSplitBearingAntiRotationFlangeThickness = userPart => {
  return userPart.b2;
};

const DIAMETER_FACTOR$4 = 0.3;
const LENGTH_FACTOR$4 = 0.3;
/**
 * Check if the two given Mcms have the same size.
 *
 * This includes that both b2 (b2d4 aka b2.1 and b2d3 aka b2.2) must be equal!
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasCTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartD4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d4);
  const userPartB3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b3);
  const userPartB2D3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2d3);
  const userPartB2D4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2d4);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartD4MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d4);
  const catalogPartB3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b3);
  const catalogPartB2D3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2d3);
  const catalogPartB2D4MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2d4);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartD3MM === catalogPartD3MM && userPartD4MM === catalogPartD4MM && userPartB3MM === catalogPartB3MM && userPartB2D3MM === catalogPartB2D3MM && userPartB2D4MM === catalogPartB2D4MM ||
  // maybe the pairs d3 b2b3 and d4 b2d4 are swapped
  userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartD3MM === catalogPartD4MM && userPartD4MM === catalogPartD3MM && userPartB3MM === catalogPartB3MM && userPartB2D3MM === catalogPartB2D4MM && userPartB2D4MM === catalogPartB2D3MM;
};
/**
 * Check if the two given Mcms have a similar size.
 *
 * Here b2 is not compared!
 */
const hasCASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartD4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d4);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartD3MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d3);
  const catalogPartD4MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d4);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$4) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$4) && catalogPartD3MM >= userPartD3MM * (1 - DIAMETER_FACTOR$4) && catalogPartD3MM <= userPartD3MM * (1 + DIAMETER_FACTOR$4) && catalogPartD4MM >= userPartD4MM * (1 - DIAMETER_FACTOR$4) && catalogPartD4MM <= userPartD4MM * (1 + DIAMETER_FACTOR$4) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR$4) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR$4);
};
/**
 * Check if an Mcm catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasCASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'd4', 'b3'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if an Mcm catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasCASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'd3', 'd4', 'b3'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Returns the wall thickness of a McmParams in mm.
 */
const getCWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the volume of the McmParams independent from the unit-system in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * When we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getCVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartD4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d4);
  const userPartB2D3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2d3);
  const userPartB2D4MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2d4);
  const userPartB3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b3);
  // It is not intended to let the user enter sb (the slot_width), so we do not know it.
  // Because of that we ignore the slot while calculating the volume of an MCM if it
  // is too big.
  // With this change the occurrence of errors like IGL-1425 should vanish.
  const userPartSMM = toMetricDistanceMilli(userPart.unitSystem, userPart.sb);
  if (userPartSMM < userPartD1MM / 2) {
    const slotAngle = userPart.slotAngle || 20;
    const slotWidthInDiameterDirection_mm = userPartSMM / Math.cos(slotAngle * Math.PI / 180);
    // volumeHollowCylinder returns mm^3 so divide by 1000 to get cm^3
    return (volumeHollowCylinder(userPartD1MM, userPartD3MM, userPartB2D3MM, slotWidthInDiameterDirection_mm) + volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB3MM, slotWidthInDiameterDirection_mm) + volumeHollowCylinder(userPartD1MM, userPartD4MM, userPartB2D4MM, slotWidthInDiameterDirection_mm)) / 1000;
  }
  // volumeHollowCylinder returns mm^3 so divide by 1000 to get cm^3
  return (volumeHollowCylinder(userPartD1MM, userPartD3MM, userPartB2D3MM) + volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB3MM) + volumeHollowCylinder(userPartD1MM, userPartD4MM, userPartB2D4MM)) / 1000;
};
/**
 * Returns the BoundingBox of a Mcm.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getCBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartD4 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d4) : userPart.d4;
  const userPartB3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b3) : userPart.b3;
  const userPartB2D3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b2d3) : userPart.b2d3;
  const userPartB2D4 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b2d4) : userPart.b2d4;
  const userPartMaxD = Math.max(userPartD3, userPartD4);
  const userPartB1 = userPartB3 + userPartB2D3 + userPartB2D4;
  return {
    h: userPartB1,
    x: userPartMaxD,
    y: userPartMaxD
  };
};
/**
 * Returns the projection area of the Mcm
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getCProjectionArea = userPart => {
  return userPart.d1 * (userPart.b3 + userPart.b2d3 + userPart.b2d4);
};
/**
 * Returns the length of a C (b1)
 *  - catalog parts define the b1 prop.
 *  - custom parts do not define b1, therefore we calculate it.
 */
const getCLength = userPart => {
  const {
    b1,
    b2d3,
    b2d4,
    b3
  } = userPart;
  return b1 === undefined ? b2d3 + b2d4 + b3 : b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedC = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.d3 = userPart.d3 * shrinkage;
  part.d4 = userPart.d4 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  part.b2d3 = userPart.b2d3 * shrinkage;
  part.b2d4 = userPart.b2d4 * shrinkage;
  part.b3 = userPart.b3 * shrinkage;
  part.sb = userPart.sb * shrinkage;
  return part;
};
/**
 * Convert the Mcm parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const CToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('d3' + keyValueDelim, userPart.d3, unit, paramDelim, precision), formatValue('d4' + keyValueDelim, userPart.d4, unit, paramDelim, precision), formatValue('b2.2' + keyValueDelim, userPart.b2d3, unit, paramDelim, precision), formatValue('b2.1' + keyValueDelim, userPart.b2d4, unit, paramDelim, precision), formatValue('b3' + keyValueDelim, userPart.b3, unit, paramDelim, precision)].join(', ');
};
/**
 * Returns the top flange thickness of a C (b2d3).
 */
const getCFlangeThicknessTop = userPart => {
  return userPart.b2d3;
};
/**
 * Returns the bottom flange thickness of a C (b2d4).
 */
const getCFlangeThicknessBottom = userPart => {
  return userPart.b2d4;
};

const DIAMETER_FACTOR$3 = 0.3;
const LENGTH_FACTOR$3 = 0.3;
/**
 * Check if the two given pistonRings have the same size.
 *
 * This includes that b2 must be equal!
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasPistonRingTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  const catalogPartB2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b2);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartB1MM === catalogPartB1MM && userPartB2MM === catalogPartB2MM;
};
/**
 * Check if the two given pistonRings have a similar size.
 *
 * Here b2 is not compared!
 */
const hasPistonRingASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$3) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$3) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR$3) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR$3);
};
/**
 * Check if a pistonRing catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasPistonRingASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if  a pistonRing catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 *
 * Here b2 is not compared!
 */
const hasPistonRingASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart for mechanical-reprocessing (MNA)
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 >= userPart.b1
 */
const hasPistonRingAGreaterSizeForMNA = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM === userPartD2MM && catalogPartB1MM >= userPartB1MM;
};
/**
 * Returns the wall thickness of a PistonRingParams in mm.
 */
const getPistonRingWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the bevels of the sleeve bearing or zero values if not set
 * l (length) is in mm
 */
const getPistonRingBevelsOrDefaults = userPart => {
  const l = userPart.unitSystem === UnitSystem.metric ? 0.3 : 0.0118;
  /* KIM-F1 is F4 */
  /* KIM-F2 is F3 */
  /* KIM-F3 is F1 */
  /* KIM-F4 is F2 */
  return {
    f1: {
      l,
      angle: 30
    },
    f2: {
      l,
      angle: 30
    },
    f3: {
      l,
      angle: 60
    },
    f4: {
      l,
      angle: 30
    }
  };
};
/**
 * Returns the volume of the PistonRingParams independent of the unit-system in [cm^3].
 *
 * If the userPart uses metric distances, the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch), the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In most cases, we need the volume only to calculate the mass. Therefore, we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getPistonRingVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const slotWidthInDiameterDirectionMM = userPartB2MM / Math.cos(userPart.slotAngle * Math.PI / 180);
  // change to volumeHollowCylinderWithBevels() when bevels can be entered in the application
  // volumeHollowCylinder returns mm^3 so divide by 1000 to get cm^3
  return volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB1MM, slotWidthInDiameterDirectionMM) / 1000;
};
/**
 * Returns the BoundingBox of a PistonRing.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getPistonRingBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the projection area of the PistonRingParams
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getPistonRingProjectionArea = userPart => {
  return userPart.d1 * userPart.b1;
};
/**
 * Returns the length of a PistonRing (b1)
 */
const getPistonRingLength = userPart => {
  return userPart.b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedPistonRing = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  return part;
};
/**
 * Convert the pistonRing parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 *
 * adjusted regarding IGL-148
 */
const pistonRingToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('b1' + keyValueDelim, userPart.b1, unit, paramDelim, precision), formatValue('b2' + keyValueDelim, userPart.b2, unit, paramDelim, precision), formatValue('slotAngle' + keyValueDelim, userPart.slotAngle, '°', paramDelim)].join(', ');
};

const DIAMETER_FACTOR$2 = 0.3;
const LENGTH_FACTOR$2 = 0.3;
/**
 * Check if the two given KnifeEdgeRollers have the same size.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasKnifeEdgeRollerTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartB1MM === catalogPartB1MM;
};
/**
 * Check if the two given KnifeEdgeRollers have a similar size.
 */
const hasKnifeEdgeRollerASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$2) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$2) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR$2) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR$2);
};
/**
 * Check if an KnifeEdgeRoller catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 */
const hasKnifeEdgeRollerASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if an KnifeEdgeRoller catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 */
const hasKnifeEdgeRollerASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart for mechanical-reprocessing (MNA)
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T, RL) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 >= userPart.b1
 *
 *  With IGL-3360 we limit MNA to shortening of b1 only
 */
const hasKnifeEdgeRollerAGreaterSizeForMNA = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM === userPartD2MM && catalogPartB1MM >= userPartB1MM;
};
/**
 * Returns the wall thickness of a KnifeEdgeRollerParams in mm.
 */
const getKnifeEdgeRollerWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the volume of the KnifeEdgeRollerParams independent from the unit-system in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * When we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getKnifeEdgeRollerVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  // volumeHollowCylinder returns mm^3 so devide by 1000 to get cm^3
  return volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB1MM) / 1000;
};
/**
 * Returns the BoundingBox of an KnifeEdgeRoller.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getKnifeEdgeRollerBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the projection area of the KnifeEdgeRoller
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getKnifeEdgeRollerProjectionArea = userPart => {
  return userPart.d1 * userPart.b1;
};
/**
 * Creates a new (virtual) PartNo for the given KnifeEdgeRollerParams.
 */
const createKnifeEdgeRollerPartNo = userPart => {
  let d1;
  let d2;
  let b1;
  if (userPart.unitSystem === UnitSystem.metric) {
    d1 = userPart.d1;
    d2 = userPart.d2;
    b1 = userPart.b1;
  } else {
    const unitSystemFactor = getUnitSystemFactor(userPart);
    d1 = Math.floor(ceil4(userPart.d1) * unitSystemFactor);
    d2 = Math.floor(ceil4(userPart.d2) * unitSystemFactor);
    b1 = Math.floor(ceil4(userPart.b1) * unitSystemFactor);
  }
  return userPart.materialCode + userPart.form + unitSystemCharacter(userPart.unitSystem) + '-' + parameterToPartNoElement(d1, 2) + parameterToPartNoElement(d2, 2) + '-' + parameterToPartNoElement(b1, 2);
};
/**
 * Returns the length of a KnifeEdgeRoller (b1)
 */
const getKnifeEdgeRollerLength = userPart => {
  return userPart.b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedKnifeEdgeRoller = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  return part;
};
/**
 * Convert the KnifeEdgeRoller parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const knifeEdgeRollerToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('b1' + keyValueDelim, userPart.b1, unit, paramDelim, precision)].join(', ');
};

const DIAMETER_FACTOR$1 = 0.3;
const LENGTH_FACTOR$1 = 0.3;
const bevelAngleRoundUp = Math.ceil;
const bevelAngleRoundDown = Math.floor;
const bevelDistanceRoundUp = ceil2;
const bevelDistanceRoundDown = floor2;
/**
 * Check if the two given sleeveBearings have the same size.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasSleeveBearingTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartB1MM === catalogPartB1MM;
};
/**
 * Check if the two given sleeveBearings have a similar size.
 */
const hasSleeveBearingASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR$1) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR$1) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR$1) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR$1);
};
/**
 * Check if the catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% smaller than userPart.b1
 */
const hasSleeveBearingASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 can be up to 30% greater than userPart.b1
 */
const hasSleeveBearingASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart for mechanical-reprocessing (MNA)
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 >= userPart.b1
 *
 * With IGL-3360 we limit MNA to shortening of b1 only
 */
const hasSleeveBearingAGreaterSizeForMNA = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM === userPartD2MM && catalogPartB1MM >= userPartB1MM;
};
/**
 * Returns the wall thickness of a SleeveBearingParams in mm.
 */
const getSleeveBearingWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the angle (in degrees) of the bevel.
 *
 * min. wt changed regarding IGL-881
 * mit. wt changed regarding IGL-1053
 */
const getSleeveBearingDefaultBevelAngleF1F4 = userPart => {
  const d1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const d2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const wtMM = (d2MM - d1MM) / 2;
  if (d1MM <= 0 || d2MM <= 0) {
    return 0;
  }
  return wtMM < 0.6 ? 20 : 30;
};
/**
 * Returns the length of the bevel F3 (outside - top) depending on d1.
 * All values in mm.
 */
const getSleeveBearingDefaultBevelLengthF1F4 = userPart => {
  const d1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const d2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const wtMM = (d2MM - d1MM) / 2;
  let l = 0;
  if (d1MM >= 1 && d1MM < 6) {
    l = 0.3;
  } else if (d1MM >= 6 && d1MM < 12) {
    l = 0.5;
  } else if (d1MM >= 12 && d1MM < 30) {
    l = 0.8;
  } else if (d1MM >= 30) {
    l = 1.2;
  }
  // In IGL-2950 we noticed that in some cases, the given length values by diameter are not possible.
  // So avoid them by going dow to that limit.
  // create unit system aware constants
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(userPart.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const hRange = {
    min: uBEVEL_RADIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown(wtMM - uBEVEL_RADIAL_MIN_DISTANCE) / 2
  };
  const defaultAngle = getSleeveBearingDefaultBevelAngleF1F4(userPart);
  // when the angle of the bevel is 0° then the possible length is also 0 - which both means: no bevel
  if (defaultAngle === 0) {
    return 0;
  }
  const lmin = bevelDistanceRoundUp(hRange.min / Math.tan(defaultAngle * Math.PI / 180));
  const lmax = bevelDistanceRoundDown(hRange.max / Math.tan(defaultAngle * Math.PI / 180));
  if (l < lmin) {
    l = lmin;
  }
  if (l > lmax) {
    l = lmax;
  }
  if (userPart.unitSystem === UnitSystem.metric) {
    return l;
  } else {
    return trunc4(fromMetricDistanceMilli(UnitSystem.imperial, l));
  }
};
/**
 * Returns the bevels of the sleeve bearing or zero values if not set
 * l (length) is in mm
 */
const getSleeveBearingBevelsOrDefaults = userPart => {
  if (userPart.bevels) {
    return userPart.bevels;
  }
  const angle = getSleeveBearingDefaultBevelAngleF1F4(userPart);
  const l = getSleeveBearingDefaultBevelLengthF1F4(userPart);
  return {
    f1: {
      l,
      angle
    },
    f2: {
      l: 0,
      angle: 0
    },
    f3: {
      l: 0,
      angle: 0
    },
    f4: {
      l,
      angle
    }
  };
};
/**
 * Returns the volume of the SleeveBearingParams independent of the unit-system in [cm^3].
 *
 * If the userPart uses metric distances, the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch), the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In most cases, we need the volume only to calculate the mass. Therefore, we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getSleeveBearingVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  if (userPart.bevels) {
    return volumeHollowCylinderWithBevels(userPartD1MM, userPartD2MM, userPartB1MM, bevelToLengthHeight(userPart.bevels.f1), bevelToLengthHeight(userPart.bevels.f2), bevelToLengthHeight(userPart.bevels.f3), bevelToLengthHeight(userPart.bevels.f4)) / 1000;
  } else {
    return volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB1MM, 0) / 1000;
  }
};
/**
 * Returns the BoundingBox of a SleeveBearing.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getSleeveBearingBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the projection area of the SleeveBearingParams
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getSleeveBearingProjectionArea = userPart => {
  // Fixme: take reduced size from bevels into account
  return userPart.d1 * userPart.b1;
};
/**
 * Returns the length of a SleeveBearing (b1)
 */
const getSleeveBearingLength = userPart => {
  return userPart.b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedSleeveBearing = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  return part;
};
/**
 * Convert the sleeveBearing parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const sleeveBearingToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('b1' + keyValueDelim, userPart.b1, unit, paramDelim, precision)].join(', ');
};
/**
 * Check if the two given sleeveBearings have the same bevels.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasSleeveBearingTheSameBevels = (userPart, catalogPart) => {
  if (userPart.bevels && catalogPart.bevels) {
    const ub = userPart.bevels;
    const cb = catalogPart.bevels;
    return ub.f1.l === cb.f1.l && ub.f1.angle === cb.f1.angle && ub.f2.l === cb.f2.l && ub.f2.angle === cb.f2.angle && ub.f3.l === cb.f3.l && ub.f3.angle === cb.f3.angle && ub.f4.l === cb.f4.l && ub.f4.angle === cb.f4.angle;
  } else {
    return !userPart.bevels;
  }
};
/**
 * return the length range for given params and bevel
 * if calculation is not possible undefined is returned
 *
 * all calculations are in the unit system that is passed in params.unitSystem
 * and also the returned values are in that unit system
 */
const getSleeveBearingBevelLengthRange = (params, forBevel) => {
  // create unit system aware constants
  const uBEVEL_AXIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_AXIAL_MIN_DISTANCE);
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const wt = (params.d2 - params.d1) / 2;
  if (!wt || !forBevel) {
    return undefined;
  }
  const bevels = params.bevels;
  // if angle or length is 0 then length range may be 0-0
  if (bevels && bevels[forBevel] && (bevels[forBevel].angle === 0 || bevels[forBevel].l === 0)) {
    return {
      min: 0,
      max: 0
    };
  }
  const effLength = params.b1;
  const usableHorizontalLength = {
    min: uBEVEL_AXIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown((effLength - uBEVEL_AXIAL_MIN_DISTANCE) / 2)
  };
  if (bevels && bevels[forBevel] && bevels[forBevel].angle > 0) {
    const h = {
      min: uBEVEL_RADIAL_MIN_DISTANCE,
      max: bevelDistanceRoundDown((wt - uBEVEL_RADIAL_MIN_DISTANCE) / 2)
    };
    const tanAngle = Math.tan(bevels[forBevel].angle * Math.PI / 180);
    const usableLengthFromHorizontal = {
      min: bevelDistanceRoundUp(h.min / tanAngle),
      max: bevelDistanceRoundDown(h.max / tanAngle)
    };
    usableHorizontalLength.min = Math.max(usableHorizontalLength.min, usableLengthFromHorizontal.min);
    usableHorizontalLength.max = Math.min(usableHorizontalLength.max, usableLengthFromHorizontal.max);
  }
  return usableHorizontalLength;
};
/**
 * returns the angle range for given params and bevel
 * if calculation is not possible undefined is returned
 *
 * all calculations are in the unit system that is passed in params.unitSystem
 *
 * result is in ° to be interpreted as inclusive values
 */
const getSleeveBearingBevelAngleRange = (params, forBevel) => {
  // create unit system aware constants
  const uBEVEL_AXIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_AXIAL_MIN_DISTANCE);
  const uBEVEL_RADIAL_MIN_DISTANCE = fromMetricDistanceMilli(params.unitSystem, BEVEL_RADIAL_MIN_DISTANCE);
  const wt = (params.d2 - params.d1) / 2;
  if (!wt || !forBevel) {
    return undefined;
  }
  const bevels = params.bevels;
  // if angle or length is 0 then angle range may be 0-0
  if (bevels && bevels[forBevel] && (bevels[forBevel].angle === 0 || bevels[forBevel].l === 0)) {
    return {
      min: 0,
      max: 0
    };
  }
  const h = {
    min: uBEVEL_RADIAL_MIN_DISTANCE,
    max: bevelDistanceRoundDown(wt - uBEVEL_RADIAL_MIN_DISTANCE) / 2
  };
  if (bevels && bevels[forBevel] && bevels[forBevel].l > 0) {
    const angleRange = {
      min: bevelAngleRoundUp(Math.atan(h.min / bevels[forBevel].l) * 180 / Math.PI),
      max: bevelAngleRoundDown(Math.atan(h.max / bevels[forBevel].l) * 180 / Math.PI)
    };
    return angleRange;
  } else {
    const effLength = params.b1;
    const l = {
      min: uBEVEL_AXIAL_MIN_DISTANCE,
      max: bevelDistanceRoundDown((effLength - uBEVEL_AXIAL_MIN_DISTANCE) / 2)
    };
    const angleRange = {
      min: bevelAngleRoundUp(Math.atan(h.min / l.max) * 180 / Math.PI),
      max: bevelAngleRoundDown(Math.atan(h.max / l.min) * 180 / Math.PI)
    };
    return angleRange;
  }
};

const DIAMETER_FACTOR = 0.3;
const LENGTH_FACTOR = 0.3;
/**
 * Check if the two given thrustWashers have the same size.
 *
 * userParts can be in mm or inch.
 * catalog parts can be in mm or inch.
 */
const hasThrustWasherTheSameSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return userPartD1MM === catalogPartD1MM && userPartD2MM === catalogPartD2MM && userPartB1MM === catalogPartB1MM;
};
/**
 * Check if the two given thrustWashers have a similar size.
 */
const hasThrustWasherASimilarSize = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM >= userPartD2MM * (1 - DIAMETER_FACTOR) && catalogPartD2MM <= userPartD2MM * (1 + DIAMETER_FACTOR) && catalogPartB1MM >= userPartB1MM * (1 - LENGTH_FACTOR) && catalogPartB1MM <= userPartB1MM * (1 + LENGTH_FACTOR);
};
/**
 * Check if the catalogPart has a smaller size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.s1 can be up to 30% smaller than userPart.s1
 */
const hasThrustWasherASimilarSmallerSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasSmallerSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.s1 can be up to 30% greater than userPart.s1
 */
const hasThrustWasherASimilarGreaterSize = (userPart, catalogPart, searchDimension, customThresholdFactor) => {
  const dimensions = ['d1', 'd2', 'b1'];
  return hasGreaterSize(userPart, catalogPart, dimensions, searchDimension, customThresholdFactor);
};
/**
 * Check if the catalogPart has a greater size than the userPart for mechanical-reprocessing (MNA)
 *
 * To have a comparison that is similar to the old 'alphabetic' one we define the following rules
 *
 *  - letter prefix is the same (material, bearing-type(F,S,T) ,unit-system)
 *  - catalogPart.d1 === userPart.d1
 *  - catalogPart.d2 === userPart.d2
 *
 *  - catalogPart.b1 >= userPart.b1
 *
 *  With IGL-3360 we limit MNA to shortening of b1 only
 */
const hasThrustWasherAGreaterSizeForMNA = (userPart, catalogPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  const catalogPartD1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d1);
  const catalogPartD2MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.d2);
  const catalogPartB1MM = toMetricDistanceMilli(catalogPart.unitSystem, catalogPart.b1);
  return catalogPartD1MM === userPartD1MM && catalogPartD2MM === userPartD2MM && catalogPartB1MM >= userPartB1MM;
};
/**
 * Returns the wall thickness of a ThrustWasherParams.
 */
const getThrustWasherWallThickness = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  return round5(userPartD2MM - userPartD1MM) / 2;
};
/**
 * Returns the volume of the ThrustWasherParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperail distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getThrustWasherVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * userPartB1MM / 1000;
};
/**
 * Returns the BoundingBox of a ThrustWasher.
 * If forceMetric is true (default) the returned values are in mm, otherwise the result is in userPart.unitSystem.
 */
const getThrustWasherBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the projection area of the ThrustWasherParams
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 */
const getThrustWasherProjectionArea = userPart => {
  // (pi*d2²)/4-(pi*d1²)/4=Projected Area.
  return userPart.d2 * userPart.d2 * PI_QUARTER - userPart.d1 * userPart.d1 * PI_QUARTER;
};
/**
 * Returns the length of a ThrustWasher (b1)
 */
const getThrustWasherLength = userPart => {
  return userPart.b1;
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedThrustWasher = (userPart, shrinkage) => {
  const part = Object.assign({}, userPart);
  part.d1 = userPart.d1 * shrinkage;
  part.d2 = userPart.d2 * shrinkage;
  part.b1 = userPart.b1 * shrinkage;
  return part;
};
/**
 * Convert the thrustWasher parameters into a string with units.
 * The attributes in the string can be replaced by a translation when they are passed in the terms dictionary.
 */
const thrustWasherToString = userPart => {
  const unit = userPart.unitSystem === UnitSystem.metric ? 'mm' : 'in';
  const precision = userPart.unitSystem === UnitSystem.metric ? 2 : 4;
  const keyValueDelim = ': ';
  const paramDelim = '';
  return [formatValue('d1' + keyValueDelim, userPart.d1, unit, paramDelim, precision), formatValue('d2' + keyValueDelim, userPart.d2, unit, paramDelim, precision), formatValue('b1' + keyValueDelim, userPart.b1, unit, paramDelim, precision)].join(', ');
};

/**
 * Returns the length of a Tem (d2).
 */
const getTeLength = userPart => {
  return userPart.d2;
};
/**
 * Returns the BoundingBox of a Te.
 * All returned values are in mm.
 */
const getTeBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartH = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.h) : userPart.h;
  const userPartT = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.t) : userPart.t;
  return {
    h: userPartH + userPartT,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the volume of the TeParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperail distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getTeVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartHMM = toMetricDistanceMilli(userPart.unitSystem, userPart.h);
  const userPartTMM = toMetricDistanceMilli(userPart.unitSystem, userPart.t);
  const totalWidth = userPartHMM + userPartTMM;
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * totalWidth / 1000;
};

const defaultToleranceClass = 'E10';
/**
 * Returns the tolerance classes values for d1 and b1 depending on the materialCode
 */
const getToleranceClassByMaterial3D = materialCode => {
  const material3D = getMaterialsFor3D().find(material => material.materialCode === materialCode);
  return material3D == null ? void 0 : material3D.toleranceClass;
};
/**
 * Returns the tolerance value for d1 depending on the materialCode
 * see Documentation/IBD_Toleranzklassen.xlsx
 */
const getToleranceClassByMaterial = materialCode => {
  switch (materialCode) {
    case 'A200':
    case 'A250':
    case 'A290':
    case 'B':
    case 'C':
    case 'F':
    case 'M250':
    case 'Q290':
    case 'RN95':
      return 'D11';
    case 'A350':
    case 'A500':
    case 'AC500':
    case 'AX500':
    case 'C500':
    case 'G1':
    case 'H':
    case 'H1':
    case 'H2':
    case 'H370':
    case 'H4':
    case 'HSD350':
    case 'J350':
    case 'L350':
    case 'L500':
    case 'RW370':
    case 'UW500':
    case 'V400':
    case 'X':
    case 'X6':
    case 'Z':
    case 'RN213':
    case 'RN224':
    case 'RN240':
    case 'RN244':
    case 'RN265':
    case 'RN307':
    case 'RN310':
    case 'RN312':
    case 'RN313':
    case 'RN291 / HLW':
    case 'xirodur A500':
    case 'xirodur A500 regranulat':
      return 'F10';
    default:
      return defaultToleranceClass;
  }
};
/**
 * determine the toleranceRange by a given toleranceString
 *
 * examples are
 *  + 0.10
 *  - 0.10
 *  +/- 0.10    ± 0.10
 *  +0.1 / -0.2
 */
const getToleranceRangeByToleranceString = toleranceString => {
  let plusValue = 0;
  let minusValue = 0;
  // to ease float parsing turn , into .
  const ts = toleranceString.replace(/,/g, '.');
  // + 0.10
  const plusRegexp = /^\s*\+\s?(\d+(?:\.\d+)?)\s*$/;
  // - 0.10
  const minusRegexp = /^\s*-\s?(\d+(?:\.\d+)?)\s*$/;
  // +/- 0.10
  const plusMinusRegexp = /^\s*\+\s*\/\s*-\s?(\d+(?:\.\d+)?)\s*$/;
  // ± 0.10
  const plusMinusSignRegexp = /^\s*±\s?(\d+(?:\.\d+)?)\s*$/;
  // + 0.10 / -0.2
  const plusAndMinusRegexp = /^\s*\+\s?(\d+(?:\.\d+)?)\s*\/\s*-\s?(\d+(?:\.\d+)?)\s*$/;
  // + 0.10 / +0.30
  const plusAndPlusRegexp = /^\s*\+\s?(\d+(?:\.\d+)?)\s*\/\s*\+\s?(\d+(?:\.\d+)?)\s*$/;
  // - 0.10 / -0.30
  const minusAndMinusRegexp = /^\s*-\s?(\d+(?:\.\d+)?)\s*\/\s*-\s?(\d+(?:\.\d+)?)\s*$/;
  let re;
  if (re = ts.match(plusRegexp)) {
    plusValue = parseFloat(re[1]);
  } else if (re = ts.match(minusRegexp)) {
    minusValue = -parseFloat(re[1]);
  } else if (re = ts.match(plusMinusRegexp)) {
    plusValue = parseFloat(re[1]);
    minusValue = -plusValue;
  } else if (re = ts.match(plusMinusSignRegexp)) {
    plusValue = parseFloat(re[1]);
    minusValue = -plusValue;
  } else if (re = ts.match(plusAndMinusRegexp)) {
    plusValue = parseFloat(re[1]);
    minusValue = -parseFloat(re[2]);
  } else if (re = ts.match(plusAndPlusRegexp)) {
    minusValue = parseFloat(re[1]);
    plusValue = parseFloat(re[2]);
  } else if (re = ts.match(minusAndMinusRegexp)) {
    minusValue = -parseFloat(re[1]);
    plusValue = -parseFloat(re[2]);
  }
  return {
    upperValue: plusValue,
    lowerValue: Math.sign(minusValue) === 0 ? 0 : minusValue
  };
};
/**
 * Returns the tolerance of the outer diameter after assembly in bore housing and being displayed as `+0.005`
 * Values from IGL-3557
 */
const getToleranceAfterAssemblyInHousing = outerDiameter => {
  let tolerance;
  const inRange = (x, min, max) => x >= min && x <= max;
  if (inRange(outerDiameter, 0, 30)) {
    tolerance = 0.005;
  } else if (inRange(outerDiameter, 30.01, 50)) {
    tolerance = 0.008;
  } else if (inRange(outerDiameter, 50.01, 80)) {
    tolerance = 0.01;
  } else if (inRange(outerDiameter, 80.01, 120)) {
    tolerance = 0.012;
  } else if (inRange(outerDiameter, 120.01, 180)) {
    tolerance = 0.015;
  } else if (inRange(outerDiameter, 180.01, 250)) {
    tolerance = 0.018;
  } else {
    tolerance = 0.018;
  }
  return tolerance;
};
/**
 * attributesAreSetAndEqual() is intended to compare attributes of tolerance fields of bearings.
 *
 * the real comparison is only done when both sides have an usefull value in other caes the result is true
 *
 * @param a  tolerance attribute - part a
 * @param b  tolerance attribute - part b
 */
const attributesAreSetAndEqual = (a, b) => {
  if (a === undefined || b === undefined) {
    return true;
  }
  if (a === null || b === null) {
    return true;
  }
  if (a === '' || b === '') {
    return true;
  }
  // akeneo data sometimes comes with units
  const aWithoutUnit = a.replace(' mm', '');
  const bWithoutUnit = b.replace(' mm', '');
  const result = aWithoutUnit === bWithoutUnit;
  // I want to keep this for local debugging purposes, because I think I'll need to check tolerances more often now
  // if (!result) {
  //   console.log('Tolerance attributes differ : default/user tolerance =', a, ' akeneo/catalog tolerance=', b);
  // }
  return result;
};

const getDefaultTolerancesC = (userPart, _isPmFastLine) => {
  const tolerances = {
    d1: undefined,
    d2: undefined,
    d3: undefined,
    d4: undefined,
    b2d3: undefined,
    b2d4: undefined,
    b3: undefined,
    sb: undefined,
    slotAngle: undefined
  };
  tolerances.d3 = 'd13';
  tolerances.d4 = 'd13';
  tolerances.b2d3 = 'h13';
  tolerances.b2d4 = 'h13';
  tolerances.sb = '';
  tolerances.slotAngle = '';
  if (userPart.unitSystem === UnitSystem.metric) {
    tolerances.d1 = '+0,1/-0,0';
    tolerances.d2 = '+0/-0,2';
    tolerances.b3 = '+0,10/+0,30';
  } else {
    tolerances.d1 = '+0.0039/-0.0';
    tolerances.d2 = '+0/-0.0079';
    tolerances.b3 = '+0.0039/+0.0118';
  }
  return tolerances;
};
const hasBearingDefaultTolerancesC = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesC(userPart);
    return userPart.tolerances.b3 === defaultTolerances.b3 && userPart.tolerances.b2d3 === defaultTolerances.b2d3 && userPart.tolerances.b2d4 === defaultTolerances.b2d4 && userPart.tolerances.sb === defaultTolerances.sb && userPart.tolerances.slotAngle === defaultTolerances.slotAngle && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2 && userPart.tolerances.d3 === defaultTolerances.d3 && userPart.tolerances.d4 === defaultTolerances.d4;
  }
};
const hasBearingTheSameTolerancesC = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b3, userPartB.tolerances.b3) && attributesAreSetAndEqual(userPartA.tolerances.b2d3, userPartB.tolerances.b2d3) && attributesAreSetAndEqual(userPartA.tolerances.b2d4, userPartB.tolerances.b2d4) && attributesAreSetAndEqual(userPartA.tolerances.sb, userPartB.tolerances.sb) && attributesAreSetAndEqual(userPartA.tolerances.slotAngle, userPartB.tolerances.slotAngle) &&
    // 2025.05.07 Lars B. decided that we do not compare d1-Tolerances for C
    // attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) &&
    attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2) && attributesAreSetAndEqual(userPartA.tolerances.d3, userPartB.tolerances.d3) && attributesAreSetAndEqual(userPartA.tolerances.d4, userPartB.tolerances.d4);
  }
};

const getDefaultTolerancesEC = (userPart, isPmFastLine) => {
  const tol = {
    d1: undefined,
    d2: undefined,
    d3: undefined,
    b2: undefined,
    b3: undefined,
    b4: undefined,
    b5: undefined,
    sb: undefined,
    sl: undefined
  };
  tol.b3 = '';
  tol.b4 = '';
  tol.b5 = '';
  tol.b2 = 'h13';
  if (isPmFastLine) {
    tol.d1 = 'E10';
  } else {
    tol.d1 = getToleranceClassByMaterial(userPart.materialCode);
  }
  const d1 = userPart.d1;
  if (userPart.unitSystem === UnitSystem.metric) {
    if (d1 < 25) {
      tol.d2 = '+0/-0,2';
    } else {
      tol.d2 = '+0/-0,3';
    }
    tol.d3 = '+0,10/+0,30';
    if (d1 < 10) {
      tol.sb = '+/- 0,5';
    } else if (d1 < 25) {
      tol.sb = '+/- 0,8';
    } else {
      tol.sb = '+/- 1';
    }
    tol.sl = '+/- 1';
  } else {
    if (d1 < 25) {
      tol.d2 = '+0/-0.0079';
    } else {
      tol.d2 = '+0/-0.0118';
    }
    tol.d3 = '+0.0039/+0.0118';
    if (d1 < 10) {
      tol.sb = '+/- 0.0197';
    } else if (d1 < 25) {
      tol.sb = '+/- 0.0314';
    } else {
      tol.sb = '+/- 0.0394';
    }
    tol.sl = '+/- 0.0394';
  }
  return tol;
};
const hasBearingDefaultTolerancesEC = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesEC(userPart, isPmFastLine);
    return userPart.tolerances.b2 === defaultTolerances.b2 && userPart.tolerances.b3 === defaultTolerances.b3 && userPart.tolerances.b4 === defaultTolerances.b4 && userPart.tolerances.b5 === defaultTolerances.b5 && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2 && userPart.tolerances.d3 === defaultTolerances.d3 && userPart.tolerances.sb === defaultTolerances.sb && userPart.tolerances.sl === defaultTolerances.sl;
  }
};
const hasBearingTheSameTolerancesEC = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b2, userPartB.tolerances.b2) && attributesAreSetAndEqual(userPartA.tolerances.b3, userPartB.tolerances.b3) && attributesAreSetAndEqual(userPartA.tolerances.b4, userPartB.tolerances.b4) && attributesAreSetAndEqual(userPartA.tolerances.b5, userPartB.tolerances.b5) && attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) && attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2) && attributesAreSetAndEqual(userPartA.tolerances.d3, userPartB.tolerances.d3) && attributesAreSetAndEqual(userPartA.tolerances.sb, userPartB.tolerances.sb) && attributesAreSetAndEqual(userPartA.tolerances.sl, userPartB.tolerances.sl);
  }
};

const getDefaultTolerancesF = (userPart, isPmFastLine) => {
  const tol = {
    d1: undefined,
    d2: undefined,
    d3: undefined,
    b1: undefined,
    b2: undefined
  };
  tol.b1 = 'h13';
  tol.d2 = 'H7';
  tol.d3 = 'd13';
  if (isPmFastLine) {
    tol.d1 = 'E10';
  } else {
    tol.d1 = getToleranceClassByMaterial(userPart.materialCode);
  }
  if (userPart.unitSystem === UnitSystem.metric) {
    tol.b2 = '-0,14';
  } else {
    tol.b2 = '-0.0055';
  }
  return tol;
};
const hasBearingDefaultTolerancesF = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesF(userPart, isPmFastLine);
    return userPart.tolerances.b1 === defaultTolerances.b1 && userPart.tolerances.b2 === defaultTolerances.b2 && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2 && userPart.tolerances.d3 === defaultTolerances.d3;
  }
};
const hasBearingTheSameTolerancesF = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b1, userPartB.tolerances.b1) &&
    // 2025.05.07 Lars B. decided that we do not compare the b2-Tolerances for F
    //            (default is -0.14 and akeneo says h13)
    // attributesAreSetAndEqual(userPartA.tolerances.b2, userPartB.tolerances.b2) &&
    attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) && attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2) && attributesAreSetAndEqual(userPartA.tolerances.d3, userPartB.tolerances.d3);
  }
};

const getDefaultTolerancesPR = (userPart, _isPmFastLine) => {
  const tol = {
    d1: undefined,
    d2: undefined,
    b1: undefined,
    b2: undefined,
    slotAngle: undefined,
    wt: undefined
  };
  tol.d1 = '';
  tol.d2 = '';
  tol.slotAngle = '';
  tol.wt = '';
  tol.b1 = 'h13';
  if (userPart.unitSystem === UnitSystem.metric) {
    tol.b2 = '±0,5';
  } else {
    tol.b2 = '±0.0197';
  }
  return tol;
};
const hasBearingDefaultTolerancesPR = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesPR(userPart);
    return userPart.tolerances.b1 === defaultTolerances.b1 && userPart.tolerances.b2 === defaultTolerances.b2 && userPart.tolerances.wt === defaultTolerances.wt && userPart.tolerances.slotAngle === defaultTolerances.slotAngle && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2;
  }
};
const hasBearingTheSameTolerancesPR = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b1, userPartB.tolerances.b1) && attributesAreSetAndEqual(userPartA.tolerances.b2, userPartB.tolerances.b2) && attributesAreSetAndEqual(userPartA.tolerances.wt, userPartB.tolerances.wt) && attributesAreSetAndEqual(userPartA.tolerances.slotAngle, userPartB.tolerances.slotAngle) && attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) && attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2);
  }
};

const getDefaultTolerancesRL = (userPart, _isPmFastLine) => {
  const tolerances = {
    d1: undefined,
    d2: undefined,
    b1: undefined
  };
  if (userPart.unitSystem === UnitSystem.metric) {
    tolerances.d1 = '+0,1/+0,2';
    tolerances.d2 = '+/-0,1';
    tolerances.b1 = '-0,3';
  } else {
    tolerances.d1 = '+0.0039/+0,0078';
    tolerances.d2 = '+/-0.0039';
    tolerances.b1 = '+0/-0.0118';
  }
  return tolerances;
};
const hasBearingDefaultTolerancesRL = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesRL(userPart);
    return userPart.tolerances.b1 === defaultTolerances.b1 && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2;
  }
};
const hasBearingTheSameTolerancesRL = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b1, userPartB.tolerances.b1) &&
    // 2025.05.07 Lars B. decided that we do not compare d1-Tolerances for RL
    //  attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) &&
    attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2);
  }
};

const getDefaultTolerancesS = (userPart, isPmFastLine) => {
  const tol = {
    d1: undefined,
    d2: undefined,
    b1: undefined
  };
  tol.d2 = 'H7';
  if (isPmFastLine) {
    tol.d1 = 'E10';
    tol.b1 = 'h13';
  } else {
    tol.d1 = getToleranceClassByMaterial(userPart.materialCode);
    tol.b1 = 'h13';
  }
  return tol;
};
const hasBearingDefaultTolerancesS = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesS(userPart, isPmFastLine);
    return userPart.tolerances.b1 === defaultTolerances.b1 && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2;
  }
};
const hasBearingTheSameTolerancesS = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b1, userPartB.tolerances.b1) && attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) && attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2);
  }
};

const getDefaultTolerancesT = (userPart, isPmFastLine) => {
  const tol = {
    d1: undefined,
    d2: undefined,
    // aka s
    b1: undefined
  };
  const b1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  tol.b1 = 'h13';
  if (isPmFastLine) {
    tol.d1 = 'E10';
  } else {
    tol.d1 = getToleranceClassByMaterial(userPart.materialCode);
  }
  if (userPart.unitSystem === UnitSystem.metric) {
    tol.d1 = '+0,25';
    tol.d2 = '-0,25';
    if (isPmFastLine) {
      tol.b1 = b1MM < 3 ? '-0,1' : '±0,1';
    } else {
      tol.b1 = b1MM < 3 ? '-0,05' : '±0,05';
    }
  } else {
    tol.d1 = '+0.0098';
    tol.d2 = '-0.0098';
    if (isPmFastLine) {
      tol.b1 = b1MM < 3 ? '-0.0039' : '±0.0039';
    } else {
      tol.b1 = b1MM < 3 ? '-0.0020' : '±0.0020';
    }
  }
  return tol;
};
const hasBearingDefaultTolerancesT = (userPart, isPmFastLine) => {
  if (typeof userPart.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    const defaultTolerances = getDefaultTolerancesT(userPart, isPmFastLine);
    return userPart.tolerances.b1 === defaultTolerances.b1 && userPart.tolerances.d1 === defaultTolerances.d1 && userPart.tolerances.d2 === defaultTolerances.d2;
  }
};
const hasBearingTheSameTolerancesT = (userPartA, userPartB) => {
  if (typeof userPartA.tolerances === 'undefined' || typeof userPartB.tolerances === 'undefined') {
    // when the tolerances are not set, we consider them to be the defaults
    return true;
  } else {
    return attributesAreSetAndEqual(userPartA.tolerances.b1, userPartB.tolerances.b1) && attributesAreSetAndEqual(userPartA.tolerances.d1, userPartB.tolerances.d1) && attributesAreSetAndEqual(userPartA.tolerances.d2, userPartB.tolerances.d2);
  }
};

const getDefaultTolerances = (userPart, isPmFastLine) => {
  switch (userPart.form) {
    case 'S':
      return getDefaultTolerancesS(userPart, isPmFastLine);
    case 'F':
      return getDefaultTolerancesF(userPart, isPmFastLine);
    case 'T':
      return getDefaultTolerancesT(userPart, isPmFastLine);
    case 'PR':
      return getDefaultTolerancesPR(userPart);
    case 'EC':
      return getDefaultTolerancesEC(userPart, isPmFastLine);
    case 'C':
      return getDefaultTolerancesC(userPart);
    case 'RL':
      return getDefaultTolerancesRL(userPart);
    default:
      throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
const hasBearingDefaultTolerances = (userPart, isPmFastLine) => {
  switch (userPart.form) {
    case 'S':
      return hasBearingDefaultTolerancesS(userPart, isPmFastLine);
    case 'F':
      return hasBearingDefaultTolerancesF(userPart, isPmFastLine);
    case 'T':
      return hasBearingDefaultTolerancesT(userPart, isPmFastLine);
    case 'PR':
      return hasBearingDefaultTolerancesPR(userPart);
    case 'EC':
      return hasBearingDefaultTolerancesEC(userPart, isPmFastLine);
    case 'C':
      return hasBearingDefaultTolerancesC(userPart);
    case 'RL':
      return hasBearingDefaultTolerancesRL(userPart);
    case 'FSG':
    case 'FL':
    case 'VFL':
    case 'Y':
    case 'YK':
    case 'TE':
    case 'D':
    case 'VF':
    case 'K':
    case 'VS':
    default:
      throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
const hasBearingTheSameTolerances = (userPart, catalogPart) => {
  switch (userPart.form) {
    case 'S':
      return hasBearingTheSameTolerancesS(userPart, catalogPart);
    case 'F':
      return hasBearingTheSameTolerancesF(userPart, catalogPart);
    case 'T':
      return hasBearingTheSameTolerancesT(userPart, catalogPart);
    case 'PR':
      return hasBearingTheSameTolerancesPR(userPart, catalogPart);
    case 'EC':
      return hasBearingTheSameTolerancesEC(userPart, catalogPart);
    case 'C':
      return hasBearingTheSameTolerancesC(userPart, catalogPart);
    case 'RL':
      return hasBearingTheSameTolerancesRL(userPart, catalogPart);
    case 'FSG':
    case 'FL':
    case 'VFL':
    case 'Y':
    case 'YK':
    case 'TE':
    case 'D':
    case 'VF':
    case 'K':
    case 'VS':
    default:
      throw new Error(`Invalid form value ${userPart.form}!`);
  }
};

const holeLowerValues = [{
  from: 0,
  to: 3,
  A1: 270.0,
  A2: 270.0,
  A3: 270.0,
  A4: 270.0,
  A5: 270.0,
  A6: 270.0,
  A7: 270.0,
  A8: 270.0,
  A9: 270.0,
  A10: 270.0,
  A11: 270.0,
  A12: 270.0,
  A13: 270.0,
  A14: 270.0,
  A15: 270.0,
  A16: 270.0,
  A17: 270.0,
  A18: 270.0,
  B1: 140.0,
  B2: 140.0,
  B3: 140.0,
  B4: 140.0,
  B5: 140.0,
  B6: 140.0,
  B7: 140.0,
  B8: 140.0,
  B9: 140.0,
  B10: 140.0,
  B11: 140.0,
  B12: 140.0,
  B13: 140.0,
  B14: 140.0,
  B15: 140.0,
  B16: 140.0,
  B17: 140.0,
  B18: 140.0,
  C1: 60.0,
  C2: 60.0,
  C3: 60.0,
  C4: 60.0,
  C5: 60.0,
  C6: 60.0,
  C7: 60.0,
  C8: 60.0,
  C9: 60.0,
  C10: 60.0,
  C11: 60.0,
  C12: 60.0,
  C13: 60.0,
  C14: 60.0,
  C15: 60.0,
  C16: 60.0,
  C17: 60.0,
  C18: 60.0,
  D1: 20.0,
  D2: 20.0,
  D3: 20.0,
  D4: 20.0,
  D5: 20.0,
  D6: 20.0,
  D7: 20.0,
  D8: 20.0,
  D9: 20.0,
  D10: 20.0,
  D11: 20.0,
  D12: 20.0,
  D13: 20.0,
  D14: 20.0,
  D15: 20.0,
  D16: 20.0,
  D17: 20.0,
  D18: 20.0,
  E1: 14.0,
  E2: 14.0,
  E3: 14.0,
  E4: 14.0,
  E5: 14.0,
  E6: 14.0,
  E7: 14.0,
  E8: 14.0,
  E9: 14.0,
  E10: 14.0,
  E11: 14.0,
  E12: 14.0,
  E13: 14.0,
  E14: 14.0,
  E15: 14.0,
  E16: 14.0,
  E17: 14.0,
  E18: 14.0,
  F1: 6.0,
  F2: 6.0,
  F3: 6.0,
  F4: 6.0,
  F5: 6.0,
  F6: 6.0,
  F7: 6.0,
  F8: 6.0,
  F9: 6.0,
  F10: 6.0,
  F11: 6.0,
  F12: 6.0,
  F13: 6.0,
  F14: 6.0,
  F15: 6.0,
  F16: 6.0,
  F17: 6.0,
  F18: 6.0,
  G1: 2.0,
  G2: 2.0,
  G3: 2.0,
  G4: 2.0,
  G5: 2.0,
  G6: 2.0,
  G7: 2.0,
  G8: 2.0,
  G9: 2.0,
  G10: 2.0,
  G11: 2.0,
  G12: 2.0,
  G13: 2.0,
  G14: 2.0,
  G15: 2.0,
  G16: 2.0,
  G17: 2.0,
  G18: 2.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.4,
  JS2: -0.6,
  JS3: -1,
  JS4: -1.5,
  JS5: -2,
  JS6: -3,
  JS7: -5,
  JS8: -7,
  JS9: -12.5,
  JS10: -20,
  JS11: -30,
  JS12: -50,
  JS13: -70,
  JS14: -125,
  JS15: -200,
  JS16: -300,
  JS17: -500,
  JS18: -700,
  J6: -4,
  J7: -6,
  J8: -8,
  K1: -0.8,
  K2: -1.2,
  K3: -2,
  K4: -3,
  K5: -4,
  K6: -6,
  K7: -10,
  K8: -14,
  M1: -2.8,
  M2: -3.2,
  M3: -4,
  M4: -5,
  M5: -6,
  M6: -8,
  M7: -12,
  M8: -16,
  N1: -4.8,
  N2: -5.2,
  N3: -6,
  N4: -7,
  N5: -8,
  N6: -10,
  N7: -14,
  N8: -18,
  P1: -6.8,
  P2: -7.2,
  P3: -8,
  P4: -9,
  P5: -10,
  P6: -12,
  P7: -16,
  P8: -20,
  P9: -31,
  P10: -46,
  P11: -66,
  P12: -106,
  P13: -146,
  P14: -256,
  P15: -406,
  P16: -606,
  P17: -1006,
  P18: -1406
}, {
  from: 3,
  to: 6,
  A1: 270.0,
  A2: 270.0,
  A3: 270.0,
  A4: 270.0,
  A5: 270.0,
  A6: 270.0,
  A7: 270.0,
  A8: 270.0,
  A9: 270.0,
  A10: 270.0,
  A11: 270.0,
  A12: 270.0,
  A13: 270.0,
  A14: 270.0,
  A15: 270.0,
  A16: 270.0,
  A17: 270.0,
  A18: 270.0,
  B1: 140.0,
  B2: 140.0,
  B3: 140.0,
  B4: 140.0,
  B5: 140.0,
  B6: 140.0,
  B7: 140.0,
  B8: 140.0,
  B9: 140.0,
  B10: 140.0,
  B11: 140.0,
  B12: 140.0,
  B13: 140.0,
  B14: 140.0,
  B15: 140.0,
  B16: 140.0,
  B17: 140.0,
  B18: 140.0,
  C1: 70.0,
  C2: 70.0,
  C3: 70.0,
  C4: 70.0,
  C5: 70.0,
  C6: 70.0,
  C7: 70.0,
  C8: 70.0,
  C9: 70.0,
  C10: 70.0,
  C11: 70.0,
  C12: 70.0,
  C13: 70.0,
  C14: 70.0,
  C15: 70.0,
  C16: 70.0,
  C17: 70.0,
  C18: 70.0,
  D1: 30.0,
  D2: 30.0,
  D3: 30.0,
  D4: 30.0,
  D5: 30.0,
  D6: 30.0,
  D7: 30.0,
  D8: 30.0,
  D9: 30.0,
  D10: 30.0,
  D11: 30.0,
  D12: 30.0,
  D13: 30.0,
  D14: 30.0,
  D15: 30.0,
  D16: 30.0,
  D17: 30.0,
  D18: 30.0,
  E1: 20.0,
  E2: 20.0,
  E3: 20.0,
  E4: 20.0,
  E5: 20.0,
  E6: 20.0,
  E7: 20.0,
  E8: 20.0,
  E9: 20.0,
  E10: 20.0,
  E11: 20.0,
  E12: 20.0,
  E13: 20.0,
  E14: 20.0,
  E15: 20.0,
  E16: 20.0,
  E17: 20.0,
  E18: 20.0,
  F1: 10.0,
  F2: 10.0,
  F3: 10.0,
  F4: 10.0,
  F5: 10.0,
  F6: 10.0,
  F7: 10.0,
  F8: 10.0,
  F9: 10.0,
  F10: 10.0,
  F11: 10.0,
  F12: 10.0,
  F13: 10.0,
  F14: 10.0,
  F15: 10.0,
  F16: 10.0,
  F17: 10.0,
  F18: 10.0,
  G1: 4.0,
  G2: 4.0,
  G3: 4.0,
  G4: 4.0,
  G5: 4.0,
  G6: 4.0,
  G7: 4.0,
  G8: 4.0,
  G9: 4.0,
  G10: 4.0,
  G11: 4.0,
  G12: 4.0,
  G13: 4.0,
  G14: 4.0,
  G15: 4.0,
  G16: 4.0,
  G17: 4.0,
  G18: 4.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.5,
  JS2: -0.8,
  JS3: -1.2,
  JS4: -2,
  JS5: -2.5,
  JS6: -4,
  JS7: -6,
  JS8: -9,
  JS9: -15,
  JS10: -24,
  JS11: -37.5,
  JS12: -60,
  JS13: -90,
  JS14: -150,
  JS15: -240,
  JS16: -375,
  JS17: -600,
  JS18: -900,
  J6: -3,
  J7: -6,
  J8: -8,
  K1: -2,
  K2: -2.5,
  K3: -2.5,
  K4: -3.5,
  K5: -5,
  K6: -6,
  K7: -9,
  K8: -13,
  M1: -5,
  M2: -5.5,
  M3: -5.5,
  M4: -6.5,
  M5: -8,
  M6: -9,
  M7: -12,
  M8: -16,
  N1: -9,
  N2: -9.5,
  N3: -9.5,
  N4: -10.5,
  N5: -12,
  N6: -13,
  N7: -16,
  N8: -20,
  P1: -13,
  P2: -13.5,
  P3: -13.5,
  P4: -14.5,
  P5: -16,
  P6: -17,
  P7: -20,
  P8: -30,
  P9: -42,
  P10: -60,
  P11: -87,
  P12: -132,
  P13: -192,
  P14: -312,
  P15: -492,
  P16: -762,
  P17: -1212,
  P18: -1812
}, {
  from: 6,
  to: 10,
  A1: 280.0,
  A2: 280.0,
  A3: 280.0,
  A4: 280.0,
  A5: 280.0,
  A6: 280.0,
  A7: 280.0,
  A8: 280.0,
  A9: 280.0,
  A10: 280.0,
  A11: 280.0,
  A12: 280.0,
  A13: 280.0,
  A14: 280.0,
  A15: 280.0,
  A16: 280.0,
  A17: 280.0,
  A18: 280.0,
  B1: 150.0,
  B2: 150.0,
  B3: 150.0,
  B4: 150.0,
  B5: 150.0,
  B6: 150.0,
  B7: 150.0,
  B8: 150.0,
  B9: 150.0,
  B10: 150.0,
  B11: 150.0,
  B12: 150.0,
  B13: 150.0,
  B14: 150.0,
  B15: 150.0,
  B16: 150.0,
  B17: 150.0,
  B18: 150.0,
  C1: 80.0,
  C2: 80.0,
  C3: 80.0,
  C4: 80.0,
  C5: 80.0,
  C6: 80.0,
  C7: 80.0,
  C8: 80.0,
  C9: 80.0,
  C10: 80.0,
  C11: 80.0,
  C12: 80.0,
  C13: 80.0,
  C14: 80.0,
  C15: 80.0,
  C16: 80.0,
  C17: 80.0,
  C18: 80.0,
  D1: 40.0,
  D2: 40.0,
  D3: 40.0,
  D4: 40.0,
  D5: 40.0,
  D6: 40.0,
  D7: 40.0,
  D8: 40.0,
  D9: 40.0,
  D10: 40.0,
  D11: 40.0,
  D12: 40.0,
  D13: 40.0,
  D14: 40.0,
  D15: 40.0,
  D16: 40.0,
  D17: 40.0,
  D18: 40.0,
  E1: 25.0,
  E2: 25.0,
  E3: 25.0,
  E4: 25.0,
  E5: 25.0,
  E6: 25.0,
  E7: 25.0,
  E8: 25.0,
  E9: 25.0,
  E10: 25.0,
  E11: 25.0,
  E12: 25.0,
  E13: 25.0,
  E14: 25.0,
  E15: 25.0,
  E16: 25.0,
  E17: 25.0,
  E18: 25.0,
  F1: 13.0,
  F2: 13.0,
  F3: 13.0,
  F4: 13.0,
  F5: 13.0,
  F6: 13.0,
  F7: 13.0,
  F8: 13.0,
  F9: 13.0,
  F10: 13.0,
  F11: 13.0,
  F12: 13.0,
  F13: 13.0,
  F14: 13.0,
  F15: 13.0,
  F16: 13.0,
  F17: 13.0,
  F18: 13.0,
  G1: 5.0,
  G2: 5.0,
  G3: 5.0,
  G4: 5.0,
  G5: 5.0,
  G6: 5.0,
  G7: 5.0,
  G8: 5.0,
  G9: 5.0,
  G10: 5.0,
  G11: 5.0,
  G12: 5.0,
  G13: 5.0,
  G14: 5.0,
  G15: 5.0,
  G16: 5.0,
  G17: 5.0,
  G18: 5.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.5,
  JS2: -0.8,
  JS3: -1.3,
  JS4: -2,
  JS5: -3,
  JS6: -4.5,
  JS7: -7.5,
  JS8: -11,
  JS9: -18,
  JS10: -29,
  JS11: -45,
  JS12: -75,
  JS13: -110,
  JS14: -180,
  JS15: -290,
  JS16: -450,
  JS17: -750,
  JS18: -1100,
  J6: -4,
  J7: -7,
  J8: -10,
  K1: -2,
  K2: -2.5,
  K3: -2.5,
  K4: -3.5,
  K5: -5,
  K6: -7,
  K7: -10,
  K8: -16,
  M1: -7,
  M2: -7.5,
  M3: -7.5,
  M4: -8.5,
  M5: -10,
  M6: -12,
  M7: -15,
  M8: -21,
  N1: -11,
  N2: -11.5,
  N3: -11.5,
  N4: -12.5,
  N5: -14,
  N6: -16,
  N7: -19,
  N8: -25,
  P1: -16,
  P2: -16.5,
  P3: -16.5,
  P4: -17.5,
  P5: -19,
  P6: -21,
  P7: -24,
  P8: -37,
  P9: -51,
  P10: -73,
  P11: -105,
  P12: -165,
  P13: -235,
  P14: -375,
  P15: -595,
  P16: -915,
  P17: -1515,
  P18: -2215
}, {
  from: 10,
  to: 18,
  A1: 290.0,
  A2: 290.0,
  A3: 290.0,
  A4: 290.0,
  A5: 290.0,
  A6: 290.0,
  A7: 290.0,
  A8: 290.0,
  A9: 290.0,
  A10: 290.0,
  A11: 290.0,
  A12: 290.0,
  A13: 290.0,
  A14: 290.0,
  A15: 290.0,
  A16: 290.0,
  A17: 290.0,
  A18: 290.0,
  B1: 150.0,
  B2: 150.0,
  B3: 150.0,
  B4: 150.0,
  B5: 150.0,
  B6: 150.0,
  B7: 150.0,
  B8: 150.0,
  B9: 150.0,
  B10: 150.0,
  B11: 150.0,
  B12: 150.0,
  B13: 150.0,
  B14: 150.0,
  B15: 150.0,
  B16: 150.0,
  B17: 150.0,
  B18: 150.0,
  C1: 95.0,
  C2: 95.0,
  C3: 95.0,
  C4: 95.0,
  C5: 95.0,
  C6: 95.0,
  C7: 95.0,
  C8: 95.0,
  C9: 95.0,
  C10: 95.0,
  C11: 95.0,
  C12: 95.0,
  C13: 95.0,
  C14: 95.0,
  C15: 95.0,
  C16: 95.0,
  C17: 95.0,
  C18: 95.0,
  D1: 50.0,
  D2: 50.0,
  D3: 50.0,
  D4: 50.0,
  D5: 50.0,
  D6: 50.0,
  D7: 50.0,
  D8: 50.0,
  D9: 50.0,
  D10: 50.0,
  D11: 50.0,
  D12: 50.0,
  D13: 50.0,
  D14: 50.0,
  D15: 50.0,
  D16: 50.0,
  D17: 50.0,
  D18: 50.0,
  E1: 32.0,
  E2: 32.0,
  E3: 32.0,
  E4: 32.0,
  E5: 32.0,
  E6: 32.0,
  E7: 32.0,
  E8: 32.0,
  E9: 32.0,
  E10: 32.0,
  E11: 32.0,
  E12: 32.0,
  E13: 32.0,
  E14: 32.0,
  E15: 32.0,
  E16: 32.0,
  E17: 32.0,
  E18: 32.0,
  F1: 16.0,
  F2: 16.0,
  F3: 16.0,
  F4: 16.0,
  F5: 16.0,
  F6: 16.0,
  F7: 16.0,
  F8: 16.0,
  F9: 16.0,
  F10: 16.0,
  F11: 16.0,
  F12: 16.0,
  F13: 16.0,
  F14: 16.0,
  F15: 16.0,
  F16: 16.0,
  F17: 16.0,
  F18: 16.0,
  G1: 6.0,
  G2: 6.0,
  G3: 6.0,
  G4: 6.0,
  G5: 6.0,
  G6: 6.0,
  G7: 6.0,
  G8: 6.0,
  G9: 6.0,
  G10: 6.0,
  G11: 6.0,
  G12: 6.0,
  G13: 6.0,
  G14: 6.0,
  G15: 6.0,
  G16: 6.0,
  G17: 6.0,
  G18: 6.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.6,
  JS2: -1,
  JS3: -1.5,
  JS4: -2.5,
  JS5: -4,
  JS6: -5.5,
  JS7: -9,
  JS8: -13.5,
  JS9: -21.5,
  JS10: -35,
  JS11: -55,
  JS12: -90,
  JS13: -135,
  JS14: -215,
  JS15: -350,
  JS16: -550,
  JS17: -900,
  JS18: -1350,
  J6: -5,
  J7: -8,
  J8: -12,
  K1: -2.2,
  K2: -3,
  K3: -3,
  K4: -4,
  K5: -6,
  K6: -9,
  K7: -12,
  K8: -19,
  M1: -8.2,
  M2: -9,
  M3: -9,
  M4: -10,
  M5: -12,
  M6: -15,
  M7: -18,
  M8: -25,
  N1: -13.2,
  N2: -14,
  N3: -14,
  N4: -15,
  N5: -17,
  N6: -20,
  N7: -23,
  N8: -30,
  P1: -19.2,
  P2: -20,
  P3: -20,
  P4: -21,
  P5: -23,
  P6: -26,
  P7: -29,
  P8: -45,
  P9: -61,
  P10: -88,
  P11: -128,
  P12: -198,
  P13: -288,
  P14: -448,
  P15: -718,
  P16: -1118,
  P17: -1818,
  P18: -2718
}, {
  from: 18,
  to: 30,
  A1: 300.0,
  A2: 300.0,
  A3: 300.0,
  A4: 300.0,
  A5: 300.0,
  A6: 300.0,
  A7: 300.0,
  A8: 300.0,
  A9: 300.0,
  A10: 300.0,
  A11: 300.0,
  A12: 300.0,
  A13: 300.0,
  A14: 300.0,
  A15: 300.0,
  A16: 300.0,
  A17: 300.0,
  A18: 300.0,
  B1: 160.0,
  B2: 160.0,
  B3: 160.0,
  B4: 160.0,
  B5: 160.0,
  B6: 160.0,
  B7: 160.0,
  B8: 160.0,
  B9: 160.0,
  B10: 160.0,
  B11: 160.0,
  B12: 160.0,
  B13: 160.0,
  B14: 160.0,
  B15: 160.0,
  B16: 160.0,
  B17: 160.0,
  B18: 160.0,
  C1: 110.0,
  C2: 110.0,
  C3: 110.0,
  C4: 110.0,
  C5: 110.0,
  C6: 110.0,
  C7: 110.0,
  C8: 110.0,
  C9: 110.0,
  C10: 110.0,
  C11: 110.0,
  C12: 110.0,
  C13: 110.0,
  C14: 110.0,
  C15: 110.0,
  C16: 110.0,
  C17: 110.0,
  C18: 110.0,
  D1: 65.0,
  D2: 65.0,
  D3: 65.0,
  D4: 65.0,
  D5: 65.0,
  D6: 65.0,
  D7: 65.0,
  D8: 65.0,
  D9: 65.0,
  D10: 65.0,
  D11: 65.0,
  D12: 65.0,
  D13: 65.0,
  D14: 65.0,
  D15: 65.0,
  D16: 65.0,
  D17: 65.0,
  D18: 65.0,
  E1: 40.0,
  E2: 40.0,
  E3: 40.0,
  E4: 40.0,
  E5: 40.0,
  E6: 40.0,
  E7: 40.0,
  E8: 40.0,
  E9: 40.0,
  E10: 40.0,
  E11: 40.0,
  E12: 40.0,
  E13: 40.0,
  E14: 40.0,
  E15: 40.0,
  E16: 40.0,
  E17: 40.0,
  E18: 40.0,
  F1: 20.0,
  F2: 20.0,
  F3: 20.0,
  F4: 20.0,
  F5: 20.0,
  F6: 20.0,
  F7: 20.0,
  F8: 20.0,
  F9: 20.0,
  F10: 20.0,
  F11: 20.0,
  F12: 20.0,
  F13: 20.0,
  F14: 20.0,
  F15: 20.0,
  F16: 20.0,
  F17: 20.0,
  F18: 20.0,
  G1: 7.0,
  G2: 7.0,
  G3: 7.0,
  G4: 7.0,
  G5: 7.0,
  G6: 7.0,
  G7: 7.0,
  G8: 7.0,
  G9: 7.0,
  G10: 7.0,
  G11: 7.0,
  G12: 7.0,
  G13: 7.0,
  G14: 7.0,
  G15: 7.0,
  G16: 7.0,
  G17: 7.0,
  G18: 7.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.8,
  JS2: -1.2,
  JS3: -2,
  JS4: -3,
  JS5: -4.5,
  JS6: -6.5,
  JS7: -10.5,
  JS8: -16.5,
  JS9: -26,
  JS10: -42,
  JS11: -65,
  JS12: -105,
  JS13: -165,
  JS14: -260,
  JS15: -420,
  JS16: -650,
  JS17: -1050,
  JS18: -1650,
  J6: -5,
  J7: -9,
  J8: -13,
  K1: -3.5,
  K2: -4.5,
  K3: -4.5,
  K4: -6,
  K5: -8,
  K6: -11,
  K7: -15,
  K8: -23,
  M1: -9.5,
  M2: -10.5,
  M3: -10.5,
  M4: -12,
  M5: -14,
  M6: -17,
  M7: -21,
  M8: -29,
  N1: -16.5,
  N2: -17.5,
  N3: -17.5,
  N4: -19,
  N5: -21,
  N6: -24,
  N7: -28,
  N8: -36,
  P1: -23.5,
  P2: -24.5,
  P3: -24.5,
  P4: -26,
  P5: -28,
  P6: -31,
  P7: -35,
  P8: -55,
  P9: -74,
  P10: -106,
  P11: -152,
  P12: -232,
  P13: -352,
  P14: -542,
  P15: -862,
  P16: -1322,
  P17: -2122,
  P18: -3322
}, {
  from: 30,
  to: 40,
  A1: 310.0,
  A2: 310.0,
  A3: 310.0,
  A4: 310.0,
  A5: 310.0,
  A6: 310.0,
  A7: 310.0,
  A8: 310.0,
  A9: 310.0,
  A10: 310.0,
  A11: 310.0,
  A12: 310.0,
  A13: 310.0,
  A14: 310.0,
  A15: 310.0,
  A16: 310.0,
  A17: 310.0,
  A18: 310.0,
  B1: 170.0,
  B2: 170.0,
  B3: 170.0,
  B4: 170.0,
  B5: 170.0,
  B6: 170.0,
  B7: 170.0,
  B8: 170.0,
  B9: 170.0,
  B10: 170.0,
  B11: 170.0,
  B12: 170.0,
  B13: 170.0,
  B14: 170.0,
  B15: 170.0,
  B16: 170.0,
  B17: 170.0,
  B18: 170.0,
  C1: 120.0,
  C2: 120.0,
  C3: 120.0,
  C4: 120.0,
  C5: 120.0,
  C6: 120.0,
  C7: 120.0,
  C8: 120.0,
  C9: 120.0,
  C10: 120.0,
  C11: 120.0,
  C12: 120.0,
  C13: 120.0,
  C14: 120.0,
  C15: 120.0,
  C16: 120.0,
  C17: 120.0,
  C18: 120.0,
  D1: 80.0,
  D2: 80.0,
  D3: 80.0,
  D4: 80.0,
  D5: 80.0,
  D6: 80.0,
  D7: 80.0,
  D8: 80.0,
  D9: 80.0,
  D10: 80.0,
  D11: 80.0,
  D12: 80.0,
  D13: 80.0,
  D14: 80.0,
  D15: 80.0,
  D16: 80.0,
  D17: 80.0,
  D18: 80.0,
  E1: 50.0,
  E2: 50.0,
  E3: 50.0,
  E4: 50.0,
  E5: 50.0,
  E6: 50.0,
  E7: 50.0,
  E8: 50.0,
  E9: 50.0,
  E10: 50.0,
  E11: 50.0,
  E12: 50.0,
  E13: 50.0,
  E14: 50.0,
  E15: 50.0,
  E16: 50.0,
  E17: 50.0,
  E18: 50.0,
  F1: 25.0,
  F2: 25.0,
  F3: 25.0,
  F4: 25.0,
  F5: 25.0,
  F6: 25.0,
  F7: 25.0,
  F8: 25.0,
  F9: 25.0,
  F10: 25.0,
  F11: 25.0,
  F12: 25.0,
  F13: 25.0,
  F14: 25.0,
  F15: 25.0,
  F16: 25.0,
  F17: 25.0,
  F18: 25.0,
  G1: 9.0,
  G2: 9.0,
  G3: 9.0,
  G4: 9.0,
  G5: 9.0,
  G6: 9.0,
  G7: 9.0,
  G8: 9.0,
  G9: 9.0,
  G10: 9.0,
  G11: 9.0,
  G12: 9.0,
  G13: 9.0,
  G14: 9.0,
  G15: 9.0,
  G16: 9.0,
  G17: 9.0,
  G18: 9.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.7,
  JS2: -1.2,
  JS3: -2,
  JS4: -3.5,
  JS5: -5.5,
  JS6: -8,
  JS7: -12.5,
  JS8: -19.5,
  JS9: -31,
  JS10: -50,
  JS11: -80,
  JS12: -125,
  JS13: -195,
  JS14: -310,
  JS15: -500,
  JS16: -800,
  JS17: -1250,
  JS18: -1950,
  J6: -6,
  J7: -11,
  J8: -15,
  K1: -3.5,
  K2: -4.5,
  K3: -4.5,
  K4: -6,
  K5: -9,
  K6: -13,
  K7: -18,
  K8: -27,
  M1: -10.5,
  M2: -11.5,
  M3: -11.5,
  M4: -13,
  M5: -16,
  M6: -20,
  M7: -25,
  M8: -34,
  N1: -18.5,
  N2: -19.5,
  N3: -19.5,
  N4: -21,
  N5: -24,
  N6: -28,
  N7: -33,
  N8: -42,
  P1: -27.5,
  P2: -28.5,
  P3: -28.5,
  P4: -30,
  P5: -33,
  P6: -37,
  P7: -42,
  P8: -65,
  P9: -88,
  P10: -126,
  P11: -186,
  P12: -276,
  P13: -416,
  P14: -646,
  P15: -1026,
  P16: -1626,
  P17: -2526,
  P18: -3926
}, {
  from: 40,
  to: 50,
  A1: 320.0,
  A2: 320.0,
  A3: 320.0,
  A4: 320.0,
  A5: 320.0,
  A6: 320.0,
  A7: 320.0,
  A8: 320.0,
  A9: 320.0,
  A10: 320.0,
  A11: 320.0,
  A12: 320.0,
  A13: 320.0,
  A14: 320.0,
  A15: 320.0,
  A16: 320.0,
  A17: 320.0,
  A18: 320.0,
  B1: 180.0,
  B2: 180.0,
  B3: 180.0,
  B4: 180.0,
  B5: 180.0,
  B6: 180.0,
  B7: 180.0,
  B8: 180.0,
  B9: 180.0,
  B10: 180.0,
  B11: 180.0,
  B12: 180.0,
  B13: 180.0,
  B14: 180.0,
  B15: 180.0,
  B16: 180.0,
  B17: 180.0,
  B18: 180.0,
  C1: 130.0,
  C2: 130.0,
  C3: 130.0,
  C4: 130.0,
  C5: 130.0,
  C6: 130.0,
  C7: 130.0,
  C8: 130.0,
  C9: 130.0,
  C10: 130.0,
  C11: 130.0,
  C12: 130.0,
  C13: 130.0,
  C14: 130.0,
  C15: 130.0,
  C16: 130.0,
  C17: 130.0,
  C18: 130.0,
  D1: 80.0,
  D2: 80.0,
  D3: 80.0,
  D4: 80.0,
  D5: 80.0,
  D6: 80.0,
  D7: 80.0,
  D8: 80.0,
  D9: 80.0,
  D10: 80.0,
  D11: 80.0,
  D12: 80.0,
  D13: 80.0,
  D14: 80.0,
  D15: 80.0,
  D16: 80.0,
  D17: 80.0,
  D18: 80.0,
  E1: 50.0,
  E2: 50.0,
  E3: 50.0,
  E4: 50.0,
  E5: 50.0,
  E6: 50.0,
  E7: 50.0,
  E8: 50.0,
  E9: 50.0,
  E10: 50.0,
  E11: 50.0,
  E12: 50.0,
  E13: 50.0,
  E14: 50.0,
  E15: 50.0,
  E16: 50.0,
  E17: 50.0,
  E18: 50.0,
  F1: 25.0,
  F2: 25.0,
  F3: 25.0,
  F4: 25.0,
  F5: 25.0,
  F6: 25.0,
  F7: 25.0,
  F8: 25.0,
  F9: 25.0,
  F10: 25.0,
  F11: 25.0,
  F12: 25.0,
  F13: 25.0,
  F14: 25.0,
  F15: 25.0,
  F16: 25.0,
  F17: 25.0,
  F18: 25.0,
  G1: 9.0,
  G2: 9.0,
  G3: 9.0,
  G4: 9.0,
  G5: 9.0,
  G6: 9.0,
  G7: 9.0,
  G8: 9.0,
  G9: 9.0,
  G10: 9.0,
  G11: 9.0,
  G12: 9.0,
  G13: 9.0,
  G14: 9.0,
  G15: 9.0,
  G16: 9.0,
  G17: 9.0,
  G18: 9.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -0.7,
  JS2: -1.2,
  JS3: -2,
  JS4: -3.5,
  JS5: -5.5,
  JS6: -8,
  JS7: -12.5,
  JS8: -19.5,
  JS9: -31,
  JS10: -50,
  JS11: -80,
  JS12: -125,
  JS13: -195,
  JS14: -310,
  JS15: -500,
  JS16: -800,
  JS17: -1250,
  JS18: -1950,
  J6: -6,
  J7: -11,
  J8: -15,
  K1: -3.5,
  K2: -4.5,
  K3: -4.5,
  K4: -6,
  K5: -9,
  K6: -13,
  K7: -18,
  K8: -27,
  M1: -10.5,
  M2: -11.5,
  M3: -11.5,
  M4: -13,
  M5: -16,
  M6: -20,
  M7: -25,
  M8: -34,
  N1: -18.5,
  N2: -19.5,
  N3: -19.5,
  N4: -21,
  N5: -24,
  N6: -28,
  N7: -33,
  N8: -42,
  P1: -27.5,
  P2: -28.5,
  P3: -28.5,
  P4: -30,
  P5: -33,
  P6: -37,
  P7: -42,
  P8: -65,
  P9: -88,
  P10: -126,
  P11: -186,
  P12: -276,
  P13: -416,
  P14: -646,
  P15: -1026,
  P16: -1626,
  P17: -2526,
  P18: -3926
}, {
  from: 50,
  to: 65,
  A1: 340.0,
  A2: 340.0,
  A3: 340.0,
  A4: 340.0,
  A5: 340.0,
  A6: 340.0,
  A7: 340.0,
  A8: 340.0,
  A9: 340.0,
  A10: 340.0,
  A11: 340.0,
  A12: 340.0,
  A13: 340.0,
  A14: 340.0,
  A15: 340.0,
  A16: 340.0,
  A17: 340.0,
  A18: 340.0,
  B1: 190.0,
  B2: 190.0,
  B3: 190.0,
  B4: 190.0,
  B5: 190.0,
  B6: 190.0,
  B7: 190.0,
  B8: 190.0,
  B9: 190.0,
  B10: 190.0,
  B11: 190.0,
  B12: 190.0,
  B13: 190.0,
  B14: 190.0,
  B15: 190.0,
  B16: 190.0,
  B17: 190.0,
  B18: 190.0,
  C1: 140.0,
  C2: 140.0,
  C3: 140.0,
  C4: 140.0,
  C5: 140.0,
  C6: 140.0,
  C7: 140.0,
  C8: 140.0,
  C9: 140.0,
  C10: 140.0,
  C11: 140.0,
  C12: 140.0,
  C13: 140.0,
  C14: 140.0,
  C15: 140.0,
  C16: 140.0,
  C17: 140.0,
  C18: 140.0,
  D1: 100.0,
  D2: 100.0,
  D3: 100.0,
  D4: 100.0,
  D5: 100.0,
  D6: 100.0,
  D7: 100.0,
  D8: 100.0,
  D9: 100.0,
  D10: 100.0,
  D11: 100.0,
  D12: 100.0,
  D13: 100.0,
  D14: 100.0,
  D15: 100.0,
  D16: 100.0,
  D17: 100.0,
  D18: 100.0,
  E1: 60.0,
  E2: 60.0,
  E3: 60.0,
  E4: 60.0,
  E5: 60.0,
  E6: 60.0,
  E7: 60.0,
  E8: 60.0,
  E9: 60.0,
  E10: 60.0,
  E11: 60.0,
  E12: 60.0,
  E13: 60.0,
  E14: 60.0,
  E15: 60.0,
  E16: 60.0,
  E17: 60.0,
  E18: 60.0,
  F1: 30.0,
  F2: 30.0,
  F3: 30.0,
  F4: 30.0,
  F5: 30.0,
  F6: 30.0,
  F7: 30.0,
  F8: 30.0,
  F9: 30.0,
  F10: 30.0,
  F11: 30.0,
  F12: 30.0,
  F13: 30.0,
  F14: 30.0,
  F15: 30.0,
  F16: 30.0,
  F17: 30.0,
  F18: 30.0,
  G1: 10.0,
  G2: 10.0,
  G3: 10.0,
  G4: 10.0,
  G5: 10.0,
  G6: 10.0,
  G7: 10.0,
  G8: 10.0,
  G9: 10.0,
  G10: 10.0,
  G11: 10.0,
  G12: 10.0,
  G13: 10.0,
  G14: 10.0,
  G15: 10.0,
  G16: 10.0,
  G17: 10.0,
  G18: 10.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1,
  JS2: -1.5,
  JS3: -2.5,
  JS4: -4,
  JS5: -6.5,
  JS6: -9.5,
  JS7: -15,
  JS8: -23,
  JS9: -37,
  JS10: -60,
  JS11: -95,
  JS12: -150,
  JS13: -230,
  JS14: -370,
  JS15: -600,
  JS16: -950,
  JS17: -1500,
  JS18: -2300,
  J6: -6,
  J7: -12,
  J8: -18,
  K1: -4,
  K2: -5,
  K3: -5,
  K4: -7,
  K5: -10,
  K6: -15,
  K7: -21,
  K8: -32,
  M1: -13,
  M2: -14,
  M3: -14,
  M4: -16,
  M5: -19,
  M6: -24,
  M7: -30,
  M8: -41,
  N1: -22,
  N2: -23,
  N3: -23,
  N4: -25,
  N5: -28,
  N6: -33,
  N7: -39,
  N8: -50,
  P1: -34,
  P2: -35,
  P3: -35,
  P4: -37,
  P5: -40,
  P6: -45,
  P7: -51,
  P8: -78,
  P9: -106,
  P10: -152,
  P11: -222,
  P12: -332,
  P13: -492,
  P14: -772,
  P15: -1232,
  P16: -1932,
  P17: -3032,
  P18: -4632
}, {
  from: 65,
  to: 80,
  A1: 360.0,
  A2: 360.0,
  A3: 360.0,
  A4: 360.0,
  A5: 360.0,
  A6: 360.0,
  A7: 360.0,
  A8: 360.0,
  A9: 360.0,
  A10: 360.0,
  A11: 360.0,
  A12: 360.0,
  A13: 360.0,
  A14: 360.0,
  A15: 360.0,
  A16: 360.0,
  A17: 360.0,
  A18: 360.0,
  B1: 200.0,
  B2: 200.0,
  B3: 200.0,
  B4: 200.0,
  B5: 200.0,
  B6: 200.0,
  B7: 200.0,
  B8: 200.0,
  B9: 200.0,
  B10: 200.0,
  B11: 200.0,
  B12: 200.0,
  B13: 200.0,
  B14: 200.0,
  B15: 200.0,
  B16: 200.0,
  B17: 200.0,
  B18: 200.0,
  C1: 150.0,
  C2: 150.0,
  C3: 150.0,
  C4: 150.0,
  C5: 150.0,
  C6: 150.0,
  C7: 150.0,
  C8: 150.0,
  C9: 150.0,
  C10: 150.0,
  C11: 150.0,
  C12: 150.0,
  C13: 150.0,
  C14: 150.0,
  C15: 150.0,
  C16: 150.0,
  C17: 150.0,
  C18: 150.0,
  D1: 100.0,
  D2: 100.0,
  D3: 100.0,
  D4: 100.0,
  D5: 100.0,
  D6: 100.0,
  D7: 100.0,
  D8: 100.0,
  D9: 100.0,
  D10: 100.0,
  D11: 100.0,
  D12: 100.0,
  D13: 100.0,
  D14: 100.0,
  D15: 100.0,
  D16: 100.0,
  D17: 100.0,
  D18: 100.0,
  E1: 60.0,
  E2: 60.0,
  E3: 60.0,
  E4: 60.0,
  E5: 60.0,
  E6: 60.0,
  E7: 60.0,
  E8: 60.0,
  E9: 60.0,
  E10: 60.0,
  E11: 60.0,
  E12: 60.0,
  E13: 60.0,
  E14: 60.0,
  E15: 60.0,
  E16: 60.0,
  E17: 60.0,
  E18: 60.0,
  F1: 30.0,
  F2: 30.0,
  F3: 30.0,
  F4: 30.0,
  F5: 30.0,
  F6: 30.0,
  F7: 30.0,
  F8: 30.0,
  F9: 30.0,
  F10: 30.0,
  F11: 30.0,
  F12: 30.0,
  F13: 30.0,
  F14: 30.0,
  F15: 30.0,
  F16: 30.0,
  F17: 30.0,
  F18: 30.0,
  G1: 10.0,
  G2: 10.0,
  G3: 10.0,
  G4: 10.0,
  G5: 10.0,
  G6: 10.0,
  G7: 10.0,
  G8: 10.0,
  G9: 10.0,
  G10: 10.0,
  G11: 10.0,
  G12: 10.0,
  G13: 10.0,
  G14: 10.0,
  G15: 10.0,
  G16: 10.0,
  G17: 10.0,
  G18: 10.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1,
  JS2: -1.5,
  JS3: -2.5,
  JS4: -4,
  JS5: -6.5,
  JS6: -9.5,
  JS7: -15,
  JS8: -23,
  JS9: -37,
  JS10: -60,
  JS11: -95,
  JS12: -150,
  JS13: -230,
  JS14: -370,
  JS15: -600,
  JS16: -950,
  JS17: -1500,
  JS18: -2300,
  J6: -6,
  J7: -12,
  J8: -18,
  K1: -4,
  K2: -5,
  K3: -5,
  K4: -7,
  K5: -10,
  K6: -15,
  K7: -21,
  K8: -32,
  M1: -13,
  M2: -14,
  M3: -14,
  M4: -16,
  M5: -19,
  M6: -24,
  M7: -30,
  M8: -41,
  N1: -22,
  N2: -23,
  N3: -23,
  N4: -25,
  N5: -28,
  N6: -33,
  N7: -39,
  N8: -50,
  P1: -34,
  P2: -35,
  P3: -35,
  P4: -37,
  P5: -40,
  P6: -45,
  P7: -51,
  P8: -78,
  P9: -106,
  P10: -152,
  P11: -222,
  P12: -332,
  P13: -492,
  P14: -772,
  P15: -1232,
  P16: -1932,
  P17: -3032,
  P18: -4632
}, {
  from: 80,
  to: 100,
  A1: 380.0,
  A2: 380.0,
  A3: 380.0,
  A4: 380.0,
  A5: 380.0,
  A6: 380.0,
  A7: 380.0,
  A8: 380.0,
  A9: 380.0,
  A10: 380.0,
  A11: 380.0,
  A12: 380.0,
  A13: 380.0,
  A14: 380.0,
  A15: 380.0,
  A16: 380.0,
  A17: 380.0,
  A18: 380.0,
  B1: 220.0,
  B2: 220.0,
  B3: 220.0,
  B4: 220.0,
  B5: 220.0,
  B6: 220.0,
  B7: 220.0,
  B8: 220.0,
  B9: 220.0,
  B10: 220.0,
  B11: 220.0,
  B12: 220.0,
  B13: 220.0,
  B14: 220.0,
  B15: 220.0,
  B16: 220.0,
  B17: 220.0,
  B18: 220.0,
  C1: 170.0,
  C2: 170.0,
  C3: 170.0,
  C4: 170.0,
  C5: 170.0,
  C6: 170.0,
  C7: 170.0,
  C8: 170.0,
  C9: 170.0,
  C10: 170.0,
  C11: 170.0,
  C12: 170.0,
  C13: 170.0,
  C14: 170.0,
  C15: 170.0,
  C16: 170.0,
  C17: 170.0,
  C18: 170.0,
  D1: 120.0,
  D2: 120.0,
  D3: 120.0,
  D4: 120.0,
  D5: 120.0,
  D6: 120.0,
  D7: 120.0,
  D8: 120.0,
  D9: 120.0,
  D10: 120.0,
  D11: 120.0,
  D12: 120.0,
  D13: 120.0,
  D14: 120.0,
  D15: 120.0,
  D16: 120.0,
  D17: 120.0,
  D18: 120.0,
  E1: 72.0,
  E2: 72.0,
  E3: 72.0,
  E4: 72.0,
  E5: 72.0,
  E6: 72.0,
  E7: 72.0,
  E8: 72.0,
  E9: 72.0,
  E10: 72.0,
  E11: 72.0,
  E12: 72.0,
  E13: 72.0,
  E14: 72.0,
  E15: 72.0,
  E16: 72.0,
  E17: 72.0,
  E18: 72.0,
  F1: 36.0,
  F2: 36.0,
  F3: 36.0,
  F4: 36.0,
  F5: 36.0,
  F6: 36.0,
  F7: 36.0,
  F8: 36.0,
  F9: 36.0,
  F10: 36.0,
  F11: 36.0,
  F12: 36.0,
  F13: 36.0,
  F14: 36.0,
  F15: 36.0,
  F16: 36.0,
  F17: 36.0,
  F18: 36.0,
  G1: 12.0,
  G2: 12.0,
  G3: 12.0,
  G4: 12.0,
  G5: 12.0,
  G6: 12.0,
  G7: 12.0,
  G8: 12.0,
  G9: 12.0,
  G10: 12.0,
  G11: 12.0,
  G12: 12.0,
  G13: 12.0,
  G14: 12.0,
  G15: 12.0,
  G16: 12.0,
  G17: 12.0,
  G18: 12.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1.2,
  JS2: -2,
  JS3: -3,
  JS4: -5,
  JS5: -7.5,
  JS6: -11,
  JS7: -17.5,
  JS8: -27,
  JS9: -43.5,
  JS10: -70,
  JS11: -110,
  JS12: -175,
  JS13: -270,
  JS14: -435,
  JS15: -700,
  JS16: -1100,
  JS17: -1750,
  JS18: -2700,
  J6: -6,
  J7: -13,
  J8: -20,
  K1: -5.5,
  K2: -7,
  K3: -7,
  K4: -9,
  K5: -13,
  K6: -18,
  K7: -25,
  K8: -38,
  M1: -15.5,
  M2: -17,
  M3: -17,
  M4: -19,
  M5: -23,
  M6: -28,
  M7: -35,
  M8: -48,
  N1: -25.5,
  N2: -27,
  N3: -27,
  N4: -29,
  N5: -33,
  N6: -38,
  N7: -45,
  N8: -58,
  P1: -39.5,
  P2: -41,
  P3: -41,
  P4: -43,
  P5: -47,
  P6: -52,
  P7: -59,
  P8: -91,
  P9: -124,
  P10: -177,
  P11: -257,
  P12: -387,
  P13: -577,
  P14: -907,
  P15: -1437,
  P16: -2237,
  P17: -3537,
  P18: -5437
}, {
  from: 100,
  to: 120,
  A1: 410.0,
  A2: 410.0,
  A3: 410.0,
  A4: 410.0,
  A5: 410.0,
  A6: 410.0,
  A7: 410.0,
  A8: 410.0,
  A9: 410.0,
  A10: 410.0,
  A11: 410.0,
  A12: 410.0,
  A13: 410.0,
  A14: 410.0,
  A15: 410.0,
  A16: 410.0,
  A17: 410.0,
  A18: 410.0,
  B1: 240.0,
  B2: 240.0,
  B3: 240.0,
  B4: 240.0,
  B5: 240.0,
  B6: 240.0,
  B7: 240.0,
  B8: 240.0,
  B9: 240.0,
  B10: 240.0,
  B11: 240.0,
  B12: 240.0,
  B13: 240.0,
  B14: 240.0,
  B15: 240.0,
  B16: 240.0,
  B17: 240.0,
  B18: 240.0,
  C1: 180.0,
  C2: 180.0,
  C3: 180.0,
  C4: 180.0,
  C5: 180.0,
  C6: 180.0,
  C7: 180.0,
  C8: 180.0,
  C9: 180.0,
  C10: 180.0,
  C11: 180.0,
  C12: 180.0,
  C13: 180.0,
  C14: 180.0,
  C15: 180.0,
  C16: 180.0,
  C17: 180.0,
  C18: 180.0,
  D1: 120.0,
  D2: 120.0,
  D3: 120.0,
  D4: 120.0,
  D5: 120.0,
  D6: 120.0,
  D7: 120.0,
  D8: 120.0,
  D9: 120.0,
  D10: 120.0,
  D11: 120.0,
  D12: 120.0,
  D13: 120.0,
  D14: 120.0,
  D15: 120.0,
  D16: 120.0,
  D17: 120.0,
  D18: 120.0,
  E1: 72.0,
  E2: 72.0,
  E3: 72.0,
  E4: 72.0,
  E5: 72.0,
  E6: 72.0,
  E7: 72.0,
  E8: 72.0,
  E9: 72.0,
  E10: 72.0,
  E11: 72.0,
  E12: 72.0,
  E13: 72.0,
  E14: 72.0,
  E15: 72.0,
  E16: 72.0,
  E17: 72.0,
  E18: 72.0,
  F1: 36.0,
  F2: 36.0,
  F3: 36.0,
  F4: 36.0,
  F5: 36.0,
  F6: 36.0,
  F7: 36.0,
  F8: 36.0,
  F9: 36.0,
  F10: 36.0,
  F11: 36.0,
  F12: 36.0,
  F13: 36.0,
  F14: 36.0,
  F15: 36.0,
  F16: 36.0,
  F17: 36.0,
  F18: 36.0,
  G1: 12.0,
  G2: 12.0,
  G3: 12.0,
  G4: 12.0,
  G5: 12.0,
  G6: 12.0,
  G7: 12.0,
  G8: 12.0,
  G9: 12.0,
  G10: 12.0,
  G11: 12.0,
  G12: 12.0,
  G13: 12.0,
  G14: 12.0,
  G15: 12.0,
  G16: 12.0,
  G17: 12.0,
  G18: 12.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1.2,
  JS2: -2,
  JS3: -3,
  JS4: -5,
  JS5: -7.5,
  JS6: -11,
  JS7: -17.5,
  JS8: -27,
  JS9: -43.5,
  JS10: -70,
  JS11: -110,
  JS12: -175,
  JS13: -270,
  JS14: -435,
  JS15: -700,
  JS16: -1100,
  JS17: -1750,
  JS18: -2700,
  J6: -6,
  J7: -13,
  J8: -20,
  K1: -5.5,
  K2: -7,
  K3: -7,
  K4: -9,
  K5: -13,
  K6: -18,
  K7: -25,
  K8: -38,
  M1: -15.5,
  M2: -17,
  M3: -17,
  M4: -19,
  M5: -23,
  M6: -28,
  M7: -35,
  M8: -48,
  N1: -25.5,
  N2: -27,
  N3: -27,
  N4: -29,
  N5: -33,
  N6: -38,
  N7: -45,
  N8: -58,
  P1: -39.5,
  P2: -41,
  P3: -41,
  P4: -43,
  P5: -47,
  P6: -52,
  P7: -59,
  P8: -91,
  P9: -124,
  P10: -177,
  P11: -257,
  P12: -387,
  P13: -577,
  P14: -907,
  P15: -1437,
  P16: -2237,
  P17: -3537,
  P18: -5437
}, {
  from: 120,
  to: 140,
  A1: 460.0,
  A2: 460.0,
  A3: 460.0,
  A4: 460.0,
  A5: 460.0,
  A6: 460.0,
  A7: 460.0,
  A8: 460.0,
  A9: 460.0,
  A10: 460.0,
  A11: 460.0,
  A12: 460.0,
  A13: 460.0,
  A14: 460.0,
  A15: 460.0,
  A16: 460.0,
  A17: 460.0,
  A18: 460.0,
  B1: 260.0,
  B2: 260.0,
  B3: 260.0,
  B4: 260.0,
  B5: 260.0,
  B6: 260.0,
  B7: 260.0,
  B8: 260.0,
  B9: 260.0,
  B10: 260.0,
  B11: 260.0,
  B12: 260.0,
  B13: 260.0,
  B14: 260.0,
  B15: 260.0,
  B16: 260.0,
  B17: 260.0,
  B18: 260.0,
  C1: 200.0,
  C2: 200.0,
  C3: 200.0,
  C4: 200.0,
  C5: 200.0,
  C6: 200.0,
  C7: 200.0,
  C8: 200.0,
  C9: 200.0,
  C10: 200.0,
  C11: 200.0,
  C12: 200.0,
  C13: 200.0,
  C14: 200.0,
  C15: 200.0,
  C16: 200.0,
  C17: 200.0,
  C18: 200.0,
  D1: 145.0,
  D2: 145.0,
  D3: 145.0,
  D4: 145.0,
  D5: 145.0,
  D6: 145.0,
  D7: 145.0,
  D8: 145.0,
  D9: 145.0,
  D10: 145.0,
  D11: 145.0,
  D12: 145.0,
  D13: 145.0,
  D14: 145.0,
  D15: 145.0,
  D16: 145.0,
  D17: 145.0,
  D18: 145.0,
  E1: 85.0,
  E2: 85.0,
  E3: 85.0,
  E4: 85.0,
  E5: 85.0,
  E6: 85.0,
  E7: 85.0,
  E8: 85.0,
  E9: 85.0,
  E10: 85.0,
  E11: 85.0,
  E12: 85.0,
  E13: 85.0,
  E14: 85.0,
  E15: 85.0,
  E16: 85.0,
  E17: 85.0,
  E18: 85.0,
  F1: 43.0,
  F2: 43.0,
  F3: 43.0,
  F4: 43.0,
  F5: 43.0,
  F6: 43.0,
  F7: 43.0,
  F8: 43.0,
  F9: 43.0,
  F10: 43.0,
  F11: 43.0,
  F12: 43.0,
  F13: 43.0,
  F14: 43.0,
  F15: 43.0,
  F16: 43.0,
  F17: 43.0,
  F18: 43.0,
  G1: 14.0,
  G2: 14.0,
  G3: 14.0,
  G4: 14.0,
  G5: 14.0,
  G6: 14.0,
  G7: 14.0,
  G8: 14.0,
  G9: 14.0,
  G10: 14.0,
  G11: 14.0,
  G12: 14.0,
  G13: 14.0,
  G14: 14.0,
  G15: 14.0,
  G16: 14.0,
  G17: 14.0,
  G18: 14.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1.7,
  JS2: -2.5,
  JS3: -4,
  JS4: -6,
  JS5: -9,
  JS6: -12.5,
  JS7: -20,
  JS8: -31.5,
  JS9: -50,
  JS10: -80,
  JS11: -125,
  JS12: -200,
  JS13: -315,
  JS14: -500,
  JS15: -800,
  JS16: -1250,
  JS17: -2e3,
  JS18: -3150,
  J6: -7,
  J7: -14,
  J8: -22,
  K1: -6.5,
  K2: -8,
  K3: -8,
  K4: -11,
  K5: -15,
  K6: -21,
  K7: -28,
  K8: -43,
  M1: -18.5,
  M2: -20,
  M3: -20,
  M4: -23,
  M5: -27,
  M6: -33,
  M7: -40,
  M8: -55,
  N1: -30.5,
  N2: -32,
  N3: -32,
  N4: -35,
  N5: -39,
  N6: -45,
  N7: -52,
  N8: -67,
  P1: -46.5,
  P2: -48,
  P3: -48,
  P4: -51,
  P5: -55,
  P6: -61,
  P7: -68,
  P8: -106,
  P9: -143,
  P10: -203,
  P11: -293,
  P12: -443,
  P13: -673,
  P14: -1043,
  P15: -1643,
  P16: -2543,
  P17: -4043,
  P18: -6343
}, {
  from: 140,
  to: 160,
  A1: 520.0,
  A2: 520.0,
  A3: 520.0,
  A4: 520.0,
  A5: 520.0,
  A6: 520.0,
  A7: 520.0,
  A8: 520.0,
  A9: 520.0,
  A10: 520.0,
  A11: 520.0,
  A12: 520.0,
  A13: 520.0,
  A14: 520.0,
  A15: 520.0,
  A16: 520.0,
  A17: 520.0,
  A18: 520.0,
  B1: 280.0,
  B2: 280.0,
  B3: 280.0,
  B4: 280.0,
  B5: 280.0,
  B6: 280.0,
  B7: 280.0,
  B8: 280.0,
  B9: 280.0,
  B10: 280.0,
  B11: 280.0,
  B12: 280.0,
  B13: 280.0,
  B14: 280.0,
  B15: 280.0,
  B16: 280.0,
  B17: 280.0,
  B18: 280.0,
  C1: 210.0,
  C2: 210.0,
  C3: 210.0,
  C4: 210.0,
  C5: 210.0,
  C6: 210.0,
  C7: 210.0,
  C8: 210.0,
  C9: 210.0,
  C10: 210.0,
  C11: 210.0,
  C12: 210.0,
  C13: 210.0,
  C14: 210.0,
  C15: 210.0,
  C16: 210.0,
  C17: 210.0,
  C18: 210.0,
  D1: 145.0,
  D2: 145.0,
  D3: 145.0,
  D4: 145.0,
  D5: 145.0,
  D6: 145.0,
  D7: 145.0,
  D8: 145.0,
  D9: 145.0,
  D10: 145.0,
  D11: 145.0,
  D12: 145.0,
  D13: 145.0,
  D14: 145.0,
  D15: 145.0,
  D16: 145.0,
  D17: 145.0,
  D18: 145.0,
  E1: 85.0,
  E2: 85.0,
  E3: 85.0,
  E4: 85.0,
  E5: 85.0,
  E6: 85.0,
  E7: 85.0,
  E8: 85.0,
  E9: 85.0,
  E10: 85.0,
  E11: 85.0,
  E12: 85.0,
  E13: 85.0,
  E14: 85.0,
  E15: 85.0,
  E16: 85.0,
  E17: 85.0,
  E18: 85.0,
  F1: 43.0,
  F2: 43.0,
  F3: 43.0,
  F4: 43.0,
  F5: 43.0,
  F6: 43.0,
  F7: 43.0,
  F8: 43.0,
  F9: 43.0,
  F10: 43.0,
  F11: 43.0,
  F12: 43.0,
  F13: 43.0,
  F14: 43.0,
  F15: 43.0,
  F16: 43.0,
  F17: 43.0,
  F18: 43.0,
  G1: 14.0,
  G2: 14.0,
  G3: 14.0,
  G4: 14.0,
  G5: 14.0,
  G6: 14.0,
  G7: 14.0,
  G8: 14.0,
  G9: 14.0,
  G10: 14.0,
  G11: 14.0,
  G12: 14.0,
  G13: 14.0,
  G14: 14.0,
  G15: 14.0,
  G16: 14.0,
  G17: 14.0,
  G18: 14.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1.7,
  JS2: -2.5,
  JS3: -4,
  JS4: -6,
  JS5: -9,
  JS6: -12.5,
  JS7: -20,
  JS8: -31.5,
  JS9: -50,
  JS10: -80,
  JS11: -125,
  JS12: -200,
  JS13: -315,
  JS14: -500,
  JS15: -800,
  JS16: -1250,
  JS17: -2e3,
  JS18: -3150,
  J6: -7,
  J7: -14,
  J8: -22,
  K1: -6.5,
  K2: -8,
  K3: -8,
  K4: -11,
  K5: -15,
  K6: -21,
  K7: -28,
  K8: -43,
  M1: -18.5,
  M2: -20,
  M3: -20,
  M4: -23,
  M5: -27,
  M6: -33,
  M7: -40,
  M8: -55,
  N1: -30.5,
  N2: -32,
  N3: -32,
  N4: -35,
  N5: -39,
  N6: -45,
  N7: -52,
  N8: -67,
  P1: -46.5,
  P2: -48,
  P3: -48,
  P4: -51,
  P5: -55,
  P6: -61,
  P7: -68,
  P8: -106,
  P9: -143,
  P10: -203,
  P11: -293,
  P12: -443,
  P13: -673,
  P14: -1043,
  P15: -1643,
  P16: -2543,
  P17: -4043,
  P18: -6343
}, {
  from: 160,
  to: 180,
  A1: 580.0,
  A2: 580.0,
  A3: 580.0,
  A4: 580.0,
  A5: 580.0,
  A6: 580.0,
  A7: 580.0,
  A8: 580.0,
  A9: 580.0,
  A10: 580.0,
  A11: 580.0,
  A12: 580.0,
  A13: 580.0,
  A14: 580.0,
  A15: 580.0,
  A16: 580.0,
  A17: 580.0,
  A18: 580.0,
  B1: 310.0,
  B2: 310.0,
  B3: 310.0,
  B4: 310.0,
  B5: 310.0,
  B6: 310.0,
  B7: 310.0,
  B8: 310.0,
  B9: 310.0,
  B10: 310.0,
  B11: 310.0,
  B12: 310.0,
  B13: 310.0,
  B14: 310.0,
  B15: 310.0,
  B16: 310.0,
  B17: 310.0,
  B18: 310.0,
  C1: 230.0,
  C2: 230.0,
  C3: 230.0,
  C4: 230.0,
  C5: 230.0,
  C6: 230.0,
  C7: 230.0,
  C8: 230.0,
  C9: 230.0,
  C10: 230.0,
  C11: 230.0,
  C12: 230.0,
  C13: 230.0,
  C14: 230.0,
  C15: 230.0,
  C16: 230.0,
  C17: 230.0,
  C18: 230.0,
  D1: 145.0,
  D2: 145.0,
  D3: 145.0,
  D4: 145.0,
  D5: 145.0,
  D6: 145.0,
  D7: 145.0,
  D8: 145.0,
  D9: 145.0,
  D10: 145.0,
  D11: 145.0,
  D12: 145.0,
  D13: 145.0,
  D14: 145.0,
  D15: 145.0,
  D16: 145.0,
  D17: 145.0,
  D18: 145.0,
  E1: 85.0,
  E2: 85.0,
  E3: 85.0,
  E4: 85.0,
  E5: 85.0,
  E6: 85.0,
  E7: 85.0,
  E8: 85.0,
  E9: 85.0,
  E10: 85.0,
  E11: 85.0,
  E12: 85.0,
  E13: 85.0,
  E14: 85.0,
  E15: 85.0,
  E16: 85.0,
  E17: 85.0,
  E18: 85.0,
  F1: 43.0,
  F2: 43.0,
  F3: 43.0,
  F4: 43.0,
  F5: 43.0,
  F6: 43.0,
  F7: 43.0,
  F8: 43.0,
  F9: 43.0,
  F10: 43.0,
  F11: 43.0,
  F12: 43.0,
  F13: 43.0,
  F14: 43.0,
  F15: 43.0,
  F16: 43.0,
  F17: 43.0,
  F18: 43.0,
  G1: 14.0,
  G2: 14.0,
  G3: 14.0,
  G4: 14.0,
  G5: 14.0,
  G6: 14.0,
  G7: 14.0,
  G8: 14.0,
  G9: 14.0,
  G10: 14.0,
  G11: 14.0,
  G12: 14.0,
  G13: 14.0,
  G14: 14.0,
  G15: 14.0,
  G16: 14.0,
  G17: 14.0,
  G18: 14.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -1.7,
  JS2: -2.5,
  JS3: -4,
  JS4: -6,
  JS5: -9,
  JS6: -12.5,
  JS7: -20,
  JS8: -31.5,
  JS9: -50,
  JS10: -80,
  JS11: -125,
  JS12: -200,
  JS13: -315,
  JS14: -500,
  JS15: -800,
  JS16: -1250,
  JS17: -2e3,
  JS18: -3150,
  J6: -7,
  J7: -14,
  J8: -22,
  K1: -6.5,
  K2: -8,
  K3: -8,
  K4: -11,
  K5: -15,
  K6: -21,
  K7: -28,
  K8: -43,
  M1: -18.5,
  M2: -20,
  M3: -20,
  M4: -23,
  M5: -27,
  M6: -33,
  M7: -40,
  M8: -55,
  N1: -30.5,
  N2: -32,
  N3: -32,
  N4: -35,
  N5: -39,
  N6: -45,
  N7: -52,
  N8: -67,
  P1: -46.5,
  P2: -48,
  P3: -48,
  P4: -51,
  P5: -55,
  P6: -61,
  P7: -68,
  P8: -106,
  P9: -143,
  P10: -203,
  P11: -293,
  P12: -443,
  P13: -673,
  P14: -1043,
  P15: -1643,
  P16: -2543,
  P17: -4043,
  P18: -6343
}, {
  from: 180,
  to: 200,
  A1: 660.0,
  A2: 660.0,
  A3: 660.0,
  A4: 660.0,
  A5: 660.0,
  A6: 660.0,
  A7: 660.0,
  A8: 660.0,
  A9: 660.0,
  A10: 660.0,
  A11: 660.0,
  A12: 660.0,
  A13: 660.0,
  A14: 660.0,
  A15: 660.0,
  A16: 660.0,
  A17: 660.0,
  A18: 660.0,
  B1: 340.0,
  B2: 340.0,
  B3: 340.0,
  B4: 340.0,
  B5: 340.0,
  B6: 340.0,
  B7: 340.0,
  B8: 340.0,
  B9: 340.0,
  B10: 340.0,
  B11: 340.0,
  B12: 340.0,
  B13: 340.0,
  B14: 340.0,
  B15: 340.0,
  B16: 340.0,
  B17: 340.0,
  B18: 340.0,
  C1: 240.0,
  C2: 240.0,
  C3: 240.0,
  C4: 240.0,
  C5: 240.0,
  C6: 240.0,
  C7: 240.0,
  C8: 240.0,
  C9: 240.0,
  C10: 240.0,
  C11: 240.0,
  C12: 240.0,
  C13: 240.0,
  C14: 240.0,
  C15: 240.0,
  C16: 240.0,
  C17: 240.0,
  C18: 240.0,
  D1: 170.0,
  D2: 170.0,
  D3: 170.0,
  D4: 170.0,
  D5: 170.0,
  D6: 170.0,
  D7: 170.0,
  D8: 170.0,
  D9: 170.0,
  D10: 170.0,
  D11: 170.0,
  D12: 170.0,
  D13: 170.0,
  D14: 170.0,
  D15: 170.0,
  D16: 170.0,
  D17: 170.0,
  D18: 170.0,
  E1: 100.0,
  E2: 100.0,
  E3: 100.0,
  E4: 100.0,
  E5: 100.0,
  E6: 100.0,
  E7: 100.0,
  E8: 100.0,
  E9: 100.0,
  E10: 100.0,
  E11: 100.0,
  E12: 100.0,
  E13: 100.0,
  E14: 100.0,
  E15: 100.0,
  E16: 100.0,
  E17: 100.0,
  E18: 100.0,
  F1: 50.0,
  F2: 50.0,
  F3: 50.0,
  F4: 50.0,
  F5: 50.0,
  F6: 50.0,
  F7: 50.0,
  F8: 50.0,
  F9: 50.0,
  F10: 50.0,
  F11: 50.0,
  F12: 50.0,
  F13: 50.0,
  F14: 50.0,
  F15: 50.0,
  F16: 50.0,
  F17: 50.0,
  F18: 50.0,
  G1: 15.0,
  G2: 15.0,
  G3: 15.0,
  G4: 15.0,
  G5: 15.0,
  G6: 15.0,
  G7: 15.0,
  G8: 15.0,
  G9: 15.0,
  G10: 15.0,
  G11: 15.0,
  G12: 15.0,
  G13: 15.0,
  G14: 15.0,
  G15: 15.0,
  G16: 15.0,
  G17: 15.0,
  G18: 15.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -2.3,
  JS2: -3.5,
  JS3: -5,
  JS4: -7,
  JS5: -10,
  JS6: -14.5,
  JS7: -23,
  JS8: -36,
  JS9: -57.5,
  JS10: -92.5,
  JS11: -145,
  JS12: -230,
  JS13: -360,
  JS14: -575,
  JS15: -925,
  JS16: -1450,
  JS17: -2300,
  JS18: -3600,
  J6: -7,
  J7: -16,
  J8: -25,
  K1: -8.5,
  K2: -11,
  K3: -11,
  K4: -14,
  K5: -18,
  K6: -24,
  K7: -33,
  K8: -50,
  M1: -21.5,
  M2: -24,
  M3: -24,
  M4: -27,
  M5: -31,
  M6: -37,
  M7: -46,
  M8: -63,
  N1: -35.5,
  N2: -38,
  N3: -38,
  N4: -41,
  N5: -45,
  N6: -51,
  N7: -60,
  N8: -77,
  P1: -54.5,
  P2: -57,
  P3: -57,
  P4: -60,
  P5: -64,
  P6: -70,
  P7: -79,
  P8: -122,
  P9: -165,
  P10: -235,
  P11: -340,
  P12: -510,
  P13: -770,
  P14: -1200,
  P15: -1900,
  P16: -2950,
  P17: -4650,
  P18: -7250
}, {
  from: 200,
  to: 225,
  A1: 740.0,
  A2: 740.0,
  A3: 740.0,
  A4: 740.0,
  A5: 740.0,
  A6: 740.0,
  A7: 740.0,
  A8: 740.0,
  A9: 740.0,
  A10: 740.0,
  A11: 740.0,
  A12: 740.0,
  A13: 740.0,
  A14: 740.0,
  A15: 740.0,
  A16: 740.0,
  A17: 740.0,
  A18: 740.0,
  B1: 380.0,
  B2: 380.0,
  B3: 380.0,
  B4: 380.0,
  B5: 380.0,
  B6: 380.0,
  B7: 380.0,
  B8: 380.0,
  B9: 380.0,
  B10: 380.0,
  B11: 380.0,
  B12: 380.0,
  B13: 380.0,
  B14: 380.0,
  B15: 380.0,
  B16: 380.0,
  B17: 380.0,
  B18: 380.0,
  C1: 260.0,
  C2: 260.0,
  C3: 260.0,
  C4: 260.0,
  C5: 260.0,
  C6: 260.0,
  C7: 260.0,
  C8: 260.0,
  C9: 260.0,
  C10: 260.0,
  C11: 260.0,
  C12: 260.0,
  C13: 260.0,
  C14: 260.0,
  C15: 260.0,
  C16: 260.0,
  C17: 260.0,
  C18: 260.0,
  D1: 170.0,
  D2: 170.0,
  D3: 170.0,
  D4: 170.0,
  D5: 170.0,
  D6: 170.0,
  D7: 170.0,
  D8: 170.0,
  D9: 170.0,
  D10: 170.0,
  D11: 170.0,
  D12: 170.0,
  D13: 170.0,
  D14: 170.0,
  D15: 170.0,
  D16: 170.0,
  D17: 170.0,
  D18: 170.0,
  E1: 100.0,
  E2: 100.0,
  E3: 100.0,
  E4: 100.0,
  E5: 100.0,
  E6: 100.0,
  E7: 100.0,
  E8: 100.0,
  E9: 100.0,
  E10: 100.0,
  E11: 100.0,
  E12: 100.0,
  E13: 100.0,
  E14: 100.0,
  E15: 100.0,
  E16: 100.0,
  E17: 100.0,
  E18: 100.0,
  F1: 50.0,
  F2: 50.0,
  F3: 50.0,
  F4: 50.0,
  F5: 50.0,
  F6: 50.0,
  F7: 50.0,
  F8: 50.0,
  F9: 50.0,
  F10: 50.0,
  F11: 50.0,
  F12: 50.0,
  F13: 50.0,
  F14: 50.0,
  F15: 50.0,
  F16: 50.0,
  F17: 50.0,
  F18: 50.0,
  G1: 15.0,
  G2: 15.0,
  G3: 15.0,
  G4: 15.0,
  G5: 15.0,
  G6: 15.0,
  G7: 15.0,
  G8: 15.0,
  G9: 15.0,
  G10: 15.0,
  G11: 15.0,
  G12: 15.0,
  G13: 15.0,
  G14: 15.0,
  G15: 15.0,
  G16: 15.0,
  G17: 15.0,
  G18: 15.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -2.3,
  JS2: -3.5,
  JS3: -5,
  JS4: -7,
  JS5: -10,
  JS6: -14.5,
  JS7: -23,
  JS8: -36,
  JS9: -57.5,
  JS10: -92.5,
  JS11: -145,
  JS12: -230,
  JS13: -360,
  JS14: -575,
  JS15: -925,
  JS16: -1450,
  JS17: -2300,
  JS18: -3600,
  J6: -7,
  J7: -16,
  J8: -25,
  K1: -8.5,
  K2: -11,
  K3: -11,
  K4: -14,
  K5: -18,
  K6: -24,
  K7: -33,
  K8: -50,
  M1: -21.5,
  M2: -24,
  M3: -24,
  M4: -27,
  M5: -31,
  M6: -37,
  M7: -46,
  M8: -63,
  N1: -35.5,
  N2: -38,
  N3: -38,
  N4: -41,
  N5: -45,
  N6: -51,
  N7: -60,
  N8: -77,
  P1: -54.5,
  P2: -57,
  P3: -57,
  P4: -60,
  P5: -64,
  P6: -70,
  P7: -79,
  P8: -122,
  P9: -165,
  P10: -235,
  P11: -340,
  P12: -510,
  P13: -770,
  P14: -1200,
  P15: -1900,
  P16: -2950,
  P17: -4650,
  P18: -7250
}, {
  from: 225,
  to: 250,
  A1: 820.0,
  A2: 820.0,
  A3: 820.0,
  A4: 820.0,
  A5: 820.0,
  A6: 820.0,
  A7: 820.0,
  A8: 820.0,
  A9: 820.0,
  A10: 820.0,
  A11: 820.0,
  A12: 820.0,
  A13: 820.0,
  A14: 820.0,
  A15: 820.0,
  A16: 820.0,
  A17: 820.0,
  A18: 820.0,
  B1: 420.0,
  B2: 420.0,
  B3: 420.0,
  B4: 420.0,
  B5: 420.0,
  B6: 420.0,
  B7: 420.0,
  B8: 420.0,
  B9: 420.0,
  B10: 420.0,
  B11: 420.0,
  B12: 420.0,
  B13: 420.0,
  B14: 420.0,
  B15: 420.0,
  B16: 420.0,
  B17: 420.0,
  B18: 420.0,
  C1: 280.0,
  C2: 280.0,
  C3: 280.0,
  C4: 280.0,
  C5: 280.0,
  C6: 280.0,
  C7: 280.0,
  C8: 280.0,
  C9: 280.0,
  C10: 280.0,
  C11: 280.0,
  C12: 280.0,
  C13: 280.0,
  C14: 280.0,
  C15: 280.0,
  C16: 280.0,
  C17: 280.0,
  C18: 280.0,
  D1: 170.0,
  D2: 170.0,
  D3: 170.0,
  D4: 170.0,
  D5: 170.0,
  D6: 170.0,
  D7: 170.0,
  D8: 170.0,
  D9: 170.0,
  D10: 170.0,
  D11: 170.0,
  D12: 170.0,
  D13: 170.0,
  D14: 170.0,
  D15: 170.0,
  D16: 170.0,
  D17: 170.0,
  D18: 170.0,
  E1: 100.0,
  E2: 100.0,
  E3: 100.0,
  E4: 100.0,
  E5: 100.0,
  E6: 100.0,
  E7: 100.0,
  E8: 100.0,
  E9: 100.0,
  E10: 100.0,
  E11: 100.0,
  E12: 100.0,
  E13: 100.0,
  E14: 100.0,
  E15: 100.0,
  E16: 100.0,
  E17: 100.0,
  E18: 100.0,
  F1: 50.0,
  F2: 50.0,
  F3: 50.0,
  F4: 50.0,
  F5: 50.0,
  F6: 50.0,
  F7: 50.0,
  F8: 50.0,
  F9: 50.0,
  F10: 50.0,
  F11: 50.0,
  F12: 50.0,
  F13: 50.0,
  F14: 50.0,
  F15: 50.0,
  F16: 50.0,
  F17: 50.0,
  F18: 50.0,
  G1: 15.0,
  G2: 15.0,
  G3: 15.0,
  G4: 15.0,
  G5: 15.0,
  G6: 15.0,
  G7: 15.0,
  G8: 15.0,
  G9: 15.0,
  G10: 15.0,
  G11: 15.0,
  G12: 15.0,
  G13: 15.0,
  G14: 15.0,
  G15: 15.0,
  G16: 15.0,
  G17: 15.0,
  G18: 15.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -2.3,
  JS2: -3.5,
  JS3: -5,
  JS4: -7,
  JS5: -10,
  JS6: -14.5,
  JS7: -23,
  JS8: -36,
  JS9: -57.5,
  JS10: -92.5,
  JS11: -145,
  JS12: -230,
  JS13: -360,
  JS14: -575,
  JS15: -925,
  JS16: -1450,
  JS17: -2300,
  JS18: -3600,
  J6: -7,
  J7: -16,
  J8: -25,
  K1: -8.5,
  K2: -11,
  K3: -11,
  K4: -14,
  K5: -18,
  K6: -24,
  K7: -33,
  K8: -50,
  M1: -21.5,
  M2: -24,
  M3: -24,
  M4: -27,
  M5: -31,
  M6: -37,
  M7: -46,
  M8: -63,
  N1: -35.5,
  N2: -38,
  N3: -38,
  N4: -41,
  N5: -45,
  N6: -51,
  N7: -60,
  N8: -77,
  P1: -54.5,
  P2: -57,
  P3: -57,
  P4: -60,
  P5: -64,
  P6: -70,
  P7: -79,
  P8: -122,
  P9: -165,
  P10: -235,
  P11: -340,
  P12: -510,
  P13: -770,
  P14: -1200,
  P15: -1900,
  P16: -2950,
  P17: -4650,
  P18: -7250
}, {
  from: 250,
  to: 280,
  A1: 920.0,
  A2: 920.0,
  A3: 920.0,
  A4: 920.0,
  A5: 920.0,
  A6: 920.0,
  A7: 920.0,
  A8: 920.0,
  A9: 920.0,
  A10: 920.0,
  A11: 920.0,
  A12: 920.0,
  A13: 920.0,
  A14: 920.0,
  A15: 920.0,
  A16: 920.0,
  A17: 920.0,
  A18: 920.0,
  B1: 480.0,
  B2: 480.0,
  B3: 480.0,
  B4: 480.0,
  B5: 480.0,
  B6: 480.0,
  B7: 480.0,
  B8: 480.0,
  B9: 480.0,
  B10: 480.0,
  B11: 480.0,
  B12: 480.0,
  B13: 480.0,
  B14: 480.0,
  B15: 480.0,
  B16: 480.0,
  B17: 480.0,
  B18: 480.0,
  C1: 300.0,
  C2: 300.0,
  C3: 300.0,
  C4: 300.0,
  C5: 300.0,
  C6: 300.0,
  C7: 300.0,
  C8: 300.0,
  C9: 300.0,
  C10: 300.0,
  C11: 300.0,
  C12: 300.0,
  C13: 300.0,
  C14: 300.0,
  C15: 300.0,
  C16: 300.0,
  C17: 300.0,
  C18: 300.0,
  D1: 190.0,
  D2: 190.0,
  D3: 190.0,
  D4: 190.0,
  D5: 190.0,
  D6: 190.0,
  D7: 190.0,
  D8: 190.0,
  D9: 190.0,
  D10: 190.0,
  D11: 190.0,
  D12: 190.0,
  D13: 190.0,
  D14: 190.0,
  D15: 190.0,
  D16: 190.0,
  D17: 190.0,
  D18: 190.0,
  E1: 110.0,
  E2: 110.0,
  E3: 110.0,
  E4: 110.0,
  E5: 110.0,
  E6: 110.0,
  E7: 110.0,
  E8: 110.0,
  E9: 110.0,
  E10: 110.0,
  E11: 110.0,
  E12: 110.0,
  E13: 110.0,
  E14: 110.0,
  E15: 110.0,
  E16: 110.0,
  E17: 110.0,
  E18: 110.0,
  F1: 56.0,
  F2: 56.0,
  F3: 56.0,
  F4: 56.0,
  F5: 56.0,
  F6: 56.0,
  F7: 56.0,
  F8: 56.0,
  F9: 56.0,
  F10: 56.0,
  F11: 56.0,
  F12: 56.0,
  F13: 56.0,
  F14: 56.0,
  F15: 56.0,
  F16: 56.0,
  F17: 56.0,
  F18: 56.0,
  G1: 17.0,
  G2: 17.0,
  G3: 17.0,
  G4: 17.0,
  G5: 17.0,
  G6: 17.0,
  G7: 17.0,
  G8: 17.0,
  G9: 17.0,
  G10: 17.0,
  G11: 17.0,
  G12: 17.0,
  G13: 17.0,
  G14: 17.0,
  G15: 17.0,
  G16: 17.0,
  G17: 17.0,
  G18: 17.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -3,
  JS2: -4,
  JS3: -6,
  JS4: -8,
  JS5: -11.5,
  JS6: -16,
  JS7: -26,
  JS8: -40.5,
  JS9: -65,
  JS10: -105,
  JS11: -160,
  JS12: -260,
  JS13: -405,
  JS14: -650,
  JS15: -1050,
  JS16: -1600,
  JS17: -2600,
  JS18: -4050,
  J6: -7,
  J7: -16,
  J8: -26,
  K1: -10,
  K2: -12,
  K3: -12,
  K4: -16,
  K5: -20,
  K6: -27,
  K7: -36,
  K8: -56,
  M1: -26,
  M2: -28,
  M3: -28,
  M4: -32,
  M5: -36,
  M6: -43,
  M7: -52,
  M8: -72,
  N1: -40,
  N2: -42,
  N3: -42,
  N4: -46,
  N5: -50,
  N6: -57,
  N7: -66,
  N8: -86,
  P1: -62,
  P2: -64,
  P3: -64,
  P4: -68,
  P5: -72,
  P6: -79,
  P7: -88,
  P8: -137,
  P9: -186,
  P10: -266,
  P11: -376,
  P12: -576,
  P13: -866,
  P14: -1356,
  P15: -2156,
  P16: -3256,
  P17: -5256,
  P18: -8156
}, {
  from: 280,
  to: 315,
  A1: 1050.0,
  A2: 1050.0,
  A3: 1050.0,
  A4: 1050.0,
  A5: 1050.0,
  A6: 1050.0,
  A7: 1050.0,
  A8: 1050.0,
  A9: 1050.0,
  A10: 1050.0,
  A11: 1050.0,
  A12: 1050.0,
  A13: 1050.0,
  A14: 1050.0,
  A15: 1050.0,
  A16: 1050.0,
  A17: 1050.0,
  A18: 1050.0,
  B1: 540.0,
  B2: 540.0,
  B3: 540.0,
  B4: 540.0,
  B5: 540.0,
  B6: 540.0,
  B7: 540.0,
  B8: 540.0,
  B9: 540.0,
  B10: 540.0,
  B11: 540.0,
  B12: 540.0,
  B13: 540.0,
  B14: 540.0,
  B15: 540.0,
  B16: 540.0,
  B17: 540.0,
  B18: 540.0,
  C1: 330.0,
  C2: 330.0,
  C3: 330.0,
  C4: 330.0,
  C5: 330.0,
  C6: 330.0,
  C7: 330.0,
  C8: 330.0,
  C9: 330.0,
  C10: 330.0,
  C11: 330.0,
  C12: 330.0,
  C13: 330.0,
  C14: 330.0,
  C15: 330.0,
  C16: 330.0,
  C17: 330.0,
  C18: 330.0,
  D1: 190.0,
  D2: 190.0,
  D3: 190.0,
  D4: 190.0,
  D5: 190.0,
  D6: 190.0,
  D7: 190.0,
  D8: 190.0,
  D9: 190.0,
  D10: 190.0,
  D11: 190.0,
  D12: 190.0,
  D13: 190.0,
  D14: 190.0,
  D15: 190.0,
  D16: 190.0,
  D17: 190.0,
  D18: 190.0,
  E1: 110.0,
  E2: 110.0,
  E3: 110.0,
  E4: 110.0,
  E5: 110.0,
  E6: 110.0,
  E7: 110.0,
  E8: 110.0,
  E9: 110.0,
  E10: 110.0,
  E11: 110.0,
  E12: 110.0,
  E13: 110.0,
  E14: 110.0,
  E15: 110.0,
  E16: 110.0,
  E17: 110.0,
  E18: 110.0,
  F1: 56.0,
  F2: 56.0,
  F3: 56.0,
  F4: 56.0,
  F5: 56.0,
  F6: 56.0,
  F7: 56.0,
  F8: 56.0,
  F9: 56.0,
  F10: 56.0,
  F11: 56.0,
  F12: 56.0,
  F13: 56.0,
  F14: 56.0,
  F15: 56.0,
  F16: 56.0,
  F17: 56.0,
  F18: 56.0,
  G1: 17.0,
  G2: 17.0,
  G3: 17.0,
  G4: 17.0,
  G5: 17.0,
  G6: 17.0,
  G7: 17.0,
  G8: 17.0,
  G9: 17.0,
  G10: 17.0,
  G11: 17.0,
  G12: 17.0,
  G13: 17.0,
  G14: 17.0,
  G15: 17.0,
  G16: 17.0,
  G17: 17.0,
  G18: 17.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -3,
  JS2: -4,
  JS3: -6,
  JS4: -8,
  JS5: -11.5,
  JS6: -16,
  JS7: -26,
  JS8: -40.5,
  JS9: -65,
  JS10: -105,
  JS11: -160,
  JS12: -260,
  JS13: -405,
  JS14: -650,
  JS15: -1050,
  JS16: -1600,
  JS17: -2600,
  JS18: -4050,
  J6: -7,
  J7: -16,
  J8: -26,
  K1: -10,
  K2: -12,
  K3: -12,
  K4: -16,
  K5: -20,
  K6: -27,
  K7: -36,
  K8: -56,
  M1: -26,
  M2: -28,
  M3: -28,
  M4: -32,
  M5: -36,
  M6: -43,
  M7: -52,
  M8: -72,
  N1: -40,
  N2: -42,
  N3: -42,
  N4: -46,
  N5: -50,
  N6: -57,
  N7: -66,
  N8: -86,
  P1: -62,
  P2: -64,
  P3: -64,
  P4: -68,
  P5: -72,
  P6: -79,
  P7: -88,
  P8: -137,
  P9: -186,
  P10: -266,
  P11: -376,
  P12: -576,
  P13: -866,
  P14: -1356,
  P15: -2156,
  P16: -3256,
  P17: -5256,
  P18: -8156
}, {
  from: 315,
  to: 355,
  A1: 1200.0,
  A2: 1200.0,
  A3: 1200.0,
  A4: 1200.0,
  A5: 1200.0,
  A6: 1200.0,
  A7: 1200.0,
  A8: 1200.0,
  A9: 1200.0,
  A10: 1200.0,
  A11: 1200.0,
  A12: 1200.0,
  A13: 1200.0,
  A14: 1200.0,
  A15: 1200.0,
  A16: 1200.0,
  A17: 1200.0,
  A18: 1200.0,
  B1: 600.0,
  B2: 600.0,
  B3: 600.0,
  B4: 600.0,
  B5: 600.0,
  B6: 600.0,
  B7: 600.0,
  B8: 600.0,
  B9: 600.0,
  B10: 600.0,
  B11: 600.0,
  B12: 600.0,
  B13: 600.0,
  B14: 600.0,
  B15: 600.0,
  B16: 600.0,
  B17: 600.0,
  B18: 600.0,
  C1: 360.0,
  C2: 360.0,
  C3: 360.0,
  C4: 360.0,
  C5: 360.0,
  C6: 360.0,
  C7: 360.0,
  C8: 360.0,
  C9: 360.0,
  C10: 360.0,
  C11: 360.0,
  C12: 360.0,
  C13: 360.0,
  C14: 360.0,
  C15: 360.0,
  C16: 360.0,
  C17: 360.0,
  C18: 360.0,
  D1: 210.0,
  D2: 210.0,
  D3: 210.0,
  D4: 210.0,
  D5: 210.0,
  D6: 210.0,
  D7: 210.0,
  D8: 210.0,
  D9: 210.0,
  D10: 210.0,
  D11: 210.0,
  D12: 210.0,
  D13: 210.0,
  D14: 210.0,
  D15: 210.0,
  D16: 210.0,
  D17: 210.0,
  D18: 210.0,
  E1: 125.0,
  E2: 125.0,
  E3: 125.0,
  E4: 125.0,
  E5: 125.0,
  E6: 125.0,
  E7: 125.0,
  E8: 125.0,
  E9: 125.0,
  E10: 125.0,
  E11: 125.0,
  E12: 125.0,
  E13: 125.0,
  E14: 125.0,
  E15: 125.0,
  E16: 125.0,
  E17: 125.0,
  E18: 125.0,
  F1: 62.0,
  F2: 62.0,
  F3: 62.0,
  F4: 62.0,
  F5: 62.0,
  F6: 62.0,
  F7: 62.0,
  F8: 62.0,
  F9: 62.0,
  F10: 62.0,
  F11: 62.0,
  F12: 62.0,
  F13: 62.0,
  F14: 62.0,
  F15: 62.0,
  F16: 62.0,
  F17: 62.0,
  F18: 62.0,
  G1: 18.0,
  G2: 18.0,
  G3: 18.0,
  G4: 18.0,
  G5: 18.0,
  G6: 18.0,
  G7: 18.0,
  G8: 18.0,
  G9: 18.0,
  G10: 18.0,
  G11: 18.0,
  G12: 18.0,
  G13: 18.0,
  G14: 18.0,
  G15: 18.0,
  G16: 18.0,
  G17: 18.0,
  G18: 18.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -3.5,
  JS2: -4.5,
  JS3: -6.5,
  JS4: -9,
  JS5: -12.5,
  JS6: -18,
  JS7: -28.5,
  JS8: -44.5,
  JS9: -70,
  JS10: -115,
  JS11: -180,
  JS12: -285,
  JS13: -445,
  JS14: -700,
  JS15: -1150,
  JS16: -1800,
  JS17: -2850,
  JS18: -4450,
  J6: -7,
  J7: -18,
  J8: -29,
  K1: -11,
  K2: -13,
  K3: -13,
  K4: -17,
  K5: -22,
  K6: -29,
  K7: -40,
  K8: -61,
  M1: -28,
  M2: -30,
  M3: -30,
  M4: -34,
  M5: -39,
  M6: -46,
  M7: -57,
  M8: -78,
  N1: -44,
  N2: -46,
  N3: -46,
  N4: -50,
  N5: -55,
  N6: -62,
  N7: -73,
  N8: -94,
  P1: -69,
  P2: -71,
  P3: -71,
  P4: -75,
  P5: -80,
  P6: -87,
  P7: -98,
  P8: -151,
  P9: -202,
  P10: -292,
  P11: -422,
  P12: -632,
  P13: -952,
  P14: -1462,
  P15: -2362,
  P16: -3662,
  P17: -5762,
  P18: -8962
}, {
  from: 355,
  to: 400,
  A1: 1350.0,
  A2: 1350.0,
  A3: 1350.0,
  A4: 1350.0,
  A5: 1350.0,
  A6: 1350.0,
  A7: 1350.0,
  A8: 1350.0,
  A9: 1350.0,
  A10: 1350.0,
  A11: 1350.0,
  A12: 1350.0,
  A13: 1350.0,
  A14: 1350.0,
  A15: 1350.0,
  A16: 1350.0,
  A17: 1350.0,
  A18: 1350.0,
  B1: 680.0,
  B2: 680.0,
  B3: 680.0,
  B4: 680.0,
  B5: 680.0,
  B6: 680.0,
  B7: 680.0,
  B8: 680.0,
  B9: 680.0,
  B10: 680.0,
  B11: 680.0,
  B12: 680.0,
  B13: 680.0,
  B14: 680.0,
  B15: 680.0,
  B16: 680.0,
  B17: 680.0,
  B18: 680.0,
  C1: 400.0,
  C2: 400.0,
  C3: 400.0,
  C4: 400.0,
  C5: 400.0,
  C6: 400.0,
  C7: 400.0,
  C8: 400.0,
  C9: 400.0,
  C10: 400.0,
  C11: 400.0,
  C12: 400.0,
  C13: 400.0,
  C14: 400.0,
  C15: 400.0,
  C16: 400.0,
  C17: 400.0,
  C18: 400.0,
  D1: 210.0,
  D2: 210.0,
  D3: 210.0,
  D4: 210.0,
  D5: 210.0,
  D6: 210.0,
  D7: 210.0,
  D8: 210.0,
  D9: 210.0,
  D10: 210.0,
  D11: 210.0,
  D12: 210.0,
  D13: 210.0,
  D14: 210.0,
  D15: 210.0,
  D16: 210.0,
  D17: 210.0,
  D18: 210.0,
  E1: 125.0,
  E2: 125.0,
  E3: 125.0,
  E4: 125.0,
  E5: 125.0,
  E6: 125.0,
  E7: 125.0,
  E8: 125.0,
  E9: 125.0,
  E10: 125.0,
  E11: 125.0,
  E12: 125.0,
  E13: 125.0,
  E14: 125.0,
  E15: 125.0,
  E16: 125.0,
  E17: 125.0,
  E18: 125.0,
  F1: 62.0,
  F2: 62.0,
  F3: 62.0,
  F4: 62.0,
  F5: 62.0,
  F6: 62.0,
  F7: 62.0,
  F8: 62.0,
  F9: 62.0,
  F10: 62.0,
  F11: 62.0,
  F12: 62.0,
  F13: 62.0,
  F14: 62.0,
  F15: 62.0,
  F16: 62.0,
  F17: 62.0,
  F18: 62.0,
  G1: 18.0,
  G2: 18.0,
  G3: 18.0,
  G4: 18.0,
  G5: 18.0,
  G6: 18.0,
  G7: 18.0,
  G8: 18.0,
  G9: 18.0,
  G10: 18.0,
  G11: 18.0,
  G12: 18.0,
  G13: 18.0,
  G14: 18.0,
  G15: 18.0,
  G16: 18.0,
  G17: 18.0,
  G18: 18.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -3.5,
  JS2: -4.5,
  JS3: -6.5,
  JS4: -9,
  JS5: -12.5,
  JS6: -18,
  JS7: -28.5,
  JS8: -44.5,
  JS9: -70,
  JS10: -115,
  JS11: -180,
  JS12: -285,
  JS13: -445,
  JS14: -700,
  JS15: -1150,
  JS16: -1800,
  JS17: -2850,
  JS18: -4450,
  J6: -7,
  J7: -18,
  J8: -29,
  K1: -11,
  K2: -13,
  K3: -13,
  K4: -17,
  K5: -22,
  K6: -29,
  K7: -40,
  K8: -61,
  M1: -28,
  M2: -30,
  M3: -30,
  M4: -34,
  M5: -39,
  M6: -46,
  M7: -57,
  M8: -78,
  N1: -44,
  N2: -46,
  N3: -46,
  N4: -50,
  N5: -55,
  N6: -62,
  N7: -73,
  N8: -94,
  P1: -69,
  P2: -71,
  P3: -71,
  P4: -75,
  P5: -80,
  P6: -87,
  P7: -98,
  P8: -151,
  P9: -202,
  P10: -292,
  P11: -422,
  P12: -632,
  P13: -952,
  P14: -1462,
  P15: -2362,
  P16: -3662,
  P17: -5762,
  P18: -8962
}, {
  from: 400,
  to: 450,
  A1: 1500.0,
  A2: 1500.0,
  A3: 1500.0,
  A4: 1500.0,
  A5: 1500.0,
  A6: 1500.0,
  A7: 1500.0,
  A8: 1500.0,
  A9: 1500.0,
  A10: 1500.0,
  A11: 1500.0,
  A12: 1500.0,
  A13: 1500.0,
  A14: 1500.0,
  A15: 1500.0,
  A16: 1500.0,
  A17: 1500.0,
  A18: 1500.0,
  B1: 760.0,
  B2: 760.0,
  B3: 760.0,
  B4: 760.0,
  B5: 760.0,
  B6: 760.0,
  B7: 760.0,
  B8: 760.0,
  B9: 760.0,
  B10: 760.0,
  B11: 760.0,
  B12: 760.0,
  B13: 760.0,
  B14: 760.0,
  B15: 760.0,
  B16: 760.0,
  B17: 760.0,
  B18: 760.0,
  C1: 440.0,
  C2: 440.0,
  C3: 440.0,
  C4: 440.0,
  C5: 440.0,
  C6: 440.0,
  C7: 440.0,
  C8: 440.0,
  C9: 440.0,
  C10: 440.0,
  C11: 440.0,
  C12: 440.0,
  C13: 440.0,
  C14: 440.0,
  C15: 440.0,
  C16: 440.0,
  C17: 440.0,
  C18: 440.0,
  D1: 230.0,
  D2: 230.0,
  D3: 230.0,
  D4: 230.0,
  D5: 230.0,
  D6: 230.0,
  D7: 230.0,
  D8: 230.0,
  D9: 230.0,
  D10: 230.0,
  D11: 230.0,
  D12: 230.0,
  D13: 230.0,
  D14: 230.0,
  D15: 230.0,
  D16: 230.0,
  D17: 230.0,
  D18: 230.0,
  E1: 135.0,
  E2: 135.0,
  E3: 135.0,
  E4: 135.0,
  E5: 135.0,
  E6: 135.0,
  E7: 135.0,
  E8: 135.0,
  E9: 135.0,
  E10: 135.0,
  E11: 135.0,
  E12: 135.0,
  E13: 135.0,
  E14: 135.0,
  E15: 135.0,
  E16: 135.0,
  E17: 135.0,
  E18: 135.0,
  F1: 68.0,
  F2: 68.0,
  F3: 68.0,
  F4: 68.0,
  F5: 68.0,
  F6: 68.0,
  F7: 68.0,
  F8: 68.0,
  F9: 68.0,
  F10: 68.0,
  F11: 68.0,
  F12: 68.0,
  F13: 68.0,
  F14: 68.0,
  F15: 68.0,
  F16: 68.0,
  F17: 68.0,
  F18: 68.0,
  G1: 20.0,
  G2: 20.0,
  G3: 20.0,
  G4: 20.0,
  G5: 20.0,
  G6: 20.0,
  G7: 20.0,
  G8: 20.0,
  G9: 20.0,
  G10: 20.0,
  G11: 20.0,
  G12: 20.0,
  G13: 20.0,
  G14: 20.0,
  G15: 20.0,
  G16: 20.0,
  G17: 20.0,
  G18: 20.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -4,
  JS2: -5,
  JS3: -7.5,
  JS4: -10,
  JS5: -13.5,
  JS6: -20,
  JS7: -31.5,
  JS8: -48.5,
  JS9: -77.5,
  JS10: -125,
  JS11: -200,
  JS12: -315,
  JS13: -485,
  JS14: -775,
  JS15: -1250,
  JS16: -2e3,
  JS17: -3150,
  JS18: -4850,
  J6: -7,
  J7: -20,
  J8: -31,
  K1: -13,
  K2: -15,
  K3: -15,
  K4: -20,
  K5: -25,
  K6: -32,
  K7: -45,
  K8: -68,
  M1: -31,
  M2: -33,
  M3: -33,
  M4: -38,
  M5: -43,
  M6: -50,
  M7: -63,
  M8: -86,
  N1: -48,
  N2: -50,
  N3: -50,
  N4: -55,
  N5: -60,
  N6: -67,
  N7: -80,
  N8: -103,
  P1: -76,
  P2: -78,
  P3: -78,
  P4: -83,
  P5: -88,
  P6: -95,
  P7: -108,
  P8: -165,
  P9: -223,
  P10: -318,
  P11: -468,
  P12: -698,
  P13: -1038,
  P14: -1618,
  P15: -2568,
  P16: -4068,
  P17: -6368,
  P18: -9768
}, {
  from: 450,
  to: 500,
  A1: 1650.0,
  A2: 1650.0,
  A3: 1650.0,
  A4: 1650.0,
  A5: 1650.0,
  A6: 1650.0,
  A7: 1650.0,
  A8: 1650.0,
  A9: 1650.0,
  A10: 1650.0,
  A11: 1650.0,
  A12: 1650.0,
  A13: 1650.0,
  A14: 1650.0,
  A15: 1650.0,
  A16: 1650.0,
  A17: 1650.0,
  A18: 1650.0,
  B1: 840.0,
  B2: 840.0,
  B3: 840.0,
  B4: 840.0,
  B5: 840.0,
  B6: 840.0,
  B7: 840.0,
  B8: 840.0,
  B9: 840.0,
  B10: 840.0,
  B11: 840.0,
  B12: 840.0,
  B13: 840.0,
  B14: 840.0,
  B15: 840.0,
  B16: 840.0,
  B17: 840.0,
  B18: 840.0,
  C1: 480.0,
  C2: 480.0,
  C3: 480.0,
  C4: 480.0,
  C5: 480.0,
  C6: 480.0,
  C7: 480.0,
  C8: 480.0,
  C9: 480.0,
  C10: 480.0,
  C11: 480.0,
  C12: 480.0,
  C13: 480.0,
  C14: 480.0,
  C15: 480.0,
  C16: 480.0,
  C17: 480.0,
  C18: 480.0,
  D1: 230.0,
  D2: 230.0,
  D3: 230.0,
  D4: 230.0,
  D5: 230.0,
  D6: 230.0,
  D7: 230.0,
  D8: 230.0,
  D9: 230.0,
  D10: 230.0,
  D11: 230.0,
  D12: 230.0,
  D13: 230.0,
  D14: 230.0,
  D15: 230.0,
  D16: 230.0,
  D17: 230.0,
  D18: 230.0,
  E1: 135.0,
  E2: 135.0,
  E3: 135.0,
  E4: 135.0,
  E5: 135.0,
  E6: 135.0,
  E7: 135.0,
  E8: 135.0,
  E9: 135.0,
  E10: 135.0,
  E11: 135.0,
  E12: 135.0,
  E13: 135.0,
  E14: 135.0,
  E15: 135.0,
  E16: 135.0,
  E17: 135.0,
  E18: 135.0,
  F1: 68.0,
  F2: 68.0,
  F3: 68.0,
  F4: 68.0,
  F5: 68.0,
  F6: 68.0,
  F7: 68.0,
  F8: 68.0,
  F9: 68.0,
  F10: 68.0,
  F11: 68.0,
  F12: 68.0,
  F13: 68.0,
  F14: 68.0,
  F15: 68.0,
  F16: 68.0,
  F17: 68.0,
  F18: 68.0,
  G1: 20.0,
  G2: 20.0,
  G3: 20.0,
  G4: 20.0,
  G5: 20.0,
  G6: 20.0,
  G7: 20.0,
  G8: 20.0,
  G9: 20.0,
  G10: 20.0,
  G11: 20.0,
  G12: 20.0,
  G13: 20.0,
  G14: 20.0,
  G15: 20.0,
  G16: 20.0,
  G17: 20.0,
  G18: 20.0,
  H1: 0.0,
  H2: 0.0,
  H3: 0.0,
  H4: 0.0,
  H5: 0.0,
  H6: 0.0,
  H7: 0.0,
  H8: 0.0,
  H9: 0.0,
  H10: 0.0,
  H11: 0.0,
  H12: 0.0,
  H13: 0.0,
  H14: 0.0,
  H15: 0.0,
  H16: 0.0,
  H17: 0.0,
  H18: 0.0,
  JS1: -4,
  JS2: -5,
  JS3: -7.5,
  JS4: -10,
  JS5: -13.5,
  JS6: -20,
  JS7: -31.5,
  JS8: -48.5,
  JS9: -77.5,
  JS10: -125,
  JS11: -200,
  JS12: -315,
  JS13: -485,
  JS14: -775,
  JS15: -1250,
  JS16: -2e3,
  JS17: -3150,
  JS18: -4850,
  J6: -7,
  J7: -20,
  J8: -31,
  K1: -13,
  K2: -15,
  K3: -15,
  K4: -20,
  K5: -25,
  K6: -32,
  K7: -45,
  K8: -68,
  M1: -31,
  M2: -33,
  M3: -33,
  M4: -38,
  M5: -43,
  M6: -50,
  M7: -63,
  M8: -86,
  N1: -48,
  N2: -50,
  N3: -50,
  N4: -55,
  N5: -60,
  N6: -67,
  N7: -80,
  N8: -103,
  P1: -76,
  P2: -78,
  P3: -78,
  P4: -83,
  P5: -88,
  P6: -95,
  P7: -108,
  P8: -165,
  P9: -223,
  P10: -318,
  P11: -468,
  P12: -698,
  P13: -1038,
  P14: -1618,
  P15: -2568,
  P16: -4068,
  P17: -6368,
  P18: -9768
}];

const holeUpperValues = [{
  from: 0,
  to: 3,
  A1: 270.8,
  A2: 271.2,
  A3: 272.0,
  A4: 273.0,
  A5: 274.0,
  A6: 276.0,
  A7: 280.0,
  A8: 284.0,
  A9: 295.0,
  A10: 310.0,
  A11: 330.0,
  A12: 370.0,
  A13: 410.0,
  A14: 520.0,
  A15: 670.0,
  A16: 870.0,
  A17: 1270.0,
  A18: 1670.0,
  B1: 140.8,
  B2: 141.2,
  B3: 142.0,
  B4: 143.0,
  B5: 144.0,
  B6: 146.0,
  B7: 150.0,
  B8: 154.0,
  B9: 165.0,
  B10: 180.0,
  B11: 200.0,
  B12: 240.0,
  B13: 280.0,
  B14: 390.0,
  B15: 540.0,
  B16: 740.0,
  B17: 1140.0,
  B18: 1540.0,
  C1: 60.8,
  C2: 61.2,
  C3: 62.0,
  C4: 63.0,
  C5: 64.0,
  C6: 66.0,
  C7: 70.0,
  C8: 74.0,
  C9: 85.0,
  C10: 100.0,
  C11: 120.0,
  C12: 160.0,
  C13: 200.0,
  C14: 310.0,
  C15: 460.0,
  C16: 660.0,
  C17: 1060.0,
  C18: 1460.0,
  D1: 20.8,
  D2: 21.2,
  D3: 22.0,
  D4: 23.0,
  D5: 24.0,
  D6: 26.0,
  D7: 30.0,
  D8: 34.0,
  D9: 45.0,
  D10: 60.0,
  D11: 80.0,
  D12: 120.0,
  D13: 160.0,
  D14: 270.0,
  D15: 420.0,
  D16: 620.0,
  D17: 1020.0,
  D18: 1420.0,
  E1: 14.8,
  E2: 15.2,
  E3: 16.0,
  E4: 17.0,
  E5: 18.0,
  E6: 20.0,
  E7: 24.0,
  E8: 28.0,
  E9: 39.0,
  E10: 54.0,
  E11: 74.0,
  E12: 114.0,
  E13: 154.0,
  E14: 264.0,
  E15: 414.0,
  E16: 614.0,
  E17: 1014.0,
  E18: 1414.0,
  F1: 6.8,
  F2: 7.2,
  F3: 8.0,
  F4: 9.0,
  F5: 10.0,
  F6: 12.0,
  F7: 16.0,
  F8: 20.0,
  F9: 31.0,
  F10: 46.0,
  F11: 66.0,
  F12: 106.0,
  F13: 146.0,
  F14: 256.0,
  F15: 406.0,
  F16: 606.0,
  F17: 1006.0,
  F18: 1406.0,
  G1: 2.8,
  G2: 3.2,
  G3: 4.0,
  G4: 5.0,
  G5: 6.0,
  G6: 8.0,
  G7: 12.0,
  G8: 16.0,
  G9: 27.0,
  G10: 42.0,
  G11: 62.0,
  G12: 102.0,
  G13: 142.0,
  G14: 252.0,
  G15: 402.0,
  G16: 602.0,
  G17: 1002.0,
  G18: 1402.0,
  H1: 0.8,
  H2: 1.2,
  H3: 2.0,
  H4: 3.0,
  H5: 4.0,
  H6: 6.0,
  H7: 10.0,
  H8: 14.0,
  H9: 25.0,
  H10: 40.0,
  H11: 60.0,
  H12: 100.0,
  H13: 140.0,
  H14: 250.0,
  H15: 400.0,
  H16: 600.0,
  H17: 1000.0,
  H18: 1400.0,
  JS1: 0.4,
  JS2: 0.6,
  JS3: 1.0,
  JS4: 1.5,
  JS5: 2.0,
  JS6: 3.0,
  JS7: 5.0,
  JS8: 7.0,
  JS9: 12.5,
  JS10: 20.0,
  JS11: 30.0,
  JS12: 50.0,
  JS13: 70.0,
  JS14: 125.0,
  JS15: 200.0,
  JS16: 300.0,
  JS17: 500.0,
  JS18: 700.0,
  J6: 2.0,
  J7: 4.0,
  J8: 6.0,
  K1: 0.0,
  K2: 0.0,
  K3: 0.0,
  K4: 0.0,
  K5: 0.0,
  K6: 0.0,
  K7: 0.0,
  K8: 0.0,
  M1: -2,
  M2: -2,
  M3: -2,
  M4: -2,
  M5: -2,
  M6: -2,
  M7: -2,
  M8: -2,
  N1: -4,
  N2: -4,
  N3: -4,
  N4: -4,
  N5: -4,
  N6: -4,
  N7: -4,
  N8: -4,
  P1: -6,
  P2: -6,
  P3: -6,
  P4: -6,
  P5: -6,
  P6: -6,
  P7: -6,
  P8: -6,
  P9: -6,
  P10: -6,
  P11: -6,
  P12: -6,
  P13: -6,
  P14: -6,
  P15: -6,
  P16: -6,
  P17: -6,
  P18: -6
}, {
  from: 3,
  to: 6,
  A1: 271.0,
  A2: 271.5,
  A3: 272.5,
  A4: 274.0,
  A5: 275.0,
  A6: 278.0,
  A7: 282.0,
  A8: 288.0,
  A9: 300.0,
  A10: 318.0,
  A11: 345.0,
  A12: 390.0,
  A13: 450.0,
  A14: 570.0,
  A15: 750.0,
  A16: 1020.0,
  A17: 1470.0,
  A18: 2070.0,
  B1: 141.0,
  B2: 141.5,
  B3: 142.5,
  B4: 144.0,
  B5: 145.0,
  B6: 148.0,
  B7: 152.0,
  B8: 158.0,
  B9: 170.0,
  B10: 188.0,
  B11: 215.0,
  B12: 260.0,
  B13: 320.0,
  B14: 440.0,
  B15: 620.0,
  B16: 890.0,
  B17: 1340.0,
  B18: 1940.0,
  C1: 71.0,
  C2: 71.5,
  C3: 72.5,
  C4: 74.0,
  C5: 75.0,
  C6: 78.0,
  C7: 82.0,
  C8: 88.0,
  C9: 100.0,
  C10: 118.0,
  C11: 145.0,
  C12: 190.0,
  C13: 250.0,
  C14: 370.0,
  C15: 550.0,
  C16: 820.0,
  C17: 1270.0,
  C18: 1870.0,
  D1: 31.0,
  D2: 31.5,
  D3: 32.5,
  D4: 34.0,
  D5: 35.0,
  D6: 38.0,
  D7: 42.0,
  D8: 48.0,
  D9: 60.0,
  D10: 78.0,
  D11: 105.0,
  D12: 150.0,
  D13: 210.0,
  D14: 330.0,
  D15: 510.0,
  D16: 780.0,
  D17: 1230.0,
  D18: 1830.0,
  E1: 21.0,
  E2: 21.5,
  E3: 22.5,
  E4: 24.0,
  E5: 25.0,
  E6: 28.0,
  E7: 32.0,
  E8: 38.0,
  E9: 50.0,
  E10: 68.0,
  E11: 95.0,
  E12: 140.0,
  E13: 200.0,
  E14: 320.0,
  E15: 500.0,
  E16: 770.0,
  E17: 1220.0,
  E18: 1820.0,
  F1: 11.0,
  F2: 11.5,
  F3: 12.5,
  F4: 14.0,
  F5: 15.0,
  F6: 18.0,
  F7: 22.0,
  F8: 28.0,
  F9: 40.0,
  F10: 58.0,
  F11: 85.0,
  F12: 130.0,
  F13: 190.0,
  F14: 310.0,
  F15: 490.0,
  F16: 760.0,
  F17: 1210.0,
  F18: 1810.0,
  G1: 5.0,
  G2: 5.5,
  G3: 6.5,
  G4: 8.0,
  G5: 9.0,
  G6: 12.0,
  G7: 16.0,
  G8: 22.0,
  G9: 34.0,
  G10: 52.0,
  G11: 79.0,
  G12: 124.0,
  G13: 184.0,
  G14: 304.0,
  G15: 484.0,
  G16: 754.0,
  G17: 1204.0,
  G18: 1804.0,
  H1: 1.0,
  H2: 1.5,
  H3: 2.5,
  H4: 4.0,
  H5: 5.0,
  H6: 8.0,
  H7: 12.0,
  H8: 18.0,
  H9: 30.0,
  H10: 48.0,
  H11: 75.0,
  H12: 120.0,
  H13: 180.0,
  H14: 300.0,
  H15: 480.0,
  H16: 750.0,
  H17: 1200.0,
  H18: 1800.0,
  JS1: 0.5,
  JS2: 0.8,
  JS3: 1.2,
  JS4: 2.0,
  JS5: 2.5,
  JS6: 4.0,
  JS7: 6.0,
  JS8: 9.0,
  JS9: 15.0,
  JS10: 24.0,
  JS11: 37.5,
  JS12: 60.0,
  JS13: 90.0,
  JS14: 150.0,
  JS15: 240.0,
  JS16: 375.0,
  JS17: 600.0,
  JS18: 900.0,
  J6: 5.0,
  J7: 6.0,
  J8: 10.0,
  K1: -1,
  K2: -1,
  K3: 0.0,
  K4: 0.5,
  K5: 0.0,
  K6: 2.0,
  K7: 3.0,
  K8: 5.0,
  M1: -4,
  M2: -4,
  M3: -3,
  M4: -2.5,
  M5: -3,
  M6: -1,
  M7: 0.0,
  M8: 2.0,
  N1: -8,
  N2: -8,
  N3: -7,
  N4: -6.5,
  N5: -7,
  N6: -5,
  N7: -4,
  N8: -2,
  P1: -12,
  P2: -12,
  P3: -11,
  P4: -10.5,
  P5: -11,
  P6: -9,
  P7: -8,
  P8: -12,
  P9: -12,
  P10: -12,
  P11: -12,
  P12: -12,
  P13: -12,
  P14: -12,
  P15: -12,
  P16: -12,
  P17: -12,
  P18: -12
}, {
  from: 6,
  to: 10,
  A1: 281.0,
  A2: 281.5,
  A3: 282.5,
  A4: 284.0,
  A5: 286.0,
  A6: 289.0,
  A7: 295.0,
  A8: 302.0,
  A9: 316.0,
  A10: 338.0,
  A11: 370.0,
  A12: 430.0,
  A13: 500.0,
  A14: 640.0,
  A15: 860.0,
  A16: 1180.0,
  A17: 1780.0,
  A18: 2480.0,
  B1: 151.0,
  B2: 151.5,
  B3: 152.5,
  B4: 154.0,
  B5: 156.0,
  B6: 159.0,
  B7: 165.0,
  B8: 172.0,
  B9: 186.0,
  B10: 208.0,
  B11: 240.0,
  B12: 300.0,
  B13: 370.0,
  B14: 510.0,
  B15: 730.0,
  B16: 1050.0,
  B17: 1650.0,
  B18: 2350.0,
  C1: 81.0,
  C2: 81.5,
  C3: 82.5,
  C4: 84.0,
  C5: 86.0,
  C6: 89.0,
  C7: 95.0,
  C8: 102.0,
  C9: 116.0,
  C10: 138.0,
  C11: 170.0,
  C12: 230.0,
  C13: 300.0,
  C14: 440.0,
  C15: 660.0,
  C16: 980.0,
  C17: 1580.0,
  C18: 2280.0,
  D1: 41.0,
  D2: 41.5,
  D3: 42.5,
  D4: 44.0,
  D5: 46.0,
  D6: 49.0,
  D7: 55.0,
  D8: 62.0,
  D9: 76.0,
  D10: 98.0,
  D11: 130.0,
  D12: 190.0,
  D13: 260.0,
  D14: 400.0,
  D15: 620.0,
  D16: 940.0,
  D17: 1540.0,
  D18: 2240.0,
  E1: 26.0,
  E2: 26.5,
  E3: 27.5,
  E4: 29.0,
  E5: 31.0,
  E6: 34.0,
  E7: 40.0,
  E8: 47.0,
  E9: 61.0,
  E10: 83.0,
  E11: 115.0,
  E12: 175.0,
  E13: 245.0,
  E14: 385.0,
  E15: 605.0,
  E16: 925.0,
  E17: 1525.0,
  E18: 2225.0,
  F1: 14.0,
  F2: 14.5,
  F3: 15.5,
  F4: 17.0,
  F5: 19.0,
  F6: 22.0,
  F7: 28.0,
  F8: 35.0,
  F9: 49.0,
  F10: 71.0,
  F11: 103.0,
  F12: 163.0,
  F13: 233.0,
  F14: 373.0,
  F15: 593.0,
  F16: 913.0,
  F17: 1513.0,
  F18: 2213.0,
  G1: 6.0,
  G2: 6.5,
  G3: 7.5,
  G4: 9.0,
  G5: 11.0,
  G6: 14.0,
  G7: 20.0,
  G8: 27.0,
  G9: 41.0,
  G10: 63.0,
  G11: 95.0,
  G12: 155.0,
  G13: 225.0,
  G14: 365.0,
  G15: 585.0,
  G16: 905.0,
  G17: 1505.0,
  G18: 2205.0,
  H1: 1.0,
  H2: 1.5,
  H3: 2.5,
  H4: 4.0,
  H5: 6.0,
  H6: 9.0,
  H7: 15.0,
  H8: 22.0,
  H9: 36.0,
  H10: 58.0,
  H11: 90.0,
  H12: 150.0,
  H13: 220.0,
  H14: 360.0,
  H15: 580.0,
  H16: 900.0,
  H17: 1500.0,
  H18: 2200.0,
  JS1: 0.5,
  JS2: 0.8,
  JS3: 1.3,
  JS4: 2.0,
  JS5: 3.0,
  JS6: 4.5,
  JS7: 7.5,
  JS8: 11.0,
  JS9: 18.0,
  JS10: 29.0,
  JS11: 45.0,
  JS12: 75.0,
  JS13: 110.0,
  JS14: 180.0,
  JS15: 290.0,
  JS16: 450.0,
  JS17: 750.0,
  JS18: 1100.0,
  J6: 5.0,
  J7: 8.0,
  J8: 12.0,
  K1: -1,
  K2: -1,
  K3: 0.0,
  K4: 0.5,
  K5: 1.0,
  K6: 2.0,
  K7: 5.0,
  K8: 6.0,
  M1: -6,
  M2: -6,
  M3: -5,
  M4: -4.5,
  M5: -4,
  M6: -3,
  M7: 0.0,
  M8: 1.0,
  N1: -10,
  N2: -10,
  N3: -9,
  N4: -8.5,
  N5: -8,
  N6: -7,
  N7: -4,
  N8: -3,
  P1: -15,
  P2: -15,
  P3: -14,
  P4: -13.5,
  P5: -13,
  P6: -12,
  P7: -9,
  P8: -15,
  P9: -15,
  P10: -15,
  P11: -15,
  P12: -15,
  P13: -15,
  P14: -15,
  P15: -15,
  P16: -15,
  P17: -15,
  P18: -15
}, {
  from: 10,
  to: 18,
  A1: 291.2,
  A2: 292.0,
  A3: 293.0,
  A4: 295.0,
  A5: 298.0,
  A6: 301.0,
  A7: 308.0,
  A8: 317.0,
  A9: 333.0,
  A10: 360.0,
  A11: 400.0,
  A12: 470.0,
  A13: 560.0,
  A14: 720.0,
  A15: 990.0,
  A16: 1390.0,
  A17: 2090.0,
  A18: 2990.0,
  B1: 151.2,
  B2: 152.0,
  B3: 153.0,
  B4: 155.0,
  B5: 158.0,
  B6: 161.0,
  B7: 168.0,
  B8: 177.0,
  B9: 193.0,
  B10: 220.0,
  B11: 260.0,
  B12: 330.0,
  B13: 420.0,
  B14: 580.0,
  B15: 850.0,
  B16: 1250.0,
  B17: 1950.0,
  B18: 2850.0,
  C1: 96.2,
  C2: 97.0,
  C3: 98.0,
  C4: 100.0,
  C5: 103.0,
  C6: 106.0,
  C7: 113.0,
  C8: 122.0,
  C9: 138.0,
  C10: 165.0,
  C11: 205.0,
  C12: 275.0,
  C13: 365.0,
  C14: 525.0,
  C15: 795.0,
  C16: 1195.0,
  C17: 1895.0,
  C18: 2795.0,
  D1: 51.2,
  D2: 52.0,
  D3: 53.0,
  D4: 55.0,
  D5: 58.0,
  D6: 61.0,
  D7: 68.0,
  D8: 77.0,
  D9: 93.0,
  D10: 120.0,
  D11: 160.0,
  D12: 230.0,
  D13: 320.0,
  D14: 480.0,
  D15: 750.0,
  D16: 1150.0,
  D17: 1850.0,
  D18: 2750.0,
  E1: 33.2,
  E2: 34.0,
  E3: 35.0,
  E4: 37.0,
  E5: 40.0,
  E6: 43.0,
  E7: 50.0,
  E8: 59.0,
  E9: 75.0,
  E10: 102.0,
  E11: 142.0,
  E12: 212.0,
  E13: 302.0,
  E14: 462.0,
  E15: 732.0,
  E16: 1132.0,
  E17: 1832.0,
  E18: 2732.0,
  F1: 17.2,
  F2: 18.0,
  F3: 19.0,
  F4: 21.0,
  F5: 24.0,
  F6: 27.0,
  F7: 34.0,
  F8: 43.0,
  F9: 59.0,
  F10: 86.0,
  F11: 126.0,
  F12: 196.0,
  F13: 286.0,
  F14: 446.0,
  F15: 716.0,
  F16: 1116.0,
  F17: 1816.0,
  F18: 2716.0,
  G1: 7.2,
  G2: 8.0,
  G3: 9.0,
  G4: 11.0,
  G5: 14.0,
  G6: 17.0,
  G7: 24.0,
  G8: 33.0,
  G9: 49.0,
  G10: 76.0,
  G11: 116.0,
  G12: 186.0,
  G13: 276.0,
  G14: 436.0,
  G15: 706.0,
  G16: 1106.0,
  G17: 1806.0,
  G18: 2706.0,
  H1: 1.2,
  H2: 2.0,
  H3: 3.0,
  H4: 5.0,
  H5: 8.0,
  H6: 11.0,
  H7: 18.0,
  H8: 27.0,
  H9: 43.0,
  H10: 70.0,
  H11: 110.0,
  H12: 180.0,
  H13: 270.0,
  H14: 430.0,
  H15: 700.0,
  H16: 1100.0,
  H17: 1800.0,
  H18: 2700.0,
  JS1: 0.6,
  JS2: 1.0,
  JS3: 1.5,
  JS4: 2.5,
  JS5: 4.0,
  JS6: 5.5,
  JS7: 9.0,
  JS8: 13.5,
  JS9: 21.5,
  JS10: 35.0,
  JS11: 55.0,
  JS12: 90.0,
  JS13: 135.0,
  JS14: 215.0,
  JS15: 350.0,
  JS16: 550.0,
  JS17: 900.0,
  JS18: 1350.0,
  J6: 6.0,
  J7: 10.0,
  J8: 15.0,
  K1: -1,
  K2: -1,
  K3: 0.0,
  K4: 1.0,
  K5: 2.0,
  K6: 2.0,
  K7: 6.0,
  K8: 8.0,
  M1: -7,
  M2: -7,
  M3: -6,
  M4: -5,
  M5: -4,
  M6: -4,
  M7: 0.0,
  M8: 2.0,
  N1: -12,
  N2: -12,
  N3: -11,
  N4: -10,
  N5: -9,
  N6: -9,
  N7: -5,
  N8: -3,
  P1: -18,
  P2: -18,
  P3: -17,
  P4: -16,
  P5: -15,
  P6: -15,
  P7: -11,
  P8: -18,
  P9: -18,
  P10: -18,
  P11: -18,
  P12: -18,
  P13: -18,
  P14: -18,
  P15: -18,
  P16: -18,
  P17: -18,
  P18: -18
}, {
  from: 18,
  to: 30,
  A1: 301.5,
  A2: 302.5,
  A3: 304.0,
  A4: 306.0,
  A5: 309.0,
  A6: 313.0,
  A7: 321.0,
  A8: 333.0,
  A9: 352.0,
  A10: 384.0,
  A11: 430.0,
  A12: 510.0,
  A13: 630.0,
  A14: 820.0,
  A15: 1140.0,
  A16: 1600.0,
  A17: 2400.0,
  A18: 3600.0,
  B1: 161.5,
  B2: 162.5,
  B3: 164.0,
  B4: 166.0,
  B5: 169.0,
  B6: 173.0,
  B7: 181.0,
  B8: 193.0,
  B9: 212.0,
  B10: 244.0,
  B11: 290.0,
  B12: 370.0,
  B13: 490.0,
  B14: 680.0,
  B15: 1000.0,
  B16: 1460.0,
  B17: 2260.0,
  B18: 3460.0,
  C1: 111.5,
  C2: 112.5,
  C3: 114.0,
  C4: 116.0,
  C5: 119.0,
  C6: 123.0,
  C7: 131.0,
  C8: 143.0,
  C9: 162.0,
  C10: 194.0,
  C11: 240.0,
  C12: 320.0,
  C13: 440.0,
  C14: 630.0,
  C15: 950.0,
  C16: 1410.0,
  C17: 2210.0,
  C18: 3410.0,
  D1: 66.5,
  D2: 67.5,
  D3: 69.0,
  D4: 71.0,
  D5: 74.0,
  D6: 78.0,
  D7: 86.0,
  D8: 98.0,
  D9: 117.0,
  D10: 149.0,
  D11: 195.0,
  D12: 275.0,
  D13: 395.0,
  D14: 585.0,
  D15: 905.0,
  D16: 1365.0,
  D17: 2165.0,
  D18: 3365.0,
  E1: 41.5,
  E2: 42.5,
  E3: 44.0,
  E4: 46.0,
  E5: 49.0,
  E6: 53.0,
  E7: 61.0,
  E8: 73.0,
  E9: 92.0,
  E10: 124.0,
  E11: 170.0,
  E12: 250.0,
  E13: 370.0,
  E14: 560.0,
  E15: 880.0,
  E16: 1340.0,
  E17: 2140.0,
  E18: 3340.0,
  F1: 21.5,
  F2: 22.5,
  F3: 24.0,
  F4: 26.0,
  F5: 29.0,
  F6: 33.0,
  F7: 41.0,
  F8: 53.0,
  F9: 72.0,
  F10: 104.0,
  F11: 150.0,
  F12: 230.0,
  F13: 350.0,
  F14: 540.0,
  F15: 860.0,
  F16: 1320.0,
  F17: 2120.0,
  F18: 3320.0,
  G1: 8.5,
  G2: 9.5,
  G3: 11.0,
  G4: 13.0,
  G5: 16.0,
  G6: 20.0,
  G7: 28.0,
  G8: 40.0,
  G9: 59.0,
  G10: 91.0,
  G11: 137.0,
  G12: 217.0,
  G13: 337.0,
  G14: 527.0,
  G15: 847.0,
  G16: 1307.0,
  G17: 2107.0,
  G18: 3307.0,
  H1: 1.5,
  H2: 2.5,
  H3: 4.0,
  H4: 6.0,
  H5: 9.0,
  H6: 13.0,
  H7: 21.0,
  H8: 33.0,
  H9: 52.0,
  H10: 84.0,
  H11: 130.0,
  H12: 210.0,
  H13: 330.0,
  H14: 520.0,
  H15: 840.0,
  H16: 1300.0,
  H17: 2100.0,
  H18: 3300.0,
  JS1: 0.8,
  JS2: 1.2,
  JS3: 2.0,
  JS4: 3.0,
  JS5: 4.5,
  JS6: 6.5,
  JS7: 10.5,
  JS8: 16.5,
  JS9: 26.0,
  JS10: 42.0,
  JS11: 65.0,
  JS12: 105.0,
  JS13: 165.0,
  JS14: 260.0,
  JS15: 420.0,
  JS16: 650.0,
  JS17: 1050.0,
  JS18: 1650.0,
  J6: 8.0,
  J7: 12.0,
  J8: 20.0,
  K1: -2,
  K2: -2,
  K3: -0.5,
  K4: 0.0,
  K5: 1.0,
  K6: 2.0,
  K7: 6.0,
  K8: 10.0,
  M1: -8,
  M2: -8,
  M3: -6.5,
  M4: -6,
  M5: -5,
  M6: -4,
  M7: 0.0,
  M8: 4.0,
  N1: -15,
  N2: -15,
  N3: -13.5,
  N4: -13,
  N5: -12,
  N6: -11,
  N7: -7,
  N8: -3,
  P1: -22,
  P2: -22,
  P3: -20.5,
  P4: -20,
  P5: -19,
  P6: -18,
  P7: -14,
  P8: -22,
  P9: -22,
  P10: -22,
  P11: -22,
  P12: -22,
  P13: -22,
  P14: -22,
  P15: -22,
  P16: -22,
  P17: -22,
  P18: -22
}, {
  from: 30,
  to: 40,
  A1: 311.5,
  A2: 312.5,
  A3: 314.0,
  A4: 317.0,
  A5: 321.0,
  A6: 326.0,
  A7: 335.0,
  A8: 349.0,
  A9: 372.0,
  A10: 410.0,
  A11: 470.0,
  A12: 560.0,
  A13: 700.0,
  A14: 930.0,
  A15: 1310.0,
  A16: 1910.0,
  A17: 2810.0,
  A18: 4210.0,
  B1: 171.5,
  B2: 172.5,
  B3: 174.0,
  B4: 177.0,
  B5: 181.0,
  B6: 186.0,
  B7: 195.0,
  B8: 209.0,
  B9: 232.0,
  B10: 270.0,
  B11: 330.0,
  B12: 420.0,
  B13: 560.0,
  B14: 790.0,
  B15: 1170.0,
  B16: 1770.0,
  B17: 2670.0,
  B18: 4070.0,
  C1: 121.5,
  C2: 122.5,
  C3: 124.0,
  C4: 127.0,
  C5: 131.0,
  C6: 136.0,
  C7: 145.0,
  C8: 159.0,
  C9: 182.0,
  C10: 220.0,
  C11: 280.0,
  C12: 370.0,
  C13: 510.0,
  C14: 740.0,
  C15: 1120.0,
  C16: 1720.0,
  C17: 2620.0,
  C18: 4020.0,
  D1: 81.5,
  D2: 82.5,
  D3: 84.0,
  D4: 87.0,
  D5: 91.0,
  D6: 96.0,
  D7: 105.0,
  D8: 119.0,
  D9: 142.0,
  D10: 180.0,
  D11: 240.0,
  D12: 330.0,
  D13: 470.0,
  D14: 700.0,
  D15: 1080.0,
  D16: 1680.0,
  D17: 2580.0,
  D18: 3980.0,
  E1: 51.5,
  E2: 52.5,
  E3: 54.0,
  E4: 57.0,
  E5: 61.0,
  E6: 66.0,
  E7: 75.0,
  E8: 89.0,
  E9: 112.0,
  E10: 150.0,
  E11: 210.0,
  E12: 300.0,
  E13: 440.0,
  E14: 670.0,
  E15: 1050.0,
  E16: 1650.0,
  E17: 2550.0,
  E18: 3950.0,
  F1: 26.5,
  F2: 27.5,
  F3: 29.0,
  F4: 32.0,
  F5: 36.0,
  F6: 41.0,
  F7: 50.0,
  F8: 64.0,
  F9: 87.0,
  F10: 125.0,
  F11: 185.0,
  F12: 275.0,
  F13: 415.0,
  F14: 645.0,
  F15: 1025.0,
  F16: 1625.0,
  F17: 2525.0,
  F18: 3925.0,
  G1: 10.5,
  G2: 11.5,
  G3: 13.0,
  G4: 16.0,
  G5: 20.0,
  G6: 25.0,
  G7: 34.0,
  G8: 48.0,
  G9: 71.0,
  G10: 109.0,
  G11: 169.0,
  G12: 259.0,
  G13: 399.0,
  G14: 629.0,
  G15: 1009.0,
  G16: 1609.0,
  G17: 2509.0,
  G18: 3909.0,
  H1: 1.5,
  H2: 2.5,
  H3: 4.0,
  H4: 7.0,
  H5: 11.0,
  H6: 16.0,
  H7: 25.0,
  H8: 39.0,
  H9: 62.0,
  H10: 100.0,
  H11: 160.0,
  H12: 250.0,
  H13: 390.0,
  H14: 620.0,
  H15: 1000.0,
  H16: 1600.0,
  H17: 2500.0,
  H18: 3900.0,
  JS1: 0.7,
  JS2: 1.2,
  JS3: 2.0,
  JS4: 3.5,
  JS5: 5.5,
  JS6: 8.0,
  JS7: 12.5,
  JS8: 19.5,
  JS9: 31.0,
  JS10: 50.0,
  JS11: 80.0,
  JS12: 125.0,
  JS13: 195.0,
  JS14: 310.0,
  JS15: 500.0,
  JS16: 800.0,
  JS17: 1250.0,
  JS18: 1950.0,
  J6: 10.0,
  J7: 14.0,
  J8: 24.0,
  K1: -2,
  K2: -2,
  K3: -0.5,
  K4: 1.0,
  K5: 2.0,
  K6: 3.0,
  K7: 7.0,
  K8: 12.0,
  M1: -9,
  M2: -9,
  M3: -7.5,
  M4: -6,
  M5: -5,
  M6: -4,
  M7: 0.0,
  M8: 5.0,
  N1: -17,
  N2: -17,
  N3: -15.5,
  N4: -14,
  N5: -13,
  N6: -12,
  N7: -8,
  N8: -3,
  P1: -26,
  P2: -26,
  P3: -24.5,
  P4: -23,
  P5: -22,
  P6: -21,
  P7: -17,
  P8: -26,
  P9: -26,
  P10: -26,
  P11: -26,
  P12: -26,
  P13: -26,
  P14: -26,
  P15: -26,
  P16: -26,
  P17: -26,
  P18: -26
}, {
  from: 40,
  to: 50,
  A1: 321.5,
  A2: 322.5,
  A3: 324.0,
  A4: 327.0,
  A5: 331.0,
  A6: 336.0,
  A7: 345.0,
  A8: 359.0,
  A9: 382.0,
  A10: 420.0,
  A11: 480.0,
  A12: 570.0,
  A13: 710.0,
  A14: 940.0,
  A15: 1320.0,
  A16: 1920.0,
  A17: 2820.0,
  A18: 4220.0,
  B1: 181.5,
  B2: 182.5,
  B3: 184.0,
  B4: 187.0,
  B5: 191.0,
  B6: 196.0,
  B7: 205.0,
  B8: 219.0,
  B9: 242.0,
  B10: 280.0,
  B11: 340.0,
  B12: 430.0,
  B13: 570.0,
  B14: 800.0,
  B15: 1180.0,
  B16: 1780.0,
  B17: 2680.0,
  B18: 4080.0,
  C1: 131.5,
  C2: 132.5,
  C3: 134.0,
  C4: 137.0,
  C5: 141.0,
  C6: 146.0,
  C7: 155.0,
  C8: 169.0,
  C9: 192.0,
  C10: 230.0,
  C11: 290.0,
  C12: 380.0,
  C13: 520.0,
  C14: 750.0,
  C15: 1130.0,
  C16: 1730.0,
  C17: 2630.0,
  C18: 4030.0,
  D1: 81.5,
  D2: 82.5,
  D3: 84.0,
  D4: 87.0,
  D5: 91.0,
  D6: 96.0,
  D7: 105.0,
  D8: 119.0,
  D9: 142.0,
  D10: 180.0,
  D11: 240.0,
  D12: 330.0,
  D13: 470.0,
  D14: 700.0,
  D15: 1080.0,
  D16: 1680.0,
  D17: 2580.0,
  D18: 3980.0,
  E1: 51.5,
  E2: 52.5,
  E3: 54.0,
  E4: 57.0,
  E5: 61.0,
  E6: 66.0,
  E7: 75.0,
  E8: 89.0,
  E9: 112.0,
  E10: 150.0,
  E11: 210.0,
  E12: 300.0,
  E13: 440.0,
  E14: 670.0,
  E15: 1050.0,
  E16: 1650.0,
  E17: 2550.0,
  E18: 3950.0,
  F1: 26.5,
  F2: 27.5,
  F3: 29.0,
  F4: 32.0,
  F5: 36.0,
  F6: 41.0,
  F7: 50.0,
  F8: 64.0,
  F9: 87.0,
  F10: 125.0,
  F11: 185.0,
  F12: 275.0,
  F13: 415.0,
  F14: 645.0,
  F15: 1025.0,
  F16: 1625.0,
  F17: 2525.0,
  F18: 3925.0,
  G1: 10.5,
  G2: 11.5,
  G3: 13.0,
  G4: 16.0,
  G5: 20.0,
  G6: 25.0,
  G7: 34.0,
  G8: 48.0,
  G9: 71.0,
  G10: 109.0,
  G11: 169.0,
  G12: 259.0,
  G13: 399.0,
  G14: 629.0,
  G15: 1009.0,
  G16: 1609.0,
  G17: 2509.0,
  G18: 3909.0,
  H1: 1.5,
  H2: 2.5,
  H3: 4.0,
  H4: 7.0,
  H5: 11.0,
  H6: 16.0,
  H7: 25.0,
  H8: 39.0,
  H9: 62.0,
  H10: 100.0,
  H11: 160.0,
  H12: 250.0,
  H13: 390.0,
  H14: 620.0,
  H15: 1000.0,
  H16: 1600.0,
  H17: 2500.0,
  H18: 3900.0,
  JS1: 0.7,
  JS2: 1.2,
  JS3: 2.0,
  JS4: 3.5,
  JS5: 5.5,
  JS6: 8.0,
  JS7: 12.5,
  JS8: 19.5,
  JS9: 31.0,
  JS10: 50.0,
  JS11: 80.0,
  JS12: 125.0,
  JS13: 195.0,
  JS14: 310.0,
  JS15: 500.0,
  JS16: 800.0,
  JS17: 1250.0,
  JS18: 1950.0,
  J6: 10.0,
  J7: 14.0,
  J8: 24.0,
  K1: -2,
  K2: -2,
  K3: -0.5,
  K4: 1.0,
  K5: 2.0,
  K6: 3.0,
  K7: 7.0,
  K8: 12.0,
  M1: -9,
  M2: -9,
  M3: -7.5,
  M4: -6,
  M5: -5,
  M6: -4,
  M7: 0.0,
  M8: 5.0,
  N1: -17,
  N2: -17,
  N3: -15.5,
  N4: -14,
  N5: -13,
  N6: -12,
  N7: -8,
  N8: -3,
  P1: -26,
  P2: -26,
  P3: -24.5,
  P4: -23,
  P5: -22,
  P6: -21,
  P7: -17,
  P8: -26,
  P9: -26,
  P10: -26,
  P11: -26,
  P12: -26,
  P13: -26,
  P14: -26,
  P15: -26,
  P16: -26,
  P17: -26,
  P18: -26
}, {
  from: 50,
  to: 65,
  A1: 342.0,
  A2: 343.0,
  A3: 345.0,
  A4: 348.0,
  A5: 353.0,
  A6: 359.0,
  A7: 370.0,
  A8: 386.0,
  A9: 414.0,
  A10: 460.0,
  A11: 530.0,
  A12: 640.0,
  A13: 800.0,
  A14: 1080.0,
  A15: 1540.0,
  A16: 2240.0,
  A17: 3340.0,
  A18: 4940.0,
  B1: 192.0,
  B2: 193.0,
  B3: 195.0,
  B4: 198.0,
  B5: 203.0,
  B6: 209.0,
  B7: 220.0,
  B8: 236.0,
  B9: 264.0,
  B10: 310.0,
  B11: 380.0,
  B12: 490.0,
  B13: 650.0,
  B14: 930.0,
  B15: 1390.0,
  B16: 2090.0,
  B17: 3190.0,
  B18: 4790.0,
  C1: 142.0,
  C2: 143.0,
  C3: 145.0,
  C4: 148.0,
  C5: 153.0,
  C6: 159.0,
  C7: 170.0,
  C8: 186.0,
  C9: 214.0,
  C10: 260.0,
  C11: 330.0,
  C12: 440.0,
  C13: 600.0,
  C14: 880.0,
  C15: 1340.0,
  C16: 2040.0,
  C17: 3140.0,
  C18: 4740.0,
  D1: 102.0,
  D2: 103.0,
  D3: 105.0,
  D4: 108.0,
  D5: 113.0,
  D6: 119.0,
  D7: 130.0,
  D8: 146.0,
  D9: 174.0,
  D10: 220.0,
  D11: 290.0,
  D12: 400.0,
  D13: 560.0,
  D14: 840.0,
  D15: 1300.0,
  D16: 2000.0,
  D17: 3100.0,
  D18: 4700.0,
  E1: 62.0,
  E2: 63.0,
  E3: 65.0,
  E4: 68.0,
  E5: 73.0,
  E6: 79.0,
  E7: 90.0,
  E8: 106.0,
  E9: 134.0,
  E10: 180.0,
  E11: 250.0,
  E12: 360.0,
  E13: 520.0,
  E14: 800.0,
  E15: 1260.0,
  E16: 1960.0,
  E17: 3060.0,
  E18: 4660.0,
  F1: 32.0,
  F2: 33.0,
  F3: 35.0,
  F4: 38.0,
  F5: 43.0,
  F6: 49.0,
  F7: 60.0,
  F8: 76.0,
  F9: 104.0,
  F10: 150.0,
  F11: 220.0,
  F12: 330.0,
  F13: 490.0,
  F14: 770.0,
  F15: 1230.0,
  F16: 1930.0,
  F17: 3030.0,
  F18: 4630.0,
  G1: 12.0,
  G2: 13.0,
  G3: 15.0,
  G4: 18.0,
  G5: 23.0,
  G6: 29.0,
  G7: 40.0,
  G8: 56.0,
  G9: 84.0,
  G10: 130.0,
  G11: 200.0,
  G12: 310.0,
  G13: 470.0,
  G14: 750.0,
  G15: 1210.0,
  G16: 1910.0,
  G17: 3010.0,
  G18: 4610.0,
  H1: 2.0,
  H2: 3.0,
  H3: 5.0,
  H4: 8.0,
  H5: 13.0,
  H6: 19.0,
  H7: 30.0,
  H8: 46.0,
  H9: 74.0,
  H10: 120.0,
  H11: 190.0,
  H12: 300.0,
  H13: 460.0,
  H14: 740.0,
  H15: 1200.0,
  H16: 1900.0,
  H17: 3000.0,
  H18: 4600.0,
  JS1: 1.0,
  JS2: 1.5,
  JS3: 2.5,
  JS4: 4.0,
  JS5: 6.5,
  JS6: 9.5,
  JS7: 15.0,
  JS8: 23.0,
  JS9: 37.0,
  JS10: 60.0,
  JS11: 95.0,
  JS12: 150.0,
  JS13: 230.0,
  JS14: 370.0,
  JS15: 600.0,
  JS16: 950.0,
  JS17: 1500.0,
  JS18: 2300.0,
  J6: 13.0,
  J7: 18.0,
  J8: 28.0,
  K1: -2,
  K2: -2,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 4.0,
  K7: 9.0,
  K8: 14.0,
  M1: -11,
  M2: -11,
  M3: -9,
  M4: -8,
  M5: -6,
  M6: -5,
  M7: 0.0,
  M8: 5.0,
  N1: -20,
  N2: -20,
  N3: -18,
  N4: -17,
  N5: -15,
  N6: -14,
  N7: -9,
  N8: -4,
  P1: -32,
  P2: -32,
  P3: -30,
  P4: -29,
  P5: -27,
  P6: -26,
  P7: -21,
  P8: -32,
  P9: -32,
  P10: -32,
  P11: -32,
  P12: -32,
  P13: -32,
  P14: -32,
  P15: -32,
  P16: -32,
  P17: -32,
  P18: -32
}, {
  from: 65,
  to: 80,
  A1: 362.0,
  A2: 363.0,
  A3: 365.0,
  A4: 368.0,
  A5: 373.0,
  A6: 379.0,
  A7: 390.0,
  A8: 406.0,
  A9: 434.0,
  A10: 480.0,
  A11: 550.0,
  A12: 660.0,
  A13: 820.0,
  A14: 1100.0,
  A15: 1560.0,
  A16: 2260.0,
  A17: 3360.0,
  A18: 4960.0,
  B1: 202.0,
  B2: 203.0,
  B3: 205.0,
  B4: 208.0,
  B5: 213.0,
  B6: 219.0,
  B7: 230.0,
  B8: 246.0,
  B9: 274.0,
  B10: 320.0,
  B11: 390.0,
  B12: 500.0,
  B13: 660.0,
  B14: 940.0,
  B15: 1400.0,
  B16: 2100.0,
  B17: 3200.0,
  B18: 4800.0,
  C1: 152.0,
  C2: 153.0,
  C3: 155.0,
  C4: 158.0,
  C5: 163.0,
  C6: 169.0,
  C7: 180.0,
  C8: 196.0,
  C9: 224.0,
  C10: 270.0,
  C11: 340.0,
  C12: 450.0,
  C13: 610.0,
  C14: 890.0,
  C15: 1350.0,
  C16: 2050.0,
  C17: 3150.0,
  C18: 4750.0,
  D1: 102.0,
  D2: 103.0,
  D3: 105.0,
  D4: 108.0,
  D5: 113.0,
  D6: 119.0,
  D7: 130.0,
  D8: 146.0,
  D9: 174.0,
  D10: 220.0,
  D11: 290.0,
  D12: 400.0,
  D13: 560.0,
  D14: 840.0,
  D15: 1300.0,
  D16: 2000.0,
  D17: 3100.0,
  D18: 4700.0,
  E1: 62.0,
  E2: 63.0,
  E3: 65.0,
  E4: 68.0,
  E5: 73.0,
  E6: 79.0,
  E7: 90.0,
  E8: 106.0,
  E9: 134.0,
  E10: 180.0,
  E11: 250.0,
  E12: 360.0,
  E13: 520.0,
  E14: 800.0,
  E15: 1260.0,
  E16: 1960.0,
  E17: 3060.0,
  E18: 4660.0,
  F1: 32.0,
  F2: 33.0,
  F3: 35.0,
  F4: 38.0,
  F5: 43.0,
  F6: 49.0,
  F7: 60.0,
  F8: 76.0,
  F9: 104.0,
  F10: 150.0,
  F11: 220.0,
  F12: 330.0,
  F13: 490.0,
  F14: 770.0,
  F15: 1230.0,
  F16: 1930.0,
  F17: 3030.0,
  F18: 4630.0,
  G1: 12.0,
  G2: 13.0,
  G3: 15.0,
  G4: 18.0,
  G5: 23.0,
  G6: 29.0,
  G7: 40.0,
  G8: 56.0,
  G9: 84.0,
  G10: 130.0,
  G11: 200.0,
  G12: 310.0,
  G13: 470.0,
  G14: 750.0,
  G15: 1210.0,
  G16: 1910.0,
  G17: 3010.0,
  G18: 4610.0,
  H1: 2.0,
  H2: 3.0,
  H3: 5.0,
  H4: 8.0,
  H5: 13.0,
  H6: 19.0,
  H7: 30.0,
  H8: 46.0,
  H9: 74.0,
  H10: 120.0,
  H11: 190.0,
  H12: 300.0,
  H13: 460.0,
  H14: 740.0,
  H15: 1200.0,
  H16: 1900.0,
  H17: 3000.0,
  H18: 4600.0,
  JS1: 1.0,
  JS2: 1.5,
  JS3: 2.5,
  JS4: 4.0,
  JS5: 6.5,
  JS6: 9.5,
  JS7: 15.0,
  JS8: 23.0,
  JS9: 37.0,
  JS10: 60.0,
  JS11: 95.0,
  JS12: 150.0,
  JS13: 230.0,
  JS14: 370.0,
  JS15: 600.0,
  JS16: 950.0,
  JS17: 1500.0,
  JS18: 2300.0,
  J6: 13.0,
  J7: 18.0,
  J8: 28.0,
  K1: -2,
  K2: -2,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 4.0,
  K7: 9.0,
  K8: 14.0,
  M1: -11,
  M2: -11,
  M3: -9,
  M4: -8,
  M5: -6,
  M6: -5,
  M7: 0.0,
  M8: 5.0,
  N1: -20,
  N2: -20,
  N3: -18,
  N4: -17,
  N5: -15,
  N6: -14,
  N7: -9,
  N8: -4,
  P1: -32,
  P2: -32,
  P3: -30,
  P4: -29,
  P5: -27,
  P6: -26,
  P7: -21,
  P8: -32,
  P9: -32,
  P10: -32,
  P11: -32,
  P12: -32,
  P13: -32,
  P14: -32,
  P15: -32,
  P16: -32,
  P17: -32,
  P18: -32
}, {
  from: 80,
  to: 100,
  A1: 382.5,
  A2: 384.0,
  A3: 386.0,
  A4: 390.0,
  A5: 395.0,
  A6: 402.0,
  A7: 415.0,
  A8: 434.0,
  A9: 467.0,
  A10: 520.0,
  A11: 600.0,
  A12: 730.0,
  A13: 920.0,
  A14: 1250.0,
  A15: 1780.0,
  A16: 2580.0,
  A17: 3880.0,
  A18: 5780.0,
  B1: 222.5,
  B2: 224.0,
  B3: 226.0,
  B4: 230.0,
  B5: 235.0,
  B6: 242.0,
  B7: 255.0,
  B8: 274.0,
  B9: 307.0,
  B10: 360.0,
  B11: 440.0,
  B12: 570.0,
  B13: 760.0,
  B14: 1090.0,
  B15: 1620.0,
  B16: 2420.0,
  B17: 3720.0,
  B18: 5620.0,
  C1: 172.5,
  C2: 174.0,
  C3: 176.0,
  C4: 180.0,
  C5: 185.0,
  C6: 192.0,
  C7: 205.0,
  C8: 224.0,
  C9: 257.0,
  C10: 310.0,
  C11: 390.0,
  C12: 520.0,
  C13: 710.0,
  C14: 1040.0,
  C15: 1570.0,
  C16: 2370.0,
  C17: 3670.0,
  C18: 5570.0,
  D1: 122.5,
  D2: 124.0,
  D3: 126.0,
  D4: 130.0,
  D5: 135.0,
  D6: 142.0,
  D7: 155.0,
  D8: 174.0,
  D9: 207.0,
  D10: 260.0,
  D11: 340.0,
  D12: 470.0,
  D13: 660.0,
  D14: 990.0,
  D15: 1520.0,
  D16: 2320.0,
  D17: 3620.0,
  D18: 5520.0,
  E1: 74.5,
  E2: 76.0,
  E3: 78.0,
  E4: 82.0,
  E5: 87.0,
  E6: 94.0,
  E7: 107.0,
  E8: 126.0,
  E9: 159.0,
  E10: 212.0,
  E11: 292.0,
  E12: 422.0,
  E13: 612.0,
  E14: 942.0,
  E15: 1472.0,
  E16: 2272.0,
  E17: 3572.0,
  E18: 5472.0,
  F1: 38.5,
  F2: 40.0,
  F3: 42.0,
  F4: 46.0,
  F5: 51.0,
  F6: 58.0,
  F7: 71.0,
  F8: 90.0,
  F9: 123.0,
  F10: 176.0,
  F11: 256.0,
  F12: 386.0,
  F13: 576.0,
  F14: 906.0,
  F15: 1436.0,
  F16: 2236.0,
  F17: 3536.0,
  F18: 5436.0,
  G1: 14.5,
  G2: 16.0,
  G3: 18.0,
  G4: 22.0,
  G5: 27.0,
  G6: 34.0,
  G7: 47.0,
  G8: 66.0,
  G9: 99.0,
  G10: 152.0,
  G11: 232.0,
  G12: 362.0,
  G13: 552.0,
  G14: 882.0,
  G15: 1412.0,
  G16: 2212.0,
  G17: 3512.0,
  G18: 5412.0,
  H1: 2.5,
  H2: 4.0,
  H3: 6.0,
  H4: 10.0,
  H5: 15.0,
  H6: 22.0,
  H7: 35.0,
  H8: 54.0,
  H9: 87.0,
  H10: 140.0,
  H11: 220.0,
  H12: 350.0,
  H13: 540.0,
  H14: 870.0,
  H15: 1400.0,
  H16: 2200.0,
  H17: 3500.0,
  H18: 5400.0,
  JS1: 1.2,
  JS2: 2.0,
  JS3: 3.0,
  JS4: 5.0,
  JS5: 7.5,
  JS6: 11.0,
  JS7: 17.5,
  JS8: 27.0,
  JS9: 43.5,
  JS10: 70.0,
  JS11: 110.0,
  JS12: 175.0,
  JS13: 270.0,
  JS14: 435.0,
  JS15: 700.0,
  JS16: 1100.0,
  JS17: 1750.0,
  JS18: 2700.0,
  J6: 16.0,
  J7: 22.0,
  J8: 34.0,
  K1: -3,
  K2: -3,
  K3: -1,
  K4: 1.0,
  K5: 2.0,
  K6: 4.0,
  K7: 10.0,
  K8: 16.0,
  M1: -13,
  M2: -13,
  M3: -11,
  M4: -9,
  M5: -8,
  M6: -6,
  M7: 0.0,
  M8: 6.0,
  N1: -23,
  N2: -23,
  N3: -21,
  N4: -19,
  N5: -18,
  N6: -16,
  N7: -10,
  N8: -4,
  P1: -37,
  P2: -37,
  P3: -35,
  P4: -33,
  P5: -32,
  P6: -30,
  P7: -24,
  P8: -37,
  P9: -37,
  P10: -37,
  P11: -37,
  P12: -37,
  P13: -37,
  P14: -37,
  P15: -37,
  P16: -37,
  P17: -37,
  P18: -37
}, {
  from: 100,
  to: 120,
  A1: 412.5,
  A2: 414.0,
  A3: 416.0,
  A4: 420.0,
  A5: 425.0,
  A6: 432.0,
  A7: 445.0,
  A8: 464.0,
  A9: 497.0,
  A10: 550.0,
  A11: 630.0,
  A12: 760.0,
  A13: 950.0,
  A14: 1280.0,
  A15: 1810.0,
  A16: 2610.0,
  A17: 3910.0,
  A18: 5810.0,
  B1: 242.5,
  B2: 244.0,
  B3: 246.0,
  B4: 250.0,
  B5: 255.0,
  B6: 262.0,
  B7: 275.0,
  B8: 294.0,
  B9: 327.0,
  B10: 380.0,
  B11: 460.0,
  B12: 590.0,
  B13: 780.0,
  B14: 1110.0,
  B15: 1640.0,
  B16: 2440.0,
  B17: 3740.0,
  B18: 5640.0,
  C1: 182.5,
  C2: 184.0,
  C3: 186.0,
  C4: 190.0,
  C5: 195.0,
  C6: 202.0,
  C7: 215.0,
  C8: 234.0,
  C9: 267.0,
  C10: 320.0,
  C11: 400.0,
  C12: 530.0,
  C13: 720.0,
  C14: 1050.0,
  C15: 1580.0,
  C16: 2380.0,
  C17: 3680.0,
  C18: 5580.0,
  D1: 122.5,
  D2: 124.0,
  D3: 126.0,
  D4: 130.0,
  D5: 135.0,
  D6: 142.0,
  D7: 155.0,
  D8: 174.0,
  D9: 207.0,
  D10: 260.0,
  D11: 340.0,
  D12: 470.0,
  D13: 660.0,
  D14: 990.0,
  D15: 1520.0,
  D16: 2320.0,
  D17: 3620.0,
  D18: 5520.0,
  E1: 74.5,
  E2: 76.0,
  E3: 78.0,
  E4: 82.0,
  E5: 87.0,
  E6: 94.0,
  E7: 107.0,
  E8: 126.0,
  E9: 159.0,
  E10: 212.0,
  E11: 292.0,
  E12: 422.0,
  E13: 612.0,
  E14: 942.0,
  E15: 1472.0,
  E16: 2272.0,
  E17: 3572.0,
  E18: 5472.0,
  F1: 38.5,
  F2: 40.0,
  F3: 42.0,
  F4: 46.0,
  F5: 51.0,
  F6: 58.0,
  F7: 71.0,
  F8: 90.0,
  F9: 123.0,
  F10: 176.0,
  F11: 256.0,
  F12: 386.0,
  F13: 576.0,
  F14: 906.0,
  F15: 1436.0,
  F16: 2236.0,
  F17: 3536.0,
  F18: 5436.0,
  G1: 14.5,
  G2: 16.0,
  G3: 18.0,
  G4: 22.0,
  G5: 27.0,
  G6: 34.0,
  G7: 47.0,
  G8: 66.0,
  G9: 99.0,
  G10: 152.0,
  G11: 232.0,
  G12: 362.0,
  G13: 552.0,
  G14: 882.0,
  G15: 1412.0,
  G16: 2212.0,
  G17: 3512.0,
  G18: 5412.0,
  H1: 2.5,
  H2: 4.0,
  H3: 6.0,
  H4: 10.0,
  H5: 15.0,
  H6: 22.0,
  H7: 35.0,
  H8: 54.0,
  H9: 87.0,
  H10: 140.0,
  H11: 220.0,
  H12: 350.0,
  H13: 540.0,
  H14: 870.0,
  H15: 1400.0,
  H16: 2200.0,
  H17: 3500.0,
  H18: 5400.0,
  JS1: 1.2,
  JS2: 2.0,
  JS3: 3.0,
  JS4: 5.0,
  JS5: 7.5,
  JS6: 11.0,
  JS7: 17.5,
  JS8: 27.0,
  JS9: 43.5,
  JS10: 70.0,
  JS11: 110.0,
  JS12: 175.0,
  JS13: 270.0,
  JS14: 435.0,
  JS15: 700.0,
  JS16: 1100.0,
  JS17: 1750.0,
  JS18: 2700.0,
  J6: 16.0,
  J7: 22.0,
  J8: 34.0,
  K1: -3,
  K2: -3,
  K3: -1,
  K4: 1.0,
  K5: 2.0,
  K6: 4.0,
  K7: 10.0,
  K8: 16.0,
  M1: -13,
  M2: -13,
  M3: -11,
  M4: -9,
  M5: -8,
  M6: -6,
  M7: 0.0,
  M8: 6.0,
  N1: -23,
  N2: -23,
  N3: -21,
  N4: -19,
  N5: -18,
  N6: -16,
  N7: -10,
  N8: -4,
  P1: -37,
  P2: -37,
  P3: -35,
  P4: -33,
  P5: -32,
  P6: -30,
  P7: -24,
  P8: -37,
  P9: -37,
  P10: -37,
  P11: -37,
  P12: -37,
  P13: -37,
  P14: -37,
  P15: -37,
  P16: -37,
  P17: -37,
  P18: -37
}, {
  from: 120,
  to: 140,
  A1: 463.5,
  A2: 465.0,
  A3: 468.0,
  A4: 472.0,
  A5: 478.0,
  A6: 485.0,
  A7: 500.0,
  A8: 523.0,
  A9: 560.0,
  A10: 620.0,
  A11: 710.0,
  A12: 860.0,
  A13: 1090.0,
  A14: 1460.0,
  A15: 2060.0,
  A16: 2960.0,
  A17: 4460.0,
  A18: 6760.0,
  B1: 263.5,
  B2: 265.0,
  B3: 268.0,
  B4: 272.0,
  B5: 278.0,
  B6: 285.0,
  B7: 300.0,
  B8: 323.0,
  B9: 360.0,
  B10: 420.0,
  B11: 510.0,
  B12: 660.0,
  B13: 890.0,
  B14: 1260.0,
  B15: 1860.0,
  B16: 2760.0,
  B17: 4260.0,
  B18: 6560.0,
  C1: 203.5,
  C2: 205.0,
  C3: 208.0,
  C4: 212.0,
  C5: 218.0,
  C6: 225.0,
  C7: 240.0,
  C8: 263.0,
  C9: 300.0,
  C10: 360.0,
  C11: 450.0,
  C12: 600.0,
  C13: 830.0,
  C14: 1200.0,
  C15: 1800.0,
  C16: 2700.0,
  C17: 4200.0,
  C18: 6500.0,
  D1: 148.5,
  D2: 150.0,
  D3: 153.0,
  D4: 157.0,
  D5: 163.0,
  D6: 170.0,
  D7: 185.0,
  D8: 208.0,
  D9: 245.0,
  D10: 305.0,
  D11: 395.0,
  D12: 545.0,
  D13: 775.0,
  D14: 1145.0,
  D15: 1745.0,
  D16: 2645.0,
  D17: 4145.0,
  D18: 6445.0,
  E1: 88.5,
  E2: 90.0,
  E3: 93.0,
  E4: 97.0,
  E5: 103.0,
  E6: 110.0,
  E7: 125.0,
  E8: 148.0,
  E9: 185.0,
  E10: 245.0,
  E11: 335.0,
  E12: 485.0,
  E13: 715.0,
  E14: 1085.0,
  E15: 1685.0,
  E16: 2585.0,
  E17: 4085.0,
  E18: 6385.0,
  F1: 46.5,
  F2: 48.0,
  F3: 51.0,
  F4: 55.0,
  F5: 61.0,
  F6: 68.0,
  F7: 83.0,
  F8: 106.0,
  F9: 143.0,
  F10: 203.0,
  F11: 293.0,
  F12: 443.0,
  F13: 673.0,
  F14: 1043.0,
  F15: 1643.0,
  F16: 2543.0,
  F17: 4043.0,
  F18: 6343.0,
  G1: 17.5,
  G2: 19.0,
  G3: 22.0,
  G4: 26.0,
  G5: 32.0,
  G6: 39.0,
  G7: 54.0,
  G8: 77.0,
  G9: 114.0,
  G10: 174.0,
  G11: 264.0,
  G12: 414.0,
  G13: 644.0,
  G14: 1014.0,
  G15: 1614.0,
  G16: 2514.0,
  G17: 4014.0,
  G18: 6314.0,
  H1: 3.5,
  H2: 5.0,
  H3: 8.0,
  H4: 12.0,
  H5: 18.0,
  H6: 25.0,
  H7: 40.0,
  H8: 63.0,
  H9: 100.0,
  H10: 160.0,
  H11: 250.0,
  H12: 400.0,
  H13: 630.0,
  H14: 1000.0,
  H15: 1600.0,
  H16: 2500.0,
  H17: 4000.0,
  H18: 6300.0,
  JS1: 1.7,
  JS2: 2.5,
  JS3: 4.0,
  JS4: 6.0,
  JS5: 9.0,
  JS6: 12.5,
  JS7: 20.0,
  JS8: 31.5,
  JS9: 50.0,
  JS10: 80.0,
  JS11: 125.0,
  JS12: 200.0,
  JS13: 315.0,
  JS14: 500.0,
  JS15: 800.0,
  JS16: 1250.0,
  JS17: 2000.0,
  JS18: 3150.0,
  J6: 18.0,
  J7: 26.0,
  J8: 41.0,
  K1: -3,
  K2: -3,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 4.0,
  K7: 12.0,
  K8: 20.0,
  M1: -15,
  M2: -15,
  M3: -12,
  M4: -11,
  M5: -9,
  M6: -8,
  M7: 0.0,
  M8: 8.0,
  N1: -27,
  N2: -27,
  N3: -24,
  N4: -23,
  N5: -21,
  N6: -20,
  N7: -12,
  N8: -4,
  P1: -43,
  P2: -43,
  P3: -40,
  P4: -39,
  P5: -37,
  P6: -36,
  P7: -28,
  P8: -43,
  P9: -43,
  P10: -43,
  P11: -43,
  P12: -43,
  P13: -43,
  P14: -43,
  P15: -43,
  P16: -43,
  P17: -43,
  P18: -43
}, {
  from: 140,
  to: 160,
  A1: 523.5,
  A2: 525.0,
  A3: 528.0,
  A4: 532.0,
  A5: 538.0,
  A6: 545.0,
  A7: 560.0,
  A8: 583.0,
  A9: 620.0,
  A10: 680.0,
  A11: 770.0,
  A12: 920.0,
  A13: 1150.0,
  A14: 1520.0,
  A15: 2120.0,
  A16: 3020.0,
  A17: 4520.0,
  A18: 6820.0,
  B1: 283.5,
  B2: 285.0,
  B3: 288.0,
  B4: 292.0,
  B5: 298.0,
  B6: 305.0,
  B7: 320.0,
  B8: 343.0,
  B9: 380.0,
  B10: 440.0,
  B11: 530.0,
  B12: 680.0,
  B13: 910.0,
  B14: 1280.0,
  B15: 1880.0,
  B16: 2780.0,
  B17: 4280.0,
  B18: 6580.0,
  C1: 213.5,
  C2: 215.0,
  C3: 218.0,
  C4: 222.0,
  C5: 228.0,
  C6: 235.0,
  C7: 250.0,
  C8: 273.0,
  C9: 310.0,
  C10: 370.0,
  C11: 460.0,
  C12: 610.0,
  C13: 840.0,
  C14: 1210.0,
  C15: 1810.0,
  C16: 2710.0,
  C17: 4210.0,
  C18: 6510.0,
  D1: 148.5,
  D2: 150.0,
  D3: 153.0,
  D4: 157.0,
  D5: 163.0,
  D6: 170.0,
  D7: 185.0,
  D8: 208.0,
  D9: 245.0,
  D10: 305.0,
  D11: 395.0,
  D12: 545.0,
  D13: 775.0,
  D14: 1145.0,
  D15: 1745.0,
  D16: 2645.0,
  D17: 4145.0,
  D18: 6445.0,
  E1: 88.5,
  E2: 90.0,
  E3: 93.0,
  E4: 97.0,
  E5: 103.0,
  E6: 110.0,
  E7: 125.0,
  E8: 148.0,
  E9: 185.0,
  E10: 245.0,
  E11: 335.0,
  E12: 485.0,
  E13: 715.0,
  E14: 1085.0,
  E15: 1685.0,
  E16: 2585.0,
  E17: 4085.0,
  E18: 6385.0,
  F1: 46.5,
  F2: 48.0,
  F3: 51.0,
  F4: 55.0,
  F5: 61.0,
  F6: 68.0,
  F7: 83.0,
  F8: 106.0,
  F9: 143.0,
  F10: 203.0,
  F11: 293.0,
  F12: 443.0,
  F13: 673.0,
  F14: 1043.0,
  F15: 1643.0,
  F16: 2543.0,
  F17: 4043.0,
  F18: 6343.0,
  G1: 17.5,
  G2: 19.0,
  G3: 22.0,
  G4: 26.0,
  G5: 32.0,
  G6: 39.0,
  G7: 54.0,
  G8: 77.0,
  G9: 114.0,
  G10: 174.0,
  G11: 264.0,
  G12: 414.0,
  G13: 644.0,
  G14: 1014.0,
  G15: 1614.0,
  G16: 2514.0,
  G17: 4014.0,
  G18: 6314.0,
  H1: 3.5,
  H2: 5.0,
  H3: 8.0,
  H4: 12.0,
  H5: 18.0,
  H6: 25.0,
  H7: 40.0,
  H8: 63.0,
  H9: 100.0,
  H10: 160.0,
  H11: 250.0,
  H12: 400.0,
  H13: 630.0,
  H14: 1000.0,
  H15: 1600.0,
  H16: 2500.0,
  H17: 4000.0,
  H18: 6300.0,
  JS1: 1.7,
  JS2: 2.5,
  JS3: 4.0,
  JS4: 6.0,
  JS5: 9.0,
  JS6: 12.5,
  JS7: 20.0,
  JS8: 31.5,
  JS9: 50.0,
  JS10: 80.0,
  JS11: 125.0,
  JS12: 200.0,
  JS13: 315.0,
  JS14: 500.0,
  JS15: 800.0,
  JS16: 1250.0,
  JS17: 2000.0,
  JS18: 3150.0,
  J6: 18.0,
  J7: 26.0,
  J8: 41.0,
  K1: -3,
  K2: -3,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 4.0,
  K7: 12.0,
  K8: 20.0,
  M1: -15,
  M2: -15,
  M3: -12,
  M4: -11,
  M5: -9,
  M6: -8,
  M7: 0.0,
  M8: 8.0,
  N1: -27,
  N2: -27,
  N3: -24,
  N4: -23,
  N5: -21,
  N6: -20,
  N7: -12,
  N8: -4,
  P1: -43,
  P2: -43,
  P3: -40,
  P4: -39,
  P5: -37,
  P6: -36,
  P7: -28,
  P8: -43,
  P9: -43,
  P10: -43,
  P11: -43,
  P12: -43,
  P13: -43,
  P14: -43,
  P15: -43,
  P16: -43,
  P17: -43,
  P18: -43
}, {
  from: 160,
  to: 180,
  A1: 583.5,
  A2: 585.0,
  A3: 588.0,
  A4: 592.0,
  A5: 598.0,
  A6: 605.0,
  A7: 620.0,
  A8: 643.0,
  A9: 680.0,
  A10: 740.0,
  A11: 830.0,
  A12: 980.0,
  A13: 1210.0,
  A14: 1580.0,
  A15: 2180.0,
  A16: 3080.0,
  A17: 4580.0,
  A18: 6880.0,
  B1: 313.5,
  B2: 315.0,
  B3: 318.0,
  B4: 322.0,
  B5: 328.0,
  B6: 335.0,
  B7: 350.0,
  B8: 373.0,
  B9: 410.0,
  B10: 470.0,
  B11: 560.0,
  B12: 710.0,
  B13: 940.0,
  B14: 1310.0,
  B15: 1910.0,
  B16: 2810.0,
  B17: 4310.0,
  B18: 6610.0,
  C1: 233.5,
  C2: 235.0,
  C3: 238.0,
  C4: 242.0,
  C5: 248.0,
  C6: 255.0,
  C7: 270.0,
  C8: 293.0,
  C9: 330.0,
  C10: 390.0,
  C11: 480.0,
  C12: 630.0,
  C13: 860.0,
  C14: 1230.0,
  C15: 1830.0,
  C16: 2730.0,
  C17: 4230.0,
  C18: 6530.0,
  D1: 148.5,
  D2: 150.0,
  D3: 153.0,
  D4: 157.0,
  D5: 163.0,
  D6: 170.0,
  D7: 185.0,
  D8: 208.0,
  D9: 245.0,
  D10: 305.0,
  D11: 395.0,
  D12: 545.0,
  D13: 775.0,
  D14: 1145.0,
  D15: 1745.0,
  D16: 2645.0,
  D17: 4145.0,
  D18: 6445.0,
  E1: 88.5,
  E2: 90.0,
  E3: 93.0,
  E4: 97.0,
  E5: 103.0,
  E6: 110.0,
  E7: 125.0,
  E8: 148.0,
  E9: 185.0,
  E10: 245.0,
  E11: 335.0,
  E12: 485.0,
  E13: 715.0,
  E14: 1085.0,
  E15: 1685.0,
  E16: 2585.0,
  E17: 4085.0,
  E18: 6385.0,
  F1: 46.5,
  F2: 48.0,
  F3: 51.0,
  F4: 55.0,
  F5: 61.0,
  F6: 68.0,
  F7: 83.0,
  F8: 106.0,
  F9: 143.0,
  F10: 203.0,
  F11: 293.0,
  F12: 443.0,
  F13: 673.0,
  F14: 1043.0,
  F15: 1643.0,
  F16: 2543.0,
  F17: 4043.0,
  F18: 6343.0,
  G1: 17.5,
  G2: 19.0,
  G3: 22.0,
  G4: 26.0,
  G5: 32.0,
  G6: 39.0,
  G7: 54.0,
  G8: 77.0,
  G9: 114.0,
  G10: 174.0,
  G11: 264.0,
  G12: 414.0,
  G13: 644.0,
  G14: 1014.0,
  G15: 1614.0,
  G16: 2514.0,
  G17: 4014.0,
  G18: 6314.0,
  H1: 3.5,
  H2: 5.0,
  H3: 8.0,
  H4: 12.0,
  H5: 18.0,
  H6: 25.0,
  H7: 40.0,
  H8: 63.0,
  H9: 100.0,
  H10: 160.0,
  H11: 250.0,
  H12: 400.0,
  H13: 630.0,
  H14: 1000.0,
  H15: 1600.0,
  H16: 2500.0,
  H17: 4000.0,
  H18: 6300.0,
  JS1: 1.7,
  JS2: 2.5,
  JS3: 4.0,
  JS4: 6.0,
  JS5: 9.0,
  JS6: 12.5,
  JS7: 20.0,
  JS8: 31.5,
  JS9: 50.0,
  JS10: 80.0,
  JS11: 125.0,
  JS12: 200.0,
  JS13: 315.0,
  JS14: 500.0,
  JS15: 800.0,
  JS16: 1250.0,
  JS17: 2000.0,
  JS18: 3150.0,
  J6: 18.0,
  J7: 26.0,
  J8: 41.0,
  K1: -3,
  K2: -3,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 4.0,
  K7: 12.0,
  K8: 20.0,
  M1: -15,
  M2: -15,
  M3: -12,
  M4: -11,
  M5: -9,
  M6: -8,
  M7: 0.0,
  M8: 8.0,
  N1: -27,
  N2: -27,
  N3: -24,
  N4: -23,
  N5: -21,
  N6: -20,
  N7: -12,
  N8: -4,
  P1: -43,
  P2: -43,
  P3: -40,
  P4: -39,
  P5: -37,
  P6: -36,
  P7: -28,
  P8: -43,
  P9: -43,
  P10: -43,
  P11: -43,
  P12: -43,
  P13: -43,
  P14: -43,
  P15: -43,
  P16: -43,
  P17: -43,
  P18: -43
}, {
  from: 180,
  to: 200,
  A1: 664.5,
  A2: 667.0,
  A3: 670.0,
  A4: 674.0,
  A5: 680.0,
  A6: 689.0,
  A7: 706.0,
  A8: 732.0,
  A9: 775.0,
  A10: 845.0,
  A11: 950.0,
  A12: 1120.0,
  A13: 1380.0,
  A14: 1810.0,
  A15: 2510.0,
  A16: 3560.0,
  A17: 5260.0,
  A18: 7860.0,
  B1: 344.5,
  B2: 347.0,
  B3: 350.0,
  B4: 354.0,
  B5: 360.0,
  B6: 369.0,
  B7: 386.0,
  B8: 412.0,
  B9: 455.0,
  B10: 525.0,
  B11: 630.0,
  B12: 800.0,
  B13: 1060.0,
  B14: 1490.0,
  B15: 2190.0,
  B16: 3240.0,
  B17: 4940.0,
  B18: 7540.0,
  C1: 244.5,
  C2: 247.0,
  C3: 250.0,
  C4: 254.0,
  C5: 260.0,
  C6: 269.0,
  C7: 286.0,
  C8: 312.0,
  C9: 355.0,
  C10: 425.0,
  C11: 530.0,
  C12: 700.0,
  C13: 960.0,
  C14: 1390.0,
  C15: 2090.0,
  C16: 3140.0,
  C17: 4840.0,
  C18: 7440.0,
  D1: 174.5,
  D2: 177.0,
  D3: 180.0,
  D4: 184.0,
  D5: 190.0,
  D6: 199.0,
  D7: 216.0,
  D8: 242.0,
  D9: 285.0,
  D10: 355.0,
  D11: 460.0,
  D12: 630.0,
  D13: 890.0,
  D14: 1320.0,
  D15: 2020.0,
  D16: 3070.0,
  D17: 4770.0,
  D18: 7370.0,
  E1: 104.5,
  E2: 107.0,
  E3: 110.0,
  E4: 114.0,
  E5: 120.0,
  E6: 129.0,
  E7: 146.0,
  E8: 172.0,
  E9: 215.0,
  E10: 285.0,
  E11: 390.0,
  E12: 560.0,
  E13: 820.0,
  E14: 1250.0,
  E15: 1950.0,
  E16: 3000.0,
  E17: 4700.0,
  E18: 7300.0,
  F1: 54.5,
  F2: 57.0,
  F3: 60.0,
  F4: 64.0,
  F5: 70.0,
  F6: 79.0,
  F7: 96.0,
  F8: 122.0,
  F9: 165.0,
  F10: 235.0,
  F11: 340.0,
  F12: 510.0,
  F13: 770.0,
  F14: 1200.0,
  F15: 1900.0,
  F16: 2950.0,
  F17: 4650.0,
  F18: 7250.0,
  G1: 19.5,
  G2: 22.0,
  G3: 25.0,
  G4: 29.0,
  G5: 35.0,
  G6: 44.0,
  G7: 61.0,
  G8: 87.0,
  G9: 130.0,
  G10: 200.0,
  G11: 305.0,
  G12: 475.0,
  G13: 735.0,
  G14: 1165.0,
  G15: 1865.0,
  G16: 2915.0,
  G17: 4615.0,
  G18: 7215.0,
  H1: 4.5,
  H2: 7.0,
  H3: 10.0,
  H4: 14.0,
  H5: 20.0,
  H6: 29.0,
  H7: 46.0,
  H8: 72.0,
  H9: 115.0,
  H10: 185.0,
  H11: 290.0,
  H12: 460.0,
  H13: 720.0,
  H14: 1150.0,
  H15: 1850.0,
  H16: 2900.0,
  H17: 4600.0,
  H18: 7200.0,
  JS1: 2.3,
  JS2: 3.5,
  JS3: 5.0,
  JS4: 7.0,
  JS5: 10.0,
  JS6: 14.5,
  JS7: 23.0,
  JS8: 36.0,
  JS9: 57.5,
  JS10: 92.5,
  JS11: 145.0,
  JS12: 230.0,
  JS13: 360.0,
  JS14: 575.0,
  JS15: 925.0,
  JS16: 1450.0,
  JS17: 2300.0,
  JS18: 3600.0,
  J6: 22.0,
  J7: 30.0,
  J8: 47.0,
  K1: -4,
  K2: -4,
  K3: -1,
  K4: 0.0,
  K5: 2.0,
  K6: 5.0,
  K7: 13.0,
  K8: 22.0,
  M1: -17,
  M2: -17,
  M3: -14,
  M4: -13,
  M5: -11,
  M6: -8,
  M7: 0.0,
  M8: 9.0,
  N1: -31,
  N2: -31,
  N3: -28,
  N4: -27,
  N5: -25,
  N6: -22,
  N7: -14,
  N8: -5,
  P1: -50,
  P2: -50,
  P3: -47,
  P4: -46,
  P5: -44,
  P6: -41,
  P7: -33,
  P8: -50,
  P9: -50,
  P10: -50,
  P11: -50,
  P12: -50,
  P13: -50,
  P14: -50,
  P15: -50,
  P16: -50,
  P17: -50,
  P18: -50
}, {
  from: 200,
  to: 225,
  A1: 744.5,
  A2: 747.0,
  A3: 750.0,
  A4: 754.0,
  A5: 760.0,
  A6: 769.0,
  A7: 786.0,
  A8: 812.0,
  A9: 855.0,
  A10: 925.0,
  A11: 1030.0,
  A12: 1200.0,
  A13: 1460.0,
  A14: 1890.0,
  A15: 2590.0,
  A16: 3640.0,
  A17: 5340.0,
  A18: 7940.0,
  B1: 384.5,
  B2: 387.0,
  B3: 390.0,
  B4: 394.0,
  B5: 400.0,
  B6: 409.0,
  B7: 426.0,
  B8: 452.0,
  B9: 495.0,
  B10: 565.0,
  B11: 670.0,
  B12: 840.0,
  B13: 1100.0,
  B14: 1530.0,
  B15: 2230.0,
  B16: 3280.0,
  B17: 4980.0,
  B18: 7580.0,
  C1: 264.5,
  C2: 267.0,
  C3: 270.0,
  C4: 274.0,
  C5: 280.0,
  C6: 289.0,
  C7: 306.0,
  C8: 332.0,
  C9: 375.0,
  C10: 445.0,
  C11: 550.0,
  C12: 720.0,
  C13: 980.0,
  C14: 1410.0,
  C15: 2110.0,
  C16: 3160.0,
  C17: 4860.0,
  C18: 7460.0,
  D1: 174.5,
  D2: 177.0,
  D3: 180.0,
  D4: 184.0,
  D5: 190.0,
  D6: 199.0,
  D7: 216.0,
  D8: 242.0,
  D9: 285.0,
  D10: 355.0,
  D11: 460.0,
  D12: 630.0,
  D13: 890.0,
  D14: 1320.0,
  D15: 2020.0,
  D16: 3070.0,
  D17: 4770.0,
  D18: 7370.0,
  E1: 104.5,
  E2: 107.0,
  E3: 110.0,
  E4: 114.0,
  E5: 120.0,
  E6: 129.0,
  E7: 146.0,
  E8: 172.0,
  E9: 215.0,
  E10: 285.0,
  E11: 390.0,
  E12: 560.0,
  E13: 820.0,
  E14: 1250.0,
  E15: 1950.0,
  E16: 3000.0,
  E17: 4700.0,
  E18: 7300.0,
  F1: 54.5,
  F2: 57.0,
  F3: 60.0,
  F4: 64.0,
  F5: 70.0,
  F6: 79.0,
  F7: 96.0,
  F8: 122.0,
  F9: 165.0,
  F10: 235.0,
  F11: 340.0,
  F12: 510.0,
  F13: 770.0,
  F14: 1200.0,
  F15: 1900.0,
  F16: 2950.0,
  F17: 4650.0,
  F18: 7250.0,
  G1: 19.5,
  G2: 22.0,
  G3: 25.0,
  G4: 29.0,
  G5: 35.0,
  G6: 44.0,
  G7: 61.0,
  G8: 87.0,
  G9: 130.0,
  G10: 200.0,
  G11: 305.0,
  G12: 475.0,
  G13: 735.0,
  G14: 1165.0,
  G15: 1865.0,
  G16: 2915.0,
  G17: 4615.0,
  G18: 7215.0,
  H1: 4.5,
  H2: 7.0,
  H3: 10.0,
  H4: 14.0,
  H5: 20.0,
  H6: 29.0,
  H7: 46.0,
  H8: 72.0,
  H9: 115.0,
  H10: 185.0,
  H11: 290.0,
  H12: 460.0,
  H13: 720.0,
  H14: 1150.0,
  H15: 1850.0,
  H16: 2900.0,
  H17: 4600.0,
  H18: 7200.0,
  JS1: 2.3,
  JS2: 3.5,
  JS3: 5.0,
  JS4: 7.0,
  JS5: 10.0,
  JS6: 14.5,
  JS7: 23.0,
  JS8: 36.0,
  JS9: 57.5,
  JS10: 92.5,
  JS11: 145.0,
  JS12: 230.0,
  JS13: 360.0,
  JS14: 575.0,
  JS15: 925.0,
  JS16: 1450.0,
  JS17: 2300.0,
  JS18: 3600.0,
  J6: 22.0,
  J7: 30.0,
  J8: 47.0,
  K1: -4,
  K2: -4,
  K3: -1,
  K4: 0.0,
  K5: 2.0,
  K6: 5.0,
  K7: 13.0,
  K8: 22.0,
  M1: -17,
  M2: -17,
  M3: -14,
  M4: -13,
  M5: -11,
  M6: -8,
  M7: 0.0,
  M8: 9.0,
  N1: -31,
  N2: -31,
  N3: -28,
  N4: -27,
  N5: -25,
  N6: -22,
  N7: -14,
  N8: -5,
  P1: -50,
  P2: -50,
  P3: -47,
  P4: -46,
  P5: -44,
  P6: -41,
  P7: -33,
  P8: -50,
  P9: -50,
  P10: -50,
  P11: -50,
  P12: -50,
  P13: -50,
  P14: -50,
  P15: -50,
  P16: -50,
  P17: -50,
  P18: -50
}, {
  from: 225,
  to: 250,
  A1: 824.5,
  A2: 827.0,
  A3: 830.0,
  A4: 834.0,
  A5: 840.0,
  A6: 849.0,
  A7: 866.0,
  A8: 892.0,
  A9: 935.0,
  A10: 1005.0,
  A11: 1110.0,
  A12: 1280.0,
  A13: 1540.0,
  A14: 1970.0,
  A15: 2670.0,
  A16: 3720.0,
  A17: 5420.0,
  A18: 8020.0,
  B1: 424.5,
  B2: 427.0,
  B3: 430.0,
  B4: 434.0,
  B5: 440.0,
  B6: 449.0,
  B7: 466.0,
  B8: 492.0,
  B9: 535.0,
  B10: 605.0,
  B11: 710.0,
  B12: 880.0,
  B13: 1140.0,
  B14: 1570.0,
  B15: 2270.0,
  B16: 3320.0,
  B17: 5020.0,
  B18: 7620.0,
  C1: 284.5,
  C2: 287.0,
  C3: 290.0,
  C4: 294.0,
  C5: 300.0,
  C6: 309.0,
  C7: 326.0,
  C8: 352.0,
  C9: 395.0,
  C10: 465.0,
  C11: 570.0,
  C12: 740.0,
  C13: 1000.0,
  C14: 1430.0,
  C15: 2130.0,
  C16: 3180.0,
  C17: 4880.0,
  C18: 7480.0,
  D1: 174.5,
  D2: 177.0,
  D3: 180.0,
  D4: 184.0,
  D5: 190.0,
  D6: 199.0,
  D7: 216.0,
  D8: 242.0,
  D9: 285.0,
  D10: 355.0,
  D11: 460.0,
  D12: 630.0,
  D13: 890.0,
  D14: 1320.0,
  D15: 2020.0,
  D16: 3070.0,
  D17: 4770.0,
  D18: 7370.0,
  E1: 104.5,
  E2: 107.0,
  E3: 110.0,
  E4: 114.0,
  E5: 120.0,
  E6: 129.0,
  E7: 146.0,
  E8: 172.0,
  E9: 215.0,
  E10: 285.0,
  E11: 390.0,
  E12: 560.0,
  E13: 820.0,
  E14: 1250.0,
  E15: 1950.0,
  E16: 3000.0,
  E17: 4700.0,
  E18: 7300.0,
  F1: 54.5,
  F2: 57.0,
  F3: 60.0,
  F4: 64.0,
  F5: 70.0,
  F6: 79.0,
  F7: 96.0,
  F8: 122.0,
  F9: 165.0,
  F10: 235.0,
  F11: 340.0,
  F12: 510.0,
  F13: 770.0,
  F14: 1200.0,
  F15: 1900.0,
  F16: 2950.0,
  F17: 4650.0,
  F18: 7250.0,
  G1: 19.5,
  G2: 22.0,
  G3: 25.0,
  G4: 29.0,
  G5: 35.0,
  G6: 44.0,
  G7: 61.0,
  G8: 87.0,
  G9: 130.0,
  G10: 200.0,
  G11: 305.0,
  G12: 475.0,
  G13: 735.0,
  G14: 1165.0,
  G15: 1865.0,
  G16: 2915.0,
  G17: 4615.0,
  G18: 7215.0,
  H1: 4.5,
  H2: 7.0,
  H3: 10.0,
  H4: 14.0,
  H5: 20.0,
  H6: 29.0,
  H7: 46.0,
  H8: 72.0,
  H9: 115.0,
  H10: 185.0,
  H11: 290.0,
  H12: 460.0,
  H13: 720.0,
  H14: 1150.0,
  H15: 1850.0,
  H16: 2900.0,
  H17: 4600.0,
  H18: 7200.0,
  JS1: 2.3,
  JS2: 3.5,
  JS3: 5.0,
  JS4: 7.0,
  JS5: 10.0,
  JS6: 14.5,
  JS7: 23.0,
  JS8: 36.0,
  JS9: 57.5,
  JS10: 92.5,
  JS11: 145.0,
  JS12: 230.0,
  JS13: 360.0,
  JS14: 575.0,
  JS15: 925.0,
  JS16: 1450.0,
  JS17: 2300.0,
  JS18: 3600.0,
  J6: 22.0,
  J7: 30.0,
  J8: 47.0,
  K1: -4,
  K2: -4,
  K3: -1,
  K4: 0.0,
  K5: 2.0,
  K6: 5.0,
  K7: 13.0,
  K8: 22.0,
  M1: -17,
  M2: -17,
  M3: -14,
  M4: -13,
  M5: -11,
  M6: -8,
  M7: 0.0,
  M8: 9.0,
  N1: -31,
  N2: -31,
  N3: -28,
  N4: -27,
  N5: -25,
  N6: -22,
  N7: -14,
  N8: -5,
  P1: -50,
  P2: -50,
  P3: -47,
  P4: -46,
  P5: -44,
  P6: -41,
  P7: -33,
  P8: -50,
  P9: -50,
  P10: -50,
  P11: -50,
  P12: -50,
  P13: -50,
  P14: -50,
  P15: -50,
  P16: -50,
  P17: -50,
  P18: -50
}, {
  from: 250,
  to: 280,
  A1: 926.0,
  A2: 928.0,
  A3: 932.0,
  A4: 936.0,
  A5: 943.0,
  A6: 952.0,
  A7: 972.0,
  A8: 1001.0,
  A9: 1050.0,
  A10: 1130.0,
  A11: 1240.0,
  A12: 1440.0,
  A13: 1730.0,
  A14: 2220.0,
  A15: 3020.0,
  A16: 4120.0,
  A17: 6120.0,
  A18: 9020.0,
  B1: 486.0,
  B2: 488.0,
  B3: 492.0,
  B4: 496.0,
  B5: 503.0,
  B6: 512.0,
  B7: 532.0,
  B8: 561.0,
  B9: 610.0,
  B10: 690.0,
  B11: 800.0,
  B12: 1000.0,
  B13: 1290.0,
  B14: 1780.0,
  B15: 2580.0,
  B16: 3680.0,
  B17: 5680.0,
  B18: 8580.0,
  C1: 306.0,
  C2: 308.0,
  C3: 312.0,
  C4: 316.0,
  C5: 323.0,
  C6: 332.0,
  C7: 352.0,
  C8: 381.0,
  C9: 430.0,
  C10: 510.0,
  C11: 620.0,
  C12: 820.0,
  C13: 1110.0,
  C14: 1600.0,
  C15: 2400.0,
  C16: 3500.0,
  C17: 5500.0,
  C18: 8400.0,
  D1: 196.0,
  D2: 198.0,
  D3: 202.0,
  D4: 206.0,
  D5: 213.0,
  D6: 222.0,
  D7: 242.0,
  D8: 271.0,
  D9: 320.0,
  D10: 400.0,
  D11: 510.0,
  D12: 710.0,
  D13: 1000.0,
  D14: 1490.0,
  D15: 2290.0,
  D16: 3390.0,
  D17: 5390.0,
  D18: 8290.0,
  E1: 116.0,
  E2: 118.0,
  E3: 122.0,
  E4: 126.0,
  E5: 133.0,
  E6: 142.0,
  E7: 162.0,
  E8: 191.0,
  E9: 240.0,
  E10: 320.0,
  E11: 430.0,
  E12: 630.0,
  E13: 920.0,
  E14: 1410.0,
  E15: 2210.0,
  E16: 3310.0,
  E17: 5310.0,
  E18: 8210.0,
  F1: 62.0,
  F2: 64.0,
  F3: 68.0,
  F4: 72.0,
  F5: 79.0,
  F6: 88.0,
  F7: 108.0,
  F8: 137.0,
  F9: 186.0,
  F10: 266.0,
  F11: 376.0,
  F12: 576.0,
  F13: 866.0,
  F14: 1356.0,
  F15: 2156.0,
  F16: 3256.0,
  F17: 5256.0,
  F18: 8156.0,
  G1: 23.0,
  G2: 25.0,
  G3: 29.0,
  G4: 33.0,
  G5: 40.0,
  G6: 49.0,
  G7: 69.0,
  G8: 98.0,
  G9: 147.0,
  G10: 227.0,
  G11: 337.0,
  G12: 537.0,
  G13: 827.0,
  G14: 1317.0,
  G15: 2117.0,
  G16: 3217.0,
  G17: 5217.0,
  G18: 8117.0,
  H1: 6.0,
  H2: 8.0,
  H3: 12.0,
  H4: 16.0,
  H5: 23.0,
  H6: 32.0,
  H7: 52.0,
  H8: 81.0,
  H9: 130.0,
  H10: 210.0,
  H11: 320.0,
  H12: 520.0,
  H13: 810.0,
  H14: 1300.0,
  H15: 2100.0,
  H16: 3200.0,
  H17: 5200.0,
  H18: 8100.0,
  JS1: 3.0,
  JS2: 4.0,
  JS3: 6.0,
  JS4: 8.0,
  JS5: 11.5,
  JS6: 16.0,
  JS7: 26.0,
  JS8: 40.5,
  JS9: 65.0,
  JS10: 105.0,
  JS11: 160.0,
  JS12: 260.0,
  JS13: 405.0,
  JS14: 650.0,
  JS15: 1050.0,
  JS16: 1600.0,
  JS17: 2600.0,
  JS18: 4050.0,
  J6: 25.0,
  J7: 36.0,
  J8: 55.0,
  K1: -4,
  K2: -4,
  K3: 0.0,
  K4: 0.0,
  K5: 3.0,
  K6: 5.0,
  K7: 16.0,
  K8: 25.0,
  M1: -20,
  M2: -20,
  M3: -16,
  M4: -16,
  M5: -13,
  M6: -11,
  M7: 0.0,
  M8: 9.0,
  N1: -34,
  N2: -34,
  N3: -30,
  N4: -30,
  N5: -27,
  N6: -25,
  N7: -14,
  N8: -5,
  P1: -56,
  P2: -56,
  P3: -52,
  P4: -52,
  P5: -49,
  P6: -47,
  P7: -36,
  P8: -56,
  P9: -56,
  P10: -56,
  P11: -56,
  P12: -56,
  P13: -56,
  P14: -56,
  P15: -56,
  P16: -56,
  P17: -56,
  P18: -56
}, {
  from: 280,
  to: 315,
  A1: 1056.0,
  A2: 1058.0,
  A3: 1062.0,
  A4: 1066.0,
  A5: 1073.0,
  A6: 1082.0,
  A7: 1102.0,
  A8: 1131.0,
  A9: 1180.0,
  A10: 1260.0,
  A11: 1370.0,
  A12: 1570.0,
  A13: 1860.0,
  A14: 2350.0,
  A15: 3150.0,
  A16: 4250.0,
  A17: 6250.0,
  A18: 9150.0,
  B1: 546.0,
  B2: 548.0,
  B3: 552.0,
  B4: 556.0,
  B5: 563.0,
  B6: 572.0,
  B7: 592.0,
  B8: 621.0,
  B9: 670.0,
  B10: 750.0,
  B11: 860.0,
  B12: 1060.0,
  B13: 1350.0,
  B14: 1840.0,
  B15: 2640.0,
  B16: 3740.0,
  B17: 5740.0,
  B18: 8640.0,
  C1: 336.0,
  C2: 338.0,
  C3: 342.0,
  C4: 346.0,
  C5: 353.0,
  C6: 362.0,
  C7: 382.0,
  C8: 411.0,
  C9: 460.0,
  C10: 540.0,
  C11: 650.0,
  C12: 850.0,
  C13: 1140.0,
  C14: 1630.0,
  C15: 2430.0,
  C16: 3530.0,
  C17: 5530.0,
  C18: 8430.0,
  D1: 196.0,
  D2: 198.0,
  D3: 202.0,
  D4: 206.0,
  D5: 213.0,
  D6: 222.0,
  D7: 242.0,
  D8: 271.0,
  D9: 320.0,
  D10: 400.0,
  D11: 510.0,
  D12: 710.0,
  D13: 1000.0,
  D14: 1490.0,
  D15: 2290.0,
  D16: 3390.0,
  D17: 5390.0,
  D18: 8290.0,
  E1: 116.0,
  E2: 118.0,
  E3: 122.0,
  E4: 126.0,
  E5: 133.0,
  E6: 142.0,
  E7: 162.0,
  E8: 191.0,
  E9: 240.0,
  E10: 320.0,
  E11: 430.0,
  E12: 630.0,
  E13: 920.0,
  E14: 1410.0,
  E15: 2210.0,
  E16: 3310.0,
  E17: 5310.0,
  E18: 8210.0,
  F1: 62.0,
  F2: 64.0,
  F3: 68.0,
  F4: 72.0,
  F5: 79.0,
  F6: 88.0,
  F7: 108.0,
  F8: 137.0,
  F9: 186.0,
  F10: 266.0,
  F11: 376.0,
  F12: 576.0,
  F13: 866.0,
  F14: 1356.0,
  F15: 2156.0,
  F16: 3256.0,
  F17: 5256.0,
  F18: 8156.0,
  G1: 23.0,
  G2: 25.0,
  G3: 29.0,
  G4: 33.0,
  G5: 40.0,
  G6: 49.0,
  G7: 69.0,
  G8: 98.0,
  G9: 147.0,
  G10: 227.0,
  G11: 337.0,
  G12: 537.0,
  G13: 827.0,
  G14: 1317.0,
  G15: 2117.0,
  G16: 3217.0,
  G17: 5217.0,
  G18: 8117.0,
  H1: 6.0,
  H2: 8.0,
  H3: 12.0,
  H4: 16.0,
  H5: 23.0,
  H6: 32.0,
  H7: 52.0,
  H8: 81.0,
  H9: 130.0,
  H10: 210.0,
  H11: 320.0,
  H12: 520.0,
  H13: 810.0,
  H14: 1300.0,
  H15: 2100.0,
  H16: 3200.0,
  H17: 5200.0,
  H18: 8100.0,
  JS1: 3.0,
  JS2: 4.0,
  JS3: 6.0,
  JS4: 8.0,
  JS5: 11.5,
  JS6: 16.0,
  JS7: 26.0,
  JS8: 40.5,
  JS9: 65.0,
  JS10: 105.0,
  JS11: 160.0,
  JS12: 260.0,
  JS13: 405.0,
  JS14: 650.0,
  JS15: 1050.0,
  JS16: 1600.0,
  JS17: 2600.0,
  JS18: 4050.0,
  J6: 25.0,
  J7: 36.0,
  J8: 55.0,
  K1: -4,
  K2: -4,
  K3: 0.0,
  K4: 0.0,
  K5: 3.0,
  K6: 5.0,
  K7: 16.0,
  K8: 25.0,
  M1: -20,
  M2: -20,
  M3: -16,
  M4: -16,
  M5: -13,
  M6: -11,
  M7: 0.0,
  M8: 9.0,
  N1: -34,
  N2: -34,
  N3: -30,
  N4: -30,
  N5: -27,
  N6: -25,
  N7: -14,
  N8: -5,
  P1: -56,
  P2: -56,
  P3: -52,
  P4: -52,
  P5: -49,
  P6: -47,
  P7: -36,
  P8: -56,
  P9: -56,
  P10: -56,
  P11: -56,
  P12: -56,
  P13: -56,
  P14: -56,
  P15: -56,
  P16: -56,
  P17: -56,
  P18: -56
}, {
  from: 315,
  to: 355,
  A1: 1207.0,
  A2: 1209.0,
  A3: 1213.0,
  A4: 1218.0,
  A5: 1225.0,
  A6: 1236.0,
  A7: 1257.0,
  A8: 1289.0,
  A9: 1340.0,
  A10: 1430.0,
  A11: 1560.0,
  A12: 1770.0,
  A13: 2090.0,
  A14: 2600.0,
  A15: 3500.0,
  A16: 4800.0,
  A17: 6900.0,
  A18: 10100.0,
  B1: 607.0,
  B2: 609.0,
  B3: 613.0,
  B4: 618.0,
  B5: 625.0,
  B6: 636.0,
  B7: 657.0,
  B8: 689.0,
  B9: 740.0,
  B10: 830.0,
  B11: 960.0,
  B12: 1170.0,
  B13: 1490.0,
  B14: 2000.0,
  B15: 2900.0,
  B16: 4200.0,
  B17: 6300.0,
  B18: 9500.0,
  C1: 367.0,
  C2: 369.0,
  C3: 373.0,
  C4: 378.0,
  C5: 385.0,
  C6: 396.0,
  C7: 417.0,
  C8: 449.0,
  C9: 500.0,
  C10: 590.0,
  C11: 720.0,
  C12: 930.0,
  C13: 1250.0,
  C14: 1760.0,
  C15: 2660.0,
  C16: 3960.0,
  C17: 6060.0,
  C18: 9260.0,
  D1: 217.0,
  D2: 219.0,
  D3: 223.0,
  D4: 228.0,
  D5: 235.0,
  D6: 246.0,
  D7: 267.0,
  D8: 299.0,
  D9: 350.0,
  D10: 440.0,
  D11: 570.0,
  D12: 780.0,
  D13: 1100.0,
  D14: 1610.0,
  D15: 2510.0,
  D16: 3810.0,
  D17: 5910.0,
  D18: 9110.0,
  E1: 132.0,
  E2: 134.0,
  E3: 138.0,
  E4: 143.0,
  E5: 150.0,
  E6: 161.0,
  E7: 182.0,
  E8: 214.0,
  E9: 265.0,
  E10: 355.0,
  E11: 485.0,
  E12: 695.0,
  E13: 1015.0,
  E14: 1525.0,
  E15: 2425.0,
  E16: 3725.0,
  E17: 5825.0,
  E18: 9025.0,
  F1: 69.0,
  F2: 71.0,
  F3: 75.0,
  F4: 80.0,
  F5: 87.0,
  F6: 98.0,
  F7: 119.0,
  F8: 151.0,
  F9: 202.0,
  F10: 292.0,
  F11: 422.0,
  F12: 632.0,
  F13: 952.0,
  F14: 1462.0,
  F15: 2362.0,
  F16: 3662.0,
  F17: 5762.0,
  F18: 8962.0,
  G1: 25.0,
  G2: 27.0,
  G3: 31.0,
  G4: 36.0,
  G5: 43.0,
  G6: 54.0,
  G7: 75.0,
  G8: 107.0,
  G9: 158.0,
  G10: 248.0,
  G11: 378.0,
  G12: 588.0,
  G13: 908.0,
  G14: 1418.0,
  G15: 2318.0,
  G16: 3618.0,
  G17: 5718.0,
  G18: 8918.0,
  H1: 7.0,
  H2: 9.0,
  H3: 13.0,
  H4: 18.0,
  H5: 25.0,
  H6: 36.0,
  H7: 57.0,
  H8: 89.0,
  H9: 140.0,
  H10: 230.0,
  H11: 360.0,
  H12: 570.0,
  H13: 890.0,
  H14: 1400.0,
  H15: 2300.0,
  H16: 3600.0,
  H17: 5700.0,
  H18: 8900.0,
  JS1: 3.5,
  JS2: 4.5,
  JS3: 6.5,
  JS4: 9.0,
  JS5: 12.5,
  JS6: 18.0,
  JS7: 28.5,
  JS8: 44.5,
  JS9: 70.0,
  JS10: 115.0,
  JS11: 180.0,
  JS12: 285.0,
  JS13: 445.0,
  JS14: 700.0,
  JS15: 1150.0,
  JS16: 1800.0,
  JS17: 2850.0,
  JS18: 4450.0,
  J6: 29.0,
  J7: 39.0,
  J8: 60.0,
  K1: -4,
  K2: -4,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 7.0,
  K7: 17.0,
  K8: 28.0,
  M1: -21,
  M2: -21,
  M3: -17,
  M4: -16,
  M5: -14,
  M6: -10,
  M7: 0.0,
  M8: 11.0,
  N1: -37,
  N2: -37,
  N3: -33,
  N4: -32,
  N5: -30,
  N6: -26,
  N7: -16,
  N8: -5,
  P1: -62,
  P2: -62,
  P3: -58,
  P4: -57,
  P5: -55,
  P6: -51,
  P7: -41,
  P8: -62,
  P9: -62,
  P10: -62,
  P11: -62,
  P12: -62,
  P13: -62,
  P14: -62,
  P15: -62,
  P16: -62,
  P17: -62,
  P18: -62
}, {
  from: 355,
  to: 400,
  A1: 1357.0,
  A2: 1359.0,
  A3: 1363.0,
  A4: 1368.0,
  A5: 1375.0,
  A6: 1386.0,
  A7: 1407.0,
  A8: 1439.0,
  A9: 1490.0,
  A10: 1580.0,
  A11: 1710.0,
  A12: 1920.0,
  A13: 2240.0,
  A14: 2750.0,
  A15: 3650.0,
  A16: 4950.0,
  A17: 7050.0,
  A18: 10250.0,
  B1: 687.0,
  B2: 689.0,
  B3: 693.0,
  B4: 698.0,
  B5: 705.0,
  B6: 716.0,
  B7: 737.0,
  B8: 769.0,
  B9: 820.0,
  B10: 910.0,
  B11: 1040.0,
  B12: 1250.0,
  B13: 1570.0,
  B14: 2080.0,
  B15: 2980.0,
  B16: 4280.0,
  B17: 6380.0,
  B18: 9580.0,
  C1: 407.0,
  C2: 409.0,
  C3: 413.0,
  C4: 418.0,
  C5: 425.0,
  C6: 436.0,
  C7: 457.0,
  C8: 489.0,
  C9: 540.0,
  C10: 630.0,
  C11: 760.0,
  C12: 970.0,
  C13: 1290.0,
  C14: 1800.0,
  C15: 2700.0,
  C16: 4000.0,
  C17: 6100.0,
  C18: 9300.0,
  D1: 217.0,
  D2: 219.0,
  D3: 223.0,
  D4: 228.0,
  D5: 235.0,
  D6: 246.0,
  D7: 267.0,
  D8: 299.0,
  D9: 350.0,
  D10: 440.0,
  D11: 570.0,
  D12: 780.0,
  D13: 1100.0,
  D14: 1610.0,
  D15: 2510.0,
  D16: 3810.0,
  D17: 5910.0,
  D18: 9110.0,
  E1: 132.0,
  E2: 134.0,
  E3: 138.0,
  E4: 143.0,
  E5: 150.0,
  E6: 161.0,
  E7: 182.0,
  E8: 214.0,
  E9: 265.0,
  E10: 355.0,
  E11: 485.0,
  E12: 695.0,
  E13: 1015.0,
  E14: 1525.0,
  E15: 2425.0,
  E16: 3725.0,
  E17: 5825.0,
  E18: 9025.0,
  F1: 69.0,
  F2: 71.0,
  F3: 75.0,
  F4: 80.0,
  F5: 87.0,
  F6: 98.0,
  F7: 119.0,
  F8: 151.0,
  F9: 202.0,
  F10: 292.0,
  F11: 422.0,
  F12: 632.0,
  F13: 952.0,
  F14: 1462.0,
  F15: 2362.0,
  F16: 3662.0,
  F17: 5762.0,
  F18: 8962.0,
  G1: 25.0,
  G2: 27.0,
  G3: 31.0,
  G4: 36.0,
  G5: 43.0,
  G6: 54.0,
  G7: 75.0,
  G8: 107.0,
  G9: 158.0,
  G10: 248.0,
  G11: 378.0,
  G12: 588.0,
  G13: 908.0,
  G14: 1418.0,
  G15: 2318.0,
  G16: 3618.0,
  G17: 5718.0,
  G18: 8918.0,
  H1: 7.0,
  H2: 9.0,
  H3: 13.0,
  H4: 18.0,
  H5: 25.0,
  H6: 36.0,
  H7: 57.0,
  H8: 89.0,
  H9: 140.0,
  H10: 230.0,
  H11: 360.0,
  H12: 570.0,
  H13: 890.0,
  H14: 1400.0,
  H15: 2300.0,
  H16: 3600.0,
  H17: 5700.0,
  H18: 8900.0,
  JS1: 3.5,
  JS2: 4.5,
  JS3: 6.5,
  JS4: 9.0,
  JS5: 12.5,
  JS6: 18.0,
  JS7: 28.5,
  JS8: 44.5,
  JS9: 70.0,
  JS10: 115.0,
  JS11: 180.0,
  JS12: 285.0,
  JS13: 445.0,
  JS14: 700.0,
  JS15: 1150.0,
  JS16: 1800.0,
  JS17: 2850.0,
  JS18: 4450.0,
  J6: 29.0,
  J7: 39.0,
  J8: 60.0,
  K1: -4,
  K2: -4,
  K3: 0.0,
  K4: 1.0,
  K5: 3.0,
  K6: 7.0,
  K7: 17.0,
  K8: 28.0,
  M1: -21,
  M2: -21,
  M3: -17,
  M4: -16,
  M5: -14,
  M6: -10,
  M7: 0.0,
  M8: 11.0,
  N1: -37,
  N2: -37,
  N3: -33,
  N4: -32,
  N5: -30,
  N6: -26,
  N7: -16,
  N8: -5,
  P1: -62,
  P2: -62,
  P3: -58,
  P4: -57,
  P5: -55,
  P6: -51,
  P7: -41,
  P8: -62,
  P9: -62,
  P10: -62,
  P11: -62,
  P12: -62,
  P13: -62,
  P14: -62,
  P15: -62,
  P16: -62,
  P17: -62,
  P18: -62
}, {
  from: 400,
  to: 450,
  A1: 1508.0,
  A2: 1510.0,
  A3: 1515.0,
  A4: 1520.0,
  A5: 1527.0,
  A6: 1540.0,
  A7: 1563.0,
  A8: 1597.0,
  A9: 1655.0,
  A10: 1750.0,
  A11: 1900.0,
  A12: 2130.0,
  A13: 2470.0,
  A14: 3050.0,
  A15: 4000.0,
  A16: 5500.0,
  A17: 7800.0,
  A18: 11200.0,
  B1: 768.0,
  B2: 770.0,
  B3: 775.0,
  B4: 780.0,
  B5: 787.0,
  B6: 800.0,
  B7: 823.0,
  B8: 857.0,
  B9: 915.0,
  B10: 1010.0,
  B11: 1160.0,
  B12: 1390.0,
  B13: 1730.0,
  B14: 2310.0,
  B15: 3260.0,
  B16: 4760.0,
  B17: 7060.0,
  B18: 10460.0,
  C1: 448.0,
  C2: 450.0,
  C3: 455.0,
  C4: 460.0,
  C5: 467.0,
  C6: 480.0,
  C7: 503.0,
  C8: 537.0,
  C9: 595.0,
  C10: 690.0,
  C11: 840.0,
  C12: 1070.0,
  C13: 1410.0,
  C14: 1990.0,
  C15: 2940.0,
  C16: 4440.0,
  C17: 6740.0,
  C18: 10140.0,
  D1: 238.0,
  D2: 240.0,
  D3: 245.0,
  D4: 250.0,
  D5: 257.0,
  D6: 270.0,
  D7: 293.0,
  D8: 327.0,
  D9: 385.0,
  D10: 480.0,
  D11: 630.0,
  D12: 860.0,
  D13: 1200.0,
  D14: 1780.0,
  D15: 2730.0,
  D16: 4230.0,
  D17: 6530.0,
  D18: 9930.0,
  E1: 143.0,
  E2: 145.0,
  E3: 150.0,
  E4: 155.0,
  E5: 162.0,
  E6: 175.0,
  E7: 198.0,
  E8: 232.0,
  E9: 290.0,
  E10: 385.0,
  E11: 535.0,
  E12: 765.0,
  E13: 1105.0,
  E14: 1685.0,
  E15: 2635.0,
  E16: 4135.0,
  E17: 6435.0,
  E18: 9835.0,
  F1: 76.0,
  F2: 78.0,
  F3: 83.0,
  F4: 88.0,
  F5: 95.0,
  F6: 108.0,
  F7: 131.0,
  F8: 165.0,
  F9: 223.0,
  F10: 318.0,
  F11: 468.0,
  F12: 698.0,
  F13: 1038.0,
  F14: 1618.0,
  F15: 2568.0,
  F16: 4068.0,
  F17: 6368.0,
  F18: 9768.0,
  G1: 28.0,
  G2: 30.0,
  G3: 35.0,
  G4: 40.0,
  G5: 47.0,
  G6: 60.0,
  G7: 83.0,
  G8: 117.0,
  G9: 175.0,
  G10: 270.0,
  G11: 420.0,
  G12: 650.0,
  G13: 990.0,
  G14: 1570.0,
  G15: 2520.0,
  G16: 4020.0,
  G17: 6320.0,
  G18: 9720.0,
  H1: 8.0,
  H2: 10.0,
  H3: 15.0,
  H4: 20.0,
  H5: 27.0,
  H6: 40.0,
  H7: 63.0,
  H8: 97.0,
  H9: 155.0,
  H10: 250.0,
  H11: 400.0,
  H12: 630.0,
  H13: 970.0,
  H14: 1550.0,
  H15: 2500.0,
  H16: 4000.0,
  H17: 6300.0,
  H18: 9700.0,
  JS1: 4.0,
  JS2: 5.0,
  JS3: 7.5,
  JS4: 10.0,
  JS5: 13.5,
  JS6: 20.0,
  JS7: 31.5,
  JS8: 48.5,
  JS9: 77.5,
  JS10: 125.0,
  JS11: 200.0,
  JS12: 315.0,
  JS13: 485.0,
  JS14: 775.0,
  JS15: 1250.0,
  JS16: 2000.0,
  JS17: 3150.0,
  JS18: 4850.0,
  J6: 33.0,
  J7: 43.0,
  J8: 66.0,
  K1: -5,
  K2: -5,
  K3: 0.0,
  K4: 0.0,
  K5: 2.0,
  K6: 8.0,
  K7: 18.0,
  K8: 29.0,
  M1: -23,
  M2: -23,
  M3: -18,
  M4: -18,
  M5: -16,
  M6: -10,
  M7: 0.0,
  M8: 11.0,
  N1: -40,
  N2: -40,
  N3: -35,
  N4: -35,
  N5: -33,
  N6: -27,
  N7: -17,
  N8: -6,
  P1: -68,
  P2: -68,
  P3: -63,
  P4: -63,
  P5: -61,
  P6: -55,
  P7: -45,
  P8: -68,
  P9: -68,
  P10: -68,
  P11: -68,
  P12: -68,
  P13: -68,
  P14: -68,
  P15: -68,
  P16: -68,
  P17: -68,
  P18: -68
}, {
  from: 450,
  to: 500,
  A1: 1658.0,
  A2: 1660.0,
  A3: 1665.0,
  A4: 1670.0,
  A5: 1677.0,
  A6: 1690.0,
  A7: 1713.0,
  A8: 1747.0,
  A9: 1805.0,
  A10: 1900.0,
  A11: 2050.0,
  A12: 2280.0,
  A13: 2620.0,
  A14: 3200.0,
  A15: 4150.0,
  A16: 5650.0,
  A17: 7950.0,
  A18: 11350.0,
  B1: 848.0,
  B2: 850.0,
  B3: 855.0,
  B4: 860.0,
  B5: 867.0,
  B6: 880.0,
  B7: 903.0,
  B8: 937.0,
  B9: 995.0,
  B10: 1090.0,
  B11: 1240.0,
  B12: 1470.0,
  B13: 1810.0,
  B14: 2390.0,
  B15: 3340.0,
  B16: 4840.0,
  B17: 7140.0,
  B18: 10540.0,
  C1: 488.0,
  C2: 490.0,
  C3: 495.0,
  C4: 500.0,
  C5: 507.0,
  C6: 520.0,
  C7: 543.0,
  C8: 577.0,
  C9: 635.0,
  C10: 730.0,
  C11: 880.0,
  C12: 1110.0,
  C13: 1450.0,
  C14: 2030.0,
  C15: 2980.0,
  C16: 4480.0,
  C17: 6780.0,
  C18: 10180.0,
  D1: 238.0,
  D2: 240.0,
  D3: 245.0,
  D4: 250.0,
  D5: 257.0,
  D6: 270.0,
  D7: 293.0,
  D8: 327.0,
  D9: 385.0,
  D10: 480.0,
  D11: 630.0,
  D12: 860.0,
  D13: 1200.0,
  D14: 1780.0,
  D15: 2730.0,
  D16: 4230.0,
  D17: 6530.0,
  D18: 9930.0,
  E1: 143.0,
  E2: 145.0,
  E3: 150.0,
  E4: 155.0,
  E5: 162.0,
  E6: 175.0,
  E7: 198.0,
  E8: 232.0,
  E9: 290.0,
  E10: 385.0,
  E11: 535.0,
  E12: 765.0,
  E13: 1105.0,
  E14: 1685.0,
  E15: 2635.0,
  E16: 4135.0,
  E17: 6435.0,
  E18: 9835.0,
  F1: 76.0,
  F2: 78.0,
  F3: 83.0,
  F4: 88.0,
  F5: 95.0,
  F6: 108.0,
  F7: 131.0,
  F8: 165.0,
  F9: 223.0,
  F10: 318.0,
  F11: 468.0,
  F12: 698.0,
  F13: 1038.0,
  F14: 1618.0,
  F15: 2568.0,
  F16: 4068.0,
  F17: 6368.0,
  F18: 9768.0,
  G1: 28.0,
  G2: 30.0,
  G3: 35.0,
  G4: 40.0,
  G5: 47.0,
  G6: 60.0,
  G7: 83.0,
  G8: 117.0,
  G9: 175.0,
  G10: 270.0,
  G11: 420.0,
  G12: 650.0,
  G13: 990.0,
  G14: 1570.0,
  G15: 2520.0,
  G16: 4020.0,
  G17: 6320.0,
  G18: 9720.0,
  H1: 8.0,
  H2: 10.0,
  H3: 15.0,
  H4: 20.0,
  H5: 27.0,
  H6: 40.0,
  H7: 63.0,
  H8: 97.0,
  H9: 155.0,
  H10: 250.0,
  H11: 400.0,
  H12: 630.0,
  H13: 970.0,
  H14: 1550.0,
  H15: 2500.0,
  H16: 4000.0,
  H17: 6300.0,
  H18: 9700.0,
  JS1: 4.0,
  JS2: 5.0,
  JS3: 7.5,
  JS4: 10.0,
  JS5: 13.5,
  JS6: 20.0,
  JS7: 31.5,
  JS8: 48.5,
  JS9: 77.5,
  JS10: 125.0,
  JS11: 200.0,
  JS12: 315.0,
  JS13: 485.0,
  JS14: 775.0,
  JS15: 1250.0,
  JS16: 2000.0,
  JS17: 3150.0,
  JS18: 4850.0,
  J6: 33.0,
  J7: 43.0,
  J8: 66.0,
  K1: -5,
  K2: -5,
  K3: 0.0,
  K4: 0.0,
  K5: 2.0,
  K6: 8.0,
  K7: 18.0,
  K8: 29.0,
  M1: -23,
  M2: -23,
  M3: -18,
  M4: -18,
  M5: -16,
  M6: -10,
  M7: 0.0,
  M8: 11.0,
  N1: -40,
  N2: -40,
  N3: -35,
  N4: -35,
  N5: -33,
  N6: -27,
  N7: -17,
  N8: -6,
  P1: -68,
  P2: -68,
  P3: -63,
  P4: -63,
  P5: -61,
  P6: -55,
  P7: -45,
  P8: -68,
  P9: -68,
  P10: -68,
  P11: -68,
  P12: -68,
  P13: -68,
  P14: -68,
  P15: -68,
  P16: -68,
  P17: -68,
  P18: -68
}];

const shaftLowerValues = [{
  from: 0,
  to: 3,
  a1: -270.8,
  a2: -271.2,
  a3: -272,
  a4: -273,
  a5: -274,
  a6: -276,
  a7: -280,
  a8: -284,
  a9: -295,
  a10: -310,
  a11: -330,
  a12: -370,
  a13: -410,
  a14: -520,
  a15: -670,
  a16: -870,
  a17: -1270,
  a18: -1670,
  b1: -140.8,
  b2: -141.2,
  b3: -142,
  b4: -143,
  b5: -144,
  b6: -146,
  b7: -150,
  b8: -154,
  b9: -165,
  b10: -180,
  b11: -200,
  b12: -240,
  b13: -280,
  b14: -390,
  b15: -540,
  b16: -740,
  b17: -1140,
  b18: -1540,
  c1: -60.8,
  c2: -61.2,
  c3: -62,
  c4: -63,
  c5: -64,
  c6: -66,
  c7: -70,
  c8: -74,
  c9: -85,
  c10: -100,
  c11: -120,
  c12: -160,
  c13: -200,
  c14: -310,
  c15: -460,
  c16: -660,
  c17: -1060,
  c18: -1460,
  d1: -20.8,
  d2: -21.2,
  d3: -22,
  d4: -23,
  d5: -24,
  d6: -26,
  d7: -30,
  d8: -34,
  d9: -45,
  d10: -60,
  d11: -80,
  d12: -120,
  d13: -160,
  d14: -270,
  d15: -420,
  d16: -620,
  d17: -1020,
  d18: -1420,
  e1: -14.8,
  e2: -15.2,
  e3: -16,
  e4: -17,
  e5: -18,
  e6: -20,
  e7: -24,
  e8: -28,
  e9: -39,
  e10: -54,
  e11: -74,
  e12: -114,
  e13: -154,
  e14: -264,
  e15: -414,
  e16: -614,
  e17: -1014,
  e18: -1414,
  f1: -6.8,
  f2: -7.2,
  f3: -8,
  f4: -9,
  f5: -10,
  f6: -12,
  f7: -16,
  f8: -20,
  f9: -31,
  f10: -46,
  f11: -66,
  f12: -106,
  f13: -146,
  f14: -256,
  f15: -406,
  f16: -606,
  f17: -1006,
  f18: -1406,
  g1: -2.8,
  g2: -3.2,
  g3: -4,
  g4: -5,
  g5: -6,
  g6: -8,
  g7: -12,
  g8: -16,
  g9: -27,
  g10: -42,
  g11: -62,
  g12: -102,
  g13: -142,
  g14: -252,
  g15: -402,
  g16: -602,
  g17: -1002,
  g18: -1402,
  h1: -0.8,
  h2: -1.2,
  h3: -2,
  h4: -3,
  h5: -4,
  h6: -6,
  h7: -10,
  h8: -14,
  h9: -25,
  h10: -40,
  h11: -60,
  h12: -100,
  h13: -140,
  h14: -250,
  h15: -400,
  h16: -600,
  h17: -1e3,
  h18: -1400,
  js1: -0.4,
  js2: -0.6,
  js3: -1,
  js4: -1.5,
  js5: -2,
  js6: -3,
  js7: -5,
  js8: -7,
  js9: -12.5,
  js10: -20,
  js11: -30,
  js12: -50,
  js13: -70,
  js14: -125,
  js15: -200,
  js16: -300,
  js17: -500,
  js18: -700,
  j5: -2,
  j6: -2,
  j7: -4,
  k4: 0,
  k5: 0,
  k6: 0,
  k7: 0,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 2,
  m2: 2,
  m3: 2,
  m4: 2,
  m5: 2,
  m6: 2,
  m7: 2,
  m8: 2,
  m9: 2,
  m10: 2,
  m11: 2,
  m12: 2,
  m13: 2,
  m14: 2,
  m15: 2,
  m16: 2,
  m17: 2,
  m18: 2,
  n1: 4,
  n2: 4,
  n3: 4,
  n4: 4,
  n5: 4,
  n6: 4,
  n7: 4,
  n8: 4,
  n9: 4,
  n10: 4,
  n11: 4,
  n12: 4,
  n13: 4,
  n14: 4,
  n15: 4,
  n16: 4,
  n17: 4,
  n18: 4,
  p1: 6,
  p2: 6,
  p3: 6,
  p4: 6,
  p5: 6,
  p6: 6,
  p7: 6,
  p8: 6,
  p9: 6,
  p10: 6,
  p11: 6,
  p12: 6,
  p13: 6,
  p14: 6,
  p15: 6,
  p16: 6,
  p17: 6,
  p18: 6,
  r1: 10,
  r2: 10,
  r3: 10,
  r4: 10,
  r5: 10,
  r6: 10,
  r7: 10,
  r8: 10,
  r9: 10,
  r10: 10,
  r11: 10,
  r12: 10,
  r13: 10,
  r14: 10,
  r15: 10,
  r16: 10,
  r17: 10,
  r18: 10,
  s1: 14.0,
  s2: 14.0,
  s3: 14.0,
  s4: 14.0,
  s5: 14.0,
  s6: 14.0,
  s7: 14.0,
  s8: 14.0,
  s9: 14.0,
  s10: 14.0,
  s11: 14.0,
  s12: 14.0,
  s13: 14.0,
  s14: 14.0,
  s15: 14.0,
  s16: 14.0,
  s17: 14.0,
  s18: 14.0
}, {
  from: 3,
  to: 6,
  a1: -271,
  a2: -271.5,
  a3: -272.5,
  a4: -274,
  a5: -275,
  a6: -278,
  a7: -282,
  a8: -288,
  a9: -300,
  a10: -318,
  a11: -345,
  a12: -390,
  a13: -450,
  a14: -570,
  a15: -750,
  a16: -1020,
  a17: -1470,
  a18: -2070,
  b1: -141,
  b2: -141.5,
  b3: -142.5,
  b4: -144,
  b5: -145,
  b6: -148,
  b7: -152,
  b8: -158,
  b9: -170,
  b10: -188,
  b11: -215,
  b12: -260,
  b13: -320,
  b14: -440,
  b15: -620,
  b16: -890,
  b17: -1340,
  b18: -1940,
  c1: -71,
  c2: -71.5,
  c3: -72.5,
  c4: -74,
  c5: -75,
  c6: -78,
  c7: -82,
  c8: -88,
  c9: -100,
  c10: -118,
  c11: -145,
  c12: -190,
  c13: -250,
  c14: -370,
  c15: -550,
  c16: -820,
  c17: -1270,
  c18: -1870,
  d1: -31,
  d2: -31.5,
  d3: -32.5,
  d4: -34,
  d5: -35,
  d6: -38,
  d7: -42,
  d8: -48,
  d9: -60,
  d10: -78,
  d11: -105,
  d12: -150,
  d13: -210,
  d14: -330,
  d15: -510,
  d16: -780,
  d17: -1230,
  d18: -1830,
  e1: -21,
  e2: -21.5,
  e3: -22.5,
  e4: -24,
  e5: -25,
  e6: -28,
  e7: -32,
  e8: -38,
  e9: -50,
  e10: -68,
  e11: -95,
  e12: -140,
  e13: -200,
  e14: -320,
  e15: -500,
  e16: -770,
  e17: -1220,
  e18: -1820,
  f1: -11,
  f2: -11.5,
  f3: -12.5,
  f4: -14,
  f5: -15,
  f6: -18,
  f7: -22,
  f8: -28,
  f9: -40,
  f10: -58,
  f11: -85,
  f12: -130,
  f13: -190,
  f14: -310,
  f15: -490,
  f16: -760,
  f17: -1210,
  f18: -1810,
  g1: -5,
  g2: -5.5,
  g3: -6.5,
  g4: -8,
  g5: -9,
  g6: -12,
  g7: -16,
  g8: -22,
  g9: -34,
  g10: -52,
  g11: -79,
  g12: -124,
  g13: -184,
  g14: -304,
  g15: -484,
  g16: -754,
  g17: -1204,
  g18: -1804,
  h1: -1,
  h2: -1.5,
  h3: -2.5,
  h4: -4,
  h5: -5,
  h6: -8,
  h7: -12,
  h8: -18,
  h9: -30,
  h10: -48,
  h11: -75,
  h12: -120,
  h13: -180,
  h14: -300,
  h15: -480,
  h16: -750,
  h17: -1200,
  h18: -1800,
  js1: -0.5,
  js2: -0.8,
  js3: -1.2,
  js4: -2,
  js5: -2.5,
  js6: -4,
  js7: -6,
  js8: -9,
  js9: -15,
  js10: -24,
  js11: -37.5,
  js12: -60,
  js13: -90,
  js14: -150,
  js15: -240,
  js16: -375,
  js17: -600,
  js18: -900,
  j5: -2,
  j6: -2,
  j7: -4,
  k4: 1,
  k5: 1,
  k6: 1,
  k7: 1,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 4,
  m2: 4,
  m3: 4,
  m4: 4,
  m5: 4,
  m6: 4,
  m7: 4,
  m8: 4,
  m9: 4,
  m10: 4,
  m11: 4,
  m12: 4,
  m13: 4,
  m14: 4,
  m15: 4,
  m16: 4,
  m17: 4,
  m18: 4,
  n1: 8,
  n2: 8,
  n3: 8,
  n4: 8,
  n5: 8,
  n6: 8,
  n7: 8,
  n8: 8,
  n9: 8,
  n10: 8,
  n11: 8,
  n12: 8,
  n13: 8,
  n14: 8,
  n15: 8,
  n16: 8,
  n17: 8,
  n18: 8,
  p1: 12,
  p2: 12,
  p3: 12,
  p4: 12,
  p5: 12,
  p6: 12,
  p7: 12,
  p8: 12,
  p9: 12,
  p10: 12,
  p11: 12,
  p12: 12,
  p13: 12,
  p14: 12,
  p15: 12,
  p16: 12,
  p17: 12,
  p18: 12,
  r1: 15,
  r2: 15,
  r3: 15,
  r4: 15,
  r5: 15,
  r6: 15,
  r7: 15,
  r8: 15,
  r9: 15,
  r10: 15,
  r11: 15,
  r12: 15,
  r13: 15,
  r14: 15,
  r15: 15,
  r16: 15,
  r17: 15,
  r18: 15,
  s1: 19.0,
  s2: 19.0,
  s3: 19.0,
  s4: 19.0,
  s5: 19.0,
  s6: 19.0,
  s7: 19.0,
  s8: 19.0,
  s9: 19.0,
  s10: 19.0,
  s11: 19.0,
  s12: 19.0,
  s13: 19.0,
  s14: 19.0,
  s15: 19.0,
  s16: 19.0,
  s17: 19.0,
  s18: 19.0
}, {
  from: 6,
  to: 10,
  a1: -281,
  a2: -281.5,
  a3: -282.5,
  a4: -284,
  a5: -286,
  a6: -289,
  a7: -295,
  a8: -302,
  a9: -316,
  a10: -338,
  a11: -370,
  a12: -430,
  a13: -500,
  a14: -640,
  a15: -860,
  a16: -1180,
  a17: -1780,
  a18: -2480,
  b1: -151,
  b2: -151.5,
  b3: -152.5,
  b4: -154,
  b5: -156,
  b6: -159,
  b7: -165,
  b8: -172,
  b9: -186,
  b10: -208,
  b11: -240,
  b12: -300,
  b13: -370,
  b14: -510,
  b15: -730,
  b16: -1050,
  b17: -1650,
  b18: -2350,
  c1: -81,
  c2: -81.5,
  c3: -82.5,
  c4: -84,
  c5: -86,
  c6: -89,
  c7: -95,
  c8: -102,
  c9: -116,
  c10: -138,
  c11: -170,
  c12: -230,
  c13: -300,
  c14: -440,
  c15: -660,
  c16: -980,
  c17: -1580,
  c18: -2280,
  d1: -41,
  d2: -41.5,
  d3: -42.5,
  d4: -44,
  d5: -46,
  d6: -49,
  d7: -55,
  d8: -62,
  d9: -76,
  d10: -98,
  d11: -130,
  d12: -190,
  d13: -260,
  d14: -400,
  d15: -620,
  d16: -940,
  d17: -1540,
  d18: -2240,
  e1: -26,
  e2: -26.5,
  e3: -27.5,
  e4: -29,
  e5: -31,
  e6: -34,
  e7: -40,
  e8: -47,
  e9: -61,
  e10: -83,
  e11: -115,
  e12: -175,
  e13: -245,
  e14: -385,
  e15: -605,
  e16: -925,
  e17: -1525,
  e18: -2225,
  f1: -14,
  f2: -14.5,
  f3: -15.5,
  f4: -17,
  f5: -19,
  f6: -22,
  f7: -28,
  f8: -35,
  f9: -49,
  f10: -71,
  f11: -103,
  f12: -163,
  f13: -233,
  f14: -373,
  f15: -593,
  f16: -913,
  f17: -1513,
  f18: -2213,
  g1: -6,
  g2: -6.5,
  g3: -7.5,
  g4: -9,
  g5: -11,
  g6: -14,
  g7: -20,
  g8: -27,
  g9: -41,
  g10: -63,
  g11: -95,
  g12: -155,
  g13: -225,
  g14: -365,
  g15: -585,
  g16: -905,
  g17: -1505,
  g18: -2205,
  h1: -1,
  h2: -1.5,
  h3: -2.5,
  h4: -4,
  h5: -6,
  h6: -9,
  h7: -15,
  h8: -22,
  h9: -36,
  h10: -58,
  h11: -90,
  h12: -150,
  h13: -220,
  h14: -360,
  h15: -580,
  h16: -900,
  h17: -1500,
  h18: -2200,
  js1: -0.5,
  js2: -0.8,
  js3: -1.3,
  js4: -2,
  js5: -3,
  js6: -4.5,
  js7: -7.5,
  js8: -11,
  js9: -18,
  js10: -29,
  js11: -45,
  js12: -75,
  js13: -110,
  js14: -180,
  js15: -290,
  js16: -450,
  js17: -750,
  js18: -1100,
  j5: -2,
  j6: -2,
  j7: -5,
  k4: 1,
  k5: 1,
  k6: 1,
  k7: 1,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 6,
  m2: 6,
  m3: 6,
  m4: 6,
  m5: 6,
  m6: 6,
  m7: 6,
  m8: 6,
  m9: 6,
  m10: 6,
  m11: 6,
  m12: 6,
  m13: 6,
  m14: 6,
  m15: 6,
  m16: 6,
  m17: 6,
  m18: 6,
  n1: 10,
  n2: 10,
  n3: 10,
  n4: 10,
  n5: 10,
  n6: 10,
  n7: 10,
  n8: 10,
  n9: 10,
  n10: 10,
  n11: 10,
  n12: 10,
  n13: 10,
  n14: 10,
  n15: 10,
  n16: 10,
  n17: 10,
  n18: 10,
  p1: 15,
  p2: 15,
  p3: 15,
  p4: 15,
  p5: 15,
  p6: 15,
  p7: 15,
  p8: 15,
  p9: 15,
  p10: 15,
  p11: 15,
  p12: 15,
  p13: 15,
  p14: 15,
  p15: 15,
  p16: 15,
  p17: 15,
  p18: 15,
  r1: 19,
  r2: 19,
  r3: 19,
  r4: 19,
  r5: 19,
  r6: 19,
  r7: 19,
  r8: 19,
  r9: 19,
  r10: 19,
  r11: 19,
  r12: 19,
  r13: 19,
  r14: 19,
  r15: 19,
  r16: 19,
  r17: 19,
  r18: 19,
  s1: 23.0,
  s2: 23.0,
  s3: 23.0,
  s4: 23.0,
  s5: 23.0,
  s6: 23.0,
  s7: 23.0,
  s8: 23.0,
  s9: 23.0,
  s10: 23.0,
  s11: 23.0,
  s12: 23.0,
  s13: 23.0,
  s14: 23.0,
  s15: 23.0,
  s16: 23.0,
  s17: 23.0,
  s18: 23.0
}, {
  from: 10,
  to: 18,
  a1: -291.2,
  a2: -292,
  a3: -293,
  a4: -295,
  a5: -298,
  a6: -301,
  a7: -308,
  a8: -317,
  a9: -333,
  a10: -360,
  a11: -400,
  a12: -470,
  a13: -560,
  a14: -720,
  a15: -990,
  a16: -1390,
  a17: -2090,
  a18: -2990,
  b1: -151.2,
  b2: -152,
  b3: -153,
  b4: -155,
  b5: -158,
  b6: -161,
  b7: -168,
  b8: -177,
  b9: -193,
  b10: -220,
  b11: -260,
  b12: -330,
  b13: -420,
  b14: -580,
  b15: -850,
  b16: -1250,
  b17: -1950,
  b18: -2850,
  c1: -96.2,
  c2: -97,
  c3: -98,
  c4: -100,
  c5: -103,
  c6: -106,
  c7: -113,
  c8: -122,
  c9: -138,
  c10: -165,
  c11: -205,
  c12: -275,
  c13: -365,
  c14: -525,
  c15: -795,
  c16: -1195,
  c17: -1895,
  c18: -2795,
  d1: -51.2,
  d2: -52,
  d3: -53,
  d4: -55,
  d5: -58,
  d6: -61,
  d7: -68,
  d8: -77,
  d9: -93,
  d10: -120,
  d11: -160,
  d12: -230,
  d13: -320,
  d14: -480,
  d15: -750,
  d16: -1150,
  d17: -1850,
  d18: -2750,
  e1: -33.2,
  e2: -34,
  e3: -35,
  e4: -37,
  e5: -40,
  e6: -43,
  e7: -50,
  e8: -59,
  e9: -75,
  e10: -102,
  e11: -142,
  e12: -212,
  e13: -302,
  e14: -462,
  e15: -732,
  e16: -1132,
  e17: -1832,
  e18: -2732,
  f1: -17.2,
  f2: -18,
  f3: -19,
  f4: -21,
  f5: -24,
  f6: -27,
  f7: -34,
  f8: -43,
  f9: -59,
  f10: -86,
  f11: -126,
  f12: -196,
  f13: -286,
  f14: -446,
  f15: -716,
  f16: -1116,
  f17: -1816,
  f18: -2716,
  g1: -7.2,
  g2: -8,
  g3: -9,
  g4: -11,
  g5: -14,
  g6: -17,
  g7: -24,
  g8: -33,
  g9: -49,
  g10: -76,
  g11: -116,
  g12: -186,
  g13: -276,
  g14: -436,
  g15: -706,
  g16: -1106,
  g17: -1806,
  g18: -2706,
  h1: -1.2,
  h2: -2,
  h3: -3,
  h4: -5,
  h5: -8,
  h6: -11,
  h7: -18,
  h8: -27,
  h9: -43,
  h10: -70,
  h11: -110,
  h12: -180,
  h13: -270,
  h14: -430,
  h15: -700,
  h16: -1100,
  h17: -1800,
  h18: -2700,
  js1: -0.6,
  js2: -1,
  js3: -1.5,
  js4: -2.5,
  js5: -4,
  js6: -5.5,
  js7: -9,
  js8: -13.5,
  js9: -21.5,
  js10: -35,
  js11: -55,
  js12: -90,
  js13: -135,
  js14: -215,
  js15: -350,
  js16: -550,
  js17: -900,
  js18: -1350,
  j5: -3,
  j6: -3,
  j7: -6,
  k4: 1,
  k5: 1,
  k6: 1,
  k7: 1,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 7,
  m2: 7,
  m3: 7,
  m4: 7,
  m5: 7,
  m6: 7,
  m7: 7,
  m8: 7,
  m9: 7,
  m10: 7,
  m11: 7,
  m12: 7,
  m13: 7,
  m14: 7,
  m15: 7,
  m16: 7,
  m17: 7,
  m18: 7,
  n1: 12,
  n2: 12,
  n3: 12,
  n4: 12,
  n5: 12,
  n6: 12,
  n7: 12,
  n8: 12,
  n9: 12,
  n10: 12,
  n11: 12,
  n12: 12,
  n13: 12,
  n14: 12,
  n15: 12,
  n16: 12,
  n17: 12,
  n18: 12,
  p1: 18,
  p2: 18,
  p3: 18,
  p4: 18,
  p5: 18,
  p6: 18,
  p7: 18,
  p8: 18,
  p9: 18,
  p10: 18,
  p11: 18,
  p12: 18,
  p13: 18,
  p14: 18,
  p15: 18,
  p16: 18,
  p17: 18,
  p18: 18,
  r1: 23,
  r2: 23,
  r3: 23,
  r4: 23,
  r5: 23,
  r6: 23,
  r7: 23,
  r8: 23,
  r9: 23,
  r10: 23,
  r11: 23,
  r12: 23,
  r13: 23,
  r14: 23,
  r15: 23,
  r16: 23,
  r17: 23,
  r18: 23,
  s1: 28.0,
  s2: 28.0,
  s3: 28.0,
  s4: 28.0,
  s5: 28.0,
  s6: 28.0,
  s7: 28.0,
  s8: 28.0,
  s9: 28.0,
  s10: 28.0,
  s11: 28.0,
  s12: 28.0,
  s13: 28.0,
  s14: 28.0,
  s15: 28.0,
  s16: 28.0,
  s17: 28.0,
  s18: 28.0
}, {
  from: 18,
  to: 30,
  a1: -301.5,
  a2: -302.5,
  a3: -304,
  a4: -306,
  a5: -309,
  a6: -313,
  a7: -321,
  a8: -333,
  a9: -352,
  a10: -384,
  a11: -430,
  a12: -510,
  a13: -630,
  a14: -820,
  a15: -1140,
  a16: -1600,
  a17: -2400,
  a18: -3600,
  b1: -161.5,
  b2: -162.5,
  b3: -164,
  b4: -166,
  b5: -169,
  b6: -173,
  b7: -181,
  b8: -193,
  b9: -212,
  b10: -244,
  b11: -290,
  b12: -370,
  b13: -490,
  b14: -680,
  b15: -1e3,
  b16: -1460,
  b17: -2260,
  b18: -3460,
  c1: -111.5,
  c2: -112.5,
  c3: -114,
  c4: -116,
  c5: -119,
  c6: -123,
  c7: -131,
  c8: -143,
  c9: -162,
  c10: -194,
  c11: -240,
  c12: -320,
  c13: -440,
  c14: -630,
  c15: -950,
  c16: -1410,
  c17: -2210,
  c18: -3410,
  d1: -66.5,
  d2: -67.5,
  d3: -69,
  d4: -71,
  d5: -74,
  d6: -78,
  d7: -86,
  d8: -98,
  d9: -117,
  d10: -149,
  d11: -195,
  d12: -275,
  d13: -395,
  d14: -585,
  d15: -905,
  d16: -1365,
  d17: -2165,
  d18: -3365,
  e1: -41.5,
  e2: -42.5,
  e3: -44,
  e4: -46,
  e5: -49,
  e6: -53,
  e7: -61,
  e8: -73,
  e9: -92,
  e10: -124,
  e11: -170,
  e12: -250,
  e13: -370,
  e14: -560,
  e15: -880,
  e16: -1340,
  e17: -2140,
  e18: -3340,
  f1: -21.5,
  f2: -22.5,
  f3: -24,
  f4: -26,
  f5: -29,
  f6: -33,
  f7: -41,
  f8: -53,
  f9: -72,
  f10: -104,
  f11: -150,
  f12: -230,
  f13: -350,
  f14: -540,
  f15: -860,
  f16: -1320,
  f17: -2120,
  f18: -3320,
  g1: -8.5,
  g2: -9.5,
  g3: -11,
  g4: -13,
  g5: -16,
  g6: -20,
  g7: -28,
  g8: -40,
  g9: -59,
  g10: -91,
  g11: -137,
  g12: -217,
  g13: -337,
  g14: -527,
  g15: -847,
  g16: -1307,
  g17: -2107,
  g18: -3307,
  h1: -1.5,
  h2: -2.5,
  h3: -4,
  h4: -6,
  h5: -9,
  h6: -13,
  h7: -21,
  h8: -33,
  h9: -52,
  h10: -84,
  h11: -130,
  h12: -210,
  h13: -330,
  h14: -520,
  h15: -840,
  h16: -1300,
  h17: -2100,
  h18: -3300,
  js1: -0.8,
  js2: -1.2,
  js3: -2,
  js4: -3,
  js5: -4.5,
  js6: -6.5,
  js7: -10.5,
  js8: -16.5,
  js9: -26,
  js10: -42,
  js11: -65,
  js12: -105,
  js13: -165,
  js14: -260,
  js15: -420,
  js16: -650,
  js17: -1050,
  js18: -1650,
  j5: -4,
  j6: -4,
  j7: -8,
  k4: 2,
  k5: 2,
  k6: 2,
  k7: 2,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 8,
  m2: 8,
  m3: 8,
  m4: 8,
  m5: 8,
  m6: 8,
  m7: 8,
  m8: 8,
  m9: 8,
  m10: 8,
  m11: 8,
  m12: 8,
  m13: 8,
  m14: 8,
  m15: 8,
  m16: 8,
  m17: 8,
  m18: 8,
  n1: 15,
  n2: 15,
  n3: 15,
  n4: 15,
  n5: 15,
  n6: 15,
  n7: 15,
  n8: 15,
  n9: 15,
  n10: 15,
  n11: 15,
  n12: 15,
  n13: 15,
  n14: 15,
  n15: 15,
  n16: 15,
  n17: 15,
  n18: 15,
  p1: 22,
  p2: 22,
  p3: 22,
  p4: 22,
  p5: 22,
  p6: 22,
  p7: 22,
  p8: 22,
  p9: 22,
  p10: 22,
  p11: 22,
  p12: 22,
  p13: 22,
  p14: 22,
  p15: 22,
  p16: 22,
  p17: 22,
  p18: 22,
  r1: 28,
  r2: 28,
  r3: 28,
  r4: 28,
  r5: 28,
  r6: 28,
  r7: 28,
  r8: 28,
  r9: 28,
  r10: 28,
  r11: 28,
  r12: 28,
  r13: 28,
  r14: 28,
  r15: 28,
  r16: 28,
  r17: 28,
  r18: 28,
  s1: 35.0,
  s2: 35.0,
  s3: 35.0,
  s4: 35.0,
  s5: 35.0,
  s6: 35.0,
  s7: 35.0,
  s8: 35.0,
  s9: 35.0,
  s10: 35.0,
  s11: 35.0,
  s12: 35.0,
  s13: 35.0,
  s14: 35.0,
  s15: 35.0,
  s16: 35.0,
  s17: 35.0,
  s18: 35.0
}, {
  from: 30,
  to: 40,
  a1: -311.5,
  a2: -312.5,
  a3: -314,
  a4: -317,
  a5: -321,
  a6: -326,
  a7: -335,
  a8: -349,
  a9: -372,
  a10: -410,
  a11: -470,
  a12: -560,
  a13: -700,
  a14: -930,
  a15: -1310,
  a16: -1910,
  a17: -2810,
  a18: -4210,
  b1: -171.5,
  b2: -172.5,
  b3: -174,
  b4: -177,
  b5: -181,
  b6: -186,
  b7: -195,
  b8: -209,
  b9: -232,
  b10: -270,
  b11: -330,
  b12: -420,
  b13: -560,
  b14: -790,
  b15: -1170,
  b16: -1770,
  b17: -2670,
  b18: -4070,
  c1: -121.5,
  c2: -122.5,
  c3: -124,
  c4: -127,
  c5: -131,
  c6: -136,
  c7: -145,
  c8: -159,
  c9: -182,
  c10: -220,
  c11: -280,
  c12: -370,
  c13: -510,
  c14: -740,
  c15: -1120,
  c16: -1720,
  c17: -2620,
  c18: -4020,
  d1: -81.5,
  d2: -82.5,
  d3: -84,
  d4: -87,
  d5: -91,
  d6: -96,
  d7: -105,
  d8: -119,
  d9: -142,
  d10: -180,
  d11: -240,
  d12: -330,
  d13: -470,
  d14: -700,
  d15: -1080,
  d16: -1680,
  d17: -2580,
  d18: -3980,
  e1: -51.5,
  e2: -52.5,
  e3: -54,
  e4: -57,
  e5: -61,
  e6: -66,
  e7: -75,
  e8: -89,
  e9: -112,
  e10: -150,
  e11: -210,
  e12: -300,
  e13: -440,
  e14: -670,
  e15: -1050,
  e16: -1650,
  e17: -2550,
  e18: -3950,
  f1: -26.5,
  f2: -27.5,
  f3: -29,
  f4: -32,
  f5: -36,
  f6: -41,
  f7: -50,
  f8: -64,
  f9: -87,
  f10: -125,
  f11: -185,
  f12: -275,
  f13: -415,
  f14: -645,
  f15: -1025,
  f16: -1625,
  f17: -2525,
  f18: -3925,
  g1: -10.5,
  g2: -11.5,
  g3: -13,
  g4: -16,
  g5: -20,
  g6: -25,
  g7: -34,
  g8: -48,
  g9: -71,
  g10: -109,
  g11: -169,
  g12: -259,
  g13: -399,
  g14: -629,
  g15: -1009,
  g16: -1609,
  g17: -2509,
  g18: -3909,
  h1: -1.5,
  h2: -2.5,
  h3: -4,
  h4: -7,
  h5: -11,
  h6: -16,
  h7: -25,
  h8: -39,
  h9: -62,
  h10: -100,
  h11: -160,
  h12: -250,
  h13: -390,
  h14: -620,
  h15: -1e3,
  h16: -1600,
  h17: -2500,
  h18: -3900,
  js1: -0.7,
  js2: -1.2,
  js3: -2,
  js4: -3.5,
  js5: -5.5,
  js6: -8,
  js7: -12.5,
  js8: -19.5,
  js9: -31,
  js10: -50,
  js11: -80,
  js12: -125,
  js13: -195,
  js14: -310,
  js15: -500,
  js16: -800,
  js17: -1250,
  js18: -1950,
  j5: -5,
  j6: -5,
  j7: -10,
  k4: 2,
  k5: 2,
  k6: 2,
  k7: 2,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 9,
  m2: 9,
  m3: 9,
  m4: 9,
  m5: 9,
  m6: 9,
  m7: 9,
  m8: 9,
  m9: 9,
  m10: 9,
  m11: 9,
  m12: 9,
  m13: 9,
  m14: 9,
  m15: 9,
  m16: 9,
  m17: 9,
  m18: 9,
  n1: 17,
  n2: 17,
  n3: 17,
  n4: 17,
  n5: 17,
  n6: 17,
  n7: 17,
  n8: 17,
  n9: 17,
  n10: 17,
  n11: 17,
  n12: 17,
  n13: 17,
  n14: 17,
  n15: 17,
  n16: 17,
  n17: 17,
  n18: 17,
  p1: 26,
  p2: 26,
  p3: 26,
  p4: 26,
  p5: 26,
  p6: 26,
  p7: 26,
  p8: 26,
  p9: 26,
  p10: 26,
  p11: 26,
  p12: 26,
  p13: 26,
  p14: 26,
  p15: 26,
  p16: 26,
  p17: 26,
  p18: 26,
  r1: 34,
  r2: 34,
  r3: 34,
  r4: 34,
  r5: 34,
  r6: 34,
  r7: 34,
  r8: 34,
  r9: 34,
  r10: 34,
  r11: 34,
  r12: 34,
  r13: 34,
  r14: 34,
  r15: 34,
  r16: 34,
  r17: 34,
  r18: 34,
  s1: 43.0,
  s2: 43.0,
  s3: 43.0,
  s4: 43.0,
  s5: 43.0,
  s6: 43.0,
  s7: 43.0,
  s8: 43.0,
  s9: 43.0,
  s10: 43.0,
  s11: 43.0,
  s12: 43.0,
  s13: 43.0,
  s14: 43.0,
  s15: 43.0,
  s16: 43.0,
  s17: 43.0,
  s18: 43.0
}, {
  from: 40,
  to: 50,
  a1: -321.5,
  a2: -322.5,
  a3: -324,
  a4: -327,
  a5: -331,
  a6: -336,
  a7: -345,
  a8: -359,
  a9: -382,
  a10: -420,
  a11: -480,
  a12: -570,
  a13: -710,
  a14: -940,
  a15: -1320,
  a16: -1920,
  a17: -2820,
  a18: -4220,
  b1: -181.5,
  b2: -182.5,
  b3: -184,
  b4: -187,
  b5: -191,
  b6: -196,
  b7: -205,
  b8: -219,
  b9: -242,
  b10: -280,
  b11: -340,
  b12: -430,
  b13: -570,
  b14: -800,
  b15: -1180,
  b16: -1780,
  b17: -2680,
  b18: -4080,
  c1: -131.5,
  c2: -132.5,
  c3: -134,
  c4: -137,
  c5: -141,
  c6: -146,
  c7: -155,
  c8: -169,
  c9: -192,
  c10: -230,
  c11: -290,
  c12: -380,
  c13: -520,
  c14: -750,
  c15: -1130,
  c16: -1730,
  c17: -2630,
  c18: -4030,
  d1: -81.5,
  d2: -82.5,
  d3: -84,
  d4: -87,
  d5: -91,
  d6: -96,
  d7: -105,
  d8: -119,
  d9: -142,
  d10: -180,
  d11: -240,
  d12: -330,
  d13: -470,
  d14: -700,
  d15: -1080,
  d16: -1680,
  d17: -2580,
  d18: -3980,
  e1: -51.5,
  e2: -52.5,
  e3: -54,
  e4: -57,
  e5: -61,
  e6: -66,
  e7: -75,
  e8: -89,
  e9: -112,
  e10: -150,
  e11: -210,
  e12: -300,
  e13: -440,
  e14: -670,
  e15: -1050,
  e16: -1650,
  e17: -2550,
  e18: -3950,
  f1: -26.5,
  f2: -27.5,
  f3: -29,
  f4: -32,
  f5: -36,
  f6: -41,
  f7: -50,
  f8: -64,
  f9: -87,
  f10: -125,
  f11: -185,
  f12: -275,
  f13: -415,
  f14: -645,
  f15: -1025,
  f16: -1625,
  f17: -2525,
  f18: -3925,
  g1: -10.5,
  g2: -11.5,
  g3: -13,
  g4: -16,
  g5: -20,
  g6: -25,
  g7: -34,
  g8: -48,
  g9: -71,
  g10: -109,
  g11: -169,
  g12: -259,
  g13: -399,
  g14: -629,
  g15: -1009,
  g16: -1609,
  g17: -2509,
  g18: -3909,
  h1: -1.5,
  h2: -2.5,
  h3: -4,
  h4: -7,
  h5: -11,
  h6: -16,
  h7: -25,
  h8: -39,
  h9: -62,
  h10: -100,
  h11: -160,
  h12: -250,
  h13: -390,
  h14: -620,
  h15: -1e3,
  h16: -1600,
  h17: -2500,
  h18: -3900,
  js1: -0.7,
  js2: -1.2,
  js3: -2,
  js4: -3.5,
  js5: -5.5,
  js6: -8,
  js7: -12.5,
  js8: -19.5,
  js9: -31,
  js10: -50,
  js11: -80,
  js12: -125,
  js13: -195,
  js14: -310,
  js15: -500,
  js16: -800,
  js17: -1250,
  js18: -1950,
  j5: -5,
  j6: -5,
  j7: -10,
  k4: 2,
  k5: 2,
  k6: 2,
  k7: 2,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 9,
  m2: 9,
  m3: 9,
  m4: 9,
  m5: 9,
  m6: 9,
  m7: 9,
  m8: 9,
  m9: 9,
  m10: 9,
  m11: 9,
  m12: 9,
  m13: 9,
  m14: 9,
  m15: 9,
  m16: 9,
  m17: 9,
  m18: 9,
  n1: 17,
  n2: 17,
  n3: 17,
  n4: 17,
  n5: 17,
  n6: 17,
  n7: 17,
  n8: 17,
  n9: 17,
  n10: 17,
  n11: 17,
  n12: 17,
  n13: 17,
  n14: 17,
  n15: 17,
  n16: 17,
  n17: 17,
  n18: 17,
  p1: 26,
  p2: 26,
  p3: 26,
  p4: 26,
  p5: 26,
  p6: 26,
  p7: 26,
  p8: 26,
  p9: 26,
  p10: 26,
  p11: 26,
  p12: 26,
  p13: 26,
  p14: 26,
  p15: 26,
  p16: 26,
  p17: 26,
  p18: 26,
  r1: 34,
  r2: 34,
  r3: 34,
  r4: 34,
  r5: 34,
  r6: 34,
  r7: 34,
  r8: 34,
  r9: 34,
  r10: 34,
  r11: 34,
  r12: 34,
  r13: 34,
  r14: 34,
  r15: 34,
  r16: 34,
  r17: 34,
  r18: 34,
  s1: 43.0,
  s2: 43.0,
  s3: 43.0,
  s4: 43.0,
  s5: 43.0,
  s6: 43.0,
  s7: 43.0,
  s8: 43.0,
  s9: 43.0,
  s10: 43.0,
  s11: 43.0,
  s12: 43.0,
  s13: 43.0,
  s14: 43.0,
  s15: 43.0,
  s16: 43.0,
  s17: 43.0,
  s18: 43.0
}, {
  from: 50,
  to: 65,
  a1: -342,
  a2: -343,
  a3: -345,
  a4: -348,
  a5: -353,
  a6: -359,
  a7: -370,
  a8: -386,
  a9: -414,
  a10: -460,
  a11: -530,
  a12: -640,
  a13: -800,
  a14: -1080,
  a15: -1540,
  a16: -2240,
  a17: -3340,
  a18: -4940,
  b1: -192,
  b2: -193,
  b3: -195,
  b4: -198,
  b5: -203,
  b6: -209,
  b7: -220,
  b8: -236,
  b9: -264,
  b10: -310,
  b11: -380,
  b12: -490,
  b13: -650,
  b14: -930,
  b15: -1390,
  b16: -2090,
  b17: -3190,
  b18: -4790,
  c1: -142,
  c2: -143,
  c3: -145,
  c4: -148,
  c5: -153,
  c6: -159,
  c7: -170,
  c8: -186,
  c9: -214,
  c10: -260,
  c11: -330,
  c12: -440,
  c13: -600,
  c14: -880,
  c15: -1340,
  c16: -2040,
  c17: -3140,
  c18: -4740,
  d1: -102,
  d2: -103,
  d3: -105,
  d4: -108,
  d5: -113,
  d6: -119,
  d7: -130,
  d8: -146,
  d9: -174,
  d10: -220,
  d11: -290,
  d12: -400,
  d13: -560,
  d14: -840,
  d15: -1300,
  d16: -2e3,
  d17: -3100,
  d18: -4700,
  e1: -62,
  e2: -63,
  e3: -65,
  e4: -68,
  e5: -73,
  e6: -79,
  e7: -90,
  e8: -106,
  e9: -134,
  e10: -180,
  e11: -250,
  e12: -360,
  e13: -520,
  e14: -800,
  e15: -1260,
  e16: -1960,
  e17: -3060,
  e18: -4660,
  f1: -32,
  f2: -33,
  f3: -35,
  f4: -38,
  f5: -43,
  f6: -49,
  f7: -60,
  f8: -76,
  f9: -104,
  f10: -150,
  f11: -220,
  f12: -330,
  f13: -490,
  f14: -770,
  f15: -1230,
  f16: -1930,
  f17: -3030,
  f18: -4630,
  g1: -12,
  g2: -13,
  g3: -15,
  g4: -18,
  g5: -23,
  g6: -29,
  g7: -40,
  g8: -56,
  g9: -84,
  g10: -130,
  g11: -200,
  g12: -310,
  g13: -470,
  g14: -750,
  g15: -1210,
  g16: -1910,
  g17: -3010,
  g18: -4610,
  h1: -2,
  h2: -3,
  h3: -5,
  h4: -8,
  h5: -13,
  h6: -19,
  h7: -30,
  h8: -46,
  h9: -74,
  h10: -120,
  h11: -190,
  h12: -300,
  h13: -460,
  h14: -740,
  h15: -1200,
  h16: -1900,
  h17: -3e3,
  h18: -4600,
  js1: -1,
  js2: -1.5,
  js3: -2.5,
  js4: -4,
  js5: -6.5,
  js6: -9.5,
  js7: -15,
  js8: -23,
  js9: -37,
  js10: -60,
  js11: -95,
  js12: -150,
  js13: -230,
  js14: -370,
  js15: -600,
  js16: -950,
  js17: -1500,
  js18: -2300,
  j5: -7,
  j6: -7,
  j7: -12,
  k4: 2,
  k5: 2,
  k6: 2,
  k7: 2,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 11,
  m2: 11,
  m3: 11,
  m4: 11,
  m5: 11,
  m6: 11,
  m7: 11,
  m8: 11,
  m9: 11,
  m10: 11,
  m11: 11,
  m12: 11,
  m13: 11,
  m14: 11,
  m15: 11,
  m16: 11,
  m17: 11,
  m18: 11,
  n1: 20,
  n2: 20,
  n3: 20,
  n4: 20,
  n5: 20,
  n6: 20,
  n7: 20,
  n8: 20,
  n9: 20,
  n10: 20,
  n11: 20,
  n12: 20,
  n13: 20,
  n14: 20,
  n15: 20,
  n16: 20,
  n17: 20,
  n18: 20,
  p1: 32,
  p2: 32,
  p3: 32,
  p4: 32,
  p5: 32,
  p6: 32,
  p7: 32,
  p8: 32,
  p9: 32,
  p10: 32,
  p11: 32,
  p12: 32,
  p13: 32,
  p14: 32,
  p15: 32,
  p16: 32,
  p17: 32,
  p18: 32,
  r1: 41,
  r2: 41,
  r3: 41,
  r4: 41,
  r5: 41,
  r6: 41,
  r7: 41,
  r8: 41,
  r9: 41,
  r10: 41,
  r11: 41,
  r12: 41,
  r13: 41,
  r14: 41,
  r15: 41,
  r16: 41,
  r17: 41,
  r18: 41,
  s1: 53.0,
  s2: 53.0,
  s3: 53.0,
  s4: 53.0,
  s5: 53.0,
  s6: 53.0,
  s7: 53.0,
  s8: 53.0,
  s9: 53.0,
  s10: 53.0,
  s11: 53.0,
  s12: 53.0,
  s13: 53.0,
  s14: 53.0,
  s15: 53.0,
  s16: 53.0,
  s17: 53.0,
  s18: 53.0
}, {
  from: 65,
  to: 80,
  a1: -362,
  a2: -363,
  a3: -365,
  a4: -368,
  a5: -373,
  a6: -379,
  a7: -390,
  a8: -406,
  a9: -434,
  a10: -480,
  a11: -550,
  a12: -660,
  a13: -820,
  a14: -1100,
  a15: -1560,
  a16: -2260,
  a17: -3360,
  a18: -4960,
  b1: -202,
  b2: -203,
  b3: -205,
  b4: -208,
  b5: -213,
  b6: -219,
  b7: -230,
  b8: -246,
  b9: -274,
  b10: -320,
  b11: -390,
  b12: -500,
  b13: -660,
  b14: -940,
  b15: -1400,
  b16: -2100,
  b17: -3200,
  b18: -4800,
  c1: -152,
  c2: -153,
  c3: -155,
  c4: -158,
  c5: -163,
  c6: -169,
  c7: -180,
  c8: -196,
  c9: -224,
  c10: -270,
  c11: -340,
  c12: -450,
  c13: -610,
  c14: -890,
  c15: -1350,
  c16: -2050,
  c17: -3150,
  c18: -4750,
  d1: -102,
  d2: -103,
  d3: -105,
  d4: -108,
  d5: -113,
  d6: -119,
  d7: -130,
  d8: -146,
  d9: -174,
  d10: -220,
  d11: -290,
  d12: -400,
  d13: -560,
  d14: -840,
  d15: -1300,
  d16: -2e3,
  d17: -3100,
  d18: -4700,
  e1: -62,
  e2: -63,
  e3: -65,
  e4: -68,
  e5: -73,
  e6: -79,
  e7: -90,
  e8: -106,
  e9: -134,
  e10: -180,
  e11: -250,
  e12: -360,
  e13: -520,
  e14: -800,
  e15: -1260,
  e16: -1960,
  e17: -3060,
  e18: -4660,
  f1: -32,
  f2: -33,
  f3: -35,
  f4: -38,
  f5: -43,
  f6: -49,
  f7: -60,
  f8: -76,
  f9: -104,
  f10: -150,
  f11: -220,
  f12: -330,
  f13: -490,
  f14: -770,
  f15: -1230,
  f16: -1930,
  f17: -3030,
  f18: -4630,
  g1: -12,
  g2: -13,
  g3: -15,
  g4: -18,
  g5: -23,
  g6: -29,
  g7: -40,
  g8: -56,
  g9: -84,
  g10: -130,
  g11: -200,
  g12: -310,
  g13: -470,
  g14: -750,
  g15: -1210,
  g16: -1910,
  g17: -3010,
  g18: -4610,
  h1: -2,
  h2: -3,
  h3: -5,
  h4: -8,
  h5: -13,
  h6: -19,
  h7: -30,
  h8: -46,
  h9: -74,
  h10: -120,
  h11: -190,
  h12: -300,
  h13: -460,
  h14: -740,
  h15: -1200,
  h16: -1900,
  h17: -3e3,
  h18: -4600,
  js1: -1,
  js2: -1.5,
  js3: -2.5,
  js4: -4,
  js5: -6.5,
  js6: -9.5,
  js7: -15,
  js8: -23,
  js9: -37,
  js10: -60,
  js11: -95,
  js12: -150,
  js13: -230,
  js14: -370,
  js15: -600,
  js16: -950,
  js17: -1500,
  js18: -2300,
  j5: -7,
  j6: -7,
  j7: -12,
  k4: 2,
  k5: 2,
  k6: 2,
  k7: 2,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 11,
  m2: 11,
  m3: 11,
  m4: 11,
  m5: 11,
  m6: 11,
  m7: 11,
  m8: 11,
  m9: 11,
  m10: 11,
  m11: 11,
  m12: 11,
  m13: 11,
  m14: 11,
  m15: 11,
  m16: 11,
  m17: 11,
  m18: 11,
  n1: 20,
  n2: 20,
  n3: 20,
  n4: 20,
  n5: 20,
  n6: 20,
  n7: 20,
  n8: 20,
  n9: 20,
  n10: 20,
  n11: 20,
  n12: 20,
  n13: 20,
  n14: 20,
  n15: 20,
  n16: 20,
  n17: 20,
  n18: 20,
  p1: 32,
  p2: 32,
  p3: 32,
  p4: 32,
  p5: 32,
  p6: 32,
  p7: 32,
  p8: 32,
  p9: 32,
  p10: 32,
  p11: 32,
  p12: 32,
  p13: 32,
  p14: 32,
  p15: 32,
  p16: 32,
  p17: 32,
  p18: 32,
  r1: 43,
  r2: 43,
  r3: 43,
  r4: 43,
  r5: 43,
  r6: 43,
  r7: 43,
  r8: 43,
  r9: 43,
  r10: 43,
  r11: 43,
  r12: 43,
  r13: 43,
  r14: 43,
  r15: 43,
  r16: 43,
  r17: 43,
  r18: 43,
  s1: 59.0,
  s2: 59.0,
  s3: 59.0,
  s4: 59.0,
  s5: 59.0,
  s6: 59.0,
  s7: 59.0,
  s8: 59.0,
  s9: 59.0,
  s10: 59.0,
  s11: 59.0,
  s12: 59.0,
  s13: 59.0,
  s14: 59.0,
  s15: 59.0,
  s16: 59.0,
  s17: 59.0,
  s18: 59.0
}, {
  from: 80,
  to: 100,
  a1: -382.5,
  a2: -384,
  a3: -386,
  a4: -390,
  a5: -395,
  a6: -402,
  a7: -415,
  a8: -434,
  a9: -467,
  a10: -520,
  a11: -600,
  a12: -730,
  a13: -920,
  a14: -1250,
  a15: -1780,
  a16: -2580,
  a17: -3880,
  a18: -5780,
  b1: -222.5,
  b2: -224,
  b3: -226,
  b4: -230,
  b5: -235,
  b6: -242,
  b7: -255,
  b8: -274,
  b9: -307,
  b10: -360,
  b11: -440,
  b12: -570,
  b13: -760,
  b14: -1090,
  b15: -1620,
  b16: -2420,
  b17: -3720,
  b18: -5620,
  c1: -172.5,
  c2: -174,
  c3: -176,
  c4: -180,
  c5: -185,
  c6: -192,
  c7: -205,
  c8: -224,
  c9: -257,
  c10: -310,
  c11: -390,
  c12: -520,
  c13: -710,
  c14: -1040,
  c15: -1570,
  c16: -2370,
  c17: -3670,
  c18: -5570,
  d1: -122.5,
  d2: -124,
  d3: -126,
  d4: -130,
  d5: -135,
  d6: -142,
  d7: -155,
  d8: -174,
  d9: -207,
  d10: -260,
  d11: -340,
  d12: -470,
  d13: -660,
  d14: -990,
  d15: -1520,
  d16: -2320,
  d17: -3620,
  d18: -5520,
  e1: -74.5,
  e2: -76,
  e3: -78,
  e4: -82,
  e5: -87,
  e6: -94,
  e7: -107,
  e8: -126,
  e9: -159,
  e10: -212,
  e11: -292,
  e12: -422,
  e13: -612,
  e14: -942,
  e15: -1472,
  e16: -2272,
  e17: -3572,
  e18: -5472,
  f1: -38.5,
  f2: -40,
  f3: -42,
  f4: -46,
  f5: -51,
  f6: -58,
  f7: -71,
  f8: -90,
  f9: -123,
  f10: -176,
  f11: -256,
  f12: -386,
  f13: -576,
  f14: -906,
  f15: -1436,
  f16: -2236,
  f17: -3536,
  f18: -5436,
  g1: -14.5,
  g2: -16,
  g3: -18,
  g4: -22,
  g5: -27,
  g6: -34,
  g7: -47,
  g8: -66,
  g9: -99,
  g10: -152,
  g11: -232,
  g12: -362,
  g13: -552,
  g14: -882,
  g15: -1412,
  g16: -2212,
  g17: -3512,
  g18: -5412,
  h1: -2.5,
  h2: -4,
  h3: -6,
  h4: -10,
  h5: -15,
  h6: -22,
  h7: -35,
  h8: -54,
  h9: -87,
  h10: -140,
  h11: -220,
  h12: -350,
  h13: -540,
  h14: -870,
  h15: -1400,
  h16: -2200,
  h17: -3500,
  h18: -5400,
  js1: -1.2,
  js2: -2,
  js3: -3,
  js4: -5,
  js5: -7.5,
  js6: -11,
  js7: -17.5,
  js8: -27,
  js9: -43.5,
  js10: -70,
  js11: -110,
  js12: -175,
  js13: -270,
  js14: -435,
  js15: -700,
  js16: -1100,
  js17: -1750,
  js18: -2700,
  j5: -9,
  j6: -9,
  j7: -15,
  k4: 3,
  k5: 3,
  k6: 3,
  k7: 3,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 13,
  m2: 13,
  m3: 13,
  m4: 13,
  m5: 13,
  m6: 13,
  m7: 13,
  m8: 13,
  m9: 13,
  m10: 13,
  m11: 13,
  m12: 13,
  m13: 13,
  m14: 13,
  m15: 13,
  m16: 13,
  m17: 13,
  m18: 13,
  n1: 23,
  n2: 23,
  n3: 23,
  n4: 23,
  n5: 23,
  n6: 23,
  n7: 23,
  n8: 23,
  n9: 23,
  n10: 23,
  n11: 23,
  n12: 23,
  n13: 23,
  n14: 23,
  n15: 23,
  n16: 23,
  n17: 23,
  n18: 23,
  p1: 37,
  p2: 37,
  p3: 37,
  p4: 37,
  p5: 37,
  p6: 37,
  p7: 37,
  p8: 37,
  p9: 37,
  p10: 37,
  p11: 37,
  p12: 37,
  p13: 37,
  p14: 37,
  p15: 37,
  p16: 37,
  p17: 37,
  p18: 37,
  r1: 51,
  r2: 51,
  r3: 51,
  r4: 51,
  r5: 51,
  r6: 51,
  r7: 51,
  r8: 51,
  r9: 51,
  r10: 51,
  r11: 51,
  r12: 51,
  r13: 51,
  r14: 51,
  r15: 51,
  r16: 51,
  r17: 51,
  r18: 51,
  s1: 71.0,
  s2: 71.0,
  s3: 71.0,
  s4: 71.0,
  s5: 71.0,
  s6: 71.0,
  s7: 71.0,
  s8: 71.0,
  s9: 71.0,
  s10: 71.0,
  s11: 71.0,
  s12: 71.0,
  s13: 71.0,
  s14: 71.0,
  s15: 71.0,
  s16: 71.0,
  s17: 71.0,
  s18: 71.0
}, {
  from: 100,
  to: 120,
  a1: -412.5,
  a2: -414,
  a3: -416,
  a4: -420,
  a5: -425,
  a6: -432,
  a7: -445,
  a8: -464,
  a9: -497,
  a10: -550,
  a11: -630,
  a12: -760,
  a13: -950,
  a14: -1280,
  a15: -1810,
  a16: -2610,
  a17: -3910,
  a18: -5810,
  b1: -242.5,
  b2: -244,
  b3: -246,
  b4: -250,
  b5: -255,
  b6: -262,
  b7: -275,
  b8: -294,
  b9: -327,
  b10: -380,
  b11: -460,
  b12: -590,
  b13: -780,
  b14: -1110,
  b15: -1640,
  b16: -2440,
  b17: -3740,
  b18: -5640,
  c1: -182.5,
  c2: -184,
  c3: -186,
  c4: -190,
  c5: -195,
  c6: -202,
  c7: -215,
  c8: -234,
  c9: -267,
  c10: -320,
  c11: -400,
  c12: -530,
  c13: -720,
  c14: -1050,
  c15: -1580,
  c16: -2380,
  c17: -3680,
  c18: -5580,
  d1: -122.5,
  d2: -124,
  d3: -126,
  d4: -130,
  d5: -135,
  d6: -142,
  d7: -155,
  d8: -174,
  d9: -207,
  d10: -260,
  d11: -340,
  d12: -470,
  d13: -660,
  d14: -990,
  d15: -1520,
  d16: -2320,
  d17: -3620,
  d18: -5520,
  e1: -74.5,
  e2: -76,
  e3: -78,
  e4: -82,
  e5: -87,
  e6: -94,
  e7: -107,
  e8: -126,
  e9: -159,
  e10: -212,
  e11: -292,
  e12: -422,
  e13: -612,
  e14: -942,
  e15: -1472,
  e16: -2272,
  e17: -3572,
  e18: -5472,
  f1: -38.5,
  f2: -40,
  f3: -42,
  f4: -46,
  f5: -51,
  f6: -58,
  f7: -71,
  f8: -90,
  f9: -123,
  f10: -176,
  f11: -256,
  f12: -386,
  f13: -576,
  f14: -906,
  f15: -1436,
  f16: -2236,
  f17: -3536,
  f18: -5436,
  g1: -14.5,
  g2: -16,
  g3: -18,
  g4: -22,
  g5: -27,
  g6: -34,
  g7: -47,
  g8: -66,
  g9: -99,
  g10: -152,
  g11: -232,
  g12: -362,
  g13: -552,
  g14: -882,
  g15: -1412,
  g16: -2212,
  g17: -3512,
  g18: -5412,
  h1: -2.5,
  h2: -4,
  h3: -6,
  h4: -10,
  h5: -15,
  h6: -22,
  h7: -35,
  h8: -54,
  h9: -87,
  h10: -140,
  h11: -220,
  h12: -350,
  h13: -540,
  h14: -870,
  h15: -1400,
  h16: -2200,
  h17: -3500,
  h18: -5400,
  js1: -1.2,
  js2: -2,
  js3: -3,
  js4: -5,
  js5: -7.5,
  js6: -11,
  js7: -17.5,
  js8: -27,
  js9: -43.5,
  js10: -70,
  js11: -110,
  js12: -175,
  js13: -270,
  js14: -435,
  js15: -700,
  js16: -1100,
  js17: -1750,
  js18: -2700,
  j5: -9,
  j6: -9,
  j7: -15,
  k4: 3,
  k5: 3,
  k6: 3,
  k7: 3,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 13,
  m2: 13,
  m3: 13,
  m4: 13,
  m5: 13,
  m6: 13,
  m7: 13,
  m8: 13,
  m9: 13,
  m10: 13,
  m11: 13,
  m12: 13,
  m13: 13,
  m14: 13,
  m15: 13,
  m16: 13,
  m17: 13,
  m18: 13,
  n1: 23,
  n2: 23,
  n3: 23,
  n4: 23,
  n5: 23,
  n6: 23,
  n7: 23,
  n8: 23,
  n9: 23,
  n10: 23,
  n11: 23,
  n12: 23,
  n13: 23,
  n14: 23,
  n15: 23,
  n16: 23,
  n17: 23,
  n18: 23,
  p1: 37,
  p2: 37,
  p3: 37,
  p4: 37,
  p5: 37,
  p6: 37,
  p7: 37,
  p8: 37,
  p9: 37,
  p10: 37,
  p11: 37,
  p12: 37,
  p13: 37,
  p14: 37,
  p15: 37,
  p16: 37,
  p17: 37,
  p18: 37,
  r1: 54,
  r2: 54,
  r3: 54,
  r4: 54,
  r5: 54,
  r6: 54,
  r7: 54,
  r8: 54,
  r9: 54,
  r10: 54,
  r11: 54,
  r12: 54,
  r13: 54,
  r14: 54,
  r15: 54,
  r16: 54,
  r17: 54,
  r18: 54,
  s1: 79.0,
  s2: 79.0,
  s3: 79.0,
  s4: 79.0,
  s5: 79.0,
  s6: 79.0,
  s7: 79.0,
  s8: 79.0,
  s9: 79.0,
  s10: 79.0,
  s11: 79.0,
  s12: 79.0,
  s13: 79.0,
  s14: 79.0,
  s15: 79.0,
  s16: 79.0,
  s17: 79.0,
  s18: 79.0
}, {
  from: 120,
  to: 140,
  a1: -463.5,
  a2: -465,
  a3: -468,
  a4: -472,
  a5: -478,
  a6: -485,
  a7: -500,
  a8: -523,
  a9: -560,
  a10: -620,
  a11: -710,
  a12: -860,
  a13: -1090,
  a14: -1460,
  a15: -2060,
  a16: -2960,
  a17: -4460,
  a18: -6760,
  b1: -263.5,
  b2: -265,
  b3: -268,
  b4: -272,
  b5: -278,
  b6: -285,
  b7: -300,
  b8: -323,
  b9: -360,
  b10: -420,
  b11: -510,
  b12: -660,
  b13: -890,
  b14: -1260,
  b15: -1860,
  b16: -2760,
  b17: -4260,
  b18: -6560,
  c1: -203.5,
  c2: -205,
  c3: -208,
  c4: -212,
  c5: -218,
  c6: -225,
  c7: -240,
  c8: -263,
  c9: -300,
  c10: -360,
  c11: -450,
  c12: -600,
  c13: -830,
  c14: -1200,
  c15: -1800,
  c16: -2700,
  c17: -4200,
  c18: -6500,
  d1: -148.5,
  d2: -150,
  d3: -153,
  d4: -157,
  d5: -163,
  d6: -170,
  d7: -185,
  d8: -208,
  d9: -245,
  d10: -305,
  d11: -395,
  d12: -545,
  d13: -775,
  d14: -1145,
  d15: -1745,
  d16: -2645,
  d17: -4145,
  d18: -6445,
  e1: -88.5,
  e2: -90,
  e3: -93,
  e4: -97,
  e5: -103,
  e6: -110,
  e7: -125,
  e8: -148,
  e9: -185,
  e10: -245,
  e11: -335,
  e12: -485,
  e13: -715,
  e14: -1085,
  e15: -1685,
  e16: -2585,
  e17: -4085,
  e18: -6385,
  f1: -46.5,
  f2: -48,
  f3: -51,
  f4: -55,
  f5: -61,
  f6: -68,
  f7: -83,
  f8: -106,
  f9: -143,
  f10: -203,
  f11: -293,
  f12: -443,
  f13: -673,
  f14: -1043,
  f15: -1643,
  f16: -2543,
  f17: -4043,
  f18: -6343,
  g1: -17.5,
  g2: -19,
  g3: -22,
  g4: -26,
  g5: -32,
  g6: -39,
  g7: -54,
  g8: -77,
  g9: -114,
  g10: -174,
  g11: -264,
  g12: -414,
  g13: -644,
  g14: -1014,
  g15: -1614,
  g16: -2514,
  g17: -4014,
  g18: -6314,
  h1: -3.5,
  h2: -5,
  h3: -8,
  h4: -12,
  h5: -18,
  h6: -25,
  h7: -40,
  h8: -63,
  h9: -100,
  h10: -160,
  h11: -250,
  h12: -400,
  h13: -630,
  h14: -1e3,
  h15: -1600,
  h16: -2500,
  h17: -4e3,
  h18: -6300,
  js1: -1.7,
  js2: -2.5,
  js3: -4,
  js4: -6,
  js5: -9,
  js6: -12.5,
  js7: -20,
  js8: -31.5,
  js9: -50,
  js10: -80,
  js11: -125,
  js12: -200,
  js13: -315,
  js14: -500,
  js15: -800,
  js16: -1250,
  js17: -2e3,
  js18: -3150,
  j5: -11,
  j6: -11,
  j7: -18,
  k4: 3,
  k5: 3,
  k6: 3,
  k7: 3,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 15,
  m2: 15,
  m3: 15,
  m4: 15,
  m5: 15,
  m6: 15,
  m7: 15,
  m8: 15,
  m9: 15,
  m10: 15,
  m11: 15,
  m12: 15,
  m13: 15,
  m14: 15,
  m15: 15,
  m16: 15,
  m17: 15,
  m18: 15,
  n1: 27,
  n2: 27,
  n3: 27,
  n4: 27,
  n5: 27,
  n6: 27,
  n7: 27,
  n8: 27,
  n9: 27,
  n10: 27,
  n11: 27,
  n12: 27,
  n13: 27,
  n14: 27,
  n15: 27,
  n16: 27,
  n17: 27,
  n18: 27,
  p1: 43,
  p2: 43,
  p3: 43,
  p4: 43,
  p5: 43,
  p6: 43,
  p7: 43,
  p8: 43,
  p9: 43,
  p10: 43,
  p11: 43,
  p12: 43,
  p13: 43,
  p14: 43,
  p15: 43,
  p16: 43,
  p17: 43,
  p18: 43,
  r1: 63,
  r2: 63,
  r3: 63,
  r4: 63,
  r5: 63,
  r6: 63,
  r7: 63,
  r8: 63,
  r9: 63,
  r10: 63,
  r11: 63,
  r12: 63,
  r13: 63,
  r14: 63,
  r15: 63,
  r16: 63,
  r17: 63,
  r18: 63,
  s1: 92.0,
  s2: 92.0,
  s3: 92.0,
  s4: 92.0,
  s5: 92.0,
  s6: 92.0,
  s7: 92.0,
  s8: 92.0,
  s9: 92.0,
  s10: 92.0,
  s11: 92.0,
  s12: 92.0,
  s13: 92.0,
  s14: 92.0,
  s15: 92.0,
  s16: 92.0,
  s17: 92.0,
  s18: 92.0
}, {
  from: 140,
  to: 160,
  a1: -523.5,
  a2: -525,
  a3: -528,
  a4: -532,
  a5: -538,
  a6: -545,
  a7: -560,
  a8: -583,
  a9: -620,
  a10: -680,
  a11: -770,
  a12: -920,
  a13: -1150,
  a14: -1520,
  a15: -2120,
  a16: -3020,
  a17: -4520,
  a18: -6820,
  b1: -283.5,
  b2: -285,
  b3: -288,
  b4: -292,
  b5: -298,
  b6: -305,
  b7: -320,
  b8: -343,
  b9: -380,
  b10: -440,
  b11: -530,
  b12: -680,
  b13: -910,
  b14: -1280,
  b15: -1880,
  b16: -2780,
  b17: -4280,
  b18: -6580,
  c1: -213.5,
  c2: -215,
  c3: -218,
  c4: -222,
  c5: -228,
  c6: -235,
  c7: -250,
  c8: -273,
  c9: -310,
  c10: -370,
  c11: -460,
  c12: -610,
  c13: -840,
  c14: -1210,
  c15: -1810,
  c16: -2710,
  c17: -4210,
  c18: -6510,
  d1: -148.5,
  d2: -150,
  d3: -153,
  d4: -157,
  d5: -163,
  d6: -170,
  d7: -185,
  d8: -208,
  d9: -245,
  d10: -305,
  d11: -395,
  d12: -545,
  d13: -775,
  d14: -1145,
  d15: -1745,
  d16: -2645,
  d17: -4145,
  d18: -6445,
  e1: -88.5,
  e2: -90,
  e3: -93,
  e4: -97,
  e5: -103,
  e6: -110,
  e7: -125,
  e8: -148,
  e9: -185,
  e10: -245,
  e11: -335,
  e12: -485,
  e13: -715,
  e14: -1085,
  e15: -1685,
  e16: -2585,
  e17: -4085,
  e18: -6385,
  f1: -46.5,
  f2: -48,
  f3: -51,
  f4: -55,
  f5: -61,
  f6: -68,
  f7: -83,
  f8: -106,
  f9: -143,
  f10: -203,
  f11: -293,
  f12: -443,
  f13: -673,
  f14: -1043,
  f15: -1643,
  f16: -2543,
  f17: -4043,
  f18: -6343,
  g1: -17.5,
  g2: -19,
  g3: -22,
  g4: -26,
  g5: -32,
  g6: -39,
  g7: -54,
  g8: -77,
  g9: -114,
  g10: -174,
  g11: -264,
  g12: -414,
  g13: -644,
  g14: -1014,
  g15: -1614,
  g16: -2514,
  g17: -4014,
  g18: -6314,
  h1: -3.5,
  h2: -5,
  h3: -8,
  h4: -12,
  h5: -18,
  h6: -25,
  h7: -40,
  h8: -63,
  h9: -100,
  h10: -160,
  h11: -250,
  h12: -400,
  h13: -630,
  h14: -1e3,
  h15: -1600,
  h16: -2500,
  h17: -4e3,
  h18: -6300,
  js1: -1.7,
  js2: -2.5,
  js3: -4,
  js4: -6,
  js5: -9,
  js6: -12.5,
  js7: -20,
  js8: -31.5,
  js9: -50,
  js10: -80,
  js11: -125,
  js12: -200,
  js13: -315,
  js14: -500,
  js15: -800,
  js16: -1250,
  js17: -2e3,
  js18: -3150,
  j5: -11,
  j6: -11,
  j7: -18,
  k4: 3,
  k5: 3,
  k6: 3,
  k7: 3,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 15,
  m2: 15,
  m3: 15,
  m4: 15,
  m5: 15,
  m6: 15,
  m7: 15,
  m8: 15,
  m9: 15,
  m10: 15,
  m11: 15,
  m12: 15,
  m13: 15,
  m14: 15,
  m15: 15,
  m16: 15,
  m17: 15,
  m18: 15,
  n1: 27,
  n2: 27,
  n3: 27,
  n4: 27,
  n5: 27,
  n6: 27,
  n7: 27,
  n8: 27,
  n9: 27,
  n10: 27,
  n11: 27,
  n12: 27,
  n13: 27,
  n14: 27,
  n15: 27,
  n16: 27,
  n17: 27,
  n18: 27,
  p1: 43,
  p2: 43,
  p3: 43,
  p4: 43,
  p5: 43,
  p6: 43,
  p7: 43,
  p8: 43,
  p9: 43,
  p10: 43,
  p11: 43,
  p12: 43,
  p13: 43,
  p14: 43,
  p15: 43,
  p16: 43,
  p17: 43,
  p18: 43,
  r1: 65,
  r2: 65,
  r3: 65,
  r4: 65,
  r5: 65,
  r6: 65,
  r7: 65,
  r8: 65,
  r9: 65,
  r10: 65,
  r11: 65,
  r12: 65,
  r13: 65,
  r14: 65,
  r15: 65,
  r16: 65,
  r17: 65,
  r18: 65,
  s1: 100.0,
  s2: 100.0,
  s3: 100.0,
  s4: 100.0,
  s5: 100.0,
  s6: 100.0,
  s7: 100.0,
  s8: 100.0,
  s9: 100.0,
  s10: 100.0,
  s11: 100.0,
  s12: 100.0,
  s13: 100.0,
  s14: 100.0,
  s15: 100.0,
  s16: 100.0,
  s17: 100.0,
  s18: 100.0
}, {
  from: 160,
  to: 180,
  a1: -583.5,
  a2: -585,
  a3: -588,
  a4: -592,
  a5: -598,
  a6: -605,
  a7: -620,
  a8: -643,
  a9: -680,
  a10: -740,
  a11: -830,
  a12: -980,
  a13: -1210,
  a14: -1580,
  a15: -2180,
  a16: -3080,
  a17: -4580,
  a18: -6880,
  b1: -313.5,
  b2: -315,
  b3: -318,
  b4: -322,
  b5: -328,
  b6: -335,
  b7: -350,
  b8: -373,
  b9: -410,
  b10: -470,
  b11: -560,
  b12: -710,
  b13: -940,
  b14: -1310,
  b15: -1910,
  b16: -2810,
  b17: -4310,
  b18: -6610,
  c1: -233.5,
  c2: -235,
  c3: -238,
  c4: -242,
  c5: -248,
  c6: -255,
  c7: -270,
  c8: -293,
  c9: -330,
  c10: -390,
  c11: -480,
  c12: -630,
  c13: -860,
  c14: -1230,
  c15: -1830,
  c16: -2730,
  c17: -4230,
  c18: -6530,
  d1: -148.5,
  d2: -150,
  d3: -153,
  d4: -157,
  d5: -163,
  d6: -170,
  d7: -185,
  d8: -208,
  d9: -245,
  d10: -305,
  d11: -395,
  d12: -545,
  d13: -775,
  d14: -1145,
  d15: -1745,
  d16: -2645,
  d17: -4145,
  d18: -6445,
  e1: -88.5,
  e2: -90,
  e3: -93,
  e4: -97,
  e5: -103,
  e6: -110,
  e7: -125,
  e8: -148,
  e9: -185,
  e10: -245,
  e11: -335,
  e12: -485,
  e13: -715,
  e14: -1085,
  e15: -1685,
  e16: -2585,
  e17: -4085,
  e18: -6385,
  f1: -46.5,
  f2: -48,
  f3: -51,
  f4: -55,
  f5: -61,
  f6: -68,
  f7: -83,
  f8: -106,
  f9: -143,
  f10: -203,
  f11: -293,
  f12: -443,
  f13: -673,
  f14: -1043,
  f15: -1643,
  f16: -2543,
  f17: -4043,
  f18: -6343,
  g1: -17.5,
  g2: -19,
  g3: -22,
  g4: -26,
  g5: -32,
  g6: -39,
  g7: -54,
  g8: -77,
  g9: -114,
  g10: -174,
  g11: -264,
  g12: -414,
  g13: -644,
  g14: -1014,
  g15: -1614,
  g16: -2514,
  g17: -4014,
  g18: -6314,
  h1: -3.5,
  h2: -5,
  h3: -8,
  h4: -12,
  h5: -18,
  h6: -25,
  h7: -40,
  h8: -63,
  h9: -100,
  h10: -160,
  h11: -250,
  h12: -400,
  h13: -630,
  h14: -1e3,
  h15: -1600,
  h16: -2500,
  h17: -4e3,
  h18: -6300,
  js1: -1.7,
  js2: -2.5,
  js3: -4,
  js4: -6,
  js5: -9,
  js6: -12.5,
  js7: -20,
  js8: -31.5,
  js9: -50,
  js10: -80,
  js11: -125,
  js12: -200,
  js13: -315,
  js14: -500,
  js15: -800,
  js16: -1250,
  js17: -2e3,
  js18: -3150,
  j5: -11,
  j6: -11,
  j7: -18,
  k4: 3,
  k5: 3,
  k6: 3,
  k7: 3,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 15,
  m2: 15,
  m3: 15,
  m4: 15,
  m5: 15,
  m6: 15,
  m7: 15,
  m8: 15,
  m9: 15,
  m10: 15,
  m11: 15,
  m12: 15,
  m13: 15,
  m14: 15,
  m15: 15,
  m16: 15,
  m17: 15,
  m18: 15,
  n1: 27,
  n2: 27,
  n3: 27,
  n4: 27,
  n5: 27,
  n6: 27,
  n7: 27,
  n8: 27,
  n9: 27,
  n10: 27,
  n11: 27,
  n12: 27,
  n13: 27,
  n14: 27,
  n15: 27,
  n16: 27,
  n17: 27,
  n18: 27,
  p1: 43,
  p2: 43,
  p3: 43,
  p4: 43,
  p5: 43,
  p6: 43,
  p7: 43,
  p8: 43,
  p9: 43,
  p10: 43,
  p11: 43,
  p12: 43,
  p13: 43,
  p14: 43,
  p15: 43,
  p16: 43,
  p17: 43,
  p18: 43,
  r1: 68,
  r2: 68,
  r3: 68,
  r4: 68,
  r5: 68,
  r6: 68,
  r7: 68,
  r8: 68,
  r9: 68,
  r10: 68,
  r11: 68,
  r12: 68,
  r13: 68,
  r14: 68,
  r15: 68,
  r16: 68,
  r17: 68,
  r18: 68,
  s1: 108.0,
  s2: 108.0,
  s3: 108.0,
  s4: 108.0,
  s5: 108.0,
  s6: 108.0,
  s7: 108.0,
  s8: 108.0,
  s9: 108.0,
  s10: 108.0,
  s11: 108.0,
  s12: 108.0,
  s13: 108.0,
  s14: 108.0,
  s15: 108.0,
  s16: 108.0,
  s17: 108.0,
  s18: 108.0
}, {
  from: 180,
  to: 200,
  a1: -664.5,
  a2: -667,
  a3: -670,
  a4: -674,
  a5: -680,
  a6: -689,
  a7: -706,
  a8: -732,
  a9: -775,
  a10: -845,
  a11: -950,
  a12: -1120,
  a13: -1380,
  a14: -1810,
  a15: -2510,
  a16: -3560,
  a17: -5260,
  a18: -7860,
  b1: -344.5,
  b2: -347,
  b3: -350,
  b4: -354,
  b5: -360,
  b6: -369,
  b7: -386,
  b8: -412,
  b9: -455,
  b10: -525,
  b11: -630,
  b12: -800,
  b13: -1060,
  b14: -1490,
  b15: -2190,
  b16: -3240,
  b17: -4940,
  b18: -7540,
  c1: -244.5,
  c2: -247,
  c3: -250,
  c4: -254,
  c5: -260,
  c6: -269,
  c7: -286,
  c8: -312,
  c9: -355,
  c10: -425,
  c11: -530,
  c12: -700,
  c13: -960,
  c14: -1390,
  c15: -2090,
  c16: -3140,
  c17: -4840,
  c18: -7440,
  d1: -174.5,
  d2: -177,
  d3: -180,
  d4: -184,
  d5: -190,
  d6: -199,
  d7: -216,
  d8: -242,
  d9: -285,
  d10: -355,
  d11: -460,
  d12: -630,
  d13: -890,
  d14: -1320,
  d15: -2020,
  d16: -3070,
  d17: -4770,
  d18: -7370,
  e1: -104.5,
  e2: -107,
  e3: -110,
  e4: -114,
  e5: -120,
  e6: -129,
  e7: -146,
  e8: -172,
  e9: -215,
  e10: -285,
  e11: -390,
  e12: -560,
  e13: -820,
  e14: -1250,
  e15: -1950,
  e16: -3e3,
  e17: -4700,
  e18: -7300,
  f1: -54.5,
  f2: -57,
  f3: -60,
  f4: -64,
  f5: -70,
  f6: -79,
  f7: -96,
  f8: -122,
  f9: -165,
  f10: -235,
  f11: -340,
  f12: -510,
  f13: -770,
  f14: -1200,
  f15: -1900,
  f16: -2950,
  f17: -4650,
  f18: -7250,
  g1: -19.5,
  g2: -22,
  g3: -25,
  g4: -29,
  g5: -35,
  g6: -44,
  g7: -61,
  g8: -87,
  g9: -130,
  g10: -200,
  g11: -305,
  g12: -475,
  g13: -735,
  g14: -1165,
  g15: -1865,
  g16: -2915,
  g17: -4615,
  g18: -7215,
  h1: -4.5,
  h2: -7,
  h3: -10,
  h4: -14,
  h5: -20,
  h6: -29,
  h7: -46,
  h8: -72,
  h9: -115,
  h10: -185,
  h11: -290,
  h12: -460,
  h13: -720,
  h14: -1150,
  h15: -1850,
  h16: -2900,
  h17: -4600,
  h18: -7200,
  js1: -2.3,
  js2: -3.5,
  js3: -5,
  js4: -7,
  js5: -10,
  js6: -14.5,
  js7: -23,
  js8: -36,
  js9: -57.5,
  js10: -92.5,
  js11: -145,
  js12: -230,
  js13: -360,
  js14: -575,
  js15: -925,
  js16: -1450,
  js17: -2300,
  js18: -3600,
  j5: -13,
  j6: -13,
  j7: -21,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 17,
  m2: 17,
  m3: 17,
  m4: 17,
  m5: 17,
  m6: 17,
  m7: 17,
  m8: 17,
  m9: 17,
  m10: 17,
  m11: 17,
  m12: 17,
  m13: 17,
  m14: 17,
  m15: 17,
  m16: 17,
  m17: 17,
  m18: 17,
  n1: 31,
  n2: 31,
  n3: 31,
  n4: 31,
  n5: 31,
  n6: 31,
  n7: 31,
  n8: 31,
  n9: 31,
  n10: 31,
  n11: 31,
  n12: 31,
  n13: 31,
  n14: 31,
  n15: 31,
  n16: 31,
  n17: 31,
  n18: 31,
  p1: 50,
  p2: 50,
  p3: 50,
  p4: 50,
  p5: 50,
  p6: 50,
  p7: 50,
  p8: 50,
  p9: 50,
  p10: 50,
  p11: 50,
  p12: 50,
  p13: 50,
  p14: 50,
  p15: 50,
  p16: 50,
  p17: 50,
  p18: 50,
  r1: 77,
  r2: 77,
  r3: 77,
  r4: 77,
  r5: 77,
  r6: 77,
  r7: 77,
  r8: 77,
  r9: 77,
  r10: 77,
  r11: 77,
  r12: 77,
  r13: 77,
  r14: 77,
  r15: 77,
  r16: 77,
  r17: 77,
  r18: 77,
  s1: 122.0,
  s2: 122.0,
  s3: 122.0,
  s4: 122.0,
  s5: 122.0,
  s6: 122.0,
  s7: 122.0,
  s8: 122.0,
  s9: 122.0,
  s10: 122.0,
  s11: 122.0,
  s12: 122.0,
  s13: 122.0,
  s14: 122.0,
  s15: 122.0,
  s16: 122.0,
  s17: 122.0,
  s18: 122.0
}, {
  from: 200,
  to: 225,
  a1: -744.5,
  a2: -747,
  a3: -750,
  a4: -754,
  a5: -760,
  a6: -769,
  a7: -786,
  a8: -812,
  a9: -855,
  a10: -925,
  a11: -1030,
  a12: -1200,
  a13: -1460,
  a14: -1890,
  a15: -2590,
  a16: -3640,
  a17: -5340,
  a18: -7940,
  b1: -384.5,
  b2: -387,
  b3: -390,
  b4: -394,
  b5: -400,
  b6: -409,
  b7: -426,
  b8: -452,
  b9: -495,
  b10: -565,
  b11: -670,
  b12: -840,
  b13: -1100,
  b14: -1530,
  b15: -2230,
  b16: -3280,
  b17: -4980,
  b18: -7580,
  c1: -264.5,
  c2: -267,
  c3: -270,
  c4: -274,
  c5: -280,
  c6: -289,
  c7: -306,
  c8: -332,
  c9: -375,
  c10: -445,
  c11: -550,
  c12: -720,
  c13: -980,
  c14: -1410,
  c15: -2110,
  c16: -3160,
  c17: -4860,
  c18: -7460,
  d1: -174.5,
  d2: -177,
  d3: -180,
  d4: -184,
  d5: -190,
  d6: -199,
  d7: -216,
  d8: -242,
  d9: -285,
  d10: -355,
  d11: -460,
  d12: -630,
  d13: -890,
  d14: -1320,
  d15: -2020,
  d16: -3070,
  d17: -4770,
  d18: -7370,
  e1: -104.5,
  e2: -107,
  e3: -110,
  e4: -114,
  e5: -120,
  e6: -129,
  e7: -146,
  e8: -172,
  e9: -215,
  e10: -285,
  e11: -390,
  e12: -560,
  e13: -820,
  e14: -1250,
  e15: -1950,
  e16: -3e3,
  e17: -4700,
  e18: -7300,
  f1: -54.5,
  f2: -57,
  f3: -60,
  f4: -64,
  f5: -70,
  f6: -79,
  f7: -96,
  f8: -122,
  f9: -165,
  f10: -235,
  f11: -340,
  f12: -510,
  f13: -770,
  f14: -1200,
  f15: -1900,
  f16: -2950,
  f17: -4650,
  f18: -7250,
  g1: -19.5,
  g2: -22,
  g3: -25,
  g4: -29,
  g5: -35,
  g6: -44,
  g7: -61,
  g8: -87,
  g9: -130,
  g10: -200,
  g11: -305,
  g12: -475,
  g13: -735,
  g14: -1165,
  g15: -1865,
  g16: -2915,
  g17: -4615,
  g18: -7215,
  h1: -4.5,
  h2: -7,
  h3: -10,
  h4: -14,
  h5: -20,
  h6: -29,
  h7: -46,
  h8: -72,
  h9: -115,
  h10: -185,
  h11: -290,
  h12: -460,
  h13: -720,
  h14: -1150,
  h15: -1850,
  h16: -2900,
  h17: -4600,
  h18: -7200,
  js1: -2.3,
  js2: -3.5,
  js3: -5,
  js4: -7,
  js5: -10,
  js6: -14.5,
  js7: -23,
  js8: -36,
  js9: -57.5,
  js10: -92.5,
  js11: -145,
  js12: -230,
  js13: -360,
  js14: -575,
  js15: -925,
  js16: -1450,
  js17: -2300,
  js18: -3600,
  j5: -13,
  j6: -13,
  j7: -21,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 17,
  m2: 17,
  m3: 17,
  m4: 17,
  m5: 17,
  m6: 17,
  m7: 17,
  m8: 17,
  m9: 17,
  m10: 17,
  m11: 17,
  m12: 17,
  m13: 17,
  m14: 17,
  m15: 17,
  m16: 17,
  m17: 17,
  m18: 17,
  n1: 31,
  n2: 31,
  n3: 31,
  n4: 31,
  n5: 31,
  n6: 31,
  n7: 31,
  n8: 31,
  n9: 31,
  n10: 31,
  n11: 31,
  n12: 31,
  n13: 31,
  n14: 31,
  n15: 31,
  n16: 31,
  n17: 31,
  n18: 31,
  p1: 50,
  p2: 50,
  p3: 50,
  p4: 50,
  p5: 50,
  p6: 50,
  p7: 50,
  p8: 50,
  p9: 50,
  p10: 50,
  p11: 50,
  p12: 50,
  p13: 50,
  p14: 50,
  p15: 50,
  p16: 50,
  p17: 50,
  p18: 50,
  r1: 80,
  r2: 80,
  r3: 80,
  r4: 80,
  r5: 80,
  r6: 80,
  r7: 80,
  r8: 80,
  r9: 80,
  r10: 80,
  r11: 80,
  r12: 80,
  r13: 80,
  r14: 80,
  r15: 80,
  r16: 80,
  r17: 80,
  r18: 80,
  s1: 130.0,
  s2: 130.0,
  s3: 130.0,
  s4: 130.0,
  s5: 130.0,
  s6: 130.0,
  s7: 130.0,
  s8: 130.0,
  s9: 130.0,
  s10: 130.0,
  s11: 130.0,
  s12: 130.0,
  s13: 130.0,
  s14: 130.0,
  s15: 130.0,
  s16: 130.0,
  s17: 130.0,
  s18: 130.0
}, {
  from: 225,
  to: 250,
  a1: -824.5,
  a2: -827,
  a3: -830,
  a4: -834,
  a5: -840,
  a6: -849,
  a7: -866,
  a8: -892,
  a9: -935,
  a10: -1005,
  a11: -1110,
  a12: -1280,
  a13: -1540,
  a14: -1970,
  a15: -2670,
  a16: -3720,
  a17: -5420,
  a18: -8020,
  b1: -424.5,
  b2: -427,
  b3: -430,
  b4: -434,
  b5: -440,
  b6: -449,
  b7: -466,
  b8: -492,
  b9: -535,
  b10: -605,
  b11: -710,
  b12: -880,
  b13: -1140,
  b14: -1570,
  b15: -2270,
  b16: -3320,
  b17: -5020,
  b18: -7620,
  c1: -284.5,
  c2: -287,
  c3: -290,
  c4: -294,
  c5: -300,
  c6: -309,
  c7: -326,
  c8: -352,
  c9: -395,
  c10: -465,
  c11: -570,
  c12: -740,
  c13: -1e3,
  c14: -1430,
  c15: -2130,
  c16: -3180,
  c17: -4880,
  c18: -7480,
  d1: -174.5,
  d2: -177,
  d3: -180,
  d4: -184,
  d5: -190,
  d6: -199,
  d7: -216,
  d8: -242,
  d9: -285,
  d10: -355,
  d11: -460,
  d12: -630,
  d13: -890,
  d14: -1320,
  d15: -2020,
  d16: -3070,
  d17: -4770,
  d18: -7370,
  e1: -104.5,
  e2: -107,
  e3: -110,
  e4: -114,
  e5: -120,
  e6: -129,
  e7: -146,
  e8: -172,
  e9: -215,
  e10: -285,
  e11: -390,
  e12: -560,
  e13: -820,
  e14: -1250,
  e15: -1950,
  e16: -3e3,
  e17: -4700,
  e18: -7300,
  f1: -54.5,
  f2: -57,
  f3: -60,
  f4: -64,
  f5: -70,
  f6: -79,
  f7: -96,
  f8: -122,
  f9: -165,
  f10: -235,
  f11: -340,
  f12: -510,
  f13: -770,
  f14: -1200,
  f15: -1900,
  f16: -2950,
  f17: -4650,
  f18: -7250,
  g1: -19.5,
  g2: -22,
  g3: -25,
  g4: -29,
  g5: -35,
  g6: -44,
  g7: -61,
  g8: -87,
  g9: -130,
  g10: -200,
  g11: -305,
  g12: -475,
  g13: -735,
  g14: -1165,
  g15: -1865,
  g16: -2915,
  g17: -4615,
  g18: -7215,
  h1: -4.5,
  h2: -7,
  h3: -10,
  h4: -14,
  h5: -20,
  h6: -29,
  h7: -46,
  h8: -72,
  h9: -115,
  h10: -185,
  h11: -290,
  h12: -460,
  h13: -720,
  h14: -1150,
  h15: -1850,
  h16: -2900,
  h17: -4600,
  h18: -7200,
  js1: -2.3,
  js2: -3.5,
  js3: -5,
  js4: -7,
  js5: -10,
  js6: -14.5,
  js7: -23,
  js8: -36,
  js9: -57.5,
  js10: -92.5,
  js11: -145,
  js12: -230,
  js13: -360,
  js14: -575,
  js15: -925,
  js16: -1450,
  js17: -2300,
  js18: -3600,
  j5: -13,
  j6: -13,
  j7: -21,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 17,
  m2: 17,
  m3: 17,
  m4: 17,
  m5: 17,
  m6: 17,
  m7: 17,
  m8: 17,
  m9: 17,
  m10: 17,
  m11: 17,
  m12: 17,
  m13: 17,
  m14: 17,
  m15: 17,
  m16: 17,
  m17: 17,
  m18: 17,
  n1: 31,
  n2: 31,
  n3: 31,
  n4: 31,
  n5: 31,
  n6: 31,
  n7: 31,
  n8: 31,
  n9: 31,
  n10: 31,
  n11: 31,
  n12: 31,
  n13: 31,
  n14: 31,
  n15: 31,
  n16: 31,
  n17: 31,
  n18: 31,
  p1: 50,
  p2: 50,
  p3: 50,
  p4: 50,
  p5: 50,
  p6: 50,
  p7: 50,
  p8: 50,
  p9: 50,
  p10: 50,
  p11: 50,
  p12: 50,
  p13: 50,
  p14: 50,
  p15: 50,
  p16: 50,
  p17: 50,
  p18: 50,
  r1: 84,
  r2: 84,
  r3: 84,
  r4: 84,
  r5: 84,
  r6: 84,
  r7: 84,
  r8: 84,
  r9: 84,
  r10: 84,
  r11: 84,
  r12: 84,
  r13: 84,
  r14: 84,
  r15: 84,
  r16: 84,
  r17: 84,
  r18: 84,
  s1: 140.0,
  s2: 140.0,
  s3: 140.0,
  s4: 140.0,
  s5: 140.0,
  s6: 140.0,
  s7: 140.0,
  s8: 140.0,
  s9: 140.0,
  s10: 140.0,
  s11: 140.0,
  s12: 140.0,
  s13: 140.0,
  s14: 140.0,
  s15: 140.0,
  s16: 140.0,
  s17: 140.0,
  s18: 140.0
}, {
  from: 250,
  to: 280,
  a1: -926,
  a2: -928,
  a3: -932,
  a4: -936,
  a5: -943,
  a6: -952,
  a7: -972,
  a8: -1001,
  a9: -1050,
  a10: -1130,
  a11: -1240,
  a12: -1440,
  a13: -1730,
  a14: -2220,
  a15: -3020,
  a16: -4120,
  a17: -6120,
  a18: -9020,
  b1: -486,
  b2: -488,
  b3: -492,
  b4: -496,
  b5: -503,
  b6: -512,
  b7: -532,
  b8: -561,
  b9: -610,
  b10: -690,
  b11: -800,
  b12: -1e3,
  b13: -1290,
  b14: -1780,
  b15: -2580,
  b16: -3680,
  b17: -5680,
  b18: -8580,
  c1: -306,
  c2: -308,
  c3: -312,
  c4: -316,
  c5: -323,
  c6: -332,
  c7: -352,
  c8: -381,
  c9: -430,
  c10: -510,
  c11: -620,
  c12: -820,
  c13: -1110,
  c14: -1600,
  c15: -2400,
  c16: -3500,
  c17: -5500,
  c18: -8400,
  d1: -196,
  d2: -198,
  d3: -202,
  d4: -206,
  d5: -213,
  d6: -222,
  d7: -242,
  d8: -271,
  d9: -320,
  d10: -400,
  d11: -510,
  d12: -710,
  d13: -1e3,
  d14: -1490,
  d15: -2290,
  d16: -3390,
  d17: -5390,
  d18: -8290,
  e1: -116,
  e2: -118,
  e3: -122,
  e4: -126,
  e5: -133,
  e6: -142,
  e7: -162,
  e8: -191,
  e9: -240,
  e10: -320,
  e11: -430,
  e12: -630,
  e13: -920,
  e14: -1410,
  e15: -2210,
  e16: -3310,
  e17: -5310,
  e18: -8210,
  f1: -62,
  f2: -64,
  f3: -68,
  f4: -72,
  f5: -79,
  f6: -88,
  f7: -108,
  f8: -137,
  f9: -186,
  f10: -266,
  f11: -376,
  f12: -576,
  f13: -866,
  f14: -1356,
  f15: -2156,
  f16: -3256,
  f17: -5256,
  f18: -8156,
  g1: -23,
  g2: -25,
  g3: -29,
  g4: -33,
  g5: -40,
  g6: -49,
  g7: -69,
  g8: -98,
  g9: -147,
  g10: -227,
  g11: -337,
  g12: -537,
  g13: -827,
  g14: -1317,
  g15: -2117,
  g16: -3217,
  g17: -5217,
  g18: -8117,
  h1: -6,
  h2: -8,
  h3: -12,
  h4: -16,
  h5: -23,
  h6: -32,
  h7: -52,
  h8: -81,
  h9: -130,
  h10: -210,
  h11: -320,
  h12: -520,
  h13: -810,
  h14: -1300,
  h15: -2100,
  h16: -3200,
  h17: -5200,
  h18: -8100,
  js1: -3,
  js2: -4,
  js3: -6,
  js4: -8,
  js5: -11.5,
  js6: -16,
  js7: -26,
  js8: -40.5,
  js9: -65,
  js10: -105,
  js11: -160,
  js12: -260,
  js13: -405,
  js14: -650,
  js15: -1050,
  js16: -1600,
  js17: -2600,
  js18: -4050,
  j5: -16,
  j6: -16,
  j7: -26,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 20,
  m2: 20,
  m3: 20,
  m4: 20,
  m5: 20,
  m6: 20,
  m7: 20,
  m8: 20,
  m9: 20,
  m10: 20,
  m11: 20,
  m12: 20,
  m13: 20,
  m14: 20,
  m15: 20,
  m16: 20,
  m17: 20,
  m18: 20,
  n1: 34,
  n2: 34,
  n3: 34,
  n4: 34,
  n5: 34,
  n6: 34,
  n7: 34,
  n8: 34,
  n9: 34,
  n10: 34,
  n11: 34,
  n12: 34,
  n13: 34,
  n14: 34,
  n15: 34,
  n16: 34,
  n17: 34,
  n18: 34,
  p1: 56,
  p2: 56,
  p3: 56,
  p4: 56,
  p5: 56,
  p6: 56,
  p7: 56,
  p8: 56,
  p9: 56,
  p10: 56,
  p11: 56,
  p12: 56,
  p13: 56,
  p14: 56,
  p15: 56,
  p16: 56,
  p17: 56,
  p18: 56,
  r1: 94,
  r2: 94,
  r3: 94,
  r4: 94,
  r5: 94,
  r6: 94,
  r7: 94,
  r8: 94,
  r9: 94,
  r10: 94,
  r11: 94,
  r12: 94,
  r13: 94,
  r14: 94,
  r15: 94,
  r16: 94,
  r17: 94,
  r18: 94,
  s1: 158.0,
  s2: 158.0,
  s3: 158.0,
  s4: 158.0,
  s5: 158.0,
  s6: 158.0,
  s7: 158.0,
  s8: 158.0,
  s9: 158.0,
  s10: 158.0,
  s11: 158.0,
  s12: 158.0,
  s13: 158.0,
  s14: 158.0,
  s15: 158.0,
  s16: 158.0,
  s17: 158.0,
  s18: 158.0
}, {
  from: 280,
  to: 315,
  a1: -1056,
  a2: -1058,
  a3: -1062,
  a4: -1066,
  a5: -1073,
  a6: -1082,
  a7: -1102,
  a8: -1131,
  a9: -1180,
  a10: -1260,
  a11: -1370,
  a12: -1570,
  a13: -1860,
  a14: -2350,
  a15: -3150,
  a16: -4250,
  a17: -6250,
  a18: -9150,
  b1: -546,
  b2: -548,
  b3: -552,
  b4: -556,
  b5: -563,
  b6: -572,
  b7: -592,
  b8: -621,
  b9: -670,
  b10: -750,
  b11: -860,
  b12: -1060,
  b13: -1350,
  b14: -1840,
  b15: -2640,
  b16: -3740,
  b17: -5740,
  b18: -8640,
  c1: -336,
  c2: -338,
  c3: -342,
  c4: -346,
  c5: -353,
  c6: -362,
  c7: -382,
  c8: -411,
  c9: -460,
  c10: -540,
  c11: -650,
  c12: -850,
  c13: -1140,
  c14: -1630,
  c15: -2430,
  c16: -3530,
  c17: -5530,
  c18: -8430,
  d1: -196,
  d2: -198,
  d3: -202,
  d4: -206,
  d5: -213,
  d6: -222,
  d7: -242,
  d8: -271,
  d9: -320,
  d10: -400,
  d11: -510,
  d12: -710,
  d13: -1e3,
  d14: -1490,
  d15: -2290,
  d16: -3390,
  d17: -5390,
  d18: -8290,
  e1: -116,
  e2: -118,
  e3: -122,
  e4: -126,
  e5: -133,
  e6: -142,
  e7: -162,
  e8: -191,
  e9: -240,
  e10: -320,
  e11: -430,
  e12: -630,
  e13: -920,
  e14: -1410,
  e15: -2210,
  e16: -3310,
  e17: -5310,
  e18: -8210,
  f1: -62,
  f2: -64,
  f3: -68,
  f4: -72,
  f5: -79,
  f6: -88,
  f7: -108,
  f8: -137,
  f9: -186,
  f10: -266,
  f11: -376,
  f12: -576,
  f13: -866,
  f14: -1356,
  f15: -2156,
  f16: -3256,
  f17: -5256,
  f18: -8156,
  g1: -23,
  g2: -25,
  g3: -29,
  g4: -33,
  g5: -40,
  g6: -49,
  g7: -69,
  g8: -98,
  g9: -147,
  g10: -227,
  g11: -337,
  g12: -537,
  g13: -827,
  g14: -1317,
  g15: -2117,
  g16: -3217,
  g17: -5217,
  g18: -8117,
  h1: -6,
  h2: -8,
  h3: -12,
  h4: -16,
  h5: -23,
  h6: -32,
  h7: -52,
  h8: -81,
  h9: -130,
  h10: -210,
  h11: -320,
  h12: -520,
  h13: -810,
  h14: -1300,
  h15: -2100,
  h16: -3200,
  h17: -5200,
  h18: -8100,
  js1: -3,
  js2: -4,
  js3: -6,
  js4: -8,
  js5: -11.5,
  js6: -16,
  js7: -26,
  js8: -40.5,
  js9: -65,
  js10: -105,
  js11: -160,
  js12: -260,
  js13: -405,
  js14: -650,
  js15: -1050,
  js16: -1600,
  js17: -2600,
  js18: -4050,
  j5: -16,
  j6: -16,
  j7: -26,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 20,
  m2: 20,
  m3: 20,
  m4: 20,
  m5: 20,
  m6: 20,
  m7: 20,
  m8: 20,
  m9: 20,
  m10: 20,
  m11: 20,
  m12: 20,
  m13: 20,
  m14: 20,
  m15: 20,
  m16: 20,
  m17: 20,
  m18: 20,
  n1: 34,
  n2: 34,
  n3: 34,
  n4: 34,
  n5: 34,
  n6: 34,
  n7: 34,
  n8: 34,
  n9: 34,
  n10: 34,
  n11: 34,
  n12: 34,
  n13: 34,
  n14: 34,
  n15: 34,
  n16: 34,
  n17: 34,
  n18: 34,
  p1: 56,
  p2: 56,
  p3: 56,
  p4: 56,
  p5: 56,
  p6: 56,
  p7: 56,
  p8: 56,
  p9: 56,
  p10: 56,
  p11: 56,
  p12: 56,
  p13: 56,
  p14: 56,
  p15: 56,
  p16: 56,
  p17: 56,
  p18: 56,
  r1: 98,
  r2: 98,
  r3: 98,
  r4: 98,
  r5: 98,
  r6: 98,
  r7: 98,
  r8: 98,
  r9: 98,
  r10: 98,
  r11: 98,
  r12: 98,
  r13: 98,
  r14: 98,
  r15: 98,
  r16: 98,
  r17: 98,
  r18: 98,
  s1: 170.0,
  s2: 170.0,
  s3: 170.0,
  s4: 170.0,
  s5: 170.0,
  s6: 170.0,
  s7: 170.0,
  s8: 170.0,
  s9: 170.0,
  s10: 170.0,
  s11: 170.0,
  s12: 170.0,
  s13: 170.0,
  s14: 170.0,
  s15: 170.0,
  s16: 170.0,
  s17: 170.0,
  s18: 170.0
}, {
  from: 315,
  to: 355,
  a1: -1207,
  a2: -1209,
  a3: -1213,
  a4: -1218,
  a5: -1225,
  a6: -1236,
  a7: -1257,
  a8: -1289,
  a9: -1340,
  a10: -1430,
  a11: -1560,
  a12: -1770,
  a13: -2090,
  a14: -2600,
  a15: -3500,
  a16: -4800,
  a17: -6900,
  a18: -10100,
  b1: -607,
  b2: -609,
  b3: -613,
  b4: -618,
  b5: -625,
  b6: -636,
  b7: -657,
  b8: -689,
  b9: -740,
  b10: -830,
  b11: -960,
  b12: -1170,
  b13: -1490,
  b14: -2e3,
  b15: -2900,
  b16: -4200,
  b17: -6300,
  b18: -9500,
  c1: -367,
  c2: -369,
  c3: -373,
  c4: -378,
  c5: -385,
  c6: -396,
  c7: -417,
  c8: -449,
  c9: -500,
  c10: -590,
  c11: -720,
  c12: -930,
  c13: -1250,
  c14: -1760,
  c15: -2660,
  c16: -3960,
  c17: -6060,
  c18: -9260,
  d1: -217,
  d2: -219,
  d3: -223,
  d4: -228,
  d5: -235,
  d6: -246,
  d7: -267,
  d8: -299,
  d9: -350,
  d10: -440,
  d11: -570,
  d12: -780,
  d13: -1100,
  d14: -1610,
  d15: -2510,
  d16: -3810,
  d17: -5910,
  d18: -9110,
  e1: -132,
  e2: -134,
  e3: -138,
  e4: -143,
  e5: -150,
  e6: -161,
  e7: -182,
  e8: -214,
  e9: -265,
  e10: -355,
  e11: -485,
  e12: -695,
  e13: -1015,
  e14: -1525,
  e15: -2425,
  e16: -3725,
  e17: -5825,
  e18: -9025,
  f1: -69,
  f2: -71,
  f3: -75,
  f4: -80,
  f5: -87,
  f6: -98,
  f7: -119,
  f8: -151,
  f9: -202,
  f10: -292,
  f11: -422,
  f12: -632,
  f13: -952,
  f14: -1462,
  f15: -2362,
  f16: -3662,
  f17: -5762,
  f18: -8962,
  g1: -25,
  g2: -27,
  g3: -31,
  g4: -36,
  g5: -43,
  g6: -54,
  g7: -75,
  g8: -107,
  g9: -158,
  g10: -248,
  g11: -378,
  g12: -588,
  g13: -908,
  g14: -1418,
  g15: -2318,
  g16: -3618,
  g17: -5718,
  g18: -8918,
  h1: -7,
  h2: -9,
  h3: -13,
  h4: -18,
  h5: -25,
  h6: -36,
  h7: -57,
  h8: -89,
  h9: -140,
  h10: -230,
  h11: -360,
  h12: -570,
  h13: -890,
  h14: -1400,
  h15: -2300,
  h16: -3600,
  h17: -5700,
  h18: -8900,
  js1: -3.5,
  js2: -4.5,
  js3: -6.5,
  js4: -9,
  js5: -12.5,
  js6: -18,
  js7: -28.5,
  js8: -44.5,
  js9: -70,
  js10: -115,
  js11: -180,
  js12: -285,
  js13: -445,
  js14: -700,
  js15: -1150,
  js16: -1800,
  js17: -2850,
  js18: -4450,
  j5: -18,
  j6: -18,
  j7: -28,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 21,
  m2: 21,
  m3: 21,
  m4: 21,
  m5: 21,
  m6: 21,
  m7: 21,
  m8: 21,
  m9: 21,
  m10: 21,
  m11: 21,
  m12: 21,
  m13: 21,
  m14: 21,
  m15: 21,
  m16: 21,
  m17: 21,
  m18: 21,
  n1: 37,
  n2: 37,
  n3: 37,
  n4: 37,
  n5: 37,
  n6: 37,
  n7: 37,
  n8: 37,
  n9: 37,
  n10: 37,
  n11: 37,
  n12: 37,
  n13: 37,
  n14: 37,
  n15: 37,
  n16: 37,
  n17: 37,
  n18: 37,
  p1: 62,
  p2: 62,
  p3: 62,
  p4: 62,
  p5: 62,
  p6: 62,
  p7: 62,
  p8: 62,
  p9: 62,
  p10: 62,
  p11: 62,
  p12: 62,
  p13: 62,
  p14: 62,
  p15: 62,
  p16: 62,
  p17: 62,
  p18: 62,
  r1: 108,
  r2: 108,
  r3: 108,
  r4: 108,
  r5: 108,
  r6: 108,
  r7: 108,
  r8: 108,
  r9: 108,
  r10: 108,
  r11: 108,
  r12: 108,
  r13: 108,
  r14: 108,
  r15: 108,
  r16: 108,
  r17: 108,
  r18: 108,
  s1: 190.0,
  s2: 190.0,
  s3: 190.0,
  s4: 190.0,
  s5: 190.0,
  s6: 190.0,
  s7: 190.0,
  s8: 190.0,
  s9: 190.0,
  s10: 190.0,
  s11: 190.0,
  s12: 190.0,
  s13: 190.0,
  s14: 190.0,
  s15: 190.0,
  s16: 190.0,
  s17: 190.0,
  s18: 190.0
}, {
  from: 355,
  to: 400,
  a1: -1357,
  a2: -1359,
  a3: -1363,
  a4: -1368,
  a5: -1375,
  a6: -1386,
  a7: -1407,
  a8: -1439,
  a9: -1490,
  a10: -1580,
  a11: -1710,
  a12: -1920,
  a13: -2240,
  a14: -2750,
  a15: -3650,
  a16: -4950,
  a17: -7050,
  a18: -10250,
  b1: -687,
  b2: -689,
  b3: -693,
  b4: -698,
  b5: -705,
  b6: -716,
  b7: -737,
  b8: -769,
  b9: -820,
  b10: -910,
  b11: -1040,
  b12: -1250,
  b13: -1570,
  b14: -2080,
  b15: -2980,
  b16: -4280,
  b17: -6380,
  b18: -9580,
  c1: -407,
  c2: -409,
  c3: -413,
  c4: -418,
  c5: -425,
  c6: -436,
  c7: -457,
  c8: -489,
  c9: -540,
  c10: -630,
  c11: -760,
  c12: -970,
  c13: -1290,
  c14: -1800,
  c15: -2700,
  c16: -4e3,
  c17: -6100,
  c18: -9300,
  d1: -217,
  d2: -219,
  d3: -223,
  d4: -228,
  d5: -235,
  d6: -246,
  d7: -267,
  d8: -299,
  d9: -350,
  d10: -440,
  d11: -570,
  d12: -780,
  d13: -1100,
  d14: -1610,
  d15: -2510,
  d16: -3810,
  d17: -5910,
  d18: -9110,
  e1: -132,
  e2: -134,
  e3: -138,
  e4: -143,
  e5: -150,
  e6: -161,
  e7: -182,
  e8: -214,
  e9: -265,
  e10: -355,
  e11: -485,
  e12: -695,
  e13: -1015,
  e14: -1525,
  e15: -2425,
  e16: -3725,
  e17: -5825,
  e18: -9025,
  f1: -69,
  f2: -71,
  f3: -75,
  f4: -80,
  f5: -87,
  f6: -98,
  f7: -119,
  f8: -151,
  f9: -202,
  f10: -292,
  f11: -422,
  f12: -632,
  f13: -952,
  f14: -1462,
  f15: -2362,
  f16: -3662,
  f17: -5762,
  f18: -8962,
  g1: -25,
  g2: -27,
  g3: -31,
  g4: -36,
  g5: -43,
  g6: -54,
  g7: -75,
  g8: -107,
  g9: -158,
  g10: -248,
  g11: -378,
  g12: -588,
  g13: -908,
  g14: -1418,
  g15: -2318,
  g16: -3618,
  g17: -5718,
  g18: -8918,
  h1: -7,
  h2: -9,
  h3: -13,
  h4: -18,
  h5: -25,
  h6: -36,
  h7: -57,
  h8: -89,
  h9: -140,
  h10: -230,
  h11: -360,
  h12: -570,
  h13: -890,
  h14: -1400,
  h15: -2300,
  h16: -3600,
  h17: -5700,
  h18: -8900,
  js1: -3.5,
  js2: -4.5,
  js3: -6.5,
  js4: -9,
  js5: -12.5,
  js6: -18,
  js7: -28.5,
  js8: -44.5,
  js9: -70,
  js10: -115,
  js11: -180,
  js12: -285,
  js13: -445,
  js14: -700,
  js15: -1150,
  js16: -1800,
  js17: -2850,
  js18: -4450,
  j5: -18,
  j6: -18,
  j7: -28,
  k4: 4,
  k5: 4,
  k6: 4,
  k7: 4,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 21,
  m2: 21,
  m3: 21,
  m4: 21,
  m5: 21,
  m6: 21,
  m7: 21,
  m8: 21,
  m9: 21,
  m10: 21,
  m11: 21,
  m12: 21,
  m13: 21,
  m14: 21,
  m15: 21,
  m16: 21,
  m17: 21,
  m18: 21,
  n1: 37,
  n2: 37,
  n3: 37,
  n4: 37,
  n5: 37,
  n6: 37,
  n7: 37,
  n8: 37,
  n9: 37,
  n10: 37,
  n11: 37,
  n12: 37,
  n13: 37,
  n14: 37,
  n15: 37,
  n16: 37,
  n17: 37,
  n18: 37,
  p1: 62,
  p2: 62,
  p3: 62,
  p4: 62,
  p5: 62,
  p6: 62,
  p7: 62,
  p8: 62,
  p9: 62,
  p10: 62,
  p11: 62,
  p12: 62,
  p13: 62,
  p14: 62,
  p15: 62,
  p16: 62,
  p17: 62,
  p18: 62,
  r1: 114,
  r2: 114,
  r3: 114,
  r4: 114,
  r5: 114,
  r6: 114,
  r7: 114,
  r8: 114,
  r9: 114,
  r10: 114,
  r11: 114,
  r12: 114,
  r13: 114,
  r14: 114,
  r15: 114,
  r16: 114,
  r17: 114,
  r18: 114,
  s1: 208.0,
  s2: 208.0,
  s3: 208.0,
  s4: 208.0,
  s5: 208.0,
  s6: 208.0,
  s7: 208.0,
  s8: 208.0,
  s9: 208.0,
  s10: 208.0,
  s11: 208.0,
  s12: 208.0,
  s13: 208.0,
  s14: 208.0,
  s15: 208.0,
  s16: 208.0,
  s17: 208.0,
  s18: 208.0
}, {
  from: 400,
  to: 450,
  a1: -1508,
  a2: -1510,
  a3: -1515,
  a4: -1520,
  a5: -1527,
  a6: -1540,
  a7: -1563,
  a8: -1597,
  a9: -1655,
  a10: -1750,
  a11: -1900,
  a12: -2130,
  a13: -2470,
  a14: -3050,
  a15: -4e3,
  a16: -5500,
  a17: -7800,
  a18: -11200,
  b1: -768,
  b2: -770,
  b3: -775,
  b4: -780,
  b5: -787,
  b6: -800,
  b7: -823,
  b8: -857,
  b9: -915,
  b10: -1010,
  b11: -1160,
  b12: -1390,
  b13: -1730,
  b14: -2310,
  b15: -3260,
  b16: -4760,
  b17: -7060,
  b18: -10460,
  c1: -448,
  c2: -450,
  c3: -455,
  c4: -460,
  c5: -467,
  c6: -480,
  c7: -503,
  c8: -537,
  c9: -595,
  c10: -690,
  c11: -840,
  c12: -1070,
  c13: -1410,
  c14: -1990,
  c15: -2940,
  c16: -4440,
  c17: -6740,
  c18: -10140,
  d1: -238,
  d2: -240,
  d3: -245,
  d4: -250,
  d5: -257,
  d6: -270,
  d7: -293,
  d8: -327,
  d9: -385,
  d10: -480,
  d11: -630,
  d12: -860,
  d13: -1200,
  d14: -1780,
  d15: -2730,
  d16: -4230,
  d17: -6530,
  d18: -9930,
  e1: -143,
  e2: -145,
  e3: -150,
  e4: -155,
  e5: -162,
  e6: -175,
  e7: -198,
  e8: -232,
  e9: -290,
  e10: -385,
  e11: -535,
  e12: -765,
  e13: -1105,
  e14: -1685,
  e15: -2635,
  e16: -4135,
  e17: -6435,
  e18: -9835,
  f1: -76,
  f2: -78,
  f3: -83,
  f4: -88,
  f5: -95,
  f6: -108,
  f7: -131,
  f8: -165,
  f9: -223,
  f10: -318,
  f11: -468,
  f12: -698,
  f13: -1038,
  f14: -1618,
  f15: -2568,
  f16: -4068,
  f17: -6368,
  f18: -9768,
  g1: -28,
  g2: -30,
  g3: -35,
  g4: -40,
  g5: -47,
  g6: -60,
  g7: -83,
  g8: -117,
  g9: -175,
  g10: -270,
  g11: -420,
  g12: -650,
  g13: -990,
  g14: -1570,
  g15: -2520,
  g16: -4020,
  g17: -6320,
  g18: -9720,
  h1: -8,
  h2: -10,
  h3: -15,
  h4: -20,
  h5: -27,
  h6: -40,
  h7: -63,
  h8: -97,
  h9: -155,
  h10: -250,
  h11: -400,
  h12: -630,
  h13: -970,
  h14: -1550,
  h15: -2500,
  h16: -4e3,
  h17: -6300,
  h18: -9700,
  js1: -4,
  js2: -5,
  js3: -7.5,
  js4: -10,
  js5: -13.5,
  js6: -20,
  js7: -31.5,
  js8: -48.5,
  js9: -77.5,
  js10: -125,
  js11: -200,
  js12: -315,
  js13: -485,
  js14: -775,
  js15: -1250,
  js16: -2e3,
  js17: -3150,
  js18: -4850,
  j5: -20,
  j6: -20,
  j7: -32,
  k4: 5,
  k5: 5,
  k6: 5,
  k7: 5,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 23,
  m2: 23,
  m3: 23,
  m4: 23,
  m5: 23,
  m6: 23,
  m7: 23,
  m8: 23,
  m9: 23,
  m10: 23,
  m11: 23,
  m12: 23,
  m13: 23,
  m14: 23,
  m15: 23,
  m16: 23,
  m17: 23,
  m18: 23,
  n1: 40,
  n2: 40,
  n3: 40,
  n4: 40,
  n5: 40,
  n6: 40,
  n7: 40,
  n8: 40,
  n9: 40,
  n10: 40,
  n11: 40,
  n12: 40,
  n13: 40,
  n14: 40,
  n15: 40,
  n16: 40,
  n17: 40,
  n18: 40,
  p1: 68,
  p2: 68,
  p3: 68,
  p4: 68,
  p5: 68,
  p6: 68,
  p7: 68,
  p8: 68,
  p9: 68,
  p10: 68,
  p11: 68,
  p12: 68,
  p13: 68,
  p14: 68,
  p15: 68,
  p16: 68,
  p17: 68,
  p18: 68,
  r1: 126,
  r2: 126,
  r3: 126,
  r4: 126,
  r5: 126,
  r6: 126,
  r7: 126,
  r8: 126,
  r9: 126,
  r10: 126,
  r11: 126,
  r12: 126,
  r13: 126,
  r14: 126,
  r15: 126,
  r16: 126,
  r17: 126,
  r18: 126,
  s1: 232.0,
  s2: 232.0,
  s3: 232.0,
  s4: 232.0,
  s5: 232.0,
  s6: 232.0,
  s7: 232.0,
  s8: 232.0,
  s9: 232.0,
  s10: 232.0,
  s11: 232.0,
  s12: 232.0,
  s13: 232.0,
  s14: 232.0,
  s15: 232.0,
  s16: 232.0,
  s17: 232.0,
  s18: 232.0
}, {
  from: 450,
  to: 500,
  a1: -1658,
  a2: -1660,
  a3: -1665,
  a4: -1670,
  a5: -1677,
  a6: -1690,
  a7: -1713,
  a8: -1747,
  a9: -1805,
  a10: -1900,
  a11: -2050,
  a12: -2280,
  a13: -2620,
  a14: -3200,
  a15: -4150,
  a16: -5650,
  a17: -7950,
  a18: -11350,
  b1: -848,
  b2: -850,
  b3: -855,
  b4: -860,
  b5: -867,
  b6: -880,
  b7: -903,
  b8: -937,
  b9: -995,
  b10: -1090,
  b11: -1240,
  b12: -1470,
  b13: -1810,
  b14: -2390,
  b15: -3340,
  b16: -4840,
  b17: -7140,
  b18: -10540,
  c1: -488,
  c2: -490,
  c3: -495,
  c4: -500,
  c5: -507,
  c6: -520,
  c7: -543,
  c8: -577,
  c9: -635,
  c10: -730,
  c11: -880,
  c12: -1110,
  c13: -1450,
  c14: -2030,
  c15: -2980,
  c16: -4480,
  c17: -6780,
  c18: -10180,
  d1: -238,
  d2: -240,
  d3: -245,
  d4: -250,
  d5: -257,
  d6: -270,
  d7: -293,
  d8: -327,
  d9: -385,
  d10: -480,
  d11: -630,
  d12: -860,
  d13: -1200,
  d14: -1780,
  d15: -2730,
  d16: -4230,
  d17: -6530,
  d18: -9930,
  e1: -143,
  e2: -145,
  e3: -150,
  e4: -155,
  e5: -162,
  e6: -175,
  e7: -198,
  e8: -232,
  e9: -290,
  e10: -385,
  e11: -535,
  e12: -765,
  e13: -1105,
  e14: -1685,
  e15: -2635,
  e16: -4135,
  e17: -6435,
  e18: -9835,
  f1: -76,
  f2: -78,
  f3: -83,
  f4: -88,
  f5: -95,
  f6: -108,
  f7: -131,
  f8: -165,
  f9: -223,
  f10: -318,
  f11: -468,
  f12: -698,
  f13: -1038,
  f14: -1618,
  f15: -2568,
  f16: -4068,
  f17: -6368,
  f18: -9768,
  g1: -28,
  g2: -30,
  g3: -35,
  g4: -40,
  g5: -47,
  g6: -60,
  g7: -83,
  g8: -117,
  g9: -175,
  g10: -270,
  g11: -420,
  g12: -650,
  g13: -990,
  g14: -1570,
  g15: -2520,
  g16: -4020,
  g17: -6320,
  g18: -9720,
  h1: -8,
  h2: -10,
  h3: -15,
  h4: -20,
  h5: -27,
  h6: -40,
  h7: -63,
  h8: -97,
  h9: -155,
  h10: -250,
  h11: -400,
  h12: -630,
  h13: -970,
  h14: -1550,
  h15: -2500,
  h16: -4e3,
  h17: -6300,
  h18: -9700,
  js1: -4,
  js2: -5,
  js3: -7.5,
  js4: -10,
  js5: -13.5,
  js6: -20,
  js7: -31.5,
  js8: -48.5,
  js9: -77.5,
  js10: -125,
  js11: -200,
  js12: -315,
  js13: -485,
  js14: -775,
  js15: -1250,
  js16: -2e3,
  js17: -3150,
  js18: -4850,
  j5: -20,
  j6: -20,
  j7: -32,
  k4: 5,
  k5: 5,
  k6: 5,
  k7: 5,
  k8: 0,
  k9: 0,
  k10: 0,
  k11: 0,
  k12: 0,
  k13: 0,
  k14: 0,
  k15: 0,
  k16: 0,
  k17: 0,
  k18: 0,
  m1: 23,
  m2: 23,
  m3: 23,
  m4: 23,
  m5: 23,
  m6: 23,
  m7: 23,
  m8: 23,
  m9: 23,
  m10: 23,
  m11: 23,
  m12: 23,
  m13: 23,
  m14: 23,
  m15: 23,
  m16: 23,
  m17: 23,
  m18: 23,
  n1: 40,
  n2: 40,
  n3: 40,
  n4: 40,
  n5: 40,
  n6: 40,
  n7: 40,
  n8: 40,
  n9: 40,
  n10: 40,
  n11: 40,
  n12: 40,
  n13: 40,
  n14: 40,
  n15: 40,
  n16: 40,
  n17: 40,
  n18: 40,
  p1: 68,
  p2: 68,
  p3: 68,
  p4: 68,
  p5: 68,
  p6: 68,
  p7: 68,
  p8: 68,
  p9: 68,
  p10: 68,
  p11: 68,
  p12: 68,
  p13: 68,
  p14: 68,
  p15: 68,
  p16: 68,
  p17: 68,
  p18: 68,
  r1: 132,
  r2: 132,
  r3: 132,
  r4: 132,
  r5: 132,
  r6: 132,
  r7: 132,
  r8: 132,
  r9: 132,
  r10: 132,
  r11: 132,
  r12: 132,
  r13: 132,
  r14: 132,
  r15: 132,
  r16: 132,
  r17: 132,
  r18: 132,
  s1: 252.0,
  s2: 252.0,
  s3: 252.0,
  s4: 252.0,
  s5: 252.0,
  s6: 252.0,
  s7: 252.0,
  s8: 252.0,
  s9: 252.0,
  s10: 252.0,
  s11: 252.0,
  s12: 252.0,
  s13: 252.0,
  s14: 252.0,
  s15: 252.0,
  s16: 252.0,
  s17: 252.0,
  s18: 252.0
}];

const shaftUpperValues = [{
  from: 0,
  to: 3,
  a1: -270,
  a2: -270,
  a3: -270,
  a4: -270,
  a5: -270,
  a6: -270,
  a7: -270,
  a8: -270,
  a9: -270,
  a10: -270,
  a11: -270,
  a12: -270,
  a13: -270,
  a14: -270,
  a15: -270,
  a16: -270,
  a17: -270,
  a18: -270,
  b1: -140,
  b2: -140,
  b3: -140,
  b4: -140,
  b5: -140,
  b6: -140,
  b7: -140,
  b8: -140,
  b9: -140,
  b10: -140,
  b11: -140,
  b12: -140,
  b13: -140,
  b14: -140,
  b15: -140,
  b16: -140,
  b17: -140,
  b18: -140,
  c1: -60,
  c2: -60,
  c3: -60,
  c4: -60,
  c5: -60,
  c6: -60,
  c7: -60,
  c8: -60,
  c9: -60,
  c10: -60,
  c11: -60,
  c12: -60,
  c13: -60,
  c14: -60,
  c15: -60,
  c16: -60,
  c17: -60,
  c18: -60,
  d1: -20,
  d2: -20,
  d3: -20,
  d4: -20,
  d5: -20,
  d6: -20,
  d7: -20,
  d8: -20,
  d9: -20,
  d10: -20,
  d11: -20,
  d12: -20,
  d13: -20,
  d14: -20,
  d15: -20,
  d16: -20,
  d17: -20,
  d18: -20,
  e1: -14,
  e2: -14,
  e3: -14,
  e4: -14,
  e5: -14,
  e6: -14,
  e7: -14,
  e8: -14,
  e9: -14,
  e10: -14,
  e11: -14,
  e12: -14,
  e13: -14,
  e14: -14,
  e15: -14,
  e16: -14,
  e17: -14,
  e18: -14,
  f1: -6,
  f2: -6,
  f3: -6,
  f4: -6,
  f5: -6,
  f6: -6,
  f7: -6,
  f8: -6,
  f9: -6,
  f10: -6,
  f11: -6,
  f12: -6,
  f13: -6,
  f14: -6,
  f15: -6,
  f16: -6,
  f17: -6,
  f18: -6,
  g1: -2,
  g2: -2,
  g3: -2,
  g4: -2,
  g5: -2,
  g6: -2,
  g7: -2,
  g8: -2,
  g9: -2,
  g10: -2,
  g11: -2,
  g12: -2,
  g13: -2,
  g14: -2,
  g15: -2,
  g16: -2,
  g17: -2,
  g18: -2,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.4,
  js2: 0.6,
  js3: 1.0,
  js4: 1.5,
  js5: 2.0,
  js6: 3.0,
  js7: 5.0,
  js8: 7.0,
  js9: 12.5,
  js10: 20.0,
  js11: 30.0,
  js12: 50.0,
  js13: 70.0,
  js14: 125.0,
  js15: 200.0,
  js16: 300.0,
  js17: 500.0,
  js18: 700.0,
  j5: 2.0,
  j6: 4.0,
  j7: 6.0,
  k4: 3.0,
  k5: 4.0,
  k6: 6.0,
  k7: 10.0,
  k8: 14.0,
  k9: 25.0,
  k10: 40.0,
  k11: 60.0,
  k12: 100.0,
  k13: 140.0,
  k14: 250.0,
  k15: 400.0,
  k16: 600.0,
  k17: 1000.0,
  k18: 1400.0,
  m1: 2.8,
  m2: 3.2,
  m3: 4.0,
  m4: 5.0,
  m5: 6.0,
  m6: 8.0,
  m7: 12.0,
  m8: 16.0,
  m9: 27.0,
  m10: 42.0,
  m11: 62.0,
  m12: 102.0,
  m13: 142.0,
  m14: 252.0,
  m15: 402.0,
  m16: 602.0,
  m17: 1002.0,
  m18: 1402.0,
  n1: 4.8,
  n2: 5.2,
  n3: 6.0,
  n4: 7.0,
  n5: 8.0,
  n6: 10.0,
  n7: 14.0,
  n8: 18.0,
  n9: 29.0,
  n10: 44.0,
  n11: 64.0,
  n12: 104.0,
  n13: 144.0,
  n14: 254.0,
  n15: 404.0,
  n16: 604.0,
  n17: 1004.0,
  n18: 1404.0,
  p1: 6.8,
  p2: 7.2,
  p3: 8.0,
  p4: 9.0,
  p5: 10.0,
  p6: 12.0,
  p7: 16.0,
  p8: 20.0,
  p9: 31.0,
  p10: 46.0,
  p11: 66.0,
  p12: 106.0,
  p13: 146.0,
  p14: 256.0,
  p15: 406.0,
  p16: 606.0,
  p17: 1006.0,
  p18: 1406.0,
  r1: 10.8,
  r2: 11.2,
  r3: 12.0,
  r4: 13.0,
  r5: 14.0,
  r6: 16.0,
  r7: 20.0,
  r8: 24.0,
  r9: 35.0,
  r10: 50.0,
  r11: 70.0,
  r12: 110.0,
  r13: 150.0,
  r14: 260.0,
  r15: 410.0,
  r16: 610.0,
  r17: 1010.0,
  r18: 1410.0,
  s1: 14.8,
  s2: 15.2,
  s3: 16.0,
  s4: 17.0,
  s5: 18.0,
  s6: 20.0,
  s7: 24.0,
  s8: 28.0,
  s9: 39.0,
  s10: 54.0,
  s11: 74.0,
  s12: 114.0,
  s13: 154.0,
  s14: 264.0,
  s15: 414.0,
  s16: 614.0,
  s17: 1014.0,
  s18: 1414.0
}, {
  from: 3,
  to: 6,
  a1: -270,
  a2: -270,
  a3: -270,
  a4: -270,
  a5: -270,
  a6: -270,
  a7: -270,
  a8: -270,
  a9: -270,
  a10: -270,
  a11: -270,
  a12: -270,
  a13: -270,
  a14: -270,
  a15: -270,
  a16: -270,
  a17: -270,
  a18: -270,
  b1: -140,
  b2: -140,
  b3: -140,
  b4: -140,
  b5: -140,
  b6: -140,
  b7: -140,
  b8: -140,
  b9: -140,
  b10: -140,
  b11: -140,
  b12: -140,
  b13: -140,
  b14: -140,
  b15: -140,
  b16: -140,
  b17: -140,
  b18: -140,
  c1: -70,
  c2: -70,
  c3: -70,
  c4: -70,
  c5: -70,
  c6: -70,
  c7: -70,
  c8: -70,
  c9: -70,
  c10: -70,
  c11: -70,
  c12: -70,
  c13: -70,
  c14: -70,
  c15: -70,
  c16: -70,
  c17: -70,
  c18: -70,
  d1: -30,
  d2: -30,
  d3: -30,
  d4: -30,
  d5: -30,
  d6: -30,
  d7: -30,
  d8: -30,
  d9: -30,
  d10: -30,
  d11: -30,
  d12: -30,
  d13: -30,
  d14: -30,
  d15: -30,
  d16: -30,
  d17: -30,
  d18: -30,
  e1: -20,
  e2: -20,
  e3: -20,
  e4: -20,
  e5: -20,
  e6: -20,
  e7: -20,
  e8: -20,
  e9: -20,
  e10: -20,
  e11: -20,
  e12: -20,
  e13: -20,
  e14: -20,
  e15: -20,
  e16: -20,
  e17: -20,
  e18: -20,
  f1: -10,
  f2: -10,
  f3: -10,
  f4: -10,
  f5: -10,
  f6: -10,
  f7: -10,
  f8: -10,
  f9: -10,
  f10: -10,
  f11: -10,
  f12: -10,
  f13: -10,
  f14: -10,
  f15: -10,
  f16: -10,
  f17: -10,
  f18: -10,
  g1: -4,
  g2: -4,
  g3: -4,
  g4: -4,
  g5: -4,
  g6: -4,
  g7: -4,
  g8: -4,
  g9: -4,
  g10: -4,
  g11: -4,
  g12: -4,
  g13: -4,
  g14: -4,
  g15: -4,
  g16: -4,
  g17: -4,
  g18: -4,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.5,
  js2: 0.8,
  js3: 1.2,
  js4: 2.0,
  js5: 2.5,
  js6: 4.0,
  js7: 6.0,
  js8: 9.0,
  js9: 15.0,
  js10: 24.0,
  js11: 37.5,
  js12: 60.0,
  js13: 90.0,
  js14: 150.0,
  js15: 240.0,
  js16: 375.0,
  js17: 600.0,
  js18: 900.0,
  j5: 3.0,
  j6: 6.0,
  j7: 8.0,
  k4: 5.0,
  k5: 6.0,
  k6: 9.0,
  k7: 13.0,
  k8: 18.0,
  k9: 30.0,
  k10: 48.0,
  k11: 75.0,
  k12: 120.0,
  k13: 180.0,
  k14: 300.0,
  k15: 480.0,
  k16: 750.0,
  k17: 1200.0,
  k18: 1800.0,
  m1: 5.0,
  m2: 5.5,
  m3: 6.5,
  m4: 8.0,
  m5: 9.0,
  m6: 12.0,
  m7: 16.0,
  m8: 22.0,
  m9: 34.0,
  m10: 52.0,
  m11: 79.0,
  m12: 124.0,
  m13: 184.0,
  m14: 304.0,
  m15: 484.0,
  m16: 754.0,
  m17: 1204.0,
  m18: 1804.0,
  n1: 9.0,
  n2: 9.5,
  n3: 10.5,
  n4: 12.0,
  n5: 13.0,
  n6: 16.0,
  n7: 20.0,
  n8: 26.0,
  n9: 38.0,
  n10: 56.0,
  n11: 83.0,
  n12: 128.0,
  n13: 188.0,
  n14: 308.0,
  n15: 488.0,
  n16: 758.0,
  n17: 1208.0,
  n18: 1808.0,
  p1: 13.0,
  p2: 13.5,
  p3: 14.5,
  p4: 16.0,
  p5: 17.0,
  p6: 20.0,
  p7: 24.0,
  p8: 30.0,
  p9: 42.0,
  p10: 60.0,
  p11: 87.0,
  p12: 132.0,
  p13: 192.0,
  p14: 312.0,
  p15: 492.0,
  p16: 762.0,
  p17: 1212.0,
  p18: 1812.0,
  r1: 16.0,
  r2: 16.5,
  r3: 17.5,
  r4: 19.0,
  r5: 20.0,
  r6: 23.0,
  r7: 27.0,
  r8: 33.0,
  r9: 45.0,
  r10: 63.0,
  r11: 90.0,
  r12: 135.0,
  r13: 195.0,
  r14: 315.0,
  r15: 495.0,
  r16: 765.0,
  r17: 1215.0,
  r18: 1815.0,
  s1: 20.0,
  s2: 20.5,
  s3: 21.5,
  s4: 23.0,
  s5: 24.0,
  s6: 27.0,
  s7: 31.0,
  s8: 37.0,
  s9: 49.0,
  s10: 67.0,
  s11: 94.0,
  s12: 139.0,
  s13: 199.0,
  s14: 319.0,
  s15: 499.0,
  s16: 769.0,
  s17: 1219.0,
  s18: 1819.0
}, {
  from: 6,
  to: 10,
  a1: -280,
  a2: -280,
  a3: -280,
  a4: -280,
  a5: -280,
  a6: -280,
  a7: -280,
  a8: -280,
  a9: -280,
  a10: -280,
  a11: -280,
  a12: -280,
  a13: -280,
  a14: -280,
  a15: -280,
  a16: -280,
  a17: -280,
  a18: -280,
  b1: -150,
  b2: -150,
  b3: -150,
  b4: -150,
  b5: -150,
  b6: -150,
  b7: -150,
  b8: -150,
  b9: -150,
  b10: -150,
  b11: -150,
  b12: -150,
  b13: -150,
  b14: -150,
  b15: -150,
  b16: -150,
  b17: -150,
  b18: -150,
  c1: -80,
  c2: -80,
  c3: -80,
  c4: -80,
  c5: -80,
  c6: -80,
  c7: -80,
  c8: -80,
  c9: -80,
  c10: -80,
  c11: -80,
  c12: -80,
  c13: -80,
  c14: -80,
  c15: -80,
  c16: -80,
  c17: -80,
  c18: -80,
  d1: -40,
  d2: -40,
  d3: -40,
  d4: -40,
  d5: -40,
  d6: -40,
  d7: -40,
  d8: -40,
  d9: -40,
  d10: -40,
  d11: -40,
  d12: -40,
  d13: -40,
  d14: -40,
  d15: -40,
  d16: -40,
  d17: -40,
  d18: -40,
  e1: -25,
  e2: -25,
  e3: -25,
  e4: -25,
  e5: -25,
  e6: -25,
  e7: -25,
  e8: -25,
  e9: -25,
  e10: -25,
  e11: -25,
  e12: -25,
  e13: -25,
  e14: -25,
  e15: -25,
  e16: -25,
  e17: -25,
  e18: -25,
  f1: -13,
  f2: -13,
  f3: -13,
  f4: -13,
  f5: -13,
  f6: -13,
  f7: -13,
  f8: -13,
  f9: -13,
  f10: -13,
  f11: -13,
  f12: -13,
  f13: -13,
  f14: -13,
  f15: -13,
  f16: -13,
  f17: -13,
  f18: -13,
  g1: -5,
  g2: -5,
  g3: -5,
  g4: -5,
  g5: -5,
  g6: -5,
  g7: -5,
  g8: -5,
  g9: -5,
  g10: -5,
  g11: -5,
  g12: -5,
  g13: -5,
  g14: -5,
  g15: -5,
  g16: -5,
  g17: -5,
  g18: -5,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.5,
  js2: 0.8,
  js3: 1.3,
  js4: 2.0,
  js5: 3.0,
  js6: 4.5,
  js7: 7.5,
  js8: 11.0,
  js9: 18.0,
  js10: 29.0,
  js11: 45.0,
  js12: 75.0,
  js13: 110.0,
  js14: 180.0,
  js15: 290.0,
  js16: 450.0,
  js17: 750.0,
  js18: 1100.0,
  j5: 4.0,
  j6: 7.0,
  j7: 10.0,
  k4: 5.0,
  k5: 7.0,
  k6: 10.0,
  k7: 16.0,
  k8: 22.0,
  k9: 36.0,
  k10: 58.0,
  k11: 90.0,
  k12: 150.0,
  k13: 220.0,
  k14: 360.0,
  k15: 580.0,
  k16: 900.0,
  k17: 1500.0,
  k18: 2200.0,
  m1: 7.0,
  m2: 7.5,
  m3: 8.5,
  m4: 10.0,
  m5: 12.0,
  m6: 15.0,
  m7: 21.0,
  m8: 28.0,
  m9: 42.0,
  m10: 64.0,
  m11: 96.0,
  m12: 156.0,
  m13: 226.0,
  m14: 366.0,
  m15: 586.0,
  m16: 906.0,
  m17: 1506.0,
  m18: 2206.0,
  n1: 11.0,
  n2: 11.5,
  n3: 12.5,
  n4: 14.0,
  n5: 16.0,
  n6: 19.0,
  n7: 25.0,
  n8: 32.0,
  n9: 46.0,
  n10: 68.0,
  n11: 100.0,
  n12: 160.0,
  n13: 230.0,
  n14: 370.0,
  n15: 590.0,
  n16: 910.0,
  n17: 1510.0,
  n18: 2210.0,
  p1: 16.0,
  p2: 16.5,
  p3: 17.5,
  p4: 19.0,
  p5: 21.0,
  p6: 24.0,
  p7: 30.0,
  p8: 37.0,
  p9: 51.0,
  p10: 73.0,
  p11: 105.0,
  p12: 165.0,
  p13: 235.0,
  p14: 375.0,
  p15: 595.0,
  p16: 915.0,
  p17: 1515.0,
  p18: 2215.0,
  r1: 20.0,
  r2: 20.5,
  r3: 21.5,
  r4: 23.0,
  r5: 25.0,
  r6: 28.0,
  r7: 34.0,
  r8: 41.0,
  r9: 55.0,
  r10: 77.0,
  r11: 109.0,
  r12: 169.0,
  r13: 239.0,
  r14: 379.0,
  r15: 599.0,
  r16: 919.0,
  r17: 1519.0,
  r18: 2219.0,
  s1: 24.0,
  s2: 24.5,
  s3: 25.5,
  s4: 27.0,
  s5: 29.0,
  s6: 32.0,
  s7: 38.0,
  s8: 45.0,
  s9: 59.0,
  s10: 81.0,
  s11: 113.0,
  s12: 173.0,
  s13: 243.0,
  s14: 383.0,
  s15: 603.0,
  s16: 923.0,
  s17: 1523.0,
  s18: 2223.0
}, {
  from: 10,
  to: 18,
  a1: -290,
  a2: -290,
  a3: -290,
  a4: -290,
  a5: -290,
  a6: -290,
  a7: -290,
  a8: -290,
  a9: -290,
  a10: -290,
  a11: -290,
  a12: -290,
  a13: -290,
  a14: -290,
  a15: -290,
  a16: -290,
  a17: -290,
  a18: -290,
  b1: -150,
  b2: -150,
  b3: -150,
  b4: -150,
  b5: -150,
  b6: -150,
  b7: -150,
  b8: -150,
  b9: -150,
  b10: -150,
  b11: -150,
  b12: -150,
  b13: -150,
  b14: -150,
  b15: -150,
  b16: -150,
  b17: -150,
  b18: -150,
  c1: -95,
  c2: -95,
  c3: -95,
  c4: -95,
  c5: -95,
  c6: -95,
  c7: -95,
  c8: -95,
  c9: -95,
  c10: -95,
  c11: -95,
  c12: -95,
  c13: -95,
  c14: -95,
  c15: -95,
  c16: -95,
  c17: -95,
  c18: -95,
  d1: -50,
  d2: -50,
  d3: -50,
  d4: -50,
  d5: -50,
  d6: -50,
  d7: -50,
  d8: -50,
  d9: -50,
  d10: -50,
  d11: -50,
  d12: -50,
  d13: -50,
  d14: -50,
  d15: -50,
  d16: -50,
  d17: -50,
  d18: -50,
  e1: -32,
  e2: -32,
  e3: -32,
  e4: -32,
  e5: -32,
  e6: -32,
  e7: -32,
  e8: -32,
  e9: -32,
  e10: -32,
  e11: -32,
  e12: -32,
  e13: -32,
  e14: -32,
  e15: -32,
  e16: -32,
  e17: -32,
  e18: -32,
  f1: -16,
  f2: -16,
  f3: -16,
  f4: -16,
  f5: -16,
  f6: -16,
  f7: -16,
  f8: -16,
  f9: -16,
  f10: -16,
  f11: -16,
  f12: -16,
  f13: -16,
  f14: -16,
  f15: -16,
  f16: -16,
  f17: -16,
  f18: -16,
  g1: -6,
  g2: -6,
  g3: -6,
  g4: -6,
  g5: -6,
  g6: -6,
  g7: -6,
  g8: -6,
  g9: -6,
  g10: -6,
  g11: -6,
  g12: -6,
  g13: -6,
  g14: -6,
  g15: -6,
  g16: -6,
  g17: -6,
  g18: -6,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.6,
  js2: 1.0,
  js3: 1.5,
  js4: 2.5,
  js5: 4.0,
  js6: 5.5,
  js7: 9.0,
  js8: 13.5,
  js9: 21.5,
  js10: 35.0,
  js11: 55.0,
  js12: 90.0,
  js13: 135.0,
  js14: 215.0,
  js15: 350.0,
  js16: 550.0,
  js17: 900.0,
  js18: 1350.0,
  j5: 5.0,
  j6: 8.0,
  j7: 12.0,
  k4: 6.0,
  k5: 9.0,
  k6: 12.0,
  k7: 19.0,
  k8: 27.0,
  k9: 43.0,
  k10: 70.0,
  k11: 110.0,
  k12: 180.0,
  k13: 270.0,
  k14: 430.0,
  k15: 700.0,
  k16: 1100.0,
  k17: 1800.0,
  k18: 2700.0,
  m1: 8.2,
  m2: 9.0,
  m3: 10.0,
  m4: 12.0,
  m5: 15.0,
  m6: 18.0,
  m7: 25.0,
  m8: 34.0,
  m9: 50.0,
  m10: 77.0,
  m11: 117.0,
  m12: 187.0,
  m13: 277.0,
  m14: 437.0,
  m15: 707.0,
  m16: 1107.0,
  m17: 1807.0,
  m18: 2707.0,
  n1: 13.2,
  n2: 14.0,
  n3: 15.0,
  n4: 17.0,
  n5: 20.0,
  n6: 23.0,
  n7: 30.0,
  n8: 39.0,
  n9: 55.0,
  n10: 82.0,
  n11: 122.0,
  n12: 192.0,
  n13: 282.0,
  n14: 442.0,
  n15: 712.0,
  n16: 1112.0,
  n17: 1812.0,
  n18: 2712.0,
  p1: 19.2,
  p2: 20.0,
  p3: 21.0,
  p4: 23.0,
  p5: 26.0,
  p6: 29.0,
  p7: 36.0,
  p8: 45.0,
  p9: 61.0,
  p10: 88.0,
  p11: 128.0,
  p12: 198.0,
  p13: 288.0,
  p14: 448.0,
  p15: 718.0,
  p16: 1118.0,
  p17: 1818.0,
  p18: 2718.0,
  r1: 24.2,
  r2: 25.0,
  r3: 26.0,
  r4: 28.0,
  r5: 31.0,
  r6: 34.0,
  r7: 41.0,
  r8: 50.0,
  r9: 66.0,
  r10: 93.0,
  r11: 133.0,
  r12: 203.0,
  r13: 293.0,
  r14: 453.0,
  r15: 723.0,
  r16: 1123.0,
  r17: 1823.0,
  r18: 2723.0,
  s1: 29.2,
  s2: 30.0,
  s3: 31.0,
  s4: 33.0,
  s5: 36.0,
  s6: 39.0,
  s7: 46.0,
  s8: 55.0,
  s9: 71.0,
  s10: 98.0,
  s11: 138.0,
  s12: 208.0,
  s13: 298.0,
  s14: 458.0,
  s15: 728.0,
  s16: 1128.0,
  s17: 1828.0,
  s18: 2728.0
}, {
  from: 18,
  to: 30,
  a1: -300,
  a2: -300,
  a3: -300,
  a4: -300,
  a5: -300,
  a6: -300,
  a7: -300,
  a8: -300,
  a9: -300,
  a10: -300,
  a11: -300,
  a12: -300,
  a13: -300,
  a14: -300,
  a15: -300,
  a16: -300,
  a17: -300,
  a18: -300,
  b1: -160,
  b2: -160,
  b3: -160,
  b4: -160,
  b5: -160,
  b6: -160,
  b7: -160,
  b8: -160,
  b9: -160,
  b10: -160,
  b11: -160,
  b12: -160,
  b13: -160,
  b14: -160,
  b15: -160,
  b16: -160,
  b17: -160,
  b18: -160,
  c1: -110,
  c2: -110,
  c3: -110,
  c4: -110,
  c5: -110,
  c6: -110,
  c7: -110,
  c8: -110,
  c9: -110,
  c10: -110,
  c11: -110,
  c12: -110,
  c13: -110,
  c14: -110,
  c15: -110,
  c16: -110,
  c17: -110,
  c18: -110,
  d1: -65,
  d2: -65,
  d3: -65,
  d4: -65,
  d5: -65,
  d6: -65,
  d7: -65,
  d8: -65,
  d9: -65,
  d10: -65,
  d11: -65,
  d12: -65,
  d13: -65,
  d14: -65,
  d15: -65,
  d16: -65,
  d17: -65,
  d18: -65,
  e1: -40,
  e2: -40,
  e3: -40,
  e4: -40,
  e5: -40,
  e6: -40,
  e7: -40,
  e8: -40,
  e9: -40,
  e10: -40,
  e11: -40,
  e12: -40,
  e13: -40,
  e14: -40,
  e15: -40,
  e16: -40,
  e17: -40,
  e18: -40,
  f1: -20,
  f2: -20,
  f3: -20,
  f4: -20,
  f5: -20,
  f6: -20,
  f7: -20,
  f8: -20,
  f9: -20,
  f10: -20,
  f11: -20,
  f12: -20,
  f13: -20,
  f14: -20,
  f15: -20,
  f16: -20,
  f17: -20,
  f18: -20,
  g1: -7,
  g2: -7,
  g3: -7,
  g4: -7,
  g5: -7,
  g6: -7,
  g7: -7,
  g8: -7,
  g9: -7,
  g10: -7,
  g11: -7,
  g12: -7,
  g13: -7,
  g14: -7,
  g15: -7,
  g16: -7,
  g17: -7,
  g18: -7,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.8,
  js2: 1.2,
  js3: 2.0,
  js4: 3.0,
  js5: 4.5,
  js6: 6.5,
  js7: 10.5,
  js8: 16.5,
  js9: 26.0,
  js10: 42.0,
  js11: 65.0,
  js12: 105.0,
  js13: 165.0,
  js14: 260.0,
  js15: 420.0,
  js16: 650.0,
  js17: 1050.0,
  js18: 1650.0,
  j5: 5.0,
  j6: 9.0,
  j7: 13.0,
  k4: 8.0,
  k5: 11.0,
  k6: 15.0,
  k7: 23.0,
  k8: 33.0,
  k9: 52.0,
  k10: 84.0,
  k11: 130.0,
  k12: 210.0,
  k13: 330.0,
  k14: 520.0,
  k15: 840.0,
  k16: 1300.0,
  k17: 2100.0,
  k18: 3300.0,
  m1: 9.5,
  m2: 10.5,
  m3: 12.0,
  m4: 14.0,
  m5: 17.0,
  m6: 21.0,
  m7: 29.0,
  m8: 41.0,
  m9: 60.0,
  m10: 92.0,
  m11: 138.0,
  m12: 218.0,
  m13: 338.0,
  m14: 528.0,
  m15: 848.0,
  m16: 1308.0,
  m17: 2108.0,
  m18: 3308.0,
  n1: 16.5,
  n2: 17.5,
  n3: 19.0,
  n4: 21.0,
  n5: 24.0,
  n6: 28.0,
  n7: 36.0,
  n8: 48.0,
  n9: 67.0,
  n10: 99.0,
  n11: 145.0,
  n12: 225.0,
  n13: 345.0,
  n14: 535.0,
  n15: 855.0,
  n16: 1315.0,
  n17: 2115.0,
  n18: 3315.0,
  p1: 23.5,
  p2: 24.5,
  p3: 26.0,
  p4: 28.0,
  p5: 31.0,
  p6: 35.0,
  p7: 43.0,
  p8: 55.0,
  p9: 74.0,
  p10: 106.0,
  p11: 152.0,
  p12: 232.0,
  p13: 352.0,
  p14: 542.0,
  p15: 862.0,
  p16: 1322.0,
  p17: 2122.0,
  p18: 3322.0,
  r1: 29.5,
  r2: 30.5,
  r3: 32.0,
  r4: 34.0,
  r5: 37.0,
  r6: 41.0,
  r7: 49.0,
  r8: 61.0,
  r9: 80.0,
  r10: 112.0,
  r11: 158.0,
  r12: 238.0,
  r13: 358.0,
  r14: 548.0,
  r15: 868.0,
  r16: 1328.0,
  r17: 2128.0,
  r18: 3328.0,
  s1: 36.5,
  s2: 37.5,
  s3: 39.0,
  s4: 41.0,
  s5: 44.0,
  s6: 48.0,
  s7: 56.0,
  s8: 68.0,
  s9: 87.0,
  s10: 119.0,
  s11: 165.0,
  s12: 245.0,
  s13: 365.0,
  s14: 555.0,
  s15: 875.0,
  s16: 1335.0,
  s17: 2135.0,
  s18: 3335.0
}, {
  from: 30,
  to: 40,
  a1: -310,
  a2: -310,
  a3: -310,
  a4: -310,
  a5: -310,
  a6: -310,
  a7: -310,
  a8: -310,
  a9: -310,
  a10: -310,
  a11: -310,
  a12: -310,
  a13: -310,
  a14: -310,
  a15: -310,
  a16: -310,
  a17: -310,
  a18: -310,
  b1: -170,
  b2: -170,
  b3: -170,
  b4: -170,
  b5: -170,
  b6: -170,
  b7: -170,
  b8: -170,
  b9: -170,
  b10: -170,
  b11: -170,
  b12: -170,
  b13: -170,
  b14: -170,
  b15: -170,
  b16: -170,
  b17: -170,
  b18: -170,
  c1: -120,
  c2: -120,
  c3: -120,
  c4: -120,
  c5: -120,
  c6: -120,
  c7: -120,
  c8: -120,
  c9: -120,
  c10: -120,
  c11: -120,
  c12: -120,
  c13: -120,
  c14: -120,
  c15: -120,
  c16: -120,
  c17: -120,
  c18: -120,
  d1: -80,
  d2: -80,
  d3: -80,
  d4: -80,
  d5: -80,
  d6: -80,
  d7: -80,
  d8: -80,
  d9: -80,
  d10: -80,
  d11: -80,
  d12: -80,
  d13: -80,
  d14: -80,
  d15: -80,
  d16: -80,
  d17: -80,
  d18: -80,
  e1: -50,
  e2: -50,
  e3: -50,
  e4: -50,
  e5: -50,
  e6: -50,
  e7: -50,
  e8: -50,
  e9: -50,
  e10: -50,
  e11: -50,
  e12: -50,
  e13: -50,
  e14: -50,
  e15: -50,
  e16: -50,
  e17: -50,
  e18: -50,
  f1: -25,
  f2: -25,
  f3: -25,
  f4: -25,
  f5: -25,
  f6: -25,
  f7: -25,
  f8: -25,
  f9: -25,
  f10: -25,
  f11: -25,
  f12: -25,
  f13: -25,
  f14: -25,
  f15: -25,
  f16: -25,
  f17: -25,
  f18: -25,
  g1: -9,
  g2: -9,
  g3: -9,
  g4: -9,
  g5: -9,
  g6: -9,
  g7: -9,
  g8: -9,
  g9: -9,
  g10: -9,
  g11: -9,
  g12: -9,
  g13: -9,
  g14: -9,
  g15: -9,
  g16: -9,
  g17: -9,
  g18: -9,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.7,
  js2: 1.2,
  js3: 2.0,
  js4: 3.5,
  js5: 5.5,
  js6: 8.0,
  js7: 12.5,
  js8: 19.5,
  js9: 31.0,
  js10: 50.0,
  js11: 80.0,
  js12: 125.0,
  js13: 195.0,
  js14: 310.0,
  js15: 500.0,
  js16: 800.0,
  js17: 1250.0,
  js18: 1950.0,
  j5: 6.0,
  j6: 11.0,
  j7: 15.0,
  k4: 9.0,
  k5: 13.0,
  k6: 18.0,
  k7: 27.0,
  k8: 39.0,
  k9: 62.0,
  k10: 100.0,
  k11: 160.0,
  k12: 250.0,
  k13: 390.0,
  k14: 620.0,
  k15: 1000.0,
  k16: 1600.0,
  k17: 2500.0,
  k18: 3900.0,
  m1: 10.5,
  m2: 11.5,
  m3: 13.0,
  m4: 16.0,
  m5: 20.0,
  m6: 25.0,
  m7: 34.0,
  m8: 48.0,
  m9: 71.0,
  m10: 109.0,
  m11: 169.0,
  m12: 259.0,
  m13: 399.0,
  m14: 629.0,
  m15: 1009.0,
  m16: 1609.0,
  m17: 2509.0,
  m18: 3909.0,
  n1: 18.5,
  n2: 19.5,
  n3: 21.0,
  n4: 24.0,
  n5: 28.0,
  n6: 33.0,
  n7: 42.0,
  n8: 56.0,
  n9: 79.0,
  n10: 117.0,
  n11: 177.0,
  n12: 267.0,
  n13: 407.0,
  n14: 637.0,
  n15: 1017.0,
  n16: 1617.0,
  n17: 2517.0,
  n18: 3917.0,
  p1: 27.5,
  p2: 28.5,
  p3: 30.0,
  p4: 33.0,
  p5: 37.0,
  p6: 42.0,
  p7: 51.0,
  p8: 65.0,
  p9: 88.0,
  p10: 126.0,
  p11: 186.0,
  p12: 276.0,
  p13: 416.0,
  p14: 646.0,
  p15: 1026.0,
  p16: 1626.0,
  p17: 2526.0,
  p18: 3926.0,
  r1: 35.5,
  r2: 36.5,
  r3: 38.0,
  r4: 41.0,
  r5: 45.0,
  r6: 50.0,
  r7: 59.0,
  r8: 73.0,
  r9: 96.0,
  r10: 134.0,
  r11: 194.0,
  r12: 284.0,
  r13: 424.0,
  r14: 654.0,
  r15: 1034.0,
  r16: 1634.0,
  r17: 2534.0,
  r18: 3934.0,
  s1: 44.5,
  s2: 45.5,
  s3: 47.0,
  s4: 50.0,
  s5: 54.0,
  s6: 59.0,
  s7: 68.0,
  s8: 82.0,
  s9: 105.0,
  s10: 143.0,
  s11: 203.0,
  s12: 293.0,
  s13: 433.0,
  s14: 663.0,
  s15: 1043.0,
  s16: 1643.0,
  s17: 2543.0,
  s18: 3943.0
}, {
  from: 40,
  to: 50,
  a1: -320,
  a2: -320,
  a3: -320,
  a4: -320,
  a5: -320,
  a6: -320,
  a7: -320,
  a8: -320,
  a9: -320,
  a10: -320,
  a11: -320,
  a12: -320,
  a13: -320,
  a14: -320,
  a15: -320,
  a16: -320,
  a17: -320,
  a18: -320,
  b1: -180,
  b2: -180,
  b3: -180,
  b4: -180,
  b5: -180,
  b6: -180,
  b7: -180,
  b8: -180,
  b9: -180,
  b10: -180,
  b11: -180,
  b12: -180,
  b13: -180,
  b14: -180,
  b15: -180,
  b16: -180,
  b17: -180,
  b18: -180,
  c1: -130,
  c2: -130,
  c3: -130,
  c4: -130,
  c5: -130,
  c6: -130,
  c7: -130,
  c8: -130,
  c9: -130,
  c10: -130,
  c11: -130,
  c12: -130,
  c13: -130,
  c14: -130,
  c15: -130,
  c16: -130,
  c17: -130,
  c18: -130,
  d1: -80,
  d2: -80,
  d3: -80,
  d4: -80,
  d5: -80,
  d6: -80,
  d7: -80,
  d8: -80,
  d9: -80,
  d10: -80,
  d11: -80,
  d12: -80,
  d13: -80,
  d14: -80,
  d15: -80,
  d16: -80,
  d17: -80,
  d18: -80,
  e1: -50,
  e2: -50,
  e3: -50,
  e4: -50,
  e5: -50,
  e6: -50,
  e7: -50,
  e8: -50,
  e9: -50,
  e10: -50,
  e11: -50,
  e12: -50,
  e13: -50,
  e14: -50,
  e15: -50,
  e16: -50,
  e17: -50,
  e18: -50,
  f1: -25,
  f2: -25,
  f3: -25,
  f4: -25,
  f5: -25,
  f6: -25,
  f7: -25,
  f8: -25,
  f9: -25,
  f10: -25,
  f11: -25,
  f12: -25,
  f13: -25,
  f14: -25,
  f15: -25,
  f16: -25,
  f17: -25,
  f18: -25,
  g1: -9,
  g2: -9,
  g3: -9,
  g4: -9,
  g5: -9,
  g6: -9,
  g7: -9,
  g8: -9,
  g9: -9,
  g10: -9,
  g11: -9,
  g12: -9,
  g13: -9,
  g14: -9,
  g15: -9,
  g16: -9,
  g17: -9,
  g18: -9,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 0.7,
  js2: 1.2,
  js3: 2.0,
  js4: 3.5,
  js5: 5.5,
  js6: 8.0,
  js7: 12.5,
  js8: 19.5,
  js9: 31.0,
  js10: 50.0,
  js11: 80.0,
  js12: 125.0,
  js13: 195.0,
  js14: 310.0,
  js15: 500.0,
  js16: 800.0,
  js17: 1250.0,
  js18: 1950.0,
  j5: 6.0,
  j6: 11.0,
  j7: 15.0,
  k4: 9.0,
  k5: 13.0,
  k6: 18.0,
  k7: 27.0,
  k8: 39.0,
  k9: 62.0,
  k10: 100.0,
  k11: 160.0,
  k12: 250.0,
  k13: 390.0,
  k14: 620.0,
  k15: 1000.0,
  k16: 1600.0,
  k17: 2500.0,
  k18: 3900.0,
  m1: 10.5,
  m2: 11.5,
  m3: 13.0,
  m4: 16.0,
  m5: 20.0,
  m6: 25.0,
  m7: 34.0,
  m8: 48.0,
  m9: 71.0,
  m10: 109.0,
  m11: 169.0,
  m12: 259.0,
  m13: 399.0,
  m14: 629.0,
  m15: 1009.0,
  m16: 1609.0,
  m17: 2509.0,
  m18: 3909.0,
  n1: 18.5,
  n2: 19.5,
  n3: 21.0,
  n4: 24.0,
  n5: 28.0,
  n6: 33.0,
  n7: 42.0,
  n8: 56.0,
  n9: 79.0,
  n10: 117.0,
  n11: 177.0,
  n12: 267.0,
  n13: 407.0,
  n14: 637.0,
  n15: 1017.0,
  n16: 1617.0,
  n17: 2517.0,
  n18: 3917.0,
  p1: 27.5,
  p2: 28.5,
  p3: 30.0,
  p4: 33.0,
  p5: 37.0,
  p6: 42.0,
  p7: 51.0,
  p8: 65.0,
  p9: 88.0,
  p10: 126.0,
  p11: 186.0,
  p12: 276.0,
  p13: 416.0,
  p14: 646.0,
  p15: 1026.0,
  p16: 1626.0,
  p17: 2526.0,
  p18: 3926.0,
  r1: 35.5,
  r2: 36.5,
  r3: 38.0,
  r4: 41.0,
  r5: 45.0,
  r6: 50.0,
  r7: 59.0,
  r8: 73.0,
  r9: 96.0,
  r10: 134.0,
  r11: 194.0,
  r12: 284.0,
  r13: 424.0,
  r14: 654.0,
  r15: 1034.0,
  r16: 1634.0,
  r17: 2534.0,
  r18: 3934.0,
  s1: 44.5,
  s2: 45.5,
  s3: 47.0,
  s4: 50.0,
  s5: 54.0,
  s6: 59.0,
  s7: 68.0,
  s8: 82.0,
  s9: 105.0,
  s10: 143.0,
  s11: 203.0,
  s12: 293.0,
  s13: 433.0,
  s14: 663.0,
  s15: 1043.0,
  s16: 1643.0,
  s17: 2543.0,
  s18: 3943.0
}, {
  from: 50,
  to: 65,
  a1: -340,
  a2: -340,
  a3: -340,
  a4: -340,
  a5: -340,
  a6: -340,
  a7: -340,
  a8: -340,
  a9: -340,
  a10: -340,
  a11: -340,
  a12: -340,
  a13: -340,
  a14: -340,
  a15: -340,
  a16: -340,
  a17: -340,
  a18: -340,
  b1: -190,
  b2: -190,
  b3: -190,
  b4: -190,
  b5: -190,
  b6: -190,
  b7: -190,
  b8: -190,
  b9: -190,
  b10: -190,
  b11: -190,
  b12: -190,
  b13: -190,
  b14: -190,
  b15: -190,
  b16: -190,
  b17: -190,
  b18: -190,
  c1: -140,
  c2: -140,
  c3: -140,
  c4: -140,
  c5: -140,
  c6: -140,
  c7: -140,
  c8: -140,
  c9: -140,
  c10: -140,
  c11: -140,
  c12: -140,
  c13: -140,
  c14: -140,
  c15: -140,
  c16: -140,
  c17: -140,
  c18: -140,
  d1: -100,
  d2: -100,
  d3: -100,
  d4: -100,
  d5: -100,
  d6: -100,
  d7: -100,
  d8: -100,
  d9: -100,
  d10: -100,
  d11: -100,
  d12: -100,
  d13: -100,
  d14: -100,
  d15: -100,
  d16: -100,
  d17: -100,
  d18: -100,
  e1: -60,
  e2: -60,
  e3: -60,
  e4: -60,
  e5: -60,
  e6: -60,
  e7: -60,
  e8: -60,
  e9: -60,
  e10: -60,
  e11: -60,
  e12: -60,
  e13: -60,
  e14: -60,
  e15: -60,
  e16: -60,
  e17: -60,
  e18: -60,
  f1: -30,
  f2: -30,
  f3: -30,
  f4: -30,
  f5: -30,
  f6: -30,
  f7: -30,
  f8: -30,
  f9: -30,
  f10: -30,
  f11: -30,
  f12: -30,
  f13: -30,
  f14: -30,
  f15: -30,
  f16: -30,
  f17: -30,
  f18: -30,
  g1: -10,
  g2: -10,
  g3: -10,
  g4: -10,
  g5: -10,
  g6: -10,
  g7: -10,
  g8: -10,
  g9: -10,
  g10: -10,
  g11: -10,
  g12: -10,
  g13: -10,
  g14: -10,
  g15: -10,
  g16: -10,
  g17: -10,
  g18: -10,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.0,
  js2: 1.5,
  js3: 2.5,
  js4: 4.0,
  js5: 6.5,
  js6: 9.5,
  js7: 15.0,
  js8: 23.0,
  js9: 37.0,
  js10: 60.0,
  js11: 95.0,
  js12: 150.0,
  js13: 230.0,
  js14: 370.0,
  js15: 600.0,
  js16: 950.0,
  js17: 1500.0,
  js18: 2300.0,
  j5: 6.0,
  j6: 12.0,
  j7: 18.0,
  k4: 10.0,
  k5: 15.0,
  k6: 21.0,
  k7: 32.0,
  k8: 46.0,
  k9: 74.0,
  k10: 120.0,
  k11: 190.0,
  k12: 300.0,
  k13: 460.0,
  k14: 740.0,
  k15: 1200.0,
  k16: 1900.0,
  k17: 3000.0,
  k18: 4600.0,
  m1: 13.0,
  m2: 14.0,
  m3: 16.0,
  m4: 19.0,
  m5: 24.0,
  m6: 30.0,
  m7: 41.0,
  m8: 57.0,
  m9: 85.0,
  m10: 131.0,
  m11: 201.0,
  m12: 311.0,
  m13: 471.0,
  m14: 751.0,
  m15: 1211.0,
  m16: 1911.0,
  m17: 3011.0,
  m18: 4611.0,
  n1: 22.0,
  n2: 23.0,
  n3: 25.0,
  n4: 28.0,
  n5: 33.0,
  n6: 39.0,
  n7: 50.0,
  n8: 66.0,
  n9: 94.0,
  n10: 140.0,
  n11: 210.0,
  n12: 320.0,
  n13: 480.0,
  n14: 760.0,
  n15: 1220.0,
  n16: 1920.0,
  n17: 3020.0,
  n18: 4620.0,
  p1: 34.0,
  p2: 35.0,
  p3: 37.0,
  p4: 40.0,
  p5: 45.0,
  p6: 51.0,
  p7: 62.0,
  p8: 78.0,
  p9: 106.0,
  p10: 152.0,
  p11: 222.0,
  p12: 332.0,
  p13: 492.0,
  p14: 772.0,
  p15: 1232.0,
  p16: 1932.0,
  p17: 3032.0,
  p18: 4632.0,
  r1: 43.0,
  r2: 44.0,
  r3: 46.0,
  r4: 49.0,
  r5: 54.0,
  r6: 60.0,
  r7: 71.0,
  r8: 87.0,
  r9: 115.0,
  r10: 161.0,
  r11: 231.0,
  r12: 341.0,
  r13: 501.0,
  r14: 781.0,
  r15: 1241.0,
  r16: 1941.0,
  r17: 3041.0,
  r18: 4641.0,
  s1: 55.0,
  s2: 56.0,
  s3: 58.0,
  s4: 61.0,
  s5: 66.0,
  s6: 72.0,
  s7: 83.0,
  s8: 99.0,
  s9: 127.0,
  s10: 173.0,
  s11: 243.0,
  s12: 353.0,
  s13: 513.0,
  s14: 793.0,
  s15: 1253.0,
  s16: 1953.0,
  s17: 3053.0,
  s18: 4653.0
}, {
  from: 65,
  to: 80,
  a1: -360,
  a2: -360,
  a3: -360,
  a4: -360,
  a5: -360,
  a6: -360,
  a7: -360,
  a8: -360,
  a9: -360,
  a10: -360,
  a11: -360,
  a12: -360,
  a13: -360,
  a14: -360,
  a15: -360,
  a16: -360,
  a17: -360,
  a18: -360,
  b1: -200,
  b2: -200,
  b3: -200,
  b4: -200,
  b5: -200,
  b6: -200,
  b7: -200,
  b8: -200,
  b9: -200,
  b10: -200,
  b11: -200,
  b12: -200,
  b13: -200,
  b14: -200,
  b15: -200,
  b16: -200,
  b17: -200,
  b18: -200,
  c1: -150,
  c2: -150,
  c3: -150,
  c4: -150,
  c5: -150,
  c6: -150,
  c7: -150,
  c8: -150,
  c9: -150,
  c10: -150,
  c11: -150,
  c12: -150,
  c13: -150,
  c14: -150,
  c15: -150,
  c16: -150,
  c17: -150,
  c18: -150,
  d1: -100,
  d2: -100,
  d3: -100,
  d4: -100,
  d5: -100,
  d6: -100,
  d7: -100,
  d8: -100,
  d9: -100,
  d10: -100,
  d11: -100,
  d12: -100,
  d13: -100,
  d14: -100,
  d15: -100,
  d16: -100,
  d17: -100,
  d18: -100,
  e1: -60,
  e2: -60,
  e3: -60,
  e4: -60,
  e5: -60,
  e6: -60,
  e7: -60,
  e8: -60,
  e9: -60,
  e10: -60,
  e11: -60,
  e12: -60,
  e13: -60,
  e14: -60,
  e15: -60,
  e16: -60,
  e17: -60,
  e18: -60,
  f1: -30,
  f2: -30,
  f3: -30,
  f4: -30,
  f5: -30,
  f6: -30,
  f7: -30,
  f8: -30,
  f9: -30,
  f10: -30,
  f11: -30,
  f12: -30,
  f13: -30,
  f14: -30,
  f15: -30,
  f16: -30,
  f17: -30,
  f18: -30,
  g1: -10,
  g2: -10,
  g3: -10,
  g4: -10,
  g5: -10,
  g6: -10,
  g7: -10,
  g8: -10,
  g9: -10,
  g10: -10,
  g11: -10,
  g12: -10,
  g13: -10,
  g14: -10,
  g15: -10,
  g16: -10,
  g17: -10,
  g18: -10,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.0,
  js2: 1.5,
  js3: 2.5,
  js4: 4.0,
  js5: 6.5,
  js6: 9.5,
  js7: 15.0,
  js8: 23.0,
  js9: 37.0,
  js10: 60.0,
  js11: 95.0,
  js12: 150.0,
  js13: 230.0,
  js14: 370.0,
  js15: 600.0,
  js16: 950.0,
  js17: 1500.0,
  js18: 2300.0,
  j5: 6.0,
  j6: 12.0,
  j7: 18.0,
  k4: 10.0,
  k5: 15.0,
  k6: 21.0,
  k7: 32.0,
  k8: 46.0,
  k9: 74.0,
  k10: 120.0,
  k11: 190.0,
  k12: 300.0,
  k13: 460.0,
  k14: 740.0,
  k15: 1200.0,
  k16: 1900.0,
  k17: 3000.0,
  k18: 4600.0,
  m1: 13.0,
  m2: 14.0,
  m3: 16.0,
  m4: 19.0,
  m5: 24.0,
  m6: 30.0,
  m7: 41.0,
  m8: 57.0,
  m9: 85.0,
  m10: 131.0,
  m11: 201.0,
  m12: 311.0,
  m13: 471.0,
  m14: 751.0,
  m15: 1211.0,
  m16: 1911.0,
  m17: 3011.0,
  m18: 4611.0,
  n1: 22.0,
  n2: 23.0,
  n3: 25.0,
  n4: 28.0,
  n5: 33.0,
  n6: 39.0,
  n7: 50.0,
  n8: 66.0,
  n9: 94.0,
  n10: 140.0,
  n11: 210.0,
  n12: 320.0,
  n13: 480.0,
  n14: 760.0,
  n15: 1220.0,
  n16: 1920.0,
  n17: 3020.0,
  n18: 4620.0,
  p1: 34.0,
  p2: 35.0,
  p3: 37.0,
  p4: 40.0,
  p5: 45.0,
  p6: 51.0,
  p7: 62.0,
  p8: 78.0,
  p9: 106.0,
  p10: 152.0,
  p11: 222.0,
  p12: 332.0,
  p13: 492.0,
  p14: 772.0,
  p15: 1232.0,
  p16: 1932.0,
  p17: 3032.0,
  p18: 4632.0,
  r1: 45.0,
  r2: 46.0,
  r3: 48.0,
  r4: 51.0,
  r5: 56.0,
  r6: 62.0,
  r7: 73.0,
  r8: 89.0,
  r9: 117.0,
  r10: 163.0,
  r11: 233.0,
  r12: 343.0,
  r13: 503.0,
  r14: 783.0,
  r15: 1243.0,
  r16: 1943.0,
  r17: 3043.0,
  r18: 4643.0,
  s1: 61.0,
  s2: 62.0,
  s3: 64.0,
  s4: 67.0,
  s5: 72.0,
  s6: 78.0,
  s7: 89.0,
  s8: 105.0,
  s9: 133.0,
  s10: 179.0,
  s11: 249.0,
  s12: 359.0,
  s13: 519.0,
  s14: 799.0,
  s15: 1259.0,
  s16: 1959.0,
  s17: 3059.0,
  s18: 4659.0
}, {
  from: 80,
  to: 100,
  a1: -380,
  a2: -380,
  a3: -380,
  a4: -380,
  a5: -380,
  a6: -380,
  a7: -380,
  a8: -380,
  a9: -380,
  a10: -380,
  a11: -380,
  a12: -380,
  a13: -380,
  a14: -380,
  a15: -380,
  a16: -380,
  a17: -380,
  a18: -380,
  b1: -220,
  b2: -220,
  b3: -220,
  b4: -220,
  b5: -220,
  b6: -220,
  b7: -220,
  b8: -220,
  b9: -220,
  b10: -220,
  b11: -220,
  b12: -220,
  b13: -220,
  b14: -220,
  b15: -220,
  b16: -220,
  b17: -220,
  b18: -220,
  c1: -170,
  c2: -170,
  c3: -170,
  c4: -170,
  c5: -170,
  c6: -170,
  c7: -170,
  c8: -170,
  c9: -170,
  c10: -170,
  c11: -170,
  c12: -170,
  c13: -170,
  c14: -170,
  c15: -170,
  c16: -170,
  c17: -170,
  c18: -170,
  d1: -120,
  d2: -120,
  d3: -120,
  d4: -120,
  d5: -120,
  d6: -120,
  d7: -120,
  d8: -120,
  d9: -120,
  d10: -120,
  d11: -120,
  d12: -120,
  d13: -120,
  d14: -120,
  d15: -120,
  d16: -120,
  d17: -120,
  d18: -120,
  e1: -72,
  e2: -72,
  e3: -72,
  e4: -72,
  e5: -72,
  e6: -72,
  e7: -72,
  e8: -72,
  e9: -72,
  e10: -72,
  e11: -72,
  e12: -72,
  e13: -72,
  e14: -72,
  e15: -72,
  e16: -72,
  e17: -72,
  e18: -72,
  f1: -36,
  f2: -36,
  f3: -36,
  f4: -36,
  f5: -36,
  f6: -36,
  f7: -36,
  f8: -36,
  f9: -36,
  f10: -36,
  f11: -36,
  f12: -36,
  f13: -36,
  f14: -36,
  f15: -36,
  f16: -36,
  f17: -36,
  f18: -36,
  g1: -12,
  g2: -12,
  g3: -12,
  g4: -12,
  g5: -12,
  g6: -12,
  g7: -12,
  g8: -12,
  g9: -12,
  g10: -12,
  g11: -12,
  g12: -12,
  g13: -12,
  g14: -12,
  g15: -12,
  g16: -12,
  g17: -12,
  g18: -12,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.2,
  js2: 2.0,
  js3: 3.0,
  js4: 5.0,
  js5: 7.5,
  js6: 11.0,
  js7: 17.5,
  js8: 27.0,
  js9: 43.5,
  js10: 70.0,
  js11: 110.0,
  js12: 175.0,
  js13: 270.0,
  js14: 435.0,
  js15: 700.0,
  js16: 1100.0,
  js17: 1750.0,
  js18: 2700.0,
  j5: 6.0,
  j6: 13.0,
  j7: 20.0,
  k4: 13.0,
  k5: 18.0,
  k6: 25.0,
  k7: 38.0,
  k8: 54.0,
  k9: 87.0,
  k10: 140.0,
  k11: 220.0,
  k12: 350.0,
  k13: 540.0,
  k14: 870.0,
  k15: 1400.0,
  k16: 2200.0,
  k17: 3500.0,
  k18: 5400.0,
  m1: 15.5,
  m2: 17.0,
  m3: 19.0,
  m4: 23.0,
  m5: 28.0,
  m6: 35.0,
  m7: 48.0,
  m8: 67.0,
  m9: 100.0,
  m10: 153.0,
  m11: 233.0,
  m12: 363.0,
  m13: 553.0,
  m14: 883.0,
  m15: 1413.0,
  m16: 2213.0,
  m17: 3513.0,
  m18: 5413.0,
  n1: 25.5,
  n2: 27.0,
  n3: 29.0,
  n4: 33.0,
  n5: 38.0,
  n6: 45.0,
  n7: 58.0,
  n8: 77.0,
  n9: 110.0,
  n10: 163.0,
  n11: 243.0,
  n12: 373.0,
  n13: 563.0,
  n14: 893.0,
  n15: 1423.0,
  n16: 2223.0,
  n17: 3523.0,
  n18: 5423.0,
  p1: 39.5,
  p2: 41.0,
  p3: 43.0,
  p4: 47.0,
  p5: 52.0,
  p6: 59.0,
  p7: 72.0,
  p8: 91.0,
  p9: 124.0,
  p10: 177.0,
  p11: 257.0,
  p12: 387.0,
  p13: 577.0,
  p14: 907.0,
  p15: 1437.0,
  p16: 2237.0,
  p17: 3537.0,
  p18: 5437.0,
  r1: 53.5,
  r2: 55.0,
  r3: 57.0,
  r4: 61.0,
  r5: 66.0,
  r6: 73.0,
  r7: 86.0,
  r8: 105.0,
  r9: 138.0,
  r10: 191.0,
  r11: 271.0,
  r12: 401.0,
  r13: 591.0,
  r14: 921.0,
  r15: 1451.0,
  r16: 2251.0,
  r17: 3551.0,
  r18: 5451.0,
  s1: 73.5,
  s2: 75.0,
  s3: 77.0,
  s4: 81.0,
  s5: 86.0,
  s6: 93.0,
  s7: 106.0,
  s8: 125.0,
  s9: 158.0,
  s10: 211.0,
  s11: 291.0,
  s12: 421.0,
  s13: 611.0,
  s14: 941.0,
  s15: 1471.0,
  s16: 2271.0,
  s17: 3571.0,
  s18: 5471.0
}, {
  from: 100,
  to: 120,
  a1: -410,
  a2: -410,
  a3: -410,
  a4: -410,
  a5: -410,
  a6: -410,
  a7: -410,
  a8: -410,
  a9: -410,
  a10: -410,
  a11: -410,
  a12: -410,
  a13: -410,
  a14: -410,
  a15: -410,
  a16: -410,
  a17: -410,
  a18: -410,
  b1: -240,
  b2: -240,
  b3: -240,
  b4: -240,
  b5: -240,
  b6: -240,
  b7: -240,
  b8: -240,
  b9: -240,
  b10: -240,
  b11: -240,
  b12: -240,
  b13: -240,
  b14: -240,
  b15: -240,
  b16: -240,
  b17: -240,
  b18: -240,
  c1: -180,
  c2: -180,
  c3: -180,
  c4: -180,
  c5: -180,
  c6: -180,
  c7: -180,
  c8: -180,
  c9: -180,
  c10: -180,
  c11: -180,
  c12: -180,
  c13: -180,
  c14: -180,
  c15: -180,
  c16: -180,
  c17: -180,
  c18: -180,
  d1: -120,
  d2: -120,
  d3: -120,
  d4: -120,
  d5: -120,
  d6: -120,
  d7: -120,
  d8: -120,
  d9: -120,
  d10: -120,
  d11: -120,
  d12: -120,
  d13: -120,
  d14: -120,
  d15: -120,
  d16: -120,
  d17: -120,
  d18: -120,
  e1: -72,
  e2: -72,
  e3: -72,
  e4: -72,
  e5: -72,
  e6: -72,
  e7: -72,
  e8: -72,
  e9: -72,
  e10: -72,
  e11: -72,
  e12: -72,
  e13: -72,
  e14: -72,
  e15: -72,
  e16: -72,
  e17: -72,
  e18: -72,
  f1: -36,
  f2: -36,
  f3: -36,
  f4: -36,
  f5: -36,
  f6: -36,
  f7: -36,
  f8: -36,
  f9: -36,
  f10: -36,
  f11: -36,
  f12: -36,
  f13: -36,
  f14: -36,
  f15: -36,
  f16: -36,
  f17: -36,
  f18: -36,
  g1: -12,
  g2: -12,
  g3: -12,
  g4: -12,
  g5: -12,
  g6: -12,
  g7: -12,
  g8: -12,
  g9: -12,
  g10: -12,
  g11: -12,
  g12: -12,
  g13: -12,
  g14: -12,
  g15: -12,
  g16: -12,
  g17: -12,
  g18: -12,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.2,
  js2: 2.0,
  js3: 3.0,
  js4: 5.0,
  js5: 7.5,
  js6: 11.0,
  js7: 17.5,
  js8: 27.0,
  js9: 43.5,
  js10: 70.0,
  js11: 110.0,
  js12: 175.0,
  js13: 270.0,
  js14: 435.0,
  js15: 700.0,
  js16: 1100.0,
  js17: 1750.0,
  js18: 2700.0,
  j5: 6.0,
  j6: 13.0,
  j7: 20.0,
  k4: 13.0,
  k5: 18.0,
  k6: 25.0,
  k7: 38.0,
  k8: 54.0,
  k9: 87.0,
  k10: 140.0,
  k11: 220.0,
  k12: 350.0,
  k13: 540.0,
  k14: 870.0,
  k15: 1400.0,
  k16: 2200.0,
  k17: 3500.0,
  k18: 5400.0,
  m1: 15.5,
  m2: 17.0,
  m3: 19.0,
  m4: 23.0,
  m5: 28.0,
  m6: 35.0,
  m7: 48.0,
  m8: 67.0,
  m9: 100.0,
  m10: 153.0,
  m11: 233.0,
  m12: 363.0,
  m13: 553.0,
  m14: 883.0,
  m15: 1413.0,
  m16: 2213.0,
  m17: 3513.0,
  m18: 5413.0,
  n1: 25.5,
  n2: 27.0,
  n3: 29.0,
  n4: 33.0,
  n5: 38.0,
  n6: 45.0,
  n7: 58.0,
  n8: 77.0,
  n9: 110.0,
  n10: 163.0,
  n11: 243.0,
  n12: 373.0,
  n13: 563.0,
  n14: 893.0,
  n15: 1423.0,
  n16: 2223.0,
  n17: 3523.0,
  n18: 5423.0,
  p1: 39.5,
  p2: 41.0,
  p3: 43.0,
  p4: 47.0,
  p5: 52.0,
  p6: 59.0,
  p7: 72.0,
  p8: 91.0,
  p9: 124.0,
  p10: 177.0,
  p11: 257.0,
  p12: 387.0,
  p13: 577.0,
  p14: 907.0,
  p15: 1437.0,
  p16: 2237.0,
  p17: 3537.0,
  p18: 5437.0,
  r1: 56.5,
  r2: 58.0,
  r3: 60.0,
  r4: 64.0,
  r5: 69.0,
  r6: 76.0,
  r7: 89.0,
  r8: 108.0,
  r9: 141.0,
  r10: 194.0,
  r11: 274.0,
  r12: 404.0,
  r13: 594.0,
  r14: 924.0,
  r15: 1454.0,
  r16: 2254.0,
  r17: 3554.0,
  r18: 5454.0,
  s1: 81.5,
  s2: 83.0,
  s3: 85.0,
  s4: 89.0,
  s5: 94.0,
  s6: 101.0,
  s7: 114.0,
  s8: 133.0,
  s9: 166.0,
  s10: 219.0,
  s11: 299.0,
  s12: 429.0,
  s13: 619.0,
  s14: 949.0,
  s15: 1479.0,
  s16: 2279.0,
  s17: 3579.0,
  s18: 5479.0
}, {
  from: 120,
  to: 140,
  a1: -460,
  a2: -460,
  a3: -460,
  a4: -460,
  a5: -460,
  a6: -460,
  a7: -460,
  a8: -460,
  a9: -460,
  a10: -460,
  a11: -460,
  a12: -460,
  a13: -460,
  a14: -460,
  a15: -460,
  a16: -460,
  a17: -460,
  a18: -460,
  b1: -260,
  b2: -260,
  b3: -260,
  b4: -260,
  b5: -260,
  b6: -260,
  b7: -260,
  b8: -260,
  b9: -260,
  b10: -260,
  b11: -260,
  b12: -260,
  b13: -260,
  b14: -260,
  b15: -260,
  b16: -260,
  b17: -260,
  b18: -260,
  c1: -200,
  c2: -200,
  c3: -200,
  c4: -200,
  c5: -200,
  c6: -200,
  c7: -200,
  c8: -200,
  c9: -200,
  c10: -200,
  c11: -200,
  c12: -200,
  c13: -200,
  c14: -200,
  c15: -200,
  c16: -200,
  c17: -200,
  c18: -200,
  d1: -145,
  d2: -145,
  d3: -145,
  d4: -145,
  d5: -145,
  d6: -145,
  d7: -145,
  d8: -145,
  d9: -145,
  d10: -145,
  d11: -145,
  d12: -145,
  d13: -145,
  d14: -145,
  d15: -145,
  d16: -145,
  d17: -145,
  d18: -145,
  e1: -85,
  e2: -85,
  e3: -85,
  e4: -85,
  e5: -85,
  e6: -85,
  e7: -85,
  e8: -85,
  e9: -85,
  e10: -85,
  e11: -85,
  e12: -85,
  e13: -85,
  e14: -85,
  e15: -85,
  e16: -85,
  e17: -85,
  e18: -85,
  f1: -43,
  f2: -43,
  f3: -43,
  f4: -43,
  f5: -43,
  f6: -43,
  f7: -43,
  f8: -43,
  f9: -43,
  f10: -43,
  f11: -43,
  f12: -43,
  f13: -43,
  f14: -43,
  f15: -43,
  f16: -43,
  f17: -43,
  f18: -43,
  g1: -14,
  g2: -14,
  g3: -14,
  g4: -14,
  g5: -14,
  g6: -14,
  g7: -14,
  g8: -14,
  g9: -14,
  g10: -14,
  g11: -14,
  g12: -14,
  g13: -14,
  g14: -14,
  g15: -14,
  g16: -14,
  g17: -14,
  g18: -14,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.7,
  js2: 2.5,
  js3: 4.0,
  js4: 6.0,
  js5: 9.0,
  js6: 12.5,
  js7: 20.0,
  js8: 31.5,
  js9: 50.0,
  js10: 80.0,
  js11: 125.0,
  js12: 200.0,
  js13: 315.0,
  js14: 500.0,
  js15: 800.0,
  js16: 1250.0,
  js17: 2000.0,
  js18: 3150.0,
  j5: 7.0,
  j6: 14.0,
  j7: 22.0,
  k4: 15.0,
  k5: 21.0,
  k6: 28.0,
  k7: 43.0,
  k8: 63.0,
  k9: 100.0,
  k10: 160.0,
  k11: 250.0,
  k12: 400.0,
  k13: 630.0,
  k14: 1000.0,
  k15: 1600.0,
  k16: 2500.0,
  k17: 4000.0,
  k18: 6300.0,
  m1: 18.5,
  m2: 20.0,
  m3: 23.0,
  m4: 27.0,
  m5: 33.0,
  m6: 40.0,
  m7: 55.0,
  m8: 78.0,
  m9: 115.0,
  m10: 175.0,
  m11: 265.0,
  m12: 415.0,
  m13: 645.0,
  m14: 1015.0,
  m15: 1615.0,
  m16: 2515.0,
  m17: 4015.0,
  m18: 6315.0,
  n1: 30.5,
  n2: 32.0,
  n3: 35.0,
  n4: 39.0,
  n5: 45.0,
  n6: 52.0,
  n7: 67.0,
  n8: 90.0,
  n9: 127.0,
  n10: 187.0,
  n11: 277.0,
  n12: 427.0,
  n13: 657.0,
  n14: 1027.0,
  n15: 1627.0,
  n16: 2527.0,
  n17: 4027.0,
  n18: 6327.0,
  p1: 46.5,
  p2: 48.0,
  p3: 51.0,
  p4: 55.0,
  p5: 61.0,
  p6: 68.0,
  p7: 83.0,
  p8: 106.0,
  p9: 143.0,
  p10: 203.0,
  p11: 293.0,
  p12: 443.0,
  p13: 673.0,
  p14: 1043.0,
  p15: 1643.0,
  p16: 2543.0,
  p17: 4043.0,
  p18: 6343.0,
  r1: 66.5,
  r2: 68.0,
  r3: 71.0,
  r4: 75.0,
  r5: 81.0,
  r6: 88.0,
  r7: 103.0,
  r8: 126.0,
  r9: 163.0,
  r10: 223.0,
  r11: 313.0,
  r12: 463.0,
  r13: 693.0,
  r14: 1063.0,
  r15: 1663.0,
  r16: 2563.0,
  r17: 4063.0,
  r18: 6363.0,
  s1: 95.5,
  s2: 97.0,
  s3: 100.0,
  s4: 104.0,
  s5: 110.0,
  s6: 117.0,
  s7: 132.0,
  s8: 155.0,
  s9: 192.0,
  s10: 252.0,
  s11: 342.0,
  s12: 492.0,
  s13: 722.0,
  s14: 1092.0,
  s15: 1692.0,
  s16: 2592.0,
  s17: 4092.0,
  s18: 6392.0
}, {
  from: 140,
  to: 160,
  a1: -520,
  a2: -520,
  a3: -520,
  a4: -520,
  a5: -520,
  a6: -520,
  a7: -520,
  a8: -520,
  a9: -520,
  a10: -520,
  a11: -520,
  a12: -520,
  a13: -520,
  a14: -520,
  a15: -520,
  a16: -520,
  a17: -520,
  a18: -520,
  b1: -280,
  b2: -280,
  b3: -280,
  b4: -280,
  b5: -280,
  b6: -280,
  b7: -280,
  b8: -280,
  b9: -280,
  b10: -280,
  b11: -280,
  b12: -280,
  b13: -280,
  b14: -280,
  b15: -280,
  b16: -280,
  b17: -280,
  b18: -280,
  c1: -210,
  c2: -210,
  c3: -210,
  c4: -210,
  c5: -210,
  c6: -210,
  c7: -210,
  c8: -210,
  c9: -210,
  c10: -210,
  c11: -210,
  c12: -210,
  c13: -210,
  c14: -210,
  c15: -210,
  c16: -210,
  c17: -210,
  c18: -210,
  d1: -145,
  d2: -145,
  d3: -145,
  d4: -145,
  d5: -145,
  d6: -145,
  d7: -145,
  d8: -145,
  d9: -145,
  d10: -145,
  d11: -145,
  d12: -145,
  d13: -145,
  d14: -145,
  d15: -145,
  d16: -145,
  d17: -145,
  d18: -145,
  e1: -85,
  e2: -85,
  e3: -85,
  e4: -85,
  e5: -85,
  e6: -85,
  e7: -85,
  e8: -85,
  e9: -85,
  e10: -85,
  e11: -85,
  e12: -85,
  e13: -85,
  e14: -85,
  e15: -85,
  e16: -85,
  e17: -85,
  e18: -85,
  f1: -43,
  f2: -43,
  f3: -43,
  f4: -43,
  f5: -43,
  f6: -43,
  f7: -43,
  f8: -43,
  f9: -43,
  f10: -43,
  f11: -43,
  f12: -43,
  f13: -43,
  f14: -43,
  f15: -43,
  f16: -43,
  f17: -43,
  f18: -43,
  g1: -14,
  g2: -14,
  g3: -14,
  g4: -14,
  g5: -14,
  g6: -14,
  g7: -14,
  g8: -14,
  g9: -14,
  g10: -14,
  g11: -14,
  g12: -14,
  g13: -14,
  g14: -14,
  g15: -14,
  g16: -14,
  g17: -14,
  g18: -14,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.7,
  js2: 2.5,
  js3: 4.0,
  js4: 6.0,
  js5: 9.0,
  js6: 12.5,
  js7: 20.0,
  js8: 31.5,
  js9: 50.0,
  js10: 80.0,
  js11: 125.0,
  js12: 200.0,
  js13: 315.0,
  js14: 500.0,
  js15: 800.0,
  js16: 1250.0,
  js17: 2000.0,
  js18: 3150.0,
  j5: 7.0,
  j6: 14.0,
  j7: 22.0,
  k4: 15.0,
  k5: 21.0,
  k6: 28.0,
  k7: 43.0,
  k8: 63.0,
  k9: 100.0,
  k10: 160.0,
  k11: 250.0,
  k12: 400.0,
  k13: 630.0,
  k14: 1000.0,
  k15: 1600.0,
  k16: 2500.0,
  k17: 4000.0,
  k18: 6300.0,
  m1: 18.5,
  m2: 20.0,
  m3: 23.0,
  m4: 27.0,
  m5: 33.0,
  m6: 40.0,
  m7: 55.0,
  m8: 78.0,
  m9: 115.0,
  m10: 175.0,
  m11: 265.0,
  m12: 415.0,
  m13: 645.0,
  m14: 1015.0,
  m15: 1615.0,
  m16: 2515.0,
  m17: 4015.0,
  m18: 6315.0,
  n1: 30.5,
  n2: 32.0,
  n3: 35.0,
  n4: 39.0,
  n5: 45.0,
  n6: 52.0,
  n7: 67.0,
  n8: 90.0,
  n9: 127.0,
  n10: 187.0,
  n11: 277.0,
  n12: 427.0,
  n13: 657.0,
  n14: 1027.0,
  n15: 1627.0,
  n16: 2527.0,
  n17: 4027.0,
  n18: 6327.0,
  p1: 46.5,
  p2: 48.0,
  p3: 51.0,
  p4: 55.0,
  p5: 61.0,
  p6: 68.0,
  p7: 83.0,
  p8: 106.0,
  p9: 143.0,
  p10: 203.0,
  p11: 293.0,
  p12: 443.0,
  p13: 673.0,
  p14: 1043.0,
  p15: 1643.0,
  p16: 2543.0,
  p17: 4043.0,
  p18: 6343.0,
  r1: 68.5,
  r2: 70.0,
  r3: 73.0,
  r4: 77.0,
  r5: 83.0,
  r6: 90.0,
  r7: 105.0,
  r8: 128.0,
  r9: 165.0,
  r10: 225.0,
  r11: 315.0,
  r12: 465.0,
  r13: 695.0,
  r14: 1065.0,
  r15: 1665.0,
  r16: 2565.0,
  r17: 4065.0,
  r18: 6365.0,
  s1: 103.5,
  s2: 105.0,
  s3: 108.0,
  s4: 112.0,
  s5: 118.0,
  s6: 125.0,
  s7: 140.0,
  s8: 163.0,
  s9: 200.0,
  s10: 260.0,
  s11: 350.0,
  s12: 500.0,
  s13: 730.0,
  s14: 1100.0,
  s15: 1700.0,
  s16: 2600.0,
  s17: 4100.0,
  s18: 6400.0
}, {
  from: 160,
  to: 180,
  a1: -580,
  a2: -580,
  a3: -580,
  a4: -580,
  a5: -580,
  a6: -580,
  a7: -580,
  a8: -580,
  a9: -580,
  a10: -580,
  a11: -580,
  a12: -580,
  a13: -580,
  a14: -580,
  a15: -580,
  a16: -580,
  a17: -580,
  a18: -580,
  b1: -310,
  b2: -310,
  b3: -310,
  b4: -310,
  b5: -310,
  b6: -310,
  b7: -310,
  b8: -310,
  b9: -310,
  b10: -310,
  b11: -310,
  b12: -310,
  b13: -310,
  b14: -310,
  b15: -310,
  b16: -310,
  b17: -310,
  b18: -310,
  c1: -230,
  c2: -230,
  c3: -230,
  c4: -230,
  c5: -230,
  c6: -230,
  c7: -230,
  c8: -230,
  c9: -230,
  c10: -230,
  c11: -230,
  c12: -230,
  c13: -230,
  c14: -230,
  c15: -230,
  c16: -230,
  c17: -230,
  c18: -230,
  d1: -145,
  d2: -145,
  d3: -145,
  d4: -145,
  d5: -145,
  d6: -145,
  d7: -145,
  d8: -145,
  d9: -145,
  d10: -145,
  d11: -145,
  d12: -145,
  d13: -145,
  d14: -145,
  d15: -145,
  d16: -145,
  d17: -145,
  d18: -145,
  e1: -85,
  e2: -85,
  e3: -85,
  e4: -85,
  e5: -85,
  e6: -85,
  e7: -85,
  e8: -85,
  e9: -85,
  e10: -85,
  e11: -85,
  e12: -85,
  e13: -85,
  e14: -85,
  e15: -85,
  e16: -85,
  e17: -85,
  e18: -85,
  f1: -43,
  f2: -43,
  f3: -43,
  f4: -43,
  f5: -43,
  f6: -43,
  f7: -43,
  f8: -43,
  f9: -43,
  f10: -43,
  f11: -43,
  f12: -43,
  f13: -43,
  f14: -43,
  f15: -43,
  f16: -43,
  f17: -43,
  f18: -43,
  g1: -14,
  g2: -14,
  g3: -14,
  g4: -14,
  g5: -14,
  g6: -14,
  g7: -14,
  g8: -14,
  g9: -14,
  g10: -14,
  g11: -14,
  g12: -14,
  g13: -14,
  g14: -14,
  g15: -14,
  g16: -14,
  g17: -14,
  g18: -14,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 1.7,
  js2: 2.5,
  js3: 4.0,
  js4: 6.0,
  js5: 9.0,
  js6: 12.5,
  js7: 20.0,
  js8: 31.5,
  js9: 50.0,
  js10: 80.0,
  js11: 125.0,
  js12: 200.0,
  js13: 315.0,
  js14: 500.0,
  js15: 800.0,
  js16: 1250.0,
  js17: 2000.0,
  js18: 3150.0,
  j5: 7.0,
  j6: 14.0,
  j7: 22.0,
  k4: 15.0,
  k5: 21.0,
  k6: 28.0,
  k7: 43.0,
  k8: 63.0,
  k9: 100.0,
  k10: 160.0,
  k11: 250.0,
  k12: 400.0,
  k13: 630.0,
  k14: 1000.0,
  k15: 1600.0,
  k16: 2500.0,
  k17: 4000.0,
  k18: 6300.0,
  m1: 18.5,
  m2: 20.0,
  m3: 23.0,
  m4: 27.0,
  m5: 33.0,
  m6: 40.0,
  m7: 55.0,
  m8: 78.0,
  m9: 115.0,
  m10: 175.0,
  m11: 265.0,
  m12: 415.0,
  m13: 645.0,
  m14: 1015.0,
  m15: 1615.0,
  m16: 2515.0,
  m17: 4015.0,
  m18: 6315.0,
  n1: 30.5,
  n2: 32.0,
  n3: 35.0,
  n4: 39.0,
  n5: 45.0,
  n6: 52.0,
  n7: 67.0,
  n8: 90.0,
  n9: 127.0,
  n10: 187.0,
  n11: 277.0,
  n12: 427.0,
  n13: 657.0,
  n14: 1027.0,
  n15: 1627.0,
  n16: 2527.0,
  n17: 4027.0,
  n18: 6327.0,
  p1: 46.5,
  p2: 48.0,
  p3: 51.0,
  p4: 55.0,
  p5: 61.0,
  p6: 68.0,
  p7: 83.0,
  p8: 106.0,
  p9: 143.0,
  p10: 203.0,
  p11: 293.0,
  p12: 443.0,
  p13: 673.0,
  p14: 1043.0,
  p15: 1643.0,
  p16: 2543.0,
  p17: 4043.0,
  p18: 6343.0,
  r1: 71.5,
  r2: 73.0,
  r3: 76.0,
  r4: 80.0,
  r5: 86.0,
  r6: 93.0,
  r7: 108.0,
  r8: 131.0,
  r9: 168.0,
  r10: 228.0,
  r11: 318.0,
  r12: 468.0,
  r13: 698.0,
  r14: 1068.0,
  r15: 1668.0,
  r16: 2568.0,
  r17: 4068.0,
  r18: 6368.0,
  s1: 111.5,
  s2: 113.0,
  s3: 116.0,
  s4: 120.0,
  s5: 126.0,
  s6: 133.0,
  s7: 148.0,
  s8: 171.0,
  s9: 208.0,
  s10: 268.0,
  s11: 358.0,
  s12: 508.0,
  s13: 738.0,
  s14: 1108.0,
  s15: 1708.0,
  s16: 2608.0,
  s17: 4108.0,
  s18: 6408.0
}, {
  from: 180,
  to: 200,
  a1: -660,
  a2: -660,
  a3: -660,
  a4: -660,
  a5: -660,
  a6: -660,
  a7: -660,
  a8: -660,
  a9: -660,
  a10: -660,
  a11: -660,
  a12: -660,
  a13: -660,
  a14: -660,
  a15: -660,
  a16: -660,
  a17: -660,
  a18: -660,
  b1: -340,
  b2: -340,
  b3: -340,
  b4: -340,
  b5: -340,
  b6: -340,
  b7: -340,
  b8: -340,
  b9: -340,
  b10: -340,
  b11: -340,
  b12: -340,
  b13: -340,
  b14: -340,
  b15: -340,
  b16: -340,
  b17: -340,
  b18: -340,
  c1: -240,
  c2: -240,
  c3: -240,
  c4: -240,
  c5: -240,
  c6: -240,
  c7: -240,
  c8: -240,
  c9: -240,
  c10: -240,
  c11: -240,
  c12: -240,
  c13: -240,
  c14: -240,
  c15: -240,
  c16: -240,
  c17: -240,
  c18: -240,
  d1: -170,
  d2: -170,
  d3: -170,
  d4: -170,
  d5: -170,
  d6: -170,
  d7: -170,
  d8: -170,
  d9: -170,
  d10: -170,
  d11: -170,
  d12: -170,
  d13: -170,
  d14: -170,
  d15: -170,
  d16: -170,
  d17: -170,
  d18: -170,
  e1: -100,
  e2: -100,
  e3: -100,
  e4: -100,
  e5: -100,
  e6: -100,
  e7: -100,
  e8: -100,
  e9: -100,
  e10: -100,
  e11: -100,
  e12: -100,
  e13: -100,
  e14: -100,
  e15: -100,
  e16: -100,
  e17: -100,
  e18: -100,
  f1: -50,
  f2: -50,
  f3: -50,
  f4: -50,
  f5: -50,
  f6: -50,
  f7: -50,
  f8: -50,
  f9: -50,
  f10: -50,
  f11: -50,
  f12: -50,
  f13: -50,
  f14: -50,
  f15: -50,
  f16: -50,
  f17: -50,
  f18: -50,
  g1: -15,
  g2: -15,
  g3: -15,
  g4: -15,
  g5: -15,
  g6: -15,
  g7: -15,
  g8: -15,
  g9: -15,
  g10: -15,
  g11: -15,
  g12: -15,
  g13: -15,
  g14: -15,
  g15: -15,
  g16: -15,
  g17: -15,
  g18: -15,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 2.3,
  js2: 3.5,
  js3: 5.0,
  js4: 7.0,
  js5: 10.0,
  js6: 14.5,
  js7: 23.0,
  js8: 36.0,
  js9: 57.5,
  js10: 92.5,
  js11: 145.0,
  js12: 230.0,
  js13: 360.0,
  js14: 575.0,
  js15: 925.0,
  js16: 1450.0,
  js17: 2300.0,
  js18: 3600.0,
  j5: 7.0,
  j6: 16.0,
  j7: 25.0,
  k4: 18.0,
  k5: 24.0,
  k6: 33.0,
  k7: 50.0,
  k8: 72.0,
  k9: 115.0,
  k10: 185.0,
  k11: 290.0,
  k12: 460.0,
  k13: 720.0,
  k14: 1150.0,
  k15: 1850.0,
  k16: 2900.0,
  k17: 4600.0,
  k18: 7200.0,
  m1: 21.5,
  m2: 24.0,
  m3: 27.0,
  m4: 31.0,
  m5: 37.0,
  m6: 46.0,
  m7: 63.0,
  m8: 89.0,
  m9: 132.0,
  m10: 202.0,
  m11: 307.0,
  m12: 477.0,
  m13: 737.0,
  m14: 1167.0,
  m15: 1867.0,
  m16: 2917.0,
  m17: 4617.0,
  m18: 7217.0,
  n1: 35.5,
  n2: 38.0,
  n3: 41.0,
  n4: 45.0,
  n5: 51.0,
  n6: 60.0,
  n7: 77.0,
  n8: 103.0,
  n9: 146.0,
  n10: 216.0,
  n11: 321.0,
  n12: 491.0,
  n13: 751.0,
  n14: 1181.0,
  n15: 1881.0,
  n16: 2931.0,
  n17: 4631.0,
  n18: 7231.0,
  p1: 54.5,
  p2: 57.0,
  p3: 60.0,
  p4: 64.0,
  p5: 70.0,
  p6: 79.0,
  p7: 96.0,
  p8: 122.0,
  p9: 165.0,
  p10: 235.0,
  p11: 340.0,
  p12: 510.0,
  p13: 770.0,
  p14: 1200.0,
  p15: 1900.0,
  p16: 2950.0,
  p17: 4650.0,
  p18: 7250.0,
  r1: 81.5,
  r2: 84.0,
  r3: 87.0,
  r4: 91.0,
  r5: 97.0,
  r6: 106.0,
  r7: 123.0,
  r8: 149.0,
  r9: 192.0,
  r10: 262.0,
  r11: 367.0,
  r12: 537.0,
  r13: 797.0,
  r14: 1227.0,
  r15: 1927.0,
  r16: 2977.0,
  r17: 4677.0,
  r18: 7277.0,
  s1: 126.5,
  s2: 129.0,
  s3: 132.0,
  s4: 136.0,
  s5: 142.0,
  s6: 151.0,
  s7: 168.0,
  s8: 194.0,
  s9: 237.0,
  s10: 307.0,
  s11: 412.0,
  s12: 582.0,
  s13: 842.0,
  s14: 1272.0,
  s15: 1972.0,
  s16: 3022.0,
  s17: 4722.0,
  s18: 7322.0
}, {
  from: 200,
  to: 225,
  a1: -740,
  a2: -740,
  a3: -740,
  a4: -740,
  a5: -740,
  a6: -740,
  a7: -740,
  a8: -740,
  a9: -740,
  a10: -740,
  a11: -740,
  a12: -740,
  a13: -740,
  a14: -740,
  a15: -740,
  a16: -740,
  a17: -740,
  a18: -740,
  b1: -380,
  b2: -380,
  b3: -380,
  b4: -380,
  b5: -380,
  b6: -380,
  b7: -380,
  b8: -380,
  b9: -380,
  b10: -380,
  b11: -380,
  b12: -380,
  b13: -380,
  b14: -380,
  b15: -380,
  b16: -380,
  b17: -380,
  b18: -380,
  c1: -260,
  c2: -260,
  c3: -260,
  c4: -260,
  c5: -260,
  c6: -260,
  c7: -260,
  c8: -260,
  c9: -260,
  c10: -260,
  c11: -260,
  c12: -260,
  c13: -260,
  c14: -260,
  c15: -260,
  c16: -260,
  c17: -260,
  c18: -260,
  d1: -170,
  d2: -170,
  d3: -170,
  d4: -170,
  d5: -170,
  d6: -170,
  d7: -170,
  d8: -170,
  d9: -170,
  d10: -170,
  d11: -170,
  d12: -170,
  d13: -170,
  d14: -170,
  d15: -170,
  d16: -170,
  d17: -170,
  d18: -170,
  e1: -100,
  e2: -100,
  e3: -100,
  e4: -100,
  e5: -100,
  e6: -100,
  e7: -100,
  e8: -100,
  e9: -100,
  e10: -100,
  e11: -100,
  e12: -100,
  e13: -100,
  e14: -100,
  e15: -100,
  e16: -100,
  e17: -100,
  e18: -100,
  f1: -50,
  f2: -50,
  f3: -50,
  f4: -50,
  f5: -50,
  f6: -50,
  f7: -50,
  f8: -50,
  f9: -50,
  f10: -50,
  f11: -50,
  f12: -50,
  f13: -50,
  f14: -50,
  f15: -50,
  f16: -50,
  f17: -50,
  f18: -50,
  g1: -15,
  g2: -15,
  g3: -15,
  g4: -15,
  g5: -15,
  g6: -15,
  g7: -15,
  g8: -15,
  g9: -15,
  g10: -15,
  g11: -15,
  g12: -15,
  g13: -15,
  g14: -15,
  g15: -15,
  g16: -15,
  g17: -15,
  g18: -15,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 2.3,
  js2: 3.5,
  js3: 5.0,
  js4: 7.0,
  js5: 10.0,
  js6: 14.5,
  js7: 23.0,
  js8: 36.0,
  js9: 57.5,
  js10: 92.5,
  js11: 145.0,
  js12: 230.0,
  js13: 360.0,
  js14: 575.0,
  js15: 925.0,
  js16: 1450.0,
  js17: 2300.0,
  js18: 3600.0,
  j5: 7.0,
  j6: 16.0,
  j7: 25.0,
  k4: 18.0,
  k5: 24.0,
  k6: 33.0,
  k7: 50.0,
  k8: 72.0,
  k9: 115.0,
  k10: 185.0,
  k11: 290.0,
  k12: 460.0,
  k13: 720.0,
  k14: 1150.0,
  k15: 1850.0,
  k16: 2900.0,
  k17: 4600.0,
  k18: 7200.0,
  m1: 21.5,
  m2: 24.0,
  m3: 27.0,
  m4: 31.0,
  m5: 37.0,
  m6: 46.0,
  m7: 63.0,
  m8: 89.0,
  m9: 132.0,
  m10: 202.0,
  m11: 307.0,
  m12: 477.0,
  m13: 737.0,
  m14: 1167.0,
  m15: 1867.0,
  m16: 2917.0,
  m17: 4617.0,
  m18: 7217.0,
  n1: 35.5,
  n2: 38.0,
  n3: 41.0,
  n4: 45.0,
  n5: 51.0,
  n6: 60.0,
  n7: 77.0,
  n8: 103.0,
  n9: 146.0,
  n10: 216.0,
  n11: 321.0,
  n12: 491.0,
  n13: 751.0,
  n14: 1181.0,
  n15: 1881.0,
  n16: 2931.0,
  n17: 4631.0,
  n18: 7231.0,
  p1: 54.5,
  p2: 57.0,
  p3: 60.0,
  p4: 64.0,
  p5: 70.0,
  p6: 79.0,
  p7: 96.0,
  p8: 122.0,
  p9: 165.0,
  p10: 235.0,
  p11: 340.0,
  p12: 510.0,
  p13: 770.0,
  p14: 1200.0,
  p15: 1900.0,
  p16: 2950.0,
  p17: 4650.0,
  p18: 7250.0,
  r1: 84.5,
  r2: 87.0,
  r3: 90.0,
  r4: 94.0,
  r5: 100.0,
  r6: 109.0,
  r7: 126.0,
  r8: 152.0,
  r9: 195.0,
  r10: 265.0,
  r11: 370.0,
  r12: 540.0,
  r13: 800.0,
  r14: 1230.0,
  r15: 1930.0,
  r16: 2980.0,
  r17: 4680.0,
  r18: 7280.0,
  s1: 134.5,
  s2: 137.0,
  s3: 140.0,
  s4: 144.0,
  s5: 150.0,
  s6: 159.0,
  s7: 176.0,
  s8: 202.0,
  s9: 245.0,
  s10: 315.0,
  s11: 420.0,
  s12: 590.0,
  s13: 850.0,
  s14: 1280.0,
  s15: 1980.0,
  s16: 3030.0,
  s17: 4730.0,
  s18: 7330.0
}, {
  from: 225,
  to: 250,
  a1: -820,
  a2: -820,
  a3: -820,
  a4: -820,
  a5: -820,
  a6: -820,
  a7: -820,
  a8: -820,
  a9: -820,
  a10: -820,
  a11: -820,
  a12: -820,
  a13: -820,
  a14: -820,
  a15: -820,
  a16: -820,
  a17: -820,
  a18: -820,
  b1: -420,
  b2: -420,
  b3: -420,
  b4: -420,
  b5: -420,
  b6: -420,
  b7: -420,
  b8: -420,
  b9: -420,
  b10: -420,
  b11: -420,
  b12: -420,
  b13: -420,
  b14: -420,
  b15: -420,
  b16: -420,
  b17: -420,
  b18: -420,
  c1: -280,
  c2: -280,
  c3: -280,
  c4: -280,
  c5: -280,
  c6: -280,
  c7: -280,
  c8: -280,
  c9: -280,
  c10: -280,
  c11: -280,
  c12: -280,
  c13: -280,
  c14: -280,
  c15: -280,
  c16: -280,
  c17: -280,
  c18: -280,
  d1: -170,
  d2: -170,
  d3: -170,
  d4: -170,
  d5: -170,
  d6: -170,
  d7: -170,
  d8: -170,
  d9: -170,
  d10: -170,
  d11: -170,
  d12: -170,
  d13: -170,
  d14: -170,
  d15: -170,
  d16: -170,
  d17: -170,
  d18: -170,
  e1: -100,
  e2: -100,
  e3: -100,
  e4: -100,
  e5: -100,
  e6: -100,
  e7: -100,
  e8: -100,
  e9: -100,
  e10: -100,
  e11: -100,
  e12: -100,
  e13: -100,
  e14: -100,
  e15: -100,
  e16: -100,
  e17: -100,
  e18: -100,
  f1: -50,
  f2: -50,
  f3: -50,
  f4: -50,
  f5: -50,
  f6: -50,
  f7: -50,
  f8: -50,
  f9: -50,
  f10: -50,
  f11: -50,
  f12: -50,
  f13: -50,
  f14: -50,
  f15: -50,
  f16: -50,
  f17: -50,
  f18: -50,
  g1: -15,
  g2: -15,
  g3: -15,
  g4: -15,
  g5: -15,
  g6: -15,
  g7: -15,
  g8: -15,
  g9: -15,
  g10: -15,
  g11: -15,
  g12: -15,
  g13: -15,
  g14: -15,
  g15: -15,
  g16: -15,
  g17: -15,
  g18: -15,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 2.3,
  js2: 3.5,
  js3: 5.0,
  js4: 7.0,
  js5: 10.0,
  js6: 14.5,
  js7: 23.0,
  js8: 36.0,
  js9: 57.5,
  js10: 92.5,
  js11: 145.0,
  js12: 230.0,
  js13: 360.0,
  js14: 575.0,
  js15: 925.0,
  js16: 1450.0,
  js17: 2300.0,
  js18: 3600.0,
  j5: 7.0,
  j6: 16.0,
  j7: 25.0,
  k4: 18.0,
  k5: 24.0,
  k6: 33.0,
  k7: 50.0,
  k8: 72.0,
  k9: 115.0,
  k10: 185.0,
  k11: 290.0,
  k12: 460.0,
  k13: 720.0,
  k14: 1150.0,
  k15: 1850.0,
  k16: 2900.0,
  k17: 4600.0,
  k18: 7200.0,
  m1: 21.5,
  m2: 24.0,
  m3: 27.0,
  m4: 31.0,
  m5: 37.0,
  m6: 46.0,
  m7: 63.0,
  m8: 89.0,
  m9: 132.0,
  m10: 202.0,
  m11: 307.0,
  m12: 477.0,
  m13: 737.0,
  m14: 1167.0,
  m15: 1867.0,
  m16: 2917.0,
  m17: 4617.0,
  m18: 7217.0,
  n1: 35.5,
  n2: 38.0,
  n3: 41.0,
  n4: 45.0,
  n5: 51.0,
  n6: 60.0,
  n7: 77.0,
  n8: 103.0,
  n9: 146.0,
  n10: 216.0,
  n11: 321.0,
  n12: 491.0,
  n13: 751.0,
  n14: 1181.0,
  n15: 1881.0,
  n16: 2931.0,
  n17: 4631.0,
  n18: 7231.0,
  p1: 54.5,
  p2: 57.0,
  p3: 60.0,
  p4: 64.0,
  p5: 70.0,
  p6: 79.0,
  p7: 96.0,
  p8: 122.0,
  p9: 165.0,
  p10: 235.0,
  p11: 340.0,
  p12: 510.0,
  p13: 770.0,
  p14: 1200.0,
  p15: 1900.0,
  p16: 2950.0,
  p17: 4650.0,
  p18: 7250.0,
  r1: 88.5,
  r2: 91.0,
  r3: 94.0,
  r4: 98.0,
  r5: 104.0,
  r6: 113.0,
  r7: 130.0,
  r8: 156.0,
  r9: 199.0,
  r10: 269.0,
  r11: 374.0,
  r12: 544.0,
  r13: 804.0,
  r14: 1234.0,
  r15: 1934.0,
  r16: 2984.0,
  r17: 4684.0,
  r18: 7284.0,
  s1: 144.5,
  s2: 147.0,
  s3: 150.0,
  s4: 154.0,
  s5: 160.0,
  s6: 169.0,
  s7: 186.0,
  s8: 212.0,
  s9: 255.0,
  s10: 325.0,
  s11: 430.0,
  s12: 600.0,
  s13: 860.0,
  s14: 1290.0,
  s15: 1990.0,
  s16: 3040.0,
  s17: 4740.0,
  s18: 7340.0
}, {
  from: 250,
  to: 280,
  a1: -920,
  a2: -920,
  a3: -920,
  a4: -920,
  a5: -920,
  a6: -920,
  a7: -920,
  a8: -920,
  a9: -920,
  a10: -920,
  a11: -920,
  a12: -920,
  a13: -920,
  a14: -920,
  a15: -920,
  a16: -920,
  a17: -920,
  a18: -920,
  b1: -480,
  b2: -480,
  b3: -480,
  b4: -480,
  b5: -480,
  b6: -480,
  b7: -480,
  b8: -480,
  b9: -480,
  b10: -480,
  b11: -480,
  b12: -480,
  b13: -480,
  b14: -480,
  b15: -480,
  b16: -480,
  b17: -480,
  b18: -480,
  c1: -300,
  c2: -300,
  c3: -300,
  c4: -300,
  c5: -300,
  c6: -300,
  c7: -300,
  c8: -300,
  c9: -300,
  c10: -300,
  c11: -300,
  c12: -300,
  c13: -300,
  c14: -300,
  c15: -300,
  c16: -300,
  c17: -300,
  c18: -300,
  d1: -190,
  d2: -190,
  d3: -190,
  d4: -190,
  d5: -190,
  d6: -190,
  d7: -190,
  d8: -190,
  d9: -190,
  d10: -190,
  d11: -190,
  d12: -190,
  d13: -190,
  d14: -190,
  d15: -190,
  d16: -190,
  d17: -190,
  d18: -190,
  e1: -110,
  e2: -110,
  e3: -110,
  e4: -110,
  e5: -110,
  e6: -110,
  e7: -110,
  e8: -110,
  e9: -110,
  e10: -110,
  e11: -110,
  e12: -110,
  e13: -110,
  e14: -110,
  e15: -110,
  e16: -110,
  e17: -110,
  e18: -110,
  f1: -56,
  f2: -56,
  f3: -56,
  f4: -56,
  f5: -56,
  f6: -56,
  f7: -56,
  f8: -56,
  f9: -56,
  f10: -56,
  f11: -56,
  f12: -56,
  f13: -56,
  f14: -56,
  f15: -56,
  f16: -56,
  f17: -56,
  f18: -56,
  g1: -17,
  g2: -17,
  g3: -17,
  g4: -17,
  g5: -17,
  g6: -17,
  g7: -17,
  g8: -17,
  g9: -17,
  g10: -17,
  g11: -17,
  g12: -17,
  g13: -17,
  g14: -17,
  g15: -17,
  g16: -17,
  g17: -17,
  g18: -17,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 3.0,
  js2: 4.0,
  js3: 6.0,
  js4: 8.0,
  js5: 11.5,
  js6: 16.0,
  js7: 26.0,
  js8: 40.5,
  js9: 65.0,
  js10: 105.0,
  js11: 160.0,
  js12: 260.0,
  js13: 405.0,
  js14: 650.0,
  js15: 1050.0,
  js16: 1600.0,
  js17: 2600.0,
  js18: 4050.0,
  j5: 7.0,
  j6: 16.0,
  j7: 26.0,
  k4: 20.0,
  k5: 27.0,
  k6: 36.0,
  k7: 56.0,
  k8: 81.0,
  k9: 130.0,
  k10: 210.0,
  k11: 320.0,
  k12: 520.0,
  k13: 810.0,
  k14: 1300.0,
  k15: 2100.0,
  k16: 3200.0,
  k17: 5200.0,
  k18: 8100.0,
  m1: 26.0,
  m2: 28.0,
  m3: 32.0,
  m4: 36.0,
  m5: 43.0,
  m6: 52.0,
  m7: 72.0,
  m8: 101.0,
  m9: 150.0,
  m10: 230.0,
  m11: 340.0,
  m12: 540.0,
  m13: 830.0,
  m14: 1320.0,
  m15: 2120.0,
  m16: 3220.0,
  m17: 5220.0,
  m18: 8120.0,
  n1: 40.0,
  n2: 42.0,
  n3: 46.0,
  n4: 50.0,
  n5: 57.0,
  n6: 66.0,
  n7: 86.0,
  n8: 115.0,
  n9: 164.0,
  n10: 244.0,
  n11: 354.0,
  n12: 554.0,
  n13: 844.0,
  n14: 1334.0,
  n15: 2134.0,
  n16: 3234.0,
  n17: 5234.0,
  n18: 8134.0,
  p1: 62.0,
  p2: 64.0,
  p3: 68.0,
  p4: 72.0,
  p5: 79.0,
  p6: 88.0,
  p7: 108.0,
  p8: 137.0,
  p9: 186.0,
  p10: 266.0,
  p11: 376.0,
  p12: 576.0,
  p13: 866.0,
  p14: 1356.0,
  p15: 2156.0,
  p16: 3256.0,
  p17: 5256.0,
  p18: 8156.0,
  r1: 100.0,
  r2: 102.0,
  r3: 106.0,
  r4: 110.0,
  r5: 117.0,
  r6: 126.0,
  r7: 146.0,
  r8: 175.0,
  r9: 224.0,
  r10: 304.0,
  r11: 414.0,
  r12: 614.0,
  r13: 904.0,
  r14: 1394.0,
  r15: 2194.0,
  r16: 3294.0,
  r17: 5294.0,
  r18: 8194.0,
  s1: 164.0,
  s2: 166.0,
  s3: 170.0,
  s4: 174.0,
  s5: 181.0,
  s6: 190.0,
  s7: 210.0,
  s8: 239.0,
  s9: 288.0,
  s10: 368.0,
  s11: 478.0,
  s12: 678.0,
  s13: 968.0,
  s14: 1458.0,
  s15: 2258.0,
  s16: 3358.0,
  s17: 5358.0,
  s18: 8258.0
}, {
  from: 280,
  to: 315,
  a1: -1050,
  a2: -1050,
  a3: -1050,
  a4: -1050,
  a5: -1050,
  a6: -1050,
  a7: -1050,
  a8: -1050,
  a9: -1050,
  a10: -1050,
  a11: -1050,
  a12: -1050,
  a13: -1050,
  a14: -1050,
  a15: -1050,
  a16: -1050,
  a17: -1050,
  a18: -1050,
  b1: -540,
  b2: -540,
  b3: -540,
  b4: -540,
  b5: -540,
  b6: -540,
  b7: -540,
  b8: -540,
  b9: -540,
  b10: -540,
  b11: -540,
  b12: -540,
  b13: -540,
  b14: -540,
  b15: -540,
  b16: -540,
  b17: -540,
  b18: -540,
  c1: -330,
  c2: -330,
  c3: -330,
  c4: -330,
  c5: -330,
  c6: -330,
  c7: -330,
  c8: -330,
  c9: -330,
  c10: -330,
  c11: -330,
  c12: -330,
  c13: -330,
  c14: -330,
  c15: -330,
  c16: -330,
  c17: -330,
  c18: -330,
  d1: -190,
  d2: -190,
  d3: -190,
  d4: -190,
  d5: -190,
  d6: -190,
  d7: -190,
  d8: -190,
  d9: -190,
  d10: -190,
  d11: -190,
  d12: -190,
  d13: -190,
  d14: -190,
  d15: -190,
  d16: -190,
  d17: -190,
  d18: -190,
  e1: -110,
  e2: -110,
  e3: -110,
  e4: -110,
  e5: -110,
  e6: -110,
  e7: -110,
  e8: -110,
  e9: -110,
  e10: -110,
  e11: -110,
  e12: -110,
  e13: -110,
  e14: -110,
  e15: -110,
  e16: -110,
  e17: -110,
  e18: -110,
  f1: -56,
  f2: -56,
  f3: -56,
  f4: -56,
  f5: -56,
  f6: -56,
  f7: -56,
  f8: -56,
  f9: -56,
  f10: -56,
  f11: -56,
  f12: -56,
  f13: -56,
  f14: -56,
  f15: -56,
  f16: -56,
  f17: -56,
  f18: -56,
  g1: -17,
  g2: -17,
  g3: -17,
  g4: -17,
  g5: -17,
  g6: -17,
  g7: -17,
  g8: -17,
  g9: -17,
  g10: -17,
  g11: -17,
  g12: -17,
  g13: -17,
  g14: -17,
  g15: -17,
  g16: -17,
  g17: -17,
  g18: -17,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 3.0,
  js2: 4.0,
  js3: 6.0,
  js4: 8.0,
  js5: 11.5,
  js6: 16.0,
  js7: 26.0,
  js8: 40.5,
  js9: 65.0,
  js10: 105.0,
  js11: 160.0,
  js12: 260.0,
  js13: 405.0,
  js14: 650.0,
  js15: 1050.0,
  js16: 1600.0,
  js17: 2600.0,
  js18: 4050.0,
  j5: 7.0,
  j6: 16.0,
  j7: 26.0,
  k4: 20.0,
  k5: 27.0,
  k6: 36.0,
  k7: 56.0,
  k8: 81.0,
  k9: 130.0,
  k10: 210.0,
  k11: 320.0,
  k12: 520.0,
  k13: 810.0,
  k14: 1300.0,
  k15: 2100.0,
  k16: 3200.0,
  k17: 5200.0,
  k18: 8100.0,
  m1: 26.0,
  m2: 28.0,
  m3: 32.0,
  m4: 36.0,
  m5: 43.0,
  m6: 52.0,
  m7: 72.0,
  m8: 101.0,
  m9: 150.0,
  m10: 230.0,
  m11: 340.0,
  m12: 540.0,
  m13: 830.0,
  m14: 1320.0,
  m15: 2120.0,
  m16: 3220.0,
  m17: 5220.0,
  m18: 8120.0,
  n1: 40.0,
  n2: 42.0,
  n3: 46.0,
  n4: 50.0,
  n5: 57.0,
  n6: 66.0,
  n7: 86.0,
  n8: 115.0,
  n9: 164.0,
  n10: 244.0,
  n11: 354.0,
  n12: 554.0,
  n13: 844.0,
  n14: 1334.0,
  n15: 2134.0,
  n16: 3234.0,
  n17: 5234.0,
  n18: 8134.0,
  p1: 62.0,
  p2: 64.0,
  p3: 68.0,
  p4: 72.0,
  p5: 79.0,
  p6: 88.0,
  p7: 108.0,
  p8: 137.0,
  p9: 186.0,
  p10: 266.0,
  p11: 376.0,
  p12: 576.0,
  p13: 866.0,
  p14: 1356.0,
  p15: 2156.0,
  p16: 3256.0,
  p17: 5256.0,
  p18: 8156.0,
  r1: 104.0,
  r2: 106.0,
  r3: 110.0,
  r4: 114.0,
  r5: 121.0,
  r6: 130.0,
  r7: 150.0,
  r8: 179.0,
  r9: 228.0,
  r10: 308.0,
  r11: 418.0,
  r12: 618.0,
  r13: 908.0,
  r14: 1398.0,
  r15: 2198.0,
  r16: 3298.0,
  r17: 5298.0,
  r18: 8198.0,
  s1: 176.0,
  s2: 178.0,
  s3: 182.0,
  s4: 186.0,
  s5: 193.0,
  s6: 202.0,
  s7: 222.0,
  s8: 251.0,
  s9: 300.0,
  s10: 380.0,
  s11: 490.0,
  s12: 690.0,
  s13: 980.0,
  s14: 1470.0,
  s15: 2270.0,
  s16: 3370.0,
  s17: 5370.0,
  s18: 8270.0
}, {
  from: 315,
  to: 355,
  a1: -1200,
  a2: -1200,
  a3: -1200,
  a4: -1200,
  a5: -1200,
  a6: -1200,
  a7: -1200,
  a8: -1200,
  a9: -1200,
  a10: -1200,
  a11: -1200,
  a12: -1200,
  a13: -1200,
  a14: -1200,
  a15: -1200,
  a16: -1200,
  a17: -1200,
  a18: -1200,
  b1: -600,
  b2: -600,
  b3: -600,
  b4: -600,
  b5: -600,
  b6: -600,
  b7: -600,
  b8: -600,
  b9: -600,
  b10: -600,
  b11: -600,
  b12: -600,
  b13: -600,
  b14: -600,
  b15: -600,
  b16: -600,
  b17: -600,
  b18: -600,
  c1: -360,
  c2: -360,
  c3: -360,
  c4: -360,
  c5: -360,
  c6: -360,
  c7: -360,
  c8: -360,
  c9: -360,
  c10: -360,
  c11: -360,
  c12: -360,
  c13: -360,
  c14: -360,
  c15: -360,
  c16: -360,
  c17: -360,
  c18: -360,
  d1: -210,
  d2: -210,
  d3: -210,
  d4: -210,
  d5: -210,
  d6: -210,
  d7: -210,
  d8: -210,
  d9: -210,
  d10: -210,
  d11: -210,
  d12: -210,
  d13: -210,
  d14: -210,
  d15: -210,
  d16: -210,
  d17: -210,
  d18: -210,
  e1: -125,
  e2: -125,
  e3: -125,
  e4: -125,
  e5: -125,
  e6: -125,
  e7: -125,
  e8: -125,
  e9: -125,
  e10: -125,
  e11: -125,
  e12: -125,
  e13: -125,
  e14: -125,
  e15: -125,
  e16: -125,
  e17: -125,
  e18: -125,
  f1: -62,
  f2: -62,
  f3: -62,
  f4: -62,
  f5: -62,
  f6: -62,
  f7: -62,
  f8: -62,
  f9: -62,
  f10: -62,
  f11: -62,
  f12: -62,
  f13: -62,
  f14: -62,
  f15: -62,
  f16: -62,
  f17: -62,
  f18: -62,
  g1: -18,
  g2: -18,
  g3: -18,
  g4: -18,
  g5: -18,
  g6: -18,
  g7: -18,
  g8: -18,
  g9: -18,
  g10: -18,
  g11: -18,
  g12: -18,
  g13: -18,
  g14: -18,
  g15: -18,
  g16: -18,
  g17: -18,
  g18: -18,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 3.5,
  js2: 4.5,
  js3: 6.5,
  js4: 9.0,
  js5: 12.5,
  js6: 18.0,
  js7: 28.5,
  js8: 44.5,
  js9: 70.0,
  js10: 115.0,
  js11: 180.0,
  js12: 285.0,
  js13: 445.0,
  js14: 700.0,
  js15: 1150.0,
  js16: 1800.0,
  js17: 2850.0,
  js18: 4450.0,
  j5: 7.0,
  j6: 18.0,
  j7: 29.0,
  k4: 22.0,
  k5: 29.0,
  k6: 40.0,
  k7: 61.0,
  k8: 89.0,
  k9: 140.0,
  k10: 230.0,
  k11: 360.0,
  k12: 570.0,
  k13: 890.0,
  k14: 1400.0,
  k15: 2300.0,
  k16: 3600.0,
  k17: 5700.0,
  k18: 8900.0,
  m1: 28.0,
  m2: 30.0,
  m3: 34.0,
  m4: 39.0,
  m5: 46.0,
  m6: 57.0,
  m7: 78.0,
  m8: 110.0,
  m9: 161.0,
  m10: 251.0,
  m11: 381.0,
  m12: 591.0,
  m13: 911.0,
  m14: 1421.0,
  m15: 2321.0,
  m16: 3621.0,
  m17: 5721.0,
  m18: 8921.0,
  n1: 44.0,
  n2: 46.0,
  n3: 50.0,
  n4: 55.0,
  n5: 62.0,
  n6: 73.0,
  n7: 94.0,
  n8: 126.0,
  n9: 177.0,
  n10: 267.0,
  n11: 397.0,
  n12: 607.0,
  n13: 927.0,
  n14: 1437.0,
  n15: 2337.0,
  n16: 3637.0,
  n17: 5737.0,
  n18: 8937.0,
  p1: 69.0,
  p2: 71.0,
  p3: 75.0,
  p4: 80.0,
  p5: 87.0,
  p6: 98.0,
  p7: 119.0,
  p8: 151.0,
  p9: 202.0,
  p10: 292.0,
  p11: 422.0,
  p12: 632.0,
  p13: 952.0,
  p14: 1462.0,
  p15: 2362.0,
  p16: 3662.0,
  p17: 5762.0,
  p18: 8962.0,
  r1: 115.0,
  r2: 117.0,
  r3: 121.0,
  r4: 126.0,
  r5: 133.0,
  r6: 144.0,
  r7: 165.0,
  r8: 197.0,
  r9: 248.0,
  r10: 338.0,
  r11: 468.0,
  r12: 678.0,
  r13: 998.0,
  r14: 1508.0,
  r15: 2408.0,
  r16: 3708.0,
  r17: 5808.0,
  r18: 9008.0,
  s1: 197.0,
  s2: 199.0,
  s3: 203.0,
  s4: 208.0,
  s5: 215.0,
  s6: 226.0,
  s7: 247.0,
  s8: 279.0,
  s9: 330.0,
  s10: 420.0,
  s11: 550.0,
  s12: 760.0,
  s13: 1080.0,
  s14: 1590.0,
  s15: 2490.0,
  s16: 3790.0,
  s17: 5890.0,
  s18: 9090.0
}, {
  from: 355,
  to: 400,
  a1: -1350,
  a2: -1350,
  a3: -1350,
  a4: -1350,
  a5: -1350,
  a6: -1350,
  a7: -1350,
  a8: -1350,
  a9: -1350,
  a10: -1350,
  a11: -1350,
  a12: -1350,
  a13: -1350,
  a14: -1350,
  a15: -1350,
  a16: -1350,
  a17: -1350,
  a18: -1350,
  b1: -680,
  b2: -680,
  b3: -680,
  b4: -680,
  b5: -680,
  b6: -680,
  b7: -680,
  b8: -680,
  b9: -680,
  b10: -680,
  b11: -680,
  b12: -680,
  b13: -680,
  b14: -680,
  b15: -680,
  b16: -680,
  b17: -680,
  b18: -680,
  c1: -400,
  c2: -400,
  c3: -400,
  c4: -400,
  c5: -400,
  c6: -400,
  c7: -400,
  c8: -400,
  c9: -400,
  c10: -400,
  c11: -400,
  c12: -400,
  c13: -400,
  c14: -400,
  c15: -400,
  c16: -400,
  c17: -400,
  c18: -400,
  d1: -210,
  d2: -210,
  d3: -210,
  d4: -210,
  d5: -210,
  d6: -210,
  d7: -210,
  d8: -210,
  d9: -210,
  d10: -210,
  d11: -210,
  d12: -210,
  d13: -210,
  d14: -210,
  d15: -210,
  d16: -210,
  d17: -210,
  d18: -210,
  e1: -125,
  e2: -125,
  e3: -125,
  e4: -125,
  e5: -125,
  e6: -125,
  e7: -125,
  e8: -125,
  e9: -125,
  e10: -125,
  e11: -125,
  e12: -125,
  e13: -125,
  e14: -125,
  e15: -125,
  e16: -125,
  e17: -125,
  e18: -125,
  f1: -62,
  f2: -62,
  f3: -62,
  f4: -62,
  f5: -62,
  f6: -62,
  f7: -62,
  f8: -62,
  f9: -62,
  f10: -62,
  f11: -62,
  f12: -62,
  f13: -62,
  f14: -62,
  f15: -62,
  f16: -62,
  f17: -62,
  f18: -62,
  g1: -18,
  g2: -18,
  g3: -18,
  g4: -18,
  g5: -18,
  g6: -18,
  g7: -18,
  g8: -18,
  g9: -18,
  g10: -18,
  g11: -18,
  g12: -18,
  g13: -18,
  g14: -18,
  g15: -18,
  g16: -18,
  g17: -18,
  g18: -18,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 3.5,
  js2: 4.5,
  js3: 6.5,
  js4: 9.0,
  js5: 12.5,
  js6: 18.0,
  js7: 28.5,
  js8: 44.5,
  js9: 70.0,
  js10: 115.0,
  js11: 180.0,
  js12: 285.0,
  js13: 445.0,
  js14: 700.0,
  js15: 1150.0,
  js16: 1800.0,
  js17: 2850.0,
  js18: 4450.0,
  j5: 7.0,
  j6: 18.0,
  j7: 29.0,
  k4: 22.0,
  k5: 29.0,
  k6: 40.0,
  k7: 61.0,
  k8: 89.0,
  k9: 140.0,
  k10: 230.0,
  k11: 360.0,
  k12: 570.0,
  k13: 890.0,
  k14: 1400.0,
  k15: 2300.0,
  k16: 3600.0,
  k17: 5700.0,
  k18: 8900.0,
  m1: 28.0,
  m2: 30.0,
  m3: 34.0,
  m4: 39.0,
  m5: 46.0,
  m6: 57.0,
  m7: 78.0,
  m8: 110.0,
  m9: 161.0,
  m10: 251.0,
  m11: 381.0,
  m12: 591.0,
  m13: 911.0,
  m14: 1421.0,
  m15: 2321.0,
  m16: 3621.0,
  m17: 5721.0,
  m18: 8921.0,
  n1: 44.0,
  n2: 46.0,
  n3: 50.0,
  n4: 55.0,
  n5: 62.0,
  n6: 73.0,
  n7: 94.0,
  n8: 126.0,
  n9: 177.0,
  n10: 267.0,
  n11: 397.0,
  n12: 607.0,
  n13: 927.0,
  n14: 1437.0,
  n15: 2337.0,
  n16: 3637.0,
  n17: 5737.0,
  n18: 8937.0,
  p1: 69.0,
  p2: 71.0,
  p3: 75.0,
  p4: 80.0,
  p5: 87.0,
  p6: 98.0,
  p7: 119.0,
  p8: 151.0,
  p9: 202.0,
  p10: 292.0,
  p11: 422.0,
  p12: 632.0,
  p13: 952.0,
  p14: 1462.0,
  p15: 2362.0,
  p16: 3662.0,
  p17: 5762.0,
  p18: 8962.0,
  r1: 121.0,
  r2: 123.0,
  r3: 127.0,
  r4: 132.0,
  r5: 139.0,
  r6: 150.0,
  r7: 171.0,
  r8: 203.0,
  r9: 254.0,
  r10: 344.0,
  r11: 474.0,
  r12: 684.0,
  r13: 1004.0,
  r14: 1514.0,
  r15: 2414.0,
  r16: 3714.0,
  r17: 5814.0,
  r18: 9014.0,
  s1: 215.0,
  s2: 217.0,
  s3: 221.0,
  s4: 226.0,
  s5: 233.0,
  s6: 244.0,
  s7: 265.0,
  s8: 297.0,
  s9: 348.0,
  s10: 438.0,
  s11: 568.0,
  s12: 778.0,
  s13: 1098.0,
  s14: 1608.0,
  s15: 2508.0,
  s16: 3808.0,
  s17: 5908.0,
  s18: 9108.0
}, {
  from: 400,
  to: 450,
  a1: -1500,
  a2: -1500,
  a3: -1500,
  a4: -1500,
  a5: -1500,
  a6: -1500,
  a7: -1500,
  a8: -1500,
  a9: -1500,
  a10: -1500,
  a11: -1500,
  a12: -1500,
  a13: -1500,
  a14: -1500,
  a15: -1500,
  a16: -1500,
  a17: -1500,
  a18: -1500,
  b1: -760,
  b2: -760,
  b3: -760,
  b4: -760,
  b5: -760,
  b6: -760,
  b7: -760,
  b8: -760,
  b9: -760,
  b10: -760,
  b11: -760,
  b12: -760,
  b13: -760,
  b14: -760,
  b15: -760,
  b16: -760,
  b17: -760,
  b18: -760,
  c1: -440,
  c2: -440,
  c3: -440,
  c4: -440,
  c5: -440,
  c6: -440,
  c7: -440,
  c8: -440,
  c9: -440,
  c10: -440,
  c11: -440,
  c12: -440,
  c13: -440,
  c14: -440,
  c15: -440,
  c16: -440,
  c17: -440,
  c18: -440,
  d1: -230,
  d2: -230,
  d3: -230,
  d4: -230,
  d5: -230,
  d6: -230,
  d7: -230,
  d8: -230,
  d9: -230,
  d10: -230,
  d11: -230,
  d12: -230,
  d13: -230,
  d14: -230,
  d15: -230,
  d16: -230,
  d17: -230,
  d18: -230,
  e1: -135,
  e2: -135,
  e3: -135,
  e4: -135,
  e5: -135,
  e6: -135,
  e7: -135,
  e8: -135,
  e9: -135,
  e10: -135,
  e11: -135,
  e12: -135,
  e13: -135,
  e14: -135,
  e15: -135,
  e16: -135,
  e17: -135,
  e18: -135,
  f1: -68,
  f2: -68,
  f3: -68,
  f4: -68,
  f5: -68,
  f6: -68,
  f7: -68,
  f8: -68,
  f9: -68,
  f10: -68,
  f11: -68,
  f12: -68,
  f13: -68,
  f14: -68,
  f15: -68,
  f16: -68,
  f17: -68,
  f18: -68,
  g1: -20,
  g2: -20,
  g3: -20,
  g4: -20,
  g5: -20,
  g6: -20,
  g7: -20,
  g8: -20,
  g9: -20,
  g10: -20,
  g11: -20,
  g12: -20,
  g13: -20,
  g14: -20,
  g15: -20,
  g16: -20,
  g17: -20,
  g18: -20,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 4.0,
  js2: 5.0,
  js3: 7.5,
  js4: 10.0,
  js5: 13.5,
  js6: 20.0,
  js7: 31.5,
  js8: 48.5,
  js9: 77.5,
  js10: 125.0,
  js11: 200.0,
  js12: 315.0,
  js13: 485.0,
  js14: 775.0,
  js15: 1250.0,
  js16: 2000.0,
  js17: 3150.0,
  js18: 4850.0,
  j5: 7.0,
  j6: 20.0,
  j7: 31.0,
  k4: 25.0,
  k5: 32.0,
  k6: 45.0,
  k7: 68.0,
  k8: 97.0,
  k9: 155.0,
  k10: 250.0,
  k11: 400.0,
  k12: 630.0,
  k13: 970.0,
  k14: 1550.0,
  k15: 2500.0,
  k16: 4000.0,
  k17: 6300.0,
  k18: 9700.0,
  m1: 31.0,
  m2: 33.0,
  m3: 38.0,
  m4: 43.0,
  m5: 50.0,
  m6: 63.0,
  m7: 86.0,
  m8: 120.0,
  m9: 178.0,
  m10: 273.0,
  m11: 423.0,
  m12: 653.0,
  m13: 993.0,
  m14: 1573.0,
  m15: 2523.0,
  m16: 4023.0,
  m17: 6323.0,
  m18: 9723.0,
  n1: 48.0,
  n2: 50.0,
  n3: 55.0,
  n4: 60.0,
  n5: 67.0,
  n6: 80.0,
  n7: 103.0,
  n8: 137.0,
  n9: 195.0,
  n10: 290.0,
  n11: 440.0,
  n12: 670.0,
  n13: 1010.0,
  n14: 1590.0,
  n15: 2540.0,
  n16: 4040.0,
  n17: 6340.0,
  n18: 9740.0,
  p1: 76.0,
  p2: 78.0,
  p3: 83.0,
  p4: 88.0,
  p5: 95.0,
  p6: 108.0,
  p7: 131.0,
  p8: 165.0,
  p9: 223.0,
  p10: 318.0,
  p11: 468.0,
  p12: 698.0,
  p13: 1038.0,
  p14: 1618.0,
  p15: 2568.0,
  p16: 4068.0,
  p17: 6368.0,
  p18: 9768.0,
  r1: 134.0,
  r2: 136.0,
  r3: 141.0,
  r4: 146.0,
  r5: 153.0,
  r6: 166.0,
  r7: 189.0,
  r8: 223.0,
  r9: 281.0,
  r10: 376.0,
  r11: 526.0,
  r12: 756.0,
  r13: 1096.0,
  r14: 1676.0,
  r15: 2626.0,
  r16: 4126.0,
  r17: 6426.0,
  r18: 9826.0,
  s1: 240.0,
  s2: 242.0,
  s3: 247.0,
  s4: 252.0,
  s5: 259.0,
  s6: 272.0,
  s7: 295.0,
  s8: 329.0,
  s9: 387.0,
  s10: 482.0,
  s11: 632.0,
  s12: 862.0,
  s13: 1202.0,
  s14: 1782.0,
  s15: 2732.0,
  s16: 4232.0,
  s17: 6532.0,
  s18: 9932.0
}, {
  from: 450,
  to: 500,
  a1: -1650,
  a2: -1650,
  a3: -1650,
  a4: -1650,
  a5: -1650,
  a6: -1650,
  a7: -1650,
  a8: -1650,
  a9: -1650,
  a10: -1650,
  a11: -1650,
  a12: -1650,
  a13: -1650,
  a14: -1650,
  a15: -1650,
  a16: -1650,
  a17: -1650,
  a18: -1650,
  b1: -840,
  b2: -840,
  b3: -840,
  b4: -840,
  b5: -840,
  b6: -840,
  b7: -840,
  b8: -840,
  b9: -840,
  b10: -840,
  b11: -840,
  b12: -840,
  b13: -840,
  b14: -840,
  b15: -840,
  b16: -840,
  b17: -840,
  b18: -840,
  c1: -480,
  c2: -480,
  c3: -480,
  c4: -480,
  c5: -480,
  c6: -480,
  c7: -480,
  c8: -480,
  c9: -480,
  c10: -480,
  c11: -480,
  c12: -480,
  c13: -480,
  c14: -480,
  c15: -480,
  c16: -480,
  c17: -480,
  c18: -480,
  d1: -230,
  d2: -230,
  d3: -230,
  d4: -230,
  d5: -230,
  d6: -230,
  d7: -230,
  d8: -230,
  d9: -230,
  d10: -230,
  d11: -230,
  d12: -230,
  d13: -230,
  d14: -230,
  d15: -230,
  d16: -230,
  d17: -230,
  d18: -230,
  e1: -135,
  e2: -135,
  e3: -135,
  e4: -135,
  e5: -135,
  e6: -135,
  e7: -135,
  e8: -135,
  e9: -135,
  e10: -135,
  e11: -135,
  e12: -135,
  e13: -135,
  e14: -135,
  e15: -135,
  e16: -135,
  e17: -135,
  e18: -135,
  f1: -68,
  f2: -68,
  f3: -68,
  f4: -68,
  f5: -68,
  f6: -68,
  f7: -68,
  f8: -68,
  f9: -68,
  f10: -68,
  f11: -68,
  f12: -68,
  f13: -68,
  f14: -68,
  f15: -68,
  f16: -68,
  f17: -68,
  f18: -68,
  g1: -20,
  g2: -20,
  g3: -20,
  g4: -20,
  g5: -20,
  g6: -20,
  g7: -20,
  g8: -20,
  g9: -20,
  g10: -20,
  g11: -20,
  g12: -20,
  g13: -20,
  g14: -20,
  g15: -20,
  g16: -20,
  g17: -20,
  g18: -20,
  h1: 0.0,
  h2: 0.0,
  h3: 0.0,
  h4: 0.0,
  h5: 0.0,
  h6: 0.0,
  h7: 0.0,
  h8: 0.0,
  h9: 0.0,
  h10: 0.0,
  h11: 0.0,
  h12: 0.0,
  h13: 0.0,
  h14: 0.0,
  h15: 0.0,
  h16: 0.0,
  h17: 0.0,
  h18: 0.0,
  js1: 4.0,
  js2: 5.0,
  js3: 7.5,
  js4: 10.0,
  js5: 13.5,
  js6: 20.0,
  js7: 31.5,
  js8: 48.5,
  js9: 77.5,
  js10: 125.0,
  js11: 200.0,
  js12: 315.0,
  js13: 485.0,
  js14: 775.0,
  js15: 1250.0,
  js16: 2000.0,
  js17: 3150.0,
  js18: 4850.0,
  j5: 7.0,
  j6: 20.0,
  j7: 31.0,
  k4: 25.0,
  k5: 32.0,
  k6: 45.0,
  k7: 68.0,
  k8: 97.0,
  k9: 155.0,
  k10: 250.0,
  k11: 400.0,
  k12: 630.0,
  k13: 970.0,
  k14: 1550.0,
  k15: 2500.0,
  k16: 4000.0,
  k17: 6300.0,
  k18: 9700.0,
  m1: 31.0,
  m2: 33.0,
  m3: 38.0,
  m4: 43.0,
  m5: 50.0,
  m6: 63.0,
  m7: 86.0,
  m8: 120.0,
  m9: 178.0,
  m10: 273.0,
  m11: 423.0,
  m12: 653.0,
  m13: 993.0,
  m14: 1573.0,
  m15: 2523.0,
  m16: 4023.0,
  m17: 6323.0,
  m18: 9723.0,
  n1: 48.0,
  n2: 50.0,
  n3: 55.0,
  n4: 60.0,
  n5: 67.0,
  n6: 80.0,
  n7: 103.0,
  n8: 137.0,
  n9: 195.0,
  n10: 290.0,
  n11: 440.0,
  n12: 670.0,
  n13: 1010.0,
  n14: 1590.0,
  n15: 2540.0,
  n16: 4040.0,
  n17: 6340.0,
  n18: 9740.0,
  p1: 76.0,
  p2: 78.0,
  p3: 83.0,
  p4: 88.0,
  p5: 95.0,
  p6: 108.0,
  p7: 131.0,
  p8: 165.0,
  p9: 223.0,
  p10: 318.0,
  p11: 468.0,
  p12: 698.0,
  p13: 1038.0,
  p14: 1618.0,
  p15: 2568.0,
  p16: 4068.0,
  p17: 6368.0,
  p18: 9768.0,
  r1: 140.0,
  r2: 142.0,
  r3: 147.0,
  r4: 152.0,
  r5: 159.0,
  r6: 172.0,
  r7: 195.0,
  r8: 229.0,
  r9: 287.0,
  r10: 382.0,
  r11: 532.0,
  r12: 762.0,
  r13: 1102.0,
  r14: 1682.0,
  r15: 2632.0,
  r16: 4132.0,
  r17: 6432.0,
  r18: 9832.0,
  s1: 260.0,
  s2: 262.0,
  s3: 267.0,
  s4: 272.0,
  s5: 279.0,
  s6: 292.0,
  s7: 315.0,
  s8: 349.0,
  s9: 407.0,
  s10: 502.0,
  s11: 652.0,
  s12: 882.0,
  s13: 1222.0,
  s14: 1802.0,
  s15: 2752.0,
  s16: 4252.0,
  s17: 6552.0,
  s18: 9952.0
}];

const standardIT10Tolerances = ['A10', 'B10', 'C10', 'D10', 'E10', 'F10', 'G10', 'H10'];
const defaultIT10Tolerance = 'E10';
/**
 * returns the ToleranceRange (upper and lower) for a given length
 * and the given toleranceString (like E10) for holes.
 *
 * The returned ToleranceRange (upper and lower) are in mm.
 *
 * This function is only useful for metric values!
 */
const getToleratorHoleValues = (length, toleranceString) => {
  const result = {
    upperValue: undefined,
    lowerValue: undefined
  };
  if (toleranceString === 'from' || toleranceString === 'to' || toleranceString === undefined) {
    return result;
  }
  const r = getToleranceRangeByToleranceString(toleranceString);
  if (r.upperValue || r.lowerValue) {
    return r;
  }
  const firstUpperRowObject = holeUpperValues.find(row => row.from < length && length <= row.to);
  const firstLowerRowObject = holeLowerValues.find(row => row.from < length && length <= row.to);
  if (typeof (firstUpperRowObject == null ? void 0 : firstUpperRowObject[toleranceString]) !== 'undefined') {
    result.upperValue = firstUpperRowObject[toleranceString] / 1000;
  }
  if (typeof (firstLowerRowObject == null ? void 0 : firstLowerRowObject[toleranceString]) !== 'undefined') {
    result.lowerValue = firstLowerRowObject[toleranceString] / 1000;
  }
  return result;
};
/**
 * returns the ToleranceRange (upper and lower) for a given length
 * and the given toleranceString (like e10) for shafts.
 *
 * The returned ToleranceRange (upper and lower) are in mm
 *
 * This function is only useful for metric values!
 */
const getToleratorShaftValues = (length, toleranceString) => {
  const result = {
    upperValue: undefined,
    lowerValue: undefined
  };
  if (toleranceString === 'from' || toleranceString === 'to' || toleranceString === undefined) {
    return result;
  }
  const r = getToleranceRangeByToleranceString(toleranceString);
  if (r.upperValue || r.lowerValue) {
    return r;
  }
  const firstUpperRowObject = shaftUpperValues.find(row => row.from < length && length <= row.to);
  const firstLowerRowObject = shaftLowerValues.find(row => row.from < length && length <= row.to);
  if (typeof (firstUpperRowObject == null ? void 0 : firstUpperRowObject[toleranceString]) !== 'undefined') {
    result.upperValue = firstUpperRowObject[toleranceString] / 1000;
  }
  if (typeof (firstLowerRowObject == null ? void 0 : firstLowerRowObject[toleranceString]) !== 'undefined') {
    result.lowerValue = firstLowerRowObject[toleranceString] / 1000;
  }
  return result;
};
/**
 * returns the ToleranceRange (upper and lower) for a given d1
 * and the given toleranceString (like E10) for holes.
 */
const getD1ToleranceValues = (d1, toleranceString, unitSystem) => {
  const d1MM = toMetricDistanceMilli(unitSystem, d1);
  const trMM = getToleratorHoleValues(d1MM, toleranceString);
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};
/**
 * getToleranceResultingDimension returns the min, max and median diameters d1 for the given values.
 * Results are in µm!
 *
 * Used at the moment only for CSV creation.
 */
const getToleranceResultingDimension = (dimension, toleranceValue, _unitSystem) => {
  const toleranceUpperValue = toleranceValue.upperValue !== undefined ? toleranceValue.upperValue : 0;
  const toleranceLowerValue = toleranceValue.lowerValue !== undefined ? toleranceValue.lowerValue : 0;
  const max = dimension + toleranceUpperValue;
  const min = dimension + toleranceLowerValue;
  const median = dimension + (toleranceUpperValue + toleranceLowerValue) / 2;
  return {
    min,
    max,
    median
  };
};
/**
 * returns the ToleranceRange (upper and lower) for a given d2
 * and the given toleranceString (like e10) for shafts.
 */
const getD2ToleranceValues = (d2, toleranceString, unitSystem) => {
  const d2MM = toMetricDistanceMilli(unitSystem, d2);
  let trMM = getToleratorShaftValues(d2MM, toleranceString);
  // In akeneo (some) articles d2 has tolerance classes with capital chars like H7.
  if (trMM.upperValue === undefined && trMM.lowerValue === undefined || isNaN(trMM.upperValue) && isNaN(trMM.lowerValue)) {
    trMM = getToleratorHoleValues(d2MM, toleranceString);
  }
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};
/**
 * returns the ToleranceRange (upper and lower) for a given d3
 * and the given toleranceString (like e10) for shafts.
 */
const getD3ToleranceValues = (d3, toleranceString, unitSystem) => {
  const d3MM = toMetricDistanceMilli(unitSystem, d3);
  const trMM = getToleratorShaftValues(d3MM, toleranceString);
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};
/**
 * returns the ToleranceRange (upper and lower) for a given d4 (same as d3, used in MCM)
 * and the given toleranceString (like e10) for shafts.
 */
const getD4ToleranceValues = (d4, toleranceString, unitSystem) => {
  const d4MM = toMetricDistanceMilli(unitSystem, d4);
  const trMM = getToleratorShaftValues(d4MM, toleranceString);
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};
/**
 * returns the ToleranceRange (upper and lower) for a given b1
 * and the given toleranceString (like e10) for shafts and distances.
 */
const getB1ToleranceValues = (b1, toleranceString, unitSystem) => {
  const b1MM = toMetricDistanceMilli(unitSystem, b1);
  const trMM = getToleratorShaftValues(b1MM, toleranceString);
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};
/**
 * returns the ToleranceRange (upper and lower) for a given b2
 * and the given toleranceString (like e10) for shaft sand distances.
 */
const getB2ToleranceValues = (b2, toleranceString, unitSystem) => {
  const b2MM = toMetricDistanceMilli(unitSystem, b2);
  const trMM = getToleratorShaftValues(b2MM, toleranceString);
  if (unitSystem === UnitSystem.imperial) {
    trMM.upperValue = trMM.upperValue ? fromMetricDistanceMilli(unitSystem, trMM.upperValue) : trMM.upperValue;
    trMM.lowerValue = trMM.lowerValue ? fromMetricDistanceMilli(unitSystem, trMM.lowerValue) : trMM.lowerValue;
  }
  return trMM;
};

// tolerances
const toleranceB1 = {
  tolerance: 'b1',
  toleranceName: 'ibd.dimensions.BEARING_WIDTH',
  toleranceRangeFn: getB1ToleranceValues
};
const toleranceB2 = {
  tolerance: 'b2',
  toleranceName: 'ibd.dimensions.FLANGE_THICKNESS',
  toleranceRangeFn: getB2ToleranceValues
};
const toleranceB2D3 = {
  tolerance: 'b2d3',
  toleranceName: 'ibd.dimensions.FLANGE_THICKNESS',
  toleranceRangeFn: getB2ToleranceValues
};
const toleranceB2D4 = {
  tolerance: 'b2d4',
  toleranceName: 'ibd.dimensions.FLANGE_THICKNESS',
  toleranceRangeFn: getB2ToleranceValues
};
const toleranceB3 = {
  tolerance: 'b3',
  toleranceName: 'ibd.dimensions.CLAMPING_WIDTH',
  toleranceRangeFn: null
};
const toleranceD1 = {
  tolerance: 'd1',
  toleranceName: 'ibd.pdf.SHAFT_DIAMETER',
  toleranceRangeFn: getD1ToleranceValues
};
const toleranceD2 = {
  tolerance: 'd2',
  toleranceName: 'ibd.dimensions.HOUSING_BORE',
  toleranceRangeFn: getD2ToleranceValues
};
const toleranceD3 = {
  tolerance: 'd3',
  toleranceName: 'ibd.dimensions.FLANGE_DIAMETER',
  toleranceRangeFn: getD3ToleranceValues
};
const toleranceD4 = {
  tolerance: 'd4',
  toleranceName: 'ibd.dimensions.FLANGE_DIAMETER',
  toleranceRangeFn: getD3ToleranceValues
};
const toleranceT = {
  tolerance: 'slotAngle',
  toleranceName: 'ibd.dimensions.SLOT_ANGLE',
  toleranceRangeFn: null
};
const toleranceSB = {
  tolerance: 'sb',
  toleranceName: 'ibd.dimensions.SLOT_WIDTH',
  toleranceRangeFn: null
};
const toleranceFrD = {
  tolerance: 'frD',
  toleranceName: 'ibd.dimensions.FELT_RING_INNER_DIAMETER',
  toleranceRangeFn: null
};
const toleranceFrW = {
  tolerance: 'frW',
  toleranceName: 'ibd.dimensions.FELT_RING_WIDTH',
  toleranceRangeFn: null
};
const toleranceFrT = {
  tolerance: 'frT',
  toleranceName: 'ibd.dimensions.FELT_RING_THICKNESS',
  toleranceRangeFn: null
};
// dimensions
const dimensionD = {
  dimension: 'd',
  dimensionFullNameKey: 'ibd.dimensions.OUTER_DIAMETER_D',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD1 = {
  dimension: 'd1',
  dimensionFullNameKey: 'ibd.dimensions.INNER_DIAMETER',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD2 = {
  dimension: 'd2',
  dimensionFullNameKey: 'ibd.dimensions.OUTER_DIAMETER',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD3 = {
  dimension: 'd3',
  dimensionFullNameKey: 'ibd.dimensions.FLANGE_DIAMETER',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD4 = {
  dimension: 'd4',
  dimensionFullNameKey: 'ibd.dimensions.FLANGE_DIAMETER',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD5 = {
  dimension: 'd5',
  dimensionFullNameKey: 'TBD',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD6 = {
  dimension: 'd6',
  dimensionFullNameKey: 'TBD',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionD7 = {
  dimension: 'd7',
  dimensionFullNameKey: 'TBD',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionB2D3 = {
  dimension: 'b2d3',
  dimensionName: 'b2.2',
  dimensionFullNameKey: 'ibd.dimensions.FLANGE_THICKNESS',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionB2D4 = {
  dimension: 'b2d3',
  dimensionName: 'b2.1',
  dimensionFullNameKey: 'ibd.dimensions.FLANGE_THICKNESS',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionB1 = {
  dimension: 'b1',
  dimensionFullNameKey: 'ibd.dimensions.BEARING_WIDTH',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionH = {
  dimension: 'h',
  dimensionFullNameKey: 'ibd.dimensions.BEARING_WIDTH_H',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionB2 = {
  dimension: 'b2',
  dimensionFullNameKey: 'ibd.dimensions.FLANGE_THICKNESS',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionB3 = {
  dimension: 'b3',
  dimensionFullNameKey: 'ibd.dimensions.CLAMPING_WIDTH',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionR = {
  dimension: 'r',
  dimensionName: 'R',
  dimensionFullNameKey: 'TBD',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionSlotAngle = {
  dimension: 'slotAngle',
  dimensionName: 'T',
  dimensionFullNameKey: 'ibd.dimensions.SLOT_ANGLE',
  dimensionUnitKeyFn: getLocalizationKeyForAngleSymbol
};
const dimensionSB = {
  dimension: 'sb',
  dimensionFullNameKey: 'ibd.dimensions.SLOT_WIDTH',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionFrD = {
  dimension: 'frD',
  dimensionFullNameKey: 'ibd.dimensions.FELT_RING_INNER_DIAMETER',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionFrW = {
  dimension: 'frW',
  dimensionFullNameKey: 'ibd.dimensions.FELT_RING_WIDTH',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const dimensionFrT = {
  dimension: 'frT',
  dimensionFullNameKey: 'ibd.dimensions.FELT_RING_THICKNESS',
  dimensionUnitKeyFn: getLocalizationKeyForDistanceMilliSymbol
};
const bearingFormTypeModelMap = {
  S: {
    formTypeNameKey: 'ibd.shapes.S',
    dimensions: [dimensionD1, dimensionD2, dimensionB1],
    tolerances: [toleranceD1, toleranceD2, toleranceB1]
  },
  F: {
    formTypeNameKey: 'ibd.shapes.F',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceB1, toleranceB2]
  },
  FSG: {
    formTypeNameKey: 'ibd.shapes.FSG',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2, dimensionFrD, dimensionFrW, dimensionFrT],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceB1, toleranceB2, toleranceFrD, toleranceFrW, toleranceFrT]
  },
  T: {
    formTypeNameKey: 'ibd.shapes.T',
    dimensions: [dimensionD1, dimensionD2, Object.assign({}, dimensionB1, {
      dimensionName: 's',
      dimensionFullNameKey: 'ibd.dimensions.THICKNESS'
    })],
    tolerances: [toleranceD1, toleranceD2, toleranceB1]
  },
  PR: {
    formTypeNameKey: 'ibd.shapes.PR',
    dimensions: [dimensionD1, dimensionD2, dimensionB1, Object.assign({}, dimensionSB, {
      dimension: 'b2'
    }), dimensionSlotAngle],
    tolerances: [toleranceD1, toleranceD2, toleranceB1, Object.assign({}, toleranceB2, {
      toleranceName: 'ibd.dimensions.SLOT_WIDTH'
    }), toleranceT]
  },
  EC: {
    formTypeNameKey: 'ibd.shapes.EC',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB2, dimensionB3, dimensionSB],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceB2, toleranceSB]
  },
  C: {
    formTypeNameKey: 'ibd.shapes.MCM',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionD4, dimensionB2D3, dimensionB2D4, dimensionB3],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceD4, toleranceB2D3, toleranceB2D4, toleranceB3]
  },
  RL: {
    formTypeNameKey: 'ibd.shapes.RL',
    dimensions: [dimensionD1, dimensionD2, dimensionB1],
    tolerances: [toleranceD1, toleranceD2, toleranceB1]
  },
  FL: {
    formTypeNameKey: 'ibd.shapes.FL',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionD4, dimensionD5, dimensionD6, dimensionD7, dimensionB1, dimensionB2, dimensionB3, dimensionR],
    tolerances: [toleranceD1]
  },
  VFL: {
    formTypeNameKey: 'ibd.shapes.FLP',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionD4, dimensionD5, dimensionD6, dimensionD7, dimensionB1, dimensionB2, dimensionB3, dimensionR],
    tolerances: [toleranceD1]
  },
  Y: {
    formTypeNameKey: 'ibd.shapes.Y',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceB1, toleranceB2, toleranceB3]
  },
  YK: {
    formTypeNameKey: 'ibd.shapes.YK',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2],
    tolerances: [toleranceD1, toleranceD2, toleranceD3, toleranceB1, toleranceB2, toleranceB3]
  },
  TE: {
    formTypeNameKey: 'ibd.shapes.TE',
    dimensions: [dimensionD, dimensionD1, dimensionSlotAngle, dimensionH],
    tolerances: []
  },
  D: {
    formTypeNameKey: 'ibd.shapes.D',
    dimensions: [dimensionD1, dimensionD2, dimensionB1, dimensionB2],
    tolerances: [toleranceD1]
  },
  VF: {
    formTypeNameKey: 'ibd.shapes.VF',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2, dimensionB3],
    tolerances: []
  },
  K: {
    formTypeNameKey: 'ibd.shapes.K',
    dimensions: [dimensionD1, dimensionD2, dimensionD3, dimensionB1, dimensionB2, dimensionB3],
    tolerances: []
  },
  VS: {
    formTypeNameKey: 'ibd.shapes.VS',
    dimensions: [dimensionD1, dimensionD2, dimensionB1, dimensionB3],
    tolerances: [toleranceD2]
  }
};
const getBearingFormTypeModel = bearingForm => {
  return bearingFormTypeModelMap[bearingForm];
};
const isBearingParamsComplete = userPart => {
  const {
    dimensions
  } = getBearingFormTypeModel(userPart.form);
  return Object.values(dimensions).every(({
    dimension
  }) => dimension in userPart && typeof userPart[dimension] !== 'undefined');
};

/**
 * Returns the length of a D (b1).
 */
const getDLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the BoundingBox of a D.
 * All returned values are in mm.
 */
const getDBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};
/**
 * Returns the volume of the DParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getDVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * userPartB1MM / 1000;
};

/**
 * Returns the length of a VF (b1).
 */
const getVFLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the BoundingBox of a VF.
 * All returned values are in mm.
 */
const getVFBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};
/**
 * Returns the volume of the VFParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getVFVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * userPartB1MM / 1000;
};
/**
 * Returns the flange thickness of a VF (b2).
 */
const getVFFlangeThickness = userPart => {
  return userPart.b2;
};

/**
 * Returns the length of a K (b1).
 */
const getKLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the BoundingBox of a K.
 * All returned values are in mm.
 */
const getKBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};
/**
 * Returns the volume of the KParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getKVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * userPartB1MM / 1000;
};

/**
 * Returns the length of a VS (b1).
 */
const getVSLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the BoundingBox of a VS.
 * All returned values are in mm.
 */
const getVSBoundingBox = (userPart, forceMetric = true) => {
  const userPartD2 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d2) : userPart.d2;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD2,
    y: userPartD2
  };
};
/**
 * Returns the volume of the VFParams in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * But when we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getVSVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartB1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b1);
  return PI_QUARTER * (userPartD2MM * userPartD2MM - userPartD1MM * userPartD1MM) * userPartB1MM / 1000;
};

/**
 * Type-Guard to check if form is a SleeveBearing
 */
const isSleeveBearingForm = form => {
  return form === 'S';
};
/**
 * Type-Guard to check if form is a FlangeBearing
 */
const isFlangeBearingForm = form => {
  return form === 'F';
};
/**
 * Type-Guard to check if form is a ThrustWasher
 */
const isThrustWasherForm = form => {
  return form === 'T';
};
/**
 * Type-Guard to check if form is a PistonRing
 */
const isPistonRingForm = form => {
  return form === 'PR';
};
/**
 * Type-Guard to check if form is a EasyClip
 */
const isEasyClipForm = form => {
  return form === 'EC';
};
/**
 * Type-Guard to check if form is a Mcm
 */
const isCForm = form => {
  return form === 'C';
};
/**
 * Type-Guard to check if form is a KnifeEdgeRoller
 */
const isKnifeEdgeRollerForm = form => {
  return form === 'RL';
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Registry that stores constructor parameters used to create an instance
 */
class ConstructorParamsRegistry {
  /**
   * Sets the parameters in registry
   *
   * @param instance An object instance
   * @param params Constructor parameters
   */
  static setParams(instance, params) {
    this.parameters.set(instance, params);
  }
  /**
   * Returns stored constructor parameters for the provided instance
   */
  static getParams(instance) {
    this.checkParams(instance);
    return this.parameters.get(instance);
  }
  /**
   * Checks if instance is set in the registry and throws error if it is not.
   */
  static checkParams(instance) {
    if (!this.hasParams(instance)) {
      throw new Error(`${instance.constructor.name} class hasn't been decoraded with '@CleanCloneable()' decorator.`);
    }
  }
  /**
   * Checks if provided instance is set in the registry
   */
  static hasParams(instance) {
    return this.parameters.has(instance);
  }
}
ConstructorParamsRegistry.parameters = new WeakMap();
/**
 * Cloner API
 */
class ReflectionCloner {
  /**
   * Makes sure that provided instance is decorated with `@CleanCloneable()`.
   * Produce proper error if it's not.
   *
   * @param instance The instance to check
   */
  static verifyDecorator(instance) {
    ConstructorParamsRegistry.checkParams(instance);
  }
  /**
   * Creates the new instance of provided class using the same constructor
   * parameters as during creation of passed object.
   *
   * @param instance The orginal instance of class wrapped by `CleanCloneable` decorator.
   The new instance of class
   */
  static cleanClone(instance) {
    const params = this.getConstructorParams(instance);
    return new instance.constructor(...params);
  }
  /**
   * Gets the params that the provided instance were constructed with.
   *
   * @param instance The class instance
   */
  static getConstructorParams(instance) {
    return ConstructorParamsRegistry.getParams(instance);
  }
}
const CleanCloneable = () => {
  const decorator = constructor => {
    return class extends constructor {
      constructor(...args) {
        super(...args);
        ConstructorParamsRegistry.setParams(this, args);
      }
    };
  };
  return decorator;
};

//import inspect from 'object-inspect';
/**
 * This function returns the maximum of the MIN values of two MinMaxRules, where a new rule
 * with stricter operators/values replaces an old rule.
 * If the new rule is less strict than the old rule an error is thrown.
 */
const getStrictMaximumOfMinRule = (oldRule, newRule) => {
  var _oldRule$min, _newRule$min;
  if (typeof ((_oldRule$min = oldRule.min) == null ? void 0 : _oldRule$min.value) === 'undefined') {
    throw new Error('MinMaxRules with variables are not yet supported in oldRule');
  }
  if (typeof ((_newRule$min = newRule.min) == null ? void 0 : _newRule$min.value) === 'undefined') {
    throw new Error('MinMaxRules with variables are not yet supported in newRule');
  }
  if (oldRule.min.op === newRule.min.op) {
    if (oldRule.min.op === '=') {
      if (oldRule.min.value !== newRule.min.value) {
        throw new Error('values change while op is =');
      }
    } else {
      if (newRule.min.value < oldRule.min.value) {
        throw new Error('new value is lower that old value I');
      }
    }
  } else if (oldRule.min.op === '>=' && newRule.min.op === '=' || oldRule.min.op === '>=' && newRule.min.op === '>' || oldRule.min.op === '>' && newRule.min.op === '=') {
    if (newRule.min.value < oldRule.min.value) {
      throw new Error('new value is lower that old value II');
    }
  } else if (oldRule.min.op === '>' && newRule.min.op === '>=' || oldRule.min.op === '=' && newRule.min.op === '>' || oldRule.min.op === '=' && newRule.min.op === '>=') {
    throw new Error('new operator allows more than old');
  }
  return newRule;
};
/**
 * This function returns the minimum of the Max values of two MinMaxRules, where a new rule
 * with stricter operators/values replaces an old rule.
 * If the new rule is less strict than the old rule an error is thrown.
 */
const getStrictMinimumOfMaxRule = (oldRule, newRule) => {
  var _oldRule$max, _newRule$max;
  if (typeof ((_oldRule$max = oldRule.max) == null ? void 0 : _oldRule$max.value) === 'undefined') {
    throw new Error('MinMaxRules with variables are not yet supported in oldRule');
  }
  if (typeof ((_newRule$max = newRule.max) == null ? void 0 : _newRule$max.value) === 'undefined') {
    throw new Error('MinMaxRules with variables are not yet supported in newRule');
  }
  if (oldRule.max.op === newRule.max.op) {
    if (oldRule.max.op === '=') {
      if (oldRule.max.value !== newRule.max.value) {
        throw new Error('values change while op is =');
      }
    } else {
      if (newRule.max.value > oldRule.max.value) {
        // throw new Error('attr: '+newRule.max.attr+' new value '+newRule.max.value +' is bigger that old value '+oldRule.max.value +' I');
        throw new Error('new value is bigger that old value I');
      }
    }
  } else if (oldRule.max.op === '<=' && newRule.max.op === '=' || oldRule.max.op === '<=' && newRule.max.op === '<' || oldRule.max.op === '<' && newRule.max.op === '=') {
    if (newRule.max.value > oldRule.max.value) {
      // console.log('oldRule');
      // console.log(inspect(oldRule, {maxStringLength: 120}));
      // console.log('newRule');
      // console.log(inspect(newRule, {maxStringLength: 120}));
      throw new Error('new value is bigger that old value II');
    }
  } else if (oldRule.max.op === '<' && newRule.max.op === '<=' || oldRule.max.op === '=' && newRule.max.op === '<' || oldRule.max.op === '=' && newRule.max.op === '<=') {
    throw new Error('new operator allows more than old');
  }
  return newRule;
};

const attributeTypeAsArray = ['DISTANCE', 'ANGLE', 'MATERIAL', 'PERCENTAGE', 'CUSTOM_STRING', 'BOOLEAN'];
const minOperatorsAsArray = ['>', '>=', '='];
const maxOperatorsAsArray = ['<', '<=', '='];
const stringOperatorsAsArray = ['in', '!in'];
const booleanOperatorsAsArray = ['==', '!='];

/**
 * Validate a MinMaxRule and throws an exception when something is wrong.
 */
const validateStaticIMinMaxRule = rule => {
  if (!rule) {
    throw new Error('rule is undefined');
  }
  if (typeof rule.attr === 'undefined') {
    throw new Error('rule.attr is undefined');
  }
  if (rule.attr === '') {
    throw new Error('rule.attr is empty');
  }
  if (rule.min) {
    if (typeof rule.min.op === 'undefined') {
      throw new Error('rule.min.op is undefined');
    }
    if (rule.min.op === '') {
      throw new Error('rule.min.op is empty');
    }
    if (minOperatorsAsArray.indexOf(rule.min.op) === -1) {
      throw new Error('rule.min.op "' + rule.min.op + '" is not a valid operand');
    }
    if (rule.min.variable === '') {
      throw new Error('rule.min.variable is empty');
    }
  }
  if (rule.max) {
    if (typeof rule.max.op === 'undefined') {
      throw new Error('rule.max.op is undefined');
    }
    if (rule.max.op === '') {
      throw new Error('rule.max.op is empty');
    }
    if (maxOperatorsAsArray.indexOf(rule.max.op) === -1) {
      throw new Error('rule.max.op "' + rule.max.op + '" is not a valid operand');
    }
    if (rule.max.variable === '') {
      throw new Error('rule.max.variable is empty');
    }
  }
  if (!rule.min && !rule.max) {
    throw new Error('Min or max has to be defined!');
  }
  if (rule.min && rule.max) {
    // check if min and max rule do not contradict
    if (rule.min.value && rule.max.value) {
      // both sub-rules are based on values (not on variables)
      if (rule.min.value === rule.max.value) {
        const equals = /=/;
        if (equals.exec(rule.min.op) && equals.exec(rule.max.op)) ; else {
          throw new Error('Rule does not make sense, because min and max are equal but op exclude the other');
        }
      } else if (rule.min.value > rule.max.value) ;
    }
    if (rule.min.op === '=' && rule.max.op === '<' || rule.min.op === '>' && rule.max.op === '=' || rule.min.op === '=' && rule.max.op === '=') {
      throw new Error('Rule does not make sense, because op exclude the other');
    }
  }
};

/**
 * Returns an array with (unique) attrIds that are used in the rules.
 */
const getUniqueRuleAttrIds = rules => {
  const dict = {};
  for (const rule of rules) {
    dict[rule.attr]++;
  }
  return Object.keys(dict);
};
/**
 * Returns true if the given rule is valid for a passed attrId.
 * By definition an undefined rule is never valid.
 */
const isValidAttrId = (attrId, rule) => {
  if (!rule) {
    return false;
  }
  return rule.attr === attrId;
};
/**
 * Returns true if the given rule is valid for one of the passed attrIds.
 */
const isValidInAttrIds = (attrIds, rule) => {
  if (!rule) {
    // by definition an undefined rule is never valid
    return false;
  }
  for (const attrId of attrIds) {
    if (isValidAttrId(attrId, rule)) {
      return true;
    }
  }
  return false;
};
/**
 * Type-Guard to check if Attribute is a AttributeValue
 */
const isAttributeValue = attr => {
  return typeof attr.fn === 'undefined';
};
/**
 * Type-Guard to check if Attribute is a AttributeFunction
 */
const isAttributeFunction = attr => {
  return !isAttributeValue(attr);
};
/**
 * Type-Guard to check if Rule is a MinMaxRule.
 */
const isIMinMaxRule = rule => {
  return typeof rule.min !== 'undefined' || typeof rule.max !== 'undefined';
};
/**
 * Type-Guard to check if Rule is a IMinValueRule.
 */
const isIMinValueRule = rule => {
  if (!rule) {
    return false;
  }
  return rule.value !== undefined;
};
/**
 * Type-Guard to check if Rule is a IMaxValueRule.
 */
const isIMaxValueRule = rule => {
  if (!rule) {
    return false;
  }
  return rule.value !== undefined;
};
/**
 * Type-Guard to check if Rule is a IMinMaxValueRule.
 */
const isIMinMaxValueRule = rule => {
  if (!rule) {
    return false;
  }
  return isIMinValueRule(rule.min) && isIMaxValueRule(rule.max);
};
/**
 * Type-Guard to check if Rule is a StringRule.
 */
const isIStringRule = rule => {
  return rule.op === 'in' || rule.op === '!in';
};
/**
 * Type-Guard to check if Rule is a BooleanRule.
 */
const isIBooleanRule = rule => {
  return rule.op === '==' || rule.op === '!=';
};
/**
 * Type-Guard to check if Rule is a BooleanValueRule.
 */
const isIBooleanValueRule = rule => {
  return isIBooleanRule(rule) && typeof rule.value !== 'undefined';
};
/**
 * Type-Guard to check if Rule is a BooleanVariableRule.
 */
const isIBooleanVariableRule = rule => {
  return isIBooleanRule(rule) && typeof rule.variable !== 'undefined';
};
/**
 * Check if the attribute type is Material.
 */
const isAttributeTypeMaterial = at => {
  return at === 'MATERIAL';
};
/**
 * Sometimes rules (containing other attributes or attributes from functions) contain conflicting declarations.
 * Like:
 *       {attr: 'd2', min: { op: '>', value: 40}, max: {attr: 'd2', op: '<', value: 15}},
 *       {attr: 'd2', min: { op: '>', value: 40}, max: {attr: 'd2', op: '<', value: 31}},
 *       {attr: 'd2', min: { op: '>', value: 40}, max: {attr: 'd2', op: '<=', value: 48}}
 *
 * In this case the min.value comes from d1. The first two rules can never be true because d2>40 can not be smaller
 * than 15 or 31, so they are not useful.
 */
const isRuleUseful = rule => {
  if (isIMinMaxRule(rule)) {
    // Check if a minMaxRule has a conflicting declaration
    if (rule.min && rule.max) {
      // only rules with min and max can have conflicting declarations
      // if one of the values is not defined then this rule can not be conflicting
      if (typeof rule.min.value === 'undefined') {
        return true;
      }
      if (typeof rule.max.value === 'undefined') {
        return true;
      }
      if (rule.min.value === rule.max.value) {
        if (rule.min.op === '>' || rule.max.op === '<') {
          return false;
        }
      } else if (rule.min.value > rule.max.value) {
        return false;
      }
    }
  }
  return true;
};
/**
 * Returns only rules that are stored in the GroupLimitsDictionary that have an attribute name that is mentioned in
 * attrNames.
 * Rules are only returened if they are useful!
 */
const getRulesByAttrNamesFromDictionary = (attrNames, gld) => {
  const newLimitingRules = [];
  for (const rulesetValue of Object.keys(gld)) {
    for (const rule of gld[rulesetValue].limitingRules) {
      if (isValidInAttrIds(attrNames, rule) && isRuleUseful(rule)) {
        if (!rule.ri) {
          throw new Error('rid not defined in rule');
        }
        newLimitingRules.push(Object.assign({}, rule));
      }
    }
  }
  return newLimitingRules;
};
/**
 * Returns the rules from limitingRules that have the given attribute name.
 * Rules are only returned if they are useful!
 */
const getRulesByAttrNameFromLimitingRules = (attrName, limitingRules) => {
  const newLimitingRules = [];
  for (const rule of limitingRules) {
    if (isValidInAttrIds([attrName], rule) && isRuleUseful(rule)) {
      newLimitingRules.push(Object.assign({}, rule));
    }
  }
  return newLimitingRules;
};
/**
 * Returns the attribue (name) that is common in all rules that are stored in a
 * GroupLimitsDictionary for a group.
 */
const getCommonLimitingAttributesFromDictionary = (rulesets, gld) => {
  const firstValue = rulesets[0].value;
  let commonLimitingAttributes = [];
  if (firstValue) {
    const rulesetValues = rulesets.map(e => e.value);
    for (const rulesetValue of rulesetValues) {
      const gldRuleset = gld[rulesetValue];
      if (gldRuleset && !gldRuleset.rulesetIsValid) {
        if (rulesetValue === firstValue) {
          commonLimitingAttributes = gldRuleset.limitingAttrIds;
        } else {
          const current = gldRuleset.limitingAttrIds;
          // The following line is planed as a replacement for the lines below. But it seems that thy dont behave the same :(
          //  commonLimitingAttributes= uniqueConcat(commonLimitingAttributes, current);
          commonLimitingAttributes = commonLimitingAttributes.concat(
          // add if not yet in the list
          current.filter(attr => !commonLimitingAttributes.indexOf(attr)));
          const intermediate = [];
          for (const attr of commonLimitingAttributes) {
            if (current.indexOf(attr) > -1 && intermediate.indexOf(attr) === -1) {
              intermediate.push(attr);
            }
          }
          commonLimitingAttributes = intermediate;
        }
      }
    }
  }
  return commonLimitingAttributes;
};
/**
 * isWallthicknessInLimitingRules checks if one of the limiting rules is the wallthickness
 */
const isWallthicknessInLimitingRules = r => {
  if (r.limitingRules) {
    for (const rule of r.limitingRules) {
      if (isIMinMaxRule(rule) && rule.attr === 'wt') {
        return true;
      }
    }
  }
  // no limitingRules -> wallthickness not inside
  return false;
};
/**
 * This function returns values for the given Rules that will validate for that rule.
 *
 * It is supposed to be used with non-joind rules.
 * If it is used with joined rules then the first rulesetValue is used!
 */
const findValuesForRules = rules => {
  const result = [];
  for (const rule of rules) {
    // Think about using expandRightSideVariableInRule before
    //    -> but then we need to move this function to the Rulebox!!!
    //    -> OnHold
    if (isIStringRule(rule)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      result.push({
        value: rule.values[0],
        rid: rule.ri.rulesetIds[0],
        attribute: rule.attr
      });
    } else if (isIBooleanRule(rule)) {
      throw new Error('How to  findValuesForRules for IBooleanRules ?');
    } else if (isIMinMaxRule(rule)) {
      var _rule$min, _rule$max, _rule$min2, _rule$max2;
      let value;
      if ((_rule$min = rule.min) != null && _rule$min.value && (_rule$max = rule.max) != null && _rule$max.value) {
        value = (rule.min.value + rule.max.value) / 2;
      } else if ((_rule$min2 = rule.min) != null && _rule$min2.value) {
        if (rule.min.op === '=') {
          value = rule.min.value;
        } else {
          value = rule.min.value + 1;
        }
      } else if ((_rule$max2 = rule.max) != null && _rule$max2.value) {
        if (rule.max.op === '=') {
          value = rule.max.value;
        } else {
          value = rule.max.value - 1;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      result.push({
        value,
        rid: rule.ri.rulesetIds[0],
        attribute: rule.attr
      });
    }
  }
  return result;
};
/**
 * This function removes rules from limiting rules that use attributes that are already in other limiting rules.
 * Which would be otherwise a subsequent fault.
 *
 * Example:
 * the passed limitingRules that where calculated before are:
 * [
 * { min: { attr: 'd1', op: '>=', value: 6 }, max: { attr: 'd1', op: '<', value: 10 }, rulesetValue: 'f_1' },
 * { min: { attr: 'd2', op: '>', value: 42 }, max: { attr: 'd2', op: '<', value: 15 }, rulesetValue: 'f_1' },
 * { min: { attr: 'd3', op: '>', value: 44 }, max: { attr: 'd3', op: '<', value: 15 }, rulesetValue: 'f_1' },
 * ]
 * so for d1, d2 and d3 there are rules that are not valid.
 *
 * And we pass the unexpanded rules, that are:
 * [
 * { min: { attr: 'd1', op: '>=', value: 6 }, max: { attr: 'd1', op: '<', value: 10 } },
 * { min: { attr: 'd2', op: '>', variable: 'd1' }, max: { attr: 'd2', op: '<', value: 15 } },
 * { min: { attr: 'd3', op: '>', variable: 'd2' }, max: { attr: 'd3', op: '<', value: 15 } },
 * ]
 *
 * The rule for d2 depends on d1 and
 * the rule for d3 depends on d2.
 *
 * So it does not make sense to tell the user *Please change d1, d2, d3* because with a little change of d1 the other
 * rules may also be valid. So we remove the rules for the dependig attributes and only return
 *
 * [
 * { min: { attr: 'd1', op: '>=', value: 6 }, max: { attr: 'd1', op: '<', value: 10 }, rulesetValue: 'f_1' },
 * ]
 * as the new limitingRule.
 */
const getValidLimitingRules = (limitingRules, unexpandedRules) => {
  const attrCandidates = getUniqueRuleAttrIds(limitingRules);
  const validAttrCandidates = [];
  for (const attrCandidate of attrCandidates) {
    let isValid = true;
    for (const currentRule of unexpandedRules) {
      if (isValidAttrId(attrCandidate, currentRule)) {
        if (isIStringRule(currentRule)) ; else if (isIMinMaxRule(currentRule)) {
          if (currentRule.min && currentRule.min.variable) {
            if (attrCandidates.indexOf(currentRule.min.variable) !== -1) {
              // dont use this attrCandidate, because its rules depend on an other attrCandidate
              isValid = false;
            }
          }
          if (currentRule.max && currentRule.max.variable) {
            if (attrCandidates.indexOf(currentRule.max.variable) !== -1) {
              // dont use this attrCandidate, because its rules depend on an other attrCandidate
              isValid = false;
            }
          }
        }
      }
    }
    if (isValid && validAttrCandidates.indexOf(attrCandidate) === -1) {
      validAttrCandidates.push(attrCandidate);
    }
  }
  return limitingRules.filter(rule => isValidInAttrIds(validAttrCandidates, rule));
};
/**
 * Concatenates two arrays of string and keep the strings unique.
 * The result is returned sorted!
 */
const uniqueConcat = (a, b) => {
  const result = [];
  if (a) {
    for (const element of a) {
      if (result.indexOf(element) === -1) {
        result.push(element);
      }
    }
  }
  if (b) {
    for (const element of b) {
      if (result.indexOf(element) === -1) {
        result.push(element);
      }
    }
  }
  return result.sort();
};
/**
 * Returns the proper unit for a given AttributeType and UnitSystem
 */
const getUnit = (type, unitSystem = UnitSystem.metric) => {
  switch (type) {
    case 'ANGLE':
      return getLocalizationKeyForAngleSymbol(unitSystem);
    case 'DISTANCE':
      return getLocalizationKeyForDistanceMilliSymbol(unitSystem);
    case 'PERCENTAGE':
      return '%';
    case 'MATERIAL':
    case 'BOOLEAN':
    case 'CUSTOM_STRING':
      return '';
  }
};
/**
 * Returns the intersection (german:Schnittmenge) of two array strings.
 *
 * The result is sorted
 */
const intersectArray = (a, b) => {
  return a.filter(e => b.indexOf(e) >= 0).sort();
};
/**
 * Returns true when the attribute exists at least once in the rulesets.
 */
const isAttributeInRulesets = (attrId, rulesets) => {
  for (const ruleset of rulesets) {
    for (const rule of ruleset.rule) {
      if (isValidAttrId(attrId, rule)) {
        return true;
      }
    }
  }
  return false;
};
/**
 * applyStricterAdjustmentRule takes basicRules and adjustmentRules.
 *
 * The result is an IRule[] where the adjustmentRules were applied to the basicRules.
 * Precondition is that the adjustmentRules do *not* allow more than the basicRules.
 *
 * If the resulting rule is stricter (or equal) then this rule is returned, otherwise
 * an error is thrown.
 */
const applyStricterAdjustmentRule = (basicRules, adjustmentRules) => {
  const resultingRules = [];
  for (const rule of basicRules) {
    const combinedRule = rule;
    const attr = rule.attr;
    if (isIMinMaxRule(rule)) {
      const adjustmentRulesWithAttr = getRulesByAttrNameFromLimitingRules(attr, adjustmentRules);
      if (adjustmentRulesWithAttr.length > 1) {
        throw new Error('More than one MinMaxRule with attr ' + attr + ' found in adjustmentRules');
      }
      let adjustmentRule;
      if (adjustmentRulesWithAttr.length === 1) {
        adjustmentRule = adjustmentRulesWithAttr[0];
      }
      if (rule.min && rule.min.value) {
        if (adjustmentRule && adjustmentRule.min && adjustmentRule.min.value) {
          const minR = getStrictMaximumOfMinRule(rule, adjustmentRule);
          combinedRule.min = minR.min;
        }
      } else {
        // basicRule has no min entry -> take it from adjustmentRule
        if (adjustmentRule && adjustmentRule.min) {
          rule.min = adjustmentRule.min;
        }
      }
      if (rule.max && rule.max.value) {
        if (adjustmentRule && adjustmentRule.max && adjustmentRule.max.value) {
          const maxR = getStrictMinimumOfMaxRule(rule, adjustmentRule);
          combinedRule.max = maxR.max;
        }
      } else {
        // basicRule has no max entry -> take it from adjustmentRule
        if (adjustmentRule && adjustmentRule.max) {
          rule.max = adjustmentRule.max;
        }
      }
      resultingRules.push(combinedRule);
    } else {
      resultingRules.push(combinedRule);
    }
  }
  return resultingRules;
};
/**
 * Returns the attributes (name) that exist in the GroupLimitsDictionary for a group.
 */
const getAllLimitingAttributesFromDictionary = (rulesets, ld) => {
  const firstValue = rulesets[0].value;
  let allLimitingAttributes = [];
  if (firstValue) {
    const rulesetValues = rulesets.map(e => e.value);
    for (const rulesetValue of rulesetValues) {
      const gldRuleset = ld[rulesetValue];
      if (gldRuleset && !gldRuleset.rulesetIsValid) {
        if (rulesetValue === firstValue) {
          allLimitingAttributes = gldRuleset.limitingAttrIds;
        } else {
          allLimitingAttributes = uniqueConcat(allLimitingAttributes, gldRuleset.limitingAttrIds);
        }
      }
    }
  }
  return allLimitingAttributes;
};
/**
 * biggerMinValueRule takes one to n IMinValueRules as parameter and returns the one with the biggest value.
 * the returned value is always a deep copy of the passed values
 */
const biggerMinValueRule = (r1, ...rest) => {
  let result = r1;
  while (rest.length) {
    const r2 = rest.shift();
    if (r1.value === r2.value) {
      if (r1.op === '=') {
        result = Object.assign({}, r1);
      } else if (r2.op === '=') {
        result = Object.assign({}, r2);
      } else if (r1.op === '>') {
        // maybe the passed r1 and r2 are of type IMinValuePrepareRule instead of IMinValueRule,
        // then we also need to take the force into account
        const r1temp = r1;
        if (typeof r1temp.force !== 'undefined' && r2.op === '>') {
          if (r1temp.force) {
            result = Object.assign({}, r1);
          } else {
            result = Object.assign({}, r2);
          }
        } else {
          result = Object.assign({}, r1);
        }
      } else if (r2.op === '>') {
        result = Object.assign({}, r2);
      }
    } else {
      result = r1.value > r2.value ? Object.assign({}, r1) : Object.assign({}, r2);
    }
  }
  return result;
};
/**
 * lesserMaxValueRule takes one to n IMinValueRules as parameter and returns the one with the smallest value.
 * the returned value is always a deep copy of the passed values
 */
const lesserMaxValueRule = (r1, ...rest) => {
  let result = r1;
  while (rest.length) {
    const r2 = rest.shift();
    if (r1.value === r2.value) {
      if (r1.op === '=') {
        result = Object.assign({}, r1);
      } else if (r2.op === '=') {
        result = Object.assign({}, r2);
      } else if (r1.op === '<') {
        // maybe the passed r1 and r2 are of type IMinValuePrepareRule instead of IMinValueRule,
        // then we also need to take the force into account
        const r1temp = r1;
        if (typeof r1temp.force !== 'undefined' && r2.op === '<') {
          if (r1temp.force) {
            result = Object.assign({}, r1);
          } else {
            result = Object.assign({}, r2);
          }
        } else {
          result = Object.assign({}, r1);
        }
      } else if (r2.op === '<') {
        result = Object.assign({}, r2);
      }
    } else {
      result = r1.value < r2.value ? Object.assign({}, r1) : Object.assign({}, r2);
    }
  }
  return result;
};
/**
 * applyRulesetIds() applies the ruleset-value to all rules in the ruleset in the ri attribute
 */
const applyRulesetIds = rulesets => {
  return rulesets.map(ruleset => {
    return {
      value: ruleset.value,
      rule: ruleset.rule.map(r => {
        r.ri = {
          rulesetIds: [ruleset.value]
        };
        return r;
      })
    };
  });
};

/**
 * tryMinmizeRule reduces rules when possible
 *
 * only works for IMinMaxRules
 *
 * If left end right have the same value AND both ops contain the equal-sign
 * then the rule can be minimized for a better display in the frontend
 *   the min op will be set to =
 *   the max part will be removed
 *
 * It is important to keep the ri values form the rules!!
 */
const tryMinmizeRule = rule => {
  if (isIMinMaxRule(rule)) {
    if (rule.min && rule.max) {
      if (typeof rule.min.value !== 'undefined' && rule.min.value === rule.max.value) {
        if (/=/.exec(rule.min.op) && /=/.exec(rule.max.op)) {
          return {
            attr: rule.attr,
            min: {
              op: '=',
              value: rule.min.value
            },
            ri: rule.ri
          };
        }
      }
    }
  }
  return rule;
};
/**
 
 
 */
const isEqualIMinMaxRule = (rLeft, rRight) => {
  let minStatus = true;
  let maxStatus = true;
  if (rLeft.min && rRight.min) {
    minStatus = rLeft.attr === rRight.attr && rLeft.min.op === rRight.min.op && rLeft.min.value === rRight.min.value;
  }
  if (rLeft.max && rRight.max) {
    maxStatus = rLeft.attr === rRight.attr && rLeft.max.op === rRight.max.op && rLeft.max.value === rRight.max.value;
  }
  if (rLeft.min && !rRight.min || rLeft.max && !rRight.max) {
    return false;
  }
  return minStatus && maxStatus;
};
/**
 
 
 */
const isEqualIStringRule = (rLeft, rRight) => {
  return rLeft.attr === rRight.attr && rLeft.op === rRight.op && rLeft.values.sort().toString() === rRight.values.sort().toString();
};
/**
 
 
 */
const isEqualIBooleanRule = (rLeft, rRight) => {
  if (isIBooleanValueRule(rLeft) && isIBooleanValueRule(rRight)) {
    return rLeft.attr === rRight.attr && rLeft.op === rRight.op && rLeft.value === rRight.value || rLeft.attr === rRight.attr && rLeft.op !== rRight.op && rLeft.value !== rRight.value;
  } else if (isIBooleanVariableRule(rLeft) && isIBooleanVariableRule(rRight)) {
    return rLeft.attr === rRight.attr && rLeft.op === rRight.op && rLeft.variable === rRight.variable;
  } else {
    // Mixing (with evaluated vars) not supported at the moment
    return false;
  }
};
/**
 
 
 */
const doesValueMatchMinRule = (value, minRule) => {
  if (minRule.op === '=') {
    if (value === minRule.value) {
      return true;
    }
  } else if (minRule.op === '>=') {
    if (value >= minRule.value) {
      return true;
    }
  } else if (minRule.op === '>') {
    if (value > minRule.value) {
      return true;
    }
  }
  return false;
};
/**
 
 
 */
const doesValueMatchMaxRule = (value, maxRule) => {
  if (maxRule.op === '=') {
    if (value === maxRule.value) {
      return true;
    }
  } else if (maxRule.op === '<=') {
    if (value <= maxRule.value) {
      return true;
    }
  } else if (maxRule.op === '<') {
    if (value < maxRule.value) {
      return true;
    }
  }
  return false;
};
/**
 
 
 */
const doesValueMatchRule = (value, rightRule) => {
  if (rightRule.min && rightRule.max) {
    return doesValueMatchMinRule(value, rightRule.min) && doesValueMatchMaxRule(value, rightRule.max);
  } else if (rightRule.min && !rightRule.max) {
    return doesValueMatchMinRule(value, rightRule.min);
  } else if (!rightRule.min && rightRule.max) {
    return doesValueMatchMaxRule(value, rightRule.max);
  }
  return true;
};
/**
 * This function proves if a leftRule is already contained in a rightRule for IMinMaxRules.
 *
 * It also returns true when the rules are equal. In the default usecase this behaviour is tested before inside of
 * compareTwoRules().
 *
 * ATTENTION: isRuleUseful() must not be used here, because it is to strict when variables come into play!
 */
const isLeftIMinMaxRuleContainedInRightIMinMaxRule = (rLeft, rRight) => {
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  if (rLeft.min && rRight.min && rLeft.max && rRight.max) {
    // left and right have min and max
    if (rLeft.attr !== rRight.attr) {
      return false;
    }
    if (rLeft.min.op === '=' || rRight.min.op === '=' || rLeft.max.op === '=' || rRight.max.op === '=') {
      // if one side contains an equal sign the rules can not contain the other (or they are equal, but that is an other case)
      return false;
    }
    if (rRight.min.value < rLeft.min.value && rRight.max.value > rLeft.max.value) {
      return true;
    } else if (rRight.min.value < rLeft.min.value && rRight.max.value === rLeft.max.value) {
      if (rRight.max.op === '<=' || rRight.max.op === '<' && rLeft.max.op === '<') {
        return true;
      }
      return false;
    } else if (rRight.min.value === rLeft.min.value && rRight.max.value > rLeft.max.value) {
      if (rRight.min.op === '>=' || rRight.min.op === '>' && rLeft.min.op === '>') {
        return true;
      }
      return false;
    } else if (rRight.min.value === rLeft.min.value && rRight.max.value === rLeft.max.value) {
      if (rRight.min.op === rLeft.min.op && rRight.max.op === rLeft.max.op) {
        return true;
      }
      if (rRight.min.op === '>=' && rRight.max.op === '<=') {
        return true;
      }
    } else {
      // left rule not in the range of the right rule
      return false;
    }
  } else if (rLeft.min && rRight.min && !rLeft.max && !rRight.max) {
    if (rLeft.attr !== rRight.attr) {
      return false;
    }
    if (rLeft.min.op === '=' || rRight.min.op === '=') {
      // if one side contains an equal sign the rules can not contain the other (or they are equal, but that is an other case)
      return false;
    }
    if (rRight.min.value < rLeft.min.value) {
      return true;
    } else if (rRight.min.value === rLeft.min.value) {
      if (rRight.min.op === rLeft.min.op) {
        return true;
      }
      if (rRight.min.op === '>=') {
        return true;
      }
    } else {
      // right value bigger than left value so it is not contained
      return false;
    }
  } else if (rLeft.max && rRight.max && !rLeft.min && !rRight.min) {
    // left and right have max (and no min)
    if (rLeft.attr !== rRight.attr) {
      return false;
    }
    if (rLeft.max.op === '=' || rRight.max.op === '=') ;
    if (rRight.max.value > rLeft.max.value) {
      return true;
    } else if (rRight.max.value === rLeft.max.value) {
      if (rRight.max.op === rLeft.max.op) {
        return true;
      }
      if (rRight.max.op === '<=') {
        return true;
      }
    } else {
      // right value smaller than left value so it is not contained
      return false;
    }
  } else if (rLeft.min && !rLeft.max && rRight.min && rRight.max) {
    if (rLeft.attr === rRight.attr) {
      if (rLeft.min.op === '=') {
        return doesValueMatchRule(rLeft.min.value, rRight);
      }
    }
    return false;
  } else if (rLeft.min && !rLeft.max && !rRight.min && rRight.max) {
    if (rLeft.attr === rRight.attr) {
      if (rLeft.min.op === '=' && /</.exec(rRight.max.op)) {
        return true;
      } else if (rLeft.min.op === '>' && /=/.exec(rRight.max.op) && rLeft.min.value < rRight.max.value) {
        return true;
      } else if (rLeft.min.op === '>=' && /=/.exec(rRight.max.op) && rLeft.min.value <= rRight.max.value) {
        return false; // TODO: check if this is correct?
      }
    }
  }
  // not compareable
  return false;
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
};
/**
 * This function proves if a leftRule is already contained in a rightRule for IMinMaxRules.
 *
 * It also returns true when the rules are equal. In the default usecase this behaviour is tested before.
 */
const isRightIMinMaxRuleContainedInLeftIMinMaxRule = (rLeft, rRight) => {
  return isLeftIMinMaxRuleContainedInRightIMinMaxRule(rRight, rLeft);
};
/**
 * This function proves if a leftRule is already contained in a rightRule for IStringRules.
 *
 * It also returns true when the rules are equal. In the default usecase this behaviour is tested before.
 */
const isLeftIStringRuleContainedInRightIStringRule = (rLeft, rRight) => {
  if (isEqualIStringRule(rLeft, rRight)) {
    return true;
  }
  if (rLeft.attr !== rRight.attr) {
    return false;
  }
  if (rLeft.op === rRight.op) {
    for (const elem of rLeft.values) {
      if (rRight.values.indexOf(elem) === -1) {
        return false;
      }
    }
    return true;
  }
  return false;
};
/**
 * This function proves if a rightRule is already contained in a leftRule for IStringRules.
 *
 * It also returns true when the rules are equal. In the default usecase this behaviour is tested before.
 */
const isRightIStringRuleContainedInLeftIStringRule = (rLeft, rRight) => {
  return isLeftIStringRuleContainedInRightIStringRule(rRight, rLeft);
};
/**
 
 
 */
const isLeftIMinMaxRuleExtendedUpByRightIMinMaxRule = (rLeft, rRight) => {
  if (rLeft.min && rRight.min && rLeft.max && rRight.max) {
    // only min-max rules can be overlapping
    if (rLeft.max.value === rRight.min.value && (/=/.exec(rLeft.max.op) || /=/.exec(rRight.min.op))) {
      // ranges touch each other
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (rLeft.max.value > rRight.min.value) {
      // overlapping possible, but only in some cases
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (rRight.min.value > rLeft.min.value) {
        return true;
      } else if (rRight.min.value === rLeft.min.value) {
        if (rRight.min.op === rLeft.min.op) {
          return true;
        } else if (rRight.min.op === '>' && rLeft.min.op === '>=') {
          return true;
        } else ;
      } else ;
    }
  }
  return false;
};
/**
 
 
 */
const isLeftIMinMaxRuleExtendedDownByRightIMinMaxRule = (rLeft, rRight) => {
  return isLeftIMinMaxRuleExtendedUpByRightIMinMaxRule(rRight, rLeft);
};
/**
 * compare two rules, a left and a right one.
 * If they can be combined the rNew is set and returned.
 * Otherwise rLeft and rRight are returned to show that they could not be combined.
 */
const compareTwoRules = (rLeft, rRight, opType) => {
  /* eslint-disable @typescript-eslint/no-non-null-assertion */
  if (typeof rLeft.ri === 'undefined') {
    rLeft.ri = {
      rulesetIds: []
    };
  }
  if (typeof rRight.ri === 'undefined') {
    rRight.ri = {
      rulesetIds: []
    };
  }
  if (rLeft.attr !== rRight.attr) {
    return {
      rLeft,
      rRight,
      hint: 'not comparable'
    };
  }
  if (isIMinMaxRule(rLeft) && isIMinMaxRule(rRight)) {
    if (isEqualIMinMaxRule(rLeft, rRight)) {
      const rNew = rLeft;
      rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      return {
        rNew,
        hint: 'isEqualIMinMaxRule'
      };
    } else if (isLeftIMinMaxRuleContainedInRightIMinMaxRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rRight;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rLeft.ri.rulesetIds);
      } else if (opType === 'AND') {
        rNew = rLeft;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      }
      return {
        rNew,
        hint: 'isLeftIMinMaxRuleContainedInRightIMinMaxRule'
      };
    } else if (isRightIMinMaxRuleContainedInLeftIMinMaxRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rLeft;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      } else if (opType === 'AND') {
        rNew = rRight;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rLeft.ri.rulesetIds);
      }
      return {
        rNew,
        hint: 'isRightIMinMaxRuleContainedInLeftIMinMaxRule'
      };
    } else if (isLeftIMinMaxRuleExtendedUpByRightIMinMaxRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rLeft;
        rNew.max = rRight.max;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      } else if (opType === 'AND') {
        rNew = rRight;
        rNew.max = rLeft.max;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rLeft.ri.rulesetIds);
      }
      return {
        rNew,
        hint: 'isLeftIMinMaxRuleExtendedUpByRightIMinMaxRule'
      };
    } else if (isLeftIMinMaxRuleExtendedDownByRightIMinMaxRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rRight;
        rNew.max = rLeft.max;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rLeft.ri.rulesetIds);
      } else if (opType === 'AND') {
        rNew = rLeft;
        rNew.max = rRight.max;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      }
      return {
        rNew,
        hint: 'isLeftIMinMaxRuleExtendedDownByRightIMinMaxRule'
      };
    } else {
      /* FIXME: need to implement this to remove not overlapping rules in AND mode
      } else if isLeftIMinMaxRuleNotOverlappingRightIMinMaxRule() {
      if (opType === 'AND') {
        return {undefined, undefined, hint: 'isLeftIMinMaxRuleNotOverlappingRightIMinMaxRule'};
      } else {
        // for 'OR' this is okay
      }*/
      return {
        rLeft,
        rRight,
        hint: 'IMinMaxRule but no function matches'
      };
    }
  } else if (isIStringRule(rLeft) && isIStringRule(rRight)) {
    if (isEqualIStringRule(rLeft, rRight)) {
      const rNew = rLeft;
      rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      return {
        rNew,
        hint: 'isEqualIStringRule'
      };
    } else if (isLeftIStringRuleContainedInRightIStringRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rRight;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rLeft.ri.rulesetIds);
      } else if (opType === 'AND') {
        // rules differ in AND mode so they can not be combined
        return {
          rLeft,
          rRight,
          hint: 'isLeftIStringRuleContainedInRightIStringRule in AND mode'
        };
      }
      return {
        rNew,
        hint: 'isLeftIStringRuleContainedInRightIStringRule'
      };
    } else if (isRightIStringRuleContainedInLeftIStringRule(rLeft, rRight)) {
      let rNew;
      if (opType === 'OR') {
        rNew = rLeft;
        rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      } else if (opType === 'AND') {
        // rules differ in AND mode so they can not be combined
        return {
          rLeft,
          rRight,
          hint: 'isRightIStringRuleContainedInLeftIStringRule in AND mode'
        };
      }
      return {
        rNew,
        hint: 'isRightIStringRuleContainedInLeftIStringRule'
      };
    } else {
      return {
        rLeft,
        rRight,
        hint: 'IStringRule but no function matches'
      };
    }
  } else if (isIBooleanRule(rLeft) && isIBooleanRule(rRight)) {
    if (isEqualIBooleanRule(rLeft, rRight)) {
      const rNew = rLeft;
      rNew.ri.rulesetIds = uniqueConcat(rNew.ri.rulesetIds, rRight.ri.rulesetIds);
      return {
        rNew,
        hint: 'isEqualIMinMaxRule'
      };
    } else {
      return {
        rLeft,
        rRight,
        hint: 'IBooleanRule but no function matches'
      };
    }
  } else {
    return {
      rLeft,
      rRight,
      hint: 'not comparable'
    };
  }
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
};
/**
 * Internal (recursive) function to compact rules by comparing two rules
 */
const recursiveCompactRules = (internalRules, opType) => {
  if (internalRules.length < 2) {
    throw new Error('recursiveCompactRules requires at least two rules');
  }
  // console.log('\n__newCompactRules ');
  // console.log(inspect(internalRules, {maxStringLength: 120}));
  let newRules = [];
  let keepRules = [];
  for (let leftIndex = 0; leftIndex < internalRules.length - 1; leftIndex++) {
    newRules = [];
    keepRules = [];
    const rLeft = internalRules[leftIndex];
    for (let keepIndex = 0; keepIndex < leftIndex; keepIndex++) {
      keepRules.push(internalRules[keepIndex]);
    }
    for (let rightIndex = leftIndex + 1; rightIndex < internalRules.length; rightIndex++) {
      const rRight = internalRules[rightIndex];
      // console.log('\n\nleftIndex : ' + leftIndex + ' cmp   rightIndex : ' + rightIndex);
      const result = compareTwoRules(rLeft, rRight, opType);
      // console.log('\ncompare  rLeft:');
      // console.log(inspect(rLeft, {maxStringLength: 120}));
      // console.log('\nwith  rRight:');
      // console.log(inspect(rRight, {maxStringLength: 120}));
      // console.log('\ngot:');
      // console.log(inspect(result, {maxStringLength: 120}));
      // FIXME: How to handle when a rule must be removed completely (in AND mode)?
      //        How can that happen?
      if (result.rNew) {
        newRules.push(result.rNew);
      } else if (result.rRight !== undefined) {
        keepRules.push(result.rRight);
      }
    }
    if (newRules.length === 0) {
      // found no changes
      // console.log('\n\n NO CHANGES - CONTINUE');
      keepRules.unshift(rLeft);
    } else {
      // found changes, break to apply them
      // console.log('\n\n FOUND CHANGES - BREAK TO APPLY THEM');
      // console.log('\nnewRules');
      // console.log(inspect(newRules, {maxStringLength: 120}));
      //
      // console.log('\nkeepRules');
      // console.log(inspect(keepRules, {maxStringLength: 120}));
      break;
    }
  }
  if (newRules.length === 0) {
    // no new rules => compacting ends
    return keepRules;
  } else {
    // did some compacting => continue
    keepRules = keepRules.concat(...newRules);
    if (keepRules.length >= 2) {
      return recursiveCompactRules(keepRules, opType);
    } else {
      // only one (or zero) entries left
      return keepRules;
    }
  }
};
/**
 * Function to compact rules depending on the operation Type.
 *
 * Rules must be expanded before!!
 *
 * The opType can be 'AND' | 'OR'.
 *
 * Joining                          3 <= d1 <= 10
 * with                             5 <= d1 <= 9
 * by OR (including) returns        3 <= d1 <= 10
 *
 * But  joining                     3 <= d1 <= 10
 * with                             5 <= d1 <= 9
 * by AND (excluding) returns       5 <= d1 <= 9
 */
const doCompactRules = (rules, opType = 'OR') => {
  // copy an array of objects: first level by map, second by destructuring
  const internalRules = rules.map(rule => Object.assign({}, tryMinmizeRule(rule)));
  if (internalRules.length >= 2) {
    return recursiveCompactRules(internalRules, opType);
  } else {
    return internalRules;
  }
};

/**
 * Implements the Parser interface and stores logging output in a sting.
 * Strongly inspired by the LogfileParser from kopla_domain.
 */
class StringParser {
  constructor() {
    this.groupLevel = 0;
    this.posInGroup = 0;
    this.emptyGroup = 0;
    this.items = [];
    this.content = '';
  }
  /**
   * Iterates over an Array of LoggerItems.
   */
  generateOutput(items) {
    this.items = items;
    for (const item of items) {
      this.posInGroup++;
      this.out(item);
    }
    return this.content;
  }
  /**
   * Stores LoggerItem messages into this.content with handling of group LoggerItems.
   */
  out(item) {
    const logLevel = item.logLevel;
    const message = item.message;
    if (item.logLevel === 'group') {
      this.groupLevel++;
    }
    const spacer = Array(this.groupLevel * 4).join(' ');
    if (this.groupLevel > 0) {
      switch (logLevel) {
        case 'group':
          if (!this.groupIsEmpty()) {
            if (this.groupSeparatorCount && this.groupSeparatorCount > 0) {
              const groupSeparator = Array(this.groupSeparatorCount).join(this.groupSeparatorSymbol);
              this.appendContent([groupSeparator], spacer.slice(0, -2));
            }
            this.appendContent(message, spacer);
          } else {
            this.emptyGroup++;
          }
          break;
        case 'groupEnd':
          if (this.emptyGroup === 0) {
            this.appendContent(message, spacer);
          } else {
            this.emptyGroup--;
          }
          break;
        default:
          this.appendContent(message, spacer);
          break;
      }
      if (item.logLevel === 'groupEnd') {
        this.groupLevel--;
      }
    } else {
      this.appendContent(message);
    }
  }
  /**
   * Appends log-content messages to the content.
   */
  appendContent(message, spacer = '') {
    if (message.length === 1) {
      this.content += `${spacer}${message[0]}\n`;
    } else {
      this.content += spacer + JSON.stringify(message) + '\n';
    }
  }
  /**
   * Checks if an opened group contains loggerItems or other groups.
   */
  groupIsEmpty() {
    let msgFound = 0;
    let groupsOpen = 0;
    for (let i = this.posInGroup; i < this.items.length; i++) {
      const item = this.items[i];
      if (item.logLevel === 'group') {
        groupsOpen++;
      } else if (item.logLevel === 'groupEnd') {
        if (groupsOpen === 0) {
          break;
        }
        groupsOpen--;
      } else {
        msgFound = 1;
        break;
      }
    }
    if (msgFound === 1) {
      return false;
    }
    return true;
  }
}

class BoxLogger {
  /**
   * Only when a logger is passed it will be used.
   */
  constructor(logger) {
    this._logger = logger;
    if (this._logger) {
      // the console output does not reflect the group limiters so we disable it by default
      this._logger.disableConsoleOutput();
    }
  }
  /**
   * Returns the passed logs, if a logger was passed to the constructor otherwise
   * it returns an empty string.
   */
  getLogContent() {
    if (this._logger) {
      const parser = new StringParser();
      return parser.generateOutput(this._logger.getBuffer());
    }
    return '';
  }
  /**
   * Set a log message, if a logger was passed to the constructor.
   */
  log(msg) {
    if (this._logger) {
      this._logger.log(msg);
    }
  }
  /**
   * log message with inspect
   */
  logInspect(msg, data) {
    if (this._logger) {
      this._logger.log('');
      this._logger.log(msg);
      this._logger.log(inspect(data, {
        maxStringLength: 120,
        indent: 2
      }));
    }
  }
  /**
   * write Rulesets to the log
   */
  logRulesets(msg, rulesets) {
    if (this._logger) {
      this._logger.log(msg);
      for (const ruleset of rulesets) {
        this._logger.log('value : ' + ruleset.value);
        for (const rule of ruleset.rule) {
          this._logger.log('  ' + inspect(rule, {
            maxStringLength: 120
          }));
        }
        this._logger.log('');
      }
    }
  }
  /**
   * write a RulesetResults to the log
   */
  logRulesetResult(msg, rulesetResult) {
    if (this._logger) {
      this._logger.log(msg);
      this.logRules('limitingRules : ', rulesetResult.limitingRules);
      this._logger.log('rulesetValue : ' + inspect(rulesetResult.rulesetValue, {
        maxStringLength: 120
      }));
      this._logger.log('');
    }
  }
  /**
   * write rules to the log
   */
  logRules(msg, rules) {
    if (this._logger) {
      this._logger.log(msg);
      for (const rule of rules) {
        this._logger.log('  ' + inspect(rule, {
          maxStringLength: 120
        }));
      }
    }
  }
  /**
   * write one rule to the log
   */
  logRule(msg, rule) {
    if (this._logger) {
      this._logger.log(msg);
      this._logger.log('  ' + inspect(rule, {
        maxStringLength: 120
      }));
    }
  }
  /**
   * write a RulesetLimitsDictionary to the log
   */
  logRulesetLimitsDictionary(msg, rld) {
    if (this._logger) {
      this._logger.log(' ');
      this._logger.log(msg);
      for (const rulesetValue of Object.getOwnPropertyNames(rld)) {
        this._logger.log(' ');
        this._logger.log('"' + rulesetValue + '"');
        this.logRules('  limitingRules : ', rld[rulesetValue].limitingRules);
        this._logger.log('  limitingAttrIds : ' + inspect(rld[rulesetValue].limitingAttrIds, {
          maxStringLength: 120
        }));
        this._logger.log(`  rulesetIsValid : ${rld[rulesetValue].rulesetIsValid ? 'true' : 'false'}`);
      }
    }
  }
  /**
   * write a VerifyResult to the log
   */
  logVerfiyResult(msg, verifyResult) {
    if (this._logger) {
      this._logger.log(msg);
      this._logger.log(`  allRulesApply : ${verifyResult.allRulesApply ? 'true' : 'false'}`);
      this._logger.log(`  rulesetValue : ${verifyResult.rulesetValue}`);
      this.logRules('  limitingRules : ', verifyResult.limitingRules);
      this._logger.log('  data : ' + inspect(verifyResult.data, {
        maxStringLength: 120,
        indent: 2
      }));
    }
  }
  /**
   * Set a logGroupStart message, if a logger was passed to the constructor.
   */
  logGroupStart(msg) {
    if (this._logger) {
      this._logger.group(msg);
    }
  }
  /**
   * Set a logGroupEnd message, if a logger was passed to the constructor.
   */
  logGroupEnd(msg) {
    if (this._logger) {
      this._logger.groupEnd(msg);
    }
  }
}

/**
 
 */
const validateStaticIBooleanRule = rule => {
  if (!rule) {
    throw new Error('rule is undefined');
  }
  if (typeof rule.attr === 'undefined') {
    throw new Error('rule.attr is undefined');
  }
  if (rule.attr === '') {
    throw new Error('rule.attr is empty');
  }
  if (typeof rule.op === 'undefined') {
    throw new Error('rule.op is undefined');
  }
  if (rule.op === '') {
    throw new Error('rule.op is empty');
  }
  if (booleanOperatorsAsArray.indexOf(rule.op) === -1) {
    throw new Error('rule.op "' + rule.op + '" not a valid operand');
  }
  if (typeof rule.value === 'undefined' && !rule.variable) {
    throw new Error('undefined subtype of IBooleanRule');
  }
};

/**
 
 */
const validateStaticIStringRule = rule => {
  if (!rule) {
    throw new Error('rule is undefined');
  }
  if (typeof rule.attr === 'undefined') {
    throw new Error('rule.attr is undefined');
  }
  if (rule.attr === '') {
    throw new Error('rule.attr is empty');
  }
  if (typeof rule.op === 'undefined') {
    throw new Error('rule.op is undefined');
  }
  if (rule.op === '') {
    throw new Error('rule.op is empty');
  }
  if (stringOperatorsAsArray.indexOf(rule.op) === -1) {
    throw new Error('rule.op "' + rule.op + '" not a valid operand');
  }
  if (typeof rule.values === 'undefined') {
    throw new Error('rule.values is undefined');
  }
  if (rule.values.length === 0) {
    throw new Error('rule.values is empty');
  }
};

/**
 * The Basicbox covers the storing and retrieval of Attributes and Shapes and the logging.
 * It its used to build the Rulebox and to keep the Rulebox small and clearly.
 *
 * TODO: Think about letting the Basicbox have a BoxLogger not be one
 */
class Basicbox extends BoxLogger {
  /**
   * Only when a logger is passed it will be used.
   */
  constructor(unitSystem, logger) {
    super(logger);
    this._attributes = [];
    this._shapes = [];
    this._data = {};
    this.unitSystem = unitSystem;
    // Run the verification in the next tick, so we can be compliant with ES6
    setTimeout(() => {
      ReflectionCloner.verifyDecorator(this);
    });
  }
  // region : Attribute stuff
  /**
   * Define the attributes that are valid in your Rulebox. These values are used for consistency checks.
   */
  addAttribute(attr) {
    this.validateAttribute(attr);
    if (typeof attr.visible === 'undefined') {
      attr.visible = true;
    }
    attr.unit = getUnit(attr.type, this.unitSystem);
    this._attributes.push(attr);
    this.log('attribute ' + attr.name + ' added');
  }
  /**
   * Validates the attribut and throws an exception when something is wrong.
   * Used by this.addAttribute().
   */
  validateAttribute(attr) {
    if (attributeTypeAsArray.findIndex(at => at === attr.type) === -1) {
      throw new Error('attribute type "' + attr.type + '" is not valid!');
    }
    if (this._attributes.findIndex(i => i.name === attr.name) !== -1) {
      throw new Error('attribute "' + attr.name + '" already defined!');
    }
  }
  /**
   * Get the list of undefined attributes.
   * This method does *not* check the functions! See getUndefinedAttributesAndFunctions()
   * Returns an array of attribute names that are not defined.
   */
  getUndefinedAttributes() {
    const result = [];
    for (const attr of this._attributes) {
      if (isAttributeValue(attr)) {
        if (typeof this._data[attr.name] === 'undefined') {
          result.push(attr.name);
        }
      }
    }
    return result;
  }
  /**
   * Get the list of defined attributes (including values defined by functions).
   * Returns an array of attribute names that are defined.
   */
  getDefinedAttributes() {
    return this._attributes.filter(attr => {
      if (attr.type === 'DISTANCE' || attr.type === 'PERCENTAGE' || attr.type === 'ANGLE') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined' && !isNaN(val);
      } else if (attr.type === 'MATERIAL') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined';
      } else if (attr.type === 'BOOLEAN') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined';
      }
    }).map(attr => attr.name);
  }
  /**
   * Get the list of defined attributes (including values defined by functions).
   * Returns an array of attribute names that are defined, but not the one passed by attrId,
   * and not depending on attrid;
   */
  getDefinedAttributesWithoutCurrent(attrId) {
    return this._attributes.filter(attr => {
      // Because we need attr in the following filters we cannot replace this by getDefinedAttributes(), because
      // it only returns the names.
      if (attr.type === 'DISTANCE' || attr.type === 'PERCENTAGE' || attr.type === 'ANGLE') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined' && !isNaN(val);
      } else if (attr.type === 'MATERIAL') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined';
      } else if (attr.type === 'BOOLEAN') {
        const val = this.getValue(attr.name);
        return typeof val !== 'undefined';
      }
    }).filter(attr => {
      return attr.name !== attrId;
    }).filter(attr => {
      return !(isAttributeFunction(attr) && attr.dependsOn.indexOf(attrId) > -1);
    }).map(attr => attr.name);
  }
  /**
   * Get the list of undefined attributes, including the ones that come from functions.
   * See getUndefinedAttributes() when you only want to check the attributes.
   * Returns an array of attribute names that are not defined.
   */
  getUndefinedAttributesAndFunctions() {
    const result = [];
    for (const attrObj of this._attributes) {
      if (isAttributeValue(attrObj)) {
        if (typeof this._data[attrObj.name] === 'undefined') {
          result.push(attrObj.name);
        }
      } else {
        const attr = attrObj;
        if (typeof attr.fn() === 'undefined' || isNaN(attr.fn())) {
          result.push(attr.name);
        }
      }
    }
    return result;
  }
  /**
   * Returns the definiton of the attribute defined by attrId.
   */
  getAttribute(attrName) {
    const attrIndex = this._attributes.findIndex(i => i.name === attrName);
    return this._attributes[attrIndex];
  }
  /**
   * Returns all attribute definitions.
   */
  getAttributes() {
    return this._attributes;
  }
  /**
   * Returns all attributes (sorted) that have the inputOrder set.
   */
  getInputOrderAttributes() {
    return this._attributes.filter(a => isAttributeValue(a) && typeof a.inputOrder !== 'undefined').sort((a, b) => {
      var _a$inputOrder, _b$inputOrder;
      return ((_a$inputOrder = a.inputOrder) != null ? _a$inputOrder : 0) - ((_b$inputOrder = b.inputOrder) != null ? _b$inputOrder : 0);
    });
  }
  // endregion
  // region : Shape stuff
  /**
   * Define the shapes that are valid in your Rulebox.
   *
   * addShape() must be called after addAttribute() because it verifies that the attributes in the rules
   * do exist in the rulebox!
   */
  addShape(shape) {
    this.validateShape(shape);
    this._shapes.push(shape);
  }
  /**
   * Validates the shape and throws an exception when something is wrong.
   * Used by this.addShape().
   * With the implementation of FastLine it is no longer an error that
   * the list of rulesets is empty.
   */
  validateShape(shape) {
    if (!shape) {
      throw new Error('shape is not defined');
    }
    if (typeof shape.name === 'undefined') {
      throw new Error('shape.name is not defined');
    }
    if (shape.name === '') {
      throw new Error('shape.name is empty');
    }
    if (typeof shape.description === 'undefined') {
      throw new Error('shape.description is not defined');
    }
    if (typeof shape.rulesetsFn === 'undefined') {
      throw new Error('shape.rulesetsFn is not defined');
    }
    if (this._shapes.findIndex(i => i.name === shape.name) !== -1) {
      throw new Error('shape "' + shape.name + '" already defined!');
    }
    this.validateRulesets(shape.rulesetsFn());
  }
  /**
   * Returns the definiton of the shape defined by shapeName.
   */
  getShape(shapeName) {
    const shapeIndex = this._shapes.findIndex(i => i.name === shapeName);
    return this._shapes[shapeIndex];
  }
  /**
   * Returns the ruleset of a shape.
   */
  getRulesets(shapeName) {
    const shape = this.getShape(shapeName);
    if (!shape) {
      throw new Error('shape ' + shapeName + ' not found!');
    }
    // Fixme: getRulesets is also used to getRulesetNames() this calles every time the
    //        rulesetsFn()
    //        think about making the rules in the Ruleset a ruleFn() and only load this when it is required
    // call the rulesetFn now on runtime
    // that allows to check against exisiting data and make the rules more precise.
    return shape.rulesetsFn();
  }
  /**
   * Returns all shape definitions.
   */
  getShapes() {
    return this._shapes;
  }
  // endregion
  // region : Group stuff
  /**
   * Validates a rulset array and throws an exception when something is wrong.
   */
  validateRulesets(rulesets) {
    for (const ruleset of rulesets) {
      for (const rule of ruleset.rule) {
        if (!rule) {
          throw new Error('rule is undefined');
        }
        if (isIMinMaxRule(rule)) {
          this.validateDynamicIMinMaxRule(rule);
        } else if (isIStringRule(rule)) {
          this.validateDynamicIStringRule(rule);
        } else if (isIBooleanRule(rule)) {
          this.validateIBooleanRule(rule);
        } else {
          throw new Error('undefined rule type');
        }
      }
    }
  }
  /**
   * validate a MinMaxRule dynamical (with data) and throw an exception
   * when something is wrong.
   */
  validateDynamicIMinMaxRule(rule) {
    if (!rule) {
      throw new Error('rule is undefined');
    }
    // test without data and attributes
    validateStaticIMinMaxRule(rule);
    // test with data and attributes
    if (this._attributes.findIndex(i => i.name === rule.attr) === -1) {
      throw new Error('rule.attr "' + rule.attr + '" is not defined in the attributes!');
    }
  }
  /**
   * Validate a StringRule dynamical (with data) and throw an exception when something is wrong.
   */
  validateDynamicIStringRule(rule) {
    if (!rule) {
      throw new Error('rule is undefined');
    }
    // test without data and attributes
    validateStaticIStringRule(rule);
    // test with data and attributes
    if (this._attributes.findIndex(i => i.name === rule.attr) === -1) {
      throw new Error('rule.attr "' + rule.attr + '" is not defined in the attributes!');
    }
  }
  /**
   * Validate a IBooleanRule dynamical (with data) and throw an exception when something is wrong.
   */
  validateIBooleanRule(rule) {
    if (!rule) {
      throw new Error('rule is undefined');
    }
    // test without data and attributes
    validateStaticIBooleanRule(rule);
    // test with data and attributes
    if (this._attributes.findIndex(i => i.name === rule.attr) === -1) {
      throw new Error('rule.attr "' + rule.attr + '" is not defined in the attributes!');
    }
  }
  /**
   * Returns the (sorted) RulesetValues for a array of rulesets.
   */
  getAllRulesetValuesOfRulesets(rulesets) {
    return rulesets.map(e => e.value).sort();
  }
  // endregion
  // region : Data stuff
  /**
   * Chainable method to set data.
   * setData() does no longer do a resetData() for you.
   * If you need it, call it.
   */
  // FIXME: Is it a problem in the designer to change DataDictionary to the real Type like EasyClipParams?
  setData(data) {
    this.logGroupStart('setData');
    Object.keys(data).forEach(dataKey => {
      this.addData(dataKey, data[dataKey]);
    });
    this.logGroupEnd('');
    return this;
  }
  /**
   * Chainable method to reset data.
   */
  resetData() {
    this._data = {};
    this.log('resetData called');
    return this;
  }
  /**
   * Returns a clean instance of a box created with the same
   * constructor parameters as orginal object.
   */
  cleanClone() {
    return ReflectionCloner.cleanClone(this);
  }
  /**
   * Chainable method to add (metric/imperial) values to the data.
   */
  addData(attrId, value) {
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    if (attrIndex === -1) {
      throw new Error('attribute "' + attrId + '" is not defined!');
    }
    this.log('addData called for ' + attrId);
    if (this.unitSystem === UnitSystem.metric) {
      this._data[attrId] = value;
    } else {
      if (this._attributes[attrIndex].type === 'DISTANCE') {
        this._data[attrId] = toMetricDistanceMilli(UnitSystem.imperial, value);
      } else {
        this._data[attrId] = value;
      }
    }
    return this;
  }
  /**
   * Returns the data that was set.
   * Use getExpandedAllData()  or getExpandedVisibleData()
   */
  getMetricData() {
    return this._data;
  }
  /**
   * Returns all, the set and the evaluated attrubutes that are marked 'visible'.
   *
   * Hints:
   *  - Use getMetricData() to only get the set values
   *  - Use getExpandedAllData() to also get the in-visible values
   */
  getExpandedVisibleData() {
    const result = {};
    if (this._data && Object.keys(this._data).length) {
      for (const attr of this._attributes) {
        if (attr.visible && !/_suggestion$/.exec(attr.name)) {
          if (this.unitSystem === UnitSystem.imperial && attr.type === 'DISTANCE') {
            const valueMM = this.getValue(attr.name);
            result[attr.name] = valueMM && round4$1(fromMetricDistanceMilli(UnitSystem.imperial, valueMM));
          } else {
            result[attr.name] = this.getValue(attr.name);
          }
        }
      }
    }
    return result;
  }
  /**
   * Returns all, the set and the evaluated attrubutes.
   *
   * Hints:
   *  - Use getMetricData() to only get the set values
   *  - Use getExpandedVisibleData to only get the visible values
   */
  getExpandedAllData() {
    const result = {};
    if (this._data && Object.keys(this._data).length) {
      for (const attr of this._attributes) {
        if (this.unitSystem === UnitSystem.imperial && attr.type === 'DISTANCE') {
          const valueMM = this.getValue(attr.name);
          result[attr.name] = valueMM && round4$1(fromMetricDistanceMilli(UnitSystem.imperial, valueMM));
        } else {
          result[attr.name] = this.getValue(attr.name);
        }
      }
    }
    return result;
  }
  /**
   * Returns only the expanded attributes that have the '_suggestion' suffix in their name.
   * For easier usage on the application side the suffix  '_suggestion' is stripped of
   */
  getSuggestions() {
    const result = {};
    if (this._data && Object.keys(this._data).length) {
      for (const attr of this._attributes) {
        if (/_suggestion$/.exec(attr.name)) {
          const visibleAttrName = attr.name.replace('_suggestion', '');
          if (this.unitSystem === UnitSystem.imperial && attr.type === 'DISTANCE') {
            const valueMM = this.getValue(attr.name);
            result[visibleAttrName] = valueMM && round4$1(fromMetricDistanceMilli(UnitSystem.imperial, valueMM));
          } else {
            result[visibleAttrName] = this.getValue(attr.name);
          }
        }
      }
    }
    return result;
  }
  /**
   * Returns the values for the given attribute names attrNames.
   */
  getValues(attrIdArray) {
    return attrIdArray.map(e => this.getValue(e));
  }
  /**
   * Check if data is set for an attribute
   */
  hasData(attrId) {
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    if (attrIndex === -1) {
      return false;
    }
    return typeof this._data[attrId] !== 'undefined';
  }
  /**
   * Returns the value for an attribute given by attrName.
   */
  getValue(attrId) {
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    if (attrIndex === -1) {
      throw new Error('attribute "' + attrId + '" is not defined!');
    }
    let result;
    const attr = this._attributes[attrIndex];
    if (isAttributeValue(attr)) {
      result = this._data[attrId];
    } else {
      // isAttributeFunction
      result = attr.fn();
    }
    return result;
  }
  /**
   * Returns the string value for an attribute given by attrName.
   * Throws if the attribute is not a string type.
   */
  getStringValue(attrId) {
    const result = this.getValue(attrId);
    // attrIndex cannot be -1 otherwise this.getValue() would have thrown an error.
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    const attr = this._attributes[attrIndex];
    if (!isAttributeTypeMaterial(attr.type)) {
      // only AttributeType Material uses strings
      throw new Error('getStringValue only available for string types like MATERIAL.');
    }
    return result;
  }
  /**
   * Returns the boolean value for an attribute given by attrName.
   * Throws if the attribute is not a boolean type.
   */
  getBooleanValue(attrId) {
    const result = this.getValue(attrId);
    // attrIndex cannot be -1 otherwise this.getValue() would have thrown an error.
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    const attr = this._attributes[attrIndex];
    if (attr.type !== 'BOOLEAN') {
      throw new Error('getBooleanValue only available for BOOLEAN types');
    }
    return !!result;
  }
  /**
   * Returns the number value for an attribute given by attrName.
   * Throws if the attribute is not a number type.
   *
   * @param attrId attrId
   */
  getNumberValue(attrId) {
    const result = this.getValue(attrId);
    // attrIndex cannot be -1 otherwise this.getValue() would have thrown an error.
    const attrIndex = this._attributes.findIndex(i => i.name === attrId);
    const attr = this._attributes[attrIndex];
    if (isAttributeTypeMaterial(attr.type)) {
      throw new Error('getNumberValue only available for number types like DISTANCE or ANGLE.');
    }
    // avoid NaN
    if (typeof result === 'number') {
      return round4$1(result);
    }
    return undefined;
  }
}

class Rulebox extends Basicbox {
  constructor(unitSystem, logger) {
    super(unitSystem, logger);
    this.attributeAtVerify = '';
  }
  /**
   * orderedVerify() checks the attributes one after the other. If the attribute under investigation is
   * fitting the rules the next one is verified.
   *
   * If one attribute is not compatible with the rules, then only previous limiting rules are returned.
   *
   * The order of the attributes is the order of inputOrder attribute.
   *
   * Related tasks: IGL-352
   */
  orderedVerify() {
    const data = {};
    this.log('orderedVerify START');
    const inputAttributes = this.getInputOrderAttributes();
    this.logInspect('inputAttributes', inputAttributes);
    // create a copy of ourself to change the data
    const cleanRulebox = this.cleanClone();
    // cleanRulebox unitSystem is always metric as the origin has already converted its data values
    cleanRulebox.unitSystem = UnitSystem.metric;
    this.logInspect(' data 1', data);
    const attributes = [];
    for (const attr of inputAttributes) {
      this.logInspect('attr :', attr);
      const attrId = attr.name;
      if (attr.visible) {
        data[attrId] = this.getValue(attrId);
        this.logInspect(' data 2', data);
        cleanRulebox.setData(data);
        this.logInspect('clone data :', cleanRulebox.getExpandedAllData());
        // eslint-disable-next-line prefer-const
        let {
          allRulesApply,
          limitingRules
        } = cleanRulebox.verifyAttribute(attrId);
        // Workaround to make IGL-743 work properly
        // TODO: improve this
        if (attrId === 'materialShrinkage') {
          allRulesApply = true;
        }
        attributes.push({
          attrId,
          limitingRules,
          allRulesApply
        });
        if (!allRulesApply) {
          break;
        }
      }
    }
    const rv = {
      attributes
    };
    this.log('orderedVerify END');
    return rv;
  }
  /**
   * Method verify() proves the given data against the rules and returns the result.
   * All rules must be TRUE!
   *
   * Usually you have definitions like this for new parts:
   *
   * Group 1               col 1                              col 2                     col 3
   *
   * | d1     |    d1 >= 5     &&  d1 <  10   |    d1 >= 10    &&  d1  <= 15   |   d1 >= 15  &&  d1  <  20   |
   * | b2     |    b2 >= 0.75  &&  b2 <= 2    |    b2 >= 0.75  &&  b2  <= 2.5  |   b2 >= 1   &&  b2 <= 3.5   |
   *
   * Group 2                       col 1                                          col 2
   * | d3     |            d3 >= d2  &&  d3 < 15             |            d3 >= d2  &&  d3  <  31            |
   *
   *
   * For each group we need to find one 'column' that fits.
   *
   * For example:  d1 = 6, b2 = 2, d3 = 16
   *
   * Here group_1 has the value (col) 1 and in group_2 has the value (col) 2.
   *
   * Details for each group are calculated in verifyRuleset().
   */
  verify() {
    let rulesFromAllShapes = [];
    let validRulesetValue;
    this.logGroupStart('START verify');
    // FIXME: Suggestion:
    //        First identify the groupSets that are not possible because of the data.
    //        Then continue on the remaining groupSets.
    //        Maybe we also need to change the interface for the returned values.
    for (const shape of this.getShapes()) {
      this.logGroupStart('START verify shape ' + shape.name);
      const rulesets = this.getRulesets(shape.name);
      if (rulesets.length > 0) {
        let limitingRulesInShape = [];
        let allRulesApplyInShape = false;
        const rulesetResult = this.verifyRuleset(rulesets);
        this.logRulesetResult('verifyRuleset result :', rulesetResult);
        if (rulesetResult.limitingRules.length) {
          limitingRulesInShape = rulesetResult.limitingRules;
        } else {
          validRulesetValue = rulesetResult.rulesetValue;
        }
        allRulesApplyInShape = !limitingRulesInShape.length;
        this.log(`allRulesApplyInShape : ${allRulesApplyInShape ? 'true' : 'false'}`);
        this.logRules('limitingRulesInShape', limitingRulesInShape);
        if (allRulesApplyInShape) {
          // found a valid ruleset this shape
          this.log('END verify (Found a matching ruleset in one shape. Stopping premature.)');
          return {
            allRulesApply: true,
            rulesetValue: validRulesetValue,
            limitingRules: this.considerUnitSystem(limitingRulesInShape),
            data: this.getExpandedAllData()
          };
        } else {
          rulesFromAllShapes = rulesFromAllShapes.concat(limitingRulesInShape);
        }
      }
      this.logGroupEnd('END verify shape ' + shape.name);
    }
    rulesFromAllShapes = this.joinRules(rulesFromAllShapes);
    // The list of limiting rules may be too long for the customer,
    // so we try to reduce it to the most important rules.
    // If it not can be reduced all rules will be returned.
    // TODO: Check if getAndMarkImportantRules is realy useful in practice
    rulesFromAllShapes = this.getAndMarkImportantRules(rulesFromAllShapes);
    this.logRules('ruleStore', rulesFromAllShapes);
    this.log('END verify  (Found no matching ruleset in all shapes)');
    return {
      allRulesApply: false,
      rulesetValue: undefined,
      limitingRules: this.considerUnitSystem(rulesFromAllShapes),
      data: this.getExpandedAllData()
    };
  }
  /**
   * Returns either the LimitingRules for the given attrId or returns that all rules apply.
   *
   * Attention: If other values are set, they *ARE* taken into account!
   *
   * If data is set (e.g. by setData) before and there is a value for that variable, then rules with variables are
   * expanded, otherwise you get back the rule with the not expanded variable.
   *
   *
   * The result always contains limitingRules that depend on the data that was set before.
   *
   * If no data is set and you ask for "d1" then the limitingRules will contain all d1 rules.
   * If you set some data (like "b2" = 2) and you ask for "d1" then the limitingRules will contain all d1 rules that from
   * rulesets that match "b2" === 2.
   *
   *
   *
   * --------
   * Implementation
   *
   * - Get the list of attributes that are already set in data
   * - Determine the possible rulesets for the set attributes (not for the requested attribute)
   * - Get possible rules that
   *     - are in the possible rulesets
   *     - have the requested attrId
   */
  verifyAttribute(attrId) {
    this.attributeAtVerify = attrId;
    const rulesForAttrIdInAllShapes = [];
    this.logGroupStart('START verifyAttribute for ' + attrId);
    // Get all the attributes that have data, but not the one we investigate on!
    const attributesToLimitThePossibleRulesets = this.getDefinedAttributesWithoutCurrent(attrId);
    this.logInspect('attributesToTest (because they have values in the data)', attributesToLimitThePossibleRulesets);
    const impossibleRulesetValuesComplete = [];
    for (const shape of this.getShapes()) {
      this.logGroupStart('START verify shape ' + shape.name);
      const rulesets = this.getRulesets(shape.name);
      this.logRulesets('rulesets:', rulesets);
      const allRulesetValues = this.getAllRulesetValuesOfRulesets(rulesets);
      let possibleRulesetValues = allRulesetValues;
      this.logInspect('possibleRulesetValues :', possibleRulesetValues);
      // Get possible rulesets for attributes that have values
      attributesToLimitThePossibleRulesets.forEach(testAttrId => {
        if (possibleRulesetValues.length > 0) {
          // continue while length>0
          this.log(`\n Test on attrId   ${testAttrId} = ${this.getValue(testAttrId)}`);
          if (isAttributeInRulesets(testAttrId, rulesets)) {
            // attribute is contained in the rulesets of this shape
            // get rulesetValues for attribute
            const rulesetValues = this.getRulesetValuesForAttributeWithData(rulesets, testAttrId);
            this.logInspect('rulesetValues ', rulesetValues);
            possibleRulesetValues = intersectArray(possibleRulesetValues, rulesetValues);
            this.logInspect('possibleRulesetValues (after intersect with previous values of the current ruleset)', possibleRulesetValues);
          } else {
            // FIXME: may be the attribute is contained only in an other shape
            //        this is only possible for PistonRings at the moment (like materialShrinkage in PR_SS)
            //        have to find out if this is the case
            //        and think about what to do then
            // This is maybe a problem when the attribute is only in one shape (like materialShrinkage in PR_SS)
            this.log('Attribute ' + testAttrId + ' is not mentioned in the rulesets of shape (' + shape.name + '), so we can ignore it.');
          }
        } else {
          this.log('Test on attrId ' + testAttrId + ' skipped because  possibleRulesetValues is []');
        }
      });
      this.logInspect('possibleRulesetValues (final) ', possibleRulesetValues);
      const impossibleRulesetValues = allRulesetValues.filter(v => {
        return possibleRulesetValues.indexOf(v) < 0;
      });
      impossibleRulesetValuesComplete.push(...impossibleRulesetValues);
      this.logInspect('impossibleRulesetValues', impossibleRulesetValues);
      this.log('');
      for (const ruleset of rulesets) {
        if (possibleRulesetValues.indexOf(ruleset.value) >= 0) {
          let rulesetRuleArray = [];
          for (const rule of ruleset.rule) {
            if (isValidAttrId(attrId, rule)) {
              let workingRule = rule;
              if (this._data && Object.keys(this._data).length) {
                workingRule = this.expandVariableInRule(rule);
              }
              this.logRule('  check rule ', workingRule);
              if (isRuleUseful(workingRule)) {
                this.log('Rule isUsefull');
                rulesetRuleArray.push(workingRule);
              } else {
                this.log('Rule is not isUsefull');
              }
            }
          }
          this.logRules('rulesetRuleArray (before compacting)', rulesetRuleArray);
          // rules inside of one ruleSet for the same attrId must be handled with 'AND'
          rulesetRuleArray = doCompactRules(rulesetRuleArray, 'AND');
          this.logRules('rulesetRuleArray (after compacting)', rulesetRuleArray);
          rulesetRuleArray.map(e => {
            rulesForAttrIdInAllShapes.push(e);
          });
          this.logRules('rulesForAttrIdInAllShapes', rulesForAttrIdInAllShapes);
        }
        // rulesForAttrIdInAllShapes = doCompactRules(rulesForAttrIdInAllShapes, 'OR');
      }
      this.logGroupEnd('END verify shape ' + shape.name);
    }
    this.log('\n\n===========================================================================');
    this.logRules('rulesForAttrIdInAllShapes (valid and usefull) for attrId "' + attrId + '" : ', rulesForAttrIdInAllShapes);
    if (rulesForAttrIdInAllShapes.length === 0) {
      this.log('The current attribute attrId is not contained in one of the rulesets!');
      this.log('So nothing limits this attribute, which means that all rules apply.');
      this.logGroupEnd('END verifyAttribute (early)');
      return {
        limitingRules: [],
        allRulesApply: true
      };
    }
    this.logInspect('impossibleRulesetValuesComplete (for all shapes)', impossibleRulesetValuesComplete);
    this.log('');
    // TODO: check if  joinRulesByAttrId() could be replaced here by compactRules(..., 'OR')
    //      joinRulesByAttrId() uses compactRules, so still need to check the differences
    const joinedRules = this.joinRulesByAttrId(attrId, rulesForAttrIdInAllShapes);
    this.logRules('joindRules', joinedRules);
    const all = this.getAndMarkImportantRules(joinedRules);
    this.logRules('all', all);
    let returnValue = {
      limitingRules: all,
      allRulesApply: false
    };
    if (typeof this.getValue(attrId) !== 'undefined') {
      this.log('Attribute ' + attrId + ' itself is defined.');
      // When the attribut itself is defined do not check the limiting,
      // because it would self-limit the results,
      // but use the result of allRulesApply
      returnValue.allRulesApply = this.executeRulesInSameAttributeContext(all, attrId);
    } else {
      this.log('Attribute ' + attrId + ' itself is *NOT* defined.');
      returnValue = {
        limitingRules: all,
        allRulesApply: false
      };
    }
    this.logGroupEnd('END verifyAttribute');
    return returnValue;
  }
  /**
   * Returns the names of all Rulesets.
   */
  getRulesetNames() {
    const allNames = [];
    for (const shape of this.getShapes()) {
      const prRulesets = this.getRulesets(shape.name);
      for (const ruleset of prRulesets) {
        const rulesetName = ruleset.value;
        if (allNames.indexOf(rulesetName) >= 0) {
          throw new Error('RulesetName ' + rulesetName + ' is not unique!');
        } else {
          allNames.push(rulesetName);
        }
      }
    }
    return allNames.sort();
  }
  /**
   * Returns the ruleset-values from the given rulesets having the given attrId and returning true.
   *
   * When all rules could be expanded (there are values for the required variables) then the result can
   * be empty. If some values for the required variables are not (yet) set the we don't know if the
   * rule applies or not. So all values (getAllRulesetValuesOfAGroup) are still possible.
   */
  getRulesetValuesForAttributeWithData(rulesets, attrId) {
    const localDebug = false;
    const definedAttribues = this.getDefinedAttributes();
    if (definedAttribues.indexOf(attrId) === -1) {
      throw new Error('Data not set for attribute ' + attrId + '!');
    }
    // Test if rulesets contains the attrId
    const attrIsInRulesets = isAttributeInRulesets(attrId, rulesets);
    let result = [];
    if (attrIsInRulesets) {
      try {
        // In the following the rules must be executed for attributes with data, to filter the rulesets.
        result = rulesets.filter(ruleset => this.doAllRulesInRulesetApplyToCurrentData(ruleset, attrId)).map(rulset => rulset.value);
        if (localDebug) ;
      } catch (e) {
        this.logInspect('cought error', e);
        // when executeRulesetByAttribute failed (because right vars could not be evaluated)
        // the the list must not be empty, it must contain all possible values
        result = this.getRulesetNames();
      }
      // FIXME: ??? remove the following soon by splitting the result into limitingRules and validRules ???
      /* re-activate temporary implementation to work around the limitingValues Problem */
      // for PistonRings (with 4 shapes) the following is not allowed
      // for SleeveBearings or EC (with 1 shape) the following is required
      // Temporary implementation:
      if (this.getShapes().length === 1) {
        if (result.length === 0) {
          // must be changed to something like getAllPossibleRulesetNames because we filtered out some before
          result = this.getRulesetNames();
        }
      }
      /* re-activate temporary implementation to work around the limitingValues Problem */
    } else {
      result = this.getRulesetNames();
    }
    return result;
  }
  /**
   * Returns a list of 'important' rules. If there are no important rules, then all rules will be returned.
   *
   * When an attribute has limiting rules in *all* relevant rulesets, then this rule is important, because you cannot
   * find a solution without changing this attribute. In this case affectsAllRulesets is set to true.
   *
   * It is also valid that more than one rules (that can not be combined) cover all rulesets.
   *
   * If a ruleset is relevant is depending on its shape and group. d1 is e.g. in the 'first_group' so it must be checked that
   * the limitingRules contain all RulesetNames that come from Groups matching the name 'first_group'.
   */
  getAndMarkImportantRules(allRules) {
    this.log('getAndMarkImportantRules START');
    // get all attribute ids from the passed allRules
    const allAttrIds = getUniqueRuleAttrIds(allRules);
    this.logInspect('allAttrIds', allAttrIds);
    const importantRules = [];
    this.logRules('allRules', allRules);
    for (const attrId of allAttrIds) {
      // e.g.  attrId  = d1
      this.logInspect('Check attribute', attrId);
      const allRulesetNames = this.getRulesetNames();
      // e.g.  allRulesetNames  = [ 'PR_SAS_1', 'PR_SAS_2', ..., 'PR_SS_2' ]
      const attrRules = getRulesByAttrNameFromLimitingRules(attrId, allRules);
      let rid = [];
      for (const aR of attrRules) {
        var _aR$ri;
        rid = uniqueConcat(rid, (_aR$ri = aR.ri) == null ? void 0 : _aR$ri.rulesetIds);
      }
      if (rid.toString() === allRulesetNames.toString()) {
        // attrId has limiting rules in all (relevant) rulesets
        attrRules.map(elem => {
          elem.ri.affectsAllRulesets = true;
        });
        importantRules.push(...attrRules);
      }
    }
    this.logRules('importantRules', importantRules);
    if (importantRules.length) {
      this.log('FOUND importantRules');
      this.logRules('returning importantRules', importantRules);
      this.log('getAndMarkImportantRules END (I)');
      return importantRules;
    } else {
      this.log('FOUND NO importantRules');
      this.logRules('returning allRules', allRules);
      this.log('getAndMarkImportantRules END (II)');
      return allRules;
    }
  }
  considerUnitSystem(rules) {
    return rules.map(rule => {
      if (isIMinMaxRule(rule) && this.unitSystem === UnitSystem.imperial) {
        const attrName = rule.attr;
        if (rule.min) {
          const attrIndex = this._attributes.findIndex(i => i.name === attrName);
          const attr = this._attributes[attrIndex];
          if (attr.type === 'DISTANCE') {
            rule.min.value = fromMetricDistanceMilli(UnitSystem.imperial, rule.min.value);
          }
        }
        if (rule.max) {
          const attrIndex = this._attributes.findIndex(i => i.name === attrName);
          const attr = this._attributes[attrIndex];
          if (attr.type === 'DISTANCE') {
            rule.max.value = fromMetricDistanceMilli(UnitSystem.imperial, rule.max.value);
          }
        }
      }
      return rule;
    });
  }
  /**
   * Internal method to verify the ruleset for one shape.
   *
   * Check is done column by column. If all rules apply for one column/ruleset then groupId and rulesetValue are returned!
   *
   * If not the next column/ruleset is checked. But here we only need to check the attributes that
   * did not match in the column/ruleset before.
   */
  verifyRuleset(rulesets) {
    this.logGroupStart('START check Rules');
    this.logRulesets('verifyRuleset', rulesets);
    // calculate the limiting Rules
    const limitingRulesetsDictionary = this.determineRulesetLimitations(rulesets);
    this.log('\n\n');
    this.logRulesetLimitsDictionary('groupLimitingRulesDictionary', limitingRulesetsDictionary);
    // It can be that one rulesetIsValid
    for (const rulesetValue of Object.getOwnPropertyNames(limitingRulesetsDictionary)) {
      const limit = limitingRulesetsDictionary[rulesetValue];
      if (limit.rulesetIsValid) {
        this.logGroupEnd('END check Rules (Found a matching ruleset)');
        return {
          limitingRules: [],
          rulesetValue
        };
      }
    }
    const commonLimitingAttributes = getCommonLimitingAttributesFromDictionary(rulesets, limitingRulesetsDictionary);
    // There are (maybe many) different rules in the rulesets that fail. To ease it for the
    // user we try to find one attribute that forces to fail the rules in all rulesets.
    // e.g. if rules concerning d3 fail in all rulesets of this group then d3 is a common limiting attribut
    this.logInspect('commonLimitingAttributes', commonLimitingAttributes);
    const allLimitingAttributes = getAllLimitingAttributesFromDictionary(rulesets, limitingRulesetsDictionary);
    this.logInspect('allLimitingAttributes', allLimitingAttributes);
    let newLimitingRules = [];
    for (const ruleset of rulesets) {
      newLimitingRules.push(...limitingRulesetsDictionary[ruleset.value].limitingRules);
    }
    newLimitingRules = this.joinRules(newLimitingRules);
    // FIXME: We should better use doCompactRules
    //        But befor we need to carefully check the difference between joinRules() and doCompactRules()
    // newLimitingRules = doCompactRules(newLimitingRules, 'AND');
    this.logRules('newLimitingRules', newLimitingRules);
    this.logGroupEnd('END check Rules (II)');
    return {
      limitingRules: newLimitingRules,
      rulesetValue: undefined
    };
  }
  /**
   * Fill the limitingRulesDictionary to know which groupset has limiting rules.
   *
   * The result contains
   *  - limitingRules  array
   *  - limitingAttrIds array
   *  - rulesetIsValid?  boolean
   */
  determineRulesetLimitations(rulesets) {
    const limitationDict = {};
    for (const ruleset of rulesets) {
      const expandedRules = this.expandVariableInRules(ruleset.rule);
      const ruleResult = this.executeRulesInSameRulesetContext(expandedRules);
      if (ruleResult.allRulesApply) {
        if (!limitationDict['common']) {
          // there were no violated common rules, so we found a hit
          const noLimitsDictionary = {};
          noLimitsDictionary[ruleset.value] = {
            limitingRules: [],
            limitingAttrIds: [],
            rulesetIsValid: true
          };
          return noLimitsDictionary;
        }
      } else {
        var _ruleResult$limitingR;
        const validLimitingRules = getValidLimitingRules((_ruleResult$limitingR = ruleResult.limitingRules) != null ? _ruleResult$limitingR : [], ruleset.rule);
        limitationDict[ruleset.value] = {
          limitingRules: validLimitingRules,
          limitingAttrIds: getUniqueRuleAttrIds(validLimitingRules)
        };
      }
    }
    return limitationDict;
  }
  /**
   * This method tries to reduce the passed IRule[].
   *
   * Idea:
   *   If only the the passed rulesByAttr (with the common attribute) block the validation, then all other values
   *   would fit for one ruleset. So we can limit the rules to the one of that ruleset.
   *
   *   Example:
   *   attribute: b
   *
   *   rulesByAttr: [
   *   { min: { attr: 'b', op: '>=', value: 2 }, max: { attr: 'b', op: '<=', value: 4 }, ri:{rulesetIds:['1'] },
   *   { min: { attr: 'b', op: '>=', value: 3 }, max: { attr: 'b', op: '<=', value: 6 }, ri:{rulesetIds:['2'] },
   *   { min: { attr: 'b', op: '>=', value: 5 }, max: { attr: 'b', op: '<=', value: 7 }, ri:{rulesetIds:['3'] } ]
   *
   *   level: 0
   *   group: <the complete Group definition>
   *
   *   Processing
   *   1. find a value for each limiting rule
   *   here the result would be 3, 4.5, 6 for the example above
   *
   *   2. iterate over the possible values and call verifyRuleset for each and
   *   see if we hit  "allRulesApply"
   *   in that case we can stop the iteration and  return the rule given by rid (rulesetIdentifier)
   */
  tryReduce(attrName, rulesByAttr, level, rulesets) {
    const attr = this.getAttribute(attrName);
    // This method calls verifyRuleset itself which then calls tryReduce to avoid
    // deep recursions the level must be 0.
    // And we do not want to guess values for attributes defined by functions (like wallthickness).
    if (level === 0 && !isAttributeFunction(attr)) {
      const validRulesWithValues = findValuesForRules(rulesByAttr);
      this.logInspect('VALUES:', validRulesWithValues);
      const attributeOriginalData = this.getValue(attrName);
      for (const newAttribute of validRulesWithValues) {
        this.logGroupStart(` TRY REDUCE START ${attrName} = ${newAttribute.value} for rid ${newAttribute.rid}`);
        this.addData(attrName, newAttribute.value);
        const subVerifyGroup = this.verifyRuleset(rulesets);
        this.logInspect('RESULT subVerifyGroup:', subVerifyGroup);
        if (subVerifyGroup.limitingRules.length === 0) {
          this.addData(attrName, attributeOriginalData);
          const limitedRulesByAttr = rulesByAttr.filter(rule => {
            var _rule$ri;
            return ((_rule$ri = rule.ri) == null ? void 0 : _rule$ri.rulesetIds.indexOf(newAttribute.rid)) !== -1;
          });
          // this.logInspect('limitedRulesByAttr:', limitedRulesByAttr);
          this.logGroupEnd(' TRY REDUCE END  by return');
          return this.joinRules(limitedRulesByAttr);
        }
        this.logGroupEnd(' TRY REDUCE END ');
      }
      this.addData(attrName, attributeOriginalData);
    }
    return this.joinRules(rulesByAttr);
  }
  /**
   * Expand possible variable in an array of rules.
   */
  expandVariableInRules(rules) {
    const rulesCopy = cloneDeep(rules);
    return rulesCopy.map(rule => {
      return this.expandVariableInRule(rule);
    });
  }
  /**
   * Expand possible variable in a rule.
   */
  expandVariableInRule(rule) {
    const ruleCopy = cloneDeep(rule);
    if (isIMinMaxRule(ruleCopy)) {
      if (ruleCopy.min) {
        if (ruleCopy.min.variable) {
          const value = this.getNumberValue(ruleCopy.min.variable);
          if (typeof value === 'undefined') {
            throw new Error('Attribute min.rightVar "' + ruleCopy.min.variable + '" is not defined!');
          }
          delete ruleCopy.min.variable;
          ruleCopy.min.value = value;
        }
      }
      if (ruleCopy.max) {
        if (ruleCopy.max.variable) {
          const value = this.getNumberValue(ruleCopy.max.variable);
          if (typeof value === 'undefined') {
            throw new Error('Attribute max.rightVar "' + ruleCopy.max.variable + '" is not defined!');
          }
          delete ruleCopy.max.variable;
          ruleCopy.max.value = value;
        }
      }
    }
    return ruleCopy;
  }
  /**
   * Execute the rule and check if it applies.
   *
   * example rule: {min: {leftVar: 'd1', op: '>=', rightVal: 4}, max: {leftVar: 'd1', op: '<', rightVal: 5}
   *
   * The values from data are expanded in the formula:
   *
   * In the example we get
   *    10 >= 4  && 10 < 5       => false
   */
  executeRule(rule) {
    if (isIMinMaxRule(rule)) {
      return this.executeIMinMaxRule(rule);
    } else if (isIStringRule(rule)) {
      return this.executeIStringRule(rule);
    } else if (isIBooleanRule(rule)) {
      return this.executeIBooleanRule(rule);
    } else {
      throw new Error('undefined rule type');
    }
  }
  /**
   * Execute all rules in the ruleset for one attribute.
   *
   * If there are more than one rules in a ruleset for the same attribute, then these rules must be evaluated by AND!
   *
   * If there are no rules in that ruleset for the given attribute the return value is TRUE!
   */
  doAllRulesInRulesetApplyToCurrentData(ruleset, attrId) {
    const ruleArray = [];
    for (const rule of ruleset.rule) {
      if (isValidAttrId(attrId, rule)) {
        if (this._data && Object.keys(this._data).length) {
          ruleArray.push(this.expandVariableInRule(rule));
        } else {
          ruleArray.push(rule);
        }
      }
    }
    return ruleArray.length ? ruleArray.every(this.executeRule.bind(this), this) : true;
  }
  /**
   * Execute an IMinMaxRule and check if it applies.
   */
  executeIMinMaxRule(rule) {
    let minStatus = true;
    let maxStatus = true;
    const attrName = rule.attr;
    if (rule.min) {
      const minAttrValue = this.getNumberValue(attrName);
      if (typeof minAttrValue === 'undefined') {
        throw new Error('min.attr "' + attrName + '" is not defined!');
      }
      const minRightValue = rule.min.variable ? this.getNumberValue(rule.min.variable) : rule.min.value;
      if (typeof minRightValue === 'undefined') {
        throw new Error(`min_right_value is not defined!`);
      }
      switch (rule.min.op) {
        case '>':
          minStatus = minAttrValue > minRightValue;
          break;
        case '>=':
          minStatus = minAttrValue >= minRightValue;
          break;
        case '=':
          minStatus = minAttrValue === minRightValue;
          break;
      }
    }
    if (rule.max) {
      const maxAttrValue = this.getNumberValue(attrName);
      if (typeof maxAttrValue === 'undefined') {
        throw new Error('max.attr "' + attrName + '" is not defined!');
      }
      const maxRightValue = rule.max.variable ? this.getNumberValue(rule.max.variable) : rule.max.value;
      if (typeof maxRightValue === 'undefined') {
        throw new Error(`max_right_value is not defined!`);
      }
      switch (rule.max.op) {
        case '<':
          maxStatus = maxAttrValue < maxRightValue;
          break;
        case '<=':
          maxStatus = maxAttrValue <= maxRightValue;
          break;
        case '=':
          maxStatus = maxAttrValue === maxRightValue;
          break;
      }
    }
    return minStatus && maxStatus;
  }
  /**
   * Execute an IStringRule and check if it applies.
   */
  executeIStringRule(rule) {
    let status = true;
    const attrValue = this.getStringValue(rule.attr);
    if (typeof attrValue === 'undefined') {
      throw new Error('attr "' + rule.attr + '" is not defined!');
    }
    const values = rule.values;
    switch (rule.op) {
      case 'in':
        status = values.indexOf(attrValue) >= 0;
        break;
      case '!in':
        status = values.indexOf(attrValue) === -1;
        break;
    }
    return status;
  }
  /**
   * Execute an IBooleanRule and check if it applies.
   */
  executeIBooleanRule(rule) {
    let status = true;
    const attrValue = this.getBooleanValue(rule.attr);
    if (typeof attrValue === 'undefined') {
      throw new Error('attr "' + rule.attr + '" is not defined!');
    }
    let value = true;
    if (typeof rule.value !== 'undefined') {
      value = rule.value === true;
    } else if (rule.variable) {
      value = this.getBooleanValue(rule.attr);
    } else {
      throw new Error('attr "' + rule.attr + '" is not defined!');
    }
    switch (rule.op) {
      case '==':
        status = attrValue === value;
        break;
      case '!=':
        status = attrValue !== value;
        break;
    }
    return status;
  }
  /**
   * Returns true if one rule is valid.
   * This function is called by verifyAttribute, so all rules are related to
   * one attrId.
   */
  executeRulesInSameAttributeContext(rules, _attrId) {
    const dict = {};
    for (const rule of rules) {
      var _rule$ri2;
      if (typeof ((_rule$ri2 = rule.ri) == null ? void 0 : _rule$ri2.rulesetIds) !== 'undefined') {
        for (const rv of (_rule$ri$rulesetIds = (_rule$ri3 = rule.ri) == null ? void 0 : _rule$ri3.rulesetIds) != null ? _rule$ri$rulesetIds : []) {
          var _rule$ri$rulesetIds, _rule$ri3;
          dict[rv] = true;
        }
      }
    }
    for (const rulesetValue of Object.getOwnPropertyNames(dict)) {
      this.log('processing rulesetValue :' + rulesetValue);
      const rvDependentRules = rules.filter(r => {
        if (!r) {
          return false;
        }
        if (!r.ri) {
          return false;
        }
        return r.ri.rulesetIds.indexOf(rulesetValue) > -1;
      });
      // all Rules (in the Ruleset (=same rv)) must be true to have a complete true result
      const failingRules = [];
      for (const rule of rvDependentRules) {
        if (!this.executeRule(rule)) {
          failingRules.push(rule);
        }
      }
      if (failingRules.length === 0) {
        return true;
      }
    }
    return false;
  }
  /**
   * Returns true if all rules return true.
   * It is called in the context of a ruleset. That means that all rules
   * needs to be combined by AND. (all must be fullfiled)
   */
  executeRulesInSameRulesetContext(rules) {
    const result = [];
    for (const rule of rules) {
      if (!this.executeRule(rule)) {
        result.push(rule);
      }
    }
    const allCompactedRules = doCompactRules(rules);
    return result.length === 0 ? {
      allRulesApply: true,
      limitingRules: allCompactedRules,
      rulesetValue: undefined
    } : {
      allRulesApply: false,
      limitingRules: result,
      rulesetValue: undefined
    };
  }
  /**
   * Joins all rules.
   * Internaly it is done by iteration over the (unique) attrIds and calling joinRulesByAttrId().
   *
   * @param ruleArray IRule[]
   */
  joinRules(ruleArray) {
    // this.logGroupStart('START joinRulesArray');
    const allAttrIds = getUniqueRuleAttrIds(ruleArray);
    const resultingRules = [];
    for (const attrId of allAttrIds) {
      const joinedRules = this.joinRulesByAttrId(attrId, ruleArray);
      resultingRules.push(...joinedRules);
    }
    // this.logGroupEnd('END joinRulesArray');
    return resultingRules;
  }
  /**
   * Joins different rules for the same attrIds together when they are connected or overlap.
   * When the rules are not connected or do not overlap, the input rules are returned.
   *
   * Requirement: At the moment the rules must be defined ordered.
   */
  joinRulesByAttrId(attrId, ruleArray) {
    let expandedRules;
    if (this._data && Object.keys(this._data).length) {
      {
        this.log('   try expandVariableInRules');
      }
      expandedRules = this.expandVariableInRules(ruleArray);
    } else {
      {
        this.log('   no expansion');
      }
      //this.log('data is NOT set, so no variables expansion');
      expandedRules = ruleArray;
    }
    {
      this.logRules('   expandedRules', expandedRules);
    }
    const rulesWithAttr = [];
    for (const currentRule of expandedRules) {
      if (isValidAttrId(attrId, currentRule) && isRuleUseful(currentRule)) {
        rulesWithAttr.push(currentRule);
      }
    }
    const result = doCompactRules(rulesWithAttr);
    {
      this.logRules('   rulesWithAttr', rulesWithAttr);
      this.logRules('    doCompactRules', result);
      this.logGroupEnd('END joinRulesByAttrId ' + attrId);
    }
    return result;
  }
}

class RuleboxMI extends Rulebox {
  constructor() {
    super(...arguments);
    this.isPmFastLine = false;
    this.isMaterialDeactivated = false;
  }
  /**
   * Expand result of verify() to contain the principalMoldName
   */
  verify() {
    const v = super.verify();
    const principalMoldName = this.getPrincipalMoldName(v.rulesetValue);
    return Object.assign({}, v, {
      principalMoldName
    });
  }
  /**
   * Check for FastLine with special material
   */
  isPmFastLineAndLimitedWallthicknessMaterial() {
    const mat = this.getStringValue('materialCode') || '';
    return this.isPmFastLine && !!mat && getFastLineLimitedWallthicknessMaterials().indexOf(mat) !== -1;
  }
}

/**
 * This is a functional copy of the executeIMinMaxRule method of the rulebox.
 * But here it is used outside the rulebox context.
 *
 * It's easier to test all the cases in a limited environment.
 *
 * TODO: Think about using this funktion also in the rulebox.
 */
const executeIMinMaxRule = (rule, data) => {
  if (!rule) {
    throw new Error('rule is not defined!');
  }
  if (!data) {
    throw new Error('data is not defined!');
  }
  if (!rule.attr) {
    throw new Error('rule.attr is not defined!');
  }
  let minStatus = true;
  let maxStatus = true;
  const attrName = rule.attr;
  if (rule.min) {
    const minAttrValue = data[attrName];
    if (typeof minAttrValue === 'undefined') {
      throw new Error('min.attr "' + attrName + '" is not defined!');
    }
    const minRuleValue = rule.min.variable ? data[rule.min.variable] : rule.min.value;
    if (typeof minRuleValue === 'undefined') {
      throw new Error(`min_rule_value is not defined!`);
    }
    switch (rule.min.op) {
      case '>':
        minStatus = minAttrValue > minRuleValue;
        break;
      case '>=':
        minStatus = minAttrValue >= minRuleValue;
        break;
      case '=':
        minStatus = minAttrValue === minRuleValue;
        break;
    }
  }
  if (rule.max) {
    const maxAttrValue = data[attrName];
    if (typeof maxAttrValue === 'undefined') {
      throw new Error('max.attr "' + attrName + '" is not defined!');
    }
    const maxRuleValue = rule.max.variable ? data[rule.max.variable] : rule.max.value;
    if (typeof maxRuleValue === 'undefined') {
      throw new Error(`max_rule_value is not defined!`);
    }
    switch (rule.max.op) {
      case '<':
        maxStatus = maxAttrValue < maxRuleValue;
        break;
      case '<=':
        maxStatus = maxAttrValue <= maxRuleValue;
        break;
      case '=':
        maxStatus = maxAttrValue === maxRuleValue;
        break;
    }
  }
  return minStatus && maxStatus;
};

const tryUpdateMinOnlyD1fromD2 = (rD1, rD2, _wtMin, wtMax) => {
  let rD1Out = rD1;
  const rD2Out = rD2;
  const calcD1value = rD2.value - 2 * wtMax;
  if (calcD1value > rD1.value) {
    rD1Out = {
      op: rD2Out.op,
      value: calcD1value,
      force: rD2Out.force
    };
  }
  return [rD1Out, rD2Out];
};
const tryUpdateMinOnlyD2fromD1 = (rD1, rD2, wtMin, _wtMax) => {
  const rD1Out = rD1;
  let rD2Out = rD2;
  const calcD2value = rD1Out.value + 2 * wtMin;
  if (calcD2value > rD2Out.value) {
    rD2Out = {
      op: rD1Out.op,
      value: calcD2value,
      force: rD1.force
    };
  }
  return [rD1Out, rD2Out];
};
const tryUpdateMaxOnlyD1fromD2 = (rD1, rD2, wtMin, _wtMax) => {
  let rD1Out = rD1;
  const rD2Out = rD2;
  const calcD1value = rD2.value - 2 * wtMin;
  if (calcD1value < rD1.value) {
    rD1Out = {
      op: rD2Out.op,
      value: calcD1value,
      force: rD2Out.force
    };
  }
  return [rD1Out, rD2Out];
};
const tryUpdateMaxOnlyD2fromD1 = (rD1, rD2, _wtMin, wtMax) => {
  const rD1Out = rD1;
  let rD2Out = rD2;
  const calcD2value = rD1Out.value + 2 * wtMax;
  if (calcD2value < rD2Out.value) {
    rD2Out = {
      op: rD1Out.op,
      value: calcD2value,
      force: rD1.force
    };
  }
  return [rD1Out, rD2Out];
};
const tryUpdateMin = (rD1, rD2, wtMin, wtMax) => {
  let rD1Out = rD1;
  let rD2Out = rD2;
  // start with D2 from D1
  const calcD2value = rD1Out.value + 2 * wtMin;
  if (calcD2value === rD2Out.value) {
    // when values are equal the more rigid one is used
    if (rD1.op === '>') {
      rD2Out = Object.assign({}, rD1Out, {
        value: calcD2value
      });
    } else {
      rD2Out = Object.assign({}, rD2Out, {
        value: calcD2value
      });
    }
  } else if (calcD2value > rD2Out.value) {
    // if the new value for d2 is bigger than the old one we can use it
    rD2Out = Object.assign({}, rD1Out, {
      value: calcD2value
    });
  } else {
    // result to low for d2 => recalculate starting with d2
    const calcD1value = rD2Out.value - 2 * wtMax;
    if (calcD1value === rD1Out.value) {
      // when values are equal the more rigid one is used
      if (rD2.op === '>') {
        rD1Out = Object.assign({}, rD2Out, {
          value: calcD1value
        });
      } else {
        rD1Out = Object.assign({}, rD1Out, {
          value: calcD1value
        });
      }
    } else if (calcD1value > rD1Out.value) {
      // if the new value for d1 is bigger than the old one we can use it
      rD1Out = Object.assign({}, rD2Out, {
        value: calcD1value
      });
    } else ;
  }
  return [rD1Out, rD2Out];
};
const tryUpdateMax = (rD1, rD2, wtMin, wtMax) => {
  let rD1Out = rD1;
  let rD2Out = rD2;
  // start with D2 from D1
  const calcD2value = rD1Out.value + 2 * wtMax;
  if (calcD2value === rD2Out.value) {
    // when values are equal the more rigid one is used
    if (rD1.op === '<') {
      rD2Out = Object.assign({}, rD1Out, {
        value: calcD2value
      });
    } else {
      rD2Out = Object.assign({}, rD2Out, {
        value: calcD2value
      });
    }
  } else if (calcD2value < rD2Out.value) {
    // if the new value for d2 is lower than the old one we can use it
    rD2Out = Object.assign({}, rD1Out, {
      value: calcD2value
    });
  } else {
    // result to high for d2 => recalculate starting with d1
    const calcD1value = rD2Out.value - 2 * wtMin;
    if (calcD1value === rD1Out.value) {
      // when values are equal the more rigid one is used
      if (rD2.op === '<') {
        rD1Out = Object.assign({}, rD2Out, {
          value: calcD1value
        });
      } else {
        rD1Out = Object.assign({}, rD1Out, {
          value: calcD1value
        });
      }
    } else if (calcD1value < rD1Out.value) {
      // if the new value for d1 is lower than the old one we can use it
      rD1Out = Object.assign({}, rD2Out, {
        value: calcD1value
      });
    } else ;
  }
  return [rD1Out, rD2Out];
};
const getforcedMinD1D2 = (rD1, rD2, wtMin, wtMax) => {
  let rD1Out = {
    op: rD1.op,
    value: rD1.value,
    force: rD1.force
  };
  let rD2Out = {
    op: rD2.op,
    value: rD2.value,
    force: rD2.force
  };
  if (rD1.force && rD2.force) {
    [rD1Out, rD2Out] = tryUpdateMin(rD1, rD2, wtMin, wtMax);
  } else if (!rD1.force && rD2.force) {
    [rD1Out, rD2Out] = tryUpdateMinOnlyD1fromD2(rD1, rD2, wtMin, wtMax);
  } else {
    [rD1Out, rD2Out] = tryUpdateMinOnlyD2fromD1(rD1, rD2, wtMin);
  }
  return [rD1Out, rD2Out];
};
const getforcedMaxD1D2 = (rD1, rD2, wtMin, wtMax) => {
  let rD1Out = {
    op: rD1.op,
    value: rD1.value,
    force: rD1.force
  };
  let rD2Out = {
    op: rD2.op,
    value: rD2.value,
    force: rD2.force
  };
  if (rD1.force && rD2.force) {
    [rD1Out, rD2Out] = tryUpdateMax(rD1, rD2, wtMin, wtMax);
  } else if (!rD1.force && rD2.force) {
    [rD1Out, rD2Out] = tryUpdateMaxOnlyD1fromD2(rD1, rD2, wtMin);
  } else {
    [rD1Out, rD2Out] = tryUpdateMaxOnlyD2fromD1(rD1, rD2, wtMin, wtMax);
  }
  return [rD1Out, rD2Out];
};
const getforcedMinD1D2D3 = (rD1, rD2, rD3, wtMin, wtMax, ftMin, ftMax) => {
  let rD1Out = {
    op: rD1.op,
    value: rD1.value,
    force: rD1.force
  };
  let rD2Out = {
    op: rD2.op,
    value: rD2.value,
    force: rD2.force
  };
  let rD3Out = {
    op: rD3.op,
    value: rD3.value,
    force: rD3.force
  };
  if (rD1.force && !rD2.force && !rD3.force) {
    [rD1Out, rD2Out] = getforcedMinD1D2(rD1, rD2, wtMin, wtMax);
    [rD2Out, rD3Out] = getforcedMinD1D2(rD2Out, rD3, ftMin, ftMax);
  } else if (!rD1.force && rD2.force && !rD3.force) {
    [rD1Out, rD2Out] = getforcedMinD1D2(rD1, rD2, wtMin, wtMax);
    [rD2Out, rD3Out] = getforcedMinD1D2(rD2, rD3, ftMin, ftMax);
  } else if (!rD1.force && !rD2.force && rD3.force) {
    [rD2Out, rD3Out] = getforcedMinD1D2(rD2, rD3, ftMin, ftMax);
    [rD1Out, rD2Out] = getforcedMinD1D2(rD1, rD2Out, wtMin, wtMax);
  } else {
    throw new Error('Other combinations not yet available in getforcedMinD1D2D3()!');
  }
  return [rD1Out, rD2Out, rD3Out];
};
const getforcedMaxD1D2D3 = (rD1, rD2, rD3, wtMin, wtMax, ftMin, ftMax) => {
  let rD1Out = {
    op: rD1.op,
    value: rD1.value,
    force: rD1.force
  };
  let rD2Out = {
    op: rD2.op,
    value: rD2.value,
    force: rD2.force
  };
  let rD3Out = {
    op: rD3.op,
    value: rD3.value,
    force: rD3.force
  };
  if (rD1.force && !rD2.force && !rD3.force) {
    [rD1Out, rD2Out] = getforcedMaxD1D2(rD1, rD2, wtMin, wtMax);
    [rD2Out, rD3Out] = getforcedMaxD1D2(rD2Out, rD3, ftMin, ftMax);
  } else if (!rD1.force && rD2.force && !rD3.force) {
    [rD1Out, rD2Out] = getforcedMaxD1D2(rD1, rD2, wtMin, wtMax);
    [rD2Out, rD3Out] = getforcedMaxD1D2(rD2, rD3, ftMin, ftMax);
  } else if (!rD1.force && !rD2.force && rD3.force) {
    [rD2Out, rD3Out] = getforcedMaxD1D2(rD2, rD3, ftMin, ftMax);
    [rD1Out, rD2Out] = getforcedMaxD1D2(rD1, rD2Out, wtMin, wtMax);
  } else {
    throw new Error('Other combinations not yet available in getforcedMaxD1D2D3()!');
  }
  return [rD1Out, rD2Out, rD3Out];
};

/**
 * This function removes all the material rules from a ruleset to make material independent checks.
 */
const removeMaterialRulesFromRuleset = rules => {
  const matAttr = ['materialCode', 'heatedMold', 'materialShrinkage'];
  return rules.map(ruleset => {
    ruleset.rule = ruleset.rule.filter(rule => matAttr.indexOf(rule.attr) === -1);
    return ruleset;
  });
};

/*
## Preparing of Rules

The rules for the parts given by the BU are the technical borders to create the parts.

For example technically the room in the machine only allows that the diameters of the parts can have a size between
10 and 80 mm and the wallthickness is allowed to be between 1 and 2 mm.

So the basic rules are:

```
{attr: 'd1', min: {op: '>=', value: 10}, max: {op: '<=', value: 80}},
{attr: 'd2', min: {op: '>=', value: 10}, max: {op: '<=', value: 80}},
{attr: 'wt', min: {op: '>=', value: 1}, max: {op: '<=', value: 2}},
```

To be more precise:
  When the minimum is 10mm then this is the minimum for d1
  When the maximum is 80mm then this is the maximum for d2

We use a flag 'force' to show that some rules are strict.

Wall thickness rules are always strict.

So we get:

```
{attr: 'd1', min: {op: '>=', value: 10, force: true},  max: {op: '<=', value: 80, force: false}},
{attr: 'd2', min: {op: '>=', value: 10, force: false}, max: {op: '<=', value: 80, force: true}},
{attr: 'wt', min: {op: '>=', value: 1,  force: true},  max: {op: '<=', value: 2,  force: true}},
```


But that does not help the user. The implicit rules must be applied before.

  So when d1_min is >=10 then d2_min must be d1_min plus the size of 2\*wtMin.

  ```
 d2_min = d1_min + 2 * wtMin =  10 + 2 * 1 = 12 mm
```

So d2_min is >= 12 mm.

  And when d2_max is <= 80 mm then d1_max must be d2_max minus the size of 2\*wtMax.

  ```
 d1_max = d2_max - 2 * wtMax =  80 - 2 * 2 = 76 mm
```

So for the user the rules are now:

  ```
{attr: 'd1', min: {op: '>=', value: 10}, max: {op: '<=', value: 76}},
{attr: 'd2', min: {op: '>=', value: 12}, max: {op: '<=', value: 80}},
{attr: 'wt', min: {op: '>=', value: 1}, max: {op: '<=', value: 2}},
```

This works for all operators in the d1 and d2 rules with the constraint the the wt-rules contain '>=' and '<='.
This is a strict limitation at the moment. Otherwise the operands in the rules needs to be changed
and so the rules would get too complicated.

In the code this is implemented in prepareDiameterNoFlangeRules()  for bearings with (only) d1 and d2.
For bearings having d1, d2 and d3 createD1D2D3Rules() exists using the same logic.

But it is important to use the either the min or the max values when calculating up or down or for min or for max!
```
+------------------------------------------------------------
|              |   d1         |      wt      |      d2      |
+------------------------------------------------------------
|              |              |              |              |
|  calculate   |  d1_min,  <-- ( -2*wtMax   +   d2_min  )  |  d1_min = Min ( d1_min, d2_min - 2 * wtMax)
|              |              |              |              |
|  min         |  ( d1_min    +   2*wtMin  ) --> , d2_min  |  d2_min = Min ( d2_min, d1_min + 2 * wtMin)
|              |              |              |              |
+------------------------------------------------------------
|              |              |              |              |
|  calculate   |  d1_max,  <-- ( -2*wtMin   +   d2_max  )  |  d1_max = Max ( d1_max, d2_max - 2 * wtMin)
|              |              |              |              |
|  max         |  ( d1_max    +   2*wtMax  ) --> , d2_max  |  d2_max = Max ( d2_max, d1_max + 2 * wtMax)
|              |              |              |              |
+------------------------------------------------------------
```

*/
/**
 * takes the simple (independent) rules for d1, d2 and wt (wallthickness) as parameters
 * and returns the depending rules for d1 and d2 from it, including the rules for the wt.
 *
 * Beside first ideas we also need the wt rule in the result.
 *
 * Background: In the frontend the user can not enter wallthickness (wt)  and also the wt is not shown.
 * That's why we need to integrate the wt rules into the simple (independent) rules for d1, d2.
 *
 * Simple example:
 *
 * From the BU we know that the box for the manufacturing tool has these limits:
 *
 * 40 <= d1 < 80
 *
 * and
 *
 * 42 <= d2 < 82
 *
 * And for the ease of the example lets say the wt is only allowed to be 2.
 *
 * wt = 2
 *
 * So if d1 starts at 40 the minimum value of d2 can be d1 + 2*wt = 40 + 4 = 44.
 *
 * And if 82 is the maximum for d2 then the d1 maximum can only be d2 - 2*wt = 82 - 4 = 78
 *
 * So the resulting rules are
 * 40 <= d1 < 78
 * 44 <= d1 < 82
 *
 * In real-life wt is also a range, which makes it all a little bit more complicated.
 * See the examples in the test.
 */
const prepareDiameterNoFlangeRules = (d1Rule, d2Rule, wtRule, attributeAtVerify, data) => {
  if (!isIMinMaxValueRule(d1Rule)) {
    throw new Error('not all values defined for d1');
  }
  if (!isIMinMaxValueRule(d2Rule)) {
    throw new Error('not all values defined for d2');
  }
  if (!isIMinMaxValueRule(wtRule)) {
    throw new Error('not all values defined for wt');
  }
  if (!data) {
    throw new Error('data not defined');
  }
  // Ensure that the wt-rule has including borders (that means that an equal sign '=' is contained in the op)
  // for min AND max, otherwise unequal comparisons get too complicated
  if (wtRule.min.op !== '>=' || wtRule.max.op !== '<=') {
    throw new Error('wt is only allowed to contain >= and <=');
  }
  // Ensure that the wt-rules are force (at the moment)
  if (!wtRule.min.force || !wtRule.max.force) {
    throw new Error('wt rules must use force');
  }
  // crete working copies without the 'force' attribute inside
  const d1RuleOut = {
    attr: d1Rule.attr,
    min: {
      op: d1Rule.min.op,
      value: d1Rule.min.value
    },
    max: {
      op: d1Rule.max.op,
      value: d1Rule.max.value
    }
  };
  const d2RuleOut = {
    attr: d2Rule.attr,
    min: {
      op: d2Rule.min.op,
      value: d2Rule.min.value
    },
    max: {
      op: d2Rule.max.op,
      value: d2Rule.max.value
    }
  };
  const wtRuleOut = {
    attr: wtRule.attr,
    min: {
      op: wtRule.min.op,
      value: wtRule.min.value
    },
    max: {
      op: wtRule.max.op,
      value: wtRule.max.value
    }
  };
  // prepare without data
  const [d1ForcedMin, d2ForcedMin] = getforcedMinD1D2(d1Rule.min, d2Rule.min, wtRule.min.value, wtRule.max.value);
  d1RuleOut.min = {
    op: d1ForcedMin.op,
    value: d1ForcedMin.value
  };
  d2RuleOut.min = {
    op: d2ForcedMin.op,
    value: d2ForcedMin.value
  };
  const [d1ForcedMax, d2ForcedMax] = getforcedMaxD1D2(d1Rule.max, d2Rule.max, wtRule.min.value, wtRule.max.value);
  d1RuleOut.max = {
    op: d1ForcedMax.op,
    value: d1ForcedMax.value
  };
  d2RuleOut.max = {
    op: d2ForcedMax.op,
    value: d2ForcedMax.value
  };
  // with data
  const d1 = data['d1'];
  if (attributeAtVerify === 'd2' && d1) {
    const d1Valid = executeIMinMaxRule(d1Rule, {
      d1
    });
    if (d1Valid) {
      // it only makes sense to adjust the d2Rules when the d1Rule is valid
      d2RuleOut.min = biggerMinValueRule(d2RuleOut.min, {
        op: '>=',
        value: d1 + 2 * wtRuleOut.min.value
      });
      d2RuleOut.max = lesserMaxValueRule(d2RuleOut.max, {
        op: '<=',
        value: d1 + 2 * wtRuleOut.max.value
      });
    }
  }
  return [d1RuleOut, d2RuleOut, wtRuleOut];
};
const prepareDiameterOneFlangeRules = (d1Rule, d2Rule, d3Rule, wtRule, ftRule, attributeAtVerify, data) => {
  if (!isIMinMaxValueRule(d1Rule)) {
    throw new Error('not all values defined for d1');
  }
  if (!isIMinMaxValueRule(d2Rule)) {
    throw new Error('not all values defined for d2');
  }
  if (!isIMinMaxValueRule(d3Rule)) {
    throw new Error('not all values defined for d3');
  }
  if (!isIMinMaxValueRule(wtRule)) {
    throw new Error('not all values defined for wt');
  }
  if (!isIMinMaxValueRule(ftRule)) {
    throw new Error('not all values defined for ft');
  }
  if (!data) {
    throw new Error('data not defined');
  }
  // Ensure that the wtRule has including borders (that means that an equal sign '=' is contained in the op)
  // for min AND max, otherwise unequal comparisons get too complicated
  if (wtRule.min.op !== '>=' || wtRule.max.op !== '<=') {
    throw new Error('wtRule is only allowed to contain >= and <=');
  }
  // Ensure that the wt-rules are force (at the moment)
  if (!wtRule.min.force || !wtRule.max.force) {
    throw new Error('wtRules must use force');
  }
  // Ensure that the ftRule has including borders (that means that an equal sign '=' is contained in the op)
  // for min AND max, otherwise unequal comparisons get too complicated
  if (ftRule.min.op !== '>=' || ftRule.max.op !== '<=') {
    throw new Error('ftRule is only allowed to contain >= and <=');
  }
  // Ensure that the wt-rules are force (at the moment)
  if (!ftRule.min.force || !ftRule.max.force) {
    throw new Error('wtRules must use force');
  }
  // crete working copies without the 'force' attribute inside
  const d1RuleOut = {
    attr: d1Rule.attr,
    min: {
      op: d1Rule.min.op,
      value: d1Rule.min.value
    },
    max: {
      op: d1Rule.max.op,
      value: d1Rule.max.value
    }
  };
  const d2RuleOut = {
    attr: d2Rule.attr,
    min: {
      op: d2Rule.min.op,
      value: d2Rule.min.value
    },
    max: {
      op: d2Rule.max.op,
      value: d2Rule.max.value
    }
  };
  const d3RuleOut = {
    attr: d3Rule.attr,
    min: {
      op: d3Rule.min.op,
      value: d3Rule.min.value
    },
    max: {
      op: d3Rule.max.op,
      value: d3Rule.max.value
    }
  };
  const wtRuleOut = {
    attr: wtRule.attr,
    min: {
      op: wtRule.min.op,
      value: wtRule.min.value
    },
    max: {
      op: wtRule.max.op,
      value: wtRule.max.value
    }
  };
  const ftRuleOut = {
    attr: ftRule.attr,
    min: {
      op: ftRule.min.op,
      value: ftRule.min.value
    },
    max: {
      op: ftRule.max.op,
      value: ftRule.max.value
    }
  };
  // prepare without data
  const [d1ForcedMin, d2ForcedMin, d3ForcedMin] = getforcedMinD1D2D3(d1Rule.min, d2Rule.min, d3Rule.min, wtRule.min.value, wtRule.max.value, ftRule.min.value, ftRule.max.value);
  d1RuleOut.min = {
    op: d1ForcedMin.op,
    value: d1ForcedMin.value
  };
  d2RuleOut.min = {
    op: d2ForcedMin.op,
    value: d2ForcedMin.value
  };
  d3RuleOut.min = {
    op: d3ForcedMin.op,
    value: d3ForcedMin.value
  };
  const [d1ForcedMax, d2ForcedMax, d3ForcedMax] = getforcedMaxD1D2D3(d1Rule.max, d2Rule.max, d3Rule.max, wtRule.min.value, wtRule.max.value, ftRule.min.value, ftRule.max.value);
  d1RuleOut.max = {
    op: d1ForcedMax.op,
    value: d1ForcedMax.value
  };
  d2RuleOut.max = {
    op: d2ForcedMax.op,
    value: d2ForcedMax.value
  };
  d3RuleOut.max = {
    op: d3ForcedMax.op,
    value: d3ForcedMax.value
  };
  // with data
  const d1 = data['d1'];
  const d2 = data['d2'];
  if (attributeAtVerify === 'd2' && d1) {
    const d1Valid = executeIMinMaxRule(d1RuleOut, {
      d1
    });
    if (d1Valid) {
      // it only makes sense to adjust the d2Rules when the d1Rule is valid
      d2RuleOut.min = biggerMinValueRule(d2RuleOut.min, {
        op: '>=',
        value: d1 + 2 * wtRuleOut.min.value
      });
      d3RuleOut.min = biggerMinValueRule(d3RuleOut.min, {
        op: '>=',
        value: d2RuleOut.min.value + 2 * ftRuleOut.min.value
      });
      d2RuleOut.max = lesserMaxValueRule(d2RuleOut.max, {
        op: '<=',
        value: d1 + 2 * wtRuleOut.max.value
      });
      d3RuleOut.max = lesserMaxValueRule(d3RuleOut.max, {
        op: '<=',
        value: d2RuleOut.max.value + 2 * ftRuleOut.max.value
      });
    }
  } else if ((attributeAtVerify === 'd3' || attributeAtVerify === 'd4') && d1 && d2) {
    const d1Valid = executeIMinMaxRule(d1RuleOut, {
      d1
    });
    const d2Valid = executeIMinMaxRule(d2RuleOut, {
      d2
    });
    if (d1Valid && d2Valid) {
      d3RuleOut.min = biggerMinValueRule(d3RuleOut.min, {
        op: '>=',
        value: d2 + 2 * ftRuleOut.min.value
      });
      d3RuleOut.max = lesserMaxValueRule(d3RuleOut.max, {
        op: '<=',
        value: d2 + 2 * ftRuleOut.max.value
      });
    }
  }
  return [d1RuleOut, d2RuleOut, d3RuleOut, wtRuleOut, ftRuleOut];
};
/**
 * prepareB2B3Rules takes the rules for b1, b2 and b3 as parameters
 * and returns rules for b2 and b3 from it.
 *
 * b1 = b2 + b3 + b4
 */
const prepareB2B3Rules = (b1Rule, b2Rule, b3Rule, attributeAtVerify, data) => {
  if (!isIMinMaxValueRule(b1Rule)) {
    throw new Error('not all values defined for b1');
  }
  if (!isIMinMaxValueRule(b2Rule)) {
    throw new Error('not all values defined for b2');
  }
  if (!isIMinMaxValueRule(b3Rule)) {
    throw new Error('not all values defined for b3');
  }
  if (!data) {
    throw new Error('data not defined');
  }
  let b4 = 0;
  const d1 = data['d1'];
  if (d1) {
    // d1 is defined and not 0
    b4 = data['b4'] || EASY_CLIP_SHORT_NOSE_LENGTH_MM;
  } else {
    // d1 not defined
    b4 = EASY_CLIP_SHORT_NOSE_LENGTH_MM;
  }
  const b1AdjustedRule = Object.assign({}, b1Rule);
  let b2AdjustedRule = Object.assign({}, b2Rule);
  let b2RuleMin = b2Rule.min;
  let b2RuleMax = b2Rule.max;
  if (typeof attributeAtVerify !== 'undefined') {
    const b2 = data['b2'];
    if (b2 && attributeAtVerify !== 'b2' && executeIMinMaxRule(b2Rule, data)) {
      b2RuleMin = {
        op: '>=',
        value: b2
      };
      b2RuleMax = {
        op: '<=',
        value: b2
      };
      b2AdjustedRule = {
        attr: 'b2',
        min: b2RuleMin,
        max: b2RuleMax
      };
    } else {
      b2AdjustedRule = Object.assign({}, b2Rule);
    }
  }
  const b3AdjustedRule = Object.assign({}, b3Rule);
  // First adjust b1Rules to be consistent, because b1Min is just a guess
  // for b1Min values the sum of b2,b3 and b4 is relevant
  const b1Min = b2Rule.min.value + b3Rule.min.value + b4;
  b1AdjustedRule.min = biggerMinValueRule(b1Rule.min, {
    op: b1Rule.min.op,
    value: b1Min
  });
  // for b1Max its own value is relevant, so no adjustments required
  // Then adjust b3Max because it is a guess
  b3AdjustedRule.max.value = b1AdjustedRule.max.value - b2AdjustedRule.min.value - b4;
  // b2 is fix, so we don't change it
  return [b2Rule, b3AdjustedRule];
};
/**
 * Prepare diameters for MCM.
 */
const prepareCDiameterRules = (d1Rule, d2Rule, d3Rule, d4Rule, wtRule, ftD3Rule, ftD4Rule, attributeAtVerify, data) => {
  // rename the attribute from ftD3 into ft so that prepareDiameterOneFlangeRules can handle it
  if (ftD3Rule) {
    ftD3Rule.attr = 'ft';
  }
  const a = prepareDiameterOneFlangeRules(d1Rule, d2Rule, d3Rule, wtRule, ftD3Rule, attributeAtVerify, data);
  // rename the ft field back in the resultset to ftD3
  if (a[4]) {
    a[4].attr = 'ftD3';
  }
  // d4 (in MCM) is similar to d3, so we can use the existing prepareDiameterOneFlangeRules() also for d4
  // only the 3rd and 4th input and the 3rd and and 4th output must be renamed
  // rename d4 into d3 so that prepareDiameterOneFlangeRules can handle it similar to the d3 calculation
  if (d4Rule) {
    d4Rule.attr = 'd3';
  }
  // rename the attribute from ftD4 into ft so that prepareDiameterOneFlangeRules can handle it
  if (ftD4Rule) {
    ftD4Rule.attr = 'ft';
  }
  const b = prepareDiameterOneFlangeRules(d1Rule, d2Rule, d4Rule, wtRule, ftD4Rule, attributeAtVerify, data);
  // rename the d3 field back in the resultset to d4
  if (b[2]) {
    b[2].attr = 'd4';
  }
  // rename the ft field back in the resultset to ftD4
  if (b[4]) {
    b[4].attr = 'ftD4';
  }
  // console.log('--------------prepareMcmDiameterRules-------------');
  // console.log('a', a);
  // console.log('b', b);
  // fields are : d1  , d2  , d3  , d4  , wt  , ftD3, ftD4
  const result = [a[0], a[1], a[2], b[2], a[3], a[4], b[4]];
  // console.log('result', result);
  // console.log('  ');
  // console.log('---------------------------');
  return result;
};
/**
 * Prepare length of an MCM
 */
const prepareMcmLengthRules = (b1Rule, b2d3Rule, b2d4Rule, b3Rule, attributeAtVerify, data) => {
  if (!isIMinMaxValueRule(b1Rule)) {
    throw new Error('not all values defined for b1');
  }
  if (!isIMinMaxValueRule(b2d3Rule)) {
    throw new Error('not all values defined for b2d3');
  }
  if (!isIMinMaxValueRule(b2d4Rule)) {
    throw new Error('not all values defined for b2d4');
  }
  if (!isIMinMaxValueRule(b3Rule)) {
    throw new Error('not all values defined for b3');
  }
  if (!data) {
    throw new Error('data not defined');
  }
  const b1AdjustedRule = Object.assign({}, b1Rule);
  let b2d3AdjustedRule = Object.assign({}, b2d3Rule);
  const b2d4AdjustedRule = Object.assign({}, b2d4Rule);
  let b2d3RuleMin = b2d3Rule.min;
  let b2d3RuleMax = b2d3Rule.max;
  if (typeof attributeAtVerify !== 'undefined') {
    const b2d3 = data['b2d3'];
    if (b2d3 && attributeAtVerify !== 'b2' && executeIMinMaxRule(b2d3Rule, data)) {
      b2d3RuleMin = {
        op: '>=',
        value: b2d3
      };
      b2d3RuleMax = {
        op: '<=',
        value: b2d3
      };
      b2d3AdjustedRule = {
        attr: 'b2',
        min: b2d3RuleMin,
        max: b2d3RuleMax
      };
    } else {
      b2d3AdjustedRule = Object.assign({}, b2d3Rule);
    }
  }
  let b2d4RuleMin = b2d4Rule.min;
  let b2d4RuleMax = b2d4Rule.max;
  if (typeof attributeAtVerify !== 'undefined') {
    const b2d4 = data['b2d4'];
    if (b2d4 && attributeAtVerify !== 'b2' && executeIMinMaxRule(b2d4Rule, data)) {
      b2d4RuleMin = {
        op: '>=',
        value: b2d4
      };
      b2d4RuleMax = {
        op: '<=',
        value: b2d4
      };
      b2d3AdjustedRule = {
        attr: 'b2',
        min: b2d4RuleMin,
        max: b2d4RuleMax
      };
    } else {
      b2d3AdjustedRule = Object.assign({}, b2d4Rule);
    }
  }
  const b3AdjustedRule = Object.assign({}, b3Rule);
  // First adjust b1Rules to be consistent, because b1Min is just a guess
  // for b1Min values the sum of b2,b3 and b4 is relevant
  const b1Min = b2d3Rule.min.value + b3Rule.min.value + b2d4Rule.min.value;
  b1AdjustedRule.min = biggerMinValueRule(b1Rule.min, {
    op: b1Rule.min.op,
    value: b1Min
  });
  // for b1Max its own value is relevant, so no adjustments required
  // Then adjust b3Max because it is a guess
  b3AdjustedRule.max.value = b1AdjustedRule.max.value - b2d3AdjustedRule.min.value - b2d4AdjustedRule.min.value;
  // b2 is fix, so we don't change it
  return [b2d3Rule, b2d4Rule, b3AdjustedRule];
};
/**
 */
const prepareJoinTwoDiameterMinMaxRules = (dRuleA, dRuleB) => {
  const min = biggerMinValueRule(dRuleA.min, dRuleB.min);
  const max = lesserMaxValueRule(dRuleA.max, dRuleB.max);
  return {
    attr: dRuleA.attr,
    min,
    max
  };
};

/**
 * getB2Suggestion returns a suggestion for attribute b2
 *
 * The value depends on d1 and must be greater than d2 and lower than d3Max.
 * Valid for FlangeBearings (F) and EasyClips (EC).
 *
 * If no suggestion is possible the return value is undefined.
 *
 * @param wt wallthickness
 * @param wtMin minimum wallthickness
 * @param wtMax maximum wallthickness
 * @param b2_max_from_b1 maximum b2 from b1
 * @param unitSystem unitSystem
 */
const getB2Suggestion = (wt, wtMin, wtMax, b2_max_from_b1, unitSystem = UnitSystem.metric) => {
  if (!wt) {
    return undefined;
  }
  if (!wtMin) {
    return undefined;
  }
  if (!wtMax) {
    return undefined;
  }
  if (!b2_max_from_b1) {
    return undefined;
  }
  if (!unitSystem) {
    return undefined;
  }
  if (wt >= wtMin && wt <= wtMax && wt <= b2_max_from_b1) {
    return wt;
  } else {
    return Math.min(b2_max_from_b1, wtMax);
  }
};
/**
 * getD3Suggestion returns a suggestion for attribute d3
 *
 * The value depends on d1 and must be greater than d2 and lower than d3Max.
 * Valid for FlangeBearings (F) and EasyClips (EC).
 *
 * If no suggestion is possible the return value is undefined.
 */
const getD3Suggestion = (d1, d2, d3Max, unitSystem) => {
  if (!d1) {
    return undefined;
  }
  if (!d2) {
    return undefined;
  }
  if (!d3Max) {
    return undefined;
  }
  // the suggested d3 must be bigger than d2
  const d3Delta = 1;
  let d3Suggestion;
  if (unitSystem === UnitSystem.metric) {
    if (d1 >= 0.1 && d1 < 2) {
      d3Suggestion = 5;
    } else if (d1 >= 2 && d1 < 3) {
      d3Suggestion = 6.5;
    } else if (d1 >= 3 && d1 < 4) {
      d3Suggestion = 7.5;
    } else if (d1 >= 4 && d1 < 5) {
      d3Suggestion = 9.5;
    } else if (d1 >= 5 && d1 < 6) {
      d3Suggestion = 10;
    } else if (d1 >= 6 && d1 < 8) {
      d3Suggestion = 12;
    } else if (d1 >= 8 && d1 < 10) {
      d3Suggestion = 15;
    } else if (d1 >= 10 && d1 < 12) {
      d3Suggestion = 18;
    } else if (d1 >= 12 && d1 < 14) {
      d3Suggestion = 20;
    } else if (d1 >= 14 && d1 < 15) {
      d3Suggestion = 22;
    } else if (d1 >= 15 && d1 < 16) {
      d3Suggestion = 23;
    } else if (d1 >= 16 && d1 < 18) {
      d3Suggestion = 24;
    } else if (d1 >= 18 && d1 < 20) {
      d3Suggestion = 26;
    } else if (d1 >= 20 && d1 < 25) {
      d3Suggestion = 30;
    } else if (d1 >= 25 && d1 < 30) {
      d3Suggestion = 35;
    } else if (d1 >= 30 && d1 < 35) {
      d3Suggestion = 42;
    } else if (d1 >= 35 && d1 < 40) {
      d3Suggestion = 47;
    } else if (d1 >= 40 && d1 < 45) {
      d3Suggestion = 53;
    } else if (d1 >= 45 && d1 < 50) {
      d3Suggestion = 58;
    } else if (d1 >= 50 && d1 < 60) {
      d3Suggestion = 63;
    } else if (d1 >= 60 && d1 < 65) {
      d3Suggestion = 73;
    } else if (d1 >= 65 && d1 < 70) {
      d3Suggestion = 78;
    } else if (d1 >= 70 && d1 < 75) {
      d3Suggestion = 80;
    } else if (d1 >= 75 && d1 < 80) {
      d3Suggestion = 88;
    } else if (d1 >= 80 && d1 < 85) {
      d3Suggestion = 93;
    } else if (d1 >= 85 && d1 < 90) {
      d3Suggestion = 98;
    } else if (d1 >= 90 && d1 < 95) {
      d3Suggestion = 103;
    } else if (d1 >= 95 && d1 < 100) {
      d3Suggestion = 108;
    } else if (d1 >= 100 && d1 < 110) {
      d3Suggestion = 113;
    } else if (d1 >= 110 && d1 < 120) {
      d3Suggestion = 123;
    } else if (d1 >= 120 && d1 < 125) {
      d3Suggestion = 133;
    } else if (d1 >= 125 && d1 < 130) {
      d3Suggestion = 138;
    } else if (d1 >= 130 && d1 < 140) {
      d3Suggestion = 143;
    } else if (d1 >= 140 && d1 < 150) {
      d3Suggestion = 153;
    } else if (d1 >= 150 && d1 < 195) {
      d3Suggestion = 163;
    } else if (d1 >= 195) {
      d3Suggestion = 240;
    }
  } else {
    if (d1 >= 0.125 && d1 < 0.188) {
      d3Suggestion = 0.312;
    } else if (d1 >= 0.188 && d1 < 0.25) {
      d3Suggestion = 0.375;
    } else if (d1 >= 0.25 && d1 < 0.313) {
      d3Suggestion = 0.5;
    } else if (d1 >= 0.313 && d1 < 0.375) {
      d3Suggestion = 0.562;
    } else if (d1 >= 0.375 && d1 < 0.438) {
      d3Suggestion = 0.687;
    } else if (d1 >= 0.438 && d1 < 0.5) {
      d3Suggestion = 0.75;
    } else if (d1 >= 0.5 && d1 < 0.625) {
      d3Suggestion = 0.875;
    } else if (d1 >= 0.625 && d1 < 0.75) {
      d3Suggestion = 0.937;
    } else if (d1 >= 0.75 && d1 < 0.875) {
      d3Suggestion = 1.125;
    } else if (d1 >= 0.875 && d1 < 1.0) {
      d3Suggestion = 1.25;
    } else if (d1 >= 1.0 && d1 < 1.125) {
      d3Suggestion = 1.375;
    } else if (d1 >= 1.125 && d1 < 1.25) {
      d3Suggestion = 1.562;
    } else if (d1 >= 1.25 && d1 < 1.375) {
      d3Suggestion = 1.687;
    } else if (d1 >= 1.375 && d1 < 1.5) {
      d3Suggestion = 1.875;
    } else if (d1 >= 1.5 && d1 < 1.75) {
      d3Suggestion = 2;
    } else if (d1 >= 1.75 && d1 < 2.0) {
      d3Suggestion = 2.375;
    } else if (d1 >= 2.0 && d1 < 2.25) {
      d3Suggestion = 2.625;
    } else if (d1 >= 2.25 && d1 < 2.5) {
      d3Suggestion = 2.75;
    } else if (d1 >= 2.5 && d1 < 2.75) {
      d3Suggestion = 3.125;
    } else if (d1 >= 2.75) {
      d3Suggestion = 3.375;
    }
  }
  if (!d3Suggestion) {
    return undefined;
  }
  // resulting d3Suggestion must be greater than d2
  if (d3Suggestion < d2) {
    d3Suggestion = d2 + d3Delta;
  } else if (d3Suggestion === d2) {
    d3Suggestion = d3Suggestion + d3Delta;
  }
  // resulting d3Suggestion must be lower or equal than d3Max
  d3Suggestion = Math.min(d3Suggestion, d3Max);
  return d3Suggestion;
};
/**
 * getSDataSetIndex
 *
 * To implement data given by
 * https://igusgmbh-my.sharepoint.com/:x:/g/personal/tschultes_igus_net/ETY6PtzQ3_5Goz4-dC2dssoB9fSVF9kQcFiQzQYoKqtquA?email=ohoos.ext%40igus.net&e=RZKlg3
 * this function returns
 *
 *                    index
 * 3 <= d1 <= 6
 * 3 <= b1 <= 12      0
 *
 * 6 < d1 <= 12
 * 3 <= b1  <= 20     1
 *
 * 12 < d1 <= 20
 * 5 <= b1 <= 25      2
 *
 * 12 < d1 <= 20
 * 25 <= b1 <= 35     3
 *
 * 20 < d1 <= 35
 * 5 <= b1 <= 25      4
 *
 * 20 < d1 <= 35
 * 25 <= b1 <= 45     5
 *
 * 35 < d1 <= 50
 * 5 <= b1 <= 35      6
 *
 * 35 < d1 <= 50
 * 35 <= b1 <= 50     7
 *
 * in other cases the index is undefined.
 */
const getSDataSetIndex = (d1, b1) => {
  let index;
  if (3 < d1 && d1 <= 6) {
    if (3 < b1 && b1 <= 12) {
      index = 0;
    }
  } else if (6 < d1 && d1 <= 12) {
    if (3 < b1 && b1 <= 20) {
      index = 1;
    }
  } else if (12 < d1 && d1 <= 20) {
    if (5 < b1 && b1 <= 25) {
      index = 2;
    } else if (25 < b1 && b1 <= 35) {
      index = 3;
    }
  } else if (20 < d1 && d1 <= 35) {
    if (5 < b1 && b1 <= 25) {
      index = 4;
    } else if (25 < b1 && b1 <= 45) {
      index = 5;
    }
  } else if (35 < d1 && d1 <= 50) {
    if (5 < b1 && b1 <= 35) {
      index = 6;
    } else if (35 < b1 && b1 <= 50) {
      index = 7;
    }
  }
  return index;
};
/**
 * getSFMinimalWallthicknessB1 calculates the minimal wallthickness for an S bearing.
 *
 * To do so it requires the material, d1 and b1.
 * That means the parameter-by-parameter check is NOT possible because we would need to know the wt to suggest min d2.
 * But at that point b1 (which is required to calculate the wt) is not yet given.
 *
 * So this can be used only in environments where the complete dataset (d1, d2, b1 for S) is given to check if the part
 * is producible. Or b1 needs to be entered before d2.
 *
 *
 * To implement data given by
 * https://igusgmbh-my.sharepoint.com/:x:/g/personal/tschultes_igus_net/ETY6PtzQ3_5Goz4-dC2dssoB9fSVF9kQcFiQzQYoKqtquA?email=ohoos.ext%40igus.net&e=RZKlg3
 * this function returns the minimal Wallthickness
 * for different materials, which is represented in a data array like this [0.6, 0.6, 0.75, 1, 1, 1.25, 1.5, 1.75]
 */
const getSFMinimalWallthicknessB1 = (materialCode, d1, b1, minWtB1Fallback) => {
  const index = getSDataSetIndex(d1, b1);
  if (typeof index === 'undefined') {
    return minWtB1Fallback;
  }
  switch (materialCode) {
    case 'G':
    case 'J':
    case 'RN56':
    case 'GLW':
    case 'W300':
    case 'L280':
    case 'M250':
    case 'J4':
    case 'J3':
    case 'A180':
    case 'JB':
    case 'P':
      return [0.6, 0.6, 0.75, 1, 1, 1.25, 1.5, 1.75][index] || minWtB1Fallback;
    case 'Z':
    case 'J350':
      return [0.7, 0.7, 1, 1.5, 1.5, 2, 2, 2][index] || minWtB1Fallback;
    case 'X':
      return [0.7, 0.7, 0.85, 1, 1, 1.25, 2, 2][index] || minWtB1Fallback;
    case 'R':
      return [0.75, 0.75, 1, 1.5, 1.5, 2, 2, 3][index] || minWtB1Fallback;
    case 'A181':
      return [0.7, 0.9, 0.9, 1, 1.25, 1.5, 2, 2][index] || minWtB1Fallback;
    case 'A350':
    case 'H':
    case 'H1':
    case 'H2':
    case 'H370':
    case 'P210':
    case 'Q2':
      return [0.7, 0.7, 0.85, 1, 1.25, 1.5, 2, 2][index] || minWtB1Fallback;
    case 'AX500':
      return [1, 1, 1, 1.25, 1.5, 2, 2, 2][index] || minWtB1Fallback;
    case 'J260':
      return [1, 1, 1.5, 1.75, 1.75, 2, 2, 2.5][index] || minWtB1Fallback;
  }
  return minWtB1Fallback;
};
/**
 * getFDataSetIndex
 *
 * To implement data given by
 * https://igusgmbh-my.sharepoint.com/:x:/g/personal/tschultes_igus_net/ETY6PtzQ3_5Goz4-dC2dssoB9fSVF9kQcFiQzQYoKqtquA?email=ohoos.ext%40igus.net&e=RZKlg3
 * this function returns
 *
 *                    index
 * 3 <= d1 <= 6
 * 3 <= b1 <= 12      0
 * d3 < d2+6
 *
 * 6 < d1 <= 12
 * 3 <= b1  <= 20     1
 * d3 < d2+6
 *
 * 12 < d1 <= 20
 * 5 <= b1 <= 25      2
 * d3 < d2+8
 *
 * 12 < d1 <= 20
 * 25 <= b1 <= 35     3
 * d3 < d2+8
 *
 * 20 < d1 <= 35
 * 5 <= b1 <= 25      4
 * d3 < d2+10
 *
 * 20 < d1 <= 35
 * 25 <= b1 <= 45     5
 * d3 < d2+10
 *
 * 35 < d1 <= 50
 * 5 <= b1 <= 35      6
 * d3 < d2+10
 *
 * 35 < d1 <= 50
 * 35 <= b1 <= 50     7
 * d3 < d2+10
 *
 * in other cases the index is undefined.
 */
const getFDataSetIndex = (d1, d2, d3, b1) => {
  let index;
  if (d1 >= d2 || d2 >= d3) {
    return index;
  }
  if (3 < d1 && d1 <= 6) {
    if (3 < b1 && b1 <= 12) {
      if (d3 < d2 + 6) {
        index = 0;
      }
    }
  } else if (6 < d1 && d1 <= 12) {
    if (3 < b1 && b1 <= 20) {
      if (d3 < d2 + 6) {
        index = 1;
      }
    }
  } else if (12 < d1 && d1 <= 20) {
    if (5 < b1 && b1 <= 25) {
      if (d3 < d2 + 8) {
        index = 2;
      }
    } else if (25 < b1 && b1 <= 35) {
      if (d3 < d2 + 8) {
        index = 3;
      }
    }
  } else if (20 < d1 && d1 <= 35) {
    if (5 < b1 && b1 <= 25) {
      if (d3 < d2 + 10) {
        index = 4;
      }
    } else if (25 < b1 && b1 <= 45) {
      if (d3 < d2 + 10) {
        index = 5;
      }
    }
  } else if (35 < d1 && d1 <= 50) {
    if (5 < b1 && b1 <= 35) {
      if (d3 < d2 + 10) {
        index = 6;
      }
    } else if (35 < b1 && b1 <= 50) {
      if (d3 < d2 + 10) {
        index = 7;
      }
    }
  }
  return index;
};
/**
 * getFWallthicknessB2MinMax calculates the minimal and maximal wallthickness for an F bearing.
 */
const getFWallthicknessB2MinMax = (materialCode, d1, d2, d3, b1, minWtB2Fallback, maxWtB2Fallback) => {
  const index = getFDataSetIndex(d1, d2, d3, b1);
  if (typeof index === 'undefined') {
    return [minWtB2Fallback, maxWtB2Fallback];
  }
  const minWtB2 = getSFMinimalWallthicknessB1(materialCode, d1, b1, minWtB2Fallback);
  let maxWtB2 = 2;
  switch (index) {
    case 0:
    case 1:
      maxWtB2 = 2;
      break;
    case 2:
    case 3:
      maxWtB2 = 3;
      break;
    case 4:
    case 5:
    case 6:
    case 7:
      maxWtB2 = 4;
      break;
  }
  return [minWtB2, maxWtB2];
};

let RuleboxMIEasyClip = class RuleboxMIEasyClip extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (this.isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 2;
    } else {
      this.wtMin = 0.75;
      this.wtMax = 2;
    }
    this.d3Max = 48;
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'd3',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'b2',
      type: 'DISTANCE',
      inputOrder: 13
    });
    this.addAttribute({
      name: 'b3',
      type: 'DISTANCE',
      inputOrder: 14
    });
    this.addAttribute({
      name: 'sb',
      type: 'DISTANCE',
      inputOrder: 15
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addAttribute({
      name: 'ft',
      type: 'DISTANCE',
      dependsOn: ['d2', 'd3'],
      fnAsReadableString: '(d3 - d2) / 2',
      fn: () => round5((this.getNumberValue('d3') - this.getNumberValue('d2')) / 2)
    });
    this.addAttribute({
      name: 'b4',
      type: 'DISTANCE',
      dependsOn: [],
      fnAsReadableString: '1.2',
      fn: () => getEasyClipNoseLength({
        d1: this.getNumberValue('d1'),
        unitSystem
      })
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      dependsOn: ['b2', 'b3', 'b4'],
      fnAsReadableString: 'b2 + b3 + b4',
      fn: () => this.getNumberValue('b2') + this.getNumberValue('b3') + this.getNumberValue('b4')
    });
    this.addAttribute({
      name: 'sl',
      type: 'DISTANCE',
      dependsOn: ['b3', 'b4'],
      fnAsReadableString: 'b3 + b4',
      fn: () => this.getNumberValue('b3') + this.getNumberValue('b4')
    });
    this.addAttribute({
      name: 'b5',
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'd1 < 10 ? 0.3 : 0.5',
      fn: () => this.getNumberValue('d1') < 10 ? 0.3 : 0.5
    });
    this.addAttribute({
      name: 'W',
      type: 'ANGLE',
      dependsOn: [],
      fnAsReadableString: '30',
      fn: () => 30
    });
    this.addAttribute({
      name: 'ab1',
      type: 'DISTANCE',
      dependsOn: ['d2'],
      fnAsReadableString: 'd2',
      fn: () => this.getNumberValue('d2')
    });
    this.addAttribute({
      name: 'ab2',
      type: 'DISTANCE',
      dependsOn: ['d2'],
      fnAsReadableString: 'd2 / 2',
      fn: () => this.getNumberValue('d2') / 2
    });
    this.addAttribute({
      name: 'b2_suggestion',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['wt', 'd2', 'd1'],
      fn: () => this.getNumberValue('wt')
    });
    this.addAttribute({
      name: 'd3_suggestion',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d2', 'd1'],
      fn: () => getD3Suggestion(this.getNumberValue('d1'), this.getNumberValue('d2'), this.d3Max, this.unitSystem)
    });
    this.addShape({
      name: 'EC_straight_slot',
      description: 'Easy Clip with straight slot',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Expand result of verify() to contain the principalMoldName
   */
  verify() {
    const v = super.verify();
    const principalMoldName = this.getPrincipalMoldName(v.rulesetValue, this.getNumberValue('d3'));
    return Object.assign({}, v, {
      principalMoldName
    });
  }
  /**
   * Convenient method to pass EasyClipDimensions directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      d3: userPart.d3,
      b2: userPart.b2,
      b3: userPart.b3,
      sb: userPart.sb
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for EasyClip
   */
  defineRulesets() {
    let wtMin = this.wtMin;
    let ftMin = 0.25; // according to IGL-330
    const ftMax = 20; // max value of ((d3-d2_min) / 2)
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
      ftMin = 2;
    }
    const allowedMaterials = this.isPmFastLine ? materialsEasyClipFastLine : materialsEasyClip;
    const all = [{
      value: 'EC_1',
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 6,
          force: true
        },
        max: {
          op: '<',
          value: 10,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 1,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareB2B3Rules({
        attr: 'b1',
        min: {
          op: '>=',
          value: 5 + 0.75 + 1.2
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: 15
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'sb',
        min: {
          op: '>=',
          value: 1
        },
        max: {
          op: '<=',
          value: 2
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'EC_2',
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 10,
          force: true
        },
        max: {
          op: '<',
          value: 25,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 10,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 10,
          force: false
        },
        max: {
          op: '<=',
          value: 31,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 1.5,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareB2B3Rules({
        attr: 'b1',
        min: {
          op: '>=',
          value: 5 + 0.75 + 1.5
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2.5
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: 15
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'sb',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 3
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'EC_3',
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 25,
          force: true
        },
        max: {
          op: '<=',
          value: 40,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 25,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 25,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 2,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareB2B3Rules({
        attr: 'b1',
        min: {
          op: '>=',
          value: 5 + 0.75 + 1.5
        },
        max: {
          op: '<=',
          value: 17
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: 17
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'sb',
        min: {
          op: '>=',
          value: 2
        },
        max: {
          op: '<=',
          value: 4
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials.filter(m => !(m === 'K230'))
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    this.logRulesets('EC allRelevant', allRelevant);
    return applyRulesetIds(allRelevant);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    return principalMoldNamesEC.indexOf(principalMoldName) >= 0;
  }
  /**
   * Returns the principal mold name for EasyClip.
   */
  getPrincipalMoldName(rulesetValue, d3) {
    let principalMoldName;
    if (!rulesetValue) {
      return undefined;
    }
    if (!d3) {
      return undefined;
    }
    if (6 <= d3 && d3 < 15) {
      principalMoldName = 'Clip1_EC';
    } else if (15 <= d3 && d3 < 31) {
      principalMoldName = 'Clip2_EC';
    } else if (31 <= d3 && d3 <= 48) {
      principalMoldName = 'Clip3_EC';
    } else {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIEasyClip = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIEasyClip);

/**
 *  B_MIN_DISTANCE [mm]
 *
 *  must be bigger than the sum of
 *
 *  getFlangeBearingRadius() [0.5]
 *    and
 *  2 * BEVEL_AXIAL_MIN_DISTANCE [0.1]
 */
const B_MIN_DISTANCE = 0.75;
let RuleboxMIFlangeBearing = class RuleboxMIFlangeBearing extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 3;
      this.d3Max = 60;
    } else {
      this.wtMin = 0.5;
      // changed regarding IGL-1939
      this.wtMax = 3;
      this.d3Max = 160;
    }
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'd3',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      inputOrder: 13
    });
    this.addAttribute({
      name: 'b2',
      type: 'DISTANCE',
      inputOrder: 14
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'heatedMold',
      type: 'BOOLEAN'
    });
    this.addAttribute({
      name: 'wt',
      visible: true,
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addAttribute({
      name: 'ft',
      type: 'DISTANCE',
      dependsOn: ['d2', 'd3'],
      fnAsReadableString: '(d3 - d2) / 2',
      fn: () => round5((this.getNumberValue('d3') - this.getNumberValue('d2')) / 2)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__13',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 13) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 13)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__45',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 45) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 45)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__51',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 51) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 51)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__80',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 80) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 80)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__90',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 90) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 90)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__100',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 100) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 100)
    });
    this.addAttribute({
      name: 'b2_max_from_b1_and_val_7',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (b1 - 0.75, 7) ',
      fn: () => Math.min(this.getNumberValue('wt'), this.getNumberValue('b1') - B_MIN_DISTANCE, 7)
    });
    this.addAttribute({
      name: 'b2_max_from_b1_and_val_10',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (b1 - 0.75, 10) ',
      fn: () => Math.min(this.getNumberValue('wt'), this.getNumberValue('b1') - B_MIN_DISTANCE, 10)
    });
    this.addAttribute({
      name: 'b2_max_from_b1_and_val_20',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (b1 - 0.75, 20) ',
      fn: () => Math.min(this.getNumberValue('wt'), this.getNumberValue('b1') - B_MIN_DISTANCE, 20)
    });
    this.addAttribute({
      name: 'b2_max_from_b1_and_val_50',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (b1 - 0.75, 50) ',
      fn: () => Math.min(this.getNumberValue('wt'), this.getNumberValue('b1') - B_MIN_DISTANCE, 50)
    });
    this.addAttribute({
      name: 'b2_suggestion',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['wt', 'd2', 'd1'],
      fn: () => getB2Suggestion(this.getNumberValue('wt'), this.wtMin, this.wtMax, Math.min(this.getNumberValue('wt'), this.getNumberValue('b1') - B_MIN_DISTANCE))
    });
    this.addAttribute({
      name: 'd3_suggestion',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d2', 'd1'],
      fn: () => getD3Suggestion(this.getNumberValue('d1'), this.getNumberValue('d2'), this.d3Max, this.unitSystem)
    });
    this.addShape({
      name: 'F',
      description: 'Flange bearing default',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Convenient method to pass FlangeBearingParams directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      d3: userPart.d3,
      b1: userPart.b1,
      b2: userPart.b2
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      heatedMold: material.heatedMold,
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for FlangeBearing
   */
  defineRulesets() {
    let wtMin = this.wtMin;
    const wtMax = this.wtMax;
    const d1Min = this.isPmFastLine ? 3 : 1;
    const b1Min = 1.25;
    const b2Min = 0.5;
    let ftMin = 0.25; // according to IGL-330
    const ftMax = 20; // max value of ((d3-d2_min) / 2)
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
      ftMin = 2;
    }
    const all = [{
      value: 'F_1',
      // S9B
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 8,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_10'
        }
      }]
    }, {
      value: 'F_2',
      // S9
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 8,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_10'
        }
      }]
    }, {
      value: 'F_3',
      // S17B
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_4',
      // S17
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__45'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_5',
      // S25
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 22,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 22,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 25,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__45'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_6',
      // S40
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 36,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 36,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 40,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__51'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_7',
      // S50
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 53,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__80'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'F_8',
      // S65
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 53,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__90'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'F_9',
      // S60
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 42,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 55,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 60,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__51'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_7'
        }
      }]
    }, {
      value: 'F_10',
      // S65
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 60,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 60,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 65,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_11',
      // S80
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 80,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 80,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 90,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }]
    }, {
      value: 'F_12',
      // S120
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'F_13',
      // S160
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_20'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'F_14',
      // S160
      rule: [...prepareDiameterOneFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 160,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 160,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 160,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, {
        attr: 'ft',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: b2Min
        },
        max: {
          op: '<=',
          variable: 'b2_max_from_b1_and_val_50'
        }
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(this.isPmFastLine ? allRelevant.filter(r => ['F_4', 'F_5', 'F_6', 'F_9'].indexOf(r.value) >= 0) : all);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    if (this.isPmFastLine) {
      return principalMoldNameSFTFastLine.indexOf(principalMoldName) >= 0;
    } else {
      return principalMoldNamesSFT.indexOf(principalMoldName) >= 0;
    }
  }
  /**
   * Returns the principal mold name for FlangeBearing.
   */
  getPrincipalMoldName(rulesetValue, _n) {
    let principalMoldName;
    if (!rulesetValue) {
      return undefined;
    }
    switch (rulesetValue) {
      case 'F_1':
        principalMoldName = 'S9B';
        break;
      case 'F_2':
        principalMoldName = 'S9';
        break;
      case 'F_3':
        principalMoldName = 'S17B';
        break;
      case 'F_4':
        principalMoldName = 'S17';
        break;
      case 'F_5':
        principalMoldName = 'S25';
        break;
      case 'F_6':
        principalMoldName = 'S40';
        break;
      case 'F_7':
        principalMoldName = 'S50';
        break;
      case 'F_8':
        principalMoldName = 'S65';
        break;
      case 'F_9':
        principalMoldName = 'S60';
        break;
      case 'F_10':
        principalMoldName = 'S65';
        break;
      case 'F_11':
        principalMoldName = 'S80';
        break;
      case 'F_12':
        principalMoldName = 'S122';
        break;
      case 'F_13':
        principalMoldName = 'S162';
        break;
      case 'F_14':
        principalMoldName = 'S162';
        break;
    }
    if (!principalMoldName) {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIFlangeBearing = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIFlangeBearing);

let RuleboxMIMcm = class RuleboxMIMcm extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (this.isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 2;
    } else {
      this.wtMin = 0.75;
      this.wtMax = 2;
    }
    this.d3Max = 48;
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'd3',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'd4',
      type: 'DISTANCE',
      inputOrder: 14
    });
    this.addAttribute({
      name: 'b2d3',
      type: 'DISTANCE',
      inputOrder: 20
    });
    this.addAttribute({
      name: 'b2d4',
      type: 'DISTANCE',
      inputOrder: 21
    });
    this.addAttribute({
      name: 'b3',
      type: 'DISTANCE',
      inputOrder: 22
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addAttribute({
      name: 'ftD3',
      type: 'DISTANCE',
      dependsOn: ['d2', 'd3'],
      fnAsReadableString: '(d3 - d2) / 2',
      fn: () => round5((this.getNumberValue('d3') - this.getNumberValue('d2')) / 2)
    });
    this.addAttribute({
      name: 'ftD4',
      type: 'DISTANCE',
      dependsOn: ['d2', 'd4'],
      fnAsReadableString: '(d4 - d2) / 2',
      fn: () => round5((this.getNumberValue('d4') - this.getNumberValue('d2')) / 2)
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      dependsOn: ['b2d3', 'b2d4', 'b3'],
      fnAsReadableString: 'b2d3 + b2d4 + b3',
      fn: () => this.getNumberValue('b3') + this.getNumberValue('b2d3') + this.getNumberValue('b2d4')
    });
    this.addShape({
      name: 'C',
      description: 'MCM with angled slot',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Expand result of verify() to contain the principalMoldName
   */
  verify() {
    const v = super.verify();
    const principalMoldName = this.getPrincipalMoldName(v.rulesetValue, this.getNumberValue('d4'));
    return Object.assign({}, v, {
      principalMoldName
    });
  }
  /**
   * Convenient method to pass McmDimensions directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      d3: userPart.d3,
      d4: userPart.d4,
      b2d3: userPart.b2d3,
      b2d4: userPart.b2d4,
      b3: userPart.b3
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for MCM
   */
  defineRulesets() {
    let ftMin = 0.25;
    const ftMax = 20; // max value of ((d3-d2Min) / 2)
    let wtMin = this.wtMin;
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
      ftMin = 2;
    }
    const allowedMaterials = this.isPmFastLine ? materialsCFastLine : materialsC;
    const all = [{
      value: 'MCM_1',
      rule: [...prepareCDiameterRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 6,
          force: true
        },
        max: {
          op: '<',
          value: 10,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<',
          value: 15,
          force: true
        }
      }, {
        attr: 'd4',
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 1,
          force: true
        }
      }, {
        attr: 'ftD3',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, {
        attr: 'ftD4',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareMcmLengthRules({
        attr: 'b1',
        min: {
          op: '>=',
          value: 1
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2d3',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2
        }
      }, {
        attr: 'b2d4',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 2
        },
        max: {
          op: '<=',
          value: 13.5
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'MCM_2',
      rule: [...prepareCDiameterRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 10,
          force: true
        },
        max: {
          op: '<',
          value: 25,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 10,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 10,
          force: false
        },
        max: {
          op: '<',
          value: 31,
          force: true
        }
      }, {
        attr: 'd4',
        min: {
          op: '>=',
          value: 10,
          force: false
        },
        max: {
          op: '<',
          value: 31,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 1.5,
          force: true
        }
      }, {
        attr: 'ftD3',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, {
        attr: 'ftD4',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareMcmLengthRules({
        attr: 'b1',
        min: {
          op: '>',
          value: 1
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2d3',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2.5
        }
      }, {
        attr: 'b2d4',
        min: {
          op: '>=',
          value: 0.75
        },
        max: {
          op: '<=',
          value: 2.5
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 2
        },
        max: {
          op: '<=',
          value: 13.5
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'MCM_3',
      rule: [...prepareCDiameterRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 25,
          force: true
        },
        max: {
          op: '<=',
          value: 40,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 25,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd3',
        min: {
          op: '>=',
          value: 25,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: true
        }
      }, {
        attr: 'd4',
        min: {
          op: '>=',
          value: 25,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 2,
          force: true
        }
      }, {
        attr: 'ftD3',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, {
        attr: 'ftD4',
        min: {
          op: '>=',
          value: ftMin,
          force: true
        },
        max: {
          op: '<=',
          value: ftMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), ...prepareMcmLengthRules({
        attr: 'b1',
        min: {
          op: '>',
          value: 1
        },
        max: {
          op: '<=',
          value: 17
        }
      }, {
        attr: 'b2d3',
        min: {
          op: '>=',
          value: 1
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'b2d4',
        min: {
          op: '>=',
          value: 1
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'b3',
        min: {
          op: '>=',
          value: 2
        },
        max: {
          op: '<=',
          value: 15.5
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials.filter(m => !(m === 'K230'))
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    this.logRulesets('MCM allRelevant', allRelevant);
    return applyRulesetIds(allRelevant);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    return principalMoldNamesMCM.indexOf(principalMoldName) >= 0;
  }
  /**
   * Returns the principal mold name for MCM.
   */
  getPrincipalMoldName(rulesetValue, d4) {
    let principalMoldName;
    if (!rulesetValue || !d4) {
      return undefined;
    }
    // MCM uses the same PrincipalMolds as EasyClip!
    if (6 <= d4 && d4 < 15) {
      principalMoldName = 'Clip1_EC';
    } else if (15 <= d4 && d4 < 31) {
      principalMoldName = 'Clip1_EC';
    } else if (31 <= d4 && d4 <= 48) {
      principalMoldName = 'Clip1_EC';
    } else {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIMcm = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIMcm);

let RuleboxMIPistonRing = class RuleboxMIPistonRing extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (this.isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 3;
    } else {
      this.wtMin = 1;
      this.wtMax = 3;
    }
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'b2',
      type: 'DISTANCE',
      inputOrder: 14
    });
    this.addAttribute({
      name: 'slotAngle',
      type: 'ANGLE',
      inputOrder: 13
    });
    this.addAttribute({
      name: 'materialShrinkage',
      type: 'PERCENTAGE',
      inputOrder: 2
    }); // value from DB - like 1.015
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      visible: true,
      // FIXME: is this 'visible' still used? It IS used, but not for visibility :(
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addAttribute({
      name: 'b2_min_gs1',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min(d1/6, 1)',
      fn: () => Math.min(this.getNumberValue('d1') / 6, 1)
    });
    this.addAttribute({
      name: 'b2_max_gs1',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'max(d1/6, 1)',
      fn: () => Math.max(this.getNumberValue('d1') / 6, 1)
    });
    this.addAttribute({
      name: 'b2_min_gs2',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min(d1/6, 2)',
      fn: () => Math.min(this.getNumberValue('d1') / 6, 2)
    });
    this.addAttribute({
      name: 'b2_max_gs2',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'max(d1/6, 2)',
      fn: () => Math.max(this.getNumberValue('d1') / 6, 2)
    });
    this.addAttribute({
      name: 'b2_min_gs3',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'max(d1/6, 3)',
      fn: () => Math.min(this.getNumberValue('d1') / 6, 3)
    });
    this.addAttribute({
      name: 'b2_max_gs3',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'max(d1/6, 3)',
      fn: () => Math.max(this.getNumberValue('d1') / 6, 3)
    });
    this.addShape({
      name: 'PR_GS',
      description: 'Pistonring with straight slot',
      rulesetsFn: this.defineRulesetsGS.bind(this)
    });
    this.addShape({
      name: 'PR_SS',
      description: 'Pistonring with angular slot with ejector',
      rulesetsFn: this.defineRulesetsSS.bind(this)
    });
    this.addShape({
      name: 'PR_SIS',
      description: 'Pistonring with angular slot and internal slider',
      rulesetsFn: this.defineRulesetsSIS.bind(this)
    });
    this.addShape({
      name: 'PR_SAS',
      description: 'Pistonring with angular slot and external slider',
      rulesetsFn: this.defineRulesetsSAS.bind(this)
    });
  }
  /**
   * Expand result of verify() to contain the principalMoldName
   */
  verify() {
    const v = super.verify();
    const principalMoldName = this.getPrincipalMoldName(v.rulesetValue, this.getNumberValue('d2'));
    return Object.assign({}, v, {
      principalMoldName
    });
  }
  /**
   * Convenient method to pass PistonRingDimensions directly to set data.
   */
  setDataFromDimensions(userPart) {
    let slotAngle = userPart.slotAngle;
    if (typeof slotAngle === 'undefined') {
      slotAngle = userPart['slotAngle'];
    }
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      b1: userPart.b1,
      b2: userPart.b2,
      slotAngle
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      materialShrinkage: material.shrinkage,
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for PistonRing GS (straight_slot)
   */
  defineRulesetsGS() {
    let d2Max;
    let d2MaxOp;
    if (this.isPmFastLine) {
      d2Max = 50;
      d2MaxOp = '<';
    } else {
      d2Max = 87;
      d2MaxOp = '<=';
    }
    const b1MaxLimit = this.isPmFastLine ? 12 : 40;
    let wtMin = this.wtMin;
    const wtMin_GS3 = 2;
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
    }
    // This d2 pre-check solves the problem of IGL-1314
    let defaultD2PrepareRule = {
      attr: 'd2',
      min: {
        op: '>=',
        value: 4,
        force: false
      },
      max: {
        op: d2MaxOp,
        value: d2Max,
        force: false
      }
    };
    if (this.isPmFastLine) {
      const defaultD2PFastLineRule = {
        min: {
          op: '>=',
          value: 6,
          force: false
        },
        max: {
          op: '<',
          value: 50,
          force: true
        }
      };
      defaultD2PrepareRule = prepareJoinTwoDiameterMinMaxRules(defaultD2PrepareRule, defaultD2PFastLineRule);
    }
    const all = [{
      value: 'GS_1',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 4,
          force: true
        },
        max: {
          op: '<',
          value: 5,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 1.5,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 3
        },
        max: {
          op: '<=',
          value: Math.min(15, b1MaxLimit)
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          variable: 'b2_min_gs1'
        },
        max: {
          op: '<=',
          variable: 'b2_max_gs1'
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '=',
          value: 0
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }, {
      value: 'GS_2',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 5,
          force: true
        },
        max: {
          op: '<',
          value: 40,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: Math.min(25, b1MaxLimit)
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          variable: 'b2_min_gs2'
        },
        max: {
          op: '<=',
          variable: 'b2_max_gs2'
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '=',
          value: 0
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }, {
      value: 'GS_3',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 40,
          force: true
        },
        max: {
          op: '<=',
          value: 80,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin_GS3,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: Math.min(40, b1MaxLimit)
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          variable: 'b2_min_gs3'
        },
        max: {
          op: '<=',
          variable: 'b2_max_gs3'
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '=',
          value: 0
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(allRelevant);
  }
  /**
   * returns the rulesets for PistonRing SS (ejector)
   */
  defineRulesetsSS() {
    let d2Max;
    let d2MaxOp;
    // latest version before SS was declined for FastLine
    if (this.isPmFastLine) {
      d2Max = 48;
      d2MaxOp = '<';
    } else {
      d2Max = 87;
      d2MaxOp = '<=';
    }
    // This d2 pre-check solves the problem of IGL-1314
    let defaultD2PrepareRule = {
      attr: 'd2',
      min: {
        op: '>=',
        value: 10,
        force: false
      },
      max: {
        op: d2MaxOp,
        value: d2Max,
        force: false
      }
    };
    if (this.isPmFastLine) {
      // FastLine is not available for these forms, so set a rule with impossible range
      // (instead of setting an empty route)
      const defaultD2PFastLineRule = {
        min: {
          op: '>=',
          value: 20,
          force: true
        },
        max: {
          op: '<',
          value: 10,
          force: true
        }
      };
      defaultD2PrepareRule = prepareJoinTwoDiameterMinMaxRules(defaultD2PrepareRule, defaultD2PFastLineRule);
    }
    const all = [{
      value: 'SS_1',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 10,
          force: true
        },
        max: {
          op: '<',
          value: 40,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: 2,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '>',
          value: 0
        },
        max: {
          op: '<=',
          value: 25
        }
      }, {
        attr: 'materialShrinkage',
        min: {
          op: '>=',
          value: 1.02
        }
      }]
    }, {
      value: 'SS_2',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 40,
          force: true
        },
        max: {
          op: '<',
          value: 80,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: 2.5,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '>',
          value: 0
        },
        max: {
          op: '<=',
          value: 30
        }
      }, {
        attr: 'materialShrinkage',
        min: {
          op: '>=',
          value: 1.02
        }
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(allRelevant);
  }
  /**
   * returns the rulesets for PistonRing SIS (internal_slider)
   */
  defineRulesetsSIS() {
    // This d2 pre-check solves the problem of IGL-1314
    let defaultD2PrepareRule = {
      attr: 'd2',
      min: {
        op: '>=',
        value: 55,
        force: false
      },
      max: {
        op: '<=',
        value: 87,
        force: false
      }
    };
    if (this.isPmFastLine) {
      // FastLine is not available for these forms, so set a rule with impossible range
      // (instead of setting an empty route)
      const defaultD2PFastLineRule = {
        min: {
          op: '>=',
          value: 20,
          force: true
        },
        max: {
          op: '<',
          value: 10,
          force: true
        }
      };
      defaultD2PrepareRule = prepareJoinTwoDiameterMinMaxRules(defaultD2PrepareRule, defaultD2PFastLineRule);
    }
    const all = [{
      value: 'SIS_1',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 55,
          force: true
        },
        max: {
          op: '<',
          value: 80,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: 2.5,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 5
        },
        max: {
          op: '<=',
          value: 20
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '>',
          value: 0
        },
        max: {
          op: '<=',
          value: 30
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(allRelevant);
  }
  /**
   * returns the rulesets for PistonRing SAS (external_slider)
   */
  defineRulesetsSAS() {
    // This d2 pre-check solves the problem of IGL-1314
    let defaultD2PrepareRule = {
      attr: 'd2',
      min: {
        op: '>=',
        value: 4,
        force: false
      },
      max: {
        op: '<=',
        value: 45,
        force: false
      }
    };
    if (this.isPmFastLine) {
      // FastLine is not available for these forms, so set a rule with impossible range
      // (instead of setting an empty route)
      const defaultD2PFastLineRule = {
        min: {
          op: '>=',
          value: 20,
          force: true
        },
        max: {
          op: '<',
          value: 10,
          force: true
        }
      };
      defaultD2PrepareRule = prepareJoinTwoDiameterMinMaxRules(defaultD2PrepareRule, defaultD2PFastLineRule);
    }
    const all = [{
      value: 'SAS_1',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 4,
          force: true
        },
        max: {
          op: '<',
          value: 5,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: 1.5,
          force: true
        },
        max: {
          op: '<=',
          value: 2,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 3
        },
        max: {
          op: '<=',
          value: 15
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 2
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '>',
          value: 0
        },
        max: {
          op: '<=',
          value: 25
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }, {
      value: 'SAS_2',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 5,
          force: true
        },
        max: {
          op: '<=',
          value: 40,
          force: true
        }
      }, defaultD2PrepareRule, {
        attr: 'wt',
        min: {
          op: '>=',
          value: 1.5,
          force: true
        },
        max: {
          op: '<=',
          value: this.wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: 20
        }
      }, {
        attr: 'b2',
        min: {
          op: '>=',
          value: 1.5
        },
        max: {
          op: '<=',
          value: 3.5
        }
      }, {
        attr: 'slotAngle',
        min: {
          op: '>',
          value: 0
        },
        max: {
          op: '<=',
          value: 30
        }
      }, {
        attr: 'materialCode',
        op: '!in',
        values: ['E7', 'A160', 'UW160']
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(allRelevant);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    if (this.isPmFastLine) {
      return principalMoldNamesPRFastLine.indexOf(principalMoldName) >= 0;
    } else {
      return principalMoldNamesPR.indexOf(principalMoldName) >= 0;
    }
  }
  /**
   * Returns the principal mold name for PistonRing.
   */
  getPrincipalMoldName(rulesetValue, d2) {
    let principalMoldName;
    if (!rulesetValue) {
      return undefined;
    }
    if (!d2) {
      return undefined;
    }
    if (/^GS_/.exec(rulesetValue)) {
      if (6 <= d2 && d2 < 15) {
        principalMoldName = 'S25_PR_GS';
      } else if (15 <= d2 && d2 < 50) {
        principalMoldName = 'S50_PR_GS';
      } else if (50 <= d2 && d2 <= 87) {
        principalMoldName = 'S120_PR_GS';
      }
    } else if (/^SS_/.exec(rulesetValue)) {
      if (14 <= d2 && d2 < 38) {
        principalMoldName = 'AS40_PR_SS';
      } else if (38 <= d2 && d2 < 48) {
        principalMoldName = 'AS61_PR_SS';
      } else if (48 <= d2 && d2 < 63) {
        principalMoldName = 'S8267_PR_SS';
      } else if (63 <= d2 && d2 <= 87) {
        principalMoldName = 'AS120_PR_SS';
      }
    } else if (/^SIS_/.exec(rulesetValue)) {
      if (60 <= d2 && d2 < 80) {
        principalMoldName = 'S80_PR_SIS';
      } else if (80 <= d2 && d2 < 87) {
        principalMoldName = 'S120_PR_SIS';
      }
    } else if (/^SAS_/.exec(rulesetValue)) {
      if (7 <= d2 && d2 < 14) {
        principalMoldName = 'Clip1_PR_SAS';
      } else if (14 <= d2 && d2 < 25) {
        principalMoldName = 'Clip2_PR_SAS';
      } else if (25 <= d2 && d2 <= 45) {
        principalMoldName = 'Clip3_PR_SAS';
      }
    } else {
      return undefined;
    }
    if (!principalMoldName) {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIPistonRing = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIPistonRing);

class Rulebox3D extends Rulebox {}

let RuleboxMIKnifeEdgeRoller = class RuleboxMIKnifeEdgeRoller extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      inputOrder: 22
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addShape({
      name: 'RL',
      description: 'Knife Edge Roler',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Expand result of verify() to contain the principalMoldName
   */
  verify() {
    const v = super.verify();
    const principalMoldName = this.getPrincipalMoldName(v.rulesetValue, this.getNumberValue('d2'));
    return Object.assign({}, v, {
      principalMoldName
    });
  }
  /**
   * Convenient method to pass KnifeEdgeRollerDimensions directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      b1: userPart.b1
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for MCM
   */
  defineRulesets() {
    let wtMin = 1;
    let wtMin_RL3 = 1.5;
    //    const d2Max = this.isPmFastLine ? 36 : 48;
    const d2Max = 36;
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
      wtMin_RL3 = 2;
    }
    const allowedMaterials = this.isPmFastLine ? materialsRLFastLine : materialsRL;
    const all = [{
      value: 'RL_1',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 3.2,
          force: true
        },
        max: {
          op: '<',
          value: 13,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 3.2,
          force: false
        },
        max: {
          op: '<=',
          value: d2Max,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 4.5,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: 70
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'RL_2',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 13,
          force: true
        },
        max: {
          op: '<',
          value: 20,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 13,
          force: false
        },
        max: {
          op: '<=',
          value: d2Max,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: 5,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: 70
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }, {
      value: 'RL_3',
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: 20,
          force: true
        },
        max: {
          op: '<',
          value: 33,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: 20,
          force: false
        },
        max: {
          op: '<=',
          value: d2Max,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin_RL3,
          force: true
        },
        max: {
          op: '<=',
          value: 5,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: 4
        },
        max: {
          op: '<=',
          value: 70
        }
      }, {
        attr: 'materialCode',
        op: 'in',
        values: allowedMaterials
      }]
    }
    // {
    //   value: 'RL_4',
    //   rule: [
    //     ...prepareDiameterNoFlangeRules(
    //       { attr: 'd1', min: { op: '>=', value: 33, force: true }, max: { op: '<=', value: 45, force: false } },
    //       { attr: 'd2', min: { op: '>=', value: 33, force: false }, max: { op: '<=', value: d2Max, force: true } },
    //       { attr: 'wt', min: { op: '>=', value: 1.5, force: true }, max: { op: '<=', value: 5, force: true } },
    //       this.attributeAtVerify,
    //       this.getExpandedAllData()
    //     ),
    //
    //     { attr: 'b1', min: { op: '>=', value: 5 }, max: { op: '<=', value: 70 } },
    //     { attr: 'materialCode', op: 'in', values: allowedMaterials},
    //   ],
    // },
    ];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    this.logRulesets('RL allRelevant', allRelevant);
    return applyRulesetIds(allRelevant);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    if (this.isPmFastLine && principalMoldName === 'S60') {
      return false;
    }
    return principalMoldNamesKnifeEdgeRollers.indexOf(principalMoldName) >= 0;
  }
  /**
   * Returns the principal mold name for KnifeEdgeRollers.
   */
  getPrincipalMoldName(rulesetValue, d2) {
    let principalMoldName;
    if (!rulesetValue || !d2) {
      return undefined;
    }
    if (5.2 <= d2 && d2 < 15) {
      // FIXME: principalMoldName are a guess at the moment
      principalMoldName = 'S50';
    } else if (15 <= d2 && d2 < 22) {
      principalMoldName = 'S50';
    } else if (22 <= d2 && d2 <= 36) {
      principalMoldName = 'S50';
    } else if (36 <= d2 && d2 <= 48) {
      // d2 >=36 is not allowed at the moment
      // principalMoldName = 'S50';
      return undefined;
    } else {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIKnifeEdgeRoller = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIKnifeEdgeRoller);

let RuleboxMISleeveBearing = class RuleboxMISleeveBearing extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 3;
    } else {
      this.wtMin = 0.5;
      // changed regarding IGL-1939
      this.wtMax = 3;
    }
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'heatedMold',
      type: 'BOOLEAN'
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      visible: true,
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__13',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 13) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 13)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__45',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 45) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 45)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__51',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 51) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 51)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__80',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 80) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 80)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__90',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 90) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 90)
    });
    this.addAttribute({
      name: 'b1_max_from_d1__100',
      visible: false,
      type: 'DISTANCE',
      dependsOn: ['d1'],
      fnAsReadableString: 'min (d1 * 2.5, 100) ',
      fn: () => Math.min(this.getNumberValue('d1') * 2.5, 100)
    });
    this.addShape({
      name: 'S',
      description: 'Sleeve bearing default',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Convenient method to pass SleeveBearingParams directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      b1: userPart.b1
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      heatedMold: material.heatedMold,
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for SleeveBearing
   */
  defineRulesets() {
    let wtMin = this.wtMin;
    const wtMax = this.wtMax;
    const d1Min = this.isPmFastLine ? 3 : 1;
    const b1Min = 1;
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
    }
    const all = [{
      value: 'S_1',
      // S9B
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 6,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }]
    }, {
      value: 'S_2',
      // S9
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 6,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 6,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }]
    }, {
      value: 'S_3',
      // S17B
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__13'
        }
      }]
    }, {
      value: 'S_4',
      // S17
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__45'
        }
      }]
    }, {
      value: 'S_5',
      // S25
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 22,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 22,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__45'
        }
      }]
    }, {
      value: 'S_6',
      // S40
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 36,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 36,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__51'
        }
      }]
    }, {
      value: 'S_7',
      // S50
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__80'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'S_8',
      // S65
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 48,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 48,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__90'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'S_9',
      // S60
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 42,
          force: true
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 55,
          force: false
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__51'
        }
      }]
    }, {
      value: 'S_10',
      // S65
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 60,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 60,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }]
    }, {
      value: 'S_11',
      // S80
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 80,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 80,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }]
    }, {
      value: 'S_12',
      // S120
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'S_13',
      // S160
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'S_14',
      // S160
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 160,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 160,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          variable: 'b1_max_from_d1__100'
        }
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(this.isPmFastLine ? allRelevant.filter(r => ['S_4', 'S_5', 'S_6', 'S_9'].indexOf(r.value) >= 0) : all);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    if (this.isPmFastLine) {
      return principalMoldNameSFTFastLine.indexOf(principalMoldName) >= 0;
    } else {
      return principalMoldNamesSFT.indexOf(principalMoldName) >= 0;
    }
  }
  /**
   * get the default tolerances for this bearing
   */
  getDefaultTolerances() {
    const tol = {
      d1: undefined,
      d2: undefined,
      b1: undefined
    };
    if (this.isPmFastLine) {
      tol.d1 = 'E10';
      tol.b1 = 'h13';
    } else {
      tol.d1 = getToleranceClassByMaterial(this.getStringValue('materialCode'));
      tol.b1 = 'h13';
    }
    return tol;
  }
  /**
   * Returns the principal mold name for SleeveBearing.
   */
  getPrincipalMoldName(rulesetValue, _n) {
    let principalMoldName;
    if (!rulesetValue) {
      return undefined;
    }
    switch (rulesetValue) {
      case 'S_1':
        principalMoldName = 'S9B';
        break;
      case 'S_2':
        principalMoldName = 'S9';
        break;
      case 'S_3':
        principalMoldName = 'S17B';
        break;
      case 'S_4':
        principalMoldName = 'S17';
        break;
      case 'S_5':
        principalMoldName = 'S25';
        break;
      case 'S_6':
        principalMoldName = 'S40';
        break;
      case 'S_7':
        principalMoldName = 'S50';
        break;
      case 'S_8':
        principalMoldName = 'S65';
        break;
      case 'S_9':
        principalMoldName = 'S60';
        break;
      case 'S_10':
        principalMoldName = 'S65';
        break;
      case 'S_11':
        principalMoldName = 'S80';
        break;
      case 'S_12':
        principalMoldName = 'S122';
        break;
      case 'S_13':
        principalMoldName = 'S162';
        break;
      case 'S_14':
        principalMoldName = 'S162';
        break;
    }
    if (!principalMoldName) {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMISleeveBearing = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMISleeveBearing);

let RuleboxMIThrustWasher = class RuleboxMIThrustWasher extends RuleboxMI {
  constructor(unitSystem, isPmFastLine = false, isMaterialDeactivated = false, logger) {
    super(unitSystem, logger);
    this.isPmFastLine = isPmFastLine;
    if (isPmFastLine) {
      this.wtMin = 1;
      this.wtMax = 40; // IGL-2907
    } else {
      this.wtMin = 0.5;
      this.wtMax = 80; // just a big guess
    }
    this.isMaterialDeactivated = isMaterialDeactivated;
    this.addAttribute({
      name: 'd1',
      type: 'DISTANCE',
      inputOrder: 10
    });
    this.addAttribute({
      name: 'd2',
      type: 'DISTANCE',
      inputOrder: 11
    });
    this.addAttribute({
      name: 'b1',
      type: 'DISTANCE',
      inputOrder: 12
    });
    this.addAttribute({
      name: 'heatedMold',
      type: 'BOOLEAN',
      inputOrder: 2
    });
    this.addAttribute({
      name: 'materialCode',
      type: 'MATERIAL',
      inputOrder: 1
    });
    this.addAttribute({
      name: 'wt',
      visible: true,
      type: 'DISTANCE',
      dependsOn: ['d1', 'd2'],
      fnAsReadableString: '(d2 - d1) / 2',
      fn: () => round5((this.getNumberValue('d2') - this.getNumberValue('d1')) / 2)
    });
    this.addShape({
      name: 'slotAngle',
      description: 'Thrust washer default',
      rulesetsFn: this.defineRulesets.bind(this)
    });
  }
  /**
   * Convenient method to pass ThrustWasherParams  directly to set data.
   */
  setDataFromDimensions(userPart) {
    return this.setData({
      d1: userPart.d1,
      d2: userPart.d2,
      b1: userPart.b1
    });
  }
  /**
   * Convenient method to pass Material directly to set data.
   */
  setDataFromMaterial(material) {
    return this.setData({
      heatedMold: material.heatedMold,
      materialCode: material.materialCode
    });
  }
  /**
   * returns the rulesets for ThrustWasher
   */
  defineRulesets() {
    let wtMin = this.wtMin;
    const wtMax = this.wtMax;
    const d1Min = this.isPmFastLine ? 3 : 1;
    // change b1Min regarding IGL-315 from 0.5 to 0.75
    // changed b1Min (FL) regarding IGL-2906 from 0.75 to 1
    const b1Min = this.isPmFastLine ? 1 : 0.75;
    // adjustment according to IGL-823
    if (this.isPmFastLineAndLimitedWallthicknessMaterial()) {
      wtMin = 2;
    }
    const all = [{
      value: 'T_1',
      // S9B
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 8,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 8,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 3
        }
      }]
    }, {
      value: 'T_2',
      // S9
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 8,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 8,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 3
        }
      }]
    }, {
      value: 'T_3',
      // S17B
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 4
        }
      }]
    }, {
      value: 'T_4',
      // S17
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 15,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 15,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 4
        }
      }]
    }, {
      value: 'T_5',
      // S25
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 25,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 25,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 5
        }
      }]
    }, {
      value: 'T_6',
      // S40
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 40,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 40,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 5
        }
      }]
    }, {
      value: 'T_7',
      // S50
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 50,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 50,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 5
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'T_8',
      // S65
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 50,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 50,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 5
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'T_9',
      // S60
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 42,
          force: true
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 60,
          force: false
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }]
    }, {
      value: 'T_10',
      // S65
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 65,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 65,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }]
    }, {
      value: 'T_11',
      // S80
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 90,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 90,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }]
    }, {
      value: 'T_12',
      // S120
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: false
      }]
    }, {
      value: 'T_13',
      // S160
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 120,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 120,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }, {
        attr: 'heatedMold',
        op: '==',
        value: true
      }]
    }, {
      value: 'T_14',
      // S160
      rule: [...prepareDiameterNoFlangeRules({
        attr: 'd1',
        min: {
          op: '>=',
          value: d1Min,
          force: true
        },
        max: {
          op: '<=',
          value: 160,
          force: false
        }
      }, {
        attr: 'd2',
        min: {
          op: '>=',
          value: d1Min,
          force: false
        },
        max: {
          op: '<=',
          value: 160,
          force: true
        }
      }, {
        attr: 'wt',
        min: {
          op: '>=',
          value: wtMin,
          force: true
        },
        max: {
          op: '<=',
          value: wtMax,
          force: true
        }
      }, this.attributeAtVerify, this.getExpandedAllData()), {
        attr: 'b1',
        min: {
          op: '>=',
          value: b1Min
        },
        max: {
          op: '<=',
          value: this.isPmFastLine ? 3 : 6
        }
      }]
    }];
    const allRelevant = this.isMaterialDeactivated ? removeMaterialRulesFromRuleset(all) : all;
    return applyRulesetIds(this.isPmFastLine ? allRelevant.filter(r => ['T_4', 'T_5', 'T_6', 'T_9'].indexOf(r.value) >= 0) : all);
  }
  /**
   * Checks if a given principalMoldName is in the defined list of principalMoldNames.
   */
  isPrincipalMoldNameValid(principalMoldName) {
    if (this.isPmFastLine) {
      return principalMoldNameSFTFastLine.indexOf(principalMoldName) >= 0;
    } else {
      return principalMoldNamesSFT.indexOf(principalMoldName) >= 0;
    }
  }
  /**
   * Returns the principal mold name for ThrustWashers.
   */
  getPrincipalMoldName(rulesetValue, _n) {
    let principalMoldName;
    if (!rulesetValue) {
      return undefined;
    }
    switch (rulesetValue) {
      case 'T_1':
        principalMoldName = 'S9B';
        break;
      case 'T_2':
        principalMoldName = 'S9';
        break;
      case 'T_3':
        principalMoldName = 'S17B';
        break;
      case 'T_4':
        principalMoldName = 'S17';
        break;
      case 'T_5':
        principalMoldName = 'S25';
        break;
      case 'T_6':
        principalMoldName = 'S40';
        break;
      case 'T_7':
        principalMoldName = 'S50';
        break;
      case 'T_8':
        principalMoldName = 'S65';
        break;
      case 'T_9':
        principalMoldName = 'S60';
        break;
      case 'T_10':
        principalMoldName = 'S65';
        break;
      case 'T_11':
        principalMoldName = 'S80';
        break;
      case 'T_12':
        principalMoldName = 'S122';
        break;
      case 'T_13':
        principalMoldName = 'S162';
        break;
      case 'T_14':
        principalMoldName = 'S162';
        break;
    }
    if (!principalMoldName) {
      return undefined;
    }
    if (!this.isPrincipalMoldNameValid(principalMoldName)) {
      throw new Error('Invalid principal mold name : ' + principalMoldName);
    }
    return principalMoldName;
  }
};
RuleboxMIThrustWasher = __decorate([CleanCloneable(), __metadata("design:paramtypes", [Number, Object, Object, DefaultLogger])], RuleboxMIThrustWasher);

class RuleboxMIFactory {
  static getRulebox(formType, unitSystem, isPmFastLine) {
    if (!formType) {
      throw new Error('FormType is not defined!');
    }
    if (formTypeAsArray.findIndex(ft => ft === formType) === -1) {
      throw new Error('FormType "' + formType + '" is not valid');
    }
    switch (formType) {
      case 'RL':
        return new RuleboxMIKnifeEdgeRoller(unitSystem, isPmFastLine);
      case 'C':
        return new RuleboxMIMcm(unitSystem, isPmFastLine);
      case 'EC':
        return new RuleboxMIEasyClip(unitSystem, isPmFastLine);
      case 'PR':
        return new RuleboxMIPistonRing(unitSystem, isPmFastLine);
      case 'S':
        return new RuleboxMISleeveBearing(unitSystem, isPmFastLine);
      case 'F':
        return new RuleboxMIFlangeBearing(unitSystem, isPmFastLine);
      case 'T':
        return new RuleboxMIThrustWasher(unitSystem, isPmFastLine);
      default:
        throw new Error(`FormType "${formType}" is not yet supported by the Rulebox`);
    }
  }
}

/**
 * Returns the volume of the EasyClipParams independent from the unit-system in [cm^3].
 *
 * If the userPart uses metric distances the result is returned in [cm^3].
 * If the userPart uses imperial distances (in inch) the values are converted into mm and the result is also
 * returned in [cm^3].
 *
 * Background: In the most cases we need the volume only to calculate the mass. Therefore we need the density.
 *
 * M = V * D
 *
 * When we would change the volume to be in inch^3 we would also need to convert the density into g/inch^3.
 * So internally we calculate volumes always in cm^3 (and masses in g).
 */
const getEasyClipVolume = userPart => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  const userPartD2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d2);
  const userPartD3MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d3);
  const userPartB2MM = toMetricDistanceMilli(userPart.unitSystem, userPart.b2);
  const userPartB1MM = getEasyClipLength(userPart);
  const userPartBbMM = toMetricDistanceMilli(userPart.unitSystem, userPart.sb);
  const rb = new RuleboxMIEasyClip(userPart.unitSystem).setDataFromDimensions(userPart);
  const sl = rb.getValue('sl');
  const slMM = toMetricDistanceMilli(userPart.unitSystem, sl);
  const b5 = rb.getValue('b5');
  const b5MM = toMetricDistanceMilli(userPart.unitSystem, b5);
  const b4 = rb.getValue('b4');
  const b4MM = toMetricDistanceMilli(userPart.unitSystem, b4);
  /**
   * calculate the volume of the nose
   *
   * The "nose" is a triangle and an rectangle rotated around the axis.
   *
   *                                 |
   *                +                |
   *               /|                |
   *              / |                |
   *             /  |                |
   *            /   | y1             |
   *           /    |                |
   *          /     |                |
   *         /      |                |
   *        /       |                |
   *       +--------+                |
   *       |        | y2             |
   *       |        |                |
   *       |        |<---------------|
   *       |        |        r       |
   *       |        |                |
   *       +--------+                |
   *            h                    | rotation axis
   */
  const noseVolume = getRotatedRectangleVolume(EASY_CLIP_NOSE_HEIGHT, b5MM, userPartD2MM / 2) + getRotatedTriangleQ2Volume(EASY_CLIP_NOSE_HEIGHT, b4MM - b5MM, userPartD2MM / 2);
  const outerRingD = userPartD2MM + 2 * EASY_CLIP_NOSE_HEIGHT;
  const innerRingD = userPartD2MM;
  const innerRingR = innerRingD / 2;
  const noseRingArea = Math.PI / 4 * (outerRingD * outerRingD - innerRingD * innerRingD);
  const virtualLenght = noseVolume / noseRingArea;
  // calculate the area of the segment of the circle
  // see https://de.wikipedia.org/wiki/Kreissegment
  const h = EASY_CLIP_NOSE_HEIGHT;
  const aNoseFlattening = innerRingR * innerRingR * Math.acos(1 - h / innerRingR) - (innerRingR - h) * Math.sqrt(innerRingD * h - h * h);
  const removedNoseVolume = aNoseFlattening * virtualLenght;
  const resultingNoseVolume = noseVolume - 3 * removedNoseVolume;
  // console.log('\nnoseVolume : ', noseVolume, ' mm^3\n');
  // console.log('\nnoseRingArea : ', noseRingArea, ' mm^2\n');
  // console.log('virtualLenght : ', virtualLenght, ' mm');
  // console.log('innerRingD : ', innerRingD, ' mm');
  // console.log('nouterRingD : ', outerRingD, ' mm');
  //
  // console.log('h       : ', h, ' mm');
  // console.log('A       : ', A_nose_flattening, ' mm^2');
  // console.log('removedNoseVolume : ', removedNoseVolume, ' mm^3');
  // console.log('resultingNoseVolume : ', resultingNoseVolume, ' mm^3');
  /*
   * calculate the volume of the slot
   *
   * The slot in the EC is not from EndToEnd (related to sl) and it has a half-round end.
   * So there is a minimal difference.
   */
  const oneSlotVolume = getEndToEndSlotVolume(userPartD1MM, userPartD2MM, slMM, userPartBbMM);
  const twoSlotVolume = 2 * oneSlotVolume;
  /**
   * The complete volume on an EC is
   *
   *  the volume of the bearing
   *  + the volume of the flange
   *  + the volume of the nose
   *  - the volume of the two slots
   *
   * and the result has to be divided by 1000 to convert mm^3 into cm^3
   */
  return (volumeHollowCylinder(userPartD1MM, userPartD2MM, userPartB1MM) + volumeHollowCylinder(userPartD2MM, userPartD3MM, userPartB2MM) + resultingNoseVolume - twoSlotVolume) / 1000;
};

/**
 * Returns the length of a FSG Bearing (b1).
 */
const getFSGLength = userPart => {
  return userPart.b1;
};
/**
 * Returns the volume of a FSG Bearing, same as Flange bearing.
 */
const getFSGVolume = userPart => {
  return getFlangeBearingVolume(userPart);
};
/**
 * Returns the boundingBox of an FSG Bearing.
 */
const getFSGBoundingBox = (userPart, forceMetric = true) => {
  const userPartD3 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.d3) : userPart.d3;
  const userPartB1 = forceMetric ? toMetricDistanceMilli(userPart.unitSystem, userPart.b1) : userPart.b1;
  return {
    h: userPartB1,
    x: userPartD3,
    y: userPartD3
  };
};

/**
 * Check if the two given bearings have the same size.
 *
 * That means that the form is the same and diameter and length fields have the same values.
 * Other fields like partNo, materialCode (and the maybe upcoming tolerances) are *not* compared!
 */
const hasBearingTheSameSize = (userPart, catalogPart) => {
  if (userPart.form === 'S') {
    return hasSleeveBearingTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'T') {
    return hasThrustWasherTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'PR') {
    return hasPistonRingTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'EC') {
    return hasEasyClipTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'C') {
    return hasCTheSameSize(userPart, catalogPart);
  } else if (userPart.form === 'RL') {
    return hasKnifeEdgeRollerTheSameSize(userPart, catalogPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Check if the two given bearings have a similar size
 */
const hasBearingASimilarSize = (userPart, catalogPart) => {
  if (userPart.form === 'S') {
    return hasSleeveBearingASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'T') {
    return hasThrustWasherASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'PR') {
    return hasPistonRingASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'EC') {
    return hasEasyClipASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'C') {
    return hasCASimilarSize(userPart, catalogPart);
  } else if (userPart.form === 'RL') {
    return hasKnifeEdgeRollerASimilarSize(userPart, catalogPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Check if the catalogPart is smaller than the userPart
 */
const hasBearingASimilarSmallerSize = (userPart, catalogPart, dimension, customThresholdFactor) => {
  if (userPart.form === 'S') {
    return hasSleeveBearingASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'T') {
    return hasThrustWasherASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'PR') {
    return hasPistonRingASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'EC') {
    dimension = dimension === 'b1' ? 'b3' : dimension;
    return hasEasyClipASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'C') {
    if (dimension === 'b1') {
      userPart.b1 = Number(userPart.b2d3) + Number(userPart.b2d4) + Number(userPart.b3);
    }
    return hasCASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'RL') {
    return hasKnifeEdgeRollerASimilarSmallerSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Check if the catalogPart is greater than the userPart
 */
const hasBearingASimilarGreaterSize = (userPart, catalogPart, dimension, customThresholdFactor) => {
  if (userPart.form === 'S') {
    return hasSleeveBearingASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'T') {
    return hasThrustWasherASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'PR') {
    return hasPistonRingASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'EC') {
    return hasEasyClipASimilarGreaterSize(userPart, catalogPart, dimension === 'b1' ? 'b3' : dimension, customThresholdFactor);
  } else if (userPart.form === 'C') {
    return hasCASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else if (userPart.form === 'RL') {
    return hasKnifeEdgeRollerASimilarGreaterSize(userPart, catalogPart, dimension, customThresholdFactor);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Check if the catalogPart can be used to create a userPart with processing method mechanical-reprocessing (MNA)
 */
const hasBearingAGreaterSizeForMNA = (userPart, catalogPart) => {
  if (userPart.form === 'S') {
    return hasSleeveBearingAGreaterSizeForMNA(userPart, catalogPart);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingAGreaterSizeForMNA(userPart, catalogPart);
  } else if (userPart.form === 'T') {
    return hasThrustWasherAGreaterSizeForMNA(userPart, catalogPart);
  } else if (userPart.form === 'PR') {
    return hasPistonRingAGreaterSizeForMNA(userPart, catalogPart);
  } else if (userPart.form === 'EC') {
    throw new Error('Form EC is not allowed for mechanical-reprocessing!');
  } else if (userPart.form === 'C') {
    throw new Error('Form MCM is not allowed for mechanical-reprocessing!');
  } else if (userPart.form === 'RL') {
    return hasKnifeEdgeRollerAGreaterSizeForMNA(userPart, catalogPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Returns the wall thickness of a userPart in mm
 */
const getWallThickness = userPart => {
  if (userPart.form === 'S') {
    return getSleeveBearingWallThickness(userPart);
  } else if (userPart.form === 'F') {
    return getFlangeBearingWallThickness(userPart);
  } else if (userPart.form === 'T') {
    return getThrustWasherWallThickness(userPart);
  } else if (userPart.form === 'PR') {
    return getPistonRingWallThickness(userPart);
  } else if (userPart.form === 'EC') {
    return getEasyClipWallThickness(userPart);
  } else if (userPart.form === 'C') {
    return getCWallThickness(userPart);
  } else if (userPart.form === 'RL') {
    return getKnifeEdgeRollerWallThickness(userPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Returns flange thickness of a userPart in mm
 */
const getFlangeThickness = userPart => {
  if (userPart.form === 'C') {
    const FTTop = getCFlangeThicknessTop(userPart);
    const FTBottom = getCFlangeThicknessBottom(userPart);
    return FTTop > FTBottom ? FTBottom : FTTop;
  } else if (userPart.form === 'F') {
    return getFlangeBearingFlangeThickness(userPart);
  } else if (userPart.form === 'FL') {
    return getTwoHoleFlangeBearingFlangeThickness(userPart);
  } else if (userPart.form === 'VF') {
    return getVFFlangeThickness(userPart);
  } else if (userPart.form === 'YK') {
    return getSplitBearingAntiRotationFlangeThickness(userPart);
  } else if (userPart.form === 'EC') {
    return getEasyClipFlangeThickness(userPart);
  } else {
    return undefined;
  }
};
/**
 * Returns the length of a userPart in mm
 */
const getLength = userPart => {
  switch (userPart.form) {
    case 'S':
      return getSleeveBearingLength(userPart);
    case 'F':
      return getFlangeBearingLength(userPart);
    case 'T':
      return getThrustWasherLength(userPart);
    case 'PR':
      return getPistonRingLength(userPart);
    case 'EC':
      return getEasyClipLength(userPart);
    case 'C':
      return getCLength(userPart);
    case 'RL':
      return getKnifeEdgeRollerLength(userPart);
    case 'FL':
      return getTwoHoleFlangeBearingPreloadLength(userPart);
    case 'Y':
      return getSplitBearingLength(userPart);
    case 'VFL':
      return getTwoHoleFlangeBearingPreloadLength(userPart);
    case 'YK':
      return getSplitBearingAntiRotationLength(userPart);
    case 'TE':
      return getTeLength(userPart);
    case 'D':
      return getDLength(userPart);
    case 'VF':
      return getVFLength(userPart);
    case 'K':
      return getKLength(userPart);
    case 'VS':
      return getVSLength(userPart);
    case 'FSG':
      return getFSGLength(userPart);
    default:
      throw new Error(`Invalid form value ${userPart == null ? void 0 : userPart['form']}!`);
  }
};
/**
 * Returns the volume of the BearingParams in [cm^3]
 */
const getBearingVolume = userPart => {
  switch (userPart.form) {
    case 'S':
      return getSleeveBearingVolume(userPart);
    case 'F':
      return getFlangeBearingVolume(userPart);
    case 'T':
      return getThrustWasherVolume(userPart);
    case 'PR':
      return getPistonRingVolume(userPart);
    case 'EC':
      return getEasyClipVolume(userPart);
    case 'C':
      return getCVolume(userPart);
    case 'RL':
      return getKnifeEdgeRollerVolume(userPart);
    case 'FL':
      return getTwoHoleFlangeBearingVolume(userPart);
    case 'Y':
      // We accept a small margin of error by not calculating the volume of the cuts and treat it as a flange bearing
      return getFlangeBearingVolume(userPart);
    case 'VFL':
      return getTwoHoleFlangeBearingVolume(userPart);
    case 'YK':
      // We accept a small margin of error by not calculating the volume of the cuts and treat it as a flange bearing
      return getFlangeBearingVolume(userPart);
    case 'TE':
      return getTeVolume(userPart);
    case 'D':
      return getDVolume(userPart);
    case 'VF':
      return getVFVolume(userPart);
    case 'K':
      return getKVolume(userPart);
    case 'VS':
      return getVSVolume(userPart);
    case 'FSG':
      return getFSGVolume(userPart);
    default:
      throw new Error(`Invalid form value ${userPart == null ? void 0 : userPart['form']}!`);
  }
};
/**
 * Returns the BoundingBox of the BearingParams.
 * All returned values are in mm.
 */
const getBearingBoundingBox = (userPart, forceMetric = true) => {
  switch (userPart.form) {
    case 'S':
      return getSleeveBearingBoundingBox(userPart, forceMetric);
    case 'F':
      return getFlangeBearingBoundingBox(userPart, forceMetric);
    case 'T':
      return getThrustWasherBoundingBox(userPart, forceMetric);
    case 'PR':
      return getPistonRingBoundingBox(userPart, forceMetric);
    case 'EC':
      return getEasyClipBoundingBox(userPart, forceMetric);
    case 'C':
      return getCBoundingBox(userPart, forceMetric);
    case 'RL':
      return getKnifeEdgeRollerBoundingBox(userPart, forceMetric);
    case 'FL':
      return getTwoHoleFlangeBearingBoundingBox(userPart, forceMetric);
    case 'Y':
      return getFlangeBearingBoundingBox(userPart, forceMetric);
    case 'VFL':
      return getTwoHoleFlangeBearingBoundingBox(userPart, forceMetric);
    case 'YK':
      return getFlangeBearingBoundingBox(userPart, forceMetric);
    case 'TE':
      return getTeBoundingBox(userPart, forceMetric);
    case 'D':
      return getDBoundingBox(userPart, forceMetric);
    case 'VF':
      return getVFBoundingBox(userPart, forceMetric);
    case 'K':
      return getKBoundingBox(userPart, forceMetric);
    case 'VS':
      return getVSBoundingBox(userPart, forceMetric);
    case 'FSG':
      return getFSGBoundingBox(userPart, forceMetric);
    default:
      throw new Error(`Invalid form value ${userPart == null ? void 0 : userPart['form']}!`);
  }
};
/**
 * Returns the projected area of the BearingParams
 *
 * in [mm^2] when selectedUnitSystem is metric [default]
 *
 * and
 *
 * in [inch^2] when selectedUnitSystem is imperial
 *
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_5_2.xlsm
 * Page       : Ausgabe Artikeldaten
 * Field      : J10
 */
const getProjectedArea = userPart => {
  if (userPart.form === 'S') {
    return getSleeveBearingProjectionArea(userPart);
  } else if (userPart.form === 'F') {
    return getFlangeBearingProjectionArea(userPart);
  } else if (userPart.form === 'T') {
    return getThrustWasherProjectionArea(userPart);
  } else if (userPart.form === 'PR') {
    return getPistonRingProjectionArea(userPart);
  } else if (userPart.form === 'EC') {
    return getEasyClipProjectionArea(userPart);
  } else if (userPart.form === 'C') {
    return getCProjectionArea(userPart);
  } else if (userPart.form === 'RL') {
    return getKnifeEdgeRollerProjectionArea(userPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Returns the Mass of the BearingParams in [g].
 *
 * beringVolume [cm^3]
 * density      [g/cm^3]
 * =>           [g]
 *
 * Note:
 * Mass is the right term to be used.
 * Weight (DE: Gewicht) is the shortform of weightforce (DE: Gewichtskraft)
 */
const getBearingMass = (userPart, density) => {
  return getBearingVolume(userPart) * density;
};
/**
 * Returns a fallback partNo for configurations not existing in the catalog.
 */
const getFallbackPartNo = (config, processingMethod = processingMethods.MOLD_INJECTION) => {
  if (isInjectionMoldParams(config)) {
    return MI_FALLBACK_PART_NO;
  }
  switch (processingMethod) {
    case processingMethods.SEMI_FINISHED:
      return SRF_FALLBACK_PART_NO;
    case processingMethods.MECHANICAL_REPROCESSING:
      return MNA_FALLBACK_PART_NO;
    case processingMethods.THREE_DIMENSIONAL_PRINT:
      return TD_FALLBACK_PART_NO;
    case processingMethods.MOLD_INJECTION:
    case processingMethods.FAST_LINE:
    default:
      return MI_FALLBACK_PART_NO;
  }
};
/**
 * Expands a bearing about the shrinking factor so that the bearing has the required size after it is cooled down
 * after molding.
 */
const getExpandedBearing = (userPart, shrinkage) => {
  if (userPart.form === 'S') {
    return getExpandedSleeveBearing(userPart, shrinkage);
  } else if (userPart.form === 'F') {
    return getExpandedFlangeBearing(userPart, shrinkage);
  } else if (userPart.form === 'T') {
    return getExpandedThrustWasher(userPart, shrinkage);
  } else if (userPart.form === 'PR') {
    return getExpandedPistonRing(userPart, shrinkage);
  } else if (userPart.form === 'EC') {
    return getExpandedEasyClip(userPart, shrinkage);
  } else if (userPart.form === 'C') {
    return getExpandedC(userPart, shrinkage);
  } else if (userPart.form === 'RL') {
    return getExpandedKnifeEdgeRoller(userPart, shrinkage);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Convert the bearing parameters into a string with units.
 */
const bearingToString = userPart => {
  if (userPart.form === 'S') {
    return sleeveBearingToString(userPart);
  } else if (userPart.form === 'F') {
    return flangeBearingToString(userPart);
  } else if (userPart.form === 'T') {
    return thrustWasherToString(userPart);
  } else if (userPart.form === 'PR') {
    return pistonRingToString(userPart);
  } else if (userPart.form === 'EC') {
    return easyClipToString(userPart);
  } else if (userPart.form === 'C') {
    return CToString(userPart);
  } else if (userPart.form === 'RL') {
    return knifeEdgeRollerToString(userPart);
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Check if the two given bearings have the same bevels.
 */
const hasBearingTheSameBevels = (userPart, catalogPart) => {
  // FIXME : use switch
  if (userPart.form === 'S') {
    return hasSleeveBearingTheSameBevels(userPart, catalogPart);
  } else if (userPart.form === 'F') {
    return hasFlangeBearingTheSameBevels(userPart, catalogPart);
  } else if (userPart.form === 'T') {
    // T has (atm) no bevels
    return true;
  } else if (userPart.form === 'PR') {
    return true;
  } else if (userPart.form === 'EC') {
    return true;
  } else if (userPart.form === 'C') {
    return true;
  } else if (userPart.form === 'RL') {
    return true;
  } else {
    throw new Error(`Invalid form value ${userPart.form}!`);
  }
};
/**
 * Returns the angle range for a bevel.
 *
 * Only possible for S and F otherwise undefined is returned
 */
const getBevelAngleRange = (userPart, forBevel) => {
  if (userPart.form === 'S') {
    return getSleeveBearingBevelAngleRange(userPart, forBevel);
  } else if (userPart.form === 'F') {
    return getFlangeBearingBevelAngleRange(userPart, forBevel);
  } else {
    return undefined;
  }
};
/**
 * Returns the length range for a bevel.
 *
 * Only possible for S and F otherwise undefined is returned
 */
const getBevelLengthRange = (userPart, forBevel) => {
  if (userPart.form === 'S') {
    return getSleeveBearingBevelLengthRange(userPart, forBevel);
  } else if (userPart.form === 'F') {
    return getFlangeBearingBevelLengthRange(userPart, forBevel);
  } else {
    return undefined;
  }
};

/**
 * Returns the part from the catalog that has the same size and the same material.
 *
 * If the tolerances are set in the userPart, then they are checked against the default values.
 */
const findPartFromCatalog = (userPart, catalogParts) => {
  return catalogParts.find(catalogPart => {
    return catalogPart.form === userPart.form && catalogPart.materialCode === userPart.materialCode && catalogPart.unitSystem === userPart.unitSystem && hasBearingTheSameSize(userPart, catalogPart) && hasBearingTheSameTolerances(userPart, catalogPart) && hasBearingTheSameBevels(userPart, catalogPart);
  });
};
/**
 * Check if a given part is already in the catalog.
 * This includes that the materials are the same!
 *
 * If the tolerances are set in the userPart, then they are checked against the default values.
 */
const isPartInCatalog = (userPart, catalogParts) => {
  return !!findPartFromCatalog(userPart, catalogParts);
};
/**
 * Check if a given part is already in the catalog and return its PartNo.
 * If the part is not found the return value is undefined.
 */
const getPartNoFromCatalog = (userPart, catalogParts) => {
  const foundParts = findPartFromCatalog(userPart, catalogParts);
  if (foundParts) {
    return foundParts.partNo;
  } else {
    return undefined;
  }
};
/**
 * Returns the parts from the catalog that have the same size and
 * also the same tolerances (if set)
 * but a different material.
 */
const getPartsFromCatalogWithDifferentMaterial = (userPart, catalogParts) => {
  return catalogParts.filter(catalogPart => {
    return catalogPart.form === userPart.form && catalogPart.materialCode !== userPart.materialCode && catalogPart.unitSystem === userPart.unitSystem && hasBearingTheSameSize(userPart, catalogPart) && hasBearingTheSameTolerances(userPart, catalogPart);
  });
};
/**
 * Check if a given part is already in the catalog, but with a different material.
 */
const isPartInCatalogWithDifferentMaterial = (userPart, catalogParts) => {
  return getPartsFromCatalogWithDifferentMaterial(userPart, catalogParts).length > 0;
};
/**
 * Returns the parts from the catalog that have a similar size, ignoring the material and ignoring the tolerances
 * Result can have zero entries, one entry or more entries.
 */
const getSimilarPartsFromCatalog = (userPart, catalogParts) => {
  return catalogParts.filter(catalogPart => {
    return catalogPart.form === userPart.form && hasBearingASimilarSize(userPart, catalogPart);
  });
};
/**
 * Returns the parts from the catalog that have a smaller similar size with the same material and same unit system
 * Result can have zero to three entries
 */
const getSimilarSmallerPartsFromCatalog = (userPart, catalogParts, customThresholdFactor) => {
  const smallerParts = new Map();
  catalogParts.filter(catalogPart => {
    return catalogPart.form === userPart.form && (userPart.materialCode && catalogPart.materialCode === userPart.materialCode || !userPart.materialCode) && catalogPart.unitSystem === userPart.unitSystem;
  }).forEach(catalogPart => {
    if (hasBearingASimilarSmallerSize(userPart, catalogPart, 'd1', customThresholdFactor)) {
      smallerParts.set('d1', catalogPart);
    } else if (hasBearingASimilarSmallerSize(userPart, catalogPart, 'd2', customThresholdFactor)) {
      smallerParts.set('d2', catalogPart);
    } else if (hasBearingASimilarSmallerSize(userPart, catalogPart, 'b1', customThresholdFactor)) {
      smallerParts.set('b1', catalogPart);
    }
  });
  return Array.from(smallerParts.values());
};
/**
 * Returns the parts from the catalog that have a similar greater size with the same material and same unit system
 * Result can have zero to three entries.
 */
const getSimilarGreaterPartsFromCatalog = (userPart, catalogParts, customThresholdFactor) => {
  const greaterParts = new Map();
  catalogParts.filter(catalogPart => {
    return catalogPart.form === userPart.form && (userPart.materialCode && catalogPart.materialCode === userPart.materialCode || !userPart.materialCode) && catalogPart.unitSystem === userPart.unitSystem;
  }).forEach(catalogPart => {
    if (!greaterParts.has('d1') && hasBearingASimilarGreaterSize(userPart, catalogPart, 'd1', customThresholdFactor)) {
      greaterParts.set('d1', catalogPart);
    } else if (!greaterParts.has('d2') && hasBearingASimilarGreaterSize(userPart, catalogPart, 'd2', customThresholdFactor)) {
      greaterParts.set('d2', catalogPart);
    } else if (!greaterParts.has('b1') && hasBearingASimilarGreaterSize(userPart, catalogPart, 'b1', customThresholdFactor)) {
      greaterParts.set('b1', catalogPart);
    }
  });
  return Array.from(greaterParts.values());
};
/**
 * Returns the parts from the catalog that have a greater similar size with the same material and same unit system
 * that can be used for MNA (Mechanical Reprocessing).
 *
 * Result can have zero, one entry or more entries.
 */
const getSimilarGreaterPartsFromCatalogForMNA = (userPart, catalogParts) => {
  return catalogParts.filter(catalogPart => {
    if (userPart.form === 'PR') {
      return (
        // PistonRing parts are only created from SleeveBearings
        catalogPart.form === 'S' && catalogPart.materialCode === userPart.materialCode && catalogPart.unitSystem === userPart.unitSystem && hasBearingAGreaterSizeForMNA(userPart, catalogPart) && hasBearingTheSameTolerances(userPart, catalogPart)
      );
    } else {
      return catalogPart.form === userPart.form && catalogPart.materialCode === userPart.materialCode && catalogPart.unitSystem === userPart.unitSystem && hasBearingAGreaterSizeForMNA(userPart, catalogPart) && hasBearingTheSameTolerances(userPart, catalogPart);
    }
  });
};
/***
 * Returns zero to six catalog parts that are similar to the userPart.
 */
const getSimilarPartsSuggestion = (userPart, catalogParts, customThresholdFactor) => {
  const smallerParts = getSimilarSmallerPartsFromCatalog(userPart, catalogParts, customThresholdFactor);
  const greaterParts = getSimilarGreaterPartsFromCatalog(userPart, catalogParts, customThresholdFactor);
  return [...smallerParts, ...greaterParts].sort((a, b) => a.partNo < b.partNo ? -1 : 1);
};

const partNoIsSleeveBearing = partNo => {
  return !!/[^V]S[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsFlangeBearing = partNo => {
  return !!/[^V]F[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsThrustWasher = partNo => {
  return !!/T[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsPistonRing = partNo => {
  return !!/PR[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsEasyClip = partNo => {
  return !!/EC[MI]-/.exec(partNo);
};
/**
 
 * This is a problem because you cannot distinguish a C (MCM) part in material E
 * (which would be ECM) from a metric Easyclip (which would also be ECM)
 */
const partNoIsC = partNo => {
  return !!/C[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsKnifeEdgeRoller = partNo => {
  return !!/RL[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsTwoHoleFlangeBearing = partNo => {
  return !!/[^V]FL-/.exec(partNo);
};
/**
 
 */
const partNoIsTwoHoleFlangeBearingPreload = partNo => {
  return !!/VFL-/.exec(partNo);
};
/**
 
 */
const partNoIsD = partNo => {
  return !!/D[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsK = partNo => {
  return !!/K[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsY = partNo => {
  return !!/Y[MI]-.*[^K]$/.exec(partNo);
};
/**
 
 */
const partNoIsYK = partNo => {
  return !!/Y[MI]-.*K$/.exec(partNo);
};
/**
 
 */
const partNoIsVK = partNo => {
  return !!/VF[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsVS = partNo => {
  return !!/VS[MI]-/.exec(partNo);
};
/**
 
 */
const partNoIsTE = partNo => {
  return !!/TE[MI]-/.exec(partNo);
};
const getIbdShapeFromPartNo = partNo => {
  let form = undefined;
  if (partNoIsSleeveBearing(partNo)) {
    form = 'S';
  } else if (partNoIsFlangeBearing(partNo)) {
    form = 'F';
  } else if (partNoIsThrustWasher(partNo)) {
    form = 'T';
  } else if (partNoIsPistonRing(partNo)) {
    form = 'PR';
  } else if (partNoIsEasyClip(partNo)) {
    form = 'EC';
  } else if (partNoIsC(partNo)) {
    form = 'C';
  } else if (partNoIsKnifeEdgeRoller(partNo)) {
    form = 'RL';
  } else {
    throw new Error(`Partno ${partNo} does not match an IBD shape.`);
  }
  return form;
};
const getShapeFromPartNo = partNo => {
  let form = undefined;
  if (partNoIsSleeveBearing(partNo)) {
    form = 'S';
  } else if (partNoIsFlangeBearing(partNo)) {
    form = 'F';
  } else if (partNoIsThrustWasher(partNo)) {
    form = 'T';
  } else if (partNoIsPistonRing(partNo)) {
    form = 'PR';
  } else if (partNoIsEasyClip(partNo)) {
    form = 'EC';
  } else if (partNoIsC(partNo)) {
    form = 'C';
  } else if (partNoIsKnifeEdgeRoller(partNo)) {
    form = 'RL';
  } else if (partNoIsTwoHoleFlangeBearing(partNo)) {
    form = 'FL';
  } else if (partNoIsTwoHoleFlangeBearingPreload(partNo)) {
    form = 'VFL';
  } else if (partNoIsD(partNo)) {
    form = 'D';
  } else if (partNoIsK(partNo)) {
    form = 'K';
  } else if (partNoIsY(partNo)) {
    form = 'Y';
  } else if (partNoIsYK(partNo)) {
    form = 'YK';
  } else if (partNoIsVK(partNo)) {
    form = 'VF';
  } else if (partNoIsVS(partNo)) {
    form = 'VS';
  } else if (partNoIsTE(partNo)) {
    form = 'TE';
  } else {
    throw new Error(`Partno ${partNo} does not match an IBH shape.`);
  }
  return form;
};

/**
 * Returns the volume of the sprue depending on the principalMoldName and the cavityCount in [cm^3].
 *
 * It is the volume for all sprue-channels in the mold.
 *
 * ```
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_3_1.xlsm
 * Page       : mould cost
 * Fields     : B152 - H165
 * ```
 *
 * The values 8055, 7727 und 7741 were changed regarding IBD-421
 */
const getSprueVolumePerMold = (principalMoldName, cavityCount) => {
  const i = cavityCountToArrayIndex(cavityCount);
  switch (principalMoldName) {
    case 'S9B':
      return [1, 1, 1][i];
    case 'S9':
      return [1, 0.85, 0.85, 1.1, 1.49][i];
    case 'S17B':
      return [1, 1, 1][i];
    case 'S17':
      return [0.517, 0.55, 1.11, 1.66, 2.18][i];
    case 'S25':
    case 'S25_PR_GS':
      return [0.712, 0.733, 1.93, 3.1][i];
    case 'S40':
      return [1.514, 1.54, 3.51][i];
    case 'S50':
    case 'S50_PR_GS':
      return [2.7, 3.6, 7, 9][i];
    case 'S60':
    case 'AS61_PR_SS':
      return [2][i];
    case 'S65':
    case 'S8267_PR_SS':
      return [8.54, 8.68, 8.96][i];
    case 'S80':
    case 'S80_PR_SIS':
      return [8.72, 9.5, 10][i];
    case 'S120_PR_GS':
    case 'S120_PR_SIS':
    case 'AS120':
    case 'AS40_PR_SS':
    case 'AS120_PR_SS':
    case 'S225':
      return [5.52, 8][i];
    case 'Clip1':
    case 'Clip1_PR_SAS':
    case 'Clip1_EC':
      return [0.75, 0.75, 1][i];
    case 'S122':
    case 'S162':
      return [5.52][i];
    case 'Clip2':
    case 'Clip2_PR_SAS':
    case 'Clip2_EC':
      return [0.5, 0.5, 1][i];
    case 'Clip3':
    case 'Clip3_PR_SAS':
    case 'Clip3_EC':
      return [2.5, 2.5][i];
    case '2134':
      return [1.5, 2, 3][i];
    case '8003':
      return [1, 1.5, 3.5][i];
    case '8008':
      return [1][i];
    case '8035':
      return [1, 1, 3][i];
    case '8062':
      return [3, 3, 4][i];
    case '8500':
      return [2.5, 2.5][i];
    case 'AS17':
      return [1, 1.5, 3][i];
    case 'AS61':
      return [2, 5, 5][i];
    case '8055':
    case '7727':
    case '7741':
      return [3][i];
    default:
      throw new Error(`principalMoldName ${principalMoldName} not supported in getSprueVolumePerMold`);
  }
};
/**
 * Returns the Mass of the sprue for one part.
 *
 * M = V * D / cavityCount
 *
 * Sprue has to be calculated per part.
 */
const getSprueMass = (principalMoldName, cavityCount, density) => {
  const volume = getSprueVolumePerMold(principalMoldName, cavityCount);
  if (typeof volume === 'undefined') {
    throw Error(`Sprune volume of mold ${principalMoldName} and for cavity count ${cavityCount} is undefined`);
  }
  return volume * density / cavityCount;
};

/**
 * Calculates how many cavities are useful for a given cavityIndex (of the material), the principalMold and the
 * requested part quantity. Below a quantity of 5000 pieces the cavity count is always 1.
 *
 * ```
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_6_0.xlsm
 * Page       : mould costs
 * Fields     : B214 - G250
 * ```
 *
 * The array contains (the zero-indexed) values for the six different cavityIndex-values.
 * cavityQuantity = [2, 2, 1, 1, 1, 1][cavityIndex - 1];
 */
const getQuantityOfCavities = (principalMoldName, cavityIndex, partsQuantity) => {
  let cavityQuantity = 1;
  if (partsQuantity < 1) {
    throw new Error('partsQuantity must be at least 1');
  }
  if ([1, 2, 3, 4, 5, 6].indexOf(cavityIndex) === -1) {
    throw new Error('cavityIndex must be 1,2,3,4,5 or 6');
  }
  // IBD-69: below 5000 the cavity count is always 1
  if (partsQuantity < 5000) {
    return 1;
  }
  switch (principalMoldName) {
    // added related to IBD-116
    case 'S9B':
    case 'S17B':
      if (partsQuantity < 20000) {
        cavityQuantity = [2, 2, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [4, 4, 2, 2, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [4, 4, 4, 2, 2, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [4, 4, 4, 2, 2, 1][cavityIndex - 1];
      }
      break;
    case 'S9':
    case 'S17':
      if (partsQuantity < 20000) {
        cavityQuantity = [2, 2, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [4, 4, 2, 2, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [6, 6, 4, 2, 2, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [8, 8, 4, 2, 2, 1][cavityIndex - 1];
      }
      break;
    case 'S25':
      if (partsQuantity < 20000) {
        cavityQuantity = [2, 2, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [4, 4, 2, 2, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [6, 6, 4, 2, 2, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [6, 6, 4, 2, 2, 1][cavityIndex - 1];
      }
      break;
    case 'S25_PR_GS':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S40':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [2, 2, 2, 2, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [4, 2, 2, 2, 2, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [4, 2, 2, 2, 2, 1][cavityIndex - 1];
      }
      break;
    case 'S50':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [2, 2, 2, 2, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [4, 2, 2, 2, 2, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [6, 4, 2, 2, 2, 1][cavityIndex - 1];
      }
      break;
    case 'S50_PR_GS':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S60':
    case 'AS61_PR_SS':
      // Added values from a talk with Lars Butenschön 2018.10.02
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S65':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [2, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S8267_PR_SS':
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S80':
    case 'S80_PR_SIS':
    case 'S122':
    case 'S120_PR_GS':
    case 'S120_PR_SIS':
    case 'AS40_PR_SS':
    case 'AS120':
    case 'AS120_PR_SS':
    case 'S162':
      // Added values from a talk with Lars Butenschön 2018.10.02
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'Clip1':
    case 'Clip1_PR_SAS':
    case 'Clip1_EC':
    case 'Clip2':
    case 'Clip2_PR_SAS':
    case 'Clip2_EC':
    case 'Clip3':
    case 'Clip3_PR_SAS':
    case 'Clip3_EC':
      // changed by IBD-378
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    case 'S225':
    case '2134':
    case '8003':
    case '8008':
    case '8035':
    case '8062':
    case 'AS17':
    case 'AS61':
    case '7727':
    case '8055':
    case '8500':
    case '7741':
      // changed by IBD-421
      if (partsQuantity < 20000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity < 50000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else if (partsQuantity <= 100000) {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      } else {
        cavityQuantity = [1, 1, 1, 1, 1, 1][cavityIndex - 1];
      }
      break;
    default:
      throw new Error(`principalMoldName ${principalMoldName} not supported in getQuantityOfCavities`);
  }
  return cavityQuantity;
};
/**
 * Returns the possible cavities for the given principalMoldName.
 */
const getPossibleCavityQuantities = principalMoldName => {
  const list = [];
  possibleCavityQuantities.forEach(cavity => {
    if (typeof getSprueVolumePerMold(principalMoldName, cavity) !== 'undefined') {
      list.push(cavity);
    }
  });
  return list;
};

/**
 * Returns the cycleTime time depending on the principalMoldName and the userPart.
 *
 * ```
 * Reference  : /documentation/kalktooldoku_3.pdf
 * Page       : Ermittlung Zykluszeit Z
 * ```
 * and
 * ```
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_8_6.xls
 * Page       : mould costs
 * Range      : B185:H211
 * ```
 *
 * The calculation follows the diagrams from Lars Butenschön. In the diagram the cycleTime times
 * are in minutes. In a talk Lars suggested to take seconds instead of minutes.
 *
 * In the flowchart kalktooldoku_3.pdf there are SNr values to determine the cycle time. These values
 * come from a table
 * in       /documentation/iglidur Spritzgusskalkulation_1_8_6.xls
 * on page  mould costs
 * range    B185:H211
 *
 * The result is returned in [s] (seconds).
 *
 * Regarding to IBD-378 and IBD-384 Lars Butenschön explained that these PrincipalMolds all have an SNr > 7
 * so the CycleTime is 0.6 min => 36s
 */
const getCycleTime = (principalMoldName, userPart) => {
  const userPartD1MM = toMetricDistanceMilli(userPart.unitSystem, userPart.d1);
  switch (principalMoldName) {
    case 'S9B':
    case 'S17B':
      return 10;
    case 'S9':
    case 'S17':
    case 'S25':
    case 'S25_PR_GS':
    case 'S40':
    case 'S50':
    case 'S50_PR_GS':
      {
        const wallThickness = getWallThickness(userPart);
        const x = userPartD1MM < wallThickness ? userPartD1MM : wallThickness;
        if (x < 1) {
          return 15;
        } else if (x <= 1.5) {
          return 20;
        } else if (x <= 2) {
          return 24;
        } else if (x <= 3) {
          return 27;
        } else {
          // x > 3
          return 30;
        }
      }
    case 'S60':
    case 'AS40_PR_SS':
    case 'AS61_PR_SS':
    case 'S65':
    case 'S8267_PR_SS':
    case 'S80':
    case 'S80_PR_SIS':
    case 'AS120':
    case 'AS120_PR_SS':
    case 'S122':
    case 'S120_PR_GS':
    case 'S120_PR_SIS':
    case 'S162':
      return 36;
    case 'Clip1':
    case 'Clip1_PR_SAS':
    case 'Clip1_EC':
    case 'Clip2':
    case 'Clip2_PR_SAS':
    case 'Clip2_EC':
    case 'Clip3':
    case 'Clip3_PR_SAS':
    case 'Clip3_EC':
      return 36;
    case 'S225':
    case '2134':
    case '8003':
    case '8008':
    case '8035':
    case '8055':
    case '8062':
    case '8500':
    case 'AS17':
    case 'AS61':
    case '7727':
    case '7741':
      return 36;
    default:
      throw new Error(`Invalid principalMoldName "${principalMoldName}"!`);
  }
};

/**
 * Returns the machineType number depending on the principalMoldName.
 *
 * ```
 * Reference  : /documentation/iglidur Spritzgusskalkulation_1_3_1.xlsm
 * Page       : mould cost
 * Fields     : I153 - I165
 * ```
 */
const getMachineTypeNo = principalMoldName => {
  switch (principalMoldName) {
    case 'S9B':
    case 'S9':
    case 'S17B':
    case 'S17':
    case 'S25':
    case 'S25_PR_GS':
    case 'S60':
    case 'AS40_PR_SS':
    case 'AS61_PR_SS':
      return 1;
    case 'S40':
    case 'S50':
    case 'S50_PR_GS':
    case 'Clip1':
    case 'Clip1_PR_SAS':
    case 'Clip1_EC':
    case 'Clip2':
    case 'Clip2_PR_SAS':
    case 'Clip2_EC':
    case '2134':
    case '8003':
    case '8008':
    case '8035':
    case '8062':
    case 'AS17':
    case 'AS61':
    case '7727':
      return 2;
    case 'S65':
    case 'S8267_PR_SS':
    case 'S80':
    case 'S80_PR_SIS':
    case 'S122':
    case 'S120_PR_GS':
    case 'S120_PR_SIS':
    case 'AS120':
    case 'AS120_PR_SS':
    case 'S162':
    case 'S225':
    case 'Clip3':
    case 'Clip3_PR_SAS':
    case 'Clip3_EC':
    case '8055':
    case '8500':
    case '7741':
      return 3;
    default:
      throw new Error(`principalMoldName ${principalMoldName} not supported in getSprueVolumePerMold`);
  }
};
/**
 * Calculate machine output per hour.
 *
 * cycleTime has to be passed in seconds.
 * Return value is in hours.
 */
const calculateOutputPerHour = (cavityCount, cycleTime) => {
  return cavityCount / cycleTime * 60 * 60;
};
/**
 * Calculate the time to produce the parts (on one machine).
 *
 * cycleTime has to be passed in seconds.
 * Return value is in hours.
 */
const calculateProductionTimeInHours = (partsQuantity, cavityCount, cycleTime) => {
  return partsQuantity / cavityCount * cycleTime / 60 / 60;
};

/**
 * Return the PrincipalMoldName and the CavityCount for the given BearingParams.
 */
const getMoldDetailsForBearingParams = (config, material, quantity, processingMethod) => {
  let principalMoldName;
  let cavityCount;
  const userPart = config;
  const isPmFastLine = processingMethod === processingMethods.FAST_LINE;
  if (typeof userPart.principalMoldName === 'undefined') {
    const rulebox = RuleboxMIFactory.getRulebox(config.form, config.unitSystem, isPmFastLine);
    rulebox.setDataFromDimensions(userPart);
    rulebox.setDataFromMaterial(material);
    const verifyResult = rulebox.verify();
    if (verifyResult) {
      if (verifyResult.allRulesApply) {
        principalMoldName = verifyResult.principalMoldName;
      } else {
        throw new Error('Some rules fail for this rulebox.\nverifyResult =\n' + inspect(verifyResult, {
          maxStringLength: 120
        }));
      }
    } else {
      throw new Error('Invalid configuration for rulebox.');
    }
  } else {
    principalMoldName = userPart.principalMoldName;
  }
  if (typeof principalMoldName === 'undefined') {
    throw new Error('undefined principalMoldName not supported in getMoldDetailsForBearingParams');
  }
  // changed with IGL-1376
  const isSFT = userPart.form === 'S' || userPart.form === 'F' || userPart.form === 'T';
  if (isPmFastLine && !isSFT) {
    cavityCount = 1;
  } else {
    if (typeof userPart.cavityCount === 'undefined') {
      cavityCount = getQuantityOfCavities(principalMoldName, material.cavityIndex, quantity);
    } else {
      const possibleCavityCounts = getPossibleCavityQuantities(principalMoldName);
      if (possibleCavityCounts.indexOf(userPart.cavityCount) === -1) {
        // the requested one is not allowed => take the biggest
        cavityCount = possibleCavityCounts[possibleCavityCounts.length - 1];
      } else {
        cavityCount = userPart.cavityCount;
      }
    }
  }
  // fallback to the most useful value
  if (typeof cavityCount === 'undefined') {
    cavityCount = 1;
  }
  // changed with IGL-1376
  if (isPmFastLine && cavityCount > 4) {
    cavityCount = 4;
  }
  const cycleTime = getCycleTime(principalMoldName, config);
  return {
    principalMoldName,
    cavityCount,
    cycleTime
  };
};
/**
 * Return the MoldDetails for the given Moldinjection values.
 */
const getMoldDetailsForInjectionMold = config => {
  const moldParams = config;
  return {
    principalMoldName: moldParams.principalMoldName,
    cavityCount: moldParams.cavityCount,
    cycleTime: moldParams.cycleTime
  };
};
/**
 * Return the PrincipalMoldName and the CavityCount for the given values.
 */
const getMoldDetails = (config, material, quantity, processingMethod) => {
  let moldDetails;
  if (isBearingParams(config)) {
    moldDetails = getMoldDetailsForBearingParams(config, material, quantity, processingMethod);
  } else {
    moldDetails = getMoldDetailsForInjectionMold(config);
  }
  return moldDetails;
};

const lerp = (start, end, weight) => {
  return start * (1 - weight) + end * weight;
};
const colorBetween = (startColorString, endColorString, gradientDegree = 0.5, outputFormat = 'hsl') => {
  const startColor = Color(startColorString).hsl();
  const endColor = Color(endColorString).hsl();
  const startHSL = startColor.object();
  const endHSL = endColor.object();
  const h = lerp(startHSL.h, endHSL.h, gradientDegree);
  const s = lerp(startHSL.s, endHSL.s, gradientDegree);
  const l = lerp(startHSL.l, endHSL.l, gradientDegree);
  const alpha = lerp(startColor.alpha(), endColor.alpha(), gradientDegree);
  const resultColor = Color.hsl(h, s, l).alpha(alpha);
  if (typeof resultColor[outputFormat] !== 'function') {
    throw new Error(`Invalid output format: ${outputFormat}`);
  }
  if (outputFormat === 'hex') {
    return resultColor.hex();
  }
  // Returns a string representation like `hsl(…)` or `rgb(…)`, etc.
  return resultColor[outputFormat]().string();
};

/**
 *  Returns the color for value in a range where the leftValue represents the leftColor and the rightValue the
 *  rightColor.
 */
const getColorFromRange = (value, leftValue, rightValue, leftColor, rightColor) => {
  if (value < leftValue) {
    throw new Error(`Value ${value} must not be smaller than leftValue ${leftValue} in getColorFromRange!`);
  }
  if (value > rightValue) {
    throw new Error(`Value ${value} must not be greater than rightValue ${rightValue} in getColorFromRange!`);
  }
  const factor = (value - leftValue) / (rightValue - leftValue);
  return colorBetween(leftColor, rightColor, factor, 'hex');
};

const closestSmallerValueInSortedArray = (array, value) => {
  let result = 0;
  let lastDelta;
  array.some(item => {
    const delta = value - item;
    if (delta < 0) {
      return false;
    }
    if (delta >= lastDelta) {
      return true;
    }
    result = item;
    lastDelta = delta;
  });
  return result;
};
/**
 *
 * @param isGermanPriceSession IGL-4308 - only valid parts accepted by rulebox will have german price session
 */
const getCalculationQuantity = (quantity, isAuthorized = false, isGermanPriceSession = true) => {
  /**
   * IGL-4308 - temporary solution for catalog parts that violates rulebox
   * sets calculation quantity to maximum possible value that still returns price from MMA
   * let sales people adjust discount manually
   */
  if (isAuthorized && !isGermanPriceSession && quantity >= FREE_LOT_SIZE_QUANTITY) {
    return FREE_LOT_SIZE_QUANTITY - 1;
  }
  if (quantity >= FREE_LOT_SIZE_QUANTITY) {
    return quantity;
  } else {
    return closestSmallerValueInSortedArray(DEFAULT_MI_QUANTITIES, quantity);
  }
};

const salesModeCookieTokenKey = 'ibd_azure_token';

class IbhRedirect {
  /**
   * Builds the shop url for the given article number and session
   * Resulting url https://www.igus.de/load?target=redirect-ibd-to-ibh&shape=F&unitSystem=metric&dimensions=d1%3A10
   * OR
   * Resulting url https://www.igus.de/load?target=redirect-ibd-to-ibh&shape=F
   */
  static buildIbhShopUrl(session, otherParams, prodEnv = true) {
    const domain = IbhRedirect.getIProDomain(session).replace(/^www\./, '');
    const pre = prodEnv ? 'www.' : 'webtest.';
    const searchParams = new URLSearchParams(otherParams);
    searchParams.set('target', 'redirect-ibd-to-ibh');
    return `https://${pre}${domain}/load?${searchParams.toString()}`;
  }
  static getIProDomain(session) {
    let match = iProDomains.find(({
      country,
      language
    }) => country === session.country && language === session.language);
    if (match) {
      return match.url;
    }
    match = iProDomains.find(({
      country
    }) => country === session.country);
    if (match) {
      return match.url;
    }
    match = iProDomains.find(({
      country
    }) => country === 'DE');
    return match.url;
  }
}

export { ABS_MAX_DIMENSION, BEVEL_AXIAL_MIN_DISTANCE, BEVEL_RADIAL_MIN_DISTANCE, BevelNamesArray, CToString, ColorCaption, Context, CustomizationType, DAYS_IN_WEEK, DEFAULT_MI_QUANTITIES, DEFAULT_QUANTITY_ORDER_LIMIT, DETAILED_MI_QUANTITIES, DistributionChannel, EASY_CLIP_LONG_NOSE_LENGTH_MM, EASY_CLIP_NOSE_HEIGHT, EASY_CLIP_SHORT_NOSE_LENGTH_MM, FREE_LOT_SIZE_QUANTITY, FastLineCalculationHint, IbhRedirect, MI_FALLBACK_PART_NO, MNA_FALLBACK_PART_NO, MOLD_SETUP_SALES_FACTOR, MechanicalReprocessingCalculationHint, NumberRangeIdentifier, OfferType, PI_QUARTER, PriceCalculationType, ProfitColorType, Rulebox3D, RuleboxMI, RuleboxMIEasyClip, RuleboxMIFactory, RuleboxMIFlangeBearing, RuleboxMIKnifeEdgeRoller, RuleboxMIMcm, RuleboxMIPistonRing, RuleboxMISleeveBearing, RuleboxMIThrustWasher, SRF_FALLBACK_PART_NO, SemiFinishedCalculationHint, TD_FALLBACK_PART_NO, Units, addDeliveryTimeDelay, allMockMaterials, allNumberRangeDefinitions, applyRulesetIds, applyStricterAdjustmentRule, attributeTypeAsArray, attributesAreSetAndEqual, bearingShapes, bearingToString, bevelToLengthAngle, bevelToLengthHeight, biggerMinValueRule, booleanOperatorsAsArray, calcPowerRegressionLeastSquaresParameters, calculateOutputPerHour, calculateProductionTimeInHours, cavityCountToArrayIndex, ceil2, ceil3, ceil4, checkIfCorrespondingDimensionsAreEqual, cleanTerm, cncProcedures, colorBetween, combineMaterials, compareTwoRules, createKnifeEdgeRollerPartNo, createLinearFn, createLogLinearFn, createPowerFn, createSigmoidFn, defaultIT10Tolerance, defaultLifetimeConfig, defaultLifetimeSpacialMotion, defaultToleranceClass, doCompactRules, doesValueMatchMaxRule, doesValueMatchMinRule, doesValueMatchRule, easyClipToString, evaluateTermBoolean, executeIMinMaxRule, filterMaterialsByCountry, findPartFromCatalog, findValuesForRules, flangeBearingToString, floor2, floor4, formTypeAsArray, formTypeAsArrayIBD, formTypeAsArrayIBH, formTypeAsArrayICN, getAllLimitingAttributesFromDictionary, getB1ToleranceValues, getB2Suggestion, getB2ToleranceValues, getBearingBoundingBox, getBearingFormTypeModel, getBearingMass, getBearingVolume, getBevelAngleRange, getBevelLengthRange, getBlanketOrderLotSize, getBlanketOrderRuntimeInYears, getCBoundingBox, getCFlangeThicknessBottom, getCFlangeThicknessTop, getCLength, getCProjectionArea, getCVolume, getCWallThickness, getCalculationQuantity, getColorFromRange, getCommonLimitingAttributesFromDictionary, getConfiguredMaterial, getCurrentDateTime, getCycleTime, getD1ToleranceValues, getD2ToleranceValues, getD3Suggestion, getD3ToleranceValues, getD4ToleranceValues, getDBoundingBox, getDLength, getDVolume, getDefaultTolerances, getDefaultTolerancesC, getDefaultTolerancesEC, getDefaultTolerancesF, getDefaultTolerancesPR, getDefaultTolerancesRL, getDefaultTolerancesS, getDefaultTolerancesT, getDeliveryTimeText, getEasyClipBoundingBox, getEasyClipFlangeThickness, getEasyClipLength, getEasyClipNoseLength, getEasyClipProjectionArea, getEasyClipWallThickness, getEffMaxLength, getEffWt, getExpandedBearing, getExpandedC, getExpandedEasyClip, getExpandedFlangeBearing, getExpandedKnifeEdgeRoller, getExpandedPistonRing, getExpandedSleeveBearing, getExpandedThrustWasher, getFDataSetIndex, getFWallthicknessB2MinMax, getFallbackPartNo, getFastLineLimitedWallthicknessMaterials, getFlangeBearingBevelAngleRange, getFlangeBearingBevelLengthRange, getFlangeBearingBevelsOrDefaults, getFlangeBearingBoundingBox, getFlangeBearingDefaultBevelAngleF1, getFlangeBearingDefaultBevelLengthF1, getFlangeBearingFlangeThickness, getFlangeBearingLength, getFlangeBearingProjectionArea, getFlangeBearingRadius, getFlangeBearingVolume, getFlangeBearingWallThickness, getFlangeThickness, getIbdShapeFromPartNo, getKBoundingBox, getKLength, getKVolume, getKnifeEdgeRollerBoundingBox, getKnifeEdgeRollerLength, getKnifeEdgeRollerProjectionArea, getKnifeEdgeRollerVolume, getKnifeEdgeRollerWallThickness, getLength, getMachineTypeNo, getMaterialCodeForLowCostMaterialCode, getMaterialDisplayName, getMaterialFilters, getMaterialFiltersMultiPm, getMaterialFiltersSinglePm, getMaterialsFor3D, getMaterialsForSemiFinished, getMoldDetails, getMoldDetailsForBearingParams, getMoldDetailsForInjectionMold, getMoldInjectionMaterialDefaults, getMoldInjectionMaterialFilters, getPartNoFromCatalog, getPartsFromCatalogWithDifferentMaterial, getPistonRingBevelsOrDefaults, getPistonRingBoundingBox, getPistonRingLength, getPistonRingProjectionArea, getPistonRingVolume, getPistonRingWallThickness, getPossibleCavityQuantities, getPrecision, getProjectedArea, getQuantityOfCavities, getRulesByAttrNameFromLimitingRules, getRulesByAttrNamesFromDictionary, getSDataSetIndex, getSFMinimalWallthicknessB1, getShapeFromPartNo, getSimilarGreaterPartsFromCatalog, getSimilarGreaterPartsFromCatalogForMNA, getSimilarPartsFromCatalog, getSimilarPartsSuggestion, getSimilarSmallerPartsFromCatalog, getSleeveBearingBevelAngleRange, getSleeveBearingBevelLengthRange, getSleeveBearingBevelsOrDefaults, getSleeveBearingBoundingBox, getSleeveBearingDefaultBevelAngleF1F4, getSleeveBearingDefaultBevelLengthF1F4, getSleeveBearingLength, getSleeveBearingProjectionArea, getSleeveBearingVolume, getSleeveBearingWallThickness, getSplitBearingAntiRotationFlangeThickness, getSplitBearingAntiRotationLength, getSplitBearingLength, getSprueMass, getSprueVolumePerMold, getTeBoundingBox, getTeLength, getTeVolume, getThrustWasherBoundingBox, getThrustWasherLength, getThrustWasherProjectionArea, getThrustWasherVolume, getThrustWasherWallThickness, getToleranceAfterAssemblyInHousing, getToleranceClassByMaterial, getToleranceClassByMaterial3D, getToleranceRangeByToleranceString, getToleranceResultingDimension, getToleratorHoleValues, getToleratorShaftValues, getTwoHoleFlangeBearingBoundingBox, getTwoHoleFlangeBearingFlangeThickness, getTwoHoleFlangeBearingPreloadLength, getTwoHoleFlangeBearingVolume, getUniqueRuleAttrIds, getUnit, getUnitSystemFactor, getVFBoundingBox, getVFFlangeThickness, getVFLength, getVFVolume, getVSBoundingBox, getVSLength, getVSVolume, getValidLimitingRules, getWallThickness, getWoundMaterialFilters, getforcedMaxD1D2, getforcedMaxD1D2D3, getforcedMinD1D2, getforcedMinD1D2D3, hasBearingAGreaterSizeForMNA, hasBearingASimilarGreaterSize, hasBearingASimilarSize, hasBearingASimilarSmallerSize, hasBearingDefaultTolerances, hasBearingDefaultTolerancesC, hasBearingDefaultTolerancesEC, hasBearingDefaultTolerancesF, hasBearingDefaultTolerancesPR, hasBearingDefaultTolerancesRL, hasBearingDefaultTolerancesS, hasBearingDefaultTolerancesT, hasBearingTheSameBevels, hasBearingTheSameSize, hasBearingTheSameTolerances, hasBearingTheSameTolerancesC, hasBearingTheSameTolerancesEC, hasBearingTheSameTolerancesF, hasBearingTheSameTolerancesPR, hasBearingTheSameTolerancesRL, hasBearingTheSameTolerancesS, hasBearingTheSameTolerancesT, hasCASimilarGreaterSize, hasCASimilarSize, hasCASimilarSmallerSize, hasCTheSameSize, hasEasyClipASimilarGreaterSize, hasEasyClipASimilarSize, hasEasyClipASimilarSmallerSize, hasEasyClipTheSameSize, hasFlangeBearingAGreaterSizeForMNA, hasFlangeBearingASimilarGreaterSize, hasFlangeBearingASimilarSize, hasFlangeBearingASimilarSmallerSize, hasFlangeBearingTheSameBevels, hasFlangeBearingTheSameSize, hasGreaterSize, hasKnifeEdgeRollerAGreaterSizeForMNA, hasKnifeEdgeRollerASimilarGreaterSize, hasKnifeEdgeRollerASimilarSize, hasKnifeEdgeRollerASimilarSmallerSize, hasKnifeEdgeRollerTheSameSize, hasPistonRingAGreaterSizeForMNA, hasPistonRingASimilarGreaterSize, hasPistonRingASimilarSize, hasPistonRingASimilarSmallerSize, hasPistonRingTheSameSize, hasSleeveBearingAGreaterSizeForMNA, hasSleeveBearingASimilarGreaterSize, hasSleeveBearingASimilarSize, hasSleeveBearingASimilarSmallerSize, hasSleeveBearingTheSameBevels, hasSleeveBearingTheSameSize, hasSmallerSize, hasThrustWasherAGreaterSizeForMNA, hasThrustWasherASimilarGreaterSize, hasThrustWasherASimilarSize, hasThrustWasherASimilarSmallerSize, hasThrustWasherTheSameSize, igutexMaterials, imperialCountries, imperialMockCatalogParts, intersectArray, isAttributeFunction, isAttributeInRulesets, isAttributeTypeMaterial, isAttributeValue, isBearingParams, isBearingParamsComplete, isBevelChargeable, isBlanketOrder, isCForm, isDeliveryTimeCode, isDeliveryTimeRange, isDeliveryTimeRangeExtended, isEasyClipForm, isEqualIBooleanRule, isEqualIMinMaxRule, isEqualIStringRule, isFlangeBearingForm, isIBooleanRule, isIBooleanValueRule, isIBooleanVariableRule, isIMaxValueRule, isIMinMaxRule, isIMinMaxValueRule, isIMinValueRule, isIStringRule, isImperialCountry, isInHigherRange, isInLowerRange, isInjectionMoldParams, isKnifeEdgeRollerForm, isLeftIMinMaxRuleContainedInRightIMinMaxRule, isLeftIMinMaxRuleExtendedDownByRightIMinMaxRule, isLeftIMinMaxRuleExtendedUpByRightIMinMaxRule, isLeftIStringRuleContainedInRightIStringRule, isLowCostMaterial, isMaterialAllowed, isMaterialSuitableByProcessingMethod, isOneOffOrder, isPartInCatalog, isPartInCatalogWithDifferentMaterial, isPistonRingForm, isRightIMinMaxRuleContainedInLeftIMinMaxRule, isRightIStringRuleContainedInLeftIStringRule, isRuleUseful, isSleeveBearingForm, isThrustWasherForm, isUserMaterial, isValidAttrId, isValidInAttrIds, isValidShrinkageForPistonRingWithEjector, isWallthicknessInLimitingRules, joinMaterials, joinStringArraysByAnd, joinStringArraysByOr, knifeEdgeRollerToString, lesserMaxValueRule, mapDaysToDeliveryTimeCode, mapDeliveryTimeCodeToDays, mapDeliveryTimeToDays, mapTimeRangeIntoTimeLevel, materialA160, materialA180, materialAvailabilityCountryIndex, materialAvailabilityIndex, materialAvailabilityMap, materialE7, materialG, materialJ, materialTradeMarks, materialsC, materialsCFastLine, materialsD, materialsDFastLine, materialsEasyClip, materialsEasyClipFastLine, materialsFL, materialsFLFastLine, materialsFSG, materialsFSGFastLine, materialsFastLine, materialsK, materialsKFastLine, materialsPistonRing, materialsPistonRingFastLine, materialsRL, materialsRLFastLine, materialsSFTDisabled, materialsTE, materialsTEFastLine, materialsVF, materialsVFFastLine, materialsVFL, materialsVFLFastLine, materialsVS, materialsVSFastLine, materialsWoundS, materialsY, materialsYFastLine, materialsYK, materialsYKFastLine, maxOperatorsAsArray, maxWallThicknessPerProcedure, metricMockCatalogParts, minOperatorsAsArray, mouldProcedures, nbsCategories, parameterToPartNoElement, partNoIsC, partNoIsD, partNoIsEasyClip, partNoIsFlangeBearing, partNoIsK, partNoIsKnifeEdgeRoller, partNoIsPistonRing, partNoIsSleeveBearing, partNoIsTE, partNoIsThrustWasher, partNoIsTwoHoleFlangeBearing, partNoIsTwoHoleFlangeBearingPreload, partNoIsVK, partNoIsVS, partNoIsY, partNoIsYK, partsMaterialsDisabled, pistonRingToString, possibleCavityQuantities, prepareB2B3Rules, prepareCDiameterRules, prepareDiameterNoFlangeRules, prepareDiameterOneFlangeRules, prepareJoinTwoDiameterMinMaxRules, prepareMcmLengthRules, principalMoldNameSFTFastLine, principalMoldNames, principalMoldNamesEC, principalMoldNamesKnifeEdgeRollers, principalMoldNamesMCM, principalMoldNamesOTHERS, principalMoldNamesPR, principalMoldNamesPRFastLine, principalMoldNamesSFT, printProcedures, procedurePerMethodMap, processingMethods, processingMethodsAsArray, processingMethodsIBHAsArray, processingMethodsIbd, processingMethodsIbdCatalog, processingMethodsIbh, processingMethodsIbhCatalog, productionDelays, profitColorGreen, profitColorRed, profitColorYellow, recursiveCompactRules, removeMaterialRulesFromRuleset, replaceOperation, replaceParentheses, requiredAttrExistInGivenAttr, round1, round10, round2, round3, round4, round5, salesModeCookieTokenKey, shapeWidenIds, simplifyTerm, sleeveBearingToString, standardIT10Tolerances, stringArraysEqual, stringOperatorsAsArray, thrustWasherToString, trunc1, trunc10, trunc2, trunc3, trunc4, trunc5, tryMinmizeRule, tryUpdateMax, tryUpdateMaxOnlyD1fromD2, tryUpdateMaxOnlyD2fromD1, tryUpdateMin, tryUpdateMinOnlyD1fromD2, tryUpdateMinOnlyD2fromD1, uniqueConcat, uniqueSortedNumArray, unitSystemCharacter, unitsConversionMap, woundProcedures };
